var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// packages/aria/src/components/Provider/Provider.tsx
import { Provider } from "@fluentui/react-northstar";

// packages/aria/src/components/Button/Button.tsx
import {
  useFluentContext as useFluentContext2,
  getElementType as getElementType2,
  useAccessibility as useAccessibility2,
  useTelemetry as useTelemetry2,
  useStyles as useStyles2,
  useUnhandledProps as useUnhandledProps2,
  createShorthandFactory as createShorthandFactory2,
  Animation,
  compose
} from "@fluentui/react-northstar";
import { buttonBehavior } from "@fluentui/accessibility";

// packages/aria/src/components/Typography/Label/Label.tsx
import { forwardRef } from "react";
import {
  useFluentContext,
  getElementType,
  useAccessibility,
  useTelemetry,
  useStyles,
  useUnhandledProps,
  createShorthandFactory
} from "@fluentui/react-northstar";
import { jsx } from "react/jsx-runtime";
var labelClassName = "ui-label";
var displayName = "Label";
var Label = forwardRef((props, ref) => {
  const context = useFluentContext();
  const { setStart, setEnd } = useTelemetry(displayName, context.telemetry);
  setStart();
  const {
    className,
    styles,
    variables,
    children,
    accessibility,
    design,
    variant = "01",
    uc,
    link,
    weight,
    maximumLines,
    inline
  } = props;
  const getA11yProps = useAccessibility(accessibility, {
    debugName: displayName,
    rtl: context.rtl
  });
  const { classes } = useStyles(displayName, {
    className: labelClassName,
    mapPropsToStyles: () => ({
      inline,
      uc,
      link,
      weight,
      variant,
      maximumLines
    }),
    mapPropsToInlineStyles: () => ({
      className,
      styles,
      variables,
      design
    }),
    rtl: context.rtl,
    unstable_props: props
  });
  const unhandledProps = useUnhandledProps(Label.handledProps, props);
  const ElementType = getElementType(props);
  const result = /* @__PURE__ */ jsx(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      children
    }
  );
  setEnd();
  return result;
});
Label.displayName = displayName;
Label.create = createShorthandFactory({
  Component: Label,
  mappedProp: "children"
});
Label.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "inline",
  "uc",
  "link",
  "weight",
  "variant",
  "maximumLines"
];

// packages/aria/src/components/Button/Button.tsx
import { Loader } from "@archipro-design/icons";

// packages/aria/src/utils/compose.ts
function commonHandledProps(config = {}) {
  const { as = true } = config;
  return [
    "className",
    "styles",
    "variables",
    "design",
    "accessibility",
    as && "as"
  ].filter(Boolean);
}

// packages/aria/src/components/Button/Button.tsx
import cx from "classnames";
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var buttonClassName = "ui-button";
var Button = compose(
  function Button2(props, ref, composeOptions) {
    const context = useFluentContext2();
    const { setStart, setEnd } = useTelemetry2(
      composeOptions.displayName,
      context.telemetry
    );
    setStart();
    const {
      className: classNameProp,
      styles,
      variables: variablesProp,
      accessibility,
      design,
      disabled = false,
      disabledFocusable = false,
      as: asProp,
      icon,
      iconBefore,
      iconAfter,
      onClick,
      children,
      loading,
      fluid,
      color = "primary",
      text,
      selected,
      transparent: transparentProp,
      size: sizeProp = 36,
      outlined: outlinedProp = false,
      variant: variantProp = "contained"
    } = props;
    const {
      transparent = transparentProp,
      outlined = outlinedProp,
      size = sizeProp,
      className = classNameProp,
      variables = variablesProp,
      variant = variantProp
    } = composeOptions.state;
    let variantValue = variant;
    if (text) {
      variantValue = "text";
    }
    if (transparentProp || transparent) {
      variantValue = "text";
    }
    if (outlinedProp || outlined) {
      variantValue = "outlined";
    }
    const getA11yProps = useAccessibility2(
      accessibility,
      {
        debugName: composeOptions.displayName,
        rtl: context.rtl,
        mapPropsToBehavior: () => ({
          disabled,
          disabledFocusable,
          as: asProp
        }),
        actionHandlers: {
          performClick: (event) => {
            event.preventDefault();
            handleClick(event);
          }
        }
      }
    );
    const hasContent = !!children;
    const { classes } = useStyles2(
      composeOptions.displayName,
      {
        className: composeOptions.className,
        mapPropsToStyles: () => ({
          disabled,
          disabledFocusable,
          loading,
          fluid,
          selected,
          hasContent,
          size,
          color,
          variant: variantValue,
          transparent
        }),
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps2(
      composeOptions.handledProps,
      props
    );
    const ElementType = getElementType2(props);
    function handleClick(e) {
      if (disabled || disabledFocusable) {
        e.preventDefault();
        return;
      }
      onClick?.(e, props);
    }
    const renderIcon = () => {
      if (!icon) {
        return null;
      }
      return /* @__PURE__ */ jsx2("span", { className: classes["icon"], children: icon });
    };
    const renderIconBefore = () => {
      if (!iconBefore) {
        return null;
      }
      return /* @__PURE__ */ jsx2("span", { className: `${classes["icon"]} ${classes["iconBefore"]}`, children: iconBefore });
    };
    const renderIconAfter = () => {
      if (!iconAfter) {
        return null;
      }
      return /* @__PURE__ */ jsx2("span", { className: `${classes["icon"]} ${classes["iconAfter"]}`, children: iconAfter });
    };
    const renderContent = () => {
      return Label.create(children, {
        defaultProps: () => ({
          className: classes["buttonText"],
          uc: false
        })
      });
    };
    const renderLoader = () => {
      if (!loading) {
        return null;
      }
      return /* @__PURE__ */ jsx2(Animation, { name: "rotateUltraSlow", className: classes["loader"], children: /* @__PURE__ */ jsx2("span", { className: classes["icon"], children: /* @__PURE__ */ jsx2(Loader, {}) }) });
    };
    const result = /* @__PURE__ */ jsxs(
      ElementType,
      {
        ...getA11yProps("root", {
          className: cx(classes["root"], { active: selected }),
          ref,
          onClick: handleClick,
          ...unhandledProps
        }),
        children: [
          renderIconBefore(),
          renderIcon(),
          renderLoader(),
          renderContent(),
          renderIconAfter()
        ]
      }
    );
    setEnd();
    return result;
  },
  {
    displayName: "Button",
    className: buttonClassName,
    handledProps: [
      ...commonHandledProps(),
      "disabled",
      "disabledFocusable",
      "fluid",
      "onClick",
      "children",
      "color",
      "text",
      "selected",
      "variables",
      "transparent",
      "icon",
      "iconAfter",
      "iconBefore",
      "size",
      "loading",
      "outlined",
      "variant"
    ],
    shorthandConfig: { mappedProp: "children" }
  }
);
Button.defaultProps = {
  as: "button",
  accessibility: buttonBehavior,
  size: 36
};
Button.create = createShorthandFactory2({
  Component: Button
});

// packages/aria/src/components/Typography/Header/Header.tsx
import { forwardRef as forwardRef2 } from "react";
import {
  useFluentContext as useFluentContext3,
  getElementType as getElementType3,
  useAccessibility as useAccessibility3,
  useTelemetry as useTelemetry3,
  useStyles as useStyles3,
  useUnhandledProps as useUnhandledProps3,
  createShorthandFactory as createShorthandFactory3
} from "@fluentui/react-northstar";
import { jsx as jsx3 } from "react/jsx-runtime";
var HeaderClassName = "ui-header";
var displayName2 = "Header";
var Header = forwardRef2((props, ref) => {
  const context = useFluentContext3();
  const { setStart, setEnd } = useTelemetry3(displayName2, context.telemetry);
  setStart();
  const {
    className,
    styles,
    variables,
    children,
    accessibility,
    design,
    inline,
    uc,
    link,
    weight,
    variant,
    maximumLines
  } = props;
  const getA11yProps = useAccessibility3(accessibility, {
    debugName: displayName2,
    rtl: context.rtl
  });
  const { classes } = useStyles3(displayName2, {
    className: HeaderClassName,
    mapPropsToStyles: () => ({
      inline,
      uc,
      link,
      weight,
      variant,
      maximumLines
    }),
    mapPropsToInlineStyles: () => ({
      className,
      styles,
      variables: { ...variables ?? {}, as: props.as },
      design
    }),
    rtl: context.rtl,
    unstable_props: props
  });
  const unhandledProps = useUnhandledProps3(Header.handledProps, props);
  const ElementType = getElementType3(props);
  const result = /* @__PURE__ */ jsx3(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      children
    }
  );
  setEnd();
  return result;
});
Header.displayName = displayName2;
Header.create = createShorthandFactory3({
  Component: Header,
  mappedProp: "children"
});
Header.defaultProps = {
  as: "h1"
};
Header.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "inline",
  "uc",
  "link",
  "weight",
  "variant",
  "maximumLines"
];

// packages/aria/src/components/Typography/Subheader/Subheader.tsx
import { forwardRef as forwardRef3 } from "react";
import {
  useFluentContext as useFluentContext4,
  getElementType as getElementType4,
  useAccessibility as useAccessibility4,
  useTelemetry as useTelemetry4,
  useStyles as useStyles4,
  useUnhandledProps as useUnhandledProps4,
  createShorthandFactory as createShorthandFactory4
} from "@fluentui/react-northstar";
import { jsx as jsx4 } from "react/jsx-runtime";
var SubheaderClassName = "ui-subheader";
var displayName3 = "Subheader";
var Subheader = forwardRef3(
  (props, ref) => {
    const context = useFluentContext4();
    const { setStart, setEnd } = useTelemetry4(
      displayName3,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      children,
      accessibility,
      design,
      inline,
      uc,
      link,
      weight = "regular",
      variant,
      maximumLines
    } = props;
    const getA11yProps = useAccessibility4(accessibility, {
      debugName: displayName3,
      rtl: context.rtl
    });
    const { classes } = useStyles4(displayName3, {
      className: SubheaderClassName,
      mapPropsToStyles: () => ({
        inline,
        uc,
        link,
        weight,
        variant,
        maximumLines
      }),
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    });
    const unhandledProps = useUnhandledProps4(Subheader.handledProps, props);
    const ElementType = getElementType4(props);
    const result = /* @__PURE__ */ jsx4(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children
      }
    );
    setEnd();
    return result;
  }
);
Subheader.displayName = displayName3;
Subheader.create = createShorthandFactory4({
  Component: Subheader,
  mappedProp: "children"
});
Subheader.defaultProps = {
  as: "div",
  variant: "01"
};
Subheader.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "inline",
  "uc",
  "link",
  "weight",
  "variant",
  "maximumLines"
];

// packages/aria/src/components/Typography/Paragraph/Paragraph.tsx
import { forwardRef as forwardRef4 } from "react";
import {
  useFluentContext as useFluentContext5,
  getElementType as getElementType5,
  useAccessibility as useAccessibility5,
  useTelemetry as useTelemetry5,
  useStyles as useStyles5,
  useUnhandledProps as useUnhandledProps5,
  createShorthandFactory as createShorthandFactory5
} from "@fluentui/react-northstar";
import { jsx as jsx5 } from "react/jsx-runtime";
var paragraphClassName = "ui-paragraph";
var displayName4 = "Paragraph";
var Paragraph = forwardRef4(
  (props, ref) => {
    const context = useFluentContext5();
    const { setStart, setEnd } = useTelemetry5(
      displayName4,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      children,
      accessibility,
      design,
      uc,
      link,
      weight,
      maximumLines,
      inline
    } = props;
    const getA11yProps = useAccessibility5(accessibility, {
      debugName: displayName4,
      rtl: context.rtl
    });
    const { classes } = useStyles5(displayName4, {
      className: paragraphClassName,
      mapPropsToStyles: () => ({
        inline,
        uc,
        link,
        weight,
        maximumLines
      }),
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    });
    const unhandledProps = useUnhandledProps5(Paragraph.handledProps, props);
    const ElementType = getElementType5(props);
    const result = /* @__PURE__ */ jsx5(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children
      }
    );
    setEnd();
    return result;
  }
);
Paragraph.displayName = displayName4;
Paragraph.create = createShorthandFactory5({
  Component: Paragraph,
  mappedProp: "children"
});
Paragraph.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "inline",
  "uc",
  "link",
  "weight",
  "maximumLines"
];
Paragraph.defaultProps = {
  as: "p"
};

// packages/aria/src/components/Typography/Caption/Caption.tsx
import {
  useFluentContext as useFluentContext6,
  getElementType as getElementType6,
  useAccessibility as useAccessibility6,
  useTelemetry as useTelemetry6,
  useStyles as useStyles6,
  useUnhandledProps as useUnhandledProps6,
  createShorthandFactory as createShorthandFactory6
} from "@fluentui/react-northstar";
import { compose as compose2 } from "@fluentui/react-bindings";

// packages/aria/src/components/Typography/utils.ts
function typographyHandledProps() {
  return ["inline", "uc", "link", "weight"];
}

// packages/aria/src/components/Typography/Caption/Caption.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
var captionClassName = "ui-caption";
var Caption = compose2(
  (props, ref, composeOptions) => {
    const context = useFluentContext6();
    const { setStart, setEnd } = useTelemetry6(
      composeOptions.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      children,
      accessibility,
      design,
      inline,
      uc,
      link,
      weight,
      variant,
      maximumLines
    } = props;
    const getA11yProps = useAccessibility6(accessibility, {
      debugName: composeOptions.displayName,
      rtl: context.rtl
    });
    const { classes } = useStyles6(
      composeOptions.displayName,
      {
        className: composeOptions.className,
        mapPropsToStyles: () => ({
          inline,
          uc,
          link,
          weight,
          variant,
          maximumLines
        }),
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props,
        composeOptions
      }
    );
    const unhandledProps = useUnhandledProps6(
      composeOptions.handledProps,
      props
    );
    const ElementType = getElementType6(props);
    const content = composeOptions.state.children || children;
    const result = /* @__PURE__ */ jsx6(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: content
      }
    );
    setEnd();
    return result;
  },
  {
    displayName: "Caption",
    className: captionClassName,
    handledProps: [
      ...commonHandledProps(),
      ...typographyHandledProps(),
      "variant",
      "maximumLines"
    ],
    overrideStyles: true
  }
);
Caption.create = createShorthandFactory6({
  Component: Caption,
  mappedProp: "children"
});
Caption.defaultProps = {
  variant: "01"
};

// packages/aria/src/components/Logo/Logo.tsx
import { createShorthandFactory as createShorthandFactory7 } from "@fluentui/react-northstar";
import { imageBehavior } from "@fluentui/react-northstar";
import { getElementType as getElementType7, useUnhandledProps as useUnhandledProps7 } from "@fluentui/react-bindings";
import {
  useAccessibility as useAccessibility7,
  useFluentContext as useFluentContext7,
  useStyles as useStyles7,
  useTelemetry as useTelemetry7
} from "@fluentui/react-bindings";
import { LogoFull } from "@archipro-design/icons";
import { Logo as ShortLogo } from "@archipro-design/icons";
import { forwardRef as forwardRef5 } from "react";
import { Animation as Animation2 } from "@fluentui/react-northstar";
import { jsx as jsx7, jsxs as jsxs2 } from "react/jsx-runtime";
var logoClassName = "ui-logo";
var displayName5 = "Logo";
var Logo = forwardRef5((props, ref) => {
  const context = useFluentContext7();
  const { setStart, setEnd } = useTelemetry7(displayName5, context.telemetry);
  setStart();
  const getA11yProps = useAccessibility7(imageBehavior, {
    debugName: displayName5,
    mapPropsToBehavior: () => ({
      "aria-label": "ArchiPro logo"
    })
  });
  const { className, styles, variables, design, showFull, animationType } = props;
  const isAnimatedDesktopLogo = animationType === "eraseOut" || animationType === "typing";
  const isAnimatedMobileLogo = animationType === "scaleUp" || animationType === "scaleDown";
  const { classes } = useStyles7(displayName5, {
    className: logoClassName,
    mapPropsToInlineStyles: () => ({
      className,
      styles,
      variables,
      design
    }),
    mapPropsToStyles: () => ({
      animationType
    }),
    unstable_props: props,
    rtl: context.rtl
  });
  const ElementType = getElementType7(props);
  const unhandledProps = useUnhandledProps7(Logo.handledProps, props);
  const renderAnimatedDesktopLogo = () => /* @__PURE__ */ jsxs2("div", { className: classes["animatedLogoWrapper"], children: [
    /* @__PURE__ */ jsx7(LogoFull, { className: classes["animatedFullLogo"] }),
    /* @__PURE__ */ jsx7(
      Animation2,
      {
        name: animationType === "typing" ? "shrinking" : "growing",
        children: /* @__PURE__ */ jsx7(ShortLogo, { className: classes["animatedShortLogo"] })
      }
    )
  ] });
  const renderAnimatedMobileLogo = () => /* @__PURE__ */ jsx7(Animation2, { name: animationType, children: /* @__PURE__ */ jsx7(ShortLogo, { className: classes["animatedMobileShortLogo"] }) });
  const Element = getA11yProps.unstable_wrapWithFocusZone(
    /* @__PURE__ */ jsxs2(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: [
          !animationType && (showFull ? /* @__PURE__ */ jsx7(LogoFull, {}) : /* @__PURE__ */ jsx7(ShortLogo, {})),
          isAnimatedMobileLogo && renderAnimatedMobileLogo(),
          isAnimatedDesktopLogo && renderAnimatedDesktopLogo()
        ]
      }
    )
  );
  setEnd();
  return Element;
});
Logo.handledProps = [
  "className",
  "variables",
  "styles",
  "design",
  "showFull",
  "animationType"
];
Logo.create = createShorthandFactory7({ Component: Logo });
Logo.defaultProps = {
  as: "span"
};
Logo.displayName = displayName5;

// packages/aria/src/components/TopNavigation/TopNavigation.tsx
import { useUnhandledProps as useUnhandledProps50 } from "@fluentui/react-bindings";
import {
  getElementType as getElementType37,
  useStyles as useStyles52,
  useTelemetry as useTelemetry52
} from "@fluentui/react-bindings";

// packages/aria/src/themes/base/colors.ts
var naturalColors = {
  gold: {
    "000": "#BEA78C",
    100: "#C3B091",
    200: "#A89788",
    300: "#806A58"
  },
  charcoal: {
    "000": "#171616",
    100: "#707070",
    200: "#555555",
    250: "#343434",
    260: "#1A1A1A",
    300: "#313131",
    350: "#1F1F21",
    400: "#1c1c1c"
  },
  grey: {
    100: "#CACBCD",
    "100A": "#D4D1CC",
    125: "#D2D2D0",
    150: "#CDCDCD",
    "150A": "#C4C4C4",
    200: "#B3B3B3",
    300: "#9B9B9B",
    "300A": "#989485",
    400: "#8C8C8C",
    "400A": "#88837F",
    "500A": "#6B7064"
  },
  white: {
    "000": "#F8F9FA",
    100: "#FAFAFA",
    200: "#F1F1F1",
    "100A": "#FAF9F7",
    "200A": "#F2F1F0",
    250: "#F5F5F5",
    300: "#E5E5E5",
    FFF: "#FFFFFF"
  },
  green: {
    200: "#83D376"
  }
};
var contextualColors = {
  danger: {
    50: "#FFB2B2",
    100: "#E6505C",
    200: "#D03B47",
    300: "#F15D5D",
    400: "#F24C41"
  },
  success: {
    50: "#97E58A",
    100: "#63B362",
    200: "#51A450"
  }
};
var primitiveColors = {
  white: "#FFFFFF",
  black: "#000000"
};
var transparencyColors = {
  black: {
    "6%": "rgba(0,0,0,0.06)",
    "10%": "rgba(0,0,0,0.1)",
    "20%": "rgba(0,0,0,0.2)"
  },
  white: {
    "10%": "rgba(255,255,255,0.1)",
    "15%": "rgba(255,255,255,0.15)",
    "20%": "rgba(255,255,255,0.2)",
    "30%": "rgba(255,255,255,0.3)"
  }
};
var colors = {
  ...naturalColors,
  ...contextualColors,
  primitive: primitiveColors,
  transparency: transparencyColors
};
var createColorScheme = (customValues = {}) => {
  return {
    foreground: void 0,
    background: void 0,
    border: void 0,
    shadow: void 0,
    foregroundHover: void 0,
    backgroundHover: void 0,
    borderHover: void 0,
    shadowHover: void 0,
    foregroundActive: void 0,
    backgroundActive: void 0,
    borderActive: void 0,
    shadowActive: void 0,
    foregroundFocus: void 0,
    backgroundFocus: void 0,
    borderFocus: void 0,
    shadowFocus: void 0,
    foregroundPressed: void 0,
    backgroundPressed: void 0,
    borderPressed: void 0,
    shadowPressed: void 0,
    foregroundDisabled: void 0,
    backgroundDisabled: void 0,
    borderDisabled: void 0,
    shadowDisabled: void 0,
    placeholder: void 0,
    placeholderAlt: void 0,
    ...customValues
  };
};
var colorScheme = {
  default: createColorScheme({
    foreground: colors.charcoal["250"],
    foreground1: colors.charcoal["350"],
    foreground2: colors.charcoal["000"],
    foreground3: colors.charcoal["100"],
    foreground4: colors.primitive.white,
    foreground5: colors.grey["400"],
    foreground6: colors.grey["300"],
    background: colors.primitive.white,
    border: colors.primitive.black,
    borderTransparent: colors.transparency.black["10%"],
    borderTransparentActive: "transparent",
    // shadow: colors.black,
    // shadowHover: colors.black,
    foregroundHover: colors.charcoal[100],
    backgroundHover: colors.transparency.black["6%"],
    backgroundHover1: colors.charcoal[250],
    borderHover: colors.transparency.black["6%"],
    foregroundPressed: colors.primitive.white,
    backgroundPressed: colors.charcoal[350],
    borderPressed: colors.transparency.black,
    foregroundActive: colors.primitive.white,
    backgroundActive: colors.charcoal[250],
    borderActive: colors.transparency.black,
    foregroundFocus: colors.primitive.white,
    backgroundFocus: colors.charcoal[250],
    borderFocusWithin: colors.primitive.white,
    borderFocus: colors.primitive.black,
    foregroundDisabled1: colors.grey[300],
    foregroundDisabled: colors.grey[300],
    backgroundDisabled: colors.grey[150],
    borderDisabled: colors.grey[150],
    errorForeground: colors.danger[100],
    sucessForeground: colors.success[100],
    errorForBM: colors.danger[400],
    placeholder: colors.white["200A"],
    placeholderAlt: colors.charcoal["000"]
  })
};

// packages/aria/src/components/TopNavigation/TopNavigation.tsx
import {
  menuBehavior as menuBehavior6,
  useAccessibility as useAccessibility47,
  useFluentContext as useFluentContext52
} from "@fluentui/react-northstar";
import { forwardRef as forwardRef50, useEffect as useEffect7 } from "react";

// packages/aria/src/components/Flex/Flex.tsx
import {
  getElementType as getElementType8,
  useUnhandledProps as useUnhandledProps8,
  useFluentContext as useFluentContext9,
  useStyles as useStyles9,
  useTelemetry as useTelemetry9
} from "@fluentui/react-bindings";
import { createShorthandFactory as createShorthandFactory8 } from "@fluentui/react-northstar";
import * as React2 from "react";

// packages/aria/src/components/Flex/FlexItem.tsx
import {
  useStyles as useStyles8,
  useTelemetry as useTelemetry8,
  useFluentContext as useFluentContext8
} from "@fluentui/react-bindings";
import { mergeStyles } from "@fluentui/styles";
import cx2 from "classnames";
import _ from "lodash";
import * as React from "react";
var applyStyles = (element, styles, classes) => {
  if (!styles) {
    return element;
  }
  if (typeof element.type === "string") {
    return React.cloneElement(element, {
      className: cx2(element.props.className, classes["root"])
    });
  }
  return React.cloneElement(element, {
    styles: mergeStyles(styles["root"] || {}, element.props.styles)
  });
};
var flexItemClassName = "ui-flex__item";
var FlexItem = (props) => {
  const context = useFluentContext8();
  const { setStart, setEnd } = useTelemetry8(
    FlexItem.displayName,
    context.telemetry
  );
  setStart();
  const {
    align,
    children,
    className,
    design,
    grow,
    flexDirection,
    push,
    shrink,
    size,
    styles,
    variables
  } = props;
  const { classes, styles: resolvedStyles } = useStyles8(
    FlexItem.displayName,
    {
      className: flexItemClassName,
      mapPropsToStyles: () => ({
        align,
        grow,
        flexDirection,
        push,
        shrink,
        size
      }),
      mapPropsToInlineStyles: () => ({
        className,
        design,
        styles,
        variables
      }),
      rtl: context.rtl
    }
  );
  let element;
  if (typeof children === "function") {
    element = children({
      styles: resolvedStyles["root"],
      classes: classes["root"]
    });
  } else if (_.isNil(children)) {
    element = null;
  } else {
    element = applyStyles(
      React.Children.only(children),
      resolvedStyles,
      classes
    );
  }
  setEnd();
  return element;
};
FlexItem.displayName = "FlexItem";
FlexItem.__isFlexItem = true;

// packages/aria/src/components/Flex/Flex.tsx
import _2 from "lodash";
import { jsx as jsx8 } from "react/jsx-runtime";
var flexClassName = "ui-flex";
var Flex = React2.forwardRef(
  (props, ref) => {
    const context = useFluentContext9();
    const { setStart, setEnd } = useTelemetry9(
      Flex.displayName,
      context.telemetry
    );
    setStart();
    const {
      children,
      className,
      column,
      debug,
      design,
      fill,
      gap,
      hAlign,
      inline,
      padding,
      space,
      styles,
      variables,
      vAlign,
      wrap
    } = props;
    const { classes } = useStyles9(Flex.displayName, {
      className: flexClassName,
      mapPropsToStyles: () => ({
        column,
        debug,
        fill,
        gap,
        hAlign,
        inline,
        padding,
        space,
        vAlign,
        wrap
      }),
      mapPropsToInlineStyles: () => ({
        className,
        design,
        styles,
        variables
      }),
      rtl: context.rtl
    });
    const ElementType = getElementType8(props);
    const unhandledProps = useUnhandledProps8(Flex.handledProps, props);
    const content = React2.Children.map(children, (child) => {
      const isFlexItemElement = !!_2.get(child, "type.__isFlexItem");
      return isFlexItemElement ? React2.cloneElement(child, {
        flexDirection: column ? "column" : "row"
      }) : child;
    });
    const element = /* @__PURE__ */ jsx8(
      ElementType,
      {
        className: classes["root"],
        ...unhandledProps,
        ref,
        children: content
      }
    );
    setEnd();
    return element;
  }
);
Flex.displayName = "Flex";
Flex.create = createShorthandFactory8({
  Component: Flex,
  mappedProp: "children"
});
Flex.handledProps = [
  "className",
  "design",
  "styles",
  "variables",
  "debug",
  "inline",
  "column",
  "wrap",
  "hAlign",
  "vAlign",
  "space",
  "gap",
  "padding",
  "fill",
  "debug"
];
Flex.Item = FlexItem;

// packages/aria/src/components/SearchBar/SearchBar.tsx
import { forwardRef as forwardRef9, useEffect, useRef } from "react";
import {
  useFluentContext as useFluentContext11,
  useAccessibility as useAccessibility8,
  useTelemetry as useTelemetry11,
  useStyles as useStyles11,
  useUnhandledProps as useUnhandledProps9,
  inputBehavior,
  createShorthandFactory as createShorthandFactory11
} from "@fluentui/react-northstar";

// packages/aria/src/components/Input/Input.tsx
import { CheckLine } from "@archipro-design/icons";
import {
  Input as InputBase,
  InputLabel,
  createShorthandFactory as createShorthandFactory9,
  inputClassName,
  inputSlotClassNames
} from "@fluentui/react-northstar";
import { forwardRef as forwardRef7, useId } from "react";
import { jsx as jsx9 } from "react/jsx-runtime";
var displayName6 = "Input";
var Input = forwardRef7((props, ref) => {
  const crossBrowserStyle = {
    '& input[type="search"]': {
      "&::-webkit-search-cancel-button": {
        display: "none"
      }
    }
  };
  return /* @__PURE__ */ jsx9(
    InputBase,
    {
      errorIndicator: false,
      ref,
      ...props,
      successIndicator: props.successIndicator || /* @__PURE__ */ jsx9(SuccessIcon, {}),
      id: `ui-input-${useId()}`,
      styles: crossBrowserStyle
    }
  );
});
Input.Label = InputLabel;
Input.create = createShorthandFactory9({ Component: Input });
Input.displayName = displayName6;
function SuccessIcon() {
  return /* @__PURE__ */ jsx9(CheckLine, {});
}

// packages/aria/src/components/SearchBar/SearchBar.tsx
import { CloseLine, SearchLine } from "@archipro-design/icons";

// packages/aria/src/utils/pxToRem.ts
import { pxToRem as basePxToRem } from "@fluentui/react-northstar";
var pxToRem = (valueInPx, baseRemSize = 16) => {
  return basePxToRem(valueInPx, baseRemSize);
};
var pxArrayToRem = (pxValues, baseRemSize = 16) => {
  return pxValues.map((px) => pxToRem(px, baseRemSize)).join(" ");
};

// packages/aria/src/components/Dropdown/Dropdown.tsx
import { forwardRef as forwardRef8 } from "react";
import {
  Dropdown as FluentUIDropdown,
  useFluentContext as useFluentContext10,
  createShorthandFactory as createShorthandFactory10,
  dropdownClassName,
  dropdownSlotClassNames,
  useTelemetry as useTelemetry10,
  useStyles as useStyles10
} from "@fluentui/react-northstar";
import cx3 from "classnames";
import { ArrowDownSLine, CheckLine as CheckLine2 } from "@archipro-design/icons";
import { jsx as jsx10 } from "react/jsx-runtime";
var Dropdown = forwardRef8((props, ref) => {
  const context = useFluentContext10();
  const { setStart } = useTelemetry10(Dropdown.displayName, context.telemetry);
  setStart();
  const { className, styles, variables, design, variant } = props;
  const { classes } = useStyles10(Dropdown.displayName, {
    className: dropdownClassName,
    mapPropsToStylesProps: () => ({ variant }),
    mapPropsToInlineStyles: () => ({
      className,
      styles,
      variables,
      design
    }),
    rtl: context.rtl,
    unstable_props: props
  });
  return /* @__PURE__ */ jsx10(
    FluentUIDropdown,
    {
      ref,
      className: cx3([classes["root"], classes[`variant${variant}`]]),
      toggleIndicator: /* @__PURE__ */ jsx10(ArrowDownSLine, { variables: { "1xSize": pxToRem(16) } }),
      checkableIndicator: /* @__PURE__ */ jsx10(CheckLine2, { variables: { "1xSize": pxToRem(16) } }),
      ...props
    }
  );
});
Dropdown.displayName = "Dropdown";
Dropdown.create = createShorthandFactory10({ Component: Dropdown });

// packages/aria/src/components/SearchBar/SearchBar.tsx
import { jsx as jsx11, jsxs as jsxs3 } from "react/jsx-runtime";
var defaultRootElementType = "div";
var searchBarClassName = "ui-searchBar";
var SearchBar = forwardRef9(
  (props, ref) => {
    const context = useFluentContext11();
    const inputRef = useRef(null);
    const { setStart, setEnd } = useTelemetry11(
      SearchBar.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      design,
      accessibility,
      searchInput,
      iconButtonBefore,
      iconButtonAfter,
      cancelButton,
      preFilterDropdown
    } = props;
    const getA11yProps = useAccessibility8(
      accessibility,
      {
        debugName: SearchBar.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles11(
      SearchBar.displayName,
      {
        className: searchBarClassName,
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        mapPropsToStyles: () => ({
          searchWithCancelButton: !!cancelButton
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps9(SearchBar.handledProps, props);
    const renderCancelButton = () => {
      return Button.create(cancelButton, {
        defaultProps: () => {
          return {
            variant: "text"
          };
        }
      });
    };
    useEffect(() => {
      if (cancelButton && inputRef.current) {
        inputRef.current.focus();
      }
    }, [cancelButton]);
    const renderSearchWithCancel = () => /* @__PURE__ */ jsxs3(Flex, { children: [
      /* @__PURE__ */ jsxs3(
        Flex,
        {
          vAlign: "center",
          ...getA11yProps("root", {
            className: classes["root"],
            ref,
            ...unhandledProps
          }),
          children: [
            Button.create(iconButtonBefore, {
              defaultProps: () => {
                return {
                  icon: /* @__PURE__ */ jsx11(SearchLine, {}),
                  variant: "text",
                  variables: { buttonPadding: 8 }
                };
              }
            }),
            Input.create(searchInput, {
              defaultProps: () => {
                return {
                  autoComplete: "off",
                  fluid: true,
                  placeholder: "Search",
                  type: "search",
                  variables: { borderWidth: 0 },
                  inputRef
                };
              }
            }),
            Button.create(iconButtonAfter, {
              defaultProps: () => {
                return {
                  icon: /* @__PURE__ */ jsx11(CloseLine, {}),
                  variant: "text",
                  variables: { buttonPadding: 8 }
                };
              }
            })
          ]
        }
      ),
      renderCancelButton()
    ] });
    const renderSearch = () => /* @__PURE__ */ jsxs3(
      Flex,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        vAlign: "center",
        children: [
          Dropdown.create(preFilterDropdown, {
            defaultProps: () => {
              return {
                fluid: true,
                type: "search",
                variables: { borderWidth: 0 },
                inputRef
              };
            }
          }),
          Button.create(iconButtonBefore, {
            defaultProps: () => {
              return {
                icon: /* @__PURE__ */ jsx11(SearchLine, {}),
                variant: "text",
                variables: { buttonPadding: 8 }
              };
            }
          }),
          Input.create(searchInput, {
            defaultProps: () => {
              return {
                autoComplete: "off",
                fluid: true,
                placeholder: "Search",
                type: "search",
                variables: {
                  borderWidth: 0,
                  fontSize: pxToRem(30)
                }
              };
            }
          }),
          Button.create(iconButtonAfter, {
            defaultProps: () => {
              return {
                icon: /* @__PURE__ */ jsx11(CloseLine, {}),
                variant: "text",
                variables: { buttonPadding: 8 }
              };
            }
          })
        ]
      }
    );
    const result = cancelButton ? renderSearchWithCancel() : renderSearch();
    setEnd();
    return result;
  }
);
SearchBar.displayName = "SearchBar";
SearchBar.create = createShorthandFactory11({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: SearchBar
});
SearchBar.defaultProps = {
  as: defaultRootElementType,
  accessibility: inputBehavior
};
SearchBar.handledProps = [
  // By default, these are all handled
  ...commonHandledProps(),
  // Extra props that are handled
  "searchInput",
  "iconButtonBefore",
  "iconButtonAfter",
  "cancelButton",
  "preFilterDropdown"
];

// packages/aria/src/components/SearchMenu/SearchMenu.tsx
import { forwardRef as forwardRef12 } from "react";
import {
  useFluentContext as useFluentContext14,
  getElementType as getElementType10,
  useAccessibility as useAccessibility11,
  useTelemetry as useTelemetry14,
  useStyles as useStyles14,
  useUnhandledProps as useUnhandledProps12,
  createShorthandFactory as createShorthandFactory14,
  popupBehavior,
  Flex as Flex3
} from "@fluentui/react-northstar";

// packages/aria/src/components/SearchMenu/SearchResults.tsx
import { Flex as Flex2 } from "@fluentui/react-northstar";
import {
  menuItemBehavior,
  createShorthandFactory as createShorthandFactory13
} from "@fluentui/react-northstar";
import {
  useAccessibility as useAccessibility10,
  useFluentContext as useFluentContext13,
  useStyles as useStyles13,
  useTelemetry as useTelemetry13,
  useUnhandledProps as useUnhandledProps11
} from "@fluentui/react-bindings";
import { forwardRef as forwardRef11 } from "react";

// packages/aria/src/components/Box/Box.tsx
import { childrenExist } from "@fluentui/react-bindings";
import {
  getElementType as getElementType9,
  useUnhandledProps as useUnhandledProps10,
  useAccessibility as useAccessibility9,
  useStyles as useStyles12,
  useTelemetry as useTelemetry12,
  useFluentContext as useFluentContext12
} from "@fluentui/react-bindings";
import * as React3 from "react";
import { createShorthandFactory as createShorthandFactory12 } from "@fluentui/react-northstar";
import { rtlTextContainer } from "@fluentui/react-northstar";
import { jsx as jsx12 } from "react/jsx-runtime";
var displayName7 = "Box";
var boxClassName = "ui-box";
var Box = React3.forwardRef((props, ref) => {
  const context = useFluentContext12();
  const { setStart, setEnd } = useTelemetry12(displayName7, context.telemetry);
  setStart();
  const {
    accessibility,
    className,
    design,
    styles,
    variables,
    children,
    content
  } = props;
  const getA11yProps = useAccessibility9(
    accessibility,
    {
      debugName: displayName7,
      rtl: context.rtl
    }
  );
  const { classes } = useStyles12(displayName7, {
    className: boxClassName,
    mapPropsToInlineStyles: () => ({
      className,
      design,
      styles,
      variables
    }),
    rtl: context.rtl,
    unstable_props: props
  });
  const unhandledProps = useUnhandledProps10(Box.handledProps, props);
  const ElementType = getElementType9(props);
  const result = getA11yProps.unstable_wrapWithFocusZone(
    /* @__PURE__ */ jsx12(
      ElementType,
      {
        ...getA11yProps("root", {
          ...rtlTextContainer.getAttributes({
            forElements: [children, content]
          }),
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: childrenExist(children) ? children : content
      }
    )
  );
  setEnd();
  return result;
});
Box.displayName = displayName7;
Box.handledProps = [
  "accessibility",
  "as",
  "className",
  "children",
  "content",
  "design",
  "styles",
  "variables"
];
Box.create = createShorthandFactory12({ Component: Box });

// packages/aria/src/components/SearchMenu/SearchResults.tsx
import cx4 from "classnames";
import { Fragment, jsx as jsx13, jsxs as jsxs4 } from "react/jsx-runtime";
var displayName8 = "SearchResult";
var searchResultClassName = "ui-SearchResult";
var searchResultSlotClassNames = {
  searchItem: `${searchResultClassName}__searchItem`,
  searchCategory: `${searchResultClassName}__searchCategory`,
  match: `${searchResultClassName}__searchItemMatch`,
  itemContainer: `${searchResultClassName}__searchItemContainer`,
  itemContentContainer: `${searchResultClassName}__searchItemContentContainer`,
  unmatch: `${searchResultClassName}__searchItemUnmatch`,
  action: `${searchResultClassName}__action`
};
var SearchResult = forwardRef11(
  (props, ref) => {
    const context = useFluentContext13();
    const { setStart, setEnd } = useTelemetry13(
      displayName8,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      category,
      items,
      action,
      value
    } = props;
    const getA11yProps = useAccessibility10(
      accessibility,
      {
        debugName: displayName8,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles13(displayName8, {
      className: searchResultClassName,
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    });
    const unhandledProps = useUnhandledProps11(
      SearchResult.handledProps,
      props
    );
    const escape = function(s) {
      return s.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
    };
    const substringRegexp = value && new RegExp(`\\b^${escape(value)}(\\s)?`, "gi");
    const renderResults = /* @__PURE__ */ jsxs4(Fragment, { children: [
      Caption.create(category, {
        generateKey: true,
        defaultProps: () => {
          return {
            className: searchResultSlotClassNames.searchCategory,
            variant: "03",
            weight: "medium"
          };
        }
      }),
      items?.length > 0 && items.map(({ button, item, category: category2 }) => {
        return Button.create(button ? button : {}, {
          generateKey: true,
          defaultProps: () => ({
            className: searchResultSlotClassNames.searchItem,
            text: true,
            variant: "text",
            variables: { buttonPadding: 0 },
            key: typeof item === "object" ? item?.["content"] : item,
            children: (Component, props2) => {
              let text;
              if (typeof item === "object") {
                text = item?.["content"];
              } else {
                text = String(item);
              }
              let match = void 0;
              let unmatched = text;
              if (substringRegexp) {
                const [found] = text?.match(
                  substringRegexp
                ) ?? [""];
                match = found.length > 0 ? text?.slice(0, found.length) : "";
                unmatched = found.length > 0 ? text?.slice(
                  found.length,
                  text.length
                ) : text;
              }
              return /* @__PURE__ */ jsx13(
                Component,
                {
                  ...props2,
                  className: cx4(
                    searchResultSlotClassNames.itemContainer,
                    props2.className
                  ),
                  children: /* @__PURE__ */ jsxs4(
                    Flex2,
                    {
                      vAlign: "center",
                      className: searchResultSlotClassNames.itemContentContainer,
                      children: [
                        /* @__PURE__ */ jsxs4(Fragment, { children: [
                          Caption.create(match, {
                            generateKey: true,
                            defaultProps: () => {
                              return {
                                as: "span",
                                variant: "02",
                                className: searchResultSlotClassNames.match
                              };
                            }
                          }),
                          Caption.create(unmatched, {
                            generateKey: true,
                            defaultProps: () => {
                              return {
                                as: "span",
                                variant: "02",
                                className: searchResultSlotClassNames.unmatch,
                                maximumLines: 1
                              };
                            }
                          })
                        ] }),
                        category2 && /* @__PURE__ */ jsxs4(Fragment, { children: [
                          Caption.create(
                            ` in `,
                            {
                              generateKey: true,
                              defaultProps: () => {
                                return {
                                  className: classes["preserveWhitespace"],
                                  inline: true,
                                  variant: "02"
                                };
                              }
                            }
                          ),
                          Caption.create(
                            category2,
                            {
                              generateKey: true,
                              defaultProps: () => {
                                return {
                                  className: searchResultSlotClassNames.match,
                                  inline: true,
                                  variant: "02"
                                };
                              }
                            }
                          )
                        ] })
                      ]
                    }
                  )
                }
              );
            }
          })
        });
      }),
      action && /* @__PURE__ */ jsx13(Box, { className: searchResultSlotClassNames.action, children: action })
    ] });
    const result = /* @__PURE__ */ jsx13(
      Flex2,
      {
        column: true,
        hAlign: "start",
        ...getA11yProps("root", {
          // All these props are passed down to the root element.
          // The root element in this case is `ElementType`
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: renderResults
      }
    );
    setEnd();
    return result;
  }
);
SearchResult.defaultProps = {
  accessibility: menuItemBehavior
};
SearchResult.displayName = displayName8;
SearchResult.create = createShorthandFactory13({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: SearchResult
});
SearchResult.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "items",
  "category",
  "action",
  "loading"
];

// packages/aria/src/components/SearchMenu/SearchMenu.tsx
import { jsx as jsx14, jsxs as jsxs5 } from "react/jsx-runtime";
var defaultRootElementType2 = "div";
var searchMenuClassName = "ui-searchMenu";
var searchMenuClassNames = {
  firstResult: `${searchMenuClassName}__firstResult`,
  additional: `${searchMenuClassName}__additional`
};
var SearchMenu = forwardRef12(
  (props, ref) => {
    const context = useFluentContext14();
    const { setStart, setEnd } = useTelemetry14(
      SearchMenu.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      matchValue,
      searchResults,
      searchResultsAdditional
    } = props;
    const getA11yProps = useAccessibility11(
      accessibility,
      {
        debugName: SearchMenu.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles14(
      SearchMenu.displayName,
      {
        className: searchMenuClassName,
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps12(
      SearchMenu.handledProps,
      props
    );
    const ElementType = getElementType10(props);
    const result = /* @__PURE__ */ jsx14(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: /* @__PURE__ */ jsxs5(Flex3, { column: true, className: classes["container"], children: [
          SearchResult.create(searchResults, {
            generateKey: true,
            defaultProps: () => {
              return {
                value: matchValue,
                ...searchResultsAdditional && {
                  className: searchMenuClassNames.firstResult
                }
              };
            }
          }),
          SearchResult.create(searchResultsAdditional, {
            generateKey: true,
            defaultProps: () => {
              return {
                value: matchValue,
                className: searchMenuClassNames.additional
              };
            }
          })
        ] })
      }
    );
    setEnd();
    return result;
  }
);
SearchMenu.displayName = "SearchMenu";
SearchMenu.create = createShorthandFactory14({
  Component: SearchMenu
});
SearchMenu.defaultProps = {
  as: defaultRootElementType2,
  accessibility: popupBehavior
};
SearchMenu.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "as",
  // Extra props that are handled
  "disabled",
  "searchResults",
  "searchResultsAdditional",
  "matchValue"
];

// packages/aria/src/components/TopNavigation/NavigationItem.tsx
import {
  menuItemBehavior as menuItemBehavior2,
  createShorthandFactory as createShorthandFactory15
} from "@fluentui/react-northstar";
import {
  useAccessibility as useAccessibility12,
  useFluentContext as useFluentContext15,
  useStyles as useStyles15,
  useTelemetry as useTelemetry15,
  useUnhandledProps as useUnhandledProps13,
  getElementType as getElementType11
} from "@fluentui/react-bindings";
import { forwardRef as forwardRef13 } from "react";

// packages/aria/src/utils/classNames.ts
import cx5 from "classnames";
var mergeClassName = (shorthandValue, className) => {
  if (!shorthandValue || typeof shorthandValue !== "object" || !shorthandValue["className"])
    return className;
  return cx5(shorthandValue["className"], className);
};

// packages/aria/src/components/TopNavigation/NavigationItem.tsx
import { jsxs as jsxs6 } from "react/jsx-runtime";
var displayName9 = "NavigationItem";
var navigationItemClassName = "ui-navigationItem";
var navigationItemSlotClassNames = {
  label: "label",
  labelPrefix: "labelPrefix"
};
var NavigationItem = forwardRef13(
  (props, ref) => {
    const context = useFluentContext15();
    const { setStart, setEnd } = useTelemetry15(
      displayName9,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      label,
      labelPrefix
    } = props;
    const getA11yProps = useAccessibility12(
      accessibility,
      {
        debugName: displayName9,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles15(displayName9, {
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    });
    const unhandledProps = useUnhandledProps13(
      NavigationItem.handledProps,
      props
    );
    const ElementType = getElementType11(props);
    const render = Label.create(label, {
      defaultProps: () => ({
        variant: "03-alt",
        uc: true
      }),
      overrideProps: () => ({
        ref,
        className: mergeClassName(
          label,
          navigationItemSlotClassNames.label
        )
      })
    }) ?? null;
    const prefixRender = Label.create(labelPrefix, {
      defaultProps: () => ({
        variant: "03-alt"
      }),
      overrideProps: () => ({
        className: mergeClassName(
          labelPrefix,
          navigationItemSlotClassNames.labelPrefix
        )
      })
    }) ?? null;
    const result = /* @__PURE__ */ jsxs6(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: [
          prefixRender,
          render
        ]
      }
    );
    setEnd();
    return result;
  }
);
NavigationItem.defaultProps = {
  accessibility: menuItemBehavior2
};
NavigationItem.displayName = displayName9;
NavigationItem.create = createShorthandFactory15({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: NavigationItem,
  mappedProp: "children"
});
NavigationItem.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "label",
  "active",
  "labelPrefix"
];

// packages/aria/src/components/TopNavigation/TopNavigation.tsx
import {
  CustomShoppingCart,
  HeartLine as HeartLine2,
  CloseLine as CloseLine3
} from "@archipro-design/icons";

// packages/aria/src/components/MegaMenu/MegaMenu.tsx
import { useEffect as useEffect6 } from "react";
import { forwardRef as forwardRef40, useRef as useRef6, useState as useState5 } from "react";
import {
  useFluentContext as useFluentContext42,
  useAccessibility as useAccessibility37,
  useTelemetry as useTelemetry42,
  useStyles as useStyles42,
  useUnhandledProps as useUnhandledProps40,
  menuBehavior as menuBehavior4,
  createShorthandFactory as createShorthandFactory40
} from "@fluentui/react-northstar";
import { useMergedRefs } from "@fluentui/react-bindings";

// packages/aria/src/components/MegaMenu/NavigationListGroup/NavigationListGroup.tsx
import { forwardRef as forwardRef16 } from "react";
import {
  useFluentContext as useFluentContext18,
  useAccessibility as useAccessibility15,
  useTelemetry as useTelemetry18,
  useStyles as useStyles18,
  useUnhandledProps as useUnhandledProps16,
  menuBehavior as menuBehavior2,
  createShorthandFactory as createShorthandFactory18
} from "@fluentui/react-northstar";

// packages/aria/src/components/MegaMenu/NavigationList/NavigationList.tsx
import { forwardRef as forwardRef14 } from "react";
import {
  useFluentContext as useFluentContext16,
  useAccessibility as useAccessibility13,
  useTelemetry as useTelemetry16,
  useStyles as useStyles16,
  useUnhandledProps as useUnhandledProps14,
  menuBehavior,
  createShorthandFactory as createShorthandFactory16
} from "@fluentui/react-northstar";
import cx6 from "classnames";
import { jsx as jsx15, jsxs as jsxs7 } from "react/jsx-runtime";
var defaultRootElementType3 = "div";
var navigationListClassName = "ui-navigationList";
var navigationListSlotClassNames = {
  selected: `${navigationListClassName}__selected`
};
var NavigationList = forwardRef14(
  (props, ref) => {
    const context = useFluentContext16();
    const { setStart, setEnd } = useTelemetry16(
      NavigationList.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      title,
      items,
      moreButton,
      defaultItemSize = 18,
      renderButton
    } = props;
    const getA11yProps = useAccessibility13(
      accessibility,
      {
        debugName: NavigationList.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles16(
      NavigationList.displayName,
      {
        className: navigationListClassName,
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps14(
      NavigationList.handledProps,
      props
    );
    const result = /* @__PURE__ */ jsxs7(
      Flex,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        column: true,
        hAlign: "start",
        children: [
          Caption.create(title, {
            defaultProps: () => ({
              className: classes["title"],
              variant: "01",
              weight: "medium"
            })
          }),
          items && items.length !== 0 && /* @__PURE__ */ jsx15(Flex, { className: classes["options"], column: true, hAlign: "start", children: items.map(
            (item, index) => Button.create(item, {
              defaultProps: () => ({
                key: index,
                size: defaultItemSize,
                variant: "text",
                variables: { buttonPadding: 0 }
              }),
              overrideProps: (props2) => {
                if (props2.selected) {
                  props2.className = cx6(
                    props2.className,
                    navigationListSlotClassNames.selected
                  );
                }
                return props2;
              },
              ...renderButton && {
                render: (Component, props2) => {
                  if (!props2.key) props2.key = index;
                  return renderButton(Component, props2);
                }
              },
              generateKey: true
            })
          ) }),
          Button.create(moreButton, {
            defaultProps: () => ({
              className: classes["more"],
              variant: "text",
              size: 16,
              variables: { buttonPadding: 0 }
            })
          })
        ]
      }
    );
    setEnd();
    return result;
  }
);
NavigationList.displayName = "NavigationList";
NavigationList.create = createShorthandFactory16({
  Component: NavigationList,
  mappedProp: "title"
});
NavigationList.defaultProps = {
  as: defaultRootElementType3,
  accessibility: menuBehavior
};
NavigationList.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "as",
  // Extra props that are handled
  "title",
  "items",
  "moreButton",
  "defaultItemSize",
  "renderButton"
];

// packages/aria/src/components/MegaMenu/NavigationList/NavigationListSkeleton.tsx
import { forwardRef as forwardRef15 } from "react";
import {
  useFluentContext as useFluentContext17,
  useAccessibility as useAccessibility14,
  useTelemetry as useTelemetry17,
  useStyles as useStyles17,
  useUnhandledProps as useUnhandledProps15,
  basicListBehavior,
  createShorthandFactory as createShorthandFactory17
} from "@fluentui/react-northstar";

// packages/aria/src/components/Skeleton/SkeletonLine.tsx
import { SkeletonLine, skeletonLineClassName } from "@fluentui/react-northstar";

// packages/aria/src/components/MegaMenu/NavigationList/NavigationListSkeleton.tsx
import { jsx as jsx16, jsxs as jsxs8 } from "react/jsx-runtime";
var navigationListSkeletonClassName = "ui-navigationListSkeleton";
var navigationListSkeletonClassNames = {
  title: `${navigationListSkeletonClassName}__title`,
  item: `${navigationListSkeletonClassName}__item`,
  button: `${navigationListSkeletonClassName}__button`
};
var NavigationListSkeleton = forwardRef15((props, ref) => {
  const context = useFluentContext17();
  const { setStart, setEnd } = useTelemetry17(
    NavigationListSkeleton.displayName,
    context.telemetry
  );
  setStart();
  const {
    className,
    styles,
    variables,
    design,
    skeletonItems = 7,
    showSkeletonMoreButton,
    size = "small"
  } = props;
  const getA11yProps = useAccessibility14(basicListBehavior, {
    debugName: NavigationListSkeleton.displayName,
    rtl: context.rtl
  });
  const { classes } = useStyles17(
    NavigationListSkeleton.displayName,
    {
      className: navigationListSkeletonClassName,
      mapPropsToStyles: () => ({ size }),
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design,
        size
      }),
      rtl: context.rtl,
      unstable_props: props
    }
  );
  const unhandledProps = useUnhandledProps15(
    NavigationListSkeleton.handledProps,
    props
  );
  const navListVariables = context.theme.componentVariables[NavigationListSkeleton.displayName](context.theme.siteVariables);
  const result = /* @__PURE__ */ jsxs8(
    Flex,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      column: true,
      children: [
        /* @__PURE__ */ jsx16(
          SkeletonLine,
          {
            className: navigationListSkeletonClassNames.title,
            width: navListVariables[`${size}SkeletonTitleWidth`],
            height: navListVariables[`${size}SkeletonTitleHeight`]
          }
        ),
        /* @__PURE__ */ jsx16(Flex, { className: classes["skeletonItemContainer"], column: true, children: Array.from({ length: skeletonItems }).map((_14, index) => {
          const itemName = `${size}Skeleton${index % 2 === 1 ? "Short" : "Long"}Item`;
          return /* @__PURE__ */ jsx16(
            SkeletonLine,
            {
              className: navigationListSkeletonClassNames.item,
              width: navListVariables[`${itemName}Width`],
              height: navListVariables[`${itemName}Height`]
            },
            index
          );
        }) }),
        showSkeletonMoreButton && /* @__PURE__ */ jsx16(
          SkeletonLine,
          {
            className: navigationListSkeletonClassNames.button,
            width: navListVariables[`${size}SkeletonButtonWidth`],
            height: navListVariables[`${size}SkeletonButtonHeight`]
          }
        )
      ]
    }
  );
  setEnd();
  return result;
});
NavigationListSkeleton.displayName = "NavigationListSkeleton";
NavigationListSkeleton.create = createShorthandFactory17({
  Component: NavigationListSkeleton
});
NavigationListSkeleton.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "skeletonItems",
  "showSkeletonMoreButton",
  "size"
];

// packages/aria/src/components/MegaMenu/NavigationListGroup/NavigationListGroup.tsx
import { jsx as jsx17 } from "react/jsx-runtime";
var navigationListGroupClassName = "ui-navigationListGroup";
var NavigationListGroup = forwardRef16((props, ref) => {
  const context = useFluentContext18();
  const { setStart, setEnd } = useTelemetry18(
    NavigationListGroup.displayName,
    context.telemetry
  );
  setStart();
  const { className, styles, variables, accessibility, design, lists } = props;
  const getA11yProps = useAccessibility15(
    accessibility,
    {
      debugName: NavigationListGroup.displayName,
      rtl: context.rtl
    }
  );
  const { classes } = useStyles18(
    NavigationListGroup.displayName,
    {
      className: navigationListGroupClassName,
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    }
  );
  const unhandledProps = useUnhandledProps16(
    NavigationListGroup.handledProps,
    props
  );
  const result = /* @__PURE__ */ jsx17(
    Flex,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      column: true,
      children: lists && lists.map((item, index) => {
        switch (item.kind) {
          case "list":
            return NavigationList.create(
              item.list,
              {
                defaultProps: () => ({
                  key: index,
                  variables: { listWidth: pxToRem(320) },
                  title: {
                    variant: "03"
                  }
                })
              }
            );
          case "loadingList":
            return NavigationListSkeleton.create(
              item.list,
              {
                defaultProps: () => ({
                  key: index,
                  skeletonItems: 3,
                  size: "small",
                  variables: { listWidth: pxToRem(320) }
                })
              }
            );
          default:
            return null;
        }
      })
    }
  );
  setEnd();
  return result;
});
NavigationListGroup.displayName = "NavigationListGroup";
NavigationListGroup.create = createShorthandFactory18({
  Component: NavigationListGroup
});
NavigationListGroup.defaultProps = {
  accessibility: menuBehavior2
};
NavigationListGroup.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  // Extra props that are handled
  "lists"
];

// packages/aria/src/components/MegaMenu/BreadcrumbNavigation/BreadcrumbNavigation.tsx
import { forwardRef as forwardRef17 } from "react";
import {
  useFluentContext as useFluentContext19,
  useAccessibility as useAccessibility16,
  useTelemetry as useTelemetry19,
  useStyles as useStyles19,
  useUnhandledProps as useUnhandledProps17,
  menuBehavior as menuBehavior3,
  createShorthandFactory as createShorthandFactory19,
  Divider
} from "@fluentui/react-northstar";
import { ArrowRightSLine } from "@archipro-design/icons";
import { jsx as jsx18, jsxs as jsxs9 } from "react/jsx-runtime";
var breadcrumbNavigationClassName = "ui-breadcrumbNavigation";
var breadcrumbNavigationSlotClassNames = {
  selectedSecondary: `${breadcrumbNavigationClassName}__selectedSecondary`
};
var BreadcrumbNavigation = forwardRef17((props, ref) => {
  const context = useFluentContext19();
  const { setStart, setEnd } = useTelemetry19(
    BreadcrumbNavigation.displayName,
    context.telemetry
  );
  setStart();
  const { className, styles, variables, accessibility, design, lists } = props;
  const getA11yProps = useAccessibility16(
    accessibility,
    {
      debugName: BreadcrumbNavigation.displayName,
      rtl: context.rtl
    }
  );
  const { classes } = useStyles19(
    BreadcrumbNavigation.displayName,
    {
      className: breadcrumbNavigationClassName,
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    }
  );
  const unhandledProps = useUnhandledProps17(
    BreadcrumbNavigation.handledProps,
    props
  );
  const result = /* @__PURE__ */ jsx18(
    Flex,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      column: false,
      children: lists && lists.map((item, index) => {
        let listInstance;
        const nextList = lists[index + 1];
        const nextListEmpty = nextList && nextList.kind === "list" && typeof nextList.list === "object" && (!nextList.list?.["items"] || !nextList.list?.["items"]["length"]);
        const hideDivider = index < lists.length - 1 ? nextListEmpty : false;
        switch (item.kind) {
          case "list":
            listInstance = NavigationList.create(
              item.list,
              {
                defaultProps: () => ({
                  key: index,
                  renderButton: (Component, { key, ...props2 }) => {
                    return /* @__PURE__ */ jsxs9(
                      Flex,
                      {
                        className: props2.className,
                        space: "between",
                        vAlign: "center",
                        design: { width: "100%" },
                        children: [
                          /* @__PURE__ */ jsx18(Component, { ...props2 }),
                          props2.size && props2.selected && !nextListEmpty && /* @__PURE__ */ jsx18(
                            ArrowRightSLine,
                            {
                              style: {
                                width: pxToRem(
                                  props2.size
                                ),
                                height: pxToRem(
                                  props2.size
                                )
                              }
                            }
                          )
                        ]
                      },
                      key
                    );
                  }
                })
              }
            );
            break;
          case "loadingList":
            listInstance = NavigationListSkeleton.create(
              item.list,
              {
                defaultProps: () => ({ key: index })
              }
            );
            break;
          default:
            listInstance = null;
        }
        if (!listInstance) return null;
        return [
          listInstance,
          /* @__PURE__ */ jsx18(
            Divider,
            {
              vertical: true,
              style: {
                visibility: hideDivider ? "hidden" : "visible"
              }
            },
            index + "Divider"
          )
        ];
      })
    }
  );
  setEnd();
  return result;
});
BreadcrumbNavigation.displayName = "BreadcrumbNavigation";
BreadcrumbNavigation.create = createShorthandFactory19({
  Component: BreadcrumbNavigation
});
BreadcrumbNavigation.defaultProps = {
  accessibility: menuBehavior3
};
BreadcrumbNavigation.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  // Extra props that are handled
  "lists"
];

// packages/aria/src/components/MegaMenu/FeaturedTile/FeaturedTile.tsx
import { forwardRef as forwardRef39 } from "react";
import {
  useFluentContext as useFluentContext41,
  useTelemetry as useTelemetry41,
  useStyles as useStyles41,
  useUnhandledProps as useUnhandledProps39,
  createShorthandFactory as createShorthandFactory39
} from "@fluentui/react-northstar";

// packages/aria/src/components/Tile/ProductTile/ProductTile.tsx
import { forwardRef as forwardRef31 } from "react";
import {
  useFluentContext as useFluentContext33,
  getElementType as getElementType21,
  useAccessibility as useAccessibility29,
  useTelemetry as useTelemetry33,
  useStyles as useStyles33,
  useUnhandledProps as useUnhandledProps31,
  cardBehavior as cardBehavior3,
  createShorthandFactory as createShorthandFactory31
} from "@fluentui/react-northstar";

// packages/aria/src/components/Image/Image.tsx
import {
  useUnhandledProps as useUnhandledProps20,
  useFluentContext as useFluentContext22,
  useStyles as useStyles22,
  useTelemetry as useTelemetry22
} from "@fluentui/react-bindings";
import * as React6 from "react";
import { createShorthandFactory as createShorthandFactory21 } from "@fluentui/react-northstar";

// packages/aria/src/components/Image/ImageElement.tsx
import * as React4 from "react";
import { imageBehavior as imageBehavior2 } from "@fluentui/accessibility";
import {
  useUnhandledProps as useUnhandledProps18,
  useAccessibility as useAccessibility17,
  useFluentContext as useFluentContext20,
  useStyles as useStyles20,
  useTelemetry as useTelemetry20
} from "@fluentui/react-bindings";

// packages/aria/src/components/Image/utils/image-utils.ts
import { version } from "react";
var normalizeSrc = (src) => {
  return src[0] === "/" ? src.slice(1) : src;
};
var generateImgAttrs = ({
  config,
  src,
  unoptimized,
  layout,
  width,
  quality,
  sizes,
  loader
}) => {
  if (unoptimized || !src) {
    return { src, srcSet: void 0, sizes: void 0 };
  }
  const { widths, kind } = getWidths(config, layout, width, sizes);
  const last = widths.length - 1;
  return {
    sizes: !sizes && kind === "w" ? "100vw" : sizes,
    srcSet: widths.map(
      (w, i) => `${loader({ src, quality, width: w })} ${kind === "w" ? w : i + 1}${kind}`
    ).join(", "),
    // It's intended to keep `src` the last attribute because React updates
    // attributes in order. If we keep `src` the first one, Safari will
    // immediately start to fetch `src`, before `sizes` and `srcSet` are even
    // updated by React. That causes multiple unnecessary requests if `srcSet`
    // and `sizes` are defined.
    // This bug cannot be reproduced in Chrome or Firefox.
    src: loader({ src, quality, width: widths[last] })
  };
};
var getInt = (x) => {
  if (typeof x === "number") {
    return x;
  }
  if (typeof x === "string") {
    return parseInt(x, 10);
  }
  return void 0;
};
var getWidths = ({ deviceSizes, allSizes }, layout, width, sizes) => {
  if (sizes && (layout === "fill" || layout === "responsive" || layout === "raw")) {
    const viewportWidthRe = /(^|\s)(1?\d?\d)vw/g;
    const percentSizes = [];
    for (let match; match = viewportWidthRe.exec(sizes); match) {
      percentSizes.push(parseInt(match[2]));
    }
    if (percentSizes.length) {
      const smallestRatio = Math.min(...percentSizes) * 0.01;
      if (deviceSizes) {
        return {
          widths: allSizes.filter(
            (s) => s >= deviceSizes[0] * smallestRatio
          ),
          kind: "w"
        };
      }
    }
    return { widths: allSizes, kind: "w" };
  }
  if (typeof width !== "number" || layout === "fill" || layout === "responsive") {
    return { widths: deviceSizes || [], kind: "w" };
  }
  const widths = [
    width,
    width * 2
    /*, width * 3*/
  ].map(
    (w) => allSizes.find((p) => p >= w) || allSizes[allSizes.length - 1]
  );
  return { widths, kind: "x" };
};
var handleLoading = (img, src, layout, placeholder, onLoadRef, onLoadingCompleteRef, onDecodeError, isDevelopment = false) => {
  if (!img || img["data-loaded-src"] === src) {
    return;
  }
  img["data-loaded-src"] = src;
  const p = "decode" in img ? img.decode().catch((e) => {
    const handler = onDecodeError ?? console.error;
    handler(`Failed to decode image: ${src}`, e);
  }) : Promise.resolve();
  void p.then(() => {
    if (!img.parentNode || !img.isConnected) {
      return;
    }
    if (onLoadRef?.current) {
      const event = new Event("load");
      Object.defineProperty(event, "target", {
        writable: false,
        value: img
      });
      let prevented = false;
      let stopped = false;
      onLoadRef.current({
        ...event,
        nativeEvent: event,
        currentTarget: img,
        target: img,
        isDefaultPrevented: () => prevented,
        isPropagationStopped: () => stopped,
        persist: () => void 0,
        preventDefault: () => {
          prevented = true;
          event.preventDefault();
        },
        stopPropagation: () => {
          stopped = true;
          event.stopPropagation();
        }
      });
    }
    if (onLoadingCompleteRef?.current) {
      onLoadingCompleteRef.current(img);
    }
    if (isDevelopment) {
      if (layout === "raw") {
        const heightModified = img.height.toString() !== img.getAttribute("height");
        const widthModified = img.width.toString() !== img.getAttribute("width");
        if (heightModified && !widthModified || !heightModified && widthModified) {
          console.info(
            `Image with src "${src}" has either width or height modified, but not the other. If you use CSS to change the size of your image, also include the styles 'width: "auto"' or 'height: "auto"' to maintain the aspect ratio.`
          );
        }
      }
      if (img.parentElement?.parentElement) {
        const parent = getComputedStyle(
          img.parentElement.parentElement
        );
        if (!parent.position) {
        } else if (layout === "responsive" && parent.display === "flex") {
          console.info(
            `Image with src "${src}" may not render properly as a child of a flex container. Consider wrapping the image with a div to configure the width.`
          );
        } else if (layout === "fill" && parent.position !== "relative" && parent.position !== "fixed" && parent.position !== "absolute") {
          console.info(
            `Image with src "${src}" may not render properly with a parent using position:"${parent.position}". Consider changing the parent style to position:"relative" with a width and height.`
          );
        }
      }
    }
  });
};
var getDynamicProps = (fetchPriority) => {
  const [majorStr, minorStr] = version.split(".");
  const major = parseInt(majorStr, 10);
  const minor = parseInt(minorStr, 10);
  if (major > 18 || major === 18 && minor >= 3) {
    return { fetchPriority };
  }
  return { fetchpriority: fetchPriority };
};

// packages/aria/src/components/Image/ImageElement.tsx
import { jsx as jsx19 } from "react/jsx-runtime";
var IMAGE_ELEMENT_CLASS_NAME = "ui-image-element";
var isServer = typeof window === "undefined";
var ImageElement = React4.forwardRef((props, forwardedRef) => {
  const context = useFluentContext20();
  const { setStart, setEnd } = useTelemetry20(
    ImageElement.displayName,
    context.telemetry
  );
  setStart();
  const {
    accessibility = imageBehavior2,
    alt,
    "aria-label": ariaLabel,
    className,
    design,
    styles,
    variables,
    layout,
    placeholder,
    srcString,
    isLazy,
    loading,
    fetchPriority,
    height,
    width,
    unoptimized,
    sizes,
    fadeIn,
    onLoadRef,
    onLoadingCompleteRef,
    onError,
    onDecodeError,
    src,
    isDevelopment = false
  } = props;
  const [loadFailed, setLoadFailed] = React4.useState(!src);
  const id = React4.useId();
  const [visible, setVisible] = React4.useState(() => {
    if (isServer) return src ? true : false;
    const el = document.getElementById(id);
    return el instanceof HTMLImageElement && el.complete && !!src;
  });
  const [transitionIn, setTransitionIn] = React4.useState(true);
  const handleImageLoading = React4.useCallback(
    (img) => {
      setVisible(true);
      handleLoading(
        img,
        src,
        layout,
        placeholder,
        onLoadRef,
        onLoadingCompleteRef,
        (errorMessage, error) => {
          onDecodeError?.(errorMessage, error);
          setLoadFailed(true);
        },
        unoptimized
      );
    },
    // Someone else broke this, i'm ignoring for CI. For gods sake stop ignoring your lint warnings!
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      src,
      layout,
      onLoadRef,
      onLoadingCompleteRef,
      placeholder,
      srcString,
      onDecodeError,
      unoptimized
    ]
  );
  const getA11Props = useAccessibility17(accessibility, {
    debugName: ImageElement.displayName,
    mapPropsToBehavior: () => ({
      alt,
      "aria-label": ariaLabel
    }),
    rtl: context.rtl
  });
  const { classes } = useStyles20(
    ImageElement.displayName,
    {
      className: IMAGE_ELEMENT_CLASS_NAME,
      mapPropsToStyles: () => ({
        layout,
        loading,
        fadeIn,
        transitionIn
      }),
      mapPropsToInlineStyles: () => ({
        className,
        design,
        styles,
        variables
      }),
      rtl: context.rtl
    }
  );
  const unhandledProps = useUnhandledProps18(ImageElement.handledProps, props);
  const loadingValue = isLazy ? "lazy" : loading;
  const imageRef = React4.useCallback(
    (img) => {
      if (forwardedRef) {
        if (typeof forwardedRef === "function") forwardedRef(img);
        else if (typeof forwardedRef === "object") {
          forwardedRef.current = img;
        }
      }
      if (!img) {
        return;
      }
      if (onError) {
        img.src = img.src;
      }
      if (isDevelopment && process.env.NODE_ENV !== "test") {
        if (!srcString) {
          console.error(
            `Image is missing required "src" property:`,
            img
          );
        }
        if (img.getAttribute("alt") === null) {
          console.error(
            `Image is missing required "alt" property. Please add Alternative Text to describe the image for screen readers and search engines.`
          );
        }
      }
      if (img.complete) {
        setTransitionIn(false);
        handleImageLoading(img);
      }
    },
    [forwardedRef, onError, isDevelopment, srcString, handleImageLoading]
  );
  const image = loadFailed ? /* @__PURE__ */ jsx19("span", { className: classes["errorImage"], children: alt }) : /* @__PURE__ */ jsx19(
    "img",
    {
      ...getA11Props("root", {
        className: classes["root"],
        "data-layout": layout,
        ...unhandledProps
      }),
      ...getDynamicProps(fetchPriority),
      alt,
      loading: loadingValue,
      height,
      width,
      sizes,
      ref: imageRef,
      onLoad: (event) => {
        const img = event.currentTarget;
        if (img) {
          handleImageLoading(img);
        }
      },
      onError: (event) => {
        setLoadFailed(true);
        if (onError) {
          onError(event);
        }
      },
      id,
      "data-evt-onload": typeof window === "undefined" ? 'this.style.removeProperty("opacity")' : void 0,
      suppressHydrationWarning: true,
      style: {
        ...loading !== "eager" && !visible && {
          opacity: 0
        }
      }
    }
  );
  setEnd();
  return image;
});
ImageElement.displayName = "ImageElement";
ImageElement.handledProps = [
  "className",
  "design",
  "styles",
  "variables",
  "styles",
  "placeholder",
  "layout",
  "onLoad",
  "onError",
  "srcString",
  "isLazy",
  "loading",
  "height",
  "width",
  "unoptimized",
  "sizes",
  "fadeIn",
  "fetchPriority",
  "onLoadRef",
  "onLoadingCompleteRef",
  "isDevelopment",
  "onDecodeError"
];

// packages/aria/src/components/Image/image-constants.ts
var VALID_LAYOUT_VALUES = [
  "fill",
  "fixed",
  "intrinsic",
  "responsive",
  "raw",
  void 0
];
var VALID_LOADING_VALUES = ["lazy", "eager", void 0];
var DEVICE_SIZES = [640, 750, 828, 1080, 1200, 1920, 2048, 3840];
var IMAGE_SIZES = [16, 32, 48, 64, 96, 128, 256, 384];
var EMPTY_DATA_URL = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

// packages/aria/src/components/Image/Context/image-config-context.tsx
import React5, { createContext } from "react";

// packages/aria/src/components/Image/loaders.ts
var archiproImageLoader = (domain, initialQuality = 80, notEscapeParams = false) => {
  const urlPath = domain ? `https://${domain}/images/cdn-images` : `/images/cdn-images`;
  return ({ src, width, quality }) => {
    const params = [
      `width=${width}`,
      `quality=${quality || initialQuality}`
    ];
    const paramsString = notEscapeParams ? params.join(",") : encodeURIComponent(params.join(","));
    return `${urlPath}/${paramsString}/${normalizeSrc(src)}`;
  };
};

// packages/aria/src/components/Image/Context/image-config-context.tsx
import { jsx as jsx20 } from "react/jsx-runtime";
var imageDefaultConfig = {
  deviceSizes: DEVICE_SIZES,
  imageSizes: IMAGE_SIZES,
  loader: archiproImageLoader(),
  isProduction: false
};
var ImageConfigContext = createContext(imageDefaultConfig);
var ImageConfigProvider = ({
  children,
  value
}) => {
  return /* @__PURE__ */ jsx20(ImageConfigContext.Provider, { value, children });
};
var useImageConfig = () => {
  const value = React5.useContext(ImageConfigContext);
  if (value === void 0) {
    throw new Error(
      "useImageConfig must be used within a ImageConfigContext"
    );
  }
  return value;
};

// packages/aria/src/components/Image/ImageOverlay.tsx
import { createShorthandFactory as createShorthandFactory20 } from "@fluentui/react-northstar";
import { imageBehavior as imageBehavior3 } from "@fluentui/react-northstar";
import { getElementType as getElementType12, useUnhandledProps as useUnhandledProps19 } from "@fluentui/react-bindings";
import {
  useAccessibility as useAccessibility18,
  useFluentContext as useFluentContext21,
  useStyles as useStyles21,
  useTelemetry as useTelemetry21
} from "@fluentui/react-bindings";
import { SearchLine as SearchLine2 } from "@archipro-design/icons";
import { forwardRef as forwardRef19 } from "react";
import { jsx as jsx21 } from "react/jsx-runtime";
var imageOverlayClassName = "ui-image-overlay";
var displayName10 = "ImageOverlay";
var ImageOverlay = forwardRef19(
  (props, ref) => {
    const context = useFluentContext21();
    const { setStart, setEnd } = useTelemetry21(
      displayName10,
      context.telemetry
    );
    setStart();
    const getA11yProps = useAccessibility18(imageBehavior3, {
      debugName: displayName10
    });
    const {
      className,
      styles,
      variables,
      design,
      icon = /* @__PURE__ */ jsx21(SearchLine2, {}),
      onClick,
      alwaysVisible
    } = props;
    const { classes } = useStyles21(displayName10, {
      className: imageOverlayClassName,
      mapPropsToStyles: () => ({
        alwaysVisible
      }),
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      unstable_props: props,
      rtl: context.rtl
    });
    const ElementType = getElementType12(props);
    const unhandledProps = useUnhandledProps19(
      ImageOverlay.handledProps,
      props
    );
    const Element = getA11yProps.unstable_wrapWithFocusZone(
      /* @__PURE__ */ jsx21(
        ElementType,
        {
          ...getA11yProps("root", {
            className: classes["root"],
            ref,
            onClick: (e) => onClick?.(e),
            ...unhandledProps
          }),
          children: icon
        }
      )
    );
    setEnd();
    return Element;
  }
);
ImageOverlay.handledProps = [
  "className",
  "variables",
  "styles",
  "design",
  "icon",
  "alwaysVisible"
];
ImageOverlay.create = createShorthandFactory20({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ImageOverlay
});
ImageOverlay.defaultProps = {
  as: "span"
};
ImageOverlay.displayName = displayName10;

// packages/aria/src/components/Image/Image.tsx
import { jsx as jsx22, jsxs as jsxs10 } from "react/jsx-runtime";
var imageClassName = "ui-image";
var allImgs = /* @__PURE__ */ new Map();
var perfObserver;
var Image = React6.forwardRef(
  (props, forwardedRef) => {
    const unhandledProps = useUnhandledProps20(Image.handledProps, props);
    const {
      className,
      design,
      styles,
      mixBlendMode,
      variables,
      alt,
      src: initialSrc,
      sizes,
      unoptimized = false,
      priority = false,
      loading,
      quality,
      width,
      height,
      objectFit,
      objectPosition,
      onLoad,
      onLoadingComplete,
      placeholder = "empty",
      placeholderColor,
      "aria-label": ariaLabel,
      overlay = null,
      fadeIn = true,
      fetchPriority,
      onError,
      onDecodeError,
      ...all
    } = props;
    const src = initialSrc.trim();
    const context = useFluentContext22();
    const { setStart, setEnd } = useTelemetry22(
      Image.displayName,
      context.telemetry
    );
    const imageConfigContext = useImageConfig();
    setStart();
    const config = React6.useMemo(() => {
      if (!imageConfigContext) {
        throw new Error("Image component requires imageConfig");
      }
      const c = imageConfigContext;
      const {
        deviceSizes: configDeviceSizes = [],
        imageSizes: configImageSizes = []
      } = c;
      const allSizes = [...configDeviceSizes, ...configImageSizes].sort(
        (a, b) => a - b
      );
      const deviceSizes = configDeviceSizes.sort((a, b) => a - b);
      return { ...c, allSizes: [...new Set(allSizes)], deviceSizes };
    }, [imageConfigContext]);
    const isDevelopment = !config.isProduction;
    const rest = all;
    let layout = sizes ? "responsive" : "intrinsic";
    if ("layout" in rest) {
      if (rest.layout) layout = rest.layout;
      delete rest.layout;
    }
    const loader = config.loader;
    const widthInt = getInt(width);
    const heightInt = getInt(height);
    let unoptimizedValue = unoptimized;
    let fetchPriorityValue = fetchPriority;
    let isLazy = !priority && (loading === "lazy" || typeof loading === "undefined");
    if (src.startsWith("data:") || src.startsWith("blob:")) {
      unoptimizedValue = true;
      isLazy = false;
    }
    if (src.endsWith(".svg")) {
      unoptimizedValue = true;
    }
    if (priority) {
      fetchPriorityValue = "high";
    }
    const qualityInt = getInt(quality);
    if (isDevelopment) {
      if (!src) {
        unoptimizedValue = true;
      }
      if (!VALID_LAYOUT_VALUES.includes(layout)) {
        throw new Error(
          `Image with src "${src}" has invalid "layout" property. Provided "${layout}" should be one of ${VALID_LAYOUT_VALUES.map(
            String
          ).join(",")}.`
        );
      }
      if (typeof widthInt !== "undefined" && isNaN(widthInt) || typeof heightInt !== "undefined" && isNaN(heightInt)) {
        throw new Error(
          `Image with src "${src}" has invalid "width" or "height" property. These should be numeric values.`
        );
      }
      if (!VALID_LOADING_VALUES.includes(loading)) {
        throw new Error(
          `Image with src "${src}" has invalid "loading" property. Provided "${loading}" should be one of ${VALID_LOADING_VALUES.map(
            String
          ).join(",")}.`
        );
      }
      if (layout === "raw" && (objectFit || objectPosition)) {
        throw new Error(
          `Image with src "${src}" has "layout='raw'" and 'objectFit' or 'objectPosition'. For raw images, these and other styles should be specified using the 'style' attribute.`
        );
      }
      if (sizes && layout !== "fill" && layout !== "responsive" && layout !== "raw") {
        console.info(
          `Image with src "${src}" has "sizes" property but it will be ignored. Only use "sizes" with "layout='fill'", "layout='responsive'", or "layout='raw'`
        );
      }
      if (placeholder === "blur") {
        if (layout !== "fill" && (widthInt || 0) * (heightInt || 0) < 1600) {
          console.info(
            `Image with src "${src}" is smaller than 40x40. Consider removing the "placeholder='blur'" property to improve performance.`
          );
        }
      }
      if ("ref" in rest) {
        console.info(
          `Image with src "${src}" is using unsupported "ref" property. Consider using the "onLoadingComplete" property instead.`
        );
      }
      if (typeof window !== "undefined" && !perfObserver && window.PerformanceObserver) {
        perfObserver = new PerformanceObserver((entryList) => {
          for (const entry of entryList.getEntries()) {
            const imgSrc = entry?.element?.src || "";
            const lcpImage = allImgs.get(imgSrc);
            if (lcpImage && lcpImage.placeholder !== "blur" && !lcpImage.src.startsWith("data:") && !lcpImage.src.startsWith("blob:")) {
              console.info(
                `Image with src "${lcpImage.src}" was detected as the Largest Contentful Paint (LCP). Consider prefecthing the url.
Read more: https://remix.run/docs/en/v1/api/conventions#links`
              );
            }
          }
        });
        try {
          perfObserver.observe({
            type: "largest-contentful-paint",
            buffered: true
          });
        } catch (err) {
          console.error(err);
        }
      }
    }
    const imgAttributes = generateImgAttrs({
      config,
      src,
      unoptimized: unoptimizedValue,
      layout,
      width: widthInt,
      quality: qualityInt,
      sizes,
      loader
    });
    const srcString = src;
    const onLoadRef = React6.useRef(onLoad);
    React6.useEffect(() => {
      onLoadRef.current = onLoad;
    }, [onLoad]);
    const onLoadingCompleteRef = React6.useRef(onLoadingComplete);
    React6.useEffect(() => {
      onLoadingCompleteRef.current = onLoadingComplete;
    }, [onLoadingComplete]);
    const { classes } = useStyles22(Image.displayName, {
      className: imageClassName,
      mapPropsToStyles: () => ({
        layout,
        width: widthInt,
        height: heightInt,
        placeholderColor
      }),
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables: mixBlendMode ? { ...variables, mixBlendMode } : variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    });
    const imgStyle = Object.assign(
      {},
      layout === "raw" ? { ...styles } : { objectFit, objectPosition }
    );
    const imgElementArgs = {
      isLazy,
      ...imgAttributes,
      onError,
      alt,
      height: heightInt,
      width: widthInt,
      layout,
      styles: { ...imgStyle },
      loading,
      placeholder,
      srcString,
      unoptimized: unoptimizedValue,
      onLoadRef,
      onLoadingCompleteRef,
      "aria-label": ariaLabel,
      fadeIn: placeholder === "blur" ? false : fadeIn,
      fetchPriority: fetchPriorityValue,
      isDevelopment,
      onDecodeError
    };
    const result = layout === "raw" ? /* @__PURE__ */ jsx22(ImageElement, { ...imgElementArgs, ...unhandledProps }) : /* @__PURE__ */ jsxs10("span", { className: classes["root"], ...unhandledProps, children: [
      /* @__PURE__ */ jsx22(ImageElement, { ...imgElementArgs, ref: forwardedRef }),
      ImageOverlay.create(overlay)
    ] });
    setEnd();
    return result;
  }
);
Image.displayName = "Image";
Image.create = createShorthandFactory21({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
  Component: Image,
  mappedProp: "src"
});
Image.handledProps = [
  "styles",
  "design",
  "variables",
  "className",
  "src",
  "layout",
  "width",
  "height",
  "alt",
  "sizes",
  "objectFit",
  "objectPosition",
  "placeholder",
  "aria-label",
  "quality",
  "loading",
  "placeholderColor",
  "overlay",
  "fadeIn",
  "onLoadingComplete",
  "fetchPriority",
  "unoptimized",
  "mixBlendMode"
];

// packages/aria/src/components/Tile/types.ts
var adminTileHandledProps = [
  "topActions",
  "adminCaption"
];

// packages/aria/src/components/Tile/TileImage/TileImage.tsx
import { useCallback as useCallback2, useEffect as useEffect4, useRef as useRef4, useState as useState3 } from "react";
import { forwardRef as forwardRef30 } from "react";
import {
  useFluentContext as useFluentContext32,
  getElementType as getElementType20,
  useAccessibility as useAccessibility28,
  useTelemetry as useTelemetry32,
  useStyles as useStyles32,
  useUnhandledProps as useUnhandledProps30,
  cardBehavior as cardBehavior2,
  createShorthandFactory as createShorthandFactory30
} from "@fluentui/react-northstar";

// packages/aria/src/components/Tile/TileOverlay/TileOverlay.tsx
import { forwardRef as forwardRef21 } from "react";
import {
  useFluentContext as useFluentContext23,
  useAccessibility as useAccessibility19,
  useTelemetry as useTelemetry23,
  useStyles as useStyles23,
  useUnhandledProps as useUnhandledProps21,
  cardBehavior,
  createShorthandFactory as createShorthandFactory22
} from "@fluentui/react-northstar";
import { jsx as jsx23, jsxs as jsxs11 } from "react/jsx-runtime";
var defaultRootElementType4 = "div";
var tileOverlayClassName = "ui-tileOverlay";
var tileOverlaySlotClassNames = {
  topActions: `${tileOverlayClassName}__topActions`,
  bottomActions: `${tileOverlayClassName}__bottomActions`
};
var TileOverlay = forwardRef21(
  (props, ref) => {
    const context = useFluentContext23();
    const { setStart, setEnd } = useTelemetry23(
      TileOverlay.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      topActions,
      bottomActions,
      topContent
    } = props;
    const getA11yProps = useAccessibility19(
      accessibility,
      {
        debugName: TileOverlay.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles23(
      TileOverlay.displayName,
      {
        className: tileOverlayClassName,
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps21(
      TileOverlay.handledProps,
      props
    );
    const result = /* @__PURE__ */ jsxs11(
      Flex,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        space: "between",
        column: true,
        children: [
          Box.create(topContent),
          /* @__PURE__ */ jsx23(
            Flex,
            {
              hAlign: "end",
              className: tileOverlaySlotClassNames.topActions,
              children: topActions?.map((item) => Box.create(item))
            }
          ),
          /* @__PURE__ */ jsx23(
            Flex,
            {
              space: "between",
              hAlign: "center",
              vAlign: "center",
              className: tileOverlaySlotClassNames.bottomActions,
              children: bottomActions?.map((item) => Box.create(item))
            }
          )
        ]
      }
    );
    setEnd();
    return result;
  }
);
TileOverlay.displayName = "TileOverlay";
TileOverlay.create = createShorthandFactory22({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: TileOverlay
});
TileOverlay.defaultProps = {
  as: defaultRootElementType4,
  accessibility: cardBehavior
};
TileOverlay.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  // Extra props that are handled
  "topActions",
  "bottomActions"
];

// packages/aria/src/components/Tile/TileImage/TileImage.tsx
import { HeartLightFill, HeartLightLine } from "@archipro-design/icons";

// packages/aria/src/components/Carousel/Carousel.tsx
import React7, { useState as useState2, forwardRef as forwardRef29, useRef as useRef3, useEffect as useEffect3 } from "react";
import {
  useFluentContext as useFluentContext31,
  getElementType as getElementType19,
  useAccessibility as useAccessibility27,
  useTelemetry as useTelemetry31,
  useStyles as useStyles31,
  useUnhandledProps as useUnhandledProps29,
  createShorthandFactory as createShorthandFactory29,
  Ref
} from "@fluentui/react-northstar";
import cx8 from "classnames";
import { Swiper } from "swiper/react";
import {
  A11y,
  Autoplay,
  Controller,
  Thumbs,
  Keyboard,
  EffectFade,
  Pagination
} from "swiper/modules";
import _5 from "lodash";
import { useAutoControlled } from "@fluentui/react-bindings";

// packages/aria/src/components/Carousel/CarouselItem.tsx
import { forwardRef as forwardRef22 } from "react";
import {
  useFluentContext as useFluentContext24,
  getElementType as getElementType13,
  useAccessibility as useAccessibility20,
  useTelemetry as useTelemetry24,
  useStyles as useStyles24,
  useUnhandledProps as useUnhandledProps22,
  createShorthandFactory as createShorthandFactory23
} from "@fluentui/react-northstar";
import { childrenExist as childrenExist2 } from "@fluentui/react-bindings";
import { SwiperSlide } from "swiper/react";
import { jsx as jsx24 } from "react/jsx-runtime";
var carouselItemClassName = "ui-carouselItem";
var displayName11 = "CarouselItem";
var CarouselItem = forwardRef22(
  (props, ref) => {
    const context = useFluentContext24();
    const { setStart, setEnd } = useTelemetry24(
      displayName11,
      context.telemetry
    );
    setStart();
    const {
      children,
      content,
      className,
      styles,
      variables,
      accessibility,
      design,
      index
    } = props;
    const getA11yProps = useAccessibility20(accessibility, {
      debugName: displayName11,
      rtl: context.rtl
    });
    const { classes } = useStyles24(displayName11, {
      className: carouselItemClassName,
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    });
    const unhandledProps = useUnhandledProps22(
      CarouselItem.handledProps,
      props
    );
    const ElementType = getElementType13(props);
    const result = /* @__PURE__ */ jsx24(SwiperSlide, { children: /* @__PURE__ */ jsx24(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: childrenExist2(children) ? children : content
      }
    ) }, `slide-${index}`);
    setEnd();
    return result;
  }
);
CarouselItem.displayName = "SwiperSlide";
CarouselItem.create = createShorthandFactory23({
  Component: CarouselItem,
  mappedProp: "content"
});
CarouselItem.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "swiperSlideIndex"
];

// packages/aria/src/components/Carousel/CarouselNavigation.tsx
import { forwardRef as forwardRef24 } from "react";
import {
  useFluentContext as useFluentContext26,
  getElementType as getElementType15,
  useAccessibility as useAccessibility22,
  useTelemetry as useTelemetry26,
  useStyles as useStyles26,
  useUnhandledProps as useUnhandledProps24,
  createShorthandFactory as createShorthandFactory25,
  tabListBehavior,
  rtlTextContainer as rtlTextContainer2
} from "@fluentui/react-northstar";
import _4 from "lodash";
import {
  mergeVariablesOverrides,
  childrenExist as childrenExist4
} from "@fluentui/react-bindings";

// packages/aria/src/components/Carousel/CarouselNavigationItem.tsx
import { forwardRef as forwardRef23 } from "react";
import {
  useFluentContext as useFluentContext25,
  getElementType as getElementType14,
  useAccessibility as useAccessibility21,
  useTelemetry as useTelemetry25,
  useStyles as useStyles25,
  useUnhandledProps as useUnhandledProps23,
  createShorthandFactory as createShorthandFactory24,
  tabBehavior
} from "@fluentui/react-northstar";
import _3 from "lodash";
import { childrenExist as childrenExist3 } from "@fluentui/react-bindings";
import { jsx as jsx25 } from "react/jsx-runtime";
var carouselNavigationItemClassName = "ui-carouselNavigationItem";
var carouselNavigationItemSlotClassNames = {
  indicator: `${carouselNavigationItemClassName}__indicator`,
  content: `${carouselNavigationItemClassName}__content`
};
var CarouselNavigationItem = forwardRef23((props, ref) => {
  const context = useFluentContext25();
  const { setStart, setEnd } = useTelemetry25(
    CarouselNavigationItem.displayName,
    context.telemetry
  );
  setStart();
  const {
    children,
    content,
    className,
    styles,
    variables,
    accessibility,
    design,
    active,
    indicator
  } = props;
  const getA11yProps = useAccessibility21(
    accessibility,
    {
      debugName: CarouselNavigationItem.displayName,
      actionHandlers: {
        performClick: (event) => !event.defaultPrevented && handleClick(event)
      },
      rtl: context.rtl
    }
  );
  const { classes, styles: resolvedStyles } = useStyles25(
    CarouselNavigationItem.displayName,
    {
      className: carouselNavigationItemClassName,
      mapPropsToStyles: () => ({
        active,
        hasContent: !!content
      }),
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    }
  );
  const unhandledProps = useUnhandledProps23(
    CarouselNavigationItem.handledProps,
    props
  );
  const ElementType = getElementType14(props);
  const handleClick = (e) => {
    _3.invoke(props, "onClick", e, props);
  };
  const handleBlur = (e) => {
    _3.invoke(props, "onBlur", e, props);
  };
  const handleFocus = (e) => {
    _3.invoke(props, "onFocus", e, props);
  };
  const renderContent = () => {
    return content ? Box.create(content, {
      defaultProps: () => ({
        as: "span",
        className: carouselNavigationItemSlotClassNames.content,
        styles: resolvedStyles["content"]
      })
    }) : Box.create(indicator, {
      defaultProps: () => ({
        className: carouselNavigationItemSlotClassNames.indicator,
        styles: resolvedStyles["indicator"]
      })
    });
  };
  const result = /* @__PURE__ */ jsx25(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        onBlur: handleBlur,
        onFocus: handleFocus,
        onClick: handleClick,
        ref,
        ...unhandledProps
      }),
      children: childrenExist3(children) ? children : renderContent()
    }
  );
  setEnd();
  return result;
});
CarouselNavigationItem.displayName = "CarouselNavigationItem";
CarouselNavigationItem.create = createShorthandFactory24({
  Component: CarouselNavigationItem,
  mappedProp: "content"
});
CarouselNavigationItem.defaultProps = {
  as: "li",
  accessibility: tabBehavior
};
CarouselNavigationItem.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "active",
  "content",
  "indicator",
  "index",
  "onClick"
];
var CarouselNavigationItem_default = CarouselNavigationItem;

// packages/aria/src/components/Carousel/CarouselNavigation.tsx
import { jsx as jsx26 } from "react/jsx-runtime";
var carouselNavigationClassName = "ui-carouselNavigation";
var CarouselNavigation = forwardRef24((props, ref) => {
  const context = useFluentContext26();
  const { setStart, setEnd } = useTelemetry26(
    CarouselNavigation.displayName,
    context.telemetry
  );
  setStart();
  const {
    className,
    styles,
    variables,
    accessibility,
    design,
    children,
    activeIndex,
    activeTabIndex,
    maxTabs,
    items
  } = props;
  const getA11yProps = useAccessibility22(
    accessibility,
    {
      debugName: CarouselNavigation.displayName,
      rtl: context.rtl
    }
  );
  const { classes } = useStyles26(
    CarouselNavigation.displayName,
    {
      className: carouselNavigationClassName,
      mapPropsToStyles: () => ({
        activeIndex,
        activeTabIndex,
        numOfItems: items?.length ?? 0,
        maxTabs
      }),
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    }
  );
  const unhandledProps = useUnhandledProps24(
    CarouselNavigation.handledProps,
    props
  );
  const ElementType = getElementType15(props);
  const handleItemOverrides = (variables2) => (predefinedProps) => ({
    onClick: (e, itemProps) => {
      _4.invoke(props, "onItemClick", e, itemProps);
      _4.invoke(predefinedProps, "onClick", e, itemProps);
    },
    variables: mergeVariablesOverrides(
      variables2,
      predefinedProps.variables
    )
  });
  const renderItems = () => {
    return _4.map(
      items,
      (item, index) => CarouselNavigationItem_default.create(item, {
        defaultProps: () => getA11yProps("item", {
          active: index === activeTabIndex,
          index
        }),
        overrideProps: handleItemOverrides(variables)
      })
    );
  };
  const result = getA11yProps.unstable_wrapWithFocusZone(
    /* @__PURE__ */ jsx26(
      ElementType,
      {
        ...getA11yProps("root", {
          // All these props are passed down to the root element.
          // The root element in this case is `ElementType`
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        ...rtlTextContainer2.getAttributes({ forElements: [children] }),
        children: childrenExist4(children) ? children : renderItems()
      }
    )
  );
  setEnd();
  return result;
});
CarouselNavigation.displayName = "CarouselNavigation";
CarouselNavigation.create = createShorthandFactory25({
  Component: CarouselNavigation,
  mappedArrayProp: "items"
});
CarouselNavigation.defaultProps = {
  as: "ul",
  accessibility: tabListBehavior
};
CarouselNavigation.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "activeIndex",
  "activeTabIndex",
  "maxTabs",
  "items",
  "onItemClick"
];
var CarouselNavigation_default = CarouselNavigation;

// packages/aria/src/components/Carousel/CarouselPaddle.tsx
import { forwardRef as forwardRef25 } from "react";
import {
  useFluentContext as useFluentContext27,
  getElementType as getElementType16,
  useAccessibility as useAccessibility23,
  useTelemetry as useTelemetry27,
  useStyles as useStyles27,
  useUnhandledProps as useUnhandledProps25,
  createShorthandFactory as createShorthandFactory26,
  buttonBehavior as buttonBehavior2,
  rtlTextContainer as rtlTextContainer3
} from "@fluentui/react-northstar";
import { childrenExist as childrenExist5 } from "@fluentui/react-bindings";
import { jsx as jsx27 } from "react/jsx-runtime";
var carouselPaddleClassName = "ui-carouselPaddle";
var carouselPaddleSlotClassNames = {
  content: `${carouselPaddleClassName}__content`
};
var CarouselPaddle = forwardRef25((props, ref) => {
  const context = useFluentContext27();
  const { setStart, setEnd } = useTelemetry27(
    CarouselPaddle.displayName,
    context.telemetry
  );
  setStart();
  const {
    children,
    as,
    content,
    className,
    styles,
    variables,
    accessibility,
    design,
    disabled,
    hidden,
    next,
    previous
  } = props;
  const hasChildren = childrenExist5(children);
  const getA11yProps = useAccessibility23(
    accessibility,
    {
      debugName: CarouselPaddle.displayName,
      mapPropsToBehavior: () => ({
        as: String(as),
        disabled
      }),
      rtl: context.rtl
    }
  );
  const { classes, styles: resolvedStyles } = useStyles27(CarouselPaddle.displayName, {
    className: carouselPaddleClassName,
    mapPropsToStyles: () => ({
      disabled,
      hidden,
      next,
      previous
    }),
    mapPropsToInlineStyles: () => ({
      className,
      styles,
      variables,
      design
    }),
    rtl: context.rtl,
    unstable_props: props
  });
  const unhandledProps = useUnhandledProps25(
    CarouselPaddle.handledProps,
    props
  );
  const ElementType = getElementType16(props);
  const renderIcon = () => {
    return Box.create(content, {
      defaultProps: () => getA11yProps("content", {
        className: carouselPaddleSlotClassNames["content"],
        styles: resolvedStyles["content"]
      })
    });
  };
  const result = /* @__PURE__ */ jsx27(
    ElementType,
    {
      ...rtlTextContainer3.getAttributes({ forElements: [children] }),
      ...getA11yProps("root", {
        disabled,
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      children: hasChildren ? children : renderIcon()
    }
  );
  setEnd();
  return result;
});
CarouselPaddle.displayName = "CarouselPaddle";
CarouselPaddle.create = createShorthandFactory26({
  Component: CarouselPaddle,
  mappedProp: "content"
});
CarouselPaddle.defaultProps = {
  as: "button",
  accessibility: buttonBehavior2,
  content: {}
};
CarouselPaddle.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "disabled",
  "hidden",
  "next",
  "previous"
];
var CarouselPaddle_default = CarouselPaddle;

// packages/aria/src/components/Carousel/CarouselPaddlesContainer.tsx
import { forwardRef as forwardRef26 } from "react";
import {
  useFluentContext as useFluentContext28,
  getElementType as getElementType17,
  useAccessibility as useAccessibility24,
  useTelemetry as useTelemetry28,
  useStyles as useStyles28,
  useUnhandledProps as useUnhandledProps26
} from "@fluentui/react-northstar";
import { childrenExist as childrenExist6 } from "@fluentui/react-bindings";
import { jsx as jsx28 } from "react/jsx-runtime";
var carouselPaddlesContainerClassName = "ui-carouselPaddlesContainer";
var CarouselPaddlesContainer = forwardRef26((props, ref) => {
  const context = useFluentContext28();
  const { setStart, setEnd } = useTelemetry28(
    CarouselPaddlesContainer.displayName,
    context.telemetry
  );
  setStart();
  const {
    className,
    styles,
    variables,
    accessibility,
    design,
    children,
    content
  } = props;
  const getA11yProps = useAccessibility24(
    accessibility,
    {
      debugName: CarouselPaddlesContainer.displayName,
      rtl: context.rtl
    }
  );
  const { classes } = useStyles28(
    CarouselPaddlesContainer.displayName,
    {
      className: carouselPaddlesContainerClassName,
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    }
  );
  const unhandledProps = useUnhandledProps26(
    CarouselPaddlesContainer.handledProps,
    props
  );
  const ElementType = getElementType17(props);
  const result = /* @__PURE__ */ jsx28(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      children: childrenExist6(children) ? children : content
    }
  );
  setEnd();
  return result;
});
CarouselPaddlesContainer.displayName = "CarouselPaddlesContainer";
CarouselPaddlesContainer.defaultProps = {
  as: "div"
};
CarouselPaddlesContainer.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility"
];
var CarouselPaddlesContainer_default = CarouselPaddlesContainer;

// packages/aria/src/components/Carousel/Carousel.tsx
import {
  ArrowLeftSLine,
  ArrowRightSLine as ArrowRightSLine2,
  ChevronLeft,
  ChevronRight
} from "@archipro-design/icons";

// packages/aria/src/components/Carousel/CarouselOverlay.tsx
import { forwardRef as forwardRef28 } from "react";
import {
  useFluentContext as useFluentContext30,
  useAccessibility as useAccessibility26,
  useTelemetry as useTelemetry30,
  useStyles as useStyles30,
  useUnhandledProps as useUnhandledProps28,
  createShorthandFactory as createShorthandFactory28,
  popupBehavior as popupBehavior2
} from "@fluentui/react-northstar";
import cx7 from "classnames";

// packages/aria/src/components/Carousel/CarouselIndicator.tsx
import { forwardRef as forwardRef27 } from "react";
import {
  useFluentContext as useFluentContext29,
  getElementType as getElementType18,
  useAccessibility as useAccessibility25,
  useTelemetry as useTelemetry29,
  useStyles as useStyles29,
  useUnhandledProps as useUnhandledProps27,
  createShorthandFactory as createShorthandFactory27
} from "@fluentui/react-northstar";
import { jsx as jsx29 } from "react/jsx-runtime";
var carouselIndicatorClassName = "ui-carouselIndicator";
var CarouselIndicator = forwardRef27((props, ref) => {
  const context = useFluentContext29();
  const { setStart, setEnd } = useTelemetry29(
    CarouselIndicator.displayName,
    context.telemetry
  );
  setStart();
  const { className, styles, variables, accessibility, design, active } = props;
  const getA11yProps = useAccessibility25(
    accessibility,
    {
      debugName: CarouselIndicator.displayName,
      rtl: context.rtl
    }
  );
  const { classes } = useStyles29(
    CarouselIndicator.displayName,
    {
      className: carouselIndicatorClassName,
      mapPropsToStyles: () => ({
        active
      }),
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    }
  );
  const unhandledProps = useUnhandledProps27(
    CarouselIndicator.handledProps,
    props
  );
  const ElementType = getElementType18(props);
  const result = /* @__PURE__ */ jsx29(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      })
    }
  );
  setEnd();
  return result;
});
CarouselIndicator.displayName = "CarouselIndicator";
CarouselIndicator.create = createShorthandFactory27({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: CarouselIndicator,
  mappedProp: "content"
});
CarouselIndicator.defaultProps = {};
CarouselIndicator.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "active"
];
var CarouselIndicator_default = CarouselIndicator;

// packages/aria/src/components/Carousel/CarouselOverlay.tsx
import { jsx as jsx30, jsxs as jsxs12 } from "react/jsx-runtime";
var defaultRootElementType5 = "div";
var carouselOverlayClassName = "ui-carouselOverlay";
var carouselOverlaySlotClassNames = {
  indicatorWrap: `${carouselOverlayClassName}__indicatorWrap`,
  indicator: `${carouselOverlayClassName}__indicator`,
  indicatorActive: `${carouselOverlayClassName}__indicatorActive`
};
var CarouselOverlay = forwardRef28(
  (props, ref) => {
    const context = useFluentContext30();
    const { setStart, setEnd } = useTelemetry30(
      CarouselOverlay.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      topActions,
      indicators,
      activeIndex,
      paginationText,
      allowClickThrough = false
    } = props;
    const getA11yProps = useAccessibility26(
      accessibility,
      {
        debugName: CarouselOverlay.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles30(
      CarouselOverlay.displayName,
      {
        className: carouselOverlayClassName,
        mapPropsToStyles: () => ({ allowClickThrough }),
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps28(
      CarouselOverlay.handledProps,
      props
    );
    const indicatorsArr = Array.from(Array(indicators).keys());
    const result = /* @__PURE__ */ jsxs12(
      Flex,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        space: "between",
        column: true,
        children: [
          /* @__PURE__ */ jsx30(Flex, { hAlign: "end", children: topActions?.map(
            (item, idx) => Box.create(item, {
              defaultProps: () => ({
                key: `carouselOverlay-topActions-${idx}`
              })
            })
          ) }),
          indicators && /* @__PURE__ */ jsx30(
            Flex,
            {
              gap: "space-10",
              hAlign: "center",
              vAlign: "center",
              className: carouselOverlaySlotClassNames.indicatorWrap,
              children: indicatorsArr.map((index) => /* @__PURE__ */ jsx30(
                CarouselIndicator_default,
                {
                  active: activeIndex === index,
                  className: cx7([
                    carouselOverlaySlotClassNames.indicator,
                    activeIndex === index && carouselOverlaySlotClassNames.indicatorActive
                  ])
                },
                `carouselIndicator--${index}`
              ))
            }
          ),
          paginationText && /* @__PURE__ */ jsx30(Flex, { hAlign: "center", children: Caption.create(paginationText, {
            defaultProps: () => ({
              className: classes["paginationText"],
              variant: "03"
            })
          }) })
        ]
      }
    );
    setEnd();
    return result;
  }
);
CarouselOverlay.displayName = "CarouselOverlay";
CarouselOverlay.create = createShorthandFactory28({
  Component: CarouselOverlay
});
CarouselOverlay.defaultProps = {
  as: defaultRootElementType5,
  accessibility: popupBehavior2
};
CarouselOverlay.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "as",
  "topActions",
  "indicators",
  "activeIndex",
  "paginationText",
  "allowClickThrough"
];
var CarouselOverlay_default = CarouselOverlay;

// packages/aria/src/components/Carousel/Carousel.tsx
import { usePrevious } from "ahooks";

// packages/aria/src/components/Carousel/swiperProps.ts
var swiperHandledProps = [
  "init",
  "enabled",
  "initialSlide",
  "freeMode",
  "thumbs",
  "speed",
  "setWrapperSize",
  "width",
  "height",
  "autoHeight",
  "roundLengths",
  "nested",
  "focusableElements",
  "allowTouchMove",
  "on",
  "cssMode",
  "spaceBetween",
  "slidesPerView",
  "slidesPerGroup",
  "slidesPerGroupSkip",
  "slidesPerGroupAuto",
  "centeredSlides",
  "centeredSlidesBounds",
  "allowSlidePrev",
  "allowSlideNext",
  "noSwiping",
  "preventClicks",
  "slideToClickedSlide",
  "watchSlidesProgress",
  "loop",
  "breakpoints",
  "breakpointsBase",
  "a11y",
  "autoplay",
  "controller",
  "keyboard",
  "pagination",
  "effect",
  "fadeEffect",
  "slidesOffsetBefore",
  "slidesOffsetAfter",
  "preventClicksPropagation"
];

// packages/aria/src/components/Carousel/Carousel.tsx
import { Fragment as Fragment2, jsx as jsx31, jsxs as jsxs13 } from "react/jsx-runtime";
function useNavTabs(defaultActiveIndex, totalItems, maxTabItems) {
  const [activeTabIndex, setActiveTabIndex] = useState2(defaultActiveIndex);
  const prevTabIndex = usePrevious(activeTabIndex) ?? defaultActiveIndex;
  const showNextTabBtn = (activeTabIndex < maxTabItems || totalItems - activeTabIndex >= maxTabItems) && totalItems > maxTabItems;
  const showPrevTabBtn = activeTabIndex >= maxTabItems;
  return {
    activeTabIndex,
    setActiveTabIndex,
    prevTabIndex,
    showNextTabBtn,
    showPrevTabBtn
  };
}
var carouselClassName = "ui-carousel";
var carouselSlotClassNames = {
  itemsContainer: `${carouselClassName}__itemscontainer`,
  paddleContainer: `${carouselClassName}__paddlecontainer`,
  paddleNext: `${carouselClassName}__paddlenext`,
  paddlePrevious: `${carouselClassName}__paddleprevious`,
  navigation: `${carouselClassName}__navigation`
};
var Carousel = forwardRef29((props, ref) => {
  const context = useFluentContext31();
  const { setStart, setEnd } = useTelemetry31(
    Carousel.displayName,
    context.telemetry
  );
  setStart();
  const {
    children,
    className,
    styles,
    variables,
    accessibility,
    "aria-roledescription": ariaRoleDescription,
    "aria-label": ariaLabel,
    defaultActiveIndex = 0,
    items,
    design,
    hasNavigation,
    maxNavigationItems = 5,
    navigation,
    overlay = {},
    topCarouselActions,
    paddlePosition = "center",
    paddleNext,
    paddlePrevious,
    hidePaddles = false,
    navigationButtonNext,
    navigationButtonPrev,
    getSwiperApi,
    allowClickThrough = false,
    activeIndex: activeIndexProp,
    preventClicksPropagation,
    customBullet,
    trackCarouselActivity,
    handleSlideChange,
    autoplayEnabled,
    mixBlendMode = "normal",
    ...rest
  } = props;
  const swiperContainer = useRef3(null);
  const [swiper, setSwiper] = useState2(null);
  const [activeIndex, setActiveIndex] = useAutoControlled({
    defaultValue: defaultActiveIndex,
    value: activeIndexProp,
    initialValue: 0
  });
  const {
    activeTabIndex,
    setActiveTabIndex,
    prevTabIndex,
    showNextTabBtn,
    showPrevTabBtn
  } = useNavTabs(defaultActiveIndex, items.length, maxNavigationItems);
  const getA11yProps = useAccessibility27(accessibility, {
    debugName: Carousel.displayName,
    rtl: context.rtl
  });
  const { classes } = useStyles31(Carousel.displayName, {
    className: carouselClassName,
    mapPropsToStyles: () => ({
      maxNavigationItems,
      activeIndex: activeIndex ?? 0,
      activeTabIndex,
      totalItems: items.length,
      showPrevTabBtn,
      showNextTabBtn,
      paddlePosition
    }),
    mapPropsToInlineStyles: () => ({
      className,
      styles,
      variables,
      design
    }),
    rtl: context.rtl,
    unstable_props: props
  });
  const unhandledProps = useUnhandledProps29(Carousel.handledProps, props);
  const ElementType = getElementType19(props);
  const paddleNextRef = useRef3(null);
  const paddlePreviousRef = useRef3(null);
  const handlePreviousPaddleFocus = () => {
    paddlePreviousRef?.current?.focus();
  };
  const handleNextPaddleFocus = () => {
    paddleNextRef?.current?.focus();
  };
  React7.useEffect(() => {
    if (swiper) {
      getSwiperApi?.(swiper);
      swiper.on("slideChangeTransitionEnd", (swiper2) => {
        if (trackCarouselActivity && swiper2.swipeDirection) {
          switch (swiper2.swipeDirection) {
            case "prev":
              trackCarouselActivity("prev");
              break;
            case "next":
              trackCarouselActivity("next");
              break;
          }
        }
      });
      swiper.on("slideChange", (swiper2) => {
        const style = swiperContainer?.current?.style;
        const numberOfBullets = customBullet?.numberOfBullets || 5;
        const bulletSize = customBullet?.size || 7;
        const bulletGap = customBullet?.gap || 2;
        if (style && customBullet && swiper2.slides.length > numberOfBullets) {
          const slidesCount = swiper2.slides.length;
          const slideIndex = swiper2.realIndex;
          const bulletsCenter = Math.ceil(numberOfBullets / 2);
          const bulletsCenterOffset = Math.floor(numberOfBullets / 2);
          const boundedIndex = slideIndex < bulletsCenter ? 0 : slideIndex > slidesCount - bulletsCenter ? slidesCount - numberOfBullets : slideIndex - bulletsCenterOffset;
          const bulletWidth = bulletSize + 2 * bulletGap;
          const offset = boundedIndex * bulletWidth;
          style.setProperty(
            "--carousel-bullets-offset",
            pxToRem(-offset)
          );
        }
        if (handleSlideChange) {
          handleSlideChange(swiper2.realIndex);
        }
      });
    }
    return void 0;
  }, [swiper]);
  useEffect3(() => {
    if (autoplayEnabled === void 0) return;
    if (autoplayEnabled) {
      swiper?.autoplay?.resume();
    } else {
      swiper?.autoplay?.pause();
    }
  }, [swiper, autoplayEnabled]);
  const CustomPaddleNav = () => {
    swiper?.on("slideChangeTransitionStart", (swiper2) => {
      setActiveIndex(swiper2.activeIndex);
      setActiveTabIndex(swiper2.activeIndex);
    });
    swiper?.on("slideChangeTransitionEnd", () => {
      if (activeTabIndex > prevTabIndex) handleNextPaddleFocus();
      if (activeTabIndex < prevTabIndex) handlePreviousPaddleFocus();
    });
    return /* @__PURE__ */ jsxs13(
      CarouselPaddlesContainer_default,
      {
        className: carouselSlotClassNames.paddleContainer,
        onClick: (e) => {
          if (preventClicksPropagation) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
        variables: {
          mixBlendMode
        },
        children: [
          /* @__PURE__ */ jsx31(Ref, { innerRef: paddlePreviousRef, children: /* @__PURE__ */ jsx31(Fragment2, { children: CarouselPaddle_default.create(paddlePrevious, {
            defaultProps: () => ({
              className: carouselSlotClassNames.paddlePrevious,
              previous: true,
              hidden: activeIndex === 0 && !props.loop || hidePaddles,
              "aria-label": "Previous Paddle",
              onClick: () => {
                swiper?.slidePrev();
                trackCarouselActivity?.("prev");
              }
            })
          }) }) }),
          /* @__PURE__ */ jsx31(Ref, { innerRef: paddleNextRef, children: /* @__PURE__ */ jsx31(Fragment2, { children: CarouselPaddle_default.create(paddleNext, {
            defaultProps: () => ({
              className: carouselSlotClassNames.paddleNext,
              styles: () => ({
                right: pxToRem(16)
              }),
              next: true,
              hidden: activeIndex === items.length - 1 && !props.loop || hidePaddles,
              "aria-label": "Next Paddle",
              onClick: () => {
                swiper?.slideNext();
                trackCarouselActivity?.("next");
              }
            })
          }) }) })
        ]
      }
    );
  };
  const renderContent = () => {
    return /* @__PURE__ */ jsxs13(
      "div",
      {
        className: cx8(
          carouselSlotClassNames.itemsContainer,
          classes["itemsContainer"]
        ),
        ref: swiperContainer,
        children: [
          children,
          (topCarouselActions || rest.pagination) && CarouselOverlay_default.create(overlay, {
            defaultProps: () => {
              return {
                allowClickThrough,
                topActions: topCarouselActions,
                variables: { mixBlendMode }
              };
            }
          }),
          /* @__PURE__ */ jsxs13(
            Swiper,
            {
              a11y: {
                enabled: true
              },
              modules: [
                A11y,
                Controller,
                Thumbs,
                Pagination,
                Autoplay,
                Keyboard,
                EffectFade
              ],
              onSwiper: setSwiper,
              initialSlide: defaultActiveIndex,
              ...rest,
              children: [
                items.map(
                  (item, index) => CarouselItem.create(item, {
                    generateKey: true,
                    defaultProps: () => ({
                      index
                    })
                  })
                ),
                !hidePaddles && /* @__PURE__ */ jsx31(CustomPaddleNav, {})
              ]
            }
          )
        ]
      }
    );
  };
  const renderTabNavigation = () => {
    if (!items || !items.length || !hasNavigation) {
      return null;
    }
    return /* @__PURE__ */ jsxs13(
      Box,
      {
        ...getA11yProps("navigationContainer", {
          className: classes["navigationContainer"]
        }),
        children: [
          showPrevTabBtn && Button.create(navigationButtonPrev, {
            defaultProps: () => ({
              className: `${classes["leftNavButton"]} ${classes["tabNavButton"]}`,
              color: "dark",
              variant: "text",
              onClick: () => setActiveTabIndex((curr) => curr - 1)
            })
          }),
          /* @__PURE__ */ jsx31(
            Box,
            {
              ...getA11yProps("listNavigation", {
                className: classes["listNavigation"]
              }),
              children: CarouselNavigation_default.create(navigation, {
                generateKey: true,
                defaultProps: () => ({
                  className: carouselSlotClassNames.navigation,
                  activeIndex,
                  activeTabIndex,
                  maxTabs: maxNavigationItems
                }),
                overrideProps: (predefinedProps) => ({
                  onItemClick: (e, itemProps) => {
                    const { index } = itemProps;
                    if (typeof index === "number") {
                      swiper?.slideTo(index);
                      setActiveTabIndex(index);
                    }
                    _5.invoke(
                      predefinedProps,
                      "onClick",
                      e,
                      itemProps
                    );
                  }
                })
              })
            }
          ),
          showNextTabBtn && Button.create(navigationButtonNext, {
            defaultProps: () => ({
              className: `${classes["rightNavButton"]} ${classes["tabNavButton"]}`,
              color: "dark",
              variant: "text",
              onClick: () => setActiveTabIndex((curr) => curr + 1)
            })
          })
        ]
      }
    );
  };
  const result = /* @__PURE__ */ jsxs13(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps,
        "aria-label": ariaLabel,
        "aria-roledescription": ariaRoleDescription
      }),
      children: [
        renderContent(),
        renderTabNavigation()
      ]
    }
  );
  setEnd();
  return result;
});
Carousel.displayName = "Carousel";
Carousel.defaultProps = {
  paddlePrevious: /* @__PURE__ */ jsx31(ArrowLeftSLine, {}),
  paddleNext: /* @__PURE__ */ jsx31(ArrowRightSLine2, {}),
  navigationButtonPrev: /* @__PURE__ */ jsx31(ChevronLeft, {}),
  navigationButtonNext: /* @__PURE__ */ jsx31(ChevronRight, {})
};
Carousel.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "children",
  "items",
  "hasNavigation",
  "maxNavigationItems",
  "navigation",
  "activeIndex",
  "defaultActiveIndex",
  "aria-roledescription",
  "aria-label",
  "overlay",
  "topCarouselActions",
  "hidePaddles",
  "paddleNext",
  "paddlePrevious",
  "navigationButtonNext",
  "navigationButtonPrev",
  "paddlePosition",
  "getSwiperApi",
  "allowClickThrough",
  "customBullet",
  "trackCarouselActivity",
  "handleSlideChange",
  "autoplayEnabled",
  "mixBlendMode",
  ...swiperHandledProps
];
Carousel.Item = CarouselItem;
Carousel.Navigation = CarouselNavigation_default;
Carousel.Paddle = CarouselPaddle_default;
Carousel.PaddlesContainer = CarouselPaddlesContainer_default;
Carousel.create = createShorthandFactory29({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: Carousel,
  mappedArrayProp: "items"
});
var Carousel_default = Carousel;

// packages/aria/src/components/Tile/TileImage/favourites.ts
var favouriteHandledProps = [
  "isFavourited",
  "onFavourite",
  "hideFavourite"
];

// packages/aria/src/components/Tile/TileImage/TileImage.tsx
import { useInViewport } from "ahooks";

// packages/aria/src/utils/appDisplayMode.tsx
import { useMemo as useMemo2 } from "react";
import { useContext, createContext as createContext2 } from "react";
import { jsx as jsx32 } from "react/jsx-runtime";
var AppDisplayModeTypeContext = createContext2(
  void 0
);
function AppDisplayModeContextProvider({
  mode = "desktop",
  children
}) {
  const AppDisplayMode = useMemo2(
    () => ({
      mobile: mode === "mobile",
      desktop: mode === "desktop"
    }),
    [mode]
  );
  return /* @__PURE__ */ jsx32(AppDisplayModeTypeContext.Provider, { value: AppDisplayMode, children });
}
function useAppDisplayModeContext() {
  const ctx = useContext(AppDisplayModeTypeContext);
  if (typeof ctx === "undefined") {
    throw new Error(
      "useAppDisplayModeContext must be used within the provider."
    );
  }
  return ctx;
}

// packages/aria/src/components/Tile/TileImage/TileImage.tsx
import { Fragment as Fragment3, jsx as jsx33, jsxs as jsxs14 } from "react/jsx-runtime";
var defaultRootElementType6 = "div";
var tileImageClassName = "ui-TileImage";
var tileImageSlotClassNames = {
  imageContainer: `${tileImageClassName}__imageContainer`,
  awardIndicator: `${tileImageClassName}__awardIndicator`
};
var TileImage = forwardRef30(
  (props, ref) => {
    const context = useFluentContext32();
    const { setStart, setEnd } = useTelemetry32(
      TileImage.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      images,
      overlay = {},
      height = "100%",
      width = "100%",
      isFavourited,
      onFavourite,
      hideFavourite,
      hidePaddles = false,
      allowClickThrough = false,
      topActions = null,
      topContent = null,
      mixBlendMode,
      trackCarouselActivity,
      awardIndicator,
      preloadCount = 1,
      inViewport,
      alt
    } = props;
    const appDisplayMode = useAppDisplayModeContext();
    const getA11yProps = useAccessibility28(
      accessibility,
      {
        debugName: TileImage.displayName,
        rtl: context.rtl
      }
    );
    const inViewRef = useRef4(null);
    const [inView] = useInViewport(inViewRef, {
      rootMargin: "100%"
    });
    const [lazyLoadingFlags, setLazyLoadingFlags] = useState3(
      images.map(() => true)
    );
    const preloadImages = (activeIndex, count) => {
      const indexesToPreload = [];
      for (let i = 1; i <= count; i++) {
        const prevIndex = activeIndex - i;
        indexesToPreload.push(
          prevIndex < 0 ? lazyLoadingFlags.length + prevIndex : prevIndex
        );
        indexesToPreload.push(
          (activeIndex + i) % lazyLoadingFlags.length
        );
      }
      setLazyLoadingFlags(
        lazyLoadingFlags.map(
          (loading, index) => !indexesToPreload.includes(index)
        )
      );
    };
    const handleSlideChange = (slideIndex) => {
      preloadImages(slideIndex, preloadCount);
    };
    useEffect4(() => {
      if (inViewport) {
        preloadImages(0, preloadCount);
      }
    }, [preloadCount, inViewport]);
    const { classes } = useStyles32(
      TileImage.displayName,
      {
        className: tileImageClassName,
        mapPropsToStyles: () => ({ height, width }),
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps30(TileImage.handledProps, props);
    const ElementType = getElementType20(props);
    const handleFavourite = (e) => {
      e.stopPropagation();
      e.preventDefault();
      onFavourite?.(e, isFavourited);
    };
    const getFavouriteAction = () => {
      if (hideFavourite) {
        return void 0;
      }
      const button = /* @__PURE__ */ jsx33(
        Button,
        {
          size: 16,
          variables: { buttonPadding: 10, iconPadding: 0 },
          color: "white",
          onClick: handleFavourite,
          icon: isFavourited ? /* @__PURE__ */ jsx33(HeartLightFill, {}) : /* @__PURE__ */ jsx33(HeartLightLine, {})
        },
        "favourite-button"
      );
      if (appDisplayMode?.desktop) {
        return MessageTooltip.create(
          {
            key: "save-fav-tooltip",
            content: {
              children: "Save",
              size: 16,
              padding: 8,
              color: "black"
            }
          },
          {
            defaultProps: () => {
              return {
                position: "above",
                offset: [0, 8],
                trigger: button
              };
            }
          }
        );
      }
      return button;
    };
    const getTopActions = () => {
      const favourite = getFavouriteAction();
      if (!topActions && !favourite) {
        return void 0;
      }
      return [
        {
          key: "tile-top-actions",
          content: /* @__PURE__ */ jsxs14(Flex, { gap: "space-6", vAlign: "center", children: [
            topActions,
            favourite
          ] })
        }
      ];
    };
    const setRefs = useCallback2(
      (node) => {
        if (ref && "current" in ref) ref.current = node;
        inViewRef.current = node;
      },
      [inViewRef, ref]
    );
    const result = /* @__PURE__ */ jsxs14(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref: setRefs,
          ...unhandledProps
        }),
        children: [
          images && images.length === 1 && /* @__PURE__ */ jsxs14(Fragment3, { children: [
            topContent,
            inView && TileOverlay.create(overlay, {
              defaultProps: () => {
                return { topActions: getTopActions() };
              }
            }),
            Image.create(images[0], {
              defaultProps: () => {
                return {
                  className: tileImageSlotClassNames.imageContainer,
                  width,
                  height,
                  layout: "fill",
                  objectFit: "cover",
                  sizes: "50vw",
                  variables: {
                    mixBlendMode
                  },
                  alt: alt ?? ""
                };
              }
            })
          ] }),
          images && images.length > 1 && Carousel_default.create(
            {
              items: images.map((imageData, index) => {
                if (imageData) {
                  imageData["loading"] = lazyLoadingFlags[index] ? "lazy" : "eager";
                }
                return Image.create(
                  imageData,
                  {
                    defaultProps: () => {
                      return {
                        width,
                        height,
                        layout: "fill",
                        objectFit: "cover",
                        sizes: "50vw",
                        loading: "lazy",
                        alt: alt ?? ""
                      };
                    }
                  }
                );
              })
            },
            {
              defaultProps: () => {
                return {
                  allowClickThrough,
                  paddlePosition: "flex-end",
                  hasNavigation: false,
                  loop: true,
                  variables: {
                    containerWidth: width,
                    containerHeight: height,
                    overlayInitialOpacity: 1,
                    overlayInitialOpacityDesktop: 0,
                    overlayPadding: pxToRem(10)
                  },
                  hidePaddles,
                  topCarouselActions: getTopActions(),
                  pagination: {
                    dynamicBullets: false,
                    clickable: false
                  },
                  preventClicksPropagation: true,
                  customBullet: {
                    size: 7,
                    gap: 2,
                    numberOfBullets: 5
                  },
                  trackCarouselActivity,
                  handleSlideChange
                };
              }
            }
          ),
          awardIndicator && /* @__PURE__ */ jsx33("div", { className: tileImageSlotClassNames.awardIndicator, children: awardIndicator })
        ]
      }
    );
    setEnd();
    return result;
  }
);
TileImage.displayName = "TileImage";
TileImage.create = createShorthandFactory30({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: TileImage,
  mappedProp: "images"
});
TileImage.defaultProps = {
  as: defaultRootElementType6,
  accessibility: cardBehavior2
};
TileImage.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "as",
  // Extra props that are handled
  "images",
  "overlay",
  "hidePaddles",
  "allowClickThrough",
  "topActions",
  "topContent",
  "mixBlendMode",
  "trackCarouselActivity",
  "awardIndicator",
  "inViewport",
  "alt",
  ...favouriteHandledProps
];

// packages/aria/src/components/Tile/ProductTile/ProductTile.tsx
import cx9 from "classnames";
import { DownloadPdf, DownloadDoc, DownloadCad } from "@archipro-design/icons";
import { jsx as jsx34, jsxs as jsxs15 } from "react/jsx-runtime";
var defaultRootElementType7 = "div";
var productTileClassName = "ui-productTile";
var productTileSlotClassNames = {
  title: `${productTileClassName}__title`,
  product: `${productTileClassName}__product`,
  priceContainer: `${productTileClassName}__price-container`,
  price: `${productTileClassName}__price`,
  professional: `${productTileClassName}__professional`,
  content: `${productTileClassName}__content`,
  variants: `${productTileClassName}__variants`,
  variant: `${productTileClassName}__variant`,
  downloads: `${productTileClassName}__downloads`
};
var SHOW_MAX_VARIANTS = 4;
var SIZE_TO_HEIGHT_MAPPING = {
  "01": {
    xsmall: pxToRem(280),
    small: pxToRem(280),
    medium: pxToRem(325),
    large: pxToRem(348),
    xlarge: pxToRem(434),
    dynamic: "100%"
  },
  "02": {
    xsmall: pxToRem(158),
    small: pxToRem(186),
    medium: pxToRem(204),
    large: pxToRem(204),
    xlarge: pxToRem(204),
    dynamic: "100%"
  },
  "03": {
    small: pxToRem(240),
    medium: pxToRem(240)
  },
  "04": {
    // for member design-board saved pin page
    small: pxToRem(203),
    large: pxToRem(286),
    dynamic: "100%"
  }
};
var DOWNLOAD_ICON_MAPPING = {
  Documents: /* @__PURE__ */ jsx34(DownloadPdf, { styles: { order: 0 } }),
  "CAD and BIM": /* @__PURE__ */ jsx34(DownloadCad, { styles: { order: 1 } }),
  BPIR: /* @__PURE__ */ jsx34(DownloadDoc, { styles: { order: 2 } })
};
var ProductTile = forwardRef31(
  (props, ref) => {
    const context = useFluentContext33();
    const { setStart, setEnd } = useTelemetry33(
      ProductTile.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      size,
      image,
      professionalName,
      variants,
      additionalVariantCount,
      productName,
      pricePrefix,
      price,
      isFavourited,
      onFavourite,
      hideFavourite,
      adminCaption,
      topActions,
      topContent,
      variant = "01",
      trackCarouselActivity,
      newDesignEnabled = false,
      awardIndicator,
      downloads,
      onDownloadsClick,
      appDisplayMode = { mobile: false, desktop: true },
      tileImage = {
        images: image,
        topActions,
        awardIndicator,
        topContent,
        height: SIZE_TO_HEIGHT_MAPPING[variant][size],
        alt: `${productName} product image`
      }
    } = props;
    const { mobile } = appDisplayMode;
    const downloadIcons = downloads?.reduce((acc, download) => {
      const icon = DOWNLOAD_ICON_MAPPING[download.Category] || DOWNLOAD_ICON_MAPPING["Documents"];
      if (!acc.find((item) => item.icon === icon)) {
        acc.push({
          type: download.Category ? download.Category === "Other" ? "Documents" : download.Category : "Documents",
          icon
        });
      }
      return acc;
    }, []) || [];
    const getA11yProps = useAccessibility29(
      accessibility,
      {
        debugName: ProductTile.displayName,
        rtl: context.rtl
      }
    );
    const unhandledProps = useUnhandledProps31(
      ProductTile.handledProps,
      props
    );
    const ElementType = getElementType21(props);
    const imageHeight = SIZE_TO_HEIGHT_MAPPING[variant][size];
    const { classes } = useStyles33(
      ProductTile.displayName,
      {
        className: productTileClassName,
        mapPropsToStyles: () => ({
          size,
          imageHeight,
          variant,
          newDesignEnabled
        }),
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const isImage = (variant2) => {
      return variant2.src !== void 0;
    };
    const isBox = (variant2) => {
      return !isImage(variant2);
    };
    const result = /* @__PURE__ */ jsxs15(
      ElementType,
      {
        ...getA11yProps("root", {
          className: cx9([
            classes["root"],
            classes[`variant${variant}`]
          ]),
          ref,
          ...unhandledProps
        }),
        children: [
          TileImage.create(tileImage, {
            defaultProps: () => {
              return {
                key: "TileImage",
                images: image,
                height: imageHeight,
                width: imageHeight,
                isFavourited,
                onFavourite,
                hideFavourite,
                trackCarouselActivity
              };
            }
          }),
          /* @__PURE__ */ jsxs15(
            Flex,
            {
              column: true,
              className: productTileSlotClassNames.content,
              children: [
                Label.create(productName, {
                  generateKey: true,
                  defaultProps: () => {
                    return {
                      link: !newDesignEnabled,
                      variant: newDesignEnabled ? "03-alt" : "04",
                      uc: !newDesignEnabled,
                      maximumLines: newDesignEnabled ? 1 : 2,
                      key: "productName"
                    };
                  },
                  overrideProps: () => {
                    return {
                      className: mergeClassName(
                        productName,
                        productTileSlotClassNames.product
                      )
                    };
                  }
                }),
                Label.create(professionalName, {
                  generateKey: true,
                  defaultProps: () => {
                    return {
                      link: !newDesignEnabled,
                      variant: "04",
                      maximumLines: 1,
                      key: "professionalName"
                    };
                  },
                  overrideProps: () => {
                    return {
                      className: mergeClassName(
                        productName,
                        productTileSlotClassNames.professional
                      )
                    };
                  }
                }),
                /* @__PURE__ */ jsxs15(
                  Flex,
                  {
                    gap: "space-4",
                    className: productTileSlotClassNames.priceContainer,
                    children: [
                      Label.create(pricePrefix, {
                        generateKey: true,
                        defaultProps: () => {
                          return {
                            variant: newDesignEnabled ? "03-alt" : "04",
                            maximumLines: 1,
                            key: "pricePrefix"
                          };
                        }
                      }),
                      Label.create(price, {
                        generateKey: true,
                        defaultProps: () => {
                          return {
                            className: productTileSlotClassNames.price,
                            variant: newDesignEnabled ? "03-alt" : "04",
                            maximumLines: 1,
                            key: "price"
                          };
                        }
                      })
                    ]
                  }
                )
              ]
            },
            `product-details`
          ),
          downloadIcons.length > 0 && /* @__PURE__ */ jsx34(Flex, { className: productTileSlotClassNames.downloads, children: downloadIcons.map((item, index) => /* @__PURE__ */ jsx34(
            MessageTooltip,
            {
              trigger: /* @__PURE__ */ jsx34(
                "div",
                {
                  onClick: (e) => {
                    onDownloadsClick?.(e, item.type);
                  },
                  children: item.icon
                }
              ),
              content: {
                children: item.type,
                size: 16,
                padding: 8,
                pointing: true,
                variables: {
                  maxWidth: pxToRem(240)
                }
              },
              position: "above",
              align: "center",
              offset: [-1, 2],
              open: mobile ? false : void 0
            },
            index
          )) }),
          !newDesignEnabled && variants && variants.length > 1 ? /* @__PURE__ */ jsxs15(
            Flex,
            {
              vAlign: "center",
              hAlign: "start",
              className: productTileSlotClassNames.variants,
              children: [
                variants.map((variant2) => {
                  if (isImage(variant2)) {
                    return Image.create(variant2, {
                      generateKey: true,
                      defaultProps: () => ({
                        className: productTileSlotClassNames.variant
                      })
                    });
                  } else if (isBox(variant2)) {
                    return Box.create(variant2, {
                      generateKey: true,
                      defaultProps: () => ({
                        className: productTileSlotClassNames.variant
                      })
                    });
                  }
                  throw new Error("Infallible");
                }).filter((v, i) => i < SHOW_MAX_VARIANTS),
                additionalVariantCount ? Caption.create(
                  typeof additionalVariantCount === "object" ? additionalVariantCount : `+${additionalVariantCount}`,
                  {
                    generateKey: true,
                    defaultProps: () => ({
                      variant: "03"
                    })
                  }
                ) : null
              ]
            },
            `product-variants`
          ) : null,
          adminCaption
        ]
      }
    );
    setEnd();
    return result;
  }
);
ProductTile.displayName = "ProductTile";
ProductTile.create = createShorthandFactory31({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ProductTile
});
ProductTile.defaultProps = {
  as: defaultRootElementType7,
  accessibility: cardBehavior3,
  size: "medium",
  isFavourited: false
};
ProductTile.handledProps = [
  ...commonHandledProps(),
  // Extra props that are handled
  "variants",
  "additionalVariantCount",
  "pricePrefix",
  "price",
  "productName",
  "professionalName",
  "size",
  "variant",
  "tileImage",
  "image",
  "topContent",
  "trackCarouselActivity",
  "newDesignEnabled",
  "awardIndicator",
  "downloads",
  "onDownloadsClick",
  "appDisplayMode",
  "professionalLink",
  ...favouriteHandledProps,
  ...adminTileHandledProps
];

// packages/aria/src/components/Tile/ProjectTile/ProjectTile.tsx
import { createShorthandFactory as createShorthandFactory32 } from "@fluentui/react-northstar";
import { cardBehavior as cardBehavior4 } from "@fluentui/react-northstar";
import {
  useFluentContext as useFluentContext34,
  useAccessibility as useAccessibility30,
  useTelemetry as useTelemetry34,
  useStyles as useStyles34,
  useUnhandledProps as useUnhandledProps32
} from "@fluentui/react-northstar";
import { getElementType as getElementType22 } from "@fluentui/react-bindings";
import { forwardRef as forwardRef32 } from "react";
import { CustomEyeLine, Bookmark } from "@archipro-design/icons";
import { jsx as jsx35, jsxs as jsxs16 } from "react/jsx-runtime";
var defaultRootElementType8 = "div";
var projectTileClassName = "ui-projectTile";
var projectTileSlotClassNames = {
  professionalName: `${projectTileClassName}__professional`,
  projectName: `${projectTileClassName}__project`,
  title: `${projectTileClassName}__title`,
  contentTile: `${projectTileClassName}__contentTile`,
  contentBlock: `${projectTileClassName}__contentBlock`,
  tag: `${projectTileClassName}__tag`,
  viewCount: `${projectTileClassName}__viewCount`
};
var SIZE_TO_HEIGHT_MAPPING2 = {
  "01": {
    xsmall: pxToRem(278),
    small: pxToRem(278),
    medium: pxToRem(290),
    large: pxToRem(390),
    xlarge: pxToRem(1e3),
    dynamic: "100%"
  },
  "02": {
    xsmall: pxToRem(124),
    small: pxToRem(198),
    medium: pxToRem(136),
    large: pxToRem(273.66),
    xlarge: pxToRem(273.66),
    dynamic: "100%"
  },
  "03": {
    // for member design-board saved pin page
    small: pxToRem(203),
    large: pxToRem(286),
    dynamic: "100%"
  }
};
var ProjectTile = forwardRef32(
  (props, ref) => {
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      size,
      image,
      projectName,
      professionalName,
      isFavourited,
      onFavourite,
      hideFavourite,
      adminCaption,
      topActions,
      awardIndicator,
      variant = "01",
      trackCarouselActivity,
      newDesignEnabled = false,
      viewedCount,
      savedCount,
      inViewport,
      tileImage = {
        images: image,
        topActions,
        awardIndicator,
        height: SIZE_TO_HEIGHT_MAPPING2[variant][size],
        inViewport
      },
      tag
    } = props;
    const unhandledProps = useUnhandledProps32(
      ProjectTile.handledProps,
      props
    );
    const context = useFluentContext34();
    const { setStart, setEnd } = useTelemetry34(
      ProjectTile.displayName,
      context.telemetry
    );
    setStart();
    const getA11yProps = useAccessibility30(
      accessibility,
      {
        debugName: ProjectTile.displayName,
        rtl: context.rtl
      }
    );
    const displayCounts = !!viewedCount || !!savedCount;
    const { classes } = useStyles34(
      ProjectTile.displayName,
      {
        className: projectTileClassName,
        mapPropsToStyles: () => ({
          size,
          variant,
          newDesignEnabled
        }),
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const ElementType = getElementType22(props);
    const imageHeight = SIZE_TO_HEIGHT_MAPPING2[variant][size];
    const viewCountFormat = (count) => {
      if (count < 1e3) {
        return count.toString();
      } else {
        const roundedCount = Math.round(count / 100) / 10;
        const nearestThousands = roundedCount % 1 === 0 ? roundedCount.toFixed(0) : roundedCount.toFixed(1);
        return `${nearestThousands}K`;
      }
    };
    const result = /* @__PURE__ */ jsxs16(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: [
          TileImage.create(tileImage, {
            defaultProps: () => {
              return {
                size,
                images: image,
                height: imageHeight,
                isFavourited,
                onFavourite,
                hideFavourite,
                trackCarouselActivity
              };
            }
          }),
          displayCounts && /* @__PURE__ */ jsxs16(Flex, { className: projectTileSlotClassNames.viewCount, children: [
            !!savedCount && Button.create(
              { children: viewCountFormat(savedCount) },
              {
                defaultProps: () => ({
                  icon: /* @__PURE__ */ jsx35(Bookmark, {}),
                  variant: "text",
                  variables: { buttonPadding: 8 }
                })
              }
            ),
            !!viewedCount && Button.create(
              { children: viewCountFormat(viewedCount) },
              {
                defaultProps: () => ({
                  icon: /* @__PURE__ */ jsx35(CustomEyeLine, {}),
                  variant: "text",
                  variables: { buttonPadding: 8 }
                })
              }
            )
          ] }),
          /* @__PURE__ */ jsxs16(
            Flex,
            {
              className: projectTileSlotClassNames.contentBlock,
              column: false,
              children: [
                /* @__PURE__ */ jsxs16(
                  Flex,
                  {
                    column: true,
                    className: projectTileSlotClassNames.contentTile,
                    children: [
                      Label.create(projectName, {
                        generateKey: true,
                        defaultProps: () => {
                          return {
                            variant: newDesignEnabled ? "03-alt" : "04",
                            maximumLines: newDesignEnabled ? 1 : 2,
                            link: !newDesignEnabled
                          };
                        },
                        overrideProps: () => ({
                          className: mergeClassName(
                            projectName,
                            projectTileSlotClassNames.projectName
                          )
                        })
                      }),
                      Label.create(professionalName, {
                        generateKey: true,
                        defaultProps: () => {
                          return {
                            variant: "04",
                            maximumLines: 1,
                            link: !newDesignEnabled
                          };
                        },
                        overrideProps: () => ({
                          className: mergeClassName(
                            professionalName,
                            projectTileSlotClassNames.professionalName
                          )
                        })
                      }),
                      adminCaption
                    ]
                  }
                ),
                Label.create(tag, {
                  generateKey: true,
                  defaultProps: () => {
                    return {
                      className: projectTileSlotClassNames.tag,
                      variant: "02",
                      link: false,
                      uc: false,
                      weight: "medium"
                    };
                  }
                })
              ]
            }
          )
        ]
      }
    );
    setEnd();
    return result;
  }
);
ProjectTile.displayName = "ProjectTile";
ProjectTile.create = createShorthandFactory32({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ProjectTile
});
ProjectTile.defaultProps = {
  as: defaultRootElementType8,
  accessibility: cardBehavior4,
  size: "small",
  isFavourited: false
};
ProjectTile.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "size",
  "projectName",
  "professionalName",
  "image",
  "variant",
  "tileImage",
  "trackCarouselActivity",
  "newDesignEnabled",
  "awardIndicator",
  "viewedCount",
  "savedCount",
  "inViewport",
  ...favouriteHandledProps,
  ...adminTileHandledProps
];
function mapImageSizeToDimensions({ size }) {
  const map = {
    height: {
      small: pxToRem(288),
      medium: pxToRem(390),
      large: pxToRem(1e3)
    }
  };
  const height = size ? map.height[size] : map.height["small"];
  return {
    height,
    width: "100%"
  };
}

// packages/aria/src/components/Tile/ProfessionalTile/ProfessionalTile.tsx
import { forwardRef as forwardRef35 } from "react";
import {
  useFluentContext as useFluentContext37,
  getElementType as getElementType25,
  useAccessibility as useAccessibility33,
  useTelemetry as useTelemetry37,
  useStyles as useStyles37,
  useUnhandledProps as useUnhandledProps35,
  cardBehavior as cardBehavior6,
  createShorthandFactory as createShorthandFactory35
} from "@fluentui/react-northstar";

// packages/aria/src/components/Avatar/Avatar.tsx
import { forwardRef as forwardRef33 } from "react";
import {
  useFluentContext as useFluentContext35,
  getElementType as getElementType23,
  useAccessibility as useAccessibility31,
  useTelemetry as useTelemetry35,
  useStyles as useStyles35,
  useUnhandledProps as useUnhandledProps33,
  imageBehavior as imageBehavior4,
  createShorthandFactory as createShorthandFactory33
} from "@fluentui/react-northstar";

// packages/aria/src/utils/isCompVarsPrep.ts
var isCompVarsPrep = (_14) => false;

// packages/aria/src/components/Avatar/Avatar.tsx
import { jsx as jsx36, jsxs as jsxs17 } from "react/jsx-runtime";
var defaultRootElementType9 = "div";
var avatarClassName = "ui-avatar";
var avatarSlotClassNames = {
  imageContainer: `${avatarClassName}__image-container`,
  image: `${avatarClassName}__image`,
  initials: `${avatarClassName}__initials`
};
var Avatar = forwardRef33((props, ref) => {
  const context = useFluentContext35();
  const { setStart, setEnd } = useTelemetry35(
    Avatar.displayName,
    context.telemetry
  );
  setStart();
  const {
    className,
    styles,
    variables,
    accessibility,
    design,
    size,
    shape,
    initials,
    icon,
    image,
    backgroundColor,
    multiply
  } = props;
  const getA11yProps = useAccessibility31(
    accessibility,
    {
      debugName: Avatar.displayName,
      rtl: context.rtl
    }
  );
  const { classes } = useStyles35(Avatar.displayName, {
    className: avatarClassName,
    mapPropsToStyles: () => ({
      size,
      shape,
      backgroundColor,
      multiply
    }),
    mapPropsToInlineStyles: () => ({
      className,
      styles,
      variables: {
        ...variables,
        showBorder: !!image
      },
      design
    }),
    rtl: context.rtl,
    unstable_props: props
  });
  const unhandledProps = useUnhandledProps33(Avatar.handledProps, props);
  const initialsMap = {
    28: { Component: Caption, override: { variant: "04" } },
    32: { Component: Caption, override: { variant: "03" } },
    40: { Component: Caption, override: { variant: "03" } },
    42: { Component: Caption, override: { variant: "03" } },
    48: { Component: Label, override: { variant: "01" } },
    64: { Component: Subheader, override: { variant: "03" } },
    72: { Component: Header, override: { variant: "05", as: "h5" } },
    80: { Component: Header, override: { variant: "05", as: "h5" } },
    90: { Component: Header, override: { variant: "05", as: "h5" } },
    106: { Component: Header, override: { variant: "04", as: "h4" } },
    113: { Component: Header, override: { variant: "04", as: "h4" } },
    133: { Component: Header, override: { variant: "04", as: "h4" } },
    150: { Component: Header, override: { variant: "04", as: "h4" } },
    214: { Component: Header, override: { variant: "02", as: "h3" } },
    230: { Component: Header, override: { variant: "02", as: "h3" } },
    235: { Component: Header, override: { variant: "02", as: "h3" } }
  };
  const { Component, override } = size ? initialsMap[size] : initialsMap[34];
  const initialsComponent = Component.create(initials, {
    overrideProps: () => {
      return { ...override, className: avatarSlotClassNames.initials };
    }
  });
  const ElementType = getElementType23(props);
  if (isCompVarsPrep(variables)) {
    throw new Error("infallible");
  }
  const result = /* @__PURE__ */ jsxs17(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      children: [
        initials && !image && !icon && size && initialsComponent,
        image && !icon && /* @__PURE__ */ jsx36("div", { className: avatarSlotClassNames.imageContainer, children: Image.create(image, {
          defaultProps: () => ({
            className: avatarSlotClassNames.image,
            height: size ?? 32,
            width: size ?? 32,
            layout: "fill",
            objectFit: "contain",
            loading: "lazy",
            variables: {
              placeholderBackgroundColorLight: variables?.avatarImageBackgroundColor,
              placeholderBackgroundColorDark: variables?.avatarImageBackgroundColor
            }
          })
        }) }),
        icon && !initials && !image && icon
      ]
    }
  );
  setEnd();
  return result;
});
Avatar.displayName = "Avatar";
Avatar.create = createShorthandFactory33({
  Component: Avatar,
  mappedProp: "image"
});
Avatar.defaultProps = {
  as: defaultRootElementType9,
  accessibility: imageBehavior4,
  size: 32,
  shape: "circular"
  //active: 'unset',
  //activeAppearance: 'ring',
};
Avatar.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  // Extra props that are handled
  "initials",
  "icon",
  "badge",
  "image",
  "imageFit",
  //'active',
  //'activeAppearance',
  "shape",
  "size",
  "backgroundColor",
  "multiply"
];

// packages/aria/src/components/Tile/ProfessionalTile/ProfessionalTile.tsx
import cx10 from "classnames";

// packages/aria/src/components/Review/Review.tsx
import { forwardRef as forwardRef34 } from "react";
import {
  useFluentContext as useFluentContext36,
  getElementType as getElementType24,
  useAccessibility as useAccessibility32,
  useTelemetry as useTelemetry36,
  useStyles as useStyles36,
  useUnhandledProps as useUnhandledProps34,
  cardBehavior as cardBehavior5,
  createShorthandFactory as createShorthandFactory34
} from "@fluentui/react-northstar";
import { StarHalfSLine, StarSFill, StarSLine } from "@archipro-design/icons";
import { jsx as jsx37, jsxs as jsxs18 } from "react/jsx-runtime";
var defaultRootElementType10 = "div";
var reviewClassName = "ui-review";
var Review = forwardRef34((props, ref) => {
  const context = useFluentContext36();
  const { setStart, setEnd } = useTelemetry36(
    Review.displayName,
    context.telemetry
  );
  setStart();
  const {
    className,
    styles,
    variables,
    accessibility,
    design,
    stars,
    totalStars = 5,
    reviewCount
  } = props;
  const getA11yProps = useAccessibility32(
    accessibility,
    {
      debugName: Review.displayName,
      rtl: context.rtl
    }
  );
  const { classes } = useStyles36(Review.displayName, {
    className: reviewClassName,
    mapPropsToInlineStyles: () => ({
      className,
      styles,
      variables,
      design
    }),
    rtl: context.rtl,
    unstable_props: props
  });
  const unhandledProps = useUnhandledProps34(Review.handledProps, props);
  const ElementType = getElementType24(props);
  const result = /* @__PURE__ */ jsx37(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      children: /* @__PURE__ */ jsxs18(Flex, { vAlign: "center", children: [
        Array.from(
          { length: totalStars },
          (x, i) => i
        ).map((i) => {
          const nearestDivison = Math.round(stars * 2) / 2;
          const fillIcon = i - nearestDivison === 0.5 ? /* @__PURE__ */ jsx37(StarHalfSLine, {}, `half-star-${i}`) : /* @__PURE__ */ jsx37(StarSFill, {}, `fill-star-${i}`);
          return stars > i - 1 ? fillIcon : /* @__PURE__ */ jsx37(StarSLine, {}, `empty-star-${i}`);
        }),
        Caption.create(reviewCount, {
          defaultProps: () => {
            return { variant: "03" };
          },
          render: (Component, props2) => {
            const { children, ...rest } = props2;
            return /* @__PURE__ */ jsxs18(Component, { ...rest, children: [
              "`($",
              children,
              " reviews)`"
            ] });
          }
        })
      ] })
    }
  );
  setEnd();
  return result;
});
Review.displayName = "Review";
Review.create = createShorthandFactory34({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: Review
});
Review.defaultProps = {
  as: defaultRootElementType10,
  accessibility: cardBehavior5
};
Review.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  // Extra props that are handled
  "stars",
  "totalStars",
  "reviewCount"
];

// packages/aria/src/components/Tile/ProfessionalTile/ProfessionalTile.tsx
import { Fragment as Fragment4, jsx as jsx38, jsxs as jsxs19 } from "react/jsx-runtime";
var defaultRootElementType11 = "div";
var professionalTileClassName = "ui-professionalTile";
var professionalTileSlotClassNames = {
  content: `${professionalTileClassName}__content`,
  title: `${professionalTileClassName}__title`,
  logo: `${professionalTileClassName}__logo`,
  category: `${professionalTileClassName}__category`,
  location: `${professionalTileClassName}__location`,
  servicingArea: `${professionalTileClassName}__servicingArea`,
  services: `${professionalTileClassName}__services`,
  service: `${professionalTileClassName}__service`,
  serviceMore: `${professionalTileClassName}__serviceMore`,
  memberSince: `${professionalTileClassName}__memberSince`,
  headerWrap: `${professionalTileClassName}__headerWrap`,
  textWrap: `${professionalTileClassName}__textWrap`,
  tag: `${professionalTileClassName}__tag`,
  review: `${professionalTileClassName}__review`
};
var SIZE_TO_HEIGHT_MAPPING3 = {
  "01": {
    xsmall: pxToRem(128),
    small: pxToRem(220),
    medium: pxToRem(231),
    large: pxToRem(303),
    xlarge: pxToRem(375),
    dynamic: "100%"
  },
  "02": {
    xsmall: pxToRem(124),
    small: pxToRem(124),
    medium: pxToRem(136),
    large: pxToRem(276),
    xlarge: pxToRem(276),
    dynamic: "100%"
  },
  "03": {
    small: pxToRem(203),
    medium: pxToRem(286),
    large: pxToRem(464),
    dynamic: "100%"
  },
  "04": {
    xsmall: pxToRem(128),
    small: pxToRem(220),
    medium: pxToRem(231),
    large: pxToRem(303),
    xlarge: pxToRem(375),
    dynamic: "100%"
  }
};
var SIZE_TO_AVATAR_MAPPING = {
  "01": {
    xsmall: 64,
    small: 106,
    medium: 113,
    large: 150,
    xlarge: 150,
    dynamic: 72
  },
  "02": {
    xsmall: 32,
    small: 32,
    medium: 32,
    large: 64,
    xlarge: 64,
    dynamic: 72
  },
  "03": {
    small: 64,
    medium: 64,
    large: 90,
    dynamic: 90
  },
  "04": {
    xsmall: 64,
    small: 106,
    medium: 113,
    large: 150,
    xlarge: 150,
    dynamic: 72
  }
};
var ProfessionalTile = forwardRef35(
  (props, ref) => {
    const context = useFluentContext37();
    const { setStart, setEnd } = useTelemetry37(
      ProfessionalTile.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      image,
      size,
      logo,
      title,
      category,
      memberSince,
      review,
      isFavourited,
      onFavourite,
      hideFavourite,
      topActions,
      adminCaption,
      variant = "01",
      awardIndicator,
      tileImage = {
        images: image,
        topActions,
        awardIndicator,
        height: SIZE_TO_HEIGHT_MAPPING3[variant][size]
      },
      location,
      servicingArea,
      services,
      tag,
      tagsToShowInFull = 2
    } = props;
    const getA11yProps = useAccessibility33(
      accessibility,
      {
        debugName: ProfessionalTile.displayName,
        rtl: context.rtl
      }
    );
    const imageHeight = SIZE_TO_HEIGHT_MAPPING3[variant][size];
    const avatarSize = SIZE_TO_AVATAR_MAPPING[variant][size];
    const { classes } = useStyles37(
      ProfessionalTile.displayName,
      {
        className: professionalTileClassName,
        mapPropsToStyles: () => ({
          size,
          avatarSize,
          tagsToShowInFull
        }),
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps35(
      ProfessionalTile.handledProps,
      props
    );
    const ElementType = getElementType25(props);
    const eleMemberSince = Caption.create(memberSince, {
      generateKey: true,
      defaultProps: () => {
        return {
          className: professionalTileSlotClassNames.memberSince,
          variant: "05",
          uc: true,
          design: {
            display: "inline"
          }
        };
      },
      render: (Component, props2) => {
        const { children } = props2;
        if (!memberSince) return null;
        return /* @__PURE__ */ jsx38(Component, { ...props2, children: ` - ${children}` });
      }
    });
    const customFontSizeCaption = {
      variables: {
        ...size === "xsmall" && variant === "01" && { fontSize: pxToRem(10) }
      }
    };
    const displayServices = variant === "04" && services && services.length > tagsToShowInFull ? services.slice(0, tagsToShowInFull) : services;
    const result = /* @__PURE__ */ jsxs19(
      ElementType,
      {
        ...getA11yProps("root", {
          className: cx10([
            classes["root"],
            classes[`variant${variant}`]
          ]),
          ref,
          ...unhandledProps
        }),
        children: [
          TileImage.create(tileImage, {
            defaultProps: () => {
              return {
                images: image,
                isFavourited,
                onFavourite,
                hideFavourite,
                height: imageHeight,
                overlay: {
                  variables: {
                    ...variant === "02" && {
                      padding: pxToRem(8)
                    }
                  }
                }
              };
            }
          }),
          /* @__PURE__ */ jsxs19(Flex, { className: professionalTileSlotClassNames.content, children: [
            /* @__PURE__ */ jsxs19(
              Flex,
              {
                column: false,
                className: professionalTileSlotClassNames.headerWrap,
                children: [
                  Avatar.create(logo, {
                    generateKey: true,
                    defaultProps: () => {
                      return {
                        size: avatarSize,
                        className: professionalTileSlotClassNames.logo,
                        shape: "square"
                      };
                    }
                  }),
                  Label.create(tag, {
                    generateKey: true,
                    defaultProps: () => {
                      return {
                        className: professionalTileSlotClassNames.tag,
                        variant: "02",
                        link: false,
                        uc: false,
                        weight: "medium"
                      };
                    }
                  })
                ]
              },
              "headerWrap"
            ),
            /* @__PURE__ */ jsxs19(
              Flex,
              {
                column: true,
                className: professionalTileSlotClassNames.textWrap,
                children: [
                  Label.create(title, {
                    generateKey: true,
                    defaultProps: () => {
                      return {
                        className: professionalTileSlotClassNames.title,
                        variant: "04",
                        link: variant !== "04",
                        uc: variant !== "04",
                        weight: "medium"
                      };
                    }
                  }),
                  Caption.create(category, {
                    generateKey: true,
                    defaultProps: () => {
                      return {
                        className: professionalTileSlotClassNames.category,
                        variant: variant === "04" ? "03" : "05",
                        uc: variant !== "04",
                        key: "category",
                        ...customFontSizeCaption
                      };
                    },
                    render: (Component, props2) => {
                      const { children } = props2;
                      return /* @__PURE__ */ jsx38(Component, { ...props2, children: /* @__PURE__ */ jsxs19(Fragment4, { children: [
                        `${children}`,
                        variant !== "04" ? eleMemberSince : null
                      ] }) });
                    }
                  }),
                  Caption.create(location, {
                    generateKey: true,
                    defaultProps: () => {
                      return {
                        className: professionalTileSlotClassNames.location,
                        variant: variant === "04" ? "03" : "05",
                        uc: variant !== "04",
                        key: "location",
                        ...customFontSizeCaption
                      };
                    }
                  }),
                  Caption.create(servicingArea, {
                    generateKey: true,
                    defaultProps: () => {
                      return {
                        className: professionalTileSlotClassNames.servicingArea,
                        variant: variant === "04" ? "03" : "05",
                        uc: variant !== "04",
                        key: "servicingArea",
                        ...customFontSizeCaption
                      };
                    }
                  }),
                  displayServices?.length ? /* @__PURE__ */ jsxs19(
                    Caption,
                    {
                      maximumLines: 1,
                      className: professionalTileSlotClassNames.services,
                      children: [
                        displayServices.map(
                          (service, index) => Caption.create(service, {
                            generateKey: true,
                            defaultProps: () => {
                              return {
                                className: professionalTileSlotClassNames.service,
                                variant: variant === "04" ? "04" : "05",
                                ...customFontSizeCaption
                              };
                            },
                            render: (Component, props2) => {
                              const { children } = props2;
                              return /* @__PURE__ */ jsxs19(
                                Component,
                                {
                                  ...props2,
                                  design: {
                                    display: variant === "04" ? "inline-block" : "inline"
                                  },
                                  children: [
                                    variant !== "04" && index > 0 ? ", " : "",
                                    children
                                  ]
                                }
                              );
                            }
                          })
                        ),
                        variant === "04" && services && services.length > 2 && Caption.create(
                          `+ ${services.length - tagsToShowInFull} more`,
                          {
                            generateKey: true,
                            defaultProps: () => {
                              return {
                                className: professionalTileSlotClassNames.serviceMore,
                                variant: "04"
                              };
                            }
                          }
                        )
                      ]
                    }
                  ) : null
                ]
              },
              "textWrap"
            ),
            Review.create(review, {
              defaultProps: () => {
                return {
                  className: professionalTileSlotClassNames.review
                };
              }
            }),
            adminCaption
          ] })
        ]
      }
    );
    setEnd();
    return result;
  }
);
ProfessionalTile.displayName = "ProfessionalTile";
ProfessionalTile.create = createShorthandFactory35({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ProfessionalTile
});
ProfessionalTile.defaultProps = {
  as: defaultRootElementType11,
  accessibility: cardBehavior6,
  size: "small",
  isFavourited: false
};
ProfessionalTile.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  // Extra props that are handled
  "title",
  "category",
  "memberSince",
  "size",
  "image",
  "logo",
  "review",
  "variant",
  "tileImage",
  "servicingArea",
  "awardIndicator",
  "tagsToShowInFull",
  ...favouriteHandledProps,
  ...adminTileHandledProps
];

// packages/aria/src/components/Tile/ArchizeenTile/ArchizeenTile.tsx
import { forwardRef as forwardRef37 } from "react";
import {
  useFluentContext as useFluentContext39,
  getElementType as getElementType27,
  useAccessibility as useAccessibility35,
  useTelemetry as useTelemetry39,
  useStyles as useStyles39,
  useUnhandledProps as useUnhandledProps37,
  cardChildrenFocusableBehavior,
  createShorthandFactory as createShorthandFactory37
} from "@fluentui/react-northstar";

// packages/aria/src/components/VideoPlayer/VideoPlayer.tsx
import { forwardRef as forwardRef36, useEffect as useEffect5, useRef as useRef5, useState as useState4 } from "react";
import FilePlayer from "react-player/file.js";
import YouTubePlayer from "react-player/youtube.js";
import VimeoPlayer from "react-player/vimeo.js";
import {
  useFluentContext as useFluentContext38,
  getElementType as getElementType26,
  useAccessibility as useAccessibility34,
  useTelemetry as useTelemetry38,
  useStyles as useStyles38,
  useUnhandledProps as useUnhandledProps36,
  createShorthandFactory as createShorthandFactory36,
  Animation as Animation3
} from "@fluentui/react-northstar";
import { useDebounce } from "ahooks";
import { PlayFill, CloseLine as CloseLine2, Loader as Loader2 } from "@archipro-design/icons";

// packages/aria/src/hooks/useForkRef.ts
import React9 from "react";
function useForkRef(refA, refB) {
  return React9.useMemo(() => {
    if (refA === null && refB === null) {
      return null;
    }
    return (refValue) => {
      setRef(refA, refValue);
      setRef(refB, refValue);
    };
  }, [refA, refB]);
}
function setRef(ref, value) {
  if (typeof ref === "function") {
    ref(value);
  } else if (ref) {
    ref.current = value;
  }
}

// packages/aria/src/components/Dialog/Dialog.tsx
import {
  Dialog,
  dialogClassName,
  dialogSlotClassNames
} from "@fluentui/react-northstar";

// packages/aria/src/utils/deviceType.tsx
import { useMemo as useMemo3 } from "react";
import { useContext as useContext2, createContext as createContext3 } from "react";
import { jsx as jsx39 } from "react/jsx-runtime";
var DeviceTypeContext = createContext3(void 0);
function DeviceTypeContextProvider({
  device = "desktop",
  children
}) {
  const deviceType = useMemo3(
    () => ({
      mobile: device === "mobile",
      tablet: device === "tablet",
      desktop: device === "desktop"
    }),
    [device]
  );
  return /* @__PURE__ */ jsx39(DeviceTypeContext.Provider, { value: deviceType, children });
}
function useDeviceTypeContext() {
  const ctx = useContext2(DeviceTypeContext);
  if (typeof ctx === "undefined") {
    throw new Error("useDeviceType must be used within the provider.");
  }
  return ctx;
}

// packages/aria/src/components/VideoPlayer/RestyledPlayIcon.tsx
import { jsx as jsx40, jsxs as jsxs20 } from "react/jsx-runtime";
var StyledPlayIcon = () => {
  const deviceType = useDeviceTypeContext();
  const isMobile = !!deviceType?.mobile;
  const containerSize = isMobile ? pxToRem(88) : pxToRem(162);
  const triangleSize = isMobile ? 15 : 24;
  const triangleWidth = isMobile ? 25 : 40;
  const triangleMargin = isMobile ? 5 : 8;
  const containerStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: containerSize,
    height: containerSize,
    cursor: "pointer",
    transition: "all 0.2s ease-in-out"
  };
  const triangleStyle = {
    width: 0,
    height: 0,
    borderTop: `${triangleSize}px solid transparent`,
    borderBottom: `${triangleSize}px solid transparent`,
    borderLeft: `${triangleWidth}px solid white`,
    marginLeft: `${triangleMargin}px`,
    position: "absolute",
    zIndex: 1
    // Ensure it's above the blur layer
  };
  const backgroundStyle = {
    backdropFilter: "blur(13.7px)",
    width: containerSize,
    height: containerSize,
    position: "absolute",
    top: "0",
    left: "0",
    backgroundColor: "rgba(216, 127, 92, 0.7)",
    transition: "all 0.2s ease-in-out",
    borderRadius: "50%"
  };
  const handleMouseEnter = (e) => {
    e.currentTarget.style.transform = "translate(-50%, -50%)";
    const bgElement = e.currentTarget.querySelector(
      "div:first-child"
    );
    if (bgElement) {
      bgElement.style.backgroundColor = "rgba(160, 94, 68, 0.5)";
    }
  };
  const handleMouseLeave = (e) => {
    e.currentTarget.style.transform = "translate(-50%, -50%)";
    const bgElement = e.currentTarget.querySelector(
      "div:first-child"
    );
    if (bgElement) {
      bgElement.style.backgroundColor = "rgba(216, 127, 92, 0.7)";
    }
  };
  return /* @__PURE__ */ jsxs20(
    "div",
    {
      style: containerStyle,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      children: [
        /* @__PURE__ */ jsx40("div", { style: backgroundStyle, "aria-hidden": "true" }),
        /* @__PURE__ */ jsx40("div", { style: triangleStyle, "aria-hidden": "true" })
      ]
    }
  );
};

// packages/aria/src/components/VideoPlayer/VideoPlayErrorMessage.tsx
import { Fragment as Fragment5, jsx as jsx41, jsxs as jsxs21 } from "react/jsx-runtime";
var VideoPlayErrorMessage = () => /* @__PURE__ */ jsxs21(Fragment5, { children: [
  /* @__PURE__ */ jsx41(Header, { variant: "04", children: "Sorry" }),
  /* @__PURE__ */ jsx41(Subheader, { variant: "02", as: "span", children: "We can\u2019t play this video because it\u2019s no longer available." })
] });

// packages/aria/src/components/VideoPlayer/VideoPlayer.tsx
import { Fragment as Fragment6, jsx as jsx42, jsxs as jsxs22 } from "react/jsx-runtime";
var MATCH_URL_YOUTUBE = /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:embed\/|v\/|watch\/|watch\?v=|watch\?.+&v=|shorts\/))((\w|-){11})|youtube\.com\/playlist\?list=|youtube\.com\/user\//;
var MATCH_URL_VIMEO = /vimeo\.com\/(?!progressive_redirect).+/;
var videoPlayerClassName = "ui-videoPlayer";
var VIDEO_TYPE = ["file", "youtube", "vimeo", "unknown"];
var KNOWN_PRIVATE_VIDEO_ERROR_CODES = ["150", "101"];
var defaultRootElementType12 = "div";
var VideoPlayer = forwardRef36(
  (props, ref) => {
    const context = useFluentContext38();
    const { setStart, setEnd } = useTelemetry38(
      VideoPlayer.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      design,
      styles,
      variables,
      accessibility,
      src,
      width,
      height,
      playing,
      disabled,
      controls,
      loop,
      muted,
      playsInline,
      coverImage,
      playIcon,
      onStart,
      onPlay,
      trackPlayEvent,
      onPause,
      onEnded,
      onReady,
      onCoverLoadComplete,
      onClickPreview,
      onClose,
      closeIcon,
      playOnMouseOver,
      playOnMouseOverDelay = 500,
      defaultError,
      alt,
      isCoverGrayscale = false
    } = props;
    const getA11yProps = useAccessibility34(
      accessibility,
      {
        debugName: VideoPlayer.displayName,
        rtl: context.rtl
      }
    );
    const unhandledProps = useUnhandledProps36(
      VideoPlayer.handledProps,
      props
    );
    const ElementType = getElementType26(props);
    const [shouldPlay, setShouldPlay] = useState4(!disabled && playing);
    const [videoReady, setVideoReady] = useState4(false);
    const [showPlayError, setShowPlayError] = useState4(false);
    const layoutRef = useRef5(null);
    const isPlayEventTracked = useRef5(false);
    const [mouseOver, setMouseOver] = useState4(false);
    const debouncedMouseOver = useDebounce(mouseOver, {
      wait: playOnMouseOverDelay
    });
    useEffect5(() => {
      if (!playOnMouseOver || disabled) return;
      setShouldPlay(debouncedMouseOver);
    }, [playOnMouseOver, debouncedMouseOver, disabled]);
    const { classes } = useStyles38(
      VideoPlayer.displayName,
      {
        className: videoPlayerClassName,
        mapPropsToInlineStyles: () => ({
          className,
          design,
          styles,
          variables,
          width,
          height
        }),
        mapPropsToStyles: () => ({
          width,
          height,
          shouldPlay,
          videoReady,
          playsInline,
          debouncedMouseOver,
          mouseOver,
          playError: showPlayError
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    useEffect5(() => {
      if (disabled) return;
      setShouldPlay(playing);
    }, [playing, disabled]);
    useEffect5(() => {
      isPlayEventTracked.current = false;
      setShowPlayError(false);
    }, [src]);
    const [loading, setLoading] = useState4(false);
    const dismissPlayError = () => {
      setShowPlayError(false);
      setShouldPlay(false);
      setLoading(false);
    };
    const playErrorClassName = classes[coverImage ? "playErrorCover" : "playError"];
    const renderPlayError = () => {
      if (!showPlayError) {
        return null;
      }
      return /* @__PURE__ */ jsxs22("div", { className: playErrorClassName, children: [
        coverImage && /* @__PURE__ */ jsx42(
          "button",
          {
            type: "button",
            className: classes["playErrorClose"],
            "aria-label": "Close",
            onClick: (e) => {
              e.stopPropagation();
              dismissPlayError();
            },
            children: /* @__PURE__ */ jsx42("span", { className: classes["playErrorCloseIcon"], children: closeIcon })
          }
        ),
        defaultError ?? /* @__PURE__ */ jsx42(VideoPlayErrorMessage, {})
      ] });
    };
    function isURL(src2) {
      return typeof src2 === "string";
    }
    const videoEmbed = isURL(src) ? getVideoEmbedFromURL(src) : getVideoEmbedFromVideoID(src.videoID, src.type);
    const videoProps = {
      width: width && playsInline ? pxToRem(width) : "100%",
      height: height && playsInline ? pxToRem(height) : "100%",
      loop: !!loop,
      muted: !!muted,
      playsInline: !!playsInline,
      controls: !!controls
    };
    const reactPlayerProps = {
      ...videoProps,
      playing: !!shouldPlay,
      light: !shouldPlay,
      onStart,
      onPlay: () => {
        if (!isPlayEventTracked.current) {
          trackPlayEvent?.();
          isPlayEventTracked.current = true;
        }
        onPlay?.();
        setLoading(false);
      },
      onPause: () => {
        onPause?.();
        setLoading(false);
      },
      onEnded,
      onReady: () => {
        setVideoReady(true);
        onReady?.();
      },
      onClickPreview: () => {
        if (disabled) return;
        if (onClickPreview) {
          onClickPreview();
        } else {
          setShouldPlay(true);
        }
      },
      onError: (e) => {
        if (videoEmbed.type === "youtube" && !KNOWN_PRIVATE_VIDEO_ERROR_CODES.includes(
          e
        )) {
          setShowPlayError(true);
        }
      }
    };
    if (isCompVarsPrep(variables)) {
      throw new Error("noop");
    }
    const PlayIcon = isCoverGrayscale ? /* @__PURE__ */ jsx42(StyledPlayIcon, {}) : /* @__PURE__ */ jsx42(PlayFill, { className: classes["playIcon"] });
    const renderVideoCover = () => {
      return /* @__PURE__ */ jsx42(
        Box,
        {
          className: classes["videoCover"],
          onClick: () => {
            if (disabled || showPlayError) return;
            if (onClickPreview) {
              onClickPreview();
            } else {
              setShouldPlay(!shouldPlay);
            }
            setLoading(true);
          },
          children: Image.create(coverImage, {
            overrideProps: () => ({
              layout: "fill"
            }),
            defaultProps: () => ({
              width: width ? pxToRem(width) : "100%",
              height: height ? pxToRem(height) : "100%",
              alt: alt ?? "",
              styles: isCoverGrayscale ? {
                ["& img"]: {
                  filter: "grayscale(80%)"
                }
              } : void 0,
              overlay: showPlayError ? void 0 : {
                alwaysVisible: true,
                icon: !loading ? playIcon ?? PlayIcon : /* @__PURE__ */ jsx42(
                  "div",
                  {
                    className: classes["animationContainer"],
                    children: /* @__PURE__ */ jsx42(Animation3, { name: "rotateUltraSlow", children: /* @__PURE__ */ jsx42(
                      Loader2,
                      {
                        className: classes["spinning"]
                      }
                    ) })
                  }
                ),
                variables: {
                  iconSizeDesktop: variables?.playIconSizeDesktop ?? 190,
                  iconSizeMobile: variables?.playIconSizeMobile ?? 190
                }
              },
              onLoadingComplete: onCoverLoadComplete
            })
          })
        }
      );
    };
    const renderVideo = () => {
      switch (videoEmbed.type) {
        case "file":
          return /* @__PURE__ */ jsx42(
            FilePlayer,
            {
              url: videoEmbed.src,
              ...reactPlayerProps
            }
          );
        case "youtube":
          return /* @__PURE__ */ jsx42(
            YouTubePlayer,
            {
              url: videoEmbed.src,
              ...reactPlayerProps
            }
          );
        case "vimeo":
          return /* @__PURE__ */ jsx42(
            VimeoPlayer,
            {
              url: videoEmbed.src,
              ...reactPlayerProps
            }
          );
        default:
          return null;
      }
    };
    const onVideoClose = () => {
      dismissPlayError();
      onClose?.();
    };
    const result = /* @__PURE__ */ jsxs22(
      ElementType,
      {
        ref: useForkRef(ref, layoutRef),
        ...getA11yProps("root", {
          className: classes["root"],
          ...unhandledProps
        }),
        ...showPlayError ? { "data-play-error": true } : {},
        onMouseOver: () => {
          if (playOnMouseOver) {
            setMouseOver(true);
          }
        },
        onMouseLeave: () => {
          if (playOnMouseOver) {
            setMouseOver(false);
          }
        },
        children: [
          coverImage && renderVideoCover(),
          playsInline ? /* @__PURE__ */ jsxs22(Fragment6, { children: [
            renderPlayError(),
            !showPlayError && renderVideo()
          ] }) : /* @__PURE__ */ jsx42(
            Dialog,
            {
              className: classes["videoModal"],
              styles: { maxWidth: "100vw", maxHeight: "100vh" },
              variables: {
                overlayBackground: "rgba(30,30,30,.9)",
                rootBackground: "transparent",
                rootWidth: "100%",
                rootHeight: "100%",
                border: "0",
                contentTopBorderColor: "transparent",
                rootPadding: "0",
                overflow: false
              },
              open: shouldPlay,
              onOpen: () => {
                setShouldPlay(true);
                setShowPlayError(false);
                setLoading(false);
              },
              onCancel: onVideoClose,
              backdrop: true,
              headerAction: {
                icon: /* @__PURE__ */ jsx42("span", { className: classes["closeIcon"], children: closeIcon }),
                title: "Close",
                onClick: (e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onVideoClose();
                }
              },
              content: /* @__PURE__ */ jsx42(Box, { children: showPlayError ? renderPlayError() : renderVideo() })
            }
          )
        ]
      }
    );
    setEnd();
    return result;
  }
);
VideoPlayer.displayName = "VideoPlayer";
VideoPlayer.create = createShorthandFactory36({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: VideoPlayer
});
VideoPlayer.defaultProps = {
  as: defaultRootElementType12,
  closeIcon: /* @__PURE__ */ jsx42(CloseLine2, {})
  // An example of accessbility behaviour is
  // import { buttonBehaviour } from '@fluentui/accessibility';
  // accessibility: FILL_ME,
};
VideoPlayer.handledProps = [
  "className",
  "accessibility",
  "disabled",
  "src",
  "width",
  "height",
  "playing",
  "controls",
  "loop",
  "muted",
  "playsInline",
  "coverImage",
  "playIcon",
  "onStart",
  "onPlay",
  "onPause",
  "onEnded",
  "onReady",
  "onCoverLoadComplete",
  "onClickPreview",
  "onClose",
  "closeIcon",
  "defaultError",
  "variables",
  "playOnMouseOver",
  "alt",
  "trackPlayEvent",
  "isCoverGrayscale"
];
function getVideoEmbedFromURL(src) {
  if (typeof src !== "string" || !src) {
    return { type: "unknown", src };
  }
  if (MATCH_URL_YOUTUBE.test(src)) {
    return { type: "youtube", src };
  }
  if (MATCH_URL_VIMEO.test(src)) {
    return { type: "vimeo", src };
  }
  const allowedVideoExtensions = ["mp4", "webm"];
  const srcSegments = src.split(".");
  if (srcSegments.length > 1) {
    const ext = srcSegments[srcSegments.length - 1];
    if (allowedVideoExtensions.includes(ext)) {
      return { type: "file", src };
    }
  }
  return { type: "unknown", src };
}
function getVideoEmbedFromVideoID(videoID, type) {
  switch (type) {
    case "vimeo":
      return {
        type: "vimeo",
        src: "https://player.vimeo.com/video/" + videoID
      };
    default:
      return {
        type: "youtube",
        src: "https://www.youtube.com/embed/" + videoID
      };
  }
}

// packages/aria/src/components/Tile/ArchizeenTile/ArchizeenTile.tsx
import { HeartFill, HeartLine } from "@archipro-design/icons";
import cx11 from "classnames";
import { jsx as jsx43, jsxs as jsxs23 } from "react/jsx-runtime";
var defaultRootElementType13 = "div";
var archizeenTileClassName = "ui-archizeenTile";
var archizeenTileSlotClassNames = {
  extraActions: `${archizeenTileClassName}__extraActions`,
  subtitle: `${archizeenTileClassName}__subtitle`,
  title: `${archizeenTileClassName}__title`,
  content: `${archizeenTileClassName}__content`,
  tag: `${archizeenTileClassName}__tag`
};
var SIZE_TO_HEIGHT_MAPPING4 = {
  "01": {
    //'square'
    xsmall: 414,
    small: 340,
    medium: 340,
    large: 700
  },
  "02": {
    // portrait
    xsmall: 336,
    small: 336,
    medium: 400,
    large: 740
  },
  "03": {
    //landscape
    xsmall: 114,
    small: 114,
    medium: 280,
    large: 504
  },
  "04": {
    //'list item'
    xsmall: 130,
    small: 120,
    medium: 136,
    large: 136
  },
  "05": {
    // for member design-board saved pin page
    small: 203,
    large: 286
  },
  "06": {
    //Films
    small: 115,
    large: 264
  },
  "07": {
    //Films desktop
    medium: 330,
    large: 504
  },
  "08": {
    // Promoted Article
    small: 371,
    // wait for mobile design
    medium: 389,
    // desktop
    large: 416
    // desktop large
  }
};
var ArchizeenTile = forwardRef37(
  (props, ref) => {
    const context = useFluentContext39();
    const { setStart, setEnd } = useTelemetry39(
      ArchizeenTile.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      image,
      title,
      subtitle,
      label,
      date,
      readTime,
      size,
      isFavourited,
      onFavourite,
      hideFavourite,
      variant = "01",
      favouritePosition = "image",
      topActions,
      extraActions,
      video,
      tag,
      appDisplayMode
    } = props;
    const getA11yProps = useAccessibility35(
      accessibility,
      {
        debugName: ArchizeenTile.displayName,
        rtl: context.rtl
      }
    );
    const imageHeight = pxToRem(SIZE_TO_HEIGHT_MAPPING4[variant][size]);
    const { classes } = useStyles39(
      ArchizeenTile.displayName,
      {
        className: archizeenTileClassName,
        mapPropsToStyles: () => ({ size, variant, imageHeight }),
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps37(
      ArchizeenTile.handledProps,
      props
    );
    const ElementType = getElementType27(props);
    const readTimeEle = Caption.create(readTime, {
      defaultProps: () => {
        return {
          variant: "05",
          uc: true,
          maximumLines: 1
        };
      }
    });
    const showHideFavourite = Boolean(hideFavourite || !onFavourite);
    const tileImageOrVideo = video && video.src && video.coverImage ? /* @__PURE__ */ jsx43("div", { className: classes["videoContainer"], children: VideoPlayer.create(video) }) : TileImage.create({
      size,
      images: [image],
      isFavourited,
      onFavourite,
      hideFavourite: favouritePosition === "card" || showHideFavourite,
      topActions,
      height: imageHeight,
      ...variant === "01" && {
        width: imageHeight
      },
      appDisplayMode
    });
    const result = /* @__PURE__ */ jsxs23(
      ElementType,
      {
        ...getA11yProps("root", {
          className: cx11([
            classes["root"],
            classes[`variant${variant}`]
          ]),
          ref,
          ...unhandledProps
        }),
        children: [
          Label.create(tag, {
            generateKey: true,
            defaultProps: () => {
              return {
                className: archizeenTileSlotClassNames.tag,
                variant: "02",
                link: false,
                uc: false,
                weight: "medium"
              };
            }
          }),
          tileImageOrVideo,
          /* @__PURE__ */ jsxs23(Flex, { column: true, className: archizeenTileSlotClassNames.content, children: [
            Caption.create(subtitle, {
              defaultProps: () => {
                return {
                  variant: "04",
                  maximumLines: 1,
                  weight: "medium"
                };
              },
              overrideProps: () => ({
                className: mergeClassName(
                  subtitle,
                  archizeenTileSlotClassNames.subtitle
                )
              })
            }),
            Subheader.create(title, {
              defaultProps: () => {
                return {
                  variant: "01",
                  maximumLines: 2,
                  link: true
                };
              },
              overrideProps: () => ({
                className: mergeClassName(
                  title,
                  archizeenTileSlotClassNames.title
                )
              })
            }),
            /* @__PURE__ */ jsxs23(Flex, { space: "between", className: classes["infoWrap"], children: [
              /* @__PURE__ */ jsxs23(
                Flex,
                {
                  column: true,
                  gap: "space-6",
                  className: classes["dateWrap"],
                  children: [
                    Caption.create(label, {
                      defaultProps: () => {
                        return {
                          variant: "05",
                          uc: true,
                          maximumLines: 1,
                          weight: "medium"
                        };
                      }
                    }),
                    /* @__PURE__ */ jsxs23(Flex, { gap: "space-6", vAlign: "center", children: [
                      Caption.create(date, {
                        defaultProps: () => {
                          return {
                            variant: "05",
                            uc: true,
                            maximumLines: 1
                          };
                        }
                      }),
                      readTimeEle && /* @__PURE__ */ jsx43("span", { className: classes["ellipse"] }),
                      readTimeEle
                    ] })
                  ]
                }
              ),
              extraActions && /* @__PURE__ */ jsx43(
                Flex,
                {
                  space: "between",
                  hAlign: "center",
                  vAlign: "center",
                  className: archizeenTileSlotClassNames.extraActions,
                  children: extraActions?.map((item) => Box.create(item))
                }
              ),
              favouritePosition === "card" && !showHideFavourite && /* @__PURE__ */ jsx43(
                Button,
                {
                  size: 18,
                  variables: {
                    buttonPadding: 8,
                    iconPadding: 0,
                    buttonRadius: "50%"
                  },
                  color: "grey",
                  onClick: (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onFavourite?.(e, isFavourited);
                  },
                  icon: isFavourited ? /* @__PURE__ */ jsx43(HeartFill, {}) : /* @__PURE__ */ jsx43(HeartLine, {})
                }
              )
            ] })
          ] })
        ]
      }
    );
    setEnd();
    return result;
  }
);
ArchizeenTile.displayName = "ArchizeenTile";
ArchizeenTile.create = createShorthandFactory37({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ArchizeenTile
});
ArchizeenTile.defaultProps = {
  as: defaultRootElementType13,
  accessibility: cardChildrenFocusableBehavior,
  size: "small",
  variant: "01",
  isFavourited: false
};
ArchizeenTile.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  // Extra props that are handled
  "image",
  "title",
  "label",
  "date",
  "readTime",
  "size",
  "favouritePosition",
  "variant",
  "extraActions",
  "video",
  ...favouriteHandledProps,
  ...adminTileHandledProps
];

// packages/aria/src/components/MegaMenu/FeaturedTile/FeaturedTileSkeleton.tsx
import { forwardRef as forwardRef38 } from "react";
import {
  useFluentContext as useFluentContext40,
  useAccessibility as useAccessibility36,
  useTelemetry as useTelemetry40,
  useStyles as useStyles40,
  useUnhandledProps as useUnhandledProps38,
  cardBehavior as cardBehavior7,
  createShorthandFactory as createShorthandFactory38,
  SkeletonLine as SkeletonLine2
} from "@fluentui/react-northstar";
import { jsx as jsx44, jsxs as jsxs24 } from "react/jsx-runtime";
var featuredTileSkeletonClassName = "ui-featuredTileSkeleton";
var featuredTileSkeletonClassNames = {
  title: `${featuredTileSkeletonClassName}__title`,
  tile: `${featuredTileSkeletonClassName}__tile`,
  header: `${featuredTileSkeletonClassName}__header`,
  subject: `${featuredTileSkeletonClassName}__subject`
};
var FeaturedTileSkeleton = forwardRef38((props, ref) => {
  const context = useFluentContext40();
  const { setStart, setEnd } = useTelemetry40(
    FeaturedTileSkeleton.displayName,
    context.telemetry
  );
  setStart();
  const { className, styles, variables, design } = props;
  const getA11yProps = useAccessibility36(cardBehavior7, {
    debugName: FeaturedTileSkeleton.displayName,
    rtl: context.rtl
  });
  const { classes } = useStyles40(
    FeaturedTileSkeleton.displayName,
    {
      className: featuredTileSkeletonClassName,
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    }
  );
  const unhandledProps = useUnhandledProps38(
    FeaturedTileSkeleton.handledProps,
    props
  );
  const result = /* @__PURE__ */ jsxs24(
    Flex,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      column: true,
      children: [
        /* @__PURE__ */ jsx44(
          SkeletonLine2,
          {
            className: featuredTileSkeletonClassNames.title,
            width: pxToRem(80),
            height: pxToRem(12)
          }
        ),
        /* @__PURE__ */ jsx44(
          SkeletonLine2,
          {
            className: featuredTileSkeletonClassNames.tile,
            width: pxToRem(280),
            height: pxToRem(280)
          }
        ),
        /* @__PURE__ */ jsx44(
          SkeletonLine2,
          {
            className: featuredTileSkeletonClassNames.header,
            width: pxToRem(200),
            height: pxToRem(12)
          }
        ),
        /* @__PURE__ */ jsx44(
          SkeletonLine2,
          {
            className: featuredTileSkeletonClassNames.subject,
            width: pxToRem(160),
            height: pxToRem(12)
          }
        )
      ]
    }
  );
  setEnd();
  return result;
});
FeaturedTileSkeleton.displayName = "FeaturedTileSkeleton";
FeaturedTileSkeleton.create = createShorthandFactory38({
  Component: FeaturedTileSkeleton
});
FeaturedTileSkeleton.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design"
];

// packages/aria/src/components/MegaMenu/FeaturedTile/FeaturedTile.tsx
import { jsxs as jsxs25 } from "react/jsx-runtime";
var featuredTileClassName = "ui-featuredTile";
var FeaturedTile = forwardRef39(
  (props, ref) => {
    const context = useFluentContext41();
    const { setStart, setEnd } = useTelemetry41(
      FeaturedTile.displayName,
      context.telemetry
    );
    setStart();
    const { className, styles, variables, design, title, tile } = props;
    const { classes } = useStyles41(
      FeaturedTile.displayName,
      {
        className: featuredTileClassName,
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps39(
      FeaturedTile.handledProps,
      props
    );
    const renderTile = () => {
      const { type, instanceProps } = tile;
      switch (type) {
        case "product":
          return ProductTile.create(
            instanceProps,
            {
              defaultProps: () => ({
                size: "small",
                variables: { smallWidth: pxToRem(308) },
                hideFavourite: true,
                variant: "01",
                newDesignEnabled: true
              })
            }
          );
        case "project":
          return ProjectTile.create(
            instanceProps,
            {
              defaultProps: () => ({
                size: "small",
                variables: { smallWidth: pxToRem(462) },
                hideFavourite: true,
                variant: "01",
                newDesignEnabled: true
              })
            }
          );
        case "professional":
          return ProfessionalTile.create(
            instanceProps,
            {
              defaultProps: () => ({
                size: "small",
                variables: { smallWidth: pxToRem(462) },
                hideFavourite: true
              })
            }
          );
        case "article":
          return ArchizeenTile.create(
            instanceProps,
            {
              defaultProps: () => ({
                size: "small",
                variables: { smallWidth: pxToRem(308) },
                hideFavourite: true
              })
            }
          );
        case "magazine":
          return ArchizeenTile.create(
            instanceProps,
            {
              defaultProps: () => ({
                size: "xsmall",
                variables: { xsmallWidth: pxToRem(308) },
                hideFavourite: true
              })
            }
          );
        case "skeleton":
          return FeaturedTileSkeleton.create(
            instanceProps
          );
        default:
          throw new Error("Unsupported Tile type:" + type);
      }
    };
    const result = /* @__PURE__ */ jsxs25(
      Flex,
      {
        className: classes["root"],
        ...unhandledProps,
        ref,
        column: true,
        children: [
          Caption.create(title, {
            defaultProps: () => ({
              className: classes["title"],
              variant: "01",
              weight: "medium",
              uc: true
            })
          }),
          renderTile()
        ]
      }
    );
    setEnd();
    return result;
  }
);
FeaturedTile.displayName = "FeaturedTile";
FeaturedTile.create = createShorthandFactory39({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: FeaturedTile,
  mappedProp: "tile"
});
FeaturedTile.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "as",
  // Extra props that are handled
  "title",
  "tile"
];

// packages/aria/src/components/MegaMenu/MegaMenu.tsx
import { jsx as jsx45, jsxs as jsxs26 } from "react/jsx-runtime";
var megaMenuClassName = "ui-megaMenu";
var MegaMenu = forwardRef40(
  (props, ref) => {
    const context = useFluentContext42();
    const { setStart, setEnd } = useTelemetry42(
      MegaMenu.displayName,
      context.telemetry
    );
    setStart();
    const [dividerHeight, setDividerHeight] = useState5("100%");
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      leftNavigationGroup,
      leftNavigationAdditionalContent,
      breadcrumbNavigation,
      featuredTile,
      featuredTileOverride
    } = props;
    const localFeaturedTileRef = useRef6();
    const featuredTileRef = useMergedRefs(
      localFeaturedTileRef,
      featuredTile?.ref
    );
    const getA11yProps = useAccessibility37(
      accessibility,
      {
        debugName: MegaMenu.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles42(
      MegaMenu.displayName,
      {
        className: megaMenuClassName,
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps40(MegaMenu.handledProps, props);
    useEffect6(() => {
      let newDividerHeight = "100%";
      if (localFeaturedTileRef.current) {
        newDividerHeight = `${localFeaturedTileRef.current.clientHeight}px`;
      }
      if (newDividerHeight !== dividerHeight)
        setDividerHeight(newDividerHeight);
    }, [
      localFeaturedTileRef.current?.clientHeight,
      dividerHeight,
      featuredTile
    ]);
    const result = /* @__PURE__ */ jsx45(
      Flex,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        gap: "space-28",
        children: /* @__PURE__ */ jsxs26(Flex, { gap: "space-16", children: [
          /* @__PURE__ */ jsxs26(Flex, { className: classes["navListContainer"], children: [
            /* @__PURE__ */ jsxs26(Flex, { column: true, children: [
              NavigationListGroup.create(leftNavigationGroup),
              Box.create(leftNavigationAdditionalContent)
            ] }),
            BreadcrumbNavigation.create(breadcrumbNavigation, {
              overrideProps: () => {
                return { variables: { dividerHeight } };
              }
            })
          ] }),
          featuredTileOverride ? featuredTileOverride : FeaturedTile.create(featuredTile, {
            overrideProps: () => ({ ref: featuredTileRef })
          })
        ] })
      }
    );
    setEnd();
    return result;
  }
);
MegaMenu.displayName = "MegaMenu";
MegaMenu.create = createShorthandFactory40({
  Component: MegaMenu
});
MegaMenu.defaultProps = {
  accessibility: menuBehavior4
};
MegaMenu.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  // Extra props that are handled
  "leftNavigationGroup",
  "breadcrumbNavigation",
  "featuredTile",
  "leftNavigationAdditionalContent",
  "featuredTileOverride"
];

// packages/aria/src/components/Grid/Grid.tsx
import { forwardRef as forwardRef41 } from "react";
import {
  useFluentContext as useFluentContext43,
  childrenExist as childrenExist7,
  getElementType as getElementType28,
  useAccessibility as useAccessibility38,
  useTelemetry as useTelemetry43,
  useStyles as useStyles43,
  useUnhandledProps as useUnhandledProps41,
  rtlTextContainer as rtlTextContainer4,
  createShorthandFactory as createShorthandFactory41
} from "@fluentui/react-northstar";
import { jsx as jsx46 } from "react/jsx-runtime";
var gridClassName = "ui-grid";
var displayName12 = "Grid";
var Grid = forwardRef41((props, ref) => {
  const context = useFluentContext43();
  const { setStart, setEnd } = useTelemetry43(displayName12, context.telemetry);
  setStart();
  const {
    accessibility,
    children,
    className,
    columns,
    inline = false,
    content,
    design,
    rows,
    styles,
    variables
  } = props;
  const getA11yProps = useAccessibility38(
    accessibility,
    {
      debugName: Grid.displayName,
      rtl: context.rtl
    }
  );
  const { classes } = useStyles43(displayName12, {
    className: gridClassName,
    mapPropsToStyles: () => ({ columns, rows, inline }),
    mapPropsToInlineStyles: () => ({
      className,
      design,
      styles,
      variables
    }),
    rtl: context.rtl
  });
  const unhandledProps = useUnhandledProps41(Grid.handledProps, props);
  const ElementType = getElementType28(props);
  const element = getA11yProps.unstable_wrapWithFocusZone(
    /* @__PURE__ */ jsx46(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...rtlTextContainer4.getAttributes({
            forElements: [children, content]
          }),
          ...unhandledProps
        }),
        children: childrenExist7(children) ? children : content
      }
    )
  );
  setEnd();
  return element;
});
Grid.displayName = displayName12;
Grid.handledProps = [
  // List should be populated with handled props
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "columns",
  "rows",
  "inline",
  "content",
  "children"
];
Grid.create = createShorthandFactory41({
  Component: Grid,
  mappedProp: "children"
});

// packages/aria/src/components/Container/Container.tsx
import { forwardRef as forwardRef42 } from "react";
import { childrenExist as childrenExist8 } from "@fluentui/react-bindings";
import {
  useFluentContext as useFluentContext44,
  getElementType as getElementType29,
  useAccessibility as useAccessibility39,
  useTelemetry as useTelemetry44,
  useStyles as useStyles44,
  useUnhandledProps as useUnhandledProps42,
  cardBehavior as cardBehavior8
} from "@fluentui/react-northstar";
import { rtlTextContainer as rtlTextContainer5 } from "@fluentui/react-northstar";
import { createShorthandFactory as createShorthandFactory42 } from "@fluentui/react-northstar";

// packages/aria/src/utils/screen.ts
var DEVICE_1080P_WIDTH = 1920;
var deviceWidths = {
  xs: "600px",
  sm: "744px",
  md: "970px",
  lg: "1024px",
  xl: "1366px",
  xxl: "1440px",
  xxxl: "1680px",
  "1080p": `${DEVICE_1080P_WIDTH}px`
};
var DEFAULT_CLAMP = deviceWidths["1080p"];
var DEFAULT_MIN_DESKTOP = deviceWidths.lg;
var minMediaQuery = mediaQueryFactory("min-width");
var maxMediaQuery = mediaQueryFactory("max-width");
function mediaQueryFactory(base) {
  return (value) => {
    if (value) {
      return `@media (${base}: ${value})`;
    }
    return `@media (${base})`;
  };
}
var screen = {
  tablet: minMediaQuery(deviceWidths.sm),
  // TEMP: align with tablet to display desktop UI in tablet without replacing all media query.
  // The shared literal type with `tablet` is intentional — it lets the compiler flag
  // accidental `[screen.tablet]` + `[screen.laptop]` collisions in the same object literal.
  // TODO: remove this when we absolutely sure we want to display desktop UI in tablet across all devices
  laptop: minMediaQuery(deviceWidths.sm),
  desktop: minMediaQuery(deviceWidths.xxl),
  clamped: minMediaQuery(`${DEVICE_1080P_WIDTH + 1}px`),
  touchScreen: `@media (hover: none) and (pointer: coarse)`,
  nonTouch: `@media (hover: hover) and (pointer: fine)`,
  // To avoid breaking backwards compatability
  min: {
    tablet: minMediaQuery(deviceWidths.sm),
    // TEMP: align with tablet to display desktop UI in tablet without replacing all media query
    // TODO: remove this when we absolutely sure we want to display desktop UI in tablet across all devices
    laptop: minMediaQuery(deviceWidths.sm),
    desktop: minMediaQuery(deviceWidths.xxl),
    clamped: minMediaQuery(`${DEVICE_1080P_WIDTH + 1}px`)
  },
  max: {
    tablet: maxMediaQuery(deviceWidths.sm),
    // TEMP: align with tablet to display desktop UI in tablet without replacing all media query
    // TODO: remove this when we absolutely sure we want to display desktop UI in tablet across all devices
    laptop: maxMediaQuery(deviceWidths.sm),
    desktop: maxMediaQuery(deviceWidths.xxl),
    clamped: maxMediaQuery(DEFAULT_CLAMP),
    shrankWindow: maxMediaQuery(DEFAULT_MIN_DESKTOP)
  }
};

// packages/aria/src/components/Container/Container.tsx
import { jsx as jsx47 } from "react/jsx-runtime";
var defaultRootElementType14 = "div";
var containerClassName = "ui-container";
var Container = forwardRef42(
  (props, ref) => {
    const context = useFluentContext44();
    const { setStart, setEnd } = useTelemetry44(
      Container.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      maxWidth = DEFAULT_CLAMP,
      children,
      content
    } = props;
    const getA11yProps = useAccessibility39(
      accessibility,
      {
        debugName: Container.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles44(
      Container.displayName,
      {
        className: containerClassName,
        mapPropsToStyles: () => ({
          maxWidth
        }),
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps42(Container.handledProps, props);
    const ElementType = getElementType29(props);
    const result = /* @__PURE__ */ jsx47(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ...rtlTextContainer5.getAttributes({
            forElements: [children, content]
          }),
          ref,
          ...unhandledProps
        }),
        children: childrenExist8(children) ? children : content
      }
    );
    setEnd();
    return result;
  }
);
Container.displayName = "Container";
Container.defaultProps = {
  as: defaultRootElementType14,
  accessibility: cardBehavior8
};
Container.handledProps = [
  ...commonHandledProps(),
  "maxWidth",
  "content",
  "children"
];
Container.create = createShorthandFactory42({ Component: Container });
var Container_default = Container;

// packages/aria/src/components/TopNavigation/AppMenu.tsx
import {
  getElementType as getElementType36,
  useUnhandledProps as useUnhandledProps49,
  useAccessibility as useAccessibility46,
  useStyles as useStyles51,
  useTelemetry as useTelemetry51,
  useFluentContext as useFluentContext51
} from "@fluentui/react-bindings";
import * as React10 from "react";
import { createShorthandFactory as createShorthandFactory50 } from "@fluentui/react-northstar";

// packages/aria/src/components/CategoryTree/CategoryTree.tsx
import { forwardRef as forwardRef46 } from "react";
import {
  useFluentContext as useFluentContext48,
  getElementType as getElementType33,
  useAccessibility as useAccessibility43,
  useTelemetry as useTelemetry48,
  useStyles as useStyles48,
  useUnhandledProps as useUnhandledProps46,
  createShorthandFactory as createShorthandFactory47
} from "@fluentui/react-northstar";

// packages/aria/src/components/CategoryTree/CategoryTreeHeader.tsx
import { forwardRef as forwardRef43 } from "react";
import {
  useFluentContext as useFluentContext45,
  getElementType as getElementType30,
  useAccessibility as useAccessibility40,
  useTelemetry as useTelemetry45,
  useStyles as useStyles45,
  useUnhandledProps as useUnhandledProps43,
  createShorthandFactory as createShorthandFactory43
} from "@fluentui/react-northstar";
import { AngleLeft } from "@archipro-design/icons";
import { jsx as jsx48, jsxs as jsxs27 } from "react/jsx-runtime";
var categoryTreeHeaderClassName = "ui-categoryTreeHeader";
var categoryTreeHeaderSlotClassNames = {
  backButton: `${categoryTreeHeaderClassName}__backButton`,
  contentContainer: `${categoryTreeHeaderClassName}__contentContainer`,
  title: `${categoryTreeHeaderClassName}__title`,
  subtitle: `${categoryTreeHeaderClassName}__subtitle`
};
var CategoryTreeHeader = forwardRef43((props, ref) => {
  const context = useFluentContext45();
  const { setStart, setEnd } = useTelemetry45(
    CategoryTreeHeader.displayName,
    context.telemetry
  );
  setStart();
  const {
    className,
    styles,
    variables,
    accessibility,
    design,
    title,
    subtitle,
    backButton
  } = props;
  const getA11yProps = useAccessibility40(
    accessibility,
    {
      debugName: CategoryTreeHeader.displayName,
      rtl: context.rtl
    }
  );
  const { classes } = useStyles45(
    CategoryTreeHeader.displayName,
    {
      className: categoryTreeHeaderClassName,
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    }
  );
  const unhandledProps = useUnhandledProps43(
    CategoryTreeHeader.handledProps,
    props
  );
  const ElementType = getElementType30(props);
  const result = /* @__PURE__ */ jsxs27(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      children: [
        Button.create(backButton, {
          defaultProps: () => ({
            icon: /* @__PURE__ */ jsx48(AngleLeft, {}),
            variant: "text",
            variables: { buttonPadding: 0, iconPadding: 0 }
          }),
          overrideProps: () => ({
            className: mergeClassName(
              backButton,
              categoryTreeHeaderSlotClassNames.backButton
            )
          })
        }),
        /* @__PURE__ */ jsxs27(
          Flex,
          {
            hAlign: "center",
            vAlign: "center",
            column: true,
            className: categoryTreeHeaderSlotClassNames.contentContainer,
            children: [
              Label.create(title, {
                defaultProps: () => ({
                  variant: "02",
                  weight: "medium"
                }),
                overrideProps: () => ({
                  className: mergeClassName(
                    title,
                    categoryTreeHeaderSlotClassNames.title
                  )
                })
              }),
              Caption.create(subtitle, {
                defaultProps: () => ({
                  className: categoryTreeHeaderSlotClassNames.subtitle,
                  variant: "04",
                  weight: "regular"
                }),
                overrideProps: () => ({
                  className: mergeClassName(
                    subtitle,
                    categoryTreeHeaderSlotClassNames.subtitle
                  )
                })
              })
            ]
          }
        )
      ]
    }
  );
  setEnd();
  return result;
});
CategoryTreeHeader.displayName = "CategoryTreeHeader";
CategoryTreeHeader.create = createShorthandFactory43({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: CategoryTreeHeader
});
CategoryTreeHeader.handledProps = [
  ...commonHandledProps(),
  "title",
  "subtitle",
  "backButton"
];

// packages/aria/src/components/CategoryTree/CategoryList.tsx
import { forwardRef as forwardRef44 } from "react";
import {
  useFluentContext as useFluentContext46,
  getElementType as getElementType31,
  useAccessibility as useAccessibility41,
  useTelemetry as useTelemetry46,
  useStyles as useStyles46,
  useUnhandledProps as useUnhandledProps44,
  createShorthandFactory as createShorthandFactory44,
  listBehavior
} from "@fluentui/react-northstar";
import { jsx as jsx49 } from "react/jsx-runtime";
var categoryListClassName = "ui-categoryList";
var categoryListSlotClassNames = {
  item: `${categoryListClassName}__item`
};
var CategoryList = forwardRef44(
  (props, ref) => {
    const context = useFluentContext46();
    const { setStart, setEnd } = useTelemetry46(
      CategoryList.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      items = []
    } = props;
    const getA11yProps = useAccessibility41(
      accessibility,
      {
        debugName: CategoryList.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles46(
      CategoryList.displayName,
      {
        className: categoryListClassName,
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps44(
      CategoryList.handledProps,
      props
    );
    const ElementType = getElementType31(props);
    const result = /* @__PURE__ */ jsx49(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: items.map(
          (item, index) => Button.create(item, {
            defaultProps: () => ({
              key: index,
              size: 16,
              variant: "text",
              variables: {
                buttonPadding: 18,
                iconPadding: 0
              }
            }),
            overrideProps: () => ({
              className: mergeClassName(
                item,
                categoryListSlotClassNames.item
              )
            })
          })
        )
      }
    );
    setEnd();
    return result;
  }
);
CategoryList.displayName = "CategoryList";
CategoryList.create = createShorthandFactory44({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: CategoryList,
  mappedProp: "items"
});
CategoryList.defaultProps = {
  accessibility: listBehavior
};
CategoryList.handledProps = [...commonHandledProps(), "items"];

// packages/aria/src/components/ProfileNavigation/ProfileNavigation.tsx
import { useRef as useRef7 } from "react";
import { forwardRef as forwardRef45 } from "react";
import {
  useFluentContext as useFluentContext47,
  getElementType as getElementType32,
  useAccessibility as useAccessibility42,
  useTelemetry as useTelemetry47,
  useStyles as useStyles47,
  useUnhandledProps as useUnhandledProps45,
  menuBehavior as menuBehavior5,
  createShorthandFactory as createShorthandFactory46
} from "@fluentui/react-northstar";
import { Filter } from "@archipro-design/icons";

// packages/aria/src/components/ProfileNavigation/ProfileNavigationItem.tsx
import { createShorthandFactory as createShorthandFactory45 } from "@fluentui/react-northstar";
import { compose as compose3 } from "@fluentui/react-bindings";
var profileNavigationItemClassName = "ui-profileNavigationItem";
var ProfileNavigationItem = compose3(Button, {
  state(props) {
    return {
      size: 16,
      className: mergeClassName(props, profileNavigationItemClassName),
      variables: { iconPadding: 0 },
      variant: "outlined"
    };
  }
});
ProfileNavigationItem.create = createShorthandFactory45({
  Component: ProfileNavigationItem
});

// packages/aria/src/components/ProfileNavigation/ProfileNavigation.tsx
import { usePrevious as usePrevious2, useScroll, useUpdateEffect } from "ahooks";
import { jsx as jsx50, jsxs as jsxs28 } from "react/jsx-runtime";
var defaultRootElementType15 = "div";
var profileNavigationClassName = "ui-profileNavigation";
var profileNavigationClassNames = {
  container: `${profileNavigationClassName}__container`,
  filterButton: `${profileNavigationClassName}__filterButton`,
  itemContainer: `${profileNavigationClassName}__itemContainer`,
  item: `${profileNavigationClassName}__item`
};
var FILTER_MIN_WIDTH = 49;
var ProfileNavigation = forwardRef45((props, ref) => {
  const context = useFluentContext47();
  const { setStart, setEnd } = useTelemetry47(
    ProfileNavigation.displayName,
    context.telemetry
  );
  setStart();
  const {
    className,
    styles,
    variables,
    accessibility,
    design,
    items,
    filterButton,
    buttonColor = "primary",
    onClick,
    logSortEvent
  } = props;
  const getA11yProps = useAccessibility42(
    accessibility,
    {
      debugName: ProfileNavigation.displayName,
      rtl: context.rtl
    }
  );
  const filterButtonRef = useRef7(null);
  const itemContainerRef = useRef7(null);
  const originalWidth = useRef7();
  const hasScrolled = useRef7(false);
  const scrollState = useScroll(itemContainerRef);
  const previousPos = usePrevious2(scrollState);
  useUpdateEffect(() => {
    if (!originalWidth?.current && filterButtonRef.current) {
      originalWidth.current = filterButtonRef.current.offsetWidth;
    }
    if (scrollState?.left) {
      hasScrolled.current = true;
    }
    if (originalWidth.current && filterButtonRef.current && scrollState) {
      if (hasScrolled.current && previousPos && scrollState) {
        if (previousPos.left > scrollState.left && scrollState.left < originalWidth.current - FILTER_MIN_WIDTH) {
          const expandWidth = filterButtonRef.current.offsetWidth + (previousPos.left - scrollState.left);
          filterButtonRef.current.style.width = `${expandWidth}px`;
        }
        if (previousPos.left < scrollState.left) {
          const shrinkWidth = filterButtonRef.current.offsetWidth - (scrollState?.left - previousPos.left);
          if (filterButtonRef.current.offsetWidth > FILTER_MIN_WIDTH) {
            filterButtonRef.current.style.width = `${shrinkWidth}px`;
          }
        }
      }
    }
  }, [previousPos, scrollState]);
  const { classes } = useStyles47(
    ProfileNavigation.displayName,
    {
      className: profileNavigationClassName,
      mapPropsToStyles: () => ({
        buttonColor
      }),
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    }
  );
  const unhandledProps = useUnhandledProps45(
    ProfileNavigation.handledProps,
    props
  );
  const handleClick = (event, type, item) => {
    onClick?.(event, { type, item });
  };
  const ElementType = getElementType32(props);
  const renderFilterButton = (filterButton2) => {
    return ProfileNavigationItem.create(filterButton2, {
      defaultProps: () => ({
        key: "filterButton",
        color: buttonColor,
        iconBefore: /* @__PURE__ */ jsx50(Filter, { "aria-label": "filter" }),
        children: "Filters",
        onClick: (event) => handleClick(event, "filter", filterButton2)
      }),
      overrideProps: () => ({
        className: mergeClassName(
          filterButton2,
          profileNavigationClassNames.filterButton
        ),
        ref: filterButtonRef
      })
    });
  };
  const renderItemButton = (itemButton, index) => {
    return ProfileNavigationItem.create(itemButton, {
      defaultProps: () => ({
        key: index,
        color: buttonColor,
        variant: "outlined",
        onClick: (event) => {
          const itemButtonObject = itemButton;
          if (itemButtonObject.to?.includes("/sort_") && itemButtonObject.children) {
            logSortEvent?.(itemButtonObject.children);
          }
          handleClick(event, "item", itemButton);
        }
      }),
      overrideProps: () => ({
        className: mergeClassName(
          itemButton,
          profileNavigationClassNames.item
        )
      })
    });
  };
  const result = /* @__PURE__ */ jsx50(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      children: /* @__PURE__ */ jsxs28(Flex, { className: profileNavigationClassNames.container, children: [
        filterButton && renderFilterButton(filterButton),
        /* @__PURE__ */ jsx50(
          Flex,
          {
            ref: itemContainerRef,
            className: profileNavigationClassNames.itemContainer,
            children: items.map((item, index) => {
              return renderItemButton(item, index);
            })
          }
        )
      ] })
    }
  );
  setEnd();
  return result;
});
ProfileNavigation.displayName = "ProfileNavigation";
ProfileNavigation.defaultProps = {
  as: defaultRootElementType15,
  accessibility: menuBehavior5
};
ProfileNavigation.create = createShorthandFactory46({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ProfileNavigation
});
ProfileNavigation.handledProps = [
  ...commonHandledProps(),
  "buttonColor",
  "filterButton",
  "items",
  "onClick",
  "logSortEvent"
];

// packages/aria/src/components/CategoryTree/CategoryTree.tsx
import { jsxs as jsxs29 } from "react/jsx-runtime";
var categoryTreeClassName = "ui-categoryTree";
var CategoryTree = forwardRef46(
  (props, ref) => {
    const context = useFluentContext48();
    const { setStart, setEnd } = useTelemetry48(
      CategoryTree.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      header,
      filter,
      list
    } = props;
    const getA11yProps = useAccessibility43(
      accessibility,
      {
        debugName: CategoryTree.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles48(
      CategoryTree.displayName,
      {
        className: categoryTreeClassName,
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps46(
      CategoryTree.handledProps,
      props
    );
    const ElementType = getElementType33(props);
    const result = /* @__PURE__ */ jsxs29(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: [
          ProfileNavigation.create(filter, {
            defaultProps: () => ({
              buttonColor: "primary",
              variables: {
                gap: pxToRem(10)
              }
            })
          }),
          CategoryTreeHeader.create(header),
          CategoryList.create(list)
        ]
      }
    );
    setEnd();
    return result;
  }
);
CategoryTree.displayName = "CategoryTree";
CategoryTree.create = createShorthandFactory47({
  Component: CategoryTree
});
CategoryTree.handledProps = [
  ...commonHandledProps(),
  "header",
  "filter",
  "list"
];

// packages/aria/src/components/List/List.tsx
import { forwardRef as forwardRef48, useState as useState6 } from "react";
import {
  useFluentContext as useFluentContext50,
  getElementType as getElementType35,
  useAccessibility as useAccessibility45,
  useTelemetry as useTelemetry50,
  useStyles as useStyles50,
  useUnhandledProps as useUnhandledProps48,
  listBehavior as listBehavior2,
  createShorthandFactory as createShorthandFactory49
} from "@fluentui/react-northstar";

// packages/aria/src/components/List/ListItem.tsx
import { forwardRef as forwardRef47 } from "react";
import {
  useFluentContext as useFluentContext49,
  getElementType as getElementType34,
  useAccessibility as useAccessibility44,
  useTelemetry as useTelemetry49,
  useStyles as useStyles49,
  useUnhandledProps as useUnhandledProps47,
  createShorthandFactory as createShorthandFactory48,
  listItemBehavior
} from "@fluentui/react-northstar";
import { Fragment as Fragment7, jsxs as jsxs30 } from "react/jsx-runtime";
var listItemClassName = "ui-listItem";
var displayName13 = "ListItem";
var ListItem = forwardRef47((props, ref) => {
  const context = useFluentContext49();
  const { setStart, setEnd } = useTelemetry49(displayName13, context.telemetry);
  setStart();
  const {
    className,
    styles,
    variables,
    accessibility = listItemBehavior,
    design,
    link,
    linkClassName,
    linkAttributes = {},
    label,
    customChildComponent,
    iconBefore,
    iconAfter,
    openNewWindow = false,
    iconPadding = 8,
    onClick
  } = props;
  const getA11yProps = useAccessibility44(accessibility, {
    debugName: displayName13,
    rtl: context.rtl
  });
  const { classes } = useStyles49(displayName13, {
    className: listItemClassName,
    mapPropsToStyles: () => ({
      isLink: !!link,
      iconPadding
    }),
    mapPropsToInlineStyles: () => ({
      className,
      styles,
      variables,
      design
    }),
    rtl: context.rtl,
    unstable_props: props
  });
  const unhandledProps = useUnhandledProps47(ListItem.handledProps, props);
  const ElementType = getElementType34(props);
  const attrs = link ? {
    ...{ href: link, className: linkClassName, ...linkAttributes },
    ...openNewWindow && { target: "_blank" }
  } : {};
  const inner = /* @__PURE__ */ jsxs30(Fragment7, { children: [
    Box.create(iconBefore, {
      defaultProps: () => ({
        as: "span",
        className: classes["iconBefore"]
      })
    }),
    Caption.create(label, {
      defaultProps: () => ({
        as: "span",
        className: classes["text"]
      }),
      overrideProps: () => ({
        styles: {
          fontSize: pxToRem(29),
          lineHeight: "100%"
        },
        variables: {
          captionColor: "inherit"
        }
      })
    }),
    Box.create(iconAfter, {
      defaultProps: () => ({
        as: "span",
        className: classes["iconAfter"]
      })
    })
  ] });
  const result = /* @__PURE__ */ jsxs30(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps,
        onClick: (e) => {
          onClick?.(e, props);
        }
      }),
      children: [
        Caption.create(inner, {
          defaultProps: () => ({
            as: link ? "a" : "span",
            className: classes["textWrapper"],
            ...attrs
          })
        }),
        customChildComponent && customChildComponent
      ]
    }
  );
  setEnd();
  return result;
});
ListItem.displayName = displayName13;
ListItem.create = createShorthandFactory48({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ListItem,
  mappedProp: "label"
});
ListItem.defaultProps = {
  as: "li",
  accessibility: listItemBehavior
};
ListItem.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "label",
  "link",
  "linkClassName",
  "linkAttributes",
  "customChildComponent",
  "iconAfter",
  "iconBefore",
  "iconPadding",
  "openNewWindow",
  "onClick"
];

// packages/aria/src/components/List/List.tsx
import { jsxs as jsxs31 } from "react/jsx-runtime";
var listClassName = "ui-list";
var listSlotClassNames = {
  moreOrLess: `${listClassName}__more-or-less`
};
var displayName14 = "List";
var List = forwardRef48((props, ref) => {
  const context = useFluentContext50();
  const { setStart, setEnd } = useTelemetry50(displayName14, context.telemetry);
  setStart();
  const {
    className,
    styles,
    variables,
    accessibility,
    design,
    items = [],
    lineLimit = 0,
    showMoreText = "More",
    showLessText,
    onItemClick
  } = props;
  const [clickedShowMore, setClickedShowMore] = useState6(false);
  const getMoreLessLink = () => {
    const linkText = clickedShowMore ? showLessText : showMoreText;
    return linkText && lineLimit > 0 && items.length > lineLimit ? ListItem.create(linkText, {
      defaultProps: () => ({
        onClick: () => setClickedShowMore(!clickedShowMore),
        className: listSlotClassNames.moreOrLess
      }),
      generateKey: true
    }) : null;
  };
  const getFilteredItems = () => {
    return items.slice(
      0,
      lineLimit < 1 || clickedShowMore ? items.length : lineLimit
    );
  };
  const getA11yProps = useAccessibility45(
    accessibility,
    {
      debugName: displayName14,
      rtl: context.rtl
    }
  );
  const { classes } = useStyles50(displayName14, {
    className: listClassName,
    mapPropsToInlineStyles: () => ({
      className,
      styles,
      variables,
      design
    }),
    rtl: context.rtl,
    unstable_props: props
  });
  const unhandledProps = useUnhandledProps48(List.handledProps, props);
  const ElementType = getElementType35(props);
  const result = /* @__PURE__ */ jsxs31(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      children: [
        items && getFilteredItems().map(
          (i, index) => ListItem.create(i, {
            defaultProps: () => ({ key: index }),
            generateKey: true,
            overrideProps: () => ({
              ...onItemClick && {
                onClick: (e, itemProps) => {
                  e.preventDefault();
                  onItemClick(e, itemProps);
                }
              }
            })
          })
        ),
        getMoreLessLink()
      ]
    }
  );
  setEnd();
  return result;
});
List.displayName = "List";
List.defaultProps = {
  as: "ul",
  accessibility: listBehavior2
};
List.create = createShorthandFactory49({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: List
});
List.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "items",
  "lineLimit",
  "showMoreText",
  "showLessText",
  "onItemClick"
];

// packages/aria/src/components/TopNavigation/AppMenu.tsx
import { Fragment as Fragment8, jsx as jsx51, jsxs as jsxs32 } from "react/jsx-runtime";
var displayName15 = "AppMenu";
var appMenuClassName = "ui-appMenu";
var appMenuSlotClassNames = {
  content: `${appMenuClassName}__content`,
  scrollableContainer: `${appMenuClassName}__scrollableContainer`,
  searchBar: `${appMenuClassName}__searchBar`,
  screen: `${appMenuClassName}__screen`,
  navMenu: `${appMenuClassName}__navMenu`,
  events: `${appMenuClassName}__events`,
  categoryTree: `${appMenuClassName}__categoryTree`
};
var AppMenu = React10.forwardRef(
  (props, ref) => {
    const context = useFluentContext51();
    const { setStart, setEnd } = useTelemetry51(
      displayName15,
      context.telemetry
    );
    setStart();
    const {
      accessibility,
      className,
      design,
      styles,
      variables,
      searchBar,
      showSearchMenu,
      searchMenu,
      showCategoryTree,
      categoryTree,
      majorNavItems,
      minorNavItems,
      eventItems,
      bottomNavigationAdditionalContent
    } = props;
    const getA11yProps = useAccessibility46(
      accessibility,
      {
        debugName: displayName15,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles51(displayName15, {
      className: appMenuClassName,
      mapPropsToStyles: () => ({
        showSearchMenu
      }),
      mapPropsToInlineStyles: () => ({
        className,
        design,
        styles,
        variables
      }),
      rtl: context.rtl,
      unstable_props: props
    });
    const unhandledProps = useUnhandledProps49(AppMenu.handledProps, props);
    const ElementType = getElementType36(props);
    const renderSearchBar = () => {
      return /* @__PURE__ */ jsx51("div", { className: appMenuSlotClassNames.searchBar, children: SearchBar.create(searchBar, {
        defaultProps: () => {
          return {
            iconButtonBefore: {
              variables: { buttonPadding: 0 }
            },
            iconButtonAfter: {
              variables: { buttonPadding: 0 }
            }
          };
        },
        overrideProps: () => {
          return {
            variables: {
              searchBarBorderRadius: pxToRem(4),
              searchInputPaddingX: pxToRem(2),
              searchBarPadding: pxArrayToRem([
                6,
                13,
                6,
                4
              ]),
              ...!showSearchMenu && {
                searchBarBackgroundColor: "transparent",
                searchBarBorderColor: "#d2d2d0"
              }
            }
          };
        }
      }) });
    };
    const renderSearchMenu = () => {
      if (!showSearchMenu) return null;
      return SearchMenu.create(searchMenu);
    };
    const renderCategoryScreen = () => {
      if (!showCategoryTree) return null;
      return /* @__PURE__ */ jsxs32(Flex, { className: appMenuSlotClassNames.screen, children: [
        CategoryTree.create(categoryTree, {
          overrideProps: () => ({
            className: mergeClassName(
              categoryTree,
              appMenuSlotClassNames.categoryTree
            )
          })
        }),
        bottomNavigationAdditionalContent && Box.create(bottomNavigationAdditionalContent)
      ] });
    };
    const renderEvents = () => {
      if (!eventItems || eventItems.length === 0) return null;
      return /* @__PURE__ */ jsxs32(Flex, { className: appMenuSlotClassNames.events, column: true, children: [
        /* @__PURE__ */ jsx51(Caption, { children: "EVENTS" }),
        /* @__PURE__ */ jsx51(Grid, { columns: 2, children: eventItems?.map((event) => Box.create(event, {})) })
      ] });
    };
    const renderDefaultScreen = () => {
      return /* @__PURE__ */ jsxs32(Flex, { className: appMenuSlotClassNames.screen, children: [
        renderSearchBar(),
        /* @__PURE__ */ jsxs32("div", { className: appMenuSlotClassNames.scrollableContainer, children: [
          showSearchMenu ? renderSearchMenu() : /* @__PURE__ */ jsxs32(Fragment8, { children: [
            List.create(majorNavItems, {
              overrideProps: () => ({
                className: appMenuSlotClassNames.navMenu,
                variables: {
                  listBorderWidth: "0",
                  listBorderColor: "transparent"
                }
              })
            }),
            renderEvents(),
            List.create(minorNavItems, {
              overrideProps: () => ({
                className: appMenuSlotClassNames.navMenu
              })
            })
          ] }),
          bottomNavigationAdditionalContent && Box.create(bottomNavigationAdditionalContent)
        ] })
      ] });
    };
    const result = getA11yProps.unstable_wrapWithFocusZone(
      /* @__PURE__ */ jsx51(
        ElementType,
        {
          ...getA11yProps("root", {
            className: classes["root"],
            ref,
            ...unhandledProps
          }),
          children: /* @__PURE__ */ jsx51(Flex, { className: appMenuSlotClassNames.content, column: true, children: showCategoryTree ? renderCategoryScreen() : renderDefaultScreen() })
        }
      )
    );
    setEnd();
    return result;
  }
);
AppMenu.create = createShorthandFactory50({ Component: AppMenu });
AppMenu.displayName = displayName15;
AppMenu.handledProps = [
  ...commonHandledProps(),
  "searchBar",
  "showSearchMenu",
  "searchMenu",
  "showCategoryTree",
  "categoryTree",
  "majorNavItems",
  "minorNavItems",
  "eventItems"
];

// packages/aria/src/components/TopNavigation/TopNavigation.tsx
import { Fragment as Fragment9, jsx as jsx52, jsxs as jsxs33 } from "react/jsx-runtime";
var topNavigationClassName = "ui-topNavigation";
var topNavigationSlotClassNames = {
  left: `${topNavigationClassName}__navigationItem-container-left`,
  right: `${topNavigationClassName}__navigationItem-container-right`,
  rightActionToggles: `${topNavigationClassName}__navigationItem-container-right-action-toggles`,
  rightButton: `${topNavigationClassName}__navigationItem-container-right-button`,
  logo: `${topNavigationClassName}__logo`,
  searchBar: `${topNavigationClassName}__searchBar`,
  flag: `${topNavigationClassName}__flag`,
  activeItem: `${topNavigationClassName}__menuActiveItem`,
  logoAndRightSideWrapper: `${topNavigationClassName}__container-logo-and-actions`,
  megaMenu: `${topNavigationClassName}__megaMenu-container`,
  megaMenuMask: `${topNavigationClassName}__megaMenu-mask`,
  secondaryNavContainer: `${topNavigationClassName}__secondaryNavu-container`,
  menuButton: `${topNavigationClassName}__menuButton`,
  appMenu: `${topNavigationClassName}__appMenu`
};
var MOBILE_TOP_NAV_HEIGHT_FULL_LOGO = 74;
var MOBILE_TOP_NAV_HEIGHT_SHORT_LOGO = 74;
var MOBILE_TOP_NAV_HEIGHT_SHORT_LOGO_WITHOUT_DVH_SUPPORT = 116;
var DESKTOP_TOP_NAV_HEIGHT_FULL_LOGO = 110;
var DESKTOP_TOP_NAV_HEIGHT_SHORT_LOGO = 110;
var TABLET_TOP_NAV_HEIGHT = 110;
var DESKTOP_TOP_NAV_HEIGHT_WITH_SECOND_NAV = 150;
var displayName16 = "TopNavigation";
var TopNavigation = forwardRef50(
  (props, ref) => {
    const context = useFluentContext52();
    const { setStart, setEnd } = useTelemetry52(
      displayName16,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      design,
      navigationItems,
      actionTextSlot,
      avatarSlot,
      menuButtonSlot,
      userButtonSlot,
      favouriteSlot,
      cartSlot,
      inboxSlot,
      flagSlot,
      showSearchBar = false,
      showSearchMenu = false,
      expanded = false,
      searchBar,
      searchMenu,
      logo = {},
      appDisplayMode = { mobile: false, desktop: true },
      megaMenu,
      hasMenuTopDivider,
      categoryTree,
      secondaryNavItems,
      appMenu,
      showCategoryTree = false,
      onMenuExpand,
      preFilterDropdown,
      deviceType
    } = props;
    const { mobile, desktop } = appDisplayMode;
    const hasSecondaryNav = secondaryNavItems && secondaryNavItems?.length > 0 && !showSearchMenu;
    const iconButtonSize = 24;
    const iconButtonWidth = pxToRem(32);
    const getA11yProps = useAccessibility47(menuBehavior6, {
      debugName: displayName16,
      rtl: context.rtl
    });
    const { classes } = useStyles52(displayName16, {
      className: topNavigationClassName,
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      mapPropsToStyles: () => ({
        expanded,
        showSearchBar,
        showSearchMenu,
        showFullLogo: logo?.["showFull"],
        logoAnimationType: logo?.["animationType"],
        showMegamenu: !!megaMenu,
        hasBorderRightNavigation: !mobile,
        hasMenuTopDivider,
        showCategoryTree,
        desktopDevice: deviceType.desktop,
        tabletDevice: false,
        hasSecondaryNav: !!hasSecondaryNav
      }),
      unstable_props: props,
      rtl: context.rtl
    });
    useEffect7(() => {
      if (typeof window != "undefined" && window.document) {
        if (appDisplayMode.mobile) {
          document.body.style.overflow = expanded ? "hidden" : "unset";
        }
      }
    }, [expanded, appDisplayMode.mobile]);
    const ElementType = getElementType37(props);
    const unhandledProps = useUnhandledProps50(
      TopNavigation.handledProps,
      props
    );
    const renderActionText = () => {
      if (desktop) {
        return Button.create(actionTextSlot, {
          generateKey: true,
          defaultProps: () => ({
            className: classes["textMenuItem"],
            children: {
              children: "List your Business",
              uc: false
            },
            variant: "text",
            size: 24,
            variables: { buttonPadding: 0 }
          })
        });
      } else {
        return null;
      }
    };
    const renderUserSlot = () => {
      return !desktop ? Button.create(userButtonSlot, {
        generateKey: true,
        defaultProps: () => ({
          className: classes["menuItem"],
          variant: "text",
          size: iconButtonSize,
          variables: {
            buttonPadding: mobile ? pxToRem(8) : 0,
            iconPadding: 0,
            buttonMinWidth: iconButtonWidth
          }
        })
      }) : null;
    };
    const renderAvatarSlot = () => {
      return Button.create(avatarSlot, {
        generateKey: true,
        defaultProps: () => ({
          className: classes["menuItem"],
          variant: "text",
          size: 32,
          variables: {
            buttonPadding: 0,
            iconPadding: 0,
            buttonMinWidth: iconButtonWidth
          }
        }),
        overrideProps: (props2) => {
          return {
            ...props2.text ? {
              className: classes["textMenuItem"]
            } : {
              className: desktop ? topNavigationSlotClassNames.rightButton : ""
            }
          };
        }
      });
    };
    const renderMenuButtonSlot = () => {
      if (desktop) return null;
      return /* @__PURE__ */ jsx52(Fragment9, { children: Button.create(menuButtonSlot, {
        generateKey: true,
        defaultProps: () => ({
          className: classes["menuItem"],
          variant: "text",
          size: iconButtonSize,
          icon: expanded ? /* @__PURE__ */ jsx52(
            CloseLine3,
            {
              size: "2x",
              "aria-label": "menu-close"
            }
          ) : /* @__PURE__ */ jsx52(
            CloseLine3,
            {
              size: "2x",
              "aria-label": "menu-open"
            }
          ),
          variables: {
            buttonPadding: 0,
            iconPadding: mobile ? 0 : 4,
            buttonMinWidth: iconButtonWidth
          }
        }),
        overrideProps: () => ({
          className: mergeClassName(
            menuButtonSlot,
            topNavigationSlotClassNames.menuButton
          ),
          onClick: (e) => {
            menuButtonSlot?.onClick?.(e);
            onMenuExpand?.(e, !expanded);
          }
        })
      }) });
    };
    const renderFavouriteSlot = () => {
      return desktop ? Button.create(favouriteSlot, {
        generateKey: true,
        defaultProps: () => ({
          className: classes["menuItem"],
          variant: "text",
          size: iconButtonSize,
          icon: /* @__PURE__ */ jsx52(HeartLine2, { "aria-label": "search" }),
          ...(mobile || mobile && expanded) && {
            styles: { display: "none" }
          },
          variables: {
            buttonPadding: mobile ? pxToRem(8) : 0,
            iconPadding: mobile ? 0 : 4,
            buttonMinWidth: iconButtonWidth
          }
        })
      }) : null;
    };
    const renderCartSlot = () => {
      return Button.create(cartSlot, {
        generateKey: true,
        defaultProps: () => ({
          className: classes["menuItem"],
          variant: "text",
          size: iconButtonSize,
          icon: /* @__PURE__ */ jsx52(CustomShoppingCart, { size: "2x", "aria-label": "cart" }),
          variables: {
            buttonPadding: mobile ? pxToRem(8) : 0,
            iconPadding: mobile ? 0 : 4,
            buttonMinWidth: iconButtonWidth
          }
        })
      });
    };
    const renderFlagSlot = () => {
      return Button.create(flagSlot, {
        generateKey: true,
        defaultProps: () => ({
          className: classes["menuItem"],
          variant: "text",
          size: iconButtonSize,
          variables: {
            buttonPadding: mobile ? pxToRem(8) : 0,
            iconPadding: 0
          }
        }),
        overrideProps: () => ({
          className: mergeClassName(
            flagSlot,
            topNavigationSlotClassNames.flag
          )
        })
      });
    };
    const renderRightSide = () => {
      const items = mobile ? [renderCartSlot(), renderAvatarSlot(), renderUserSlot()] : [
        renderActionText(),
        renderCartSlot(),
        renderFavouriteSlot(),
        renderInbox(),
        renderAvatarSlot(),
        renderFlagSlot(),
        renderUserSlot()
      ];
      return /* @__PURE__ */ jsxs33(
        Flex,
        {
          hAlign: "end",
          className: topNavigationSlotClassNames.right,
          children: [
            !mobile && /* @__PURE__ */ jsx52(Flex, { children: renderSearch() }),
            /* @__PURE__ */ jsx52(
              Grid,
              {
                columns: `repeat(${mobile ? 2 : 4}, min-content)`,
                className: topNavigationSlotClassNames.rightActionToggles,
                children: items
              }
            )
          ]
        }
      );
    };
    const renderLeftSide = () => {
      return /* @__PURE__ */ jsx52("div", { className: topNavigationSlotClassNames.left, children: navigationItems.map(
        (item, index) => {
          if (!item) return null;
          if (typeof item === "object") {
            return NavigationItem.create(item, {
              defaultProps: () => ({ key: index }),
              overrideProps: ({ label, active }) => ({
                children: label,
                active,
                className: mergeClassName(
                  item,
                  active ? topNavigationSlotClassNames.activeItem : ""
                )
              }),
              generateKey: true
            });
          }
          return null;
        }
      ) });
    };
    const renderLogo = (showFull = false) => {
      return Logo.create(logo, {
        defaultProps: () => {
          return {
            showFull,
            className: topNavigationSlotClassNames.logo,
            ...expanded && mobile && {
              variables: {
                //When the appmenu is expanded, set the logo to #343434 since its background is always white.
                logoPrimaryColor: naturalColors.charcoal[250]
              }
            }
          };
        },
        overrideProps: () => ({
          className: mergeClassName(
            logo,
            topNavigationSlotClassNames.logo
          )
        })
      });
    };
    const renderSearch = () => {
      return /* @__PURE__ */ jsxs33("div", { className: topNavigationSlotClassNames.searchBar, children: [
        SearchBar.create(searchBar, {
          defaultProps: () => {
            return {
              iconButtonBefore: {
                variables: {
                  buttonPadding: desktop ? 8 : 0
                }
              },
              iconButtonAfter: {
                variables: {
                  buttonPadding: desktop ? 8 : 0
                }
              },
              preFilterDropdown
            };
          }
        }),
        showSearchMenu && SearchMenu.create(searchMenu, {
          defaultProps: () => {
            return {
              variables: {
                searchMenuPadding: desktop ? pxArrayToRem([24, 12, 89]) : pxArrayToRem([30, 0, 0, 0])
              }
            };
          }
        })
      ] });
    };
    const renderInbox = () => {
      return Button.create(inboxSlot, {
        generateKey: true,
        defaultProps: () => ({
          className: classes["menuItem"],
          variant: "text",
          size: iconButtonSize,
          styles: {
            ...mobile && expanded && { display: "none" }
          },
          variables: {
            buttonPadding: mobile ? pxToRem(8) : 0,
            iconPadding: mobile ? 0 : 8,
            buttonMinWidth: iconButtonWidth
          }
        })
      });
    };
    const renderSecondaryNav = () => {
      return hasSecondaryNav ? /* @__PURE__ */ jsx52(
        Flex,
        {
          className: topNavigationSlotClassNames.secondaryNavContainer,
          children: secondaryNavItems?.map((item, index) => {
            if (!item) return null;
            if (typeof item === "object") {
              return Button.create(item, {
                defaultProps: () => ({ key: index }),
                generateKey: true
              });
            }
            return null;
          })
        }
      ) : null;
    };
    const renderAppMenu = () => {
      if (!expanded) return null;
      return AppMenu.create(appMenu, {
        overrideProps: () => ({
          categoryTree,
          searchBar,
          searchMenu,
          showSearchMenu,
          showCategoryTree,
          className: topNavigationSlotClassNames.appMenu
        })
      });
    };
    const renderMobile = () => {
      return /* @__PURE__ */ jsxs33("div", { className: classes["topNav"], children: [
        /* @__PURE__ */ jsxs33(
          Flex,
          {
            vAlign: "center",
            space: "between",
            className: classes["mobileTopNav"],
            children: [
              renderMenuButtonSlot(),
              renderLogo(true),
              renderRightSide()
            ]
          }
        ),
        renderAppMenu()
      ] });
    };
    const renderDesktop = () => {
      return /* @__PURE__ */ jsxs33(Fragment9, { children: [
        /* @__PURE__ */ jsxs33("div", { className: classes["topNav"], children: [
          renderLeftSide(),
          /* @__PURE__ */ jsxs33(
            "div",
            {
              className: topNavigationSlotClassNames.logoAndRightSideWrapper,
              children: [
                renderLogo(showSearchBar || logo?.["showFull"]),
                renderRightSide()
              ]
            }
          )
        ] }, "topnav-content"),
        renderSecondaryNav(),
        hasMenuTopDivider ? /* @__PURE__ */ jsx52("div", { className: classes["divider"] }, "divider") : null,
        /* @__PURE__ */ jsx52(
          "div",
          {
            className: topNavigationSlotClassNames.megaMenu,
            children: /* @__PURE__ */ jsx52(
              Container_default,
              {
                variables: {
                  background: "transparent"
                },
                className: topNavigationSlotClassNames.megaMenuMask,
                children: MegaMenu.create(megaMenu)
              }
            )
          },
          "mega-menu"
        )
      ] });
    };
    const result = /* @__PURE__ */ jsx52(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: /* @__PURE__ */ jsxs33(
          Container_default,
          {
            variables: {
              background: "transparent"
            },
            children: [
              mobile && renderMobile(),
              desktop && renderDesktop()
            ]
          }
        )
      }
    );
    setEnd();
    return result;
  }
);
TopNavigation.displayName = displayName16;
TopNavigation.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "menuItems",
  "navigationItems",
  "searchBar",
  "searchMenu",
  "showSearchBar",
  "showSearchMenu",
  "expanded",
  "onMenuExpand",
  "logo",
  "hasMenuTopDivider",
  "megaMenu",
  "appDisplayMode",
  "searchSlot",
  "cartSlot",
  "favouriteSlot",
  "avatarSlot",
  "flagSlot",
  "inboxSlot",
  "userButtonSlot",
  "menuButtonSlot",
  "actionTextSlot",
  "categoryTree",
  "secondaryNavItems",
  "appMenu",
  "showCategoryTree",
  "preFilterDropdown",
  "deviceType"
];

// packages/aria/src/components/Tile/ArticleTile/ArticleTile.tsx
import { forwardRef as forwardRef51 } from "react";
import {
  useFluentContext as useFluentContext53,
  getElementType as getElementType38,
  useAccessibility as useAccessibility48,
  useTelemetry as useTelemetry53,
  useStyles as useStyles53,
  useUnhandledProps as useUnhandledProps51,
  cardBehavior as cardBehavior9,
  createShorthandFactory as createShorthandFactory51
} from "@fluentui/react-northstar";
import { jsxs as jsxs34 } from "react/jsx-runtime";
var defaultRootElementType16 = "div";
var articleTileClassName = "ui-articleTile";
var ArticleTile = forwardRef51(
  (props, ref) => {
    const context = useFluentContext53();
    const { setStart, setEnd } = useTelemetry53(
      ArticleTile.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      image,
      size,
      title,
      preview,
      readTime,
      isFavourited,
      onFavourite,
      hideFavourite,
      appDisplayMode
    } = props;
    const getA11yProps = useAccessibility48(
      accessibility,
      {
        debugName: ArticleTile.displayName,
        rtl: context.rtl
      }
    );
    const sizeToHeightMapping = {
      xsmall: pxToRem(280),
      small: pxToRem(280),
      medium: pxToRem(340),
      large: pxToRem(340),
      xlarge: pxToRem(340)
    };
    const imageHeight = sizeToHeightMapping[size];
    const { classes } = useStyles53(
      ArticleTile.displayName,
      {
        className: articleTileClassName,
        mapPropsToStyles: () => ({ size, imageHeight }),
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps51(
      ArticleTile.handledProps,
      props
    );
    const ElementType = getElementType38(props);
    const result = /* @__PURE__ */ jsxs34(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: [
          TileImage.create({
            size,
            images: image,
            isFavourited,
            onFavourite,
            hideFavourite,
            height: imageHeight,
            appDisplayMode
          }),
          Caption.create(readTime, {
            defaultProps: () => {
              return {
                variant: "03",
                uc: true,
                maximumLines: 1
              };
            }
          }),
          /* @__PURE__ */ jsxs34(Flex, { column: true, gap: "space-24", children: [
            Subheader.create(title, {
              defaultProps: () => {
                return {
                  variant: "01",
                  maximumLines: 2
                };
              }
            }),
            preview && Caption.create(preview, {
              defaultProps: () => {
                return {
                  variant: "03",
                  maximumLines: 2
                };
              }
            })
          ] })
        ]
      }
    );
    setEnd();
    return result;
  }
);
ArticleTile.displayName = "ArticleTile";
ArticleTile.create = createShorthandFactory51({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ArticleTile
});
ArticleTile.defaultProps = {
  as: defaultRootElementType16,
  accessibility: cardBehavior9,
  size: "small",
  isFavourited: false
};
ArticleTile.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  // Extra props that are handled
  "image",
  "size",
  "readTime",
  "title",
  "preview",
  ...favouriteHandledProps
];

// packages/aria/src/components/ProductInfo/ProductInfo.tsx
import { forwardRef as forwardRef52 } from "react";
import {
  useFluentContext as useFluentContext54,
  getElementType as getElementType39,
  useAccessibility as useAccessibility49,
  useTelemetry as useTelemetry54,
  useStyles as useStyles54,
  useUnhandledProps as useUnhandledProps52,
  cardBehavior as cardBehavior10,
  createShorthandFactory as createShorthandFactory52
} from "@fluentui/react-northstar";
import cx12 from "classnames";
import { jsx as jsx53, jsxs as jsxs35 } from "react/jsx-runtime";
var productInfoClassName = "ui-ProductInfo";
var productInfoSlotClassNames = {
  container: `${productTileClassName}__container`,
  details: `${productTileClassName}__details`,
  product: `${productTileClassName}__product`,
  professional: `${productTileClassName}__professional`,
  price: `${productTileClassName}__price`,
  variants: `${productTileClassName}__variants`,
  variant: `${productTileClassName}__variant`,
  cta: `${productTileClassName}__cta`
};
var defaultRootElementType17 = "div";
var SHOW_MAX_VARIANTS2 = 4;
var ProductInfo = forwardRef52(
  (props, ref) => {
    const context = useFluentContext54();
    const { setStart, setEnd } = useTelemetry54(
      ProductInfo.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      design,
      accessibility,
      productName,
      professionalName,
      pricePrefix,
      price,
      variants,
      additionalVariantCount,
      cta,
      variant = "01"
    } = props;
    const getA11yProps = useAccessibility49(
      accessibility,
      {
        debugName: ProductInfo.displayName,
        rtl: context.rtl
      }
    );
    const unhandledProps = useUnhandledProps52(
      ProductInfo.handledProps,
      props
    );
    const ElementType = getElementType39(props);
    const { classes } = useStyles54(
      ProductInfo.displayName,
      {
        className: productInfoClassName,
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        mapPropsToStyles: () => ({ variant }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const result = /* @__PURE__ */ jsx53(
      ElementType,
      {
        ...getA11yProps("root", {
          className: cx12([
            classes["root"],
            classes[`variant${variant}`]
          ]),
          ref,
          ...unhandledProps
        }),
        children: /* @__PURE__ */ jsxs35(
          Flex,
          {
            column: true,
            className: productInfoSlotClassNames.container,
            children: [
              /* @__PURE__ */ jsxs35("div", { className: productInfoSlotClassNames.details, children: [
                Label.create(productName, {
                  generateKey: true,
                  defaultProps: () => {
                    return {
                      link: false,
                      variant: "04",
                      uc: true,
                      maximumLines: 2,
                      key: "productName"
                    };
                  },
                  overrideProps: () => {
                    return {
                      className: mergeClassName(
                        productName,
                        productInfoSlotClassNames.product
                      ),
                      styles: {
                        ["& div.ui-caption"]: {
                          lineClamp: "3",
                          WebkitLineClamp: "3"
                        }
                      }
                    };
                  }
                }),
                Label.create(professionalName, {
                  generateKey: true,
                  defaultProps: () => {
                    return {
                      link: false,
                      variant: "04",
                      key: "professionalName"
                    };
                  },
                  overrideProps: () => {
                    return {
                      className: mergeClassName(
                        professionalName,
                        productInfoSlotClassNames.professional
                      ),
                      styles: {
                        ["& div.ui-caption"]: {
                          lineClamp: "3",
                          WebkitLineClamp: "3",
                          lineHeight: "unset"
                        }
                      }
                    };
                  }
                }),
                /* @__PURE__ */ jsxs35(
                  Flex,
                  {
                    gap: "space-8",
                    className: productInfoSlotClassNames.price,
                    children: [
                      Label.create(pricePrefix, {
                        generateKey: true,
                        defaultProps: () => {
                          return {
                            variant: "04",
                            maximumLines: 1,
                            key: "pricePrefix"
                          };
                        }
                      }),
                      Label.create(price, {
                        generateKey: true,
                        defaultProps: () => {
                          return {
                            variant: "04",
                            maximumLines: 1,
                            key: "price"
                          };
                        }
                      })
                    ]
                  }
                ),
                /* @__PURE__ */ jsxs35(
                  Flex,
                  {
                    vAlign: "center",
                    hAlign: "start",
                    className: productInfoSlotClassNames.variants,
                    children: [
                      variants && variants.map((variant2) => {
                        return Box.create(variant2, {
                          generateKey: true,
                          defaultProps: () => ({}),
                          overrideProps: () => {
                            return {
                              className: mergeClassName(
                                variant2,
                                productInfoSlotClassNames.variant
                              )
                            };
                          }
                        });
                      }).filter((v, i) => i < SHOW_MAX_VARIANTS2),
                      Caption.create(additionalVariantCount, {
                        generateKey: true,
                        defaultProps: () => ({
                          variant: "03"
                        }),
                        render: (Component, props2) => {
                          if (!additionalVariantCount) return;
                          const children = typeof additionalVariantCount === "object" ? props2.children : `+${additionalVariantCount}`;
                          return /* @__PURE__ */ jsx53(Component, { ...props2, children });
                        }
                      })
                    ]
                  },
                  `product-variants`
                )
              ] }),
              /* @__PURE__ */ jsx53("div", { children: Button.create(cta, {
                defaultProps: () => ({
                  className: productInfoSlotClassNames.cta,
                  size: 14,
                  variables: {
                    buttonPadding: pxToRem(9),
                    buttonSize14TextFontSize: pxToRem(12)
                  }
                }),
                overrideProps: () => {
                  return {
                    className: mergeClassName(
                      cta,
                      productInfoSlotClassNames.cta
                    ),
                    styles: {
                      marginLeft: pxToRem(4)
                    }
                  };
                }
              }) })
            ]
          },
          `product-info`
        )
      }
    );
    setEnd();
    return result;
  }
);
ProductInfo.displayName = "ProductInfo";
ProductInfo.create = createShorthandFactory52({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ProductInfo
});
ProductInfo.defaultProps = {
  as: defaultRootElementType17,
  accessibility: cardBehavior10
};
ProductInfo.handledProps = [
  ...commonHandledProps(),
  "productName",
  "professionalName",
  "pricePrefix",
  "price",
  "variants",
  "additionalVariantCount",
  "cta",
  "variant"
];

// packages/aria/src/components/FooterNavigation/FooterNavigation.tsx
import { forwardRef as forwardRef55, Fragment as Fragment10 } from "react";
import {
  useFluentContext as useFluentContext57,
  getElementType as getElementType42,
  useAccessibility as useAccessibility52,
  useTelemetry as useTelemetry57,
  useStyles as useStyles57,
  useUnhandledProps as useUnhandledProps56
} from "@fluentui/react-northstar";

// packages/aria/src/components/FooterNavigation/FooterNavigationItem.tsx
import { forwardRef as forwardRef53 } from "react";
import {
  useFluentContext as useFluentContext55,
  getElementType as getElementType40,
  useAccessibility as useAccessibility50,
  useTelemetry as useTelemetry55,
  useStyles as useStyles55,
  useUnhandledProps as useUnhandledProps53,
  createShorthandFactory as createShorthandFactory53
} from "@fluentui/react-northstar";
import { menuBehavior as menuBehavior7 } from "@fluentui/react-northstar";
import { jsx as jsx54 } from "react/jsx-runtime";
var footerNavigationItemClassName = "ui-FooterNavigationItem";
var FooterNavigationItem = forwardRef53((props, ref) => {
  const context = useFluentContext55();
  const { setStart, setEnd } = useTelemetry55(
    FooterNavigationItem.displayName,
    context.telemetry
  );
  setStart();
  const {
    className,
    styles,
    variables,
    accessibility = menuBehavior7,
    design,
    content
  } = props;
  const getA11yProps = useAccessibility50(accessibility, {
    debugName: FooterNavigationItem.displayName,
    rtl: context.rtl
  });
  const { classes } = useStyles55(FooterNavigationItem.displayName, {
    className: footerNavigationItemClassName,
    mapPropsToInlineStyles: () => ({
      className,
      styles,
      variables,
      design
    }),
    rtl: context.rtl,
    unstable_props: props
  });
  const unhandledProps = useUnhandledProps53(
    FooterNavigationItem.handledProps,
    props
  );
  const ElementType = getElementType40(props);
  const result = /* @__PURE__ */ jsx54(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      children: Label.create(content, {
        defaultProps: () => ({
          variant: "01"
        })
      })
    }
  );
  setEnd();
  return result;
});
FooterNavigationItem.create = createShorthandFactory53({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: FooterNavigationItem,
  mappedProp: "content"
});
FooterNavigationItem.displayName = "FooterNavigationItem";
FooterNavigationItem.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "content",
  "as"
];
FooterNavigationItem.defaultProps = {
  accessibility: menuBehavior7,
  as: "li"
};
var FooterNavigationItem_default = FooterNavigationItem;

// packages/aria/src/components/Subscription/Subscription.tsx
import { forwardRef as forwardRef54 } from "react";
import {
  useFluentContext as useFluentContext56,
  getElementType as getElementType41,
  useAccessibility as useAccessibility51,
  useTelemetry as useTelemetry56,
  useStyles as useStyles56,
  useUnhandledProps as useUnhandledProps54,
  Box as Box2,
  createShorthandFactory as createShorthandFactory54
} from "@fluentui/react-northstar";
import { jsxs as jsxs36 } from "react/jsx-runtime";
var defaultRootElementType18 = "div";
var subscriptionClassName = "ui-subscription";
var Subscription = forwardRef54(
  (props, ref) => {
    const context = useFluentContext56();
    const { setStart, setEnd } = useTelemetry56(
      Subscription.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      input,
      submit,
      error,
      success,
      header
    } = props;
    const getA11yProps = useAccessibility51(
      accessibility,
      {
        debugName: Subscription.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles56(
      Subscription.displayName,
      {
        className: subscriptionClassName,
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps54(
      Subscription.handledProps,
      props
    );
    const ElementType = getElementType41(props);
    const result = /* @__PURE__ */ jsxs36(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: [
          Caption.create(header, {
            defaultProps: () => ({
              children: "Stay in the loop",
              className: classes["header"],
              variant: "03"
            })
          }),
          /* @__PURE__ */ jsxs36(Box2, { children: [
            /* @__PURE__ */ jsxs36(Flex, { fill: true, className: classes["inputRow"], children: [
              Input.create(input, {
                defaultProps: () => ({
                  name: "subscribe",
                  type: "text",
                  placeholder: "Email address"
                })
              }),
              Button.create(submit, {
                defaultProps: () => ({
                  type: "submit",
                  children: "SUBSCRIBE"
                })
              })
            ] }),
            Box2.create(error, {
              defaultProps: () => ({
                className: classes["displayError"]
              })
            })
          ] }),
          Caption.create(success, {
            defaultProps: () => ({
              variant: "03",
              className: classes["success"]
            })
          })
        ]
      }
    );
    setEnd();
    return result;
  }
);
Subscription.displayName = "Subscription";
Subscription.defaultProps = {
  as: defaultRootElementType18
};
Subscription.create = createShorthandFactory54({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: Subscription
});
Subscription.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "disabled",
  "input",
  "submit",
  "error",
  "success"
];

// packages/aria/src/components/Accordion/Accordion.tsx
import React12 from "react";
import { useUnhandledProps as useUnhandledProps55 } from "@fluentui/react-northstar";
import { accordionTitleClassName } from "@fluentui/react-northstar";
import { Accordion as AccordionBase } from "@fluentui/react-northstar";
import { AddFill, SubtractFill } from "@archipro-design/icons";
import {
  accordionClassName,
  accordionSlotClassNames,
  accordionTitleClassName as accordionTitleClassName2,
  accordionTitleSlotClassNames,
  accordionContentClassName
} from "@fluentui/react-northstar";
import { jsx as jsx55 } from "react/jsx-runtime";
var displayName17 = "Accordion";
var accordionTitleCustomSlotClassNames = {
  titleIndicator: `${accordionTitleClassName}__indicator`
};
var Accordion = React12.forwardRef(
  (props, ref) => {
    const className = props.className;
    const unhandledProps = useUnhandledProps55(Accordion.handledProps, props);
    return /* @__PURE__ */ jsx55(
      AccordionBase,
      {
        className,
        renderPanelTitle: (Component, itemProps) => /* @__PURE__ */ jsx55(
          Component,
          {
            indicator: {
              className: accordionTitleCustomSlotClassNames.titleIndicator,
              children: itemProps.active ? props.indicatorIconActive ?? /* @__PURE__ */ jsx55(
                SubtractFill,
                {
                  styles: () => ({
                    width: pxToRem(32),
                    height: pxToRem(32)
                  })
                }
              ) : props.indicatorIcon ?? /* @__PURE__ */ jsx55(
                AddFill,
                {
                  styles: () => ({
                    width: pxToRem(32),
                    height: pxToRem(32)
                  })
                }
              )
            },
            content: itemProps.content,
            ...itemProps
          }
        ),
        ...unhandledProps,
        ref
      }
    );
  }
);
Accordion.displayName = displayName17;
Accordion.handledProps = [
  ...commonHandledProps(),
  "indicatorIcon",
  "indicatorIconActive"
];

// packages/aria/src/components/FooterNavigation/FooterNavigation.tsx
import { ArrowDownSLine as ArrowDownSLine2 } from "@archipro-design/icons";
import cx13 from "classnames";
import { jsx as jsx56, jsxs as jsxs37 } from "react/jsx-runtime";
var defaultRootElementType19 = "footer";
var footerNavigationClassName = "ui-footerNavigation";
var footerNavigationSlotClassNames = {
  copyright: `${footerNavigationClassName}__copyright`,
  footerCopyrightLink: `${footerNavigationClassName}__footerCopyrightLink`,
  footerLinksHeading: `${footerNavigationClassName}__footerLinksHeading`,
  left: `${footerNavigationClassName}__container_left`,
  right: `${footerNavigationClassName}__container_right`,
  container: `${footerNavigationClassName}__container`,
  footerLinkGroups: `${footerNavigationClassName}__footerLinkGroups`,
  accordion: `${footerNavigationClassName}__accordion`,
  subscription: `${footerNavigationClassName}__subscription`
};
var FooterNavigation = forwardRef55(
  (props, ref) => {
    const context = useFluentContext57();
    const { setStart, setEnd } = useTelemetry57(
      FooterNavigation.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      design,
      footerText,
      footerNavigationItem,
      footerCopyRightsLinks,
      subscription,
      accessibility,
      appDisplayMode = { mobile: false, desktop: true },
      logo = {}
    } = props;
    const { desktop } = appDisplayMode;
    const getA11yProps = useAccessibility52(
      accessibility,
      {
        debugName: FooterNavigation.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles57(
      FooterNavigation.displayName,
      {
        className: footerNavigationClassName,
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        mapPropsToStyles: () => ({
          isDesktop: appDisplayMode.desktop
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps56(
      FooterNavigation.handledProps,
      props
    );
    const ElementType = getElementType42(props);
    const renderFooterLogo = /* @__PURE__ */ jsxs37(Flex, { className: classes["footerLogoAndText"], children: [
      Logo.create(logo, {
        defaultProps: () => ({
          className: classes["footerMobile"],
          showFull: true,
          variables: {
            logoHeight: pxToRem(17.94),
            logoWidth: pxToRem(150)
          }
        })
      }),
      Logo.create(logo, {
        defaultProps: () => ({
          className: classes["footerDesktop"],
          showFull: true,
          variables: {
            logoHeight: pxToRem(17.94),
            logoWidth: pxToRem(150)
          }
        })
      })
    ] });
    const buildMobileAccordion = (navigationLinks) => {
      const buildPanelNavigation = navigationLinks.map((link, i) => {
        const headerLinks = Object.keys(link)[0];
        const panelContent = /* @__PURE__ */ jsx56(Flex, { className: classes["footerLinkGroups"], children: /* @__PURE__ */ jsx56("ul", { children: link[headerLinks].map((val, i2) => {
          return FooterNavigationItem_default.create(val, {
            defaultProps: () => ({
              key: i2
            })
          });
        }) }) });
        return {
          title: {
            content: headerLinks,
            id: "title" + i,
            key: "keytitle" + i
          },
          content: {
            content: panelContent,
            id: "content" + i,
            key: "keycontent" + i
          },
          key: i
        };
      });
      return /* @__PURE__ */ jsx56(
        Accordion,
        {
          panels: buildPanelNavigation,
          indicatorIconActive: /* @__PURE__ */ jsx56(ArrowDownSLine2, { style: { transform: "scale(-1)" } }),
          indicatorIcon: /* @__PURE__ */ jsx56(ArrowDownSLine2, {})
        }
      );
    };
    const renderFooterNavigationLinksMobile = /* @__PURE__ */ jsx56("div", { className: classes["footerMobile"], children: buildMobileAccordion(footerNavigationItem) });
    const renderFooterNavigationLinksDesktop = /* @__PURE__ */ jsx56("div", { className: classes["footerDesktop"], children: /* @__PURE__ */ jsx56(
      Flex,
      {
        fill: true,
        className: footerNavigationSlotClassNames.footerLinkGroups,
        children: footerNavigationItem.map((link, index) => {
          const headerLinks = Object.keys(link)[0];
          return /* @__PURE__ */ jsxs37(
            Flex,
            {
              className: classes["footerLinkGroups"],
              children: [
                /* @__PURE__ */ jsx56(
                  Label,
                  {
                    variant: "01",
                    className: footerNavigationSlotClassNames.footerLinksHeading,
                    children: headerLinks
                  }
                ),
                /* @__PURE__ */ jsx56("ul", { children: link[headerLinks].map((val, i) => {
                  return FooterNavigationItem_default.create(
                    val,
                    {
                      defaultProps: () => ({
                        key: i
                      })
                    }
                  );
                }) })
              ]
            },
            index
          );
        })
      }
    ) });
    const renderFooterCopyright = /* @__PURE__ */ jsxs37(
      Flex,
      {
        className: classes["footerCopyright"],
        gap: "space-50",
        vAlign: "center",
        children: [
          /* @__PURE__ */ jsx56(
            Label,
            {
              variant: "02",
              className: footerNavigationSlotClassNames.copyright,
              children: `\xA9 ArchiPro ${(/* @__PURE__ */ new Date()).getFullYear()}`
            }
          ),
          /* @__PURE__ */ jsx56(Flex, { children: footerCopyRightsLinks.map((item, i) => {
            return /* @__PURE__ */ jsxs37(Fragment10, { children: [
              i !== 0 ? /* @__PURE__ */ jsx56(
                "div",
                {
                  className: classes["divider"]
                },
                `divider-${i}`
              ) : null,
              Label.create(item, {
                defaultProps: () => ({
                  variant: "02",
                  link: true,
                  className: footerNavigationSlotClassNames.footerCopyrightLink
                })
              })
            ] }, i);
          }) })
        ]
      }
    );
    const renderSubscription = /* @__PURE__ */ jsx56(
      Box,
      {
        className: cx13([
          classes["subscription"],
          footerNavigationSlotClassNames.subscription
        ]),
        children: Subscription.create(subscription)
      }
    );
    const result = /* @__PURE__ */ jsx56(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: /* @__PURE__ */ jsxs37(
          Container_default,
          {
            variables: {
              background: "transparent"
            },
            children: [
              /* @__PURE__ */ jsxs37(
                Flex,
                {
                  space: "between",
                  className: footerNavigationSlotClassNames.container,
                  children: [
                    /* @__PURE__ */ jsxs37(
                      Flex,
                      {
                        column: true,
                        className: footerNavigationSlotClassNames.left,
                        children: [
                          renderFooterLogo,
                          desktop && /* @__PURE__ */ jsx56(
                            Flex,
                            {
                              className: classes["footerCopyrightSubscription"],
                              children: renderSubscription
                            }
                          )
                        ]
                      }
                    ),
                    /* @__PURE__ */ jsxs37(
                      Flex,
                      {
                        column: true,
                        className: footerNavigationSlotClassNames.right,
                        children: [
                          desktop && /* @__PURE__ */ jsx56(
                            Box,
                            {
                              className: classes["footerDescription"],
                              content: footerText
                            }
                          ),
                          desktop && renderFooterNavigationLinksDesktop,
                          !desktop && renderFooterNavigationLinksMobile,
                          !desktop && renderSubscription
                        ]
                      }
                    )
                  ]
                }
              ),
              renderFooterCopyright
            ]
          }
        )
      }
    );
    setEnd();
    return result;
  }
);
FooterNavigation.displayName = "FooterNavigation";
FooterNavigation.defaultProps = {
  as: defaultRootElementType19
};
FooterNavigation.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "footerText",
  "footerNavigationItem",
  "footerCopyRightsLinks",
  "subscription",
  "accessibility",
  "logo",
  "appDisplayMode"
];

// packages/aria/src/components/Breadcrumb/Breadcrumb.tsx
import { forwardRef as forwardRef58 } from "react";
import {
  useFluentContext as useFluentContext60,
  useAccessibility as useAccessibility55,
  useTelemetry as useTelemetry60,
  useStyles as useStyles60,
  useUnhandledProps as useUnhandledProps59,
  breadcrumbBehavior,
  getElementType as getElementType45
} from "@fluentui/react-northstar";

// packages/aria/src/components/Breadcrumb/BreadcrumbItem.tsx
import { forwardRef as forwardRef56 } from "react";
import {
  useFluentContext as useFluentContext58,
  getElementType as getElementType43,
  useAccessibility as useAccessibility53,
  useTelemetry as useTelemetry58,
  useStyles as useStyles58,
  useUnhandledProps as useUnhandledProps57,
  breadcrumbItemBehavior,
  createShorthandFactory as createShorthandFactory55
} from "@fluentui/react-northstar";
import { jsx as jsx57 } from "react/jsx-runtime";
var breadcrumbItemClassName = "ui-breadcrumbItem";
var displayName18 = "BreadcrumbItem";
var BreadcrumbItem = forwardRef56(
  (props, ref) => {
    const context = useFluentContext58();
    const { setStart, setEnd } = useTelemetry58(
      displayName18,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility = breadcrumbItemBehavior,
      design,
      link = true
    } = props;
    const getA11yProps = useAccessibility53(accessibility, {
      debugName: displayName18,
      rtl: context.rtl
    });
    const { classes } = useStyles58(displayName18, {
      className: breadcrumbItemClassName,
      mapPropsToStyles: () => ({
        link
      }),
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    });
    const unhandledProps = useUnhandledProps57(
      BreadcrumbItem.handledProps,
      props
    );
    const ElementType = getElementType43(props);
    const result = /* @__PURE__ */ jsx57(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: /* @__PURE__ */ jsx57(
          Caption,
          {
            variant: "03",
            variables: { captionColor: "inherit" },
            as: "span",
            children: props.label
          }
        )
      }
    );
    setEnd();
    return result;
  }
);
BreadcrumbItem.displayName = displayName18;
BreadcrumbItem.create = createShorthandFactory55({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: BreadcrumbItem,
  mappedProp: "label"
});
BreadcrumbItem.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "label",
  "link"
];
BreadcrumbItem.defaultProps = {
  as: "span"
};
var BreadcrumbItem_default = BreadcrumbItem;

// packages/aria/src/components/Breadcrumb/BreadcrumbDivider.tsx
import { forwardRef as forwardRef57 } from "react";
import {
  useFluentContext as useFluentContext59,
  getElementType as getElementType44,
  useAccessibility as useAccessibility54,
  useTelemetry as useTelemetry59,
  useStyles as useStyles59,
  useUnhandledProps as useUnhandledProps58,
  breadcrumbDividerBehavior,
  createShorthandFactory as createShorthandFactory56
} from "@fluentui/react-northstar";
import { jsx as jsx58 } from "react/jsx-runtime";
var breadcrumbDividerClassName = "ui-breadcrumbDivider";
var displayName19 = "BreadcrumbDivider";
var BreadcrumbDivider = forwardRef57((props, ref) => {
  const context = useFluentContext59();
  const { setStart, setEnd } = useTelemetry59(displayName19, context.telemetry);
  setStart();
  const {
    className,
    styles,
    variables,
    accessibility = breadcrumbDividerBehavior,
    design,
    content = "/"
  } = props;
  const getA11yProps = useAccessibility54(accessibility, {
    debugName: displayName19,
    rtl: context.rtl
  });
  const { classes } = useStyles59(displayName19, {
    className: breadcrumbDividerClassName,
    mapPropsToInlineStyles: () => ({
      className,
      styles,
      variables,
      design
    }),
    rtl: context.rtl,
    unstable_props: props
  });
  const unhandledProps = useUnhandledProps58(
    BreadcrumbDivider.handledProps,
    props
  );
  const ElementType = getElementType44(props);
  const result = /* @__PURE__ */ jsx58(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      children: /* @__PURE__ */ jsx58(Caption, { variant: "03", variables: { captionColor: "inherit" }, children: content })
    }
  );
  setEnd();
  return result;
});
BreadcrumbDivider.defaultProps = {
  as: "span"
};
BreadcrumbDivider.displayName = displayName19;
BreadcrumbDivider.create = createShorthandFactory56({
  Component: BreadcrumbDivider,
  mappedProp: "content"
});
BreadcrumbDivider.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "content"
];

// packages/aria/src/themes/base/components/Breadcrumb/breadcrumbVariables.ts
var breadcrumbVariables = (siteVars) => ({
  breadcrumbLogoColor: siteVars.colorScheme.default?.["foreground2"],
  breadcrumbLogoBackgroundHover: `rgba(255,255,255,0.1)`,
  breadcrumbLogoWidth: pxToRem(16),
  breadcrumbLogoHeight: pxToRem(14),
  breadcrumbLogoSpacing: "0",
  breadcrumbHeight: 22,
  breadcrumbDivider: "/",
  breadcrumbFirstChildLeftMargin: pxToRem(0),
  breadcrumbFontSize: pxToRem(16)
});

// packages/aria/src/components/Breadcrumb/Breadcrumb.tsx
import { jsx as jsx59, jsxs as jsxs38 } from "react/jsx-runtime";
var breadcrumbClassName = "ui-breadcrumb";
var displayName20 = "Breadcrumb";
var Breadcrumb = forwardRef58(
  (props, ref) => {
    const context = useFluentContext60();
    const { setStart, setEnd } = useTelemetry60(
      displayName20,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility = breadcrumbBehavior,
      design,
      items,
      logo,
      home
    } = props;
    const getA11yProps = useAccessibility55(accessibility, {
      debugName: displayName20,
      rtl: context.rtl
    });
    const { classes } = useStyles60(displayName20, {
      className: breadcrumbClassName,
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    });
    const unhandledProps = useUnhandledProps59(
      Breadcrumb.handledProps,
      props
    );
    const breadcrumbVars = context.theme.componentVariables["Breadcrumb"] ? context.theme.componentVariables["Breadcrumb"] : breadcrumbVariables;
    const compVars = breadcrumbVars(context.theme.siteVariables);
    const ElementType = getElementType45(props);
    const divider = BreadcrumbDivider.create(compVars.breadcrumbDivider);
    const result = /* @__PURE__ */ jsx59(
      Flex,
      {
        hAlign: "start",
        vAlign: "center",
        as: ElementType,
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: /* @__PURE__ */ jsxs38(Flex, { className: classes["wrapper"], vAlign: "center", children: [
          logo ? /* @__PURE__ */ jsx59(Box, { className: classes["logo"], children: Logo.create(logo) }) : home && /* @__PURE__ */ jsx59(Box, { className: classes["group"], children: BreadcrumbItem_default.create(home) }),
          items.map((i, index) => {
            return /* @__PURE__ */ jsxs38(Box, { className: classes["group"], children: [
              (home || logo || index !== 0) && divider,
              BreadcrumbItem_default.create(i)
            ] }, index);
          })
        ] })
      }
    );
    setEnd();
    return result;
  }
);
Breadcrumb.displayName = displayName20;
Breadcrumb.defaultProps = {
  as: "nav"
};
Breadcrumb.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "items",
  "logo",
  "home"
];

// packages/aria/src/components/Profile/PersonalProfile/PersonalProfile.tsx
import { forwardRef as forwardRef59 } from "react";
import {
  useFluentContext as useFluentContext61,
  getElementType as getElementType46,
  useAccessibility as useAccessibility56,
  useTelemetry as useTelemetry61,
  useStyles as useStyles61,
  useUnhandledProps as useUnhandledProps60,
  createShorthandFactory as createShorthandFactory57
} from "@fluentui/react-northstar";
import { menuItemBehavior as menuItemBehavior3 } from "@fluentui/accessibility";
import { jsx as jsx60, jsxs as jsxs39 } from "react/jsx-runtime";
var personalProfileClassName = "ui-personalProfile";
var personalProfileSlotClassNames = {
  contentContainer: `${personalProfileClassName}__contentContainer`,
  avatar: `${personalProfileClassName}__avatar`,
  fullName: `${personalProfileClassName}__fullName`,
  viewProfileLink: `${personalProfileClassName}__viewProfileLink`
};
var PersonalProfile = forwardRef59(
  (props, ref) => {
    const context = useFluentContext61();
    const { setStart, setEnd } = useTelemetry61(
      PersonalProfile.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      avatar,
      fullName,
      profileLabel = "Edit Profile",
      profileLink,
      variant = "01"
    } = props;
    const getA11yProps = useAccessibility56(
      accessibility,
      {
        debugName: PersonalProfile.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles61(
      PersonalProfile.displayName,
      {
        className: personalProfileClassName,
        mapPropsToStyles: () => ({
          variant
        }),
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps60(
      PersonalProfile.handledProps,
      props
    );
    const ElementType = getElementType46(props);
    const result = /* @__PURE__ */ jsxs39(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        onClick: () => {
          if (profileLink) window.location.href = profileLink;
        },
        children: [
          Avatar.create(avatar, {
            defaultProps: () => ({
              size: 40,
              className: personalProfileSlotClassNames.avatar
            })
          }),
          /* @__PURE__ */ jsxs39(
            Flex,
            {
              column: true,
              className: personalProfileSlotClassNames.contentContainer,
              children: [
                Label.create(fullName, {
                  defaultProps: () => ({
                    className: personalProfileSlotClassNames.fullName,
                    weight: variant === "01" ? "medium" : "regular"
                  })
                }),
                /* @__PURE__ */ jsx60(
                  "span",
                  {
                    className: personalProfileSlotClassNames.viewProfileLink,
                    children: profileLabel
                  }
                )
              ]
            }
          )
        ]
      }
    );
    setEnd();
    return result;
  }
);
PersonalProfile.displayName = "PersonalProfile";
PersonalProfile.create = createShorthandFactory57({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: PersonalProfile,
  mappedProp: "fullName"
});
PersonalProfile.defaultProps = {
  accessibility: menuItemBehavior3
};
PersonalProfile.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "fullName",
  "profileLabel",
  "profileLink"
];

// packages/aria/src/components/NavigationMenu/NavigationMenu.tsx
import { forwardRef as forwardRef60 } from "react";
import {
  useFluentContext as useFluentContext62,
  getElementType as getElementType47,
  useAccessibility as useAccessibility57,
  useTelemetry as useTelemetry62,
  useStyles as useStyles62,
  useUnhandledProps as useUnhandledProps61,
  menuBehavior as menuBehavior8
} from "@fluentui/react-northstar";

// packages/aria/src/utils/formatNumber.ts
var getFormatNumberWithCommas = (num) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

// packages/aria/src/components/NavigationMenu/NavigationMenu.tsx
import { Fragment as Fragment11, jsx as jsx61, jsxs as jsxs40 } from "react/jsx-runtime";
var defaultRootElementType20 = "div";
var navigationMenuClassName = "ui-navigationMenu";
var NavigationMenu = forwardRef60(
  (props, ref) => {
    const context = useFluentContext62();
    const { setStart, setEnd } = useTelemetry62(
      NavigationMenu.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      majorNavigation,
      minorNavigation
    } = props;
    const getA11yProps = useAccessibility57(
      accessibility,
      {
        debugName: NavigationMenu.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles62(
      NavigationMenu.displayName,
      {
        className: navigationMenuClassName,
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps61(
      NavigationMenu.handledProps,
      props
    );
    const ElementType = getElementType47(props);
    const result = /* @__PURE__ */ jsxs40(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: [
          /* @__PURE__ */ jsx61("div", { className: classes["majorNavigation"], children: majorNavigation.map(({ label, count, ...rest }) => {
            return Flex.create(rest, {
              defaultProps: () => ({
                key: `minorNavLink-${label}`,
                vAlign: "start",
                className: classes["majorNavItem"],
                children: /* @__PURE__ */ jsxs40(Fragment11, { children: [
                  Header.create(label, {
                    defaultProps: () => ({
                      as: "h4",
                      variant: "04",
                      maximumLines: 1
                    })
                  }),
                  count ? Caption.create(
                    getFormatNumberWithCommas(
                      count ?? 0
                    ),
                    {
                      defaultProps: () => ({
                        variant: "03",
                        maximumLines: 1
                      })
                    }
                  ) : null
                ] })
              })
            });
          }) }),
          /* @__PURE__ */ jsx61("div", { className: classes["minorNavigation"], children: minorNavigation.map(({ label, ...rest }, index) => {
            return Flex.create(rest, {
              defaultProps: () => ({
                key: `minorNavLink-${index}`,
                vAlign: "start",
                className: classes["minorNavItem"],
                children: Label.create(label, {
                  defaultProps: () => ({
                    variant: "01",
                    maximumLines: 1
                  })
                })
              })
            });
          }) }),
          /* @__PURE__ */ jsx61(Box, { className: classes["copyrightSpacer"] }),
          /* @__PURE__ */ jsx61(Flex, { hAlign: "end", children: /* @__PURE__ */ jsx61(
            Caption,
            {
              color: "grey",
              variant: "02",
              className: classes["copyright"],
              children: `${"\xA9"} ArchiPro ${(/* @__PURE__ */ new Date()).getFullYear()}`
            }
          ) })
        ]
      }
    );
    setEnd();
    return result;
  }
);
NavigationMenu.displayName = "NavigationMenu";
NavigationMenu.defaultProps = {
  as: defaultRootElementType20,
  accessibility: menuBehavior8
};
NavigationMenu.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  // Extra props that are handled
  "majorNavigation",
  "minorNavigation"
];

// packages/aria/src/components/Badge/Badge.tsx
import { forwardRef as forwardRef61 } from "react";
import {
  useFluentContext as useFluentContext63,
  getElementType as getElementType48,
  useAccessibility as useAccessibility58,
  useTelemetry as useTelemetry63,
  useStyles as useStyles63,
  useUnhandledProps as useUnhandledProps62,
  createShorthandFactory as createShorthandFactory58
} from "@fluentui/react-northstar";

// packages/aria/src/themes/base/components/Badge/badgeVariables.ts
var badgeVariables = (siteVars) => ({
  badgeWidth: pxToRem(16),
  badgeHeight: pxToRem(16),
  badgeBackgroundColor: siteVars.colors.gold["000"],
  textFontSize: pxToRem(14),
  textLineHeight: pxToRem(16)
});
var resolveBadgeStyleVariables = (siteVars, props, multipleNumbers = false) => {
  const { size, color } = props;
  const isLarge = size === "lg";
  const badgeLargeWidth = multipleNumbers ? pxToRem(36) : pxToRem(24);
  const badgeMediumWidth = multipleNumbers ? pxToRem(26) : pxToRem(16);
  return {
    badgeWidth: isLarge ? badgeLargeWidth : badgeMediumWidth,
    badgeHeight: isLarge ? pxToRem(24) : pxToRem(16),
    badgeBackgroundColor: color === "red" ? siteVars.colors.danger[200] : siteVars.colors.gold["000"],
    textFontSize: isLarge ? pxToRem(16) : pxToRem(14),
    textLineHeight: isLarge ? "150%" : pxToRem(16)
  };
};
var resolveBadgePositionVariables = (props, hasChildren = false, multipleNumbers = false) => {
  if (!hasChildren) return {};
  const { size, anchorVertical, anchorHorizontal, badgeInsideBox } = props;
  const isLarge = size === "lg";
  const yOffset = multipleNumbers ? isLarge ? pxToRem(6) : pxToRem(5) : "";
  let translateX, translateY;
  let top, bottom, left, right;
  if (anchorVertical === "top") {
    top = "0";
    bottom = "unset";
    translateY = "translateY(-50%)";
  } else {
    top = "unset";
    bottom = "0";
    translateY = "translateY(50%)";
  }
  if (anchorHorizontal === "left") {
    left = "0";
    right = "unset";
    translateX = "translateX(-50%)";
    if (yOffset) translateX = `${translateX} translateX(-${yOffset})`;
  } else {
    left = "unset";
    right = "0";
    translateX = "translateX(50%)";
    if (yOffset) translateX = `${translateX} translateX(${yOffset})`;
  }
  const transform = badgeInsideBox ? "unset" : `scale(1) ${translateX} ${translateY}`;
  return {
    badgePosition: {
      position: "absolute",
      top,
      bottom,
      left,
      right,
      transform
    }
  };
};

// packages/aria/src/components/Badge/Badge.tsx
import { jsx as jsx62, jsxs as jsxs41 } from "react/jsx-runtime";
var defaultRootElementType21 = "div";
var badgeClassName = "ui-badge";
var DEFAULT_MAX_COUNT = 9;
var Badge = forwardRef61((props, ref) => {
  const context = useFluentContext63();
  const { setStart, setEnd } = useTelemetry63(
    Badge.displayName,
    context.telemetry
  );
  setStart();
  const {
    className,
    styles,
    variables,
    accessibility,
    design,
    children,
    count,
    dotOnly
  } = props;
  const hasChildren = !!children;
  const getA11yProps = useAccessibility58(
    accessibility,
    {
      debugName: Badge.displayName,
      rtl: context.rtl
    }
  );
  const multipleNumbers = !!count && count > DEFAULT_MAX_COUNT && !dotOnly;
  const { classes } = useStyles63(Badge.displayName, {
    className: badgeClassName,
    mapPropsToStyles: () => ({
      ...props
    }),
    mapPropsToInlineStyles: () => ({
      className,
      styles,
      variables: {
        ...resolveBadgeStyleVariables(
          context.theme.siteVariables,
          props,
          multipleNumbers
        ),
        ...resolveBadgePositionVariables(
          props,
          hasChildren,
          multipleNumbers
        ),
        ...variables
      },
      design
    }),
    rtl: context.rtl,
    unstable_props: props
  });
  const unhandledProps = useUnhandledProps62(Badge.handledProps, props);
  const ElementType = getElementType48(props);
  const displayNum = multipleNumbers ? `${DEFAULT_MAX_COUNT}+` : count;
  const result = /* @__PURE__ */ jsxs41(
    "span",
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      children: [
        children,
        !!count && count > 0 && /* @__PURE__ */ jsx62(ElementType, { className: classes["badge"], "data-testid": "badge", children: !dotOnly && /* @__PURE__ */ jsx62("span", { className: classes["counter"], children: displayNum }) })
      ]
    }
  );
  setEnd();
  return result;
});
Badge.displayName = "Badge";
Badge.create = createShorthandFactory58({
  Component: Badge,
  mappedProp: "count"
});
Badge.defaultProps = {
  as: defaultRootElementType21,
  count: 0
};
Badge.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "count",
  "badgeInsideBox",
  "anchorHorizontal",
  "anchorVertical",
  "color",
  "dotOnly",
  "size"
];
var Badge_default = Badge;

// packages/aria/src/components/PersonalMenu/PersonalMenu.tsx
import { forwardRef as forwardRef65 } from "react";
import {
  useFluentContext as useFluentContext67,
  useAccessibility as useAccessibility62,
  useTelemetry as useTelemetry67,
  useStyles as useStyles67,
  useUnhandledProps as useUnhandledProps66,
  menuBehavior as menuBehavior10,
  createShorthandFactory as createShorthandFactory62
} from "@fluentui/react-northstar";

// packages/aria/src/components/Profile/PersonalProfileMenu/PersonalProfileMenu.tsx
import { forwardRef as forwardRef64 } from "react";
import {
  useFluentContext as useFluentContext66,
  getElementType as getElementType50,
  useAccessibility as useAccessibility61,
  useTelemetry as useTelemetry66,
  useStyles as useStyles66,
  useUnhandledProps as useUnhandledProps65,
  menuBehavior as menuBehavior9,
  createShorthandFactory as createShorthandFactory61
} from "@fluentui/react-northstar";

// packages/aria/src/components/Profile/ProfileMenuItem/ProfileMenuItem.tsx
import { forwardRef as forwardRef62 } from "react";
import {
  useFluentContext as useFluentContext64,
  getElementType as getElementType49,
  useAccessibility as useAccessibility59,
  useTelemetry as useTelemetry64,
  useStyles as useStyles64,
  useUnhandledProps as useUnhandledProps63,
  menuItemBehavior as menuItemBehavior4,
  createShorthandFactory as createShorthandFactory59
} from "@fluentui/react-northstar";
import { jsx as jsx63, jsxs as jsxs42 } from "react/jsx-runtime";
var profileMenuItemClassName = "ui-profileMenuItem";
var ProfileMenuItem = forwardRef62(
  (props, ref) => {
    const context = useFluentContext64();
    const { setStart, setEnd } = useTelemetry64(
      ProfileMenuItem.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      icon,
      iconAfter,
      label,
      link,
      badge,
      onClick,
      variant = "01"
    } = props;
    const getA11yProps = useAccessibility59(
      accessibility,
      {
        debugName: ProfileMenuItem.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles64(
      ProfileMenuItem.displayName,
      {
        className: profileMenuItemClassName,
        mapPropsToStyles: () => ({
          variant
        }),
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps63(
      ProfileMenuItem.handledProps,
      props
    );
    const ElementType = getElementType49(props);
    const result = /* @__PURE__ */ jsxs42(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        onClick: (e) => {
          if (link) window.location.href = link;
          onClick?.(e);
        },
        children: [
          icon && /* @__PURE__ */ jsx63(Flex, { className: classes["iconContainer"], children: icon }),
          /* @__PURE__ */ jsx63("span", { className: classes["label"], children: label }),
          Badge.create(badge),
          iconAfter && /* @__PURE__ */ jsx63(Flex, { className: classes["iconAfterContainer"], children: iconAfter })
        ]
      }
    );
    setEnd();
    return result;
  }
);
ProfileMenuItem.displayName = "ProfileMenuItem";
ProfileMenuItem.create = createShorthandFactory59({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ProfileMenuItem,
  mappedProp: "label"
});
ProfileMenuItem.defaultProps = {
  as: "div",
  accessibility: menuItemBehavior4,
  badge: { count: 0 }
};
ProfileMenuItem.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "as",
  // Extra props that are handled
  "icon",
  "label",
  "link",
  "badge",
  "onClick"
];

// packages/aria/src/components/Profile/PersonalProfileMenu/PersonalProfileMenu.tsx
import { SignOutLight } from "@archipro-design/icons";

// packages/aria/src/components/Profile/BusinessProfile/BusinessProfile.tsx
import { forwardRef as forwardRef63, useState as useState7 } from "react";
import {
  useFluentContext as useFluentContext65,
  useAccessibility as useAccessibility60,
  useTelemetry as useTelemetry65,
  useStyles as useStyles65,
  useUnhandledProps as useUnhandledProps64,
  buttonBehavior as buttonBehavior3,
  createShorthandFactory as createShorthandFactory60
} from "@fluentui/react-northstar";
import { AngleRight } from "@archipro-design/icons";
import { jsx as jsx64, jsxs as jsxs43 } from "react/jsx-runtime";
var defaultRootElementType22 = "div";
var businessProfileClassName = "ui-businessProfile";
var BusinessProfile = forwardRef63(
  (props, ref) => {
    const context = useFluentContext65();
    const { setStart, setEnd } = useTelemetry65(
      BusinessProfile.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      logo,
      professionalName,
      countryTag,
      unreadMessages,
      professionalHref,
      menuItems,
      variant = "01"
    } = props;
    const getA11yProps = useAccessibility60(
      accessibility,
      {
        debugName: BusinessProfile.displayName,
        rtl: context.rtl
      }
    );
    const [toggleAccordian, setToggleAccordian] = useState7(false);
    const { classes } = useStyles65(
      BusinessProfile.displayName,
      {
        className: businessProfileClassName,
        mapPropsToStyles: () => ({
          toggleAccordian,
          variant
        }),
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps64(
      BusinessProfile.handledProps,
      props
    );
    const result = /* @__PURE__ */ jsxs43(
      Flex,
      {
        column: true,
        fill: true,
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: [
          /* @__PURE__ */ jsxs43(
            Flex,
            {
              className: classes["accordianTitle"],
              space: "between",
              vAlign: "start",
              children: [
                logo && /* @__PURE__ */ jsx64(
                  Badge_default,
                  {
                    count: unreadMessages ?? 0,
                    dotOnly: true,
                    badgeInsideBox: true,
                    anchorVertical: "top",
                    children: Avatar.create(logo, {
                      defaultProps: () => {
                        return {
                          size: variant === "01" ? 64 : 42
                        };
                      }
                    })
                  }
                ),
                /* @__PURE__ */ jsxs43(
                  Flex,
                  {
                    column: true,
                    fill: true,
                    className: classes["accordianTitleText"],
                    gap: "space-4",
                    onClick: () => variant === "02" && setToggleAccordian(!toggleAccordian),
                    children: [
                      Label.create(professionalName, {
                        defaultProps: () => ({
                          variant: "01",
                          maximumLines: 1,
                          weight: variant === "01" ? "medium" : "regular"
                        })
                      }),
                      /* @__PURE__ */ jsxs43(Flex, { vAlign: "center", children: [
                        /* @__PURE__ */ jsx64("div", { className: classes["countryTag"], children: Caption.create(countryTag, {
                          defaultProps: () => ({
                            variant: "03",
                            uc: true,
                            color: "white"
                          })
                        }) }),
                        variant === "01" && /* @__PURE__ */ jsx64(
                          "a",
                          {
                            className: classes["label"],
                            href: professionalHref,
                            children: "Switch to Business"
                          }
                        )
                      ] })
                    ]
                  }
                ),
                menuItems && variant === "02" && /* @__PURE__ */ jsx64(
                  Button,
                  {
                    color: "dark",
                    icon: /* @__PURE__ */ jsx64(AngleRight, {}),
                    onClick: () => setToggleAccordian(!toggleAccordian),
                    variant: "text",
                    size: 24,
                    variables: {
                      buttonPadding: 0,
                      iconPadding: 0
                    }
                  }
                )
              ]
            }
          ),
          menuItems && variant === "02" && /* @__PURE__ */ jsx64(Flex, { column: true, className: classes["accordianContent"], children: menuItems.map((item, index) => {
            return ProfileMenuItem.create(item, {
              defaultProps: () => ({ key: index, variant })
            });
          }) })
        ]
      }
    );
    setEnd();
    return result;
  }
);
BusinessProfile.displayName = "BusinessProfile";
BusinessProfile.defaultProps = {
  as: defaultRootElementType22,
  accessibility: buttonBehavior3
};
BusinessProfile.create = createShorthandFactory60({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: BusinessProfile
});
BusinessProfile.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  // Extra props that are handled
  "logo",
  "professionalName",
  "countryTag",
  "unreadMessages",
  "professionalHref",
  "menuItems"
];

// packages/aria/src/components/Profile/PersonalProfileMenu/PersonalProfileMenu.tsx
import { jsx as jsx65, jsxs as jsxs44 } from "react/jsx-runtime";
var personalProfileMenuClassName = "ui-personalProfileMenu";
var DEFAULT_MENU_ITEMS = [
  {
    label: "Edit Profile",
    link: "/member/edit-profile"
  },
  {
    label: "Inbox",
    link: "/member/inbox"
  },
  {
    label: "Orders",
    link: "/member/orders"
  },
  {
    label: "My Mood Boards",
    link: "/app/my-mood-boards"
  },
  {
    label: "Notifications",
    link: "/member/notifications"
  },
  {
    label: "Settings",
    link: "/member/settings"
  }
];
var PersonalProfileMenu = forwardRef64((props, ref) => {
  const context = useFluentContext66();
  const { setStart, setEnd } = useTelemetry66(
    PersonalProfileMenu.displayName,
    context.telemetry
  );
  setStart();
  const {
    className,
    styles,
    variables,
    design,
    menuItems,
    additionalItems,
    eventItems,
    logout,
    variant = "01",
    businessProfiles
  } = props;
  const getA11yProps = useAccessibility61(menuBehavior9, {
    debugName: PersonalProfileMenu.displayName,
    rtl: context.rtl
  });
  const { classes } = useStyles66(
    PersonalProfileMenu.displayName,
    {
      className: personalProfileMenuClassName,
      mapPropsToStyles: () => ({
        variant
      }),
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    }
  );
  const unhandledProps = useUnhandledProps65(
    PersonalProfileMenu.handledProps,
    props
  );
  const ElementType = getElementType50(props);
  const renderBusinessProfiles = businessProfiles && businessProfiles.length > 0 && /* @__PURE__ */ jsxs44(Flex, { column: true, className: classes["group"], children: [
    variant === "02" ? /* @__PURE__ */ jsx65(
      Caption,
      {
        className: personalMenuSlotClassNames.sectionTitle,
        children: "Business"
      }
    ) : null,
    /* @__PURE__ */ jsx65(
      Flex,
      {
        className: personalMenuSlotClassNames.professionalList,
        column: true,
        children: businessProfiles.map(
          (businessProfile, index) => BusinessProfile.create(businessProfile, {
            defaultProps: () => ({
              key: index,
              variant
            }),
            generateKey: true
          })
        )
      }
    )
  ] });
  const result = /* @__PURE__ */ jsx65(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      children: /* @__PURE__ */ jsxs44(Flex, { column: true, children: [
        /* @__PURE__ */ jsx65(
          Flex,
          {
            column: true,
            className: classes["group"],
            ...variant === "01" && { gap: "space-8" },
            children: menuItems.map((item) => {
              if (!item) return null;
              return ProfileMenuItem.create(item, {
                defaultProps: () => ({
                  variant,
                  key: item["label"]
                })
              });
            })
          }
        ),
        variant === "01" && renderBusinessProfiles,
        additionalItems && /* @__PURE__ */ jsx65(
          Flex,
          {
            column: true,
            className: classes["group"],
            ...variant === "01" && { gap: "space-8" },
            children: additionalItems.map((item) => {
              if (!item) return null;
              return ProfileMenuItem.create(item, {
                defaultProps: () => ({
                  variant,
                  key: item["label"]
                })
              });
            })
          }
        ),
        eventItems && /* @__PURE__ */ jsx65(
          Flex,
          {
            column: true,
            className: classes["group"],
            ...variant === "01" && { gap: "space-8" },
            children: eventItems.map((item) => {
              if (!item) return null;
              return ProfileMenuItem.create(item, {
                defaultProps: () => ({
                  variant,
                  key: item["label"]
                })
              });
            })
          }
        ),
        /* @__PURE__ */ jsx65(Flex, { column: true, className: classes["logout"], children: ProfileMenuItem.create(logout, {
          defaultProps: () => ({
            variant,
            icon: /* @__PURE__ */ jsx65(
              SignOutLight,
              {
                size: "1x",
                variables: { "1xSize": pxToRem(20) }
              }
            ),
            label: "Log out"
          })
        }) }),
        variant === "02" && renderBusinessProfiles
      ] })
    }
  );
  setEnd();
  return result;
});
PersonalProfileMenu.displayName = "PersonalProfileMenu";
PersonalProfileMenu.defaultProps = {
  menuItems: DEFAULT_MENU_ITEMS
};
PersonalProfileMenu.create = createShorthandFactory61({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: PersonalProfileMenu,
  mappedProp: "children"
});
PersonalProfileMenu.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "menuItems",
  "additionalItems",
  "eventItems",
  "businessProfiles",
  "logout"
];

// packages/aria/src/components/PersonalMenu/PersonalMenu.tsx
import { jsx as jsx66, jsxs as jsxs45 } from "react/jsx-runtime";
var personalMenuClassName = "ui-personalMenu";
var personalMenuSlotClassNames = {
  mainContentList: `${personalMenuClassName}__mainContentList`,
  personalProfile: `${personalMenuClassName}__personalProfile`,
  professionalList: `${personalMenuClassName}__professionalList`,
  sections: `${personalMenuClassName}__sections`,
  sectionTitle: `${personalMenuClassName}__sectionTitle`
};
var PersonalMenu = forwardRef65(
  (props, ref) => {
    const context = useFluentContext67();
    const { setStart, setEnd } = useTelemetry67(
      PersonalMenu.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      design,
      personalProfile,
      businessProfiles,
      personalProfileMenu,
      variant = "01"
    } = props;
    const getA11yProps = useAccessibility62(menuBehavior10, {
      debugName: PersonalMenu.displayName,
      rtl: context.rtl
    });
    const { classes } = useStyles67(
      PersonalMenu.displayName,
      {
        className: personalMenuClassName,
        mapPropsToStyles: () => ({
          variant
        }),
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps66(
      PersonalMenu.handledProps,
      props
    );
    const result = /* @__PURE__ */ jsx66(
      Flex,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        fill: true,
        column: true,
        children: /* @__PURE__ */ jsxs45(
          Flex,
          {
            className: personalMenuSlotClassNames.mainContentList,
            column: true,
            children: [
              variant === "02" && /* @__PURE__ */ jsx66(
                Caption,
                {
                  className: personalMenuSlotClassNames.sectionTitle,
                  children: "Personal"
                }
              ),
              PersonalProfile.create(personalProfile, {
                defaultProps: () => ({
                  variant
                }),
                overrideProps: () => ({
                  className: personalMenuSlotClassNames.personalProfile
                })
              }),
              /* @__PURE__ */ jsx66(Flex, { className: personalMenuSlotClassNames.sections, children: PersonalProfileMenu.create(personalProfileMenu, {
                defaultProps: () => ({
                  variant
                }),
                overrideProps: () => ({
                  businessProfiles
                })
              }) })
            ]
          }
        )
      }
    );
    setEnd();
    return result;
  }
);
PersonalMenu.displayName = "PersonalMenu";
PersonalMenu.create = createShorthandFactory62({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: PersonalMenu
});
PersonalMenu.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "personalProfile",
  "businessProfiles",
  "personalProfileMenu"
];

// packages/aria/src/components/ReadMore/ReadMore.tsx
import {
  useState as useState8,
  useCallback as useCallback3,
  useRef as useRef8,
  useEffect as useEffect8,
  forwardRef as forwardRef66
} from "react";
import { useInViewport as useInViewport2, useIsomorphicLayoutEffect } from "ahooks";
import {
  useFluentContext as useFluentContext68,
  getElementType as getElementType51,
  useAccessibility as useAccessibility63,
  useTelemetry as useTelemetry68,
  useStyles as useStyles68,
  useUnhandledProps as useUnhandledProps67,
  useAutoControlled as useAutoControlled2
} from "@fluentui/react-northstar";

// packages/aria/src/utils/deviceBrowser.tsx
import { useContext as useContext3, createContext as createContext4 } from "react";
import { jsx as jsx67 } from "react/jsx-runtime";
var DeviceBrowserContext = createContext4({
  isSafari: false,
  isFirefox: false,
  isInstagramOrFacebookApp: false,
  name: ""
});
function DeviceBrowserContextProvider({
  browser = {
    isSafari: false,
    isFirefox: false,
    isInstagramOrFacebookApp: false,
    name: ""
  },
  children
}) {
  return /* @__PURE__ */ jsx67(DeviceBrowserContext.Provider, { value: browser, children });
}
function useDeviceBrowserContext() {
  const ctx = useContext3(DeviceBrowserContext);
  if (typeof ctx === "undefined") {
    throw new Error("useDeviceBrowser must be used within the provider.");
  }
  return ctx;
}

// packages/aria/src/components/ReadMore/ReadMore.tsx
import _6 from "lodash";
import { jsxs as jsxs46 } from "react/jsx-runtime";
var readMoreClassName = "ui-readMore";
var displayName21 = "ReadMore";
var ReadMore = forwardRef66((props, ref) => {
  const context = useFluentContext68();
  const { setStart, setEnd } = useTelemetry68(
    ReadMore.displayName,
    context.telemetry
  );
  setStart();
  const {
    className,
    styles,
    variables,
    accessibility,
    design,
    content,
    clamped: clampedProp,
    defaultClampedValue: defaultClampedValueProp = true,
    maxLines,
    toggleButton = {},
    readMoreText = "More",
    readLessText = "Less",
    appDisplayMode,
    autoScroll = true,
    scrollOffset = 0,
    scrollViewportThreshold = 0.75,
    forceClamp,
    forceClampMinHeight
  } = props;
  const getA11yProps = useAccessibility63(accessibility, {
    debugName: displayName21,
    rtl: context.rtl
  });
  const layoutRef = useRef8(null);
  const [contentOverflow, setContentOverflow] = useState8(false);
  const [clamped, setClamped] = useAutoControlled2({
    defaultValue: defaultClampedValueProp,
    value: clampedProp
  });
  const [inViewport] = useInViewport2(layoutRef, {
    threshold: scrollViewportThreshold
  });
  const { isSafari } = useDeviceBrowserContext();
  const { classes } = useStyles68(displayName21, {
    className: readMoreClassName,
    mapPropsToStyles: () => ({
      design,
      clamped: clamped ?? true,
      maxLines,
      contentOverflow,
      isSafari,
      forceClamp,
      forceClampMinHeight
    }),
    mapPropsToInlineStyles: () => ({
      className,
      styles,
      variables
    }),
    rtl: context.rtl,
    unstable_props: props
  });
  const unhandledProps = useUnhandledProps67(ReadMore.handledProps, props);
  const ElementType = getElementType51(props);
  const detectContentOverflow = useCallback3(() => {
    const textContent = layoutRef.current;
    if (!textContent) return;
    const clampedHeight = textContent.clientHeight;
    const inlineStyle = textContent.style.cssText;
    textContent.style.webkitLineClamp = "unset";
    textContent.style.display = "block";
    textContent.style.maxHeight = "none";
    const fullHeight = textContent.scrollHeight;
    textContent.style.cssText = inlineStyle;
    setContentOverflow(fullHeight > clampedHeight + 1);
  }, []);
  useIsomorphicLayoutEffect(() => {
    detectContentOverflow();
  }, [detectContentOverflow, clamped, maxLines]);
  useEffect8(() => {
    if (typeof window === "undefined") return;
    const handleResize = () => detectContentOverflow();
    window.addEventListener("resize", handleResize);
    let cancelled = false;
    void document.fonts?.ready.then(() => {
      if (!cancelled) detectContentOverflow();
    });
    return () => {
      cancelled = true;
      window.removeEventListener("resize", handleResize);
    };
  }, [detectContentOverflow]);
  const onToggle = (event) => {
    event.stopPropagation();
    setClamped(!clamped);
    if (!clamped && autoScroll) {
      const element = layoutRef.current;
      if (element) {
        const deviceOffset = appDisplayMode?.mobile ? MOBILE_TOP_NAV_HEIGHT_FULL_LOGO : DESKTOP_TOP_NAV_HEIGHT_SHORT_LOGO;
        const offset = scrollOffset || deviceOffset;
        const top = element.getBoundingClientRect().top;
        if (!inViewport) {
          window.scrollTo({
            behavior: "smooth",
            top: top + window.scrollY - offset
          });
        }
      }
    }
  };
  const buttonText = clamped ? readMoreText : readLessText;
  const result = /* @__PURE__ */ jsxs46(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      children: [
        Box.create(content, {
          defaultProps: () => ({
            className: classes["paragraphContent"],
            role: "row",
            "aria-expanded": clamped,
            ref: layoutRef
          })
        }),
        (contentOverflow || !clamped || Boolean(forceClampMinHeight)) && Button.create(toggleButton, {
          defaultProps: () => ({
            children: buttonText,
            className: classes["toggleButton"],
            variant: "text",
            variables: { buttonPadding: 0 }
          }),
          overrideProps: (itemProps) => ({
            onClick: (e) => {
              onToggle(e);
              _6.invoke(itemProps, "onClick", e, clamped);
            },
            variables: {
              primaryButtonBackgroundColorNormal: "transparent",
              primaryButtonBackgroundColorHover: "transparent"
            }
          })
        })
      ]
    }
  );
  setEnd();
  return result;
});
ReadMore.displayName = "ReadMore";
ReadMore.defaultProps = {
  as: "div"
};
ReadMore.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "content",
  "clamped",
  "maxLines",
  "toggleButton",
  "readMoreText",
  "readLessText",
  "defaultClampedValue",
  "autoScroll",
  "scrollOffset",
  "scrollViewportThreshold",
  "forceClamp",
  "forceClampMinHeight",
  "appDisplayMode"
];

// packages/aria/src/components/Popup/Popup.tsx
import { Popup, popupClassName } from "@fluentui/react-northstar";

// packages/aria/src/components/DropdownMenuOutline/DropdownMenuOutlineOptionsList.tsx
import { forwardRef as forwardRef68 } from "react";
import {
  useFluentContext as useFluentContext70,
  getElementType as getElementType53,
  useAccessibility as useAccessibility65,
  useTelemetry as useTelemetry70,
  useStyles as useStyles70,
  useUnhandledProps as useUnhandledProps69,
  createShorthandFactory as createShorthandFactory64
} from "@fluentui/react-northstar";
import { buttonBehavior as buttonBehavior4 } from "@fluentui/accessibility";

// packages/aria/src/components/DropdownMenuOutline/DropdownMenuOutlineOption.tsx
import { forwardRef as forwardRef67 } from "react";
import {
  useFluentContext as useFluentContext69,
  getElementType as getElementType52,
  useAccessibility as useAccessibility64,
  useTelemetry as useTelemetry69,
  useStyles as useStyles69,
  createShorthandFactory as createShorthandFactory63,
  useUnhandledProps as useUnhandledProps68
} from "@fluentui/react-northstar";
import { jsx as jsx68 } from "react/jsx-runtime";
var dropdownMenuOutlineOptionClassName = "ui-dropdownMenuOutlineOption";
var DropdownMenuOutlineOption = forwardRef67((props, ref) => {
  const context = useFluentContext69();
  const { setStart, setEnd } = useTelemetry69(
    DropdownMenuOutlineOption.displayName,
    context.telemetry
  );
  setStart();
  const {
    className,
    styles,
    variables,
    accessibility,
    design,
    label,
    id,
    onItemSelect = () => null,
    isSelectedID
  } = props;
  const getA11yProps = useAccessibility64(
    accessibility,
    {
      debugName: DropdownMenuOutlineOption.displayName,
      rtl: context.rtl
    }
  );
  const { classes } = useStyles69(
    DropdownMenuOutlineOption.displayName,
    {
      className: dropdownMenuOutlineOptionClassName,
      mapPropsToStyles: () => ({}),
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    }
  );
  const unhandledProps = useUnhandledProps68(
    DropdownMenuOutlineOption.handledProps,
    props
  );
  const ElementType = getElementType52(props);
  const result = /* @__PURE__ */ jsx68(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      children: /* @__PURE__ */ jsx68(
        "div",
        {
          className: classes["menuItemContainer"],
          onClick: (e) => {
            onItemSelect(e, id);
          },
          children: isSelectedID ? /* @__PURE__ */ jsx68("span", { children: label }) : /* @__PURE__ */ jsx68("span", { className: classes["menuListItemNotSelected"], children: label })
        }
      )
    }
  );
  setEnd();
  return result;
});
DropdownMenuOutlineOption.displayName = "DropdownMenuOutlineOption";
DropdownMenuOutlineOption.create = createShorthandFactory63({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: DropdownMenuOutlineOption,
  mappedProp: "label"
});
DropdownMenuOutlineOption.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "as",
  "label",
  "id",
  "onItemSelect",
  "isSelectedID",
  // Extra props that are handled
  "disabled"
];
var DropdownMenuOutlineOption_default = DropdownMenuOutlineOption;

// packages/aria/src/components/DropdownMenuOutline/DropdownMenuOutlineOptionsList.tsx
import { jsx as jsx69 } from "react/jsx-runtime";
var defaultRootElementType23 = "div";
var dropdownMenuOutlineOptionsListClassName = "ui-dropdownMenuOutlineOptionsList";
var DropdownMenuOutlineOptionsList = forwardRef68((props, ref) => {
  const context = useFluentContext70();
  const { setStart, setEnd } = useTelemetry70(
    DropdownMenuOutlineOptionsList.displayName,
    context.telemetry
  );
  setStart();
  const {
    className,
    styles,
    variables,
    accessibility,
    design,
    menuItems,
    selectedID,
    onItemSelect
  } = props;
  const getA11yProps = useAccessibility65(
    accessibility,
    {
      debugName: DropdownMenuOutlineOptionsList.displayName,
      rtl: context.rtl
    }
  );
  const { classes } = useStyles70(
    DropdownMenuOutlineOptionsList.displayName,
    {
      className: dropdownMenuOutlineOptionsListClassName,
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    }
  );
  const unhandledProps = useUnhandledProps69(
    DropdownMenuOutlineOptionsList.handledProps,
    props
  );
  const ElementType = getElementType53(props);
  const result = /* @__PURE__ */ jsx69(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      children: menuItems.map(
        (menuItem) => DropdownMenuOutlineOption_default.create(menuItem, {
          defaultProps: () => ({
            onItemSelect,
            isSelectedID: menuItem.id === selectedID,
            key: `dropdownMenuOutlineOption--${menuItem.id}`
          })
        })
      )
    }
  );
  setEnd();
  return result;
});
DropdownMenuOutlineOptionsList.displayName = "DropdownMenuOutlineOptionsList";
DropdownMenuOutlineOptionsList.defaultProps = {
  as: defaultRootElementType23,
  accessibility: buttonBehavior4
};
DropdownMenuOutlineOptionsList.create = createShorthandFactory64({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: DropdownMenuOutlineOptionsList
});
DropdownMenuOutlineOptionsList.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "as",
  "disabled",
  "selectedID",
  "menuItems"
];

// packages/aria/src/components/DropdownMenuOutline/DropdownMenuOutlineButtonTop.tsx
import { forwardRef as forwardRef69 } from "react";
import {
  useFluentContext as useFluentContext71,
  getElementType as getElementType54,
  useAccessibility as useAccessibility66,
  useTelemetry as useTelemetry71,
  useStyles as useStyles71,
  useUnhandledProps as useUnhandledProps70,
  createShorthandFactory as createShorthandFactory65
} from "@fluentui/react-northstar";
import { buttonBehavior as buttonBehavior5 } from "@fluentui/accessibility";
import { CustomCaretDown, CustomCaretUp } from "@archipro-design/icons";
import { jsx as jsx70, jsxs as jsxs47 } from "react/jsx-runtime";
var defaultRootElementType24 = "div";
var DropdownMenuOutlineButtonTopClassName = "ui-DropdownMenuOutlineButtonTop";
var DropdownMenuOutlineButtonTop = forwardRef69((props, ref) => {
  const context = useFluentContext71();
  const { setStart, setEnd } = useTelemetry71(
    DropdownMenuOutlineButtonTop.displayName,
    context.telemetry
  );
  setStart();
  const {
    className,
    styles,
    variables,
    accessibility,
    design,
    label,
    isOpen,
    onClick = () => null
  } = props;
  const getA11yProps = useAccessibility66(
    accessibility,
    {
      debugName: DropdownMenuOutlineButtonTop.displayName,
      rtl: context.rtl
    }
  );
  const { classes } = useStyles71(
    DropdownMenuOutlineButtonTop.displayName,
    {
      className: DropdownMenuOutlineButtonTopClassName,
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    }
  );
  const unhandledProps = useUnhandledProps70(
    DropdownMenuOutlineButtonTop.handledProps,
    props
  );
  const ElementType = getElementType54(props);
  const result = /* @__PURE__ */ jsxs47(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      onClick,
      children: [
        Paragraph.create(label, {
          defaultProps: () => ({
            variant: "01",
            as: "span"
          })
        }),
        /* @__PURE__ */ jsx70("div", { className: classes["navMenuIconDiv"], children: isOpen ? /* @__PURE__ */ jsx70(CustomCaretUp, { className: classes["navMenuDropdownIcon"] }) : /* @__PURE__ */ jsx70(
          CustomCaretDown,
          {
            className: classes["navMenuDropdownIcon"]
          }
        ) })
      ]
    }
  );
  setEnd();
  return result;
});
DropdownMenuOutlineButtonTop.displayName = "DropdownMenuOutlineButtonTop";
DropdownMenuOutlineButtonTop.defaultProps = {
  as: defaultRootElementType24,
  accessibility: buttonBehavior5
};
DropdownMenuOutlineButtonTop.create = createShorthandFactory65({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: DropdownMenuOutlineButtonTop,
  mappedProp: "label"
});
DropdownMenuOutlineButtonTop.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "as",
  "isOpen",
  "label",
  "disabled"
];

// packages/aria/src/components/DropdownMenuOutline/DropdownMenuOutline.tsx
import { forwardRef as forwardRef70 } from "react";
import {
  useFluentContext as useFluentContext72,
  getElementType as getElementType55,
  useAccessibility as useAccessibility67,
  useTelemetry as useTelemetry72,
  useStyles as useStyles72,
  useUnhandledProps as useUnhandledProps71,
  useAutoControlled as useAutoControlled3
} from "@fluentui/react-northstar";
import { buttonBehavior as buttonBehavior6 } from "@fluentui/accessibility";
import { jsx as jsx71, jsxs as jsxs48 } from "react/jsx-runtime";
var defaultRootElementType25 = "div";
var dropdownMenuClassName = "ui-dropdownMenu";
var DropdownMenuOutline = forwardRef70(
  (props, ref) => {
    const context = useFluentContext72();
    const { setStart, setEnd } = useTelemetry72(
      DropdownMenuOutline.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      selectedID: selectedIDProp,
      menuItems,
      isOpen,
      onTopNavClick,
      defaultValueProp = "0",
      onSelect: onSelectProp = () => null
    } = props;
    const [selectedId, setSelectedId] = useAutoControlled3({
      defaultValue: defaultValueProp,
      value: selectedIDProp
    });
    const getA11yProps = useAccessibility67(
      accessibility,
      {
        debugName: DropdownMenuOutline.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles72(
      DropdownMenuOutline.displayName,
      {
        className: dropdownMenuClassName,
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps71(
      DropdownMenuOutline.handledProps,
      props
    );
    const ElementType = getElementType55(props);
    const selectedLabel = menuItems.find((m) => m.id === selectedId)?.label || "";
    const toggleIsOpen = (e) => {
      e.stopPropagation();
      onTopNavClick(e);
    };
    const onSelect = (e, id) => {
      e.preventDefault();
      if (selectedIDProp) {
        onSelectProp(id);
      }
      setSelectedId(id);
    };
    const topButton = DropdownMenuOutlineButtonTop.create(selectedLabel, {
      defaultProps: () => ({
        isOpen,
        onClick: toggleIsOpen
      })
    });
    const result = /* @__PURE__ */ jsxs48(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: [
          topButton,
          isOpen && /* @__PURE__ */ jsx71(
            DropdownMenuOutlineOptionsList,
            {
              menuItems,
              selectedID: selectedId,
              onItemSelect: onSelect
            }
          )
        ]
      }
    );
    setEnd();
    return result;
  }
);
DropdownMenuOutline.displayName = "DropdownMenuOutline";
DropdownMenuOutline.defaultProps = {
  as: defaultRootElementType25,
  accessibility: buttonBehavior6
};
DropdownMenuOutline.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "as",
  // Extra props that are handled
  "menuItems",
  "isOpen",
  "onSelect",
  "onTopNavClick",
  "defaultValueProp",
  "selectedID",
  "disabled"
];

// packages/aria/src/components/Tile/TileVideo/TileVideo.tsx
import { useCallback as useCallback4, useEffect as useEffect9, useRef as useRef9, useState as useState9 } from "react";
import { forwardRef as forwardRef71 } from "react";
import {
  useFluentContext as useFluentContext73,
  getElementType as getElementType56,
  useAccessibility as useAccessibility68,
  useTelemetry as useTelemetry73,
  useStyles as useStyles73,
  useUnhandledProps as useUnhandledProps72,
  cardBehavior as cardBehavior11,
  createShorthandFactory as createShorthandFactory66
} from "@fluentui/react-northstar";
import { HeartFill as HeartFill2, HeartLine as HeartLine3 } from "@archipro-design/icons";
import FilePlayer2 from "react-player/file.js";
import { useInViewport as useInViewport3 } from "ahooks";
import { Fragment as Fragment12, jsx as jsx72, jsxs as jsxs49 } from "react/jsx-runtime";
var defaultRootElementType26 = "div";
var tileVideoClassName = "ui-TileVideo";
var tileVideoSlotClassNames = {
  videoContainer: `${tileVideoClassName}__videoContainer`,
  playOnHover: `${tileVideoClassName}__playOnHover`
};
var TileVideo = forwardRef71(
  (props, ref) => {
    const context = useFluentContext73();
    const { setStart, setEnd } = useTelemetry73(
      TileVideo.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      overlay = {},
      isFavourited,
      onFavourite,
      hideFavourite,
      topActions = null,
      topContent = null,
      src,
      width = 320,
      height = 320,
      playing,
      playsInline = false,
      controls = false,
      loop = true,
      muted = true,
      hoverTrigger = {
        children: /* @__PURE__ */ jsx72("div", {})
      },
      poster,
      lazy
    } = props;
    const getA11yProps = useAccessibility68(
      accessibility,
      {
        debugName: TileVideo.displayName,
        rtl: context.rtl
      }
    );
    const inViewRef = useRef9(null);
    const [inView] = useInViewport3(inViewRef, {
      rootMargin: "100%"
    });
    const [videoRendered, setVideoRendered] = useState9(false);
    useEffect9(() => {
      if (!videoRendered && inView) {
        setVideoRendered(true);
      }
    }, [inView, videoRendered]);
    const [shouldPlay, setShouldPlay] = useState9(playing);
    useEffect9(() => {
      setShouldPlay(playing);
    }, [playing]);
    const { classes } = useStyles73(
      TileVideo.displayName,
      {
        className: tileVideoClassName,
        mapPropsToStyles: () => ({
          aspectRatio: width / height,
          width,
          height
        }),
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps72(TileVideo.handledProps, props);
    const ElementType = getElementType56(props);
    const handleFavourite = (e) => {
      e.stopPropagation();
      e.preventDefault();
      onFavourite?.(e, isFavourited);
    };
    const getFavouriteAction = () => {
      if (hideFavourite) {
        return void 0;
      }
      return /* @__PURE__ */ jsx72(
        Button,
        {
          size: 24,
          variables: { buttonPadding: 12, iconPadding: 0 },
          color: "grey",
          onClick: handleFavourite,
          icon: isFavourited ? /* @__PURE__ */ jsx72(HeartFill2, {}) : /* @__PURE__ */ jsx72(HeartLine3, {})
        },
        "favourite-button"
      );
    };
    const getTopActions = () => {
      const favourite = getFavouriteAction();
      if (!topActions && !favourite) {
        return void 0;
      }
      return [
        {
          key: "tile-top-actions",
          content: /* @__PURE__ */ jsxs49(Flex, { gap: "space-16", vAlign: "center", children: [
            topActions?.map((item) => Box.create(item)),
            favourite
          ] })
        }
      ];
    };
    const setRefs = useCallback4(
      (node) => {
        if (ref && "current" in ref) ref.current = node;
        inViewRef.current = node;
      },
      [inViewRef, ref]
    );
    const result = /* @__PURE__ */ jsx72(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref: setRefs,
          ...unhandledProps
        }),
        children: src && /* @__PURE__ */ jsxs49(Fragment12, { children: [
          Box.create(topContent),
          inView && TileOverlay.create(overlay, {
            defaultProps: () => {
              return { topActions: getTopActions() };
            }
          }),
          Box.create(hoverTrigger, {
            defaultProps: () => {
              return {
                className: tileVideoSlotClassNames.playOnHover
              };
            },
            overrideProps: () => {
              return {
                onMouseEnter: () => {
                  setShouldPlay(true);
                },
                onMouseLeave: () => {
                  setShouldPlay(false);
                }
              };
            }
          }),
          videoRendered && /* @__PURE__ */ jsx72(
            FilePlayer2,
            {
              url: src,
              ...{
                width: "100%",
                height: "100%",
                loop: !!loop,
                muted: !!muted,
                playing: !!shouldPlay,
                controls: !!controls,
                playsinline: playsInline,
                style: {
                  aspectRatio: width / height,
                  lineHeight: 0
                }
              },
              config: {
                attributes: {
                  preload: lazy ? "none" : void 0,
                  poster: poster ?? void 0
                }
              }
            }
          )
        ] })
      }
    );
    setEnd();
    return result;
  }
);
TileVideo.displayName = "TileVideo";
TileVideo.create = createShorthandFactory66({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: TileVideo,
  mappedProp: "images"
});
TileVideo.defaultProps = {
  as: defaultRootElementType26,
  accessibility: cardBehavior11
};
TileVideo.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "as",
  // Extra props that are handled
  "overlay",
  "topActions",
  "topContent",
  ...favouriteHandledProps,
  "src",
  "width",
  "height",
  "playing",
  "controls",
  "loop",
  "muted",
  "hoverTrigger",
  "lazy",
  "poster"
];

// packages/aria/src/components/Popup/PopupContent.tsx
import {
  PopupContent,
  popupContentClassName,
  popupContentSlotClassNames
} from "@fluentui/react-northstar";

// packages/aria/src/components/Tabs/Tabs.tsx
import { forwardRef as forwardRef72 } from "react";
import {
  useFluentContext as useFluentContext74,
  getElementType as getElementType57,
  useAccessibility as useAccessibility69,
  useTelemetry as useTelemetry74,
  useStyles as useStyles74,
  useUnhandledProps as useUnhandledProps73,
  tabListBehavior as tabListBehavior2,
  createShorthandFactory as createShorthandFactory67
} from "@fluentui/react-northstar";
import { useAutoControlled as useAutoControlled4 } from "@fluentui/react-bindings";

// packages/aria/src/themes/base/components/Tabs/tabsVariables.ts
var tabsVariables = (siteVars) => ({
  tabsButtonMargin: pxArrayToRem([0, 32, 0, 32]),
  tabsButtonMarginX: pxToRem(32),
  tabsUnderlinePaddingTop: pxToRem(14),
  tabsUnderlineWidth: pxToRem(2),
  tabsBorderColor: siteVars.colors.charcoal["000"],
  tabsRoundedBorderColor: siteVars.colors.charcoal[300],
  tabsRoundedBorderWidth: pxToRem(1),
  tabsRoundedBorderRadius: pxToRem(100)
});
var getButtonColorVariant = (variant, isActive) => {
  const colorMap = {
    square: {
      active: { color: "dark", transparent: false },
      default: { color: "dark", transparent: true }
    },
    rounded: {
      active: { color: "white", transparent: false },
      default: { color: "white", transparent: false }
    }
  };
  return isActive ? colorMap[variant].active : colorMap[variant].default;
};

// packages/aria/src/components/Tabs/Tabs.tsx
import { jsx as jsx73 } from "react/jsx-runtime";
var defaultRootElementType27 = "div";
var tabsClassName = "ui-tabs";
var tabsClassNames = {
  active: `${tabsClassName}__active`
};
var Tabs = forwardRef72((props, ref) => {
  const context = useFluentContext74();
  const { setStart, setEnd } = useTelemetry74(
    Tabs.displayName,
    context.telemetry
  );
  setStart();
  const {
    className,
    styles,
    variables,
    accessibility,
    design,
    tabs,
    active,
    defaultActive,
    underline,
    variant = "square",
    onChange
  } = props;
  const getA11yProps = useAccessibility69(
    accessibility,
    {
      debugName: Tabs.displayName,
      rtl: context.rtl
    }
  );
  const [selectedId, setSelectedId] = useAutoControlled4({
    defaultValue: defaultActive,
    value: active,
    initialValue: 0
  });
  const { classes } = useStyles74(Tabs.displayName, {
    className: tabsClassName,
    mapPropsToStyles: () => ({ underline, variant }),
    mapPropsToInlineStyles: () => ({
      className,
      styles,
      variables,
      design
    }),
    rtl: context.rtl,
    unstable_props: props
  });
  const handleClick = (event, id) => {
    setSelectedId(id);
    if (onChange) {
      onChange(event);
    }
  };
  const unhandledProps = useUnhandledProps73(Tabs.handledProps, props);
  const ElementType = getElementType57(props);
  const result = /* @__PURE__ */ jsx73(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      children: tabs.map((tab, i) => {
        return Button.create(tab, {
          generateKey: true,
          defaultProps: () => {
            const isActive = i === selectedId;
            const buttonProps = getButtonColorVariant(
              variant,
              isActive
            );
            return {
              ...isActive && {
                className: tabsClassNames.active
              },
              variables: { buttonPadding: 24 },
              size: 20,
              ...buttonProps,
              onClick: (event) => {
                handleClick(event, i);
              }
            };
          }
        });
      })
    }
  );
  setEnd();
  return result;
});
Tabs.displayName = "Tabs";
Tabs.create = createShorthandFactory67({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: Tabs,
  mappedProp: "tabs"
});
Tabs.defaultProps = {
  as: defaultRootElementType27,
  accessibility: tabListBehavior2,
  underline: true
};
Tabs.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "as",
  // Extra props that are handled
  "active",
  "defaultActive",
  "tabs",
  "underline",
  "variant",
  "onChange"
];

// packages/aria/src/components/DropdownProduct/DropdownProduct.tsx
import React14, { useId as useId3 } from "react";
import { Provider as Provider2 } from "@fluentui/react-northstar";
import { Dropdown as BaseDropdown } from "@fluentui/react-northstar";
import { CheckLine as CheckLine3 } from "@archipro-design/icons";

// packages/aria/src/utils/scrollbarStyle.ts
function getScrollbarStyle({ siteVariables: v }, trackMargin = pxArrayToRem([16, 0])) {
  return {
    "&::-webkit-scrollbar": {
      width: pxToRem(6)
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: v.colors.white[300],
      margin: trackMargin
    },
    "&::-webkit-scrollbar-thumb:vertical": {
      backgroundColor: v.colors.charcoal[250]
    }
  };
}

// packages/aria/src/themes/base/utils/colorHelper.ts
var hex2rgba = (hex, opacity) => {
  if (/^#?([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    const [r, g, b] = hex2rgbArray(hex);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  } else {
    return hex;
  }
};
var hex2rgbArray = (hex) => {
  let c = hex.replace(/^#/, "").split("");
  if (c.length === 3) {
    c = [c[0], c[0], c[1], c[1], c[2], c[2]];
  }
  const color = parseInt(c.join(""), 16) || 0;
  const r = color >> 16 & 255;
  const g = color >> 8 & 255;
  const b = color & 255;
  return [r, g, b];
};

// packages/aria/src/components/DropdownProduct/DropdownProduct.tsx
import { dropdownSlotClassNames as dropdownSlotClassNames2 } from "@fluentui/react-northstar";
import { Fragment as Fragment13, jsx as jsx74, jsxs as jsxs50 } from "react/jsx-runtime";
var dropdownProductClassName = `ui-dropdownProduct`;
var dropdownProductSlotClassNames = dropdownSlotClassNames2;
var DropdownProduct = React14.forwardRef(
  function DropdownProduct2(props, ref) {
    const id = useId3();
    return /* @__PURE__ */ jsx74(
      Provider2,
      {
        theme: {
          componentVariables: {
            Dropdown: ({
              colors: colors2,
              zIndexes: zIndexes2,
              lineHeightSmallest: lineHeightSmallest2
            }) => ({
              backgroundColor: colors2.primitive.white,
              backgroundHoverColor: hex2rgba(
                colors2.primitive.black,
                0.06
              ),
              zIndex: zIndexes2.background,
              zIndexOpen: zIndexes2.foreground,
              lineHeight: `${lineHeightSmallest2}`,
              color: colors2.charcoal["250"],
              hoverColor: colors2.primitive.black,
              listBackgroundColor: colors2.primitive.white,
              listOutline: `solid ${pxToRem(2)} ${colors2.charcoal[250]}`,
              listPadding: `${pxToRem(16)} 0`,
              listMaxHeight: pxToRem(288)
            }),
            DropdownItem: ({ colors: colors2 }) => ({
              backgroundColor: colors2.primitive.white,
              backgroundHoverColor: hex2rgba(
                colors2.primitive.black,
                0.06
              ),
              backgroundSelectedColor: hex2rgba(
                colors2.primitive.black,
                0.06
              ),
              color: colors2.charcoal["250"],
              hoverColor: colors2.primitive.black,
              selectedColor: colors2.primitive.black,
              padding: pxArrayToRem([8, 8, 8, 52]),
              letterSpacing: "-0.02em"
            })
          },
          componentStyles: {
            Dropdown: {
              root: ({ props: props2, variables: v }) => ({
                fontSize: pxToRem(24),
                lineHeight: v.lineHeight,
                position: "relative",
                zIndex: props2.open ? v.zIndexOpen : v.zIndex
              }),
              triggerButton: ({ variables: v }) => ({
                fontSize: pxToRem(24),
                justifyContent: "flex-start",
                backgroundColor: v.backgroundColor,
                color: v.color,
                padding: `${pxToRem(8)} ${pxToRem(8)} ${pxToRem(
                  8
                )} ${pxToRem(52)}`,
                "&:hover": {
                  backgroundColor: v.backgroundHoverColor,
                  color: v.hoverColor
                }
              }),
              toggleIndicator: () => ({
                position: "absolute"
              }),
              list: ({ variables: v, theme }) => ({
                width: "100%",
                overflow: "hidden auto",
                maxHeight: v.listMaxHeight,
                backgroundColor: v.listBackgroundColor,
                '&[aria-hidden="false"]': {
                  outline: v.listOutline,
                  padding: v.listPadding
                },
                ...getScrollbarStyle(theme)
              })
            },
            DropdownItem: {
              root: ({ variables: v }) => ({
                padding: v.padding,
                color: v.color,
                letterSpacing: v.letterSpacing,
                "&:hover": {
                  backgroundColor: v.backgroundHoverColor,
                  color: v.hoverColor
                },
                '&[aria-selected="true"]': {
                  backgroundColor: v.backgroundSelectedColor,
                  color: v.selectedColor
                },
                '&:not([aria-disabled="true"])': {
                  cursor: "pointer"
                }
              }),
              header: ({ props: props2 }) => props2.selected ? {
                display: "grid",
                gridTemplateColumns: `1fr ${pxToRem(
                  24
                )}`,
                columnGap: pxToRem(8)
              } : {}
            }
          }
        },
        children: /* @__PURE__ */ jsx74(
          BaseDropdown,
          {
            toggleIndicator: false,
            renderItem: (Item, itemProps) => /* @__PURE__ */ jsx74(
              Item,
              {
                ...itemProps,
                header: itemProps.selected ? /* @__PURE__ */ jsxs50(Fragment13, { children: [
                  itemProps.header,
                  " ",
                  /* @__PURE__ */ jsx74(CheckLine3, {})
                ] }) : itemProps.header
              }
            ),
            "aria-labelledby": `downshift-${id}-label`,
            ...props,
            ref
          }
        )
      }
    );
  }
);
DropdownProduct.displayName = "DropdownProduct";

// packages/aria/src/components/Tile/TileGrid.tsx
import { forwardRef as forwardRef73 } from "react";
import {
  useFluentContext as useFluentContext75,
  getElementType as getElementType58,
  useAccessibility as useAccessibility70,
  useTelemetry as useTelemetry75,
  useStyles as useStyles75,
  useUnhandledProps as useUnhandledProps74,
  listBehavior as listBehavior3
} from "@fluentui/react-northstar";
import { jsx as jsx75, jsxs as jsxs51 } from "react/jsx-runtime";
var defaultRootElementType28 = "div";
var tileGridClassName = "ui-tileGrid";
var DEFAULT_COLUMNS = 4;
var TileGrid = forwardRef73((props, ref) => {
  const context = useFluentContext75();
  const { setStart, setEnd } = useTelemetry75(
    TileGrid.displayName,
    context.telemetry
  );
  setStart();
  const {
    className,
    styles,
    variables,
    accessibility,
    design,
    heading,
    children,
    columns = DEFAULT_COLUMNS
  } = props;
  const getA11yProps = useAccessibility70(
    accessibility,
    {
      debugName: TileGrid.displayName,
      rtl: context.rtl
    }
  );
  const { classes } = useStyles75(TileGrid.displayName, {
    className: tileGridClassName,
    mapPropsToInlineStyles: () => ({
      className,
      styles,
      variables,
      design
    }),
    rtl: context.rtl,
    unstable_props: props
  });
  const unhandledProps = useUnhandledProps74(TileGrid.handledProps, props);
  const ElementType = getElementType58(props);
  const header = Header.create(heading, {
    defaultProps: () => ({
      as: "h3",
      variant: "03",
      maximumLines: 2,
      className: classes["header"]
    })
  });
  const result = /* @__PURE__ */ jsxs51(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      children: [
        header,
        /* @__PURE__ */ jsx75(Grid, { className: classes["tiles"], columns, children })
      ]
    }
  );
  setEnd();
  return result;
});
TileGrid.displayName = "TileGrid";
TileGrid.defaultProps = {
  as: defaultRootElementType28,
  accessibility: listBehavior3,
  columns: DEFAULT_COLUMNS
};
TileGrid.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "as",
  "heading",
  "columns"
];

// packages/aria/src/components/Skeleton/Skeleton.tsx
import { Skeleton, skeletonClassName } from "@fluentui/react-northstar";

// packages/aria/src/components/VariantColor/VariantColor.tsx
import { forwardRef as forwardRef74 } from "react";
import {
  useFluentContext as useFluentContext76,
  getElementType as getElementType59,
  useAccessibility as useAccessibility71,
  useTelemetry as useTelemetry76,
  useStyles as useStyles76,
  useUnhandledProps as useUnhandledProps75,
  createShorthandFactory as createShorthandFactory68
} from "@fluentui/react-northstar";
import { BlurOffLine } from "@archipro-design/icons";
import { jsx as jsx76, jsxs as jsxs52 } from "react/jsx-runtime";
var defaultRootElementType29 = "div";
var variantColorClassName = "ui-variant-color";
var variantColorSlotClassNames = {
  imageContainer: variantColorClassName + "_image__container",
  image: variantColorClassName + "_image",
  filler: variantColorClassName + "_filler",
  svg: variantColorClassName + "_svg",
  gradient: variantColorClassName + "_gradient",
  soloCircle: variantColorClassName + "_solo_circle",
  circleContainer: variantColorClassName + "_circle_container",
  swatchIcon: variantColorClassName + "_swatch_icon",
  ring: variantColorClassName + "_ring"
};
var VariantColor = forwardRef74(
  (props, ref) => {
    const context = useFluentContext76();
    const { setStart, setEnd } = useTelemetry76(
      VariantColor.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      highlighted,
      colorCode,
      image,
      size,
      viewBoxSize = 80,
      description,
      gradient
    } = props;
    const getA11yProps = useAccessibility71(
      accessibility,
      {
        debugName: VariantColor.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles76(
      VariantColor.displayName,
      {
        className: variantColorClassName,
        mapPropsToStyles: () => ({
          highlighted,
          size,
          colorCode
        }),
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps75(
      VariantColor.handledProps,
      props
    );
    const ElementType = getElementType59(props);
    const colors2 = colorCode?.split(",") ?? [];
    const radius = viewBoxSize / 2;
    const precision = 4;
    const isSoloBackground = Boolean(colors2.length <= 1 && !image);
    const result = /* @__PURE__ */ jsxs52(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: [
          /* @__PURE__ */ jsxs52(
            "div",
            {
              className: classes[variantColorSlotClassNames.imageContainer],
              children: [
                image ? Image.create(image, {
                  defaultProps: () => ({
                    objectFit: "cover",
                    width: "100%",
                    height: "100%",
                    layout: "responsive",
                    placeholderColor: "transparent",
                    className: classes[variantColorSlotClassNames.image],
                    alt: description
                  })
                }) : /* @__PURE__ */ jsxs52(
                  "div",
                  {
                    className: classes[variantColorSlotClassNames.circleContainer],
                    children: [
                      gradient ? /* @__PURE__ */ jsx76(
                        "div",
                        {
                          role: "img",
                          "aria-hidden": !description,
                          "aria-label": description,
                          className: variantColorSlotClassNames.gradient
                        }
                      ) : isSoloBackground ? /* @__PURE__ */ jsx76(
                        "div",
                        {
                          role: "img",
                          "aria-hidden": !description,
                          "aria-label": description,
                          className: classes[variantColorSlotClassNames.soloCircle]
                        }
                      ) : /* @__PURE__ */ jsxs52(
                        "svg",
                        {
                          width: viewBoxSize,
                          height: viewBoxSize,
                          viewBox: `0 0 ${viewBoxSize} ${viewBoxSize}`,
                          xmlns: "http://www.w3.org/2000/svg",
                          className: classes[variantColorSlotClassNames.svg],
                          role: "img",
                          "aria-hidden": !description,
                          children: [
                            description && /* @__PURE__ */ jsx76("desc", { children: description }),
                            /* @__PURE__ */ jsx76(
                              "circle",
                              {
                                r: radius,
                                cx: radius,
                                cy: radius,
                                fill: colors2[0],
                                strokeWidth: 0,
                                shapeRendering: "geometricPrecision"
                              }
                            ),
                            colors2.length === 2 && /* @__PURE__ */ jsx76(
                              "g",
                              {
                                transform: `rotate(-45, ${radius}, ${radius})`,
                                fill: "none",
                                strokeWidth: radius,
                                children: /* @__PURE__ */ jsx76(
                                  "circle",
                                  {
                                    r: radius / 2,
                                    cx: radius,
                                    cy: radius,
                                    shapeRendering: "geometricPrecision",
                                    stroke: colors2[1],
                                    strokeDasharray: `${(radius * Math.PI / 2).toFixed(precision)} ${(radius * Math.PI).toFixed(precision)}`
                                  }
                                )
                              }
                            ),
                            colors2.length === 3 && /* @__PURE__ */ jsxs52("g", { fill: "none", strokeWidth: radius, children: [
                              /* @__PURE__ */ jsx76(
                                "circle",
                                {
                                  r: radius / 2,
                                  cx: radius,
                                  cy: radius,
                                  stroke: colors2[1],
                                  strokeDasharray: `${(radius * Math.PI / 2).toFixed(precision)} ${(radius * Math.PI).toFixed(precision)}`,
                                  transform: `rotate(-90, ${radius}, ${radius})`,
                                  shapeRendering: "geometricPrecision"
                                }
                              ),
                              /* @__PURE__ */ jsx76(
                                "circle",
                                {
                                  r: radius / 2,
                                  cx: radius,
                                  cy: radius,
                                  stroke: colors2[2],
                                  strokeDasharray: `${(radius * Math.PI / 3).toFixed(precision)} ${(radius * Math.PI).toFixed(precision)}`,
                                  transform: `rotate(30, ${radius}, ${radius})`,
                                  shapeRendering: "geometricPrecision"
                                }
                              )
                            ] })
                          ]
                        }
                      ),
                      !colorCode && /* @__PURE__ */ jsx76(
                        BlurOffLine,
                        {
                          className: classes[variantColorSlotClassNames.swatchIcon],
                          design: {
                            width: "100%",
                            height: "100%"
                          }
                        }
                      )
                    ]
                  }
                ),
                /* @__PURE__ */ jsx76(
                  "div",
                  {
                    className: classes[variantColorSlotClassNames.ring]
                  }
                )
              ]
            }
          ),
          /* @__PURE__ */ jsx76(
            "div",
            {
              className: classes[variantColorSlotClassNames.filler]
            }
          )
        ]
      }
    );
    setEnd();
    return result;
  }
);
VariantColor.displayName = "VariantColor";
VariantColor.create = createShorthandFactory68({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: VariantColor
});
VariantColor.defaultProps = {
  as: defaultRootElementType29
};
VariantColor.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "as",
  "highlighted",
  "colorCode",
  "size",
  "image",
  "viewBoxSize",
  "description",
  "gradient"
];
var VariantColor_default = VariantColor;

// packages/aria/src/components/Input/NumberInput.tsx
import React15 from "react";

// packages/aria/src/utils/numberFormat.ts
var getPureValue = (val, format) => {
  if (!format) return val;
  const { prefix, suffix, thousandSeparator, nullValueText } = format;
  if (val === nullValueText) {
    return "";
  }
  if (thousandSeparator) {
    val = val.split(thousandSeparator).join("");
  }
  if (prefix && val.startsWith(prefix)) {
    val = val.substr(prefix.length);
  }
  if (suffix && val.endsWith(suffix)) {
    val = val.substr(0, val.length - suffix.length);
  }
  return val;
};
var thousandfy = (strValue, thousandSeparator) => {
  if (!thousandSeparator) {
    return strValue;
  }
  const [integers, decimals] = strValue.split(".");
  if (integers.length <= 3) {
    return strValue;
  }
  const integerArray = [];
  for (let i = integers.length; i > 0; i -= 3) {
    const startIndex = Math.max(0, i - 3);
    integerArray.unshift(integers.substring(startIndex, i));
    if (startIndex === 0) {
      break;
    }
  }
  if (decimals !== void 0) {
    return [integerArray.join(thousandSeparator), decimals].join(".");
  } else {
    return integerArray.join(thousandSeparator);
  }
};
var formatValue = (value, format) => {
  if (!format) {
    return value === null || value === void 0 ? "" : String(value);
  }
  const {
    prefix = "",
    suffix = "",
    fixedDecimalPlaces,
    maxDecimalPlaces,
    thousandSeparator
  } = format;
  if (value === null) {
    return format.nullValueText || "";
  }
  if (value === void 0) {
    return "";
  }
  let strValue;
  if (fixedDecimalPlaces !== void 0) {
    strValue = (value || 0).toFixed(fixedDecimalPlaces);
  } else if (maxDecimalPlaces && maxDecimalPlaces > 0) {
    strValue = (value || 0).toString();
    const arr = strValue.split(".");
    if (arr.length > 1) {
      strValue = arr[0] + "." + arr[1].slice(0, Math.min(maxDecimalPlaces, arr[1].length));
    }
  } else {
    strValue = String(value);
  }
  if (thousandSeparator && value >= 1e3) {
    strValue = thousandfy(strValue, thousandSeparator);
  }
  return `${prefix}${strValue}${suffix}`;
};
var sanitiseInput = (inputValue, {
  maxDecimalPlaces,
  fixedDecimalPlaces,
  prefix = "",
  suffix = "",
  thousandSeparator
}) => {
  if (fixedDecimalPlaces !== void 0) {
    maxDecimalPlaces = fixedDecimalPlaces;
  }
  if (maxDecimalPlaces !== void 0 && maxDecimalPlaces >= 0) {
    const arr = inputValue.split(".");
    if (arr.length > 1) {
      if (maxDecimalPlaces && arr[1].length > maxDecimalPlaces) {
        arr[1] = arr[1].substr(0, maxDecimalPlaces);
        inputValue = arr[0] + "." + arr[1];
      } else if (maxDecimalPlaces === 0) {
        inputValue = arr[0];
      }
    }
  }
  const numValue = Number(inputValue);
  const textValue = prefix + thousandfy(inputValue, thousandSeparator) + suffix;
  return { textValue, numValue };
};

// packages/aria/src/components/Input/NumberInput.tsx
import { useLatest } from "ahooks";
import { useAutoControlled as useAutoControlled5, useMergedRefs as useMergedRefs2 } from "@fluentui/react-bindings";
import { createShorthandFactory as createShorthandFactory69 } from "@fluentui/react-northstar";
import { jsx as jsx77 } from "react/jsx-runtime";
var displayName22 = "NumberInput";
var voidFunc = () => void 0;
var NumberInput = React15.forwardRef(
  ({
    max,
    min,
    value: propValue,
    defaultValue,
    onChange: onValueChange = voidFunc,
    format = {
      prefix: "",
      suffix: "",
      nullValueText: ""
    },
    onBlur,
    onFocus,
    allowsEmpty,
    ...rest
  }, ref) => {
    const { prefix = "", suffix = "", fixedDecimalPlaces } = format;
    const formatRef = useLatest(format);
    const localInputRef = React15.useRef(null);
    const adjustedValueRef = React15.useRef(false);
    const mergedRef = useMergedRefs2(localInputRef, ref);
    const [value, setValue] = useAutoControlled5({
      defaultValue,
      value: propValue
    });
    const [textValue, setTextValue] = React15.useState(
      formatValue(value, format)
    );
    const onChange = (e) => {
      const input = e.target;
      const originalValue = input.value;
      const value2 = getPureValue(originalValue || "", format);
      if (isNaN(Number(value2))) {
        if ((min ?? 0) >= 0 || value2 !== "-") {
          return;
        } else if (value2 === "-") {
          setTextValue("-");
          return;
        }
      }
      let numValue = Number(value2);
      adjustedValueRef.current = false;
      if (min && numValue < min || max && numValue > max) {
        if (min && numValue < min) numValue = min;
        if (max && numValue > max) numValue = max;
        adjustedValueRef.current = true;
      }
      if (value2 === "") {
        if (allowsEmpty) {
          setValue(null);
          onValueChange(e, null);
          setTextValue("");
          return;
        } else if (!fixedDecimalPlaces) {
          setTextValue(prefix || suffix);
          setValue(adjustedValueRef.current ? numValue : 0);
          onValueChange(e, adjustedValueRef.current ? numValue : 0);
          return;
        }
      }
      const { textValue: sanitisedText, numValue: sanitisedValue } = sanitiseInput(value2, format);
      const newValue = sanitisedText.length < textValue.length || sanitisedText === textValue && originalValue.length < sanitisedText.length ? originalValue : sanitisedText;
      setTextValue(newValue);
      setValue(adjustedValueRef.current ? numValue : sanitisedValue);
      onValueChange(
        e,
        adjustedValueRef.current ? numValue : sanitisedValue
      );
    };
    const onInputFocus = (e) => {
      onFocus?.(e);
    };
    const onInputBlur = (e) => {
      if (allowsEmpty && textValue === "") {
        setValue(null);
        onValueChange(e, null);
        onBlur?.(e);
        return;
      }
      const value2 = getPureValue(textValue || "", format);
      let numValue = Number(value2);
      if (min && numValue < min) numValue = min;
      if (max && numValue > max) numValue = max;
      setTextValue(formatValue(numValue, format));
      onBlur?.(e);
      if (max && Number(value2) > max) {
        adjustedValueRef.current = false;
      }
    };
    React15.useEffect(() => {
      if (adjustedValueRef.current) return;
      setTextValue((current) => {
        const format2 = formatRef.current;
        const pureValue = getPureValue(current, format2);
        const numericValue = Number(pureValue);
        return pureValue === format2.nullValueText && value !== void 0 && value !== null || numericValue !== value ? formatValue(value, format2) : current;
      });
    }, [value, formatRef]);
    return /* @__PURE__ */ jsx77(
      Input,
      {
        ref: mergedRef,
        value: textValue,
        onChange,
        onBlur: onInputBlur,
        onFocus: onInputFocus,
        ...rest
      }
    );
  }
);
NumberInput.displayName = displayName22;
NumberInput.create = createShorthandFactory69({
  Component: NumberInput
});

// packages/aria/src/components/DropdownSort/DropdownSort.tsx
import React16, { useId as useId4 } from "react";
import { createShorthandFactory as createShorthandFactory70 } from "@fluentui/react-northstar";
import { getElementType as getElementType60 } from "@fluentui/react-northstar";
import { dropdownSlotClassNames as dropdownSlotClassNames3 } from "@fluentui/react-northstar";
import { useStyles as useStyles77 } from "@fluentui/react-northstar";
import { Dropdown as BaseDropdown2 } from "@fluentui/react-northstar";
import { CheckLine as CheckLine4 } from "@archipro-design/icons";
import { Fragment as Fragment14, jsx as jsx78, jsxs as jsxs53 } from "react/jsx-runtime";
var dropdownSortClassName = `ui-dropdownSort`;
var dropdownSortSlotClassNames = {
  ...dropdownSlotClassNames3,
  currentItem: `${dropdownSortClassName}__current-item`
};
var displayName23 = "DropdownSort";
var DropdownSort = React16.forwardRef(
  function Dropdown2(props, ref) {
    const getContentNode = (componentProps) => {
      if (typeof componentProps.content === "object") {
        return componentProps?.content && componentProps?.content["content"];
      } else {
        return componentProps.content;
      }
    };
    const { classes } = useStyles77(displayName23, {
      className: dropdownSortClassName,
      mapPropsToInlineStyles: () => ({
        className: props.className,
        styles: props.styles,
        variables: props.variables
      })
    });
    const ElementType = getElementType60(props);
    const {
      expandIcon,
      collapseIcon,
      offset = [5, 0],
      ...handledProps
    } = props;
    const id = useId4();
    return /* @__PURE__ */ jsx78(ElementType, { className: classes["root"], ref, children: /* @__PURE__ */ jsx78(
      BaseDropdown2,
      {
        offset,
        checkable: true,
        checkableIndicator: /* @__PURE__ */ jsx78(CheckLine4, { variables: { "1xSize": pxToRem(20) } }),
        toggleIndicator: false,
        triggerButton: {
          children: (Component, componentProps) => {
            return /* @__PURE__ */ jsx78(
              Component,
              {
                ...componentProps,
                content: !props.multiple ? /* @__PURE__ */ jsxs53(Fragment14, { children: [
                  componentProps["aria-expanded"] ? expandIcon : collapseIcon,
                  /* @__PURE__ */ jsx78(
                    Box,
                    {
                      className: dropdownSortSlotClassNames.currentItem,
                      children: props.defaultValue === getContentNode(
                        componentProps
                      ) ? props.placeholder : getContentNode(
                        componentProps
                      )
                    }
                  )
                ] }) : getContentNode(componentProps)
              }
            );
          }
        },
        "aria-labelledby": `downshift-${id}-label`,
        ...handledProps
      }
    ) });
  }
);
DropdownSort.displayName = displayName23;
DropdownSort.create = createShorthandFactory70({
  Component: DropdownSort
});
DropdownSort.defaultProps = {
  variables: {
    belowListBorderRadius: "0"
  }
};
DropdownSort.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "offset",
  "expandIcon",
  "collapseIcon"
];

// packages/aria/src/components/Tile/MemberTile/MemberTile.tsx
import {
  useFluentContext as useFluentContext77,
  useAccessibility as useAccessibility72,
  useTelemetry as useTelemetry77,
  useStyles as useStyles78,
  useUnhandledProps as useUnhandledProps76,
  cardBehavior as cardBehavior12,
  createShorthandFactory as createShorthandFactory71
} from "@fluentui/react-northstar";
import { getElementType as getElementType61 } from "@fluentui/react-bindings";
import { forwardRef as forwardRef75 } from "react";

// packages/aria/src/themes/base/components/Tile/MemberTile/memberTileVariables.ts
var memberTileVariables = (siteVars) => ({
  titleColor: siteVars.colors.charcoal[350],
  titleColorHover: siteVars.colors.charcoal[350],
  jobTitleColor: siteVars.colors.charcoal[100],
  jobTitleColorHover: siteVars.colors.charcoal[100],
  tileWidth: 395,
  tileHeight: 465,
  tileBorderRadius: 10
});

// packages/aria/src/components/Tile/MemberTile/MemberTile.tsx
import { jsx as jsx79, jsxs as jsxs54 } from "react/jsx-runtime";
var defaultRootElementType30 = "div";
var MemberTileClassName = "ui-MemberTile";
var MemberTile = forwardRef75(
  (props, ref) => {
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      image,
      name,
      jobTitle,
      onToggleDetails
    } = props;
    const unhandledProps = useUnhandledProps76(
      MemberTile.handledProps,
      props
    );
    const context = useFluentContext77();
    const { setStart, setEnd } = useTelemetry77(
      MemberTile.displayName,
      context.telemetry
    );
    setStart();
    const getA11yProps = useAccessibility72(
      accessibility,
      {
        debugName: MemberTile.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles78(MemberTile.displayName, {
      className: MemberTileClassName,
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    });
    const ElementType = getElementType61(props);
    const tileVars = memberTileVariables(context.theme.siteVariables);
    const result = /* @__PURE__ */ jsxs54(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: [
          /* @__PURE__ */ jsx79(Box, { className: classes["imageWrapper"], children: Image.create(image, {
            defaultProps: () => ({
              className: classes["image"],
              objectFit: "cover",
              height: tileVars.tileHeight,
              width: tileVars.tileWidth,
              overlay: {
                onClick: (e) => onToggleDetails(e)
              }
            })
          }) }),
          Caption.create(name, {
            defaultProps: () => {
              return {
                className: classes["firstLabel"],
                variables: {
                  color: tileVars.titleColor
                },
                variant: "04",
                onClick: (e) => onToggleDetails(e)
              };
            }
          }),
          Caption.create(jobTitle, {
            defaultProps: () => {
              return {
                className: classes["secondLabel"],
                onClick: (e) => onToggleDetails(e),
                variant: "04",
                variables: {
                  color: tileVars.jobTitleColor
                }
              };
            }
          })
        ]
      }
    );
    setEnd();
    return result;
  }
);
MemberTile.displayName = "MemberTile";
MemberTile.create = createShorthandFactory71({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: MemberTile
});
MemberTile.defaultProps = {
  as: defaultRootElementType30,
  accessibility: cardBehavior12
};
MemberTile.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "image",
  "name",
  "jobTitle",
  "onToggleDetails"
];

// packages/aria/src/components/Divider/Divider.tsx
import {
  Divider as Divider2,
  DividerContent,
  dividerClassName,
  dividerContentClassName
} from "@fluentui/react-northstar";

// packages/aria/src/components/Tooltip/MessageTooltipContent.tsx
import { forwardRef as forwardRef76 } from "react";
import {
  useFluentContext as useFluentContext78,
  getElementType as getElementType62,
  useAccessibility as useAccessibility73,
  useTelemetry as useTelemetry78,
  useStyles as useStyles79,
  useUnhandledProps as useUnhandledProps77,
  createShorthandFactory as createShorthandFactory72,
  rtlTextContainer as rtlTextContainer6,
  TooltipContent
} from "@fluentui/react-northstar";
import { childrenExist as childrenExist9 } from "@fluentui/react-bindings";

// packages/aria/src/utils/getBasePlacement.ts
var rtlMapping = {
  left: "right",
  right: "left"
};
function getBasePlacement(placement, rtl) {
  const basePlacement = (placement || "").split("-", 1).pop();
  return rtl && rtlMapping[basePlacement] || basePlacement;
}

// packages/aria/src/components/Tooltip/MessageTooltipContent.tsx
import { jsx as jsx80, jsxs as jsxs55 } from "react/jsx-runtime";
var messageTooltipContentClassName = "ui-messageTooltipContent";
var messageTooltipSlotClassNames = {
  icon: messageTooltipContentClassName + "__icon"
};
var MessageTooltipContent = forwardRef76((props, ref) => {
  const context = useFluentContext78();
  const { setStart, setEnd } = useTelemetry78(
    MessageTooltipContent.displayName,
    context.telemetry
  );
  setStart();
  const {
    accessibility,
    children,
    className,
    content,
    design,
    open = false,
    placement,
    pointing = true,
    pointerRef,
    styles,
    variables,
    subtle = false,
    iconPosition,
    icon,
    size,
    color,
    padding
  } = props;
  const getA11yProps = useAccessibility73(
    accessibility,
    {
      debugName: MessageTooltipContent.displayName,
      rtl: context.rtl
    }
  );
  const { classes } = useStyles79(
    MessageTooltipContent.displayName,
    {
      className: messageTooltipContentClassName,
      mapPropsToStyles: () => ({
        basePlacement: getBasePlacement(placement, context.rtl),
        open,
        pointing,
        subtle,
        size,
        color,
        padding,
        iconPosition
      }),
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    }
  );
  const unhandledProps = useUnhandledProps77(
    MessageTooltipContent.handledProps,
    props
  );
  const ElementType = getElementType62(props);
  const renderIcon = () => {
    return Box.create(
      {},
      {
        defaultProps: () => ({
          as: "span",
          className: messageTooltipSlotClassNames.icon,
          content: icon
        })
      }
    );
  };
  const result = /* @__PURE__ */ jsxs55(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...rtlTextContainer6.getAttributes({
          forElements: [children, content]
        }),
        ...unhandledProps
      }),
      children: [
        open && pointing && /* @__PURE__ */ jsx80("div", { className: classes["pointer"], ref: pointerRef }),
        /* @__PURE__ */ jsxs55(
          "div",
          {
            ...getA11yProps("content", { className: classes["content"] }),
            children: [
              iconPosition === "before" && renderIcon(),
              childrenExist9(children) ? children : content,
              iconPosition === "after" && renderIcon()
            ]
          }
        )
      ]
    }
  );
  setEnd();
  return result;
});
MessageTooltipContent.displayName = "MessageTooltipContent";
MessageTooltipContent.create = createShorthandFactory72({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: MessageTooltipContent,
  mappedProp: "children"
});
MessageTooltipContent.defaultProps = {
  as: "div",
  size: 24,
  color: "black",
  padding: 12,
  iconPosition: "before"
};
MessageTooltipContent.handledProps = [
  ...TooltipContent.handledProps,
  "iconPosition",
  "icon",
  "children"
];
var MessageTooltipContent_default = MessageTooltipContent;

// packages/aria/src/components/Tooltip/MessageTooltip.tsx
import {
  useFluentContext as useFluentContext79,
  useTelemetry as useTelemetry79,
  useUnhandledProps as useUnhandledProps78,
  Tooltip,
  createShorthandFactory as createShorthandFactory73
} from "@fluentui/react-northstar";
import { useEffect as useEffect10, useId as useId5, useState as useState10 } from "react";
import { jsx as jsx81 } from "react/jsx-runtime";
var messageTooltipClassName = "ui-messageTooltip";
var MessageTooltip = ((props) => {
  const context = useFluentContext79();
  const { setStart, setEnd } = useTelemetry79(
    MessageTooltip.displayName,
    context.telemetry
  );
  setStart();
  const { content } = props;
  const unhandledProps = useUnhandledProps78(
    MessageTooltip.handledProps,
    props
  );
  const [hydrated, setHydrated] = useState10(false);
  useEffect10(() => {
    setHydrated(true);
  }, []);
  const id = useId5();
  const result = hydrated ? /* @__PURE__ */ jsx81(
    Tooltip,
    {
      content: {
        // ID is required to be passed in so we don't get inconsistent results between server and client.
        // react18 useId hook is perfect for this
        id: `tooltip-content-${id}`,
        // Hydration issues occur because of something inside the fluentui implementation of the tooltip
        children: (_14, props2) => {
          return MessageTooltipContent_default.create(content, {
            defaultProps: () => props2
          });
        }
      },
      ...unhandledProps
    }
  ) : null;
  setEnd();
  return result;
});
MessageTooltip.displayName = "MessageTooltip";
MessageTooltip.create = createShorthandFactory73({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: MessageTooltip
});
MessageTooltip.handledProps = ["content"];

// packages/aria/src/components/Pagination/Pagination.tsx
import { forwardRef as forwardRef79 } from "react";
import {
  useFluentContext as useFluentContext83,
  getElementType as getElementType65,
  useAccessibility as useAccessibility77,
  useTelemetry as useTelemetry83,
  useStyles as useStyles83,
  useUnhandledProps as useUnhandledProps82,
  menuBehavior as menuBehavior11,
  createShorthandFactory as createShorthandFactory77,
  createShorthand as createShorthand3
} from "@fluentui/react-northstar";

// packages/aria/src/components/Pagination/PaginationItem.tsx
import { forwardRef as forwardRef78 } from "react";
import {
  useFluentContext as useFluentContext81,
  getElementType as getElementType64,
  useAccessibility as useAccessibility75,
  useTelemetry as useTelemetry81,
  useStyles as useStyles81,
  useUnhandledProps as useUnhandledProps80,
  createShorthandFactory as createShorthandFactory75
} from "@fluentui/react-northstar";

// packages/aria/src/components/Pagination/PaginationItemContent.tsx
import { forwardRef as forwardRef77 } from "react";
import {
  useFluentContext as useFluentContext80,
  getElementType as getElementType63,
  useAccessibility as useAccessibility74,
  useTelemetry as useTelemetry80,
  useStyles as useStyles80,
  useUnhandledProps as useUnhandledProps79,
  buttonBehavior as buttonBehavior7,
  createShorthandFactory as createShorthandFactory74,
  createShorthand
} from "@fluentui/react-northstar";

// packages/aria/src/components/Pagination/utils/index.ts
var getUsePaginationHandledProps = () => [
  "getItemAriaLabel",
  "boundaryCount",
  "count",
  "defaultPage",
  "disabled",
  "hideNextButton",
  "hidePrevButton",
  "onChange",
  "page",
  "showFirstButton",
  "showLastButton",
  "siblingCount",
  "hidePageNumbers"
];
var getPaginationItemHandledProps = () => [
  "onClick",
  "type",
  "page",
  "selected",
  "disabled",
  "count"
];

// packages/aria/src/components/Pagination/PaginationItemContent.tsx
import {
  ArrowLeftSLine as ArrowLeftSLine2,
  ArrowRightSLine as ArrowRightSLine3,
  MoreLine
} from "@archipro-design/icons";
import cx14 from "classnames";
import { jsx as jsx82 } from "react/jsx-runtime";
var paginationItemContentClassName = "ui-paginationItemContent";
var paginationItemPageClassName = `${paginationItemContentClassName}__page`;
var paginationItemPrevNextClassName = `${paginationItemContentClassName}__prevNext`;
var paginationItemEllipsisClassName = `${paginationItemContentClassName}__ellipsis`;
function isEllipsis(type) {
  return type === "start-ellipsis" || type === "end-ellipsis";
}
var PaginationItemContent = forwardRef77((props, ref) => {
  const context = useFluentContext80();
  const { setStart, setEnd } = useTelemetry80(
    PaginationItemContent.displayName,
    context.telemetry
  );
  setStart();
  const {
    className,
    styles,
    variables,
    accessibility,
    design,
    disabled,
    color,
    shape,
    size,
    variant,
    onClick,
    type,
    page,
    selected
  } = props;
  const isButton = !["start-ellipsis", "end-ellipsis"].includes(type);
  const getA11yProps = useAccessibility74(
    accessibility,
    {
      debugName: PaginationItemContent.displayName,
      rtl: context.rtl,
      mapPropsToBehavior: () => ({
        disabled,
        disabledFocusable: !isButton,
        as: isButton ? "span" : "button"
      })
    }
  );
  const { classes } = useStyles80(
    PaginationItemContent.displayName,
    {
      className: paginationItemContentClassName,
      mapPropsToStyles: () => ({
        disabled,
        color,
        shape,
        size,
        variant,
        type,
        selected
      }),
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    }
  );
  const unhandledProps = useUnhandledProps79(
    PaginationItemContent.handledProps,
    props
  );
  const ElementType = getElementType63(props);
  const renderItem = () => {
    const itemProps = getA11yProps("root", {
      className: classes["root"],
      ref,
      onClick,
      disabled,
      ...unhandledProps
    });
    if (isEllipsis(type)) {
      return Flex.create(/* @__PURE__ */ jsx82(MoreLine, {}), {
        defaultProps: () => ({
          vAlign: "center",
          fill: true
        }),
        overrideProps: (props2) => ({
          className: cx14(
            props2.className,
            paginationItemEllipsisClassName
          )
        })
      });
    } else if (type === "page") {
      return createShorthand(ElementType, type === "page" ? page : null, {
        defaultProps: () => ({
          ...itemProps,
          variant: "text",
          size: 18,
          selected
        }),
        overrideProps: (props2) => ({
          className: cx14(props2.className, paginationItemPageClassName)
        })
      });
    } else if (["next", "previous"].includes(type)) {
      const text = type === "next" ? "Next" : "Prev";
      return createShorthand(ElementType, text, {
        defaultProps: () => {
          return {
            ...itemProps,
            ...type === "next" && {
              iconAfter: /* @__PURE__ */ jsx82(ArrowRightSLine3, {})
            },
            ...type === "previous" && {
              iconBefore: /* @__PURE__ */ jsx82(ArrowLeftSLine2, {})
            },
            ...disabled && {
              pointer: "disabled"
            },
            variant: "text",
            size: 18,
            variables: {
              buttonPadding: 16,
              buttonSize18IconXMargin: 0,
              iconPadding: 0
            }
          };
        },
        overrideProps: (props2) => ({
          className: cx14(
            props2.className,
            paginationItemPrevNextClassName
          )
        })
      });
    }
    return null;
  };
  const result = renderItem();
  setEnd();
  return result;
});
PaginationItemContent.displayName = "PaginationItemContent";
PaginationItemContent.create = createShorthandFactory74({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: PaginationItemContent
});
PaginationItemContent.defaultProps = {
  as: Button,
  accessibility: buttonBehavior7
};
PaginationItemContent.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "as",
  // Extra props that are handled
  "disabled",
  "color",
  "shape",
  "size",
  "variant",
  "type",
  "icons",
  ...getPaginationItemHandledProps()
];

// packages/aria/src/components/Pagination/PaginationItem.tsx
import { jsx as jsx83 } from "react/jsx-runtime";
var defaultRootElementType31 = "li";
var paginationItemClassName = "ui-paginationItem";
var PaginationItem = forwardRef78(
  (props, ref) => {
    const context = useFluentContext81();
    const { setStart, setEnd } = useTelemetry81(
      PaginationItem.displayName,
      context.telemetry
    );
    setStart();
    const { className, styles, variables, accessibility, design, content } = props;
    const getA11yProps = useAccessibility75(
      accessibility,
      {
        debugName: PaginationItem.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles81(
      PaginationItem.displayName,
      {
        className: paginationItemClassName,
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps80(
      PaginationItem.handledProps,
      props
    );
    const ElementType = getElementType64(props);
    const result = /* @__PURE__ */ jsx83(
      ElementType,
      {
        ...getA11yProps("root", {
          // All these props are passed down to the root element.
          // The root element in this case is `ElementType`
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: PaginationItemContent.create(content)
      }
    );
    setEnd();
    return result;
  }
);
PaginationItem.displayName = "PaginationItem";
PaginationItem.create = createShorthandFactory75({
  Component: PaginationItem,
  mappedProp: "content"
});
PaginationItem.defaultProps = {
  as: defaultRootElementType31
};
PaginationItem.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "as",
  // Extra props that are handled
  "content"
];

// packages/aria/src/components/Pagination/hooks/usePagination.ts
import { useAutoControlled as useAutoControlled6 } from "@fluentui/react-bindings";
function defaultGetAriaLabel(type, page, selected) {
  if (type === "page") {
    return `${selected ? "" : "Go to "}page ${page}`;
  }
  return `Go to ${type} page`;
}
function usePagination(props = {}) {
  const {
    getItemAriaLabel = defaultGetAriaLabel,
    boundaryCount = 1,
    count = 1,
    defaultPage = 1,
    disabled = false,
    hideNextButton = false,
    hidePrevButton = false,
    onChange: handleChange,
    page: pageProp,
    showFirstButton = false,
    showLastButton = false,
    siblingCount = 1,
    ...other
  } = props;
  const [_page, setPageState] = useAutoControlled6({
    value: pageProp,
    defaultValue: defaultPage,
    initialValue: 0
  });
  const page = _page;
  const handleClick = (event, pageValue) => {
    if (pageValue && pageValue > count) {
      return;
    }
    if (!pageProp) {
      setPageState(pageValue || page);
    }
    if (handleChange) {
      handleChange(event, pageValue);
    }
  };
  const range = (start, end) => {
    const length = end - start + 1;
    return Array.from({ length }, (_14, i) => start + i);
  };
  const startPages = range(1, Math.min(boundaryCount, count));
  const endPages = range(
    Math.max(count - boundaryCount + 1, boundaryCount + 1),
    count
  );
  const siblingsStart = Math.max(
    Math.min(
      // Natural start
      page - siblingCount,
      // Lower boundary when page is high
      count - boundaryCount - siblingCount * 2 - 1
    ),
    // Greater than startPages
    boundaryCount + 2
  );
  const siblingsEnd = Math.min(
    Math.max(
      // Natural end
      page + siblingCount,
      // Upper boundary when page is low
      boundaryCount + siblingCount * 2 + 2
    ),
    // Less than endPages
    endPages.length > 0 ? endPages[0] - 2 : count - 1
  );
  const itemList = [
    ...showFirstButton ? ["first"] : [],
    ...hidePrevButton ? [] : ["previous"],
    ...startPages,
    // Start ellipsis
    // eslint-disable-next-line no-nested-ternary
    ...siblingsStart > boundaryCount + 2 ? ["start-ellipsis"] : boundaryCount + 1 < count - boundaryCount ? [boundaryCount + 1] : [],
    // Sibling pages
    ...range(siblingsStart, siblingsEnd),
    // End ellipsis
    // eslint-disable-next-line no-nested-ternary
    ...siblingsEnd < count - boundaryCount - 1 ? ["end-ellipsis"] : count - boundaryCount > boundaryCount ? [count - boundaryCount] : [],
    ...endPages,
    ...hideNextButton ? [] : ["next"],
    ...showLastButton ? ["last"] : []
  ];
  const buttonPage = (type) => {
    switch (type) {
      case "first":
        return 1;
      case "previous":
        return page - 1;
      case "next":
        return page + 1;
      case "last":
        return count;
      default:
        return null;
    }
  };
  const nextItemNumber = buttonPage("next");
  const nextItem = {
    onClick: (event) => {
      handleClick(event, nextItemNumber);
    },
    type: "next",
    page: nextItemNumber,
    selected: false,
    disabled: disabled || page >= count,
    "aria-label": getItemAriaLabel(
      "page",
      nextItemNumber,
      nextItemNumber === page
    )
  };
  const previousItemNumber = buttonPage(
    "previous"
  );
  const previousItem = {
    onClick: (event) => {
      handleClick(event, previousItemNumber);
    },
    type: "previous",
    page: previousItemNumber,
    selected: false,
    disabled: disabled || page <= 1,
    "aria-label": getItemAriaLabel(
      "page",
      previousItemNumber,
      previousItemNumber === page
    )
  };
  const items = itemList.map((item) => {
    const isNumber = typeof item === "number";
    const itemPage = isNumber ? item : buttonPage(item);
    return isNumber ? {
      onClick: (event) => {
        handleClick(event, item);
      },
      type: "page",
      page: itemPage,
      selected: itemPage === page,
      disabled,
      "aria-current": itemPage === page ? "true" : void 0,
      "aria-label": getItemAriaLabel(
        "page",
        itemPage,
        itemPage === page
      )
    } : {
      onClick: (event) => {
        handleClick(
          event,
          buttonPage(item)
        );
      },
      type: item,
      page: buttonPage(item),
      selected: false,
      disabled: disabled || item.indexOf("ellipsis") === -1 && (item === "next" || item === "last" ? page >= count : page <= 1),
      "aria-label": getItemAriaLabel(
        "page",
        itemPage,
        itemPage === page
      )
    };
  });
  return {
    items,
    nextItem,
    previousItem,
    ...other
  };
}

// packages/aria/src/components/Pagination/PaginationItemCounter.tsx
import { createShorthandFactory as createShorthandFactory76 } from "@fluentui/react-northstar";
import { compose as compose4 } from "@fluentui/react-bindings";
var paginationItemCounterClassName = "ui-paginationItemCounter";
function defaultTextRenderer(range, total) {
  return `${range.from} - ${range.to} of ${total} items`;
}
var PaginationItemCounter = compose4(Caption, {
  displayName: "PaginationItemCounter",
  className: paginationItemCounterClassName,
  handledProps: ["range", "total", "textRenderer"],
  overrideStyles: true,
  state: ({ textRenderer = defaultTextRenderer, range, total }) => {
    return {
      children: textRenderer(range, total)
    };
  }
});
PaginationItemCounter.create = createShorthandFactory76({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: PaginationItemCounter
});
PaginationItemCounter.defaultProps = {
  textRenderer: defaultTextRenderer
};

// packages/aria/src/components/Pagination/PaginationItemsPerPage.tsx
import {
  useFluentContext as useFluentContext82,
  useAccessibility as useAccessibility76,
  useTelemetry as useTelemetry82,
  useStyles as useStyles82,
  useUnhandledProps as useUnhandledProps81,
  createShorthand as createShorthand2
} from "@fluentui/react-northstar";
import { compose as compose5 } from "@fluentui/react-bindings";
import cx15 from "classnames";
import { jsxs as jsxs56 } from "react/jsx-runtime";
var paginationItemsPerPageClassName = "ui-paginationItemsPerPage";
var paginationItemsPerPageSlotClassNames = {
  caption: `${paginationItemsPerPageClassName}__caption`,
  dropdown: `${paginationItemsPerPageClassName}__dropdown`,
  selectedIcon: `${paginationItemsPerPageClassName}__selectedIcon`
};
var PaginationItemsPerPage = compose5(
  (props, ref, composeOptions) => {
    const context = useFluentContext82();
    const { setStart, setEnd } = useTelemetry82(
      composeOptions.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      dropdown = {},
      label = "items per page:"
    } = props;
    const getA11yProps = useAccessibility76(
      accessibility,
      {
        debugName: composeOptions.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles82(
      composeOptions.displayName,
      {
        className: composeOptions.className,
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps81(
      composeOptions.handledProps,
      props
    );
    const result = (
      // @ts-ignore tsgo: getA11yProps spread produces type incompatible with FlexProps (TS2322)
      /* @__PURE__ */ jsxs56(
        Flex,
        {
          ...getA11yProps("root", {
            className: classes["root"],
            ref,
            vAlign: "center",
            ...unhandledProps
          }),
          children: [
            Caption.create(label, {
              defaultProps: () => ({
                className: paginationItemsPerPageSlotClassNames.caption,
                inline: true
              })
            }),
            createShorthand2(
              Dropdown,
              // I don't know why it's complaining.. the types match :(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              dropdown,
              {
                defaultProps: () => ({
                  className: cx15(
                    props.className,
                    paginationItemsPerPageSlotClassNames.dropdown
                  ),
                  inline: true,
                  checkable: true,
                  position: "above",
                  defaultValue: 12,
                  items: [12, 24, 48, 72]
                })
              }
            )
          ]
        }
      )
    );
    setEnd();
    return result;
  },
  {
    displayName: "PaginationItemsPerPage",
    className: paginationItemsPerPageClassName,
    handledProps: [...commonHandledProps(), "dropdown", "label"]
  }
);

// packages/aria/src/components/Pagination/Pagination.tsx
import { jsx as jsx84, jsxs as jsxs57 } from "react/jsx-runtime";
var defaultRootElementType32 = "nav";
var paginationClassName = "ui-pagination";
var paginationSlotClassNames = {
  list: `${paginationClassName}__list`,
  itemsPerPage: `${paginationClassName}__itemsPerPage`,
  paginationMetaData: `${paginationClassName}__paginationMetaData`,
  divider: `${paginationClassName}__divider`
};
var Pagination2 = forwardRef79(
  (props, ref) => {
    const context = useFluentContext83();
    const { setStart, setEnd } = useTelemetry83(
      Pagination2.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      counter,
      divider = {},
      itemsPerPage,
      boundaryCount = 1,
      //color = 'standard',
      count = 1,
      defaultPage = 1,
      disabled = false,
      appDisplayMode,
      hideNextButton = appDisplayMode?.mobile,
      hidePrevButton = appDisplayMode?.mobile,
      onChange,
      page,
      renderItem = (item) => PaginationItem.create(
        { content: item, key: item.type + item.count },
        { generateKey: true }
      ),
      //shape = 'circular',
      showFirstButton = false,
      showLastButton = false,
      siblingCount = 1,
      //size = 'medium',
      //variant = 'text',
      hidePageNumbers,
      getItemAriaLabel
    } = props;
    const getA11yProps = useAccessibility77(
      accessibility,
      {
        debugName: Pagination2.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles83(
      Pagination2.displayName,
      {
        className: paginationClassName,
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps82(
      Pagination2.handledProps,
      props
    );
    const ElementType = getElementType65(props);
    const { items, nextItem, previousItem } = usePagination({
      page,
      count,
      disabled,
      onChange,
      defaultPage,
      siblingCount,
      boundaryCount,
      hideNextButton,
      hidePrevButton,
      showLastButton,
      hidePageNumbers,
      showFirstButton,
      getItemAriaLabel
    });
    const renderItems = () => {
      return items.map((item) => renderItem(item));
    };
    const renderMobilePrev = () => {
      return renderItem(previousItem);
    };
    const renderMobileNext = () => {
      return renderItem(nextItem);
    };
    const renderMobileMeta = () => {
      return /* @__PURE__ */ jsxs57(
        Flex,
        {
          gap: "space-20",
          vAlign: "center",
          hAlign: "center",
          space: "between",
          className: paginationSlotClassNames.paginationMetaData,
          children: [
            renderMobilePrev(),
            PaginationItemCounter.create(counter, {
              defaultProps: () => ({
                variant: appDisplayMode?.desktop ? "03" : "04"
              })
            }),
            renderMobileNext()
          ]
        }
      );
    };
    const renderMeta = () => {
      return /* @__PURE__ */ jsxs57(
        Flex,
        {
          gap: "space-12",
          vAlign: "center",
          hAlign: "center",
          className: paginationSlotClassNames.paginationMetaData,
          children: [
            PaginationItemCounter.create(counter, {
              defaultProps: () => ({
                variant: "03"
              })
            }),
            createShorthand3(Divider2, divider, {
              defaultProps: () => ({
                className: paginationSlotClassNames.divider,
                size: 1,
                vertical: true,
                fitted: true,
                color: "secondary"
              })
            }),
            createShorthand3(PaginationItemsPerPage, itemsPerPage)
          ]
        }
      );
    };
    const result = /* @__PURE__ */ jsxs57(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: [
          /* @__PURE__ */ jsx84(
            Flex,
            {
              as: "ul",
              vAlign: "center",
              hAlign: "center",
              className: paginationSlotClassNames.list,
              children: renderItems()
            }
          ),
          appDisplayMode?.mobile ? renderMobileMeta() : renderMeta()
        ]
      }
    );
    setEnd();
    return result;
  }
);
Pagination2.displayName = "Pagination";
Pagination2.create = createShorthandFactory77({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: Pagination2
});
Pagination2.defaultProps = {
  as: defaultRootElementType32,
  accessibility: menuBehavior11
};
Pagination2.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "as",
  // Extra props that are handled
  "disabled",
  ...getUsePaginationHandledProps(),
  "counter",
  "color",
  "shape",
  "divider",
  "itemsPerPage",
  "appDisplayMode",
  "pageSize",
  "totalCount",
  "renderItem",
  "nextButton",
  "prevButton"
];
Pagination2.PaginationItem = PaginationItem;
Pagination2.PaginationItemContent = PaginationItemContent;
Pagination2.PaginationItemCounter = PaginationItemCounter;

// packages/aria/src/components/Dropdown/DropdownItem.tsx
import {
  DropdownItem,
  dropdownItemClassName,
  dropdownItemSlotClassNames
} from "@fluentui/react-northstar";

// packages/aria/src/components/FeaturedArticle/FeaturedArticle.tsx
import { forwardRef as forwardRef80 } from "react";
import {
  useFluentContext as useFluentContext84,
  getElementType as getElementType66,
  useAccessibility as useAccessibility78,
  useTelemetry as useTelemetry84,
  useStyles as useStyles84,
  useUnhandledProps as useUnhandledProps83,
  cardBehavior as cardBehavior13,
  createShorthandFactory as createShorthandFactory78
} from "@fluentui/react-northstar";
import { HeartFill as HeartFill3, HeartLine as HeartLine4 } from "@archipro-design/icons";
import { useAutoControlled as useAutoControlled7 } from "@fluentui/react-bindings";
import { jsx as jsx85, jsxs as jsxs58 } from "react/jsx-runtime";
var defaultRootElementType33 = "div";
var FeaturedArticleClassName = "ui-FeaturedArticle";
var featuredArticleSlotClassNames = {
  contentButton: `${FeaturedArticleClassName}__contentButton`
};
var FeaturedArticle = forwardRef80(
  (props, ref) => {
    const context = useFluentContext84();
    const { setStart, setEnd } = useTelemetry84(
      FeaturedArticle.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      image,
      video,
      title,
      categoryTitle,
      date,
      readTime,
      isFavourited = false,
      onFavourite,
      hasVideo = false,
      content,
      viewMoreButton,
      variant = "01",
      contentButton,
      topActions
    } = props;
    const [isFavourite, setFavourite] = useAutoControlled7({
      defaultValue: isFavourited || false,
      value: isFavourited
    });
    const handleFavourite = (e) => {
      e.stopPropagation();
      e.preventDefault();
      setFavourite(!isFavourite);
      if (onFavourite) onFavourite(e);
    };
    const getA11yProps = useAccessibility78(
      accessibility,
      {
        debugName: FeaturedArticle.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles84(FeaturedArticle.displayName, {
      className: FeaturedArticleClassName,
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      mapPropsToStyles: () => ({
        variant
      }),
      rtl: context.rtl,
      unstable_props: props
    });
    const unhandledProps = useUnhandledProps83(
      FeaturedArticle.handledProps,
      props
    );
    const ElementType = getElementType66(props);
    const readTimeEle = Caption.create(readTime, {
      defaultProps: () => {
        return {
          variant: "05",
          uc: true,
          maximumLines: 1
        };
      }
    });
    const CONTENT_LAYOUT_MAPPING = {
      "01": pxArrayToRem([498, 754]),
      "02": "100%",
      "03": "100%"
    };
    const imageOrVideo = hasVideo && video && video.src && video.coverImage ? /* @__PURE__ */ jsx85("div", { className: classes["videoContainer"], children: VideoPlayer.create(video) }) : Image.create(image, {
      defaultProps: () => {
        return {
          width: "100%",
          height: pxToRem(800),
          layout: "fill",
          objectFit: "cover",
          ...variant === "02" && {
            width: pxToRem(780),
            height: pxToRem(620)
          }
        };
      }
    });
    const result = /* @__PURE__ */ jsxs58(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: [
          /* @__PURE__ */ jsxs58(Box, { className: classes["imageWrap"], children: [
            imageOrVideo,
            /* @__PURE__ */ jsxs58(
              Flex,
              {
                hAlign: "end",
                vAlign: "center",
                gap: "space-16",
                className: classes["topButtons"],
                children: [
                  topActions && (variant === "02" || variant === "03") && /* @__PURE__ */ jsx85(
                    Flex,
                    {
                      className: classes["hotButtons"],
                      hAlign: variant === "02" ? "start" : "end",
                      children: /* @__PURE__ */ jsx85(Box, { children: topActions })
                    }
                  ),
                  variant === "01" && /* @__PURE__ */ jsx85(Box, { children: /* @__PURE__ */ jsx85(
                    Button,
                    {
                      size: 24,
                      variables: {
                        buttonPadding: 12,
                        iconPadding: 0
                      },
                      color: "grey",
                      onClick: handleFavourite,
                      icon: isFavourited ? /* @__PURE__ */ jsx85(HeartFill3, {}) : /* @__PURE__ */ jsx85(HeartLine4, {})
                    },
                    "favourite-button"
                  ) })
                ]
              }
            )
          ] }),
          /* @__PURE__ */ jsxs58(
            Grid,
            {
              className: classes["contentWrap"],
              columns: CONTENT_LAYOUT_MAPPING[variant],
              children: [
                /* @__PURE__ */ jsxs58(Flex, { column: true, children: [
                  Caption.create(title, {
                    defaultProps: () => {
                      return {
                        maximumLines: 2,
                        className: classes["contentTitle"]
                      };
                    }
                  }),
                  /* @__PURE__ */ jsxs58(Flex, { column: true, gap: "space-6", children: [
                    Caption.create(categoryTitle, {
                      defaultProps: () => {
                        return {
                          variant: "05",
                          uc: true,
                          maximumLines: 1,
                          weight: "medium"
                        };
                      }
                    }),
                    /* @__PURE__ */ jsxs58(Flex, { gap: "space-6", vAlign: "center", children: [
                      Caption.create(date, {
                        defaultProps: () => {
                          return {
                            variant: "05",
                            uc: true,
                            maximumLines: 1
                          };
                        }
                      }),
                      readTimeEle && /* @__PURE__ */ jsx85("span", { className: classes["ellipse"] }),
                      readTimeEle
                    ] }),
                    contentButton && /* @__PURE__ */ jsx85(Flex, { children: Button.create(contentButton, {
                      defaultProps: () => ({
                        className: featuredArticleSlotClassNames.contentButton,
                        size: 16
                      })
                    }) })
                  ] })
                ] }),
                variant === "01" && /* @__PURE__ */ jsxs58(Box, { children: [
                  Caption.create(content, {
                    defaultProps: () => {
                      return {
                        className: classes["content"],
                        maximumLines: 3,
                        variant: "03"
                      };
                    }
                  }),
                  Button.create(viewMoreButton, {
                    defaultProps: () => ({
                      className: classes["toggleButton"],
                      color: "white",
                      size: 16,
                      variables: {
                        buttonPadding: 0,
                        whiteButtonBackgroundColorNormal: "transparent",
                        whiteButtonBackgroundColorHover: "transparent"
                      }
                    })
                  })
                ] })
              ]
            }
          )
        ]
      }
    );
    setEnd();
    return result;
  }
);
FeaturedArticle.displayName = "FeaturedArticle";
FeaturedArticle.create = createShorthandFactory78({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: FeaturedArticle
});
FeaturedArticle.defaultProps = {
  as: defaultRootElementType33,
  accessibility: cardBehavior13,
  isFavourited: false,
  onFavourite: () => void 0
};
FeaturedArticle.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  // Extra props that are handled
  "image",
  "title",
  "categoryTitle",
  "date",
  "readTime",
  "isFavourited",
  "onFavourite",
  "hasVideo",
  "video",
  "content",
  "viewMoreButton",
  "contentButton",
  ...adminTileHandledProps
];

// packages/aria/src/components/Checkbox/Checkbox.tsx
import { forwardRef as forwardRef84 } from "react";
import {
  useFluentContext as useFluentContext88,
  getElementType as getElementType70,
  useAccessibility as useAccessibility82,
  useTelemetry as useTelemetry88,
  useStyles as useStyles88,
  useUnhandledProps as useUnhandledProps87,
  checkboxBehavior as checkboxBehavior4,
  createShorthandFactory as createShorthandFactory82
} from "@fluentui/react-northstar";
import { useStateManager } from "@fluentui/react-bindings";
import { createCheckboxManager } from "@fluentui/state";
import cx16 from "classnames";

// packages/aria/src/components/Checkbox/IndicatorCheckbox.tsx
import { forwardRef as forwardRef81 } from "react";
import {
  useFluentContext as useFluentContext85,
  getElementType as getElementType67,
  useAccessibility as useAccessibility79,
  useTelemetry as useTelemetry85,
  useStyles as useStyles85,
  useUnhandledProps as useUnhandledProps84,
  checkboxBehavior,
  createShorthandFactory as createShorthandFactory79
} from "@fluentui/react-northstar";
import { CheckLine as CheckLine5 } from "@archipro-design/icons";
import { jsx as jsx86 } from "react/jsx-runtime";
var indicatorCheckboxClassName = "ui-indicatorCheckbox";
var IndicatorCheckbox = forwardRef81((props, ref) => {
  const context = useFluentContext85();
  const { setStart, setEnd } = useTelemetry85(
    IndicatorCheckbox.displayName,
    context.telemetry
  );
  setStart();
  const {
    className,
    styles,
    variables,
    accessibility,
    design,
    disabled,
    checked
  } = props;
  const getA11yProps = useAccessibility79(
    accessibility,
    {
      debugName: IndicatorCheckbox.displayName,
      rtl: context.rtl
    }
  );
  const { classes } = useStyles85(
    IndicatorCheckbox.displayName,
    {
      className: indicatorCheckboxClassName,
      mapPropsToStyles: () => ({
        disabled,
        checked
      }),
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    }
  );
  const unhandledProps = useUnhandledProps84(
    IndicatorCheckbox.handledProps,
    props
  );
  const ElementType = getElementType67(props);
  const result = /* @__PURE__ */ jsx86(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      children: checked && /* @__PURE__ */ jsx86(CheckLine5, {})
    }
  );
  setEnd();
  return result;
});
IndicatorCheckbox.displayName = "IndicatorCheckbox";
IndicatorCheckbox.create = createShorthandFactory79({
  Component: IndicatorCheckbox
});
IndicatorCheckbox.defaultProps = {
  accessibility: checkboxBehavior
};
IndicatorCheckbox.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  // Extra props that are handled
  "disabled",
  "checked"
];

// packages/aria/src/components/Checkbox/IndicatorRadio.tsx
import { forwardRef as forwardRef82 } from "react";
import {
  useFluentContext as useFluentContext86,
  getElementType as getElementType68,
  useAccessibility as useAccessibility80,
  useTelemetry as useTelemetry86,
  useStyles as useStyles86,
  useUnhandledProps as useUnhandledProps85,
  createShorthandFactory as createShorthandFactory80,
  checkboxBehavior as checkboxBehavior2
} from "@fluentui/react-northstar";
import { jsx as jsx87 } from "react/jsx-runtime";
var indicatorRadioClassName = "ui-indicatorRadio";
var IndicatorRadio = forwardRef82(
  (props, ref) => {
    const context = useFluentContext86();
    const { setStart, setEnd } = useTelemetry86(
      IndicatorRadio.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      disabled,
      checked
    } = props;
    const getA11yProps = useAccessibility80(
      accessibility,
      {
        debugName: IndicatorRadio.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles86(
      IndicatorRadio.displayName,
      {
        className: indicatorRadioClassName,
        mapPropsToStyles: () => ({
          disabled,
          checked
        }),
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps85(
      IndicatorRadio.handledProps,
      props
    );
    const ElementType = getElementType68(props);
    const result = /* @__PURE__ */ jsx87(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: checked && /* @__PURE__ */ jsx87("span", { className: classes["middleRing"], children: /* @__PURE__ */ jsx87("span", { className: classes["innerRing"] }) })
      }
    );
    setEnd();
    return result;
  }
);
IndicatorRadio.displayName = "IndicatorRadio";
IndicatorRadio.create = createShorthandFactory80({
  Component: IndicatorRadio
});
IndicatorRadio.defaultProps = {
  accessibility: checkboxBehavior2
};
IndicatorRadio.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  // Extra props that are handled
  "disabled",
  "checked"
];

// packages/aria/src/components/Checkbox/IndicatorColor.tsx
import { forwardRef as forwardRef83 } from "react";
import {
  useFluentContext as useFluentContext87,
  getElementType as getElementType69,
  useAccessibility as useAccessibility81,
  useTelemetry as useTelemetry87,
  useStyles as useStyles87,
  useUnhandledProps as useUnhandledProps86,
  createShorthandFactory as createShorthandFactory81,
  checkboxBehavior as checkboxBehavior3
} from "@fluentui/react-northstar";
import { CheckLine as CheckLine6 } from "@archipro-design/icons";
import { jsx as jsx88, jsxs as jsxs59 } from "react/jsx-runtime";
var indicatorColorClassName = "ui-indicatorColor";
var DEFAULT_COLOR_CODE = "#343434";
var DEFAULT_COLOR_SIZE = 30;
var IndicatorColor = forwardRef83(
  (props, ref) => {
    const context = useFluentContext87();
    const { setStart, setEnd } = useTelemetry87(
      IndicatorColor.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      disabled,
      checked,
      color = {
        colorCode: DEFAULT_COLOR_CODE,
        size: DEFAULT_COLOR_SIZE,
        highlighted: false
      }
    } = props;
    const getA11yProps = useAccessibility81(
      accessibility,
      {
        debugName: IndicatorColor.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles87(
      IndicatorColor.displayName,
      {
        className: indicatorColorClassName,
        mapPropsToStyles: () => ({
          disabled,
          checked
        }),
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps86(
      IndicatorColor.handledProps,
      props
    );
    const ElementType = getElementType69(props);
    const result = /* @__PURE__ */ jsxs59(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: [
          VariantColor_default.create(color, {
            overrideProps: () => ({
              highlighted: checked
            }),
            defaultProps: () => {
              return {
                colorCode: DEFAULT_COLOR_CODE,
                size: DEFAULT_COLOR_SIZE,
                variables: {
                  containerBorder: "2px solid transparent",
                  imageBorder: "unset"
                }
              };
            }
          }),
          checked && /* @__PURE__ */ jsx88(Flex, { className: classes["iconContainer"], children: /* @__PURE__ */ jsx88(CheckLine6, {}) })
        ]
      }
    );
    setEnd();
    return result;
  }
);
IndicatorColor.displayName = "IndicatorColor";
IndicatorColor.create = createShorthandFactory81({
  Component: IndicatorColor
});
IndicatorColor.defaultProps = {
  accessibility: checkboxBehavior3
};
IndicatorColor.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  // Extra props that are handled
  "disabled",
  "checked",
  "color"
];

// packages/aria/src/components/Checkbox/Checkbox.tsx
import { jsx as jsx89, jsxs as jsxs60 } from "react/jsx-runtime";
var checkboxClassName = "ui-checkbox";
var checkboxSlotClassNames = {
  indicatorContainer: `${checkboxClassName}__indicatorContainer`
};
var Checkbox = forwardRef84(
  (props, ref) => {
    const context = useFluentContext88();
    const { setStart, setEnd } = useTelemetry88(
      Checkbox.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      defaultChecked,
      checked,
      disabled,
      indicator = {
        kind: "checkbox"
      },
      label,
      labelPosition = "end",
      onChange
    } = props;
    const { state, actions } = useStateManager(createCheckboxManager, {
      mapPropsToInitialState: () => ({ checked: defaultChecked }),
      mapPropsToState: () => ({ checked })
    });
    const getA11yProps = useAccessibility82(
      accessibility,
      {
        debugName: Checkbox.displayName,
        mapPropsToBehavior: () => ({
          checked: state.checked,
          disabled
        }),
        rtl: context.rtl
      }
    );
    const { classes } = useStyles88(
      Checkbox.displayName,
      {
        className: checkboxClassName,
        mapPropsToStyles: () => ({
          checked: state.checked,
          disabled,
          indicatorType: indicator.kind
        }),
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps87(Checkbox.handledProps, props);
    const ElementType = getElementType70(props);
    const labelElement = Label.create(label, {
      defaultProps: () => ({ variant: "02" })
    });
    const renderIndicator = () => {
      const indicatorItem = indicator.indicator ?? {};
      switch (indicator.kind) {
        case "checkbox":
          return IndicatorCheckbox.create(
            indicatorItem,
            {
              overrideProps: () => ({
                checked: state.checked,
                disabled
              }),
              defaultProps: () => ({
                className: classes["indicator"]
              })
            }
          );
        case "radio":
          return IndicatorRadio.create(
            indicatorItem,
            {
              overrideProps: () => ({
                checked: state.checked,
                disabled
              }),
              defaultProps: () => ({
                className: classes["indicator"]
              })
            }
          );
        case "color":
          return IndicatorColor.create(
            indicatorItem,
            {
              overrideProps: () => ({
                checked: state.checked,
                disabled
              }),
              defaultProps: () => ({
                className: classes["indicator"]
              })
            }
          );
        default:
          throw new Error(
            `Unsupported indicator kind: ${indicator.kind}`
          );
      }
    };
    const handleClick = (e) => {
      if (!disabled) {
        const newChecked = !state.checked;
        actions.toggle(newChecked);
        onChange?.(e, { ...props, checked: newChecked });
      }
    };
    const result = /* @__PURE__ */ jsxs60(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps,
          onClick: handleClick
        }),
        children: [
          labelPosition === "start" && labelElement,
          /* @__PURE__ */ jsx89(
            Flex,
            {
              className: cx16(
                checkboxSlotClassNames.indicatorContainer,
                state.checked && "checked"
              ),
              children: renderIndicator()
            }
          ),
          labelPosition === "end" && labelElement
        ]
      }
    );
    setEnd();
    return result;
  }
);
Checkbox.displayName = "Checkbox";
Checkbox.create = createShorthandFactory82({
  Component: Checkbox
});
Checkbox.defaultProps = {
  accessibility: checkboxBehavior4
};
Checkbox.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  // Extra props that are handled
  "checked",
  "disabled",
  "defaultChecked",
  "indicator",
  "label",
  "labelPosition",
  "onChange"
];

// packages/aria/src/components/CheckboxGroup/CheckboxGroup.tsx
import {
  useFluentContext as useFluentContext89,
  getElementType as getElementType71,
  useAccessibility as useAccessibility83,
  useTelemetry as useTelemetry89,
  useStyles as useStyles89,
  useUnhandledProps as useUnhandledProps88,
  createShorthandFactory as createShorthandFactory83,
  selectableListBehavior
} from "@fluentui/react-northstar";
import { useAutoControlled as useAutoControlled8 } from "@fluentui/react-bindings";
import _7 from "lodash";
import { jsxs as jsxs61 } from "react/jsx-runtime";
var checkboxGroupClassName = "ui-checkboxGroup";
var CheckboxGroup = (props) => {
  const context = useFluentContext89();
  const { setStart, setEnd } = useTelemetry89(
    CheckboxGroup.displayName,
    context.telemetry
  );
  setStart();
  const {
    className,
    styles,
    variables,
    accessibility,
    design,
    items,
    defaultCheckedItems,
    checkedItems: controlledCheckedItems,
    selectAllOption,
    horizontal = false,
    disabled,
    ref,
    numberOfColumns = 1
  } = props;
  const getA11yProps = useAccessibility83(
    accessibility,
    {
      debugName: CheckboxGroup.displayName,
      rtl: context.rtl,
      mapPropsToBehavior: () => ({
        selectable: true,
        horizontal
      })
    }
  );
  const { classes } = useStyles89(
    CheckboxGroup.displayName,
    {
      className: checkboxGroupClassName,
      mapPropsToStyles: () => ({
        horizontal,
        numberOfColumns
      }),
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    }
  );
  const unhandledProps = useUnhandledProps88(CheckboxGroup.handledProps, props);
  const [checkedItems, setCheckedItems] = useAutoControlled8({
    defaultValue: defaultCheckedItems,
    value: controlledCheckedItems,
    initialValue: controlledCheckedItems ?? defaultCheckedItems
  });
  const ElementType = getElementType71(props);
  const allAvailableOptions = [];
  items.forEach((item) => {
    if (!item.disabled) allAvailableOptions.push(item);
  });
  const selectedAll = checkedItems && items && _7.isEqual(
    _7.orderBy(checkedItems, ["label"], ["asc"]),
    _7.orderBy(allAvailableOptions, ["label"], ["asc"])
  );
  const updateCheckedItems = (event, checkedItems2) => {
    setCheckedItems(checkedItems2);
    _7.invoke(props, "onCheckedItemsChange", event, {
      ...props,
      checkedItems: checkedItems2
    });
  };
  const renderCheckbox = (option, checked, onChange) => {
    if (props.renderCheckbox)
      return props.renderCheckbox(option, checked, onChange);
    return Checkbox.create({
      key: option.label,
      indicator: { kind: "checkbox" },
      label: option.label,
      disabled: disabled ?? option.disabled,
      checked,
      onChange
    });
  };
  const renderSelectAll = () => {
    if (!selectAllOption) return null;
    return Checkbox.create(selectAllOption, {
      defaultProps: () => ({
        indicator: { kind: "checkbox" },
        label: "All"
      }),
      overrideProps: () => ({
        key: "all",
        disabled,
        checked: !!selectedAll,
        onChange: (event, data) => {
          updateCheckedItems(
            event,
            data?.checked ? allAvailableOptions : []
          );
        }
      })
    });
  };
  const renderItems = () => {
    return items.map(
      (item) => renderCheckbox(
        item,
        !!checkedItems?.find((v) => v.label === item.label),
        (event, data) => {
          if (data?.checked) {
            if (checkedItems?.find((v) => v.label === item.label))
              return;
            updateCheckedItems(
              event,
              checkedItems ? [...checkedItems, item] : [item]
            );
          } else {
            if (!checkedItems) return;
            const index = checkedItems.findIndex(
              (v) => v.label === item.label
            );
            if (index === -1) return;
            const newCheckedValues = [...checkedItems];
            newCheckedValues.splice(index, 1);
            updateCheckedItems(event, newCheckedValues);
          }
        }
      )
    );
  };
  const result = /* @__PURE__ */ jsxs61(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      children: [
        renderSelectAll(),
        renderItems()
      ]
    }
  );
  setEnd();
  return result;
};
CheckboxGroup.displayName = "CheckboxGroup";
CheckboxGroup.create = createShorthandFactory83({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: CheckboxGroup
});
CheckboxGroup.defaultProps = {
  accessibility: selectableListBehavior
};
CheckboxGroup.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  // Extra props that are handled
  "ref",
  "items",
  "defaultCheckedItems",
  "checkedItems",
  "onCheckedItemsChange",
  "selectAllOption",
  "horizontal",
  "disabled",
  "renderCheckbox",
  "numberOfColumns"
];

// packages/aria/src/components/RangeSelector/RangeSelector.tsx
import { forwardRef as forwardRef87 } from "react";
import {
  useFluentContext as useFluentContext92,
  getElementType as getElementType74,
  useAccessibility as useAccessibility86,
  useTelemetry as useTelemetry92,
  useStyles as useStyles92,
  useUnhandledProps as useUnhandledProps91,
  formFieldBehavior,
  createShorthandFactory as createShorthandFactory86
} from "@fluentui/react-northstar";
import { useAutoControlled as useAutoControlled10 } from "@fluentui/react-bindings";
import _9 from "lodash";
import { useSetState, useUpdateEffect as useUpdateEffect2 } from "ahooks";

// packages/aria/src/components/MultiSlider/MultiSlider.tsx
import { sliderBehavior } from "@fluentui/accessibility";
import { useAutoControlled as useAutoControlled9 } from "@fluentui/react-bindings";
import {
  getElementType as getElementType72,
  useUnhandledProps as useUnhandledProps89,
  useAccessibility as useAccessibility84,
  useFluentContext as useFluentContext90,
  useStyles as useStyles90,
  useTelemetry as useTelemetry90
} from "@fluentui/react-bindings";
import { handleRef, Ref as Ref2 } from "@fluentui/react-component-ref";
import { Box as Box3 } from "@fluentui/react-northstar";
import {
  setWhatInputSource,
  createShorthandFactory as createShorthandFactory84
} from "@fluentui/react-northstar";
import cx17 from "classnames";
import _8 from "lodash";
import * as React17 from "react";
import { useRef as useRef11 } from "react";
import { jsx as jsx90, jsxs as jsxs62 } from "react/jsx-runtime";
var validateRange = (currentRange, inputValue) => {
  const { min: minRange, max: maxRange } = currentRange;
  if (minRange > maxRange) throw new Error("Invalid range");
  if (!inputValue) return;
  const { min, max } = inputValue;
  if (min > max || min < minRange || max > maxRange)
    throw new Error(
      "Invalid input range, currentRange:" + minRange + "-" + maxRange + " inputValue:" + min + "-" + max
    );
};
var rangeToPercentage = (range, selectedRange) => {
  const { min: selectedMin, max: selectedMax } = selectedRange ?? range;
  const { min, max } = range;
  const isSameValue = min === max;
  const selectedMinPercentage = isSameValue ? "0%" : `${100 * (selectedMin - min) / (max - min)}%`;
  const selectedMaxPercentage = isSameValue ? "100%" : `${100 * (selectedMax - min) / (max - min)}%`;
  return {
    selectedMinPercentage,
    selectedMaxPercentage,
    inputValue: selectedMin
  };
};
var multiSliderClassName = "ui-multiSlider";
var multiSliderSlotClassNames = {
  input: `${multiSliderClassName}__input`,
  inputWrapper: `${multiSliderClassName}__input-wrapper`,
  rail: `${multiSliderClassName}__rail`,
  thumb: `${multiSliderClassName}__thumb`,
  track: `${multiSliderClassName}__track`
};
var MultiSlider = React17.forwardRef(
  (props, ref) => {
    const context = useFluentContext90();
    const { setStart, setEnd } = useTelemetry90(
      MultiSlider.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      design,
      accessibility = sliderBehavior,
      range = { min: 0, max: 100 },
      disabled,
      input,
      inputRef,
      step = 1,
      defaultValue,
      value
    } = props;
    const containerRef = useRef11(null);
    validateRange(range, value);
    const [selectedRange, setSelectedRange] = useAutoControlled9({
      defaultValue,
      value,
      initialValue: range
    });
    const dragState = React17.useRef();
    const { selectedMinPercentage, selectedMaxPercentage, inputValue } = rangeToPercentage(range, selectedRange);
    const getA11Props = useAccessibility84(accessibility, {
      debugName: MultiSlider.displayName,
      rtl: context.rtl,
      mapPropsToBehavior: () => ({
        disabled,
        getA11yValueMessageOnChange: () => {
          const currentRange = selectedRange ?? range;
          return `${currentRange.min} - ${currentRange.max}`;
        },
        max: range.max,
        min: range.min,
        value: inputValue,
        vertical: false
      })
    });
    const { classes, styles: resolvedStyles } = useStyles90(MultiSlider.displayName, {
      className: multiSliderClassName,
      mapPropsToStyles: () => ({
        disabled
      }),
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl
    });
    const resetDragState = () => {
      const currentRange = selectedRange ?? range;
      dragState.current = { ...currentRange, dragThumbType: void 0 };
      if (context.target) setWhatInputSource(context.target, "mouse");
    };
    const handleInputOverrides = () => ({
      onChange: (e) => {
        const value2 = parseFloat(_8.get(e, "target.value"));
        if (!dragState.current?.dragThumbType) {
          const { min, max } = selectedRange ?? range;
          const dragThumbType2 = Math.abs(min - value2) <= Math.abs(max - value2) ? "min" : "max";
          dragState.current = { dragThumbType: dragThumbType2, min, max };
        }
        const {
          dragThumbType,
          min: originalMin,
          max: originalMax
        } = dragState.current;
        let newRange;
        if (dragThumbType === "min") {
          if (value2 > originalMax) {
            newRange = { min: originalMax, max: value2 };
          } else {
            newRange = { min: value2, max: originalMax };
          }
        } else {
          if (value2 < originalMin) {
            newRange = { min: value2, max: originalMin };
          } else {
            newRange = { min: originalMin, max: value2 };
          }
        }
        setSelectedRange(newRange);
        _8.invoke(props, "onChange", e, { ...props, value: newRange });
      },
      onMouseDown: (e) => {
        resetDragState();
        _8.invoke(props, "onMouseDown", e, props);
      },
      onMouseUp: (e) => {
        _8.invoke(props, "onMouseUp", e, props);
      },
      onTouchStart: (e) => {
        const container = containerRef.current;
        if (container) {
          const touch = e.touches[0];
          const containerRect = container.getBoundingClientRect();
          const offsetX = touch.clientX - containerRect.left;
          const availableWidth = container.offsetWidth;
          const targetValue = offsetX / availableWidth * range.max;
          const { min, max } = selectedRange ?? range;
          let activeValue = min;
          if (Math.abs(targetValue - min) <= Math.abs(targetValue - max)) {
            activeValue = min;
          } else {
            activeValue = max;
          }
          const input2 = e.target;
          input2.value = activeValue.toString();
          input2.blur();
          input2.focus();
        }
        resetDragState();
        _8.invoke(props, "onTouchStart", e, props);
      },
      onTouchEnd: (e) => {
        _8.invoke(props, "onTouchEnd", e, props);
      }
    });
    const ElementType = getElementType72(props);
    const unhandledProps = useUnhandledProps89(
      MultiSlider.handledProps,
      props
    );
    const type = "range";
    const inputElement = Box3.create(input || type, {
      defaultProps: () => getA11Props("input", {
        ...unhandledProps,
        as: "input",
        "data-testid": "MultiSliderInput",
        className: multiSliderSlotClassNames.input,
        step,
        styles: resolvedStyles["input"],
        min: range.min,
        max: range.max,
        type,
        disabled
      }),
      overrideProps: handleInputOverrides
    });
    const element = /* @__PURE__ */ jsxs62(
      ElementType,
      {
        ...getA11Props("root", {
          className: classes["root"],
          ref
        }),
        children: [
          /* @__PURE__ */ jsx90(
            "span",
            {
              ...getA11Props("rail", {
                className: cx17(
                  multiSliderSlotClassNames.rail,
                  classes["rail"]
                )
              })
            }
          ),
          /* @__PURE__ */ jsx90(
            "span",
            {
              ...getA11Props("track", {
                className: cx17(
                  multiSliderSlotClassNames.track,
                  classes["track"]
                )
              }),
              style: {
                left: selectedMinPercentage,
                width: `calc(${selectedMaxPercentage} - ${selectedMinPercentage})`
              }
            }
          ),
          inputElement && /* @__PURE__ */ jsx90(
            Ref2,
            {
              innerRef: (inputElement2) => handleRef(inputRef, inputElement2),
              children: inputElement
            }
          ),
          /* @__PURE__ */ jsxs62(
            "div",
            {
              ...getA11Props("thumbWrapper", {
                className: cx17(
                  multiSliderSlotClassNames.inputWrapper,
                  classes["thumbWrapper"]
                )
              }),
              ref: containerRef,
              children: [
                /* @__PURE__ */ jsx90(
                  "span",
                  {
                    ...getA11Props("thumbMin", {
                      className: cx17(
                        multiSliderSlotClassNames.thumb,
                        classes["thumb"]
                      )
                    }),
                    style: { left: selectedMinPercentage }
                  }
                ),
                /* @__PURE__ */ jsx90(
                  "span",
                  {
                    ...getA11Props("thumbMax", {
                      className: cx17(
                        multiSliderSlotClassNames.thumb,
                        classes["thumb"]
                      )
                    }),
                    style: { left: selectedMaxPercentage }
                  }
                )
              ]
            }
          )
        ]
      }
    );
    setEnd();
    return element;
  }
);
MultiSlider.displayName = "MultiSlider";
MultiSlider.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  // Extra props that are handled
  "range",
  "defaultValue",
  "value",
  "disabled",
  "input",
  "inputRef",
  "onChange",
  "step"
];
MultiSlider.create = createShorthandFactory84({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: MultiSlider
});

// packages/aria/src/components/RangeInput/RangeInput.tsx
import { forwardRef as forwardRef86 } from "react";
import {
  useFluentContext as useFluentContext91,
  getElementType as getElementType73,
  useAccessibility as useAccessibility85,
  useTelemetry as useTelemetry91,
  useStyles as useStyles91,
  useUnhandledProps as useUnhandledProps90,
  inputBehavior as inputBehavior2,
  createShorthandFactory as createShorthandFactory85
} from "@fluentui/react-northstar";
import cx18 from "classnames";
import { jsxs as jsxs63 } from "react/jsx-runtime";
var rangeInputClassName = "ui-rangeInput";
var rangeInputSlotClassNames = {
  unit: `${rangeInputClassName}__unit`,
  input: `${rangeInputClassName}__input`
};
var RangeInput = forwardRef86(
  (props, ref) => {
    const context = useFluentContext91();
    const { setStart, setEnd } = useTelemetry91(
      RangeInput.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      disabled,
      error,
      unit,
      unitPosition = "end"
    } = props;
    const getA11yProps = useAccessibility85(
      accessibility,
      {
        debugName: RangeInput.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles91(
      RangeInput.displayName,
      {
        className: rangeInputClassName,
        mapPropsToStyles: () => ({
          disabled,
          error
        }),
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps90(
      RangeInput.handledProps,
      props
    );
    const ElementType = getElementType73(props);
    const unitElement = Caption.create(unit, {
      defaultProps: () => ({ variant: "02" }),
      overrideProps: () => ({
        className: rangeInputSlotClassNames.unit
      })
    });
    const isUnitAtStart = unitPosition === "start";
    const result = /* @__PURE__ */ jsxs63(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref
        }),
        children: [
          isUnitAtStart && unitElement,
          NumberInput.create({
            ...unhandledProps,
            className: cx18(
              rangeInputSlotClassNames.input,
              classes["input"]
            )
          }),
          !isUnitAtStart && unitElement
        ]
      }
    );
    setEnd();
    return result;
  }
);
RangeInput.displayName = "RangeInput";
RangeInput.create = createShorthandFactory85({
  Component: RangeInput
});
RangeInput.defaultProps = {
  accessibility: inputBehavior2
};
RangeInput.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  // Extra props that are handled
  "error",
  "unit",
  "unitPosition"
];

// packages/aria/src/components/RangeSelector/RangeSelector.tsx
import { jsx as jsx91, jsxs as jsxs64 } from "react/jsx-runtime";
var rangeSelectorClassName = "ui-rangeSelector";
var rangeSelectorSlotClassNames = {
  title: `${rangeSelectorClassName}__title`,
  slider: `${rangeSelectorClassName}__slider`,
  inputWrapper: `${rangeSelectorClassName}__input-wrapper`,
  minInput: `${rangeSelectorClassName}__minInput`,
  maxInput: `${rangeSelectorClassName}__maxInput`
};
var RangeSelector = forwardRef87(
  (props, ref) => {
    const context = useFluentContext92();
    const { setStart, setEnd } = useTelemetry92(
      RangeSelector.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      title,
      range,
      unit,
      unitPosition,
      rangeInputFormat = { thousandSeparator: "," },
      disabled,
      multiSlider
    } = props;
    const [selectedRange, setSelectedRange] = useAutoControlled10({
      defaultValue: props.defaultValue,
      value: props.value,
      initialValue: props.range
    });
    const { min: valueMin, max: valueMax } = props.value ?? props.range;
    const { min: rangeMin, max: rangeMax } = props.range;
    const [inputState, setInputState] = useSetState({
      minInput: {
        value: valueMin,
        error: false
      },
      maxInput: {
        value: valueMax,
        error: false
      }
    });
    useUpdateEffect2(() => {
      setSelectedRange({ min: valueMin, max: valueMax });
      const isMinInRange = valueMin >= rangeMin && valueMin <= rangeMax;
      const isMaxInRange = valueMax >= rangeMin && valueMax <= rangeMax;
      setInputState({
        maxInput: { value: valueMax, error: !isMaxInRange },
        minInput: { value: valueMin, error: !isMinInRange }
      });
    }, [
      valueMin,
      valueMax,
      rangeMin,
      rangeMax,
      setSelectedRange,
      setInputState
    ]);
    const getA11yProps = useAccessibility86(
      accessibility,
      {
        debugName: RangeSelector.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles92(
      RangeSelector.displayName,
      {
        className: rangeSelectorClassName,
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps91(
      RangeSelector.handledProps,
      props
    );
    const ElementType = getElementType74(props);
    const handleRangeChange = (e, newRange) => {
      setSelectedRange(newRange);
      _9.invoke(props, "onChange", e, { ...props, value: newRange });
    };
    const handleMultiSliderChange = (e, value) => {
      setInputState({
        minInput: { value: value.min, error: false },
        maxInput: { value: value.max, error: false }
      });
      handleRangeChange(e, value);
    };
    const handleMinInputChange = (value) => {
      const isInRange = value >= props.range.min && value < currentRange.max;
      setInputState({
        ...inputState,
        minInput: { value, error: !isInRange }
      });
    };
    const handleMinBlur = (e) => {
      const value = inputState.minInput.value;
      const isInRange = value >= props.range.min && value < currentRange.max;
      if (!isInRange || value === currentRange.min) return;
      handleRangeChange(e, { min: value, max: currentRange.max });
    };
    const handleMaxInputChange = (value) => {
      const isInRange = value <= props.range.max && value > currentRange.min;
      setInputState({
        ...inputState,
        maxInput: { value, error: !isInRange }
      });
    };
    const handleMaxBlur = (e) => {
      const value = inputState.maxInput.value;
      const isInRange = value <= props.range.max && value > currentRange.min;
      if (!isInRange || value === currentRange.max) return;
      handleRangeChange(e, { min: currentRange.min, max: value });
    };
    const currentRange = selectedRange ?? range;
    const result = /* @__PURE__ */ jsxs64(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: [
          Caption.create(title, {
            overrideProps: () => ({
              className: rangeSelectorSlotClassNames.title
            })
          }),
          MultiSlider.create(multiSlider ?? {}, {
            overrideProps: () => ({
              disabled,
              value: selectedRange,
              range,
              onChange: (e, data) => {
                if (data) handleMultiSliderChange(e, data.value);
              }
            })
          }),
          /* @__PURE__ */ jsxs64(
            Flex,
            {
              ...getA11yProps("inputWrapper", {
                className: rangeSelectorSlotClassNames.inputWrapper
              }),
              children: [
                /* @__PURE__ */ jsx91(
                  RangeInput,
                  {
                    ...getA11yProps("minInput", {
                      className: rangeSelectorSlotClassNames.minInput
                    }),
                    "aria-label": "minInput",
                    unit,
                    unitPosition,
                    value: inputState.minInput.value,
                    error: inputState.minInput.error,
                    disabled,
                    format: rangeInputFormat,
                    onChange: (_14, value) => {
                      if (value !== void 0 && value !== null)
                        handleMinInputChange(value);
                    },
                    onBlur: handleMinBlur
                  }
                ),
                Caption.create("\u2013"),
                /* @__PURE__ */ jsx91(
                  RangeInput,
                  {
                    ...getA11yProps("maxInput", {
                      className: rangeSelectorSlotClassNames.maxInput
                    }),
                    "aria-label": "maxInput",
                    unit,
                    unitPosition,
                    value: inputState.maxInput.value,
                    error: inputState.maxInput.error,
                    disabled,
                    format: rangeInputFormat,
                    onChange: (_14, value) => {
                      if (value !== void 0 && value !== null)
                        handleMaxInputChange(value);
                    },
                    onBlur: handleMaxBlur
                  }
                )
              ]
            }
          )
        ]
      }
    );
    setEnd();
    return result;
  }
);
RangeSelector.displayName = "RangeSelector";
RangeSelector.create = createShorthandFactory86({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: RangeSelector,
  mappedProp: "value"
});
RangeSelector.defaultProps = {
  accessibility: formFieldBehavior
};
RangeSelector.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  // Extra props that are handled
  "title",
  "range",
  "defaultValue",
  "value",
  "unit",
  "unitPosition",
  "rangeInputFormat",
  "disabled",
  "onChange",
  "multiSlider"
];

// packages/aria/src/components/Tile/MagazineTile/MagazineTile.tsx
import { forwardRef as forwardRef88 } from "react";
import {
  useFluentContext as useFluentContext93,
  getElementType as getElementType75,
  useAccessibility as useAccessibility87,
  useTelemetry as useTelemetry93,
  useStyles as useStyles93,
  useUnhandledProps as useUnhandledProps92,
  createShorthandFactory as createShorthandFactory87,
  cardBehavior as cardBehavior14
} from "@fluentui/react-northstar";
import { jsx as jsx92, jsxs as jsxs65 } from "react/jsx-runtime";
var defaultRootElementType34 = "div";
var magazineTileClassName = "ui-magazineTile";
var MagazineTile = forwardRef88(
  (props, ref) => {
    const context = useFluentContext93();
    const { setStart, setEnd } = useTelemetry93(
      MagazineTile.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      image,
      title,
      subtitle
    } = props;
    const getA11yProps = useAccessibility87(
      accessibility,
      {
        debugName: MagazineTile.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles93(
      MagazineTile.displayName,
      {
        className: magazineTileClassName,
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps92(
      MagazineTile.handledProps,
      props
    );
    const ElementType = getElementType75(props);
    const result = /* @__PURE__ */ jsxs65(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: [
          Subheader.create(subtitle, {
            defaultProps: () => {
              return {
                className: classes["subtitle"],
                variant: "05",
                as: "div"
              };
            }
          }),
          Subheader.create(title, {
            defaultProps: () => {
              return {
                className: classes["title"],
                variant: "03",
                as: "div"
              };
            }
          }),
          /* @__PURE__ */ jsx92(Box, { className: classes["imageWrap"], children: Image.create(image, {
            defaultProps: () => {
              return {
                layout: "fill",
                objectFit: "cover"
              };
            },
            overrideProps: (props2) => {
              return typeof props2.width !== "string" && {
                sizes: props2.sizes ?? `(min-width:1024px) ${(props2.width / 1920 * 100).toFixed(0)}vw, 100vw`
              };
            }
          }) })
        ]
      }
    );
    setEnd();
    return result;
  }
);
MagazineTile.displayName = "MagazineTile";
MagazineTile.create = createShorthandFactory87({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: MagazineTile
});
MagazineTile.defaultProps = {
  as: defaultRootElementType34,
  accessibility: cardBehavior14
};
MagazineTile.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "as",
  // Extra props that are handled
  "disabled",
  "title",
  "subtitle",
  "image"
];

// packages/aria/src/components/Tile/BannerAdTile/BannerAdTile.tsx
import { forwardRef as forwardRef89 } from "react";
import {
  useFluentContext as useFluentContext94,
  getElementType as getElementType76,
  useAccessibility as useAccessibility88,
  useTelemetry as useTelemetry94,
  useStyles as useStyles94,
  useUnhandledProps as useUnhandledProps93,
  cardBehavior as cardBehavior15,
  createShorthandFactory as createShorthandFactory88
} from "@fluentui/react-northstar";
import cx19 from "classnames";
import { jsx as jsx93, jsxs as jsxs66 } from "react/jsx-runtime";
var defaultRootElementType35 = "div";
var bannerAdTileClassName = "ui-bannerAdTile";
var bannerAdTileSlotClassNames = {
  contentWrap: `${bannerAdTileClassName}__contentWrap`,
  company: `${bannerAdTileClassName}__company`,
  title: `${bannerAdTileClassName}__title`,
  description: `${bannerAdTileClassName}__description`,
  actionButton: `${bannerAdTileClassName}__actionButton`
};
var SIZE_TO_ASPECT_MAPPING = {
  "01": {
    medium: {
      ratio: "1820 / 820"
    }
  },
  "02": {
    medium: {
      ratio: "1358 / 900"
    }
  },
  "03": {
    medium: {
      ratio: "378 / 177"
    }
  }
};
var VARIANT_TO_MAXIMUMLINES_MAPPING = {
  "01": {
    title: 1,
    description: 2
  },
  "02": {
    title: 1,
    description: 2
  },
  "03": {
    title: 2,
    description: 3
  }
};
var BannerAdTile = forwardRef89(
  (props, ref) => {
    const context = useFluentContext94();
    const { setStart, setEnd } = useTelemetry94(
      BannerAdTile.displayName,
      context.telemetry
    );
    setStart();
    const {
      company,
      className,
      styles,
      variables,
      accessibility,
      design,
      images,
      size = "medium",
      title,
      description,
      actionButton,
      variant = "01",
      tileImage = {
        images,
        height: "100%"
      },
      tileVideo,
      onTitleClick,
      onSubtitleClick,
      onCTAClick,
      onTileClick
    } = props;
    const getA11yProps = useAccessibility88(
      accessibility,
      {
        debugName: BannerAdTile.displayName,
        rtl: context.rtl
      }
    );
    const imageLayout = SIZE_TO_ASPECT_MAPPING[variant][size];
    const { classes } = useStyles94(
      BannerAdTile.displayName,
      {
        className: bannerAdTileClassName,
        mapPropsToStyles: () => ({
          size,
          variant,
          aspectRatio: imageLayout.ratio
        }),
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps93(
      BannerAdTile.handledProps,
      props
    );
    const ElementType = getElementType76(props);
    const isMobile = variant === "03";
    const actionButtonDefaultProps = {
      className: bannerAdTileSlotClassNames.actionButton,
      outlined: true,
      size: 16,
      variables: {
        buttonPadding: isMobile ? 14 : pxArrayToRem([16.5, 17.5]),
        iconPadding: 0,
        buttonSize16IconXMargin: pxToRem(8)
      },
      onClick: onCTAClick
    };
    const result = /* @__PURE__ */ jsxs66(
      ElementType,
      {
        ...getA11yProps("root", {
          className: cx19([
            classes["root"],
            classes[`variant${variant}`]
          ]),
          ref,
          ...unhandledProps
        }),
        children: [
          /* @__PURE__ */ jsx93(Flex, { className: classes["mediaWrap"], onClick: onTileClick, children: tileVideo ? TileVideo.create(tileVideo, {}) : TileImage.create(tileImage, {
            defaultProps: () => {
              return {
                className: classes["image"],
                width: "100%",
                hideFavourite: true,
                allowClickThrough: true
              };
            }
          }) }),
          /* @__PURE__ */ jsxs66(
            Flex,
            {
              className: bannerAdTileSlotClassNames.contentWrap,
              column: false,
              children: [
                /* @__PURE__ */ jsx93(Flex, { column: true, children: /* @__PURE__ */ jsxs66(Flex, { className: classes["content"], column: true, children: [
                  Caption.create(company, {
                    defaultProps: () => {
                      return {
                        className: bannerAdTileSlotClassNames.company,
                        variant: "03",
                        maximumLines: 1
                      };
                    }
                  }),
                  Paragraph.create(title, {
                    defaultProps: () => {
                      return {
                        className: bannerAdTileSlotClassNames.title,
                        variant: "03",
                        maximumLines: VARIANT_TO_MAXIMUMLINES_MAPPING[variant].title,
                        onClick: onTitleClick
                      };
                    }
                  }),
                  Label.create(description, {
                    defaultProps: () => {
                      return {
                        className: bannerAdTileSlotClassNames.description,
                        variant: "02",
                        maximumLines: VARIANT_TO_MAXIMUMLINES_MAPPING[variant].description,
                        onClick: onSubtitleClick
                      };
                    }
                  })
                ] }) }),
                !isMobile && /* @__PURE__ */ jsx93(Flex, { vAlign: "center", children: Button.create(actionButton, {
                  defaultProps: () => {
                    return actionButtonDefaultProps;
                  }
                }) })
              ]
            }
          ),
          isMobile && /* @__PURE__ */ jsx93(Flex, { vAlign: "center", children: Button.create(actionButton, {
            defaultProps: () => {
              return actionButtonDefaultProps;
            }
          }) })
        ]
      }
    );
    setEnd();
    return result;
  }
);
BannerAdTile.displayName = "BannerAdTile";
BannerAdTile.create = createShorthandFactory88({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: BannerAdTile
});
BannerAdTile.defaultProps = {
  as: defaultRootElementType35,
  accessibility: cardBehavior15
};
BannerAdTile.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  "onTitleClick",
  "onSubtitleClick",
  "onCTAClick",
  "onTileClick",
  // Extra props that are handled
  "tileImage",
  "size",
  "description",
  "company",
  "title",
  "actionButton",
  "variant",
  "tileVideo"
];

// packages/aria/src/components/Drawer/SwipeableEdge.tsx
import { forwardRef as forwardRef90, useState as useState11 } from "react";
import {
  useFluentContext as useFluentContext95,
  getElementType as getElementType77,
  useAccessibility as useAccessibility89,
  useTelemetry as useTelemetry95,
  useStyles as useStyles95,
  useUnhandledProps as useUnhandledProps94,
  createShorthandFactory as createShorthandFactory89
} from "@fluentui/react-northstar";
import { jsx as jsx94 } from "react/jsx-runtime";
var defaultRootElementType36 = "div";
var swipeableEdgeClassName = "ui-swipeableEdge";
var swipeableEdgeSlotClassNames = {
  puller: `${swipeableEdgeClassName}__puller`
};
var SwipeableEdge = forwardRef90(
  (props, ref) => {
    const context = useFluentContext95();
    const { setStart, setEnd } = useTelemetry95(
      SwipeableEdge.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      onEdgeSwipe,
      minSwipeDistance = 10,
      anchor = "bottom",
      puller = /* @__PURE__ */ jsx94("div", {})
    } = props;
    const [touchStart, setTouchStart] = useState11(null);
    const [touchEnd, setTouchEnd] = useState11(null);
    const isHorizontalSwipe = anchor === "left" || anchor === "right";
    const getA11yProps = useAccessibility89(
      accessibility,
      {
        debugName: SwipeableEdge.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles95(
      SwipeableEdge.displayName,
      {
        className: swipeableEdgeClassName,
        mapPropsToStyles: () => ({
          anchor
        }),
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps94(
      SwipeableEdge.handledProps,
      props
    );
    const ElementType = getElementType77(props);
    const result = /* @__PURE__ */ jsx94(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: Box.create(puller, {
          defaultProps: () => ({}),
          overrideProps: () => ({
            className: mergeClassName(
              puller,
              swipeableEdgeSlotClassNames.puller
            ),
            onTouchStart: (e) => {
              setTouchEnd(null);
              setTouchStart(
                isHorizontalSwipe ? e.targetTouches[0].clientX : e.targetTouches[0].clientY
              );
            },
            onTouchMove: (e) => {
              setTouchEnd(
                isHorizontalSwipe ? e.targetTouches[0].clientX : e.targetTouches[0].clientY
              );
            },
            onTouchEnd: (e) => {
              e.stopPropagation();
              if (!touchStart || !touchEnd) return;
              const distance = touchStart - touchEnd;
              const over = distance > minSwipeDistance;
              const under = distance < -minSwipeDistance;
              const swipeTrigged = over || under;
              if (!swipeTrigged) return;
              const dir = isHorizontalSwipe ? over ? "left" : "right" : over ? "up" : "down";
              onEdgeSwipe?.(e, dir);
            }
          })
        })
      }
    );
    setEnd();
    return result;
  }
);
SwipeableEdge.displayName = "SwipeableEdge";
SwipeableEdge.defaultProps = {
  as: defaultRootElementType36
};
SwipeableEdge.create = createShorthandFactory89({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: SwipeableEdge
});
SwipeableEdge.handledProps = [
  ...commonHandledProps(),
  "disabled",
  "onEdgeSwipe",
  "minSwipeDistance",
  "anchor"
];

// packages/aria/src/components/CategorySelector/CategorySelector.tsx
import { useMemo as useMemo4 } from "react";
import { forwardRef as forwardRef91 } from "react";
import {
  useFluentContext as useFluentContext96,
  useAccessibility as useAccessibility90,
  useTelemetry as useTelemetry96,
  useStyles as useStyles96,
  useUnhandledProps as useUnhandledProps95,
  breadcrumbBehavior as breadcrumbBehavior2,
  createShorthandFactory as createShorthandFactory90
} from "@fluentui/react-northstar";
import { useAutoControlled as useAutoControlled11 } from "@fluentui/react-bindings";
import { ArrowLeftLine, AngleRight as AngleRight2 } from "@archipro-design/icons";
import { useUpdateEffect as useUpdateEffect3 } from "ahooks";
import cx20 from "classnames";
import { jsx as jsx95, jsxs as jsxs67 } from "react/jsx-runtime";
var categorySelectorClassName = "ui-categorySelector";
var categorySelectorSlotClassNames = {
  header: `${categorySelectorClassName}__header`,
  selectedCategoriesContainer: `${categorySelectorClassName}__selectedCategoriesContainer`,
  selectedCategory: `${categorySelectorClassName}__selectedCategory`,
  selectedHeaderCategory: `${categorySelectorClassName}__selectedHeaderCategory`,
  categoriesContainer: `${categorySelectorClassName}__categoriesContainer`,
  category: `${categorySelectorClassName}__category`,
  headerCategory: `${categorySelectorClassName}__headerCategory`,
  backButton: `${categorySelectorClassName}__backButton`
};
var getSelectedChain = (category, selectedCategory) => {
  if (!selectedCategory || category.id === selectedCategory.id)
    return [category];
  if (!category.children || !category.children.length) return null;
  let chain = null;
  category.children.forEach((c) => {
    const currentChain = getSelectedChain(c, selectedCategory);
    if (currentChain) {
      chain = [category, ...currentChain];
    }
  });
  return chain;
};
var CategorySelector = forwardRef91((props, ref) => {
  const context = useFluentContext96();
  const { setStart, setEnd } = useTelemetry96(
    CategorySelector.displayName,
    context.telemetry
  );
  setStart();
  const {
    className,
    styles,
    variables,
    accessibility,
    design,
    category,
    defaultSelectedCategory,
    selectedCategory: controlledSelectedCategory,
    onChange,
    disabled,
    defaultCategoryHeaderProps = {},
    defaultCategoryItemProps = {},
    viewAllItem = { children: "View all" },
    showSelectedChain,
    categoryHeaderIcon,
    backButton
  } = props;
  const [selectedCategory, setSelectedCategory] = useAutoControlled11({
    defaultValue: defaultSelectedCategory ?? category,
    value: controlledSelectedCategory,
    initialValue: controlledSelectedCategory ?? defaultSelectedCategory
  });
  useUpdateEffect3(() => {
    if (controlledSelectedCategory !== void 0) return;
    const newSelectedChain = getSelectedChain(category, selectedCategory);
    const newSelectedCategory = newSelectedChain && newSelectedChain.length > 0 ? newSelectedChain[newSelectedChain.length - 1] : category;
    setSelectedCategory(newSelectedCategory);
  }, [category]);
  const getA11yProps = useAccessibility90(
    accessibility,
    {
      debugName: CategorySelector.displayName,
      rtl: context.rtl
    }
  );
  const { classes } = useStyles96(
    CategorySelector.displayName,
    {
      className: categorySelectorClassName,
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design,
        disabled
      }),
      rtl: context.rtl,
      unstable_props: props
    }
  );
  const unhandledProps = useUnhandledProps95(
    CategorySelector.handledProps,
    props
  );
  const selectedChain = useMemo4(
    () => getSelectedChain(category, selectedCategory) ?? [category],
    [category, selectedCategory]
  );
  let listCategoryIndex = selectedChain.length - 1;
  let listCategory = selectedChain[listCategoryIndex];
  let parentCategoryIndex = listCategoryIndex - 1;
  if (!listCategory.children?.length) {
    listCategoryIndex = selectedChain.length - 2;
    parentCategoryIndex = listCategoryIndex;
    listCategory = selectedChain[listCategoryIndex] ?? category;
  }
  if (!listCategory) {
    listCategoryIndex = 0;
    parentCategoryIndex = listCategoryIndex;
    listCategory = category;
  }
  parentCategoryIndex = parentCategoryIndex >= 0 ? parentCategoryIndex : 0;
  const parentCategory = selectedChain?.[parentCategoryIndex] ?? category;
  const handlerClickCategory = (e, category2, source) => {
    setSelectedCategory(category2);
    onChange?.(e, {
      category: category2,
      source
    });
  };
  const renderCategoryHeader = (cat) => {
    const selected = selectedCategory?.id === cat.id;
    const categoryHeader = typeof defaultCategoryHeaderProps === "function" ? defaultCategoryHeaderProps(cat) : defaultCategoryHeaderProps;
    return Button.create(categoryHeader, {
      generateKey: true,
      overrideProps: () => ({
        className: cx20(
          categoryHeader?.["className"],
          categorySelectorSlotClassNames.headerCategory,
          selected && categorySelectorSlotClassNames.selectedHeaderCategory
        ),
        onClick: (e) => handlerClickCategory(
          e,
          showSelectedChain ? cat : parentCategory,
          "header"
        ),
        disabled
      }),
      defaultProps: () => ({
        children: {
          children: cat.name === "Blogpost" ? "Article" : cat.name
        },
        key: cat.id,
        variant: "text",
        size: 16,
        variables: { buttonPadding: 0, iconPadding: 0 },
        iconBefore: selectedChain && selectedChain.length > 1 || showSelectedChain ? categoryHeaderIcon ?? /* @__PURE__ */ jsx95(ArrowLeftLine, {}) : void 0
      })
    });
  };
  const renderCategory = (cat, isViewAll = false) => {
    const categoryItem = typeof defaultCategoryItemProps === "function" ? defaultCategoryItemProps(cat) : defaultCategoryItemProps;
    return Button.create(isViewAll ? viewAllItem : categoryItem, {
      overrideProps: () => ({
        key: cat.id,
        className: cx20(
          categoryItem?.["className"],
          categorySelectorSlotClassNames.category,
          selectedCategory?.id === cat.id && categorySelectorSlotClassNames.selectedCategory
        ),
        selected: selectedCategory?.id === cat.id,
        disabled: disabled || cat.disabled,
        onClick: (e) => handlerClickCategory(e, cat, "item")
      }),
      defaultProps: () => ({
        ...!isViewAll && { children: cat.name },
        variant: "text",
        size: 30,
        variables: { buttonPadding: 0, iconPadding: 0 },
        iconAfter: cat.children && cat.children.length > 1 && !isViewAll ? /* @__PURE__ */ jsx95(AngleRight2, {}) : void 0
      })
    });
  };
  const result = /* @__PURE__ */ jsxs67(
    Flex,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      column: true,
      children: [
        /* @__PURE__ */ jsxs67(
          Flex,
          {
            column: true,
            className: categorySelectorSlotClassNames.header,
            "data-testid": "header",
            children: [
              Button.create(backButton, {
                generateKey: true,
                overrideProps: () => ({
                  className: cx20(
                    backButton?.["className"],
                    categorySelectorSlotClassNames.backButton
                  ),
                  onClick: (e) => handlerClickCategory(e, parentCategory, "back"),
                  disabled
                }),
                defaultProps: () => ({
                  children: "Back",
                  variant: "text",
                  size: 16,
                  variables: { buttonPadding: 0, iconPadding: 0 },
                  iconBefore: /* @__PURE__ */ jsx95(ArrowLeftLine, {})
                })
              }),
              showSelectedChain && selectedChain?.filter((c) => c.children?.length)?.map((c) => renderCategoryHeader(c)),
              !showSelectedChain && listCategory && renderCategoryHeader(listCategory)
            ]
          }
        ),
        /* @__PURE__ */ jsx95(
          Flex,
          {
            column: true,
            className: categorySelectorSlotClassNames.categoriesContainer,
            "data-testid": "categoriesContainer",
            children: listCategory.children?.map((c) => renderCategory(c))
          }
        )
      ]
    }
  );
  setEnd();
  return result;
});
CategorySelector.displayName = "CategorySelector";
CategorySelector.create = createShorthandFactory90({
  Component: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    CategorySelector
  )
});
CategorySelector.defaultProps = {
  accessibility: breadcrumbBehavior2
};
CategorySelector.handledProps = [
  ...commonHandledProps(),
  "category",
  "defaultSelectedCategory",
  "selectedCategory",
  "disabled",
  "onChange",
  "defaultCategoryHeaderProps",
  "defaultCategoryItemProps",
  "viewAllItem",
  "showSelectedChain",
  "categoryHeaderIcon",
  "backButton"
];

// packages/aria/src/components/SearchableList/SearchableList.tsx
import {
  useFluentContext as useFluentContext99,
  getElementType as getElementType79,
  useAccessibility as useAccessibility93,
  useTelemetry as useTelemetry99,
  useStyles as useStyles99,
  useUnhandledProps as useUnhandledProps98,
  inputBehavior as inputBehavior3,
  createShorthandFactory as createShorthandFactory93
} from "@fluentui/react-northstar";
import { useAutoControlled as useAutoControlled12 } from "@fluentui/react-bindings";
import _10 from "lodash";
import { SearchLine as SearchLine3, CloseLine as CloseLine4 } from "@archipro-design/icons";

// packages/aria/src/components/VirtualList/VirtualList.tsx
import { useCallback as useCallback5, useRef as useRef13, useState as useState12 } from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import InfiniteLoader from "react-window-infinite-loader";
import { VariableSizeList } from "react-window";
import {
  useFluentContext as useFluentContext98,
  useAccessibility as useAccessibility92,
  useTelemetry as useTelemetry98,
  useStyles as useStyles98,
  useUnhandledProps as useUnhandledProps97,
  createShorthandFactory as createShorthandFactory92,
  listBehavior as listBehavior4,
  getElementType as getElementType78
} from "@fluentui/react-northstar";

// packages/aria/src/components/VirtualList/VirtualListItem.tsx
import { createElement } from "react";
import { useLayoutEffect, useRef as useRef12 } from "react";
import {
  useFluentContext as useFluentContext97,
  useAccessibility as useAccessibility91,
  useTelemetry as useTelemetry97,
  useStyles as useStyles97,
  useUnhandledProps as useUnhandledProps96,
  listItemBehavior as listItemBehavior2,
  createShorthandFactory as createShorthandFactory91
} from "@fluentui/react-northstar";
import { jsx as jsx96 } from "react/jsx-runtime";
var virtualListItemClassName = "ui-virtualListItem";
var virtualListItemSlotClassNames = {
  contentContainer: `${virtualListItemClassName}__contentContainer`
};
var VirtualListItem = (props) => {
  const context = useFluentContext97();
  const { setStart, setEnd } = useTelemetry97(
    VirtualListItem.displayName,
    context.telemetry
  );
  setStart();
  const {
    children,
    className,
    styles,
    variables,
    accessibility,
    design,
    index,
    style,
    data,
    loading = false,
    setHeight
  } = props;
  if (!children) throw Error("children is required");
  const itemRef = useRef12(null);
  useLayoutEffect(() => {
    if (!itemRef.current) return;
    setHeight(index, itemRef.current.getBoundingClientRect().height);
  }, [index, setHeight]);
  const getA11yProps = useAccessibility91(
    accessibility,
    {
      debugName: VirtualListItem.displayName,
      rtl: context.rtl
    }
  );
  const { classes } = useStyles97(
    VirtualListItem.displayName,
    {
      className: virtualListItemClassName,
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    }
  );
  const unhandledProps = useUnhandledProps96(
    VirtualListItem.handledProps,
    props
  );
  const result = /* @__PURE__ */ jsx96(
    "div",
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ...unhandledProps
      }),
      style,
      className: classes["root"],
      children: /* @__PURE__ */ jsx96(
        "div",
        {
          ref: itemRef,
          className: virtualListItemSlotClassNames.contentContainer,
          children: createElement(children, { index, data, loading })
        }
      )
    }
  );
  setEnd();
  return result;
};
VirtualListItem.displayName = "VirtualListItem";
VirtualListItem.create = createShorthandFactory91({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: VirtualListItem
});
VirtualListItem.defaultProps = {
  accessibility: listItemBehavior2
};
VirtualListItem.handledProps = [
  ...commonHandledProps(),
  "index",
  "style",
  "data",
  "loading",
  "setHeight",
  "children"
];

// packages/aria/src/components/VirtualList/VirtualList.tsx
import { useUpdateEffect as useUpdateEffect4 } from "ahooks";
import { jsx as jsx97 } from "react/jsx-runtime";
var LIST_ITEM_INITIAL_DEFAULT_HEIGHT = 30;
var virtualListClassName = "ui-virtualList";
var virtualListSlotClassNames = {
  listContainer: `${virtualListClassName}__listContainer`
};
var VirtualList = (props) => {
  const context = useFluentContext98();
  const { setStart, setEnd } = useTelemetry98(
    VirtualList.displayName,
    context.telemetry
  );
  setStart();
  const {
    children,
    className,
    styles,
    variables,
    accessibility,
    design,
    items,
    hasMoreItems,
    loadMoreItems
  } = props;
  const loaderRef = useRef13();
  const itemHeights = useRef13({});
  const [listContentHeight, setListContentHeight] = useState12(
    items.length > 0 || hasMoreItems ? LIST_ITEM_INITIAL_DEFAULT_HEIGHT * 2 : 0
  );
  const setItemHeight = useCallback5(
    (index, height) => {
      itemHeights.current = { ...itemHeights.current, [index]: height };
      let newListContentHeight = 0;
      for (const i in itemHeights.current) {
        if (itemHeights.current[i]) {
          newListContentHeight += itemHeights.current[i];
        } else {
          newListContentHeight += LIST_ITEM_INITIAL_DEFAULT_HEIGHT;
        }
      }
      setListContentHeight(newListContentHeight);
      loaderRef.current?._listRef?.resetAfterIndex(index);
    },
    [itemHeights, setListContentHeight]
  );
  useUpdateEffect4(() => {
    const originalHeights = itemHeights.current;
    if (!originalHeights) return;
    const newHeights = {};
    let newListContentHeight = 0;
    items.forEach((_14, index) => {
      newHeights[index] = originalHeights[index];
      if (originalHeights[index]) {
        newListContentHeight += originalHeights[index];
      } else {
        newListContentHeight += LIST_ITEM_INITIAL_DEFAULT_HEIGHT;
      }
    });
    setListContentHeight(newListContentHeight);
    itemHeights.current = newHeights;
  }, [items]);
  const getItemHeight = (index) => {
    return itemHeights.current?.[index] ?? 0;
  };
  const getA11yProps = useAccessibility92(
    accessibility,
    {
      debugName: VirtualList.displayName,
      rtl: context.rtl
    }
  );
  const { classes } = useStyles98(
    VirtualList.displayName,
    {
      className: virtualListClassName,
      mapPropsToStyles: () => ({
        listContentHeight
      }),
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    }
  );
  const unhandledProps = useUnhandledProps97(VirtualList.handledProps, props);
  const ElementType = getElementType78(props);
  const result = /* @__PURE__ */ jsx97(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ...unhandledProps
      }),
      children: /* @__PURE__ */ jsx97(
        AutoSizer,
        {
          disableHeight: Object.keys(itemHeights.current).length === 0,
          children: ({ height, width }) => /* @__PURE__ */ jsx97(
            InfiniteLoader,
            {
              isItemLoaded: (index) => !!items[index],
              itemCount: hasMoreItems ? items.length + 1 : items.length,
              loadMoreItems,
              ref: loaderRef,
              children: ({ onItemsRendered, ref }) => /* @__PURE__ */ jsx97(
                VariableSizeList,
                {
                  className: virtualListSlotClassNames.listContainer,
                  width,
                  height: height <= LIST_ITEM_INITIAL_DEFAULT_HEIGHT * 2 || !height ? listContentHeight : height,
                  itemCount: hasMoreItems ? items.length + 1 : items.length,
                  itemSize: getItemHeight,
                  onItemsRendered,
                  ref,
                  children: ({ index, style }) => /* @__PURE__ */ jsx97(
                    VirtualListItem,
                    {
                      index,
                      data: items[index],
                      loading: index >= items.length,
                      style,
                      setHeight: setItemHeight,
                      children
                    }
                  )
                }
              )
            }
          )
        }
      )
    }
  );
  setEnd();
  return result;
};
VirtualList.displayName = "VirtualList";
VirtualList.create = createShorthandFactory92({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: VirtualList
});
VirtualList.defaultProps = {
  accessibility: listBehavior4
};
VirtualList.handledProps = [
  ...commonHandledProps(),
  "items",
  "hasMoreItems",
  "loadMoreItems",
  "children"
];

// packages/aria/src/components/SearchableList/SearchableList.tsx
import cx21 from "classnames";
import { jsx as jsx98, jsxs as jsxs68 } from "react/jsx-runtime";
var searchableListClassName = "ui-searchableList";
var searchableListSlotClassNames = {
  input: `${searchableListClassName}__input`,
  options: `${searchableListClassName}__options`,
  noResult: `${searchableListClassName}__noResult`,
  subtext: `${searchableListClassName}__subtext`
};
var SearchableList = (props) => {
  const context = useFluentContext99();
  const { setStart, setEnd } = useTelemetry99(
    SearchableList.displayName,
    context.telemetry
  );
  setStart();
  const {
    children,
    className,
    styles,
    variables,
    accessibility,
    design,
    inputValue: controlledInputValue,
    defaultInputValue,
    disabled,
    searchInput,
    onSearchInputChange,
    items,
    hasMoreItems,
    loadMoreItems,
    noResult,
    subtext
  } = props;
  const [inputValue, setInputValue] = useAutoControlled12({
    defaultValue: defaultInputValue,
    value: controlledInputValue
  });
  const getA11yProps = useAccessibility93(
    accessibility,
    {
      debugName: SearchableList.displayName,
      rtl: context.rtl
    }
  );
  const { classes } = useStyles99(
    SearchableList.displayName,
    {
      className: searchableListClassName,
      mapPropsToStyles: () => ({
        disabled
      }),
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    }
  );
  const unhandledProps = useUnhandledProps98(
    SearchableList.handledProps,
    props
  );
  const ElementType = getElementType79(props);
  const result = /* @__PURE__ */ jsxs68(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ...unhandledProps
      }),
      children: [
        SearchBar.create(
          _10.merge(
            {
              iconButtonAfter: {
                icon: inputValue ? /* @__PURE__ */ jsx98(CloseLine4, {}) : /* @__PURE__ */ jsx98(SearchLine3, {})
              },
              searchInput: { placeholder: "Search" }
            },
            searchInput ?? {},
            {
              className: mergeClassName(
                searchInput,
                searchableListSlotClassNames.input
              ),
              iconButtonAfter: {
                disabled,
                onClick: (e) => {
                  if (inputValue) {
                    setInputValue("");
                    onSearchInputChange?.(e, { value: "" });
                  }
                }
              },
              searchInput: {
                value: inputValue,
                disabled,
                onChange: (e) => {
                  const input = e.target;
                  setInputValue(input.value);
                  onSearchInputChange?.(e, {
                    value: input.value
                  });
                }
              }
            }
          )
        ),
        Label.create(subtext, {
          overrideProps: (props2) => {
            return {
              className: cx21([
                props2.className,
                searchableListSlotClassNames.subtext
              ])
            };
          }
        }),
        !!items.length && /* @__PURE__ */ jsx98(
          VirtualList,
          {
            className: searchableListSlotClassNames.options,
            items,
            hasMoreItems,
            loadMoreItems,
            children
          }
        ),
        Box.create(noResult, {
          overrideProps: (props2) => {
            return {
              className: cx21([
                props2.className,
                searchableListSlotClassNames.noResult
              ])
            };
          }
        })
      ]
    }
  );
  setEnd();
  return result;
};
SearchableList.displayName = "SearchableList";
SearchableList.create = createShorthandFactory93({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: SearchableList
});
SearchableList.defaultProps = {
  accessibility: inputBehavior3
};
SearchableList.handledProps = [
  // By default, these are all handled
  "className",
  "styles",
  "variables",
  "design",
  "accessibility",
  // Extra props that are handled
  "inputValue",
  "defaultInputValue",
  "disabled",
  "searchInput",
  "onSearchInputChange",
  "children",
  "items",
  "hasMoreItems",
  "loadMoreItems",
  "noResult",
  "subtext"
];

// packages/aria/src/components/Drawer/Drawer.tsx
import { useEffect as useEffect11, useRef as useRef14 } from "react";
import {
  useFluentContext as useFluentContext100,
  useTelemetry as useTelemetry100,
  useUnhandledProps as useUnhandledProps99,
  createShorthandFactory as createShorthandFactory94,
  useStyles as useStyles100,
  Popup as Popup2,
  Animation as Animation4,
  Provider as Provider3
} from "@fluentui/react-northstar";
import { useKeyPress } from "ahooks";
import { createTheme } from "@fluentui/styles";
import { useAutoControlled as useAutoControlled13 } from "@fluentui/react-bindings";

// packages/aria/src/themes/base/animations/timingFunctions.ts
var linear = "cubic-bezier(0,0,1,1)";

// packages/aria/src/themes/base/animations/durations.ts
var ultraFast = "50ms";
var faster = "100ms";
var fast = "150ms";
var normal = "200ms";
var slow = "300ms";
var slower = "400ms";
var ultraSlow = "500ms";

// packages/aria/src/themes/base/animations/fade.ts
var fadeInOutAnimations = {
  // Fade Ins
  // Basic Fade In Animation -- Ultra Fast
  fadeEnterUltraFast: {
    keyframe: ({ delta }) => ({
      "0%": { opacity: delta },
      "100%": { opacity: 1 }
    }),
    keyframeParams: { delta: 0 },
    duration: ultraFast,
    timingFunction: linear,
    fillMode: "forwards"
  },
  // Basic Fade In Animation -- Faster
  fadeEnterFaster: {
    keyframe: ({ delta }) => ({
      "0%": { opacity: delta },
      "100%": { opacity: 1 }
    }),
    keyframeParams: { delta: 0 },
    duration: faster,
    timingFunction: linear,
    fillMode: "forwards"
  },
  // Basic Fade In Animation -- Fast
  fadeEnterFast: {
    keyframe: ({ delta }) => ({
      "0%": { opacity: delta },
      "100%": { opacity: 1 }
    }),
    keyframeParams: { delta: 0 },
    duration: fast,
    timingFunction: linear,
    fillMode: "forwards"
  },
  // Basic Fade In Animation --Normal
  fadeEnterNormal: {
    keyframe: ({ delta }) => ({
      "0%": { opacity: delta },
      "100%": { opacity: 1 }
    }),
    keyframeParams: { delta: 0 },
    duration: normal,
    timingFunction: linear,
    fillMode: "forwards"
  },
  // Basic Fade In Animation --Slow
  fadeEnterSlow: {
    keyframe: ({ delta }) => ({
      "0%": { opacity: delta },
      "100%": { opacity: 1 }
    }),
    keyframeParams: { delta: 0 },
    duration: slow,
    timingFunction: linear,
    fillMode: "forwards"
  },
  // Basic Fade In Animation --Slow
  fadeEnterSlower: {
    keyframe: ({ delta }) => ({
      "0%": { opacity: delta },
      "100%": { opacity: 1 }
    }),
    keyframeParams: { delta: 0 },
    duration: slower,
    timingFunction: linear,
    fillMode: "forwards"
  },
  // Basic Fade In Animation --UltraSlow
  fadeEnterUltraSlow: {
    keyframe: ({ delta }) => ({
      "0%": { opacity: delta },
      "100%": { opacity: 1 }
    }),
    keyframeParams: { delta: 0 },
    duration: ultraSlow,
    timingFunction: linear,
    fillMode: "forwards"
  },
  fadeBGEnterNormal: {
    keyframe: ({ delta }) => ({
      "0%": { background: `rgba(0, 0, 0, ${delta})` },
      "100%": { background: `rgba(0, 0, 0, 0.5)` }
    }),
    keyframeParams: { delta: 0 },
    duration: normal,
    timingFunction: linear,
    fillMode: "forwards"
  },
  fadeBGEnterNormalLight: {
    keyframe: ({ delta }) => ({
      "0%": { background: `rgba(0, 0, 0, ${delta})` },
      "100%": { background: `rgba(0, 0, 0, 0.25)` }
    }),
    keyframeParams: { delta: 0 },
    duration: normal,
    timingFunction: linear,
    fillMode: "forwards"
  },
  // Fade Outs
  // Basic Fade Out Animation --UltraFast
  fadeExitUltraFast: {
    keyframe: ({ delta }) => ({
      "0%": { opacity: 1 },
      "100%": { opacity: delta }
    }),
    keyframeParams: { delta: 0 },
    duration: ultraFast,
    timingFunction: linear,
    fillMode: "forwards"
  },
  // Basic Fade Out Animation --Faster
  fadeExitFaster: {
    keyframe: ({ delta }) => ({
      "0%": { opacity: 1 },
      "100%": { opacity: delta }
    }),
    keyframeParams: { delta: 0 },
    duration: faster,
    timingFunction: linear,
    fillMode: "forwards"
  },
  // Basic Fade Out Animation --Fast
  fadeExitFast: {
    keyframe: ({ delta }) => ({
      "0%": { opacity: 1 },
      "100%": { opacity: delta }
    }),
    keyframeParams: { delta: 0 },
    duration: fast,
    timingFunction: linear,
    fillMode: "forwards"
  },
  // Basic Fade Out Animation --Normal
  fadeExitNormal: {
    keyframe: ({ delta }) => ({
      "0%": { opacity: 1 },
      "100%": { opacity: delta }
    }),
    keyframeParams: { delta: 0 },
    duration: normal,
    timingFunction: linear,
    fillMode: "forwards"
  },
  // Basic Fade Out Animation --Slow
  fadeExitSlow: {
    keyframe: ({ delta }) => ({
      "0%": { opacity: 1 },
      "100%": { opacity: delta }
    }),
    keyframeParams: { delta: 0 },
    duration: slow,
    timingFunction: linear,
    fillMode: "forwards"
  },
  // Basic Fade Out Animation --Slower
  fadeExitSlower: {
    keyframe: ({ delta }) => ({
      "0%": { opacity: 1 },
      "100%": { opacity: delta }
    }),
    keyframeParams: { delta: 0 },
    duration: slower,
    timingFunction: linear,
    fillMode: "forwards"
  },
  // Basic Fade Out Animation --UltraSlow
  fadeExitUltraSlow: {
    keyframe: ({ delta }) => ({
      "0%": { opacity: 1 },
      "100%": { opacity: delta }
    }),
    keyframeParams: { delta: 0 },
    duration: ultraSlow,
    timingFunction: linear,
    fillMode: "forwards"
  },
  fadeBGExitNormal: {
    keyframe: ({ delta }) => ({
      "0%": { background: `rgba(0, 0, 0, 0.5)` },
      "100%": { background: `rgba(0, 0, 0, ${delta})` }
    }),
    keyframeParams: { delta: 0 },
    duration: normal,
    timingFunction: linear,
    fillMode: "forwards"
  },
  fadeBGExitNormalLight: {
    keyframe: ({ delta }) => ({
      "0%": { background: `rgba(0, 0, 0, 0.25)` },
      "100%": { background: `rgba(0, 0, 0, ${delta})` }
    }),
    keyframeParams: { delta: 0 },
    duration: normal,
    timingFunction: linear,
    fillMode: "forwards"
  }
};

// packages/aria/src/components/Drawer/Drawer.tsx
import { jsx as jsx99, jsxs as jsxs69 } from "react/jsx-runtime";
var drawerClassName = "ui-drawer";
var drawerSlotClassNames = {
  content: `${drawerClassName}__content`,
  swipeHandle: `${drawerClassName}__swipeHandle`
};
var getContentAnimationByAnchor = (anchor, open) => {
  switch (anchor) {
    case "left":
      return open ? "enterFromLeft" : "exitToLeft";
    case "right":
      return open ? "enterFromRight" : "exitToRight";
    case "bottom":
      return open ? "enterFromBottom" : "exitToBottom";
    case "top":
      return open ? "enterFromTop" : "exitToTop";
  }
};
var BOTTOM_DRAWER_CLOSE_DRAG_THRESHOLD = 100;
var DRAWER_CLOSE_ANIMATION_DELAY = 300;
var Drawer = (props) => {
  const context = useFluentContext100();
  const { setStart, setEnd } = useTelemetry100(
    Drawer.displayName,
    context.telemetry
  );
  setStart();
  const {
    className,
    styles,
    variables,
    design,
    defaultOpen,
    open,
    anchor = "left",
    fluid,
    content,
    autoDisableBodyScroll = true,
    swipeableEdge,
    appDisplayMode = { mobile: false, desktop: true }
  } = props;
  const contentRef = useRef14();
  const canSafeCloseRef = useRef14(true);
  const { mobile } = appDisplayMode;
  const touchStart = useRef14(null);
  const touchEnd = useRef14(null);
  const [drawerOpen, setDrawerOpen] = useAutoControlled13({
    defaultValue: defaultOpen,
    value: open,
    initialValue: false
  });
  const { classes } = useStyles100(Drawer.displayName, {
    className: drawerClassName,
    mapPropsToStyles: () => ({
      anchor,
      fluid,
      hasSwipeableEdge: !!swipeableEdge
    }),
    mapPropsToInlineStyles: () => ({
      className,
      styles,
      variables,
      design
    }),
    rtl: context.rtl,
    unstable_props: props
  });
  const unhandledProps = useUnhandledProps99(Drawer.handledProps, props);
  const handleOpenChange = (e, data) => {
    setDrawerOpen(data?.open);
    props.onOpenChange?.(e, data);
  };
  useKeyPress(
    (event) => (event.key === "Escape" || event.key === "Esc") && !!drawerOpen,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event) => handleOpenChange(event, { ...unhandledProps, open: false }),
    { events: ["keydown"] }
  );
  const didDrawerOpen = useRef14(false);
  useEffect11(() => {
    if (!autoDisableBodyScroll) return;
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
      didDrawerOpen.current = true;
    } else if (didDrawerOpen.current) {
      document.body.style.overflow = "unset";
      didDrawerOpen.current = false;
    }
    return () => {
      if (didDrawerOpen.current) {
        setTimeout(() => {
          document.body.style.overflow = "unset";
          didDrawerOpen.current = false;
        }, 0);
      }
    };
  }, [drawerOpen, autoDisableBodyScroll]);
  const primaryContent = Box.create(content, {
    overrideProps: () => ({
      "data-testid": "drawer-content"
    })
  });
  if (!primaryContent) throw new Error("Drawer content is required");
  const drawerContent = /* @__PURE__ */ jsx99(
    Animation4,
    {
      mountOnEnter: true,
      unmountOnExit: true,
      visible: drawerOpen,
      name: getContentAnimationByAnchor(anchor, drawerOpen),
      duration: normal,
      children: /* @__PURE__ */ jsxs69(
        Flex,
        {
          className: drawerSlotClassNames.content,
          onClick: (e) => e.stopPropagation(),
          onMouseDown: () => canSafeCloseRef.current = false,
          onMouseUp: () => canSafeCloseRef.current = true,
          children: [
            swipeableEdge ? SwipeableEdge.create(swipeableEdge, {
              overrideProps: () => ({
                anchor
              })
            }) : null,
            primaryContent
          ]
        }
      )
    }
  );
  const result = /* @__PURE__ */ jsx99(
    Provider3,
    {
      theme: createTheme(
        {
          animations: {
            "popup-show": fadeInOutAnimations.fadeEnterFast,
            "popup-hide": fadeInOutAnimations.fadeExitFast
          }
        },
        "drawer"
      ),
      children: /* @__PURE__ */ jsx99(
        Popup2,
        {
          ...unhandledProps,
          open: drawerOpen,
          onOpenChange: handleOpenChange,
          content: {
            ref: contentRef,
            className: classes["root"],
            "data-testid": "drawer-container",
            onClick: (e) => {
              if (canSafeCloseRef.current) {
                handleOpenChange(e, {
                  ...unhandledProps,
                  open: false
                });
              } else {
                canSafeCloseRef.current = true;
              }
            },
            children: mobile && swipeableEdge && anchor === "bottom" ? (
              // use-gesture/react offers velocity gesture thresholds and can simplify this BUT
              // encountering issues where stopPropagation from child arent being handled, need to dig deeper.
              /* @__PURE__ */ jsx99(
                "div",
                {
                  className: drawerSlotClassNames.swipeHandle,
                  onTouchStart: (e) => {
                    touchStart.current = e.targetTouches[0].clientY;
                  },
                  onTouchMove: (e) => {
                    const start = touchStart.current;
                    const end = e.targetTouches[0].clientY;
                    touchEnd.current = end;
                    const distance = start && end ? end - start : 0;
                    if (contentRef?.current?.style) {
                      contentRef.current.style.transition = `none`;
                      contentRef.current.style.transform = distance > 0 ? `translateY(${distance}px)` : `none`;
                    }
                  },
                  onTouchEnd: (e) => {
                    const start = touchStart.current;
                    const end = touchEnd.current;
                    const distance = start && end ? end - start : 0;
                    if (distance > BOTTOM_DRAWER_CLOSE_DRAG_THRESHOLD && typeof swipeableEdge === "object" && "onEdgeSwipe" in swipeableEdge) {
                      swipeableEdge.onEdgeSwipe?.(e, "down");
                      setTimeout(() => {
                        touchStart.current = null;
                        touchEnd.current = null;
                      }, DRAWER_CLOSE_ANIMATION_DELAY);
                    } else {
                      if (contentRef?.current?.style) {
                        contentRef.current.style.transition = `transform 200ms ease`;
                        contentRef.current.style.transform = `none`;
                      }
                      touchStart.current = null;
                      touchEnd.current = null;
                    }
                  },
                  children: drawerContent
                }
              )
            ) : drawerContent
          }
        }
      )
    }
  );
  setEnd();
  return result;
};
Drawer.displayName = "Drawer";
Drawer.create = createShorthandFactory94({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: Drawer
});
Drawer.handledProps = [
  ...commonHandledProps(),
  "defaultOpen",
  "open",
  "anchor",
  "fluid",
  "onOpenChange",
  "autoDisableBodyScroll",
  "swipeableEdge",
  "appDisplayMode"
];

// packages/aria/src/components/ToastMessage/ToastMessage.tsx
import { useEffect as useEffect12, useRef as useRef15, useState as useState13 } from "react";
import { forwardRef as forwardRef92 } from "react";
import {
  useFluentContext as useFluentContext101,
  getElementType as getElementType80,
  useAccessibility as useAccessibility94,
  useTelemetry as useTelemetry101,
  useStyles as useStyles101,
  useUnhandledProps as useUnhandledProps100,
  popupBehavior as popupBehavior3,
  Animation as Animation5
} from "@fluentui/react-northstar";
import { useAutoControlled as useAutoControlled14 } from "@fluentui/react-bindings";
import {
  CheckboxCircleFill,
  ErrorWarningFill,
  TimesLight
} from "@archipro-design/icons";
import { useClickAway, useMemoizedFn } from "ahooks";
import { jsx as jsx100, jsxs as jsxs70 } from "react/jsx-runtime";
var defaultRootElementType37 = "div";
var toastMessageClassName = "ui-toastMessage";
var toastMessageClassNames = {
  container: `${toastMessageClassName}__container`,
  content: `${toastMessageClassName}__content`,
  iconBefore: `${toastMessageClassName}__iconBefore`,
  closeButton: `${toastMessageClassName}__closeButton`,
  message: `${toastMessageClassName}__message`,
  clickHere: `${toastMessageClassName}__clickHere`
};
var getIconBefore = (type) => {
  switch (type) {
    case "success":
      return /* @__PURE__ */ jsx100(CheckboxCircleFill, {});
    case "notification":
      return null;
    case "error":
      return /* @__PURE__ */ jsx100(ErrorWarningFill, {});
    default:
      return /* @__PURE__ */ jsx100(ErrorWarningFill, {});
  }
};
var ToastMessage = forwardRef92(
  (props, ref) => {
    const context = useFluentContext101();
    const { setStart, setEnd } = useTelemetry101(
      ToastMessage.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      onClose,
      type = "error",
      iconBefore = getIconBefore(type),
      closeButton = {},
      message,
      variant = "regular",
      color = "dark",
      clickHereLink,
      autoHideDelay = 6,
      closeOnClickAway = false,
      visibleValue,
      defaultValue = true
    } = props;
    const [visible, setVisible] = useAutoControlled14({
      defaultValue,
      value: visibleValue,
      initialValue: true
    });
    const getA11yProps = useAccessibility94(
      accessibility,
      {
        debugName: ToastMessage.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles101(
      ToastMessage.displayName,
      {
        className: toastMessageClassName,
        mapPropsToStyles: () => ({
          variant,
          color
        }),
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps100(
      ToastMessage.handledProps,
      props
    );
    const ElementType = getElementType80(props);
    const toastRef = useRef15(null);
    const memoizedOnClose = useMemoizedFn(onClose);
    const [isHover, setIsHover] = useState13(false);
    const [animationKey, setAnimationKey] = useState13(1);
    const previousMsgRef = useRef15(message);
    const clickAwayCountRef = useRef15(0);
    useClickAway(() => {
      if (!closeOnClickAway) {
        return;
      }
      if (clickAwayCountRef.current > 0 && previousMsgRef.current === message) {
        memoizedOnClose();
        clickAwayCountRef.current = 0;
      } else {
        clickAwayCountRef.current += 1;
        previousMsgRef.current = message;
      }
    }, [toastRef]);
    useEffect12(() => {
      const prefix = typeof message === "string" ? message.length : 1;
      setAnimationKey(prefix + Math.random());
      clickAwayCountRef.current = 0;
    }, [message]);
    useEffect12(() => {
      if (autoHideDelay === 0) {
        return;
      }
      const timeoutId = isHover ? null : setTimeout(() => {
        setVisible(false);
      }, autoHideDelay * 1e3);
      if (isHover && timeoutId) {
        clearTimeout(timeoutId);
      }
      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }, [message, isHover, autoHideDelay, setVisible]);
    const result = /* @__PURE__ */ jsx100(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: message ? /* @__PURE__ */ jsx100(
          Animation5,
          {
            visible,
            name: visible ? "enterFromBottomToast" : "exitToBottomToast",
            onExited: () => message && memoizedOnClose(),
            children: /* @__PURE__ */ jsx100(
              Box,
              {
                className: toastMessageClassNames.container,
                ref: toastRef,
                onMouseEnter: () => {
                  setIsHover(true);
                },
                onMouseLeave: () => {
                  setIsHover(false);
                },
                children: /* @__PURE__ */ jsxs70(
                  Flex,
                  {
                    className: toastMessageClassNames.content,
                    space: "between",
                    vAlign: "center",
                    fill: true,
                    children: [
                      Box.create(iconBefore, {
                        defaultProps: () => ({
                          className: toastMessageClassNames.iconBefore
                        })
                      }),
                      /* @__PURE__ */ jsxs70(Box, { className: toastMessageClassNames.message, children: [
                        message,
                        clickHereLink && Label.create(clickHereLink, {
                          defaultProps: () => {
                            return {
                              variant: "02",
                              weight: "medium",
                              className: toastMessageClassNames.clickHere,
                              as: "a",
                              maximumLines: 2,
                              children: "Click here"
                            };
                          }
                        })
                      ] }),
                      Button.create(closeButton, {
                        generateKey: true,
                        defaultProps: () => {
                          return {
                            icon: /* @__PURE__ */ jsx100(TimesLight, {}),
                            variant: "text",
                            variables: { buttonPadding: 8 },
                            onClick: memoizedOnClose,
                            color,
                            size: 24,
                            className: toastMessageClassNames.closeButton
                          };
                        }
                      })
                    ]
                  }
                )
              }
            )
          },
          animationKey
        ) : null
      }
    );
    setEnd();
    return result;
  }
);
ToastMessage.displayName = "ToastMessage";
ToastMessage.defaultProps = {
  as: defaultRootElementType37,
  accessibility: popupBehavior3
};
ToastMessage.handledProps = [
  ...commonHandledProps(),
  "onClose",
  "message",
  "iconBefore",
  "closeButton",
  "clickHereLink",
  "autoHideDelay",
  "closeOnClickAway",
  "variant",
  "color",
  "visibleValue"
];

// packages/aria/src/components/MobileHeaderSection/MobileHeaderSection.tsx
import { forwardRef as forwardRef93 } from "react";
import {
  useFluentContext as useFluentContext104,
  getElementType as getElementType81,
  useAccessibility as useAccessibility95,
  useTelemetry as useTelemetry102,
  useStyles as useStyles103,
  useUnhandledProps as useUnhandledProps101,
  Animation as Animation6
} from "@fluentui/react-northstar";

// packages/aria/src/hooks/useStyles.ts
import { useFluentContext as useFluentContext102 } from "@fluentui/react-bindings";
import { combineRules } from "fela";
function mergePropsWithTheme(props, theme) {
  if (props) {
    return {
      ...props,
      theme
    };
  }
  return {
    theme
  };
}
function useStyles102(props) {
  const { theme, renderer: fluentuiRenderer } = useFluentContext102();
  const renderer = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call
    fluentuiRenderer.getOriginalRenderer()
  );
  if (!renderer) {
    throw new Error(
      'The "useStyles" hook can only be used inside a "RendererProvider"'
    );
  }
  const propsWithTheme = mergePropsWithTheme(props, theme);
  function css(...rules) {
    return renderer.renderRule(
      combineRules(...rules),
      propsWithTheme
    );
  }
  function staticStyle(style, selector) {
    return renderer.renderStatic(style, selector);
  }
  function keyframe(rule) {
    return renderer.renderKeyframe(rule, propsWithTheme);
  }
  return {
    css,
    staticStyle,
    keyframe
  };
}

// packages/aria/src/hooks/useTheme.ts
import { useFluentContext as useFluentContext103 } from "@fluentui/react-bindings";
var useTheme = () => {
  const ctx = useFluentContext103();
  return ctx.theme;
};

// packages/aria/src/hooks/useScrollLock.ts
import {
  clearAllBodyScrollLocks,
  disableBodyScroll,
  enableBodyScroll
} from "body-scroll-lock";
import { useEffect as useEffect13 } from "react";
var useScrollLock = (scrollRef, enable) => {
  useEffect13(() => {
    if (!scrollRef?.current) return;
    if (enable) {
      disableBodyScroll(scrollRef.current);
    } else {
      enableBodyScroll(scrollRef.current);
    }
    return () => {
      clearAllBodyScrollLocks();
    };
  }, [enable, scrollRef]);
};

// packages/aria/src/components/MobileHeaderSection/MobileHeaderSection.tsx
import { jsx as jsx101, jsxs as jsxs71 } from "react/jsx-runtime";
var mobileHeaderSectionClassName = "ui-mobileHeaderSection";
var mobileHeaderSectionSlotClassNames = {
  title: `${mobileHeaderSectionClassName}__title`,
  subtitle: `${mobileHeaderSectionClassName}__subtitle`,
  description: `${mobileHeaderSectionClassName}__description`
};
var MobileHeaderSection = forwardRef93((props, ref) => {
  const context = useFluentContext104();
  const { setStart, setEnd } = useTelemetry102(
    MobileHeaderSection.displayName,
    context.telemetry
  );
  setStart();
  const theme = useTheme();
  const {
    className,
    styles,
    variables,
    accessibility,
    design,
    title,
    subtitle,
    description,
    animationState,
    content
  } = props;
  const getA11yProps = useAccessibility95(
    accessibility,
    {
      debugName: MobileHeaderSection.displayName,
      rtl: context.rtl
    }
  );
  const { classes } = useStyles103(
    MobileHeaderSection.displayName,
    {
      className: mobileHeaderSectionClassName,
      mapPropsToStyles: () => ({
        animationState
      }),
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    }
  );
  const unhandledProps = useUnhandledProps101(
    MobileHeaderSection.handledProps,
    props
  );
  const ElementType = getElementType81(props);
  const result = /* @__PURE__ */ jsx101(
    ElementType,
    {
      ...getA11yProps("root", {
        ref,
        className: classes["root"],
        ...unhandledProps
      }),
      children: /* @__PURE__ */ jsxs71("div", { className: classes["fixedWrapper"], children: [
        /* @__PURE__ */ jsxs71("div", { className: classes["container"], children: [
          /* @__PURE__ */ jsxs71(Flex, { className: classes["headings"], children: [
            Label.create(title, {
              defaultProps: () => ({
                variant: "02",
                weight: "medium",
                variables: {
                  ...theme.siteVariables.textStyles.Heading.Medium65,
                  lineHeight: 1
                }
              }),
              overrideProps: () => ({
                className: mergeClassName(
                  title,
                  mobileHeaderSectionSlotClassNames.title
                )
              })
            }),
            Caption.create(subtitle, {
              defaultProps: () => ({
                variant: "04",
                weight: "regular"
              }),
              overrideProps: () => ({
                className: mergeClassName(
                  subtitle,
                  mobileHeaderSectionSlotClassNames.subtitle
                )
              })
            })
          ] }),
          description && Caption.create(description, {
            defaultProps: () => ({
              variant: "04",
              weight: "regular"
            }),
            overrideProps: () => ({
              className: mergeClassName(
                description,
                mobileHeaderSectionSlotClassNames.description
              )
            })
          })
        ] }),
        content && /* @__PURE__ */ jsx101(Animation6, { name: animationState, children: /* @__PURE__ */ jsx101("div", { className: classes["contentWrapper"], children: content }) })
      ] })
    }
  );
  setEnd();
  return result;
});
MobileHeaderSection.displayName = "MobileHeaderSection";
MobileHeaderSection.handledProps = [
  ...commonHandledProps(),
  "title",
  "subtitle",
  "description",
  "animationState"
];

// packages/aria/src/components/FilterContentLayout/FilterContentLayout.tsx
import { forwardRef as forwardRef94 } from "react";
import {
  useFluentContext as useFluentContext105,
  getElementType as getElementType82,
  useAccessibility as useAccessibility96,
  useTelemetry as useTelemetry103,
  useStyles as useStyles104,
  useUnhandledProps as useUnhandledProps102
} from "@fluentui/react-northstar";
import { cardBehavior as cardBehavior16 } from "@fluentui/accessibility";
import { CloseLine as CloseLine5 } from "@archipro-design/icons";
import cx22 from "classnames";
import { jsx as jsx102, jsxs as jsxs72 } from "react/jsx-runtime";
var defaultRootElementType38 = "div";
var filterContentLayoutClassName = "ui-filterContentLayout";
var filterContentLayoutSlotClassNames = {
  header: `${filterContentLayoutClassName}__header`,
  headerAction: `${filterContentLayoutClassName}__headerAction`,
  title: `${filterContentLayoutClassName}__title`,
  closeBtn: `${filterContentLayoutClassName}__closeBtn`,
  content: `${filterContentLayoutClassName}__content`,
  footer: `${filterContentLayoutClassName}__footer`
};
var FilterContentLayout = forwardRef94((props, ref) => {
  const context = useFluentContext105();
  const { setStart, setEnd } = useTelemetry103(
    FilterContentLayout.displayName,
    context.telemetry
  );
  setStart();
  const {
    className,
    styles,
    variables,
    accessibility,
    design,
    title,
    submitButton,
    closeButton,
    content,
    headerAction,
    onClose,
    onSubmit,
    onHeaderAction
  } = props;
  const getA11yProps = useAccessibility96(
    accessibility,
    {
      debugName: FilterContentLayout.displayName,
      rtl: context.rtl
    }
  );
  const { classes } = useStyles104(
    FilterContentLayout.displayName,
    {
      className: filterContentLayoutClassName,
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    }
  );
  const unhandledProps = useUnhandledProps102(
    FilterContentLayout.handledProps,
    props
  );
  const ElementType = getElementType82(props);
  const result = /* @__PURE__ */ jsx102(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps
      }),
      children: /* @__PURE__ */ jsxs72(Grid, { columns: "100%", className: classes["layout"], children: [
        /* @__PURE__ */ jsx102(
          Flex,
          {
            className: cx22(
              filterContentLayoutSlotClassNames.header,
              classes["header"]
            ),
            children: /* @__PURE__ */ jsxs72(Flex, { className: classes["headerInner"], children: [
              /* @__PURE__ */ jsx102(
                Flex,
                {
                  hAlign: "center",
                  className: filterContentLayoutSlotClassNames.headerAction,
                  children: Button.create(headerAction, {
                    generateKey: true,
                    defaultProps: () => {
                      return {
                        color: "primary",
                        variant: "text",
                        variables: {
                          buttonPadding: 0
                        },
                        onClick: (e) => onHeaderAction?.(e),
                        size: 14
                      };
                    }
                  })
                }
              ),
              /* @__PURE__ */ jsx102(
                Flex,
                {
                  hAlign: "center",
                  vAlign: "center",
                  className: filterContentLayoutSlotClassNames.title,
                  children: Caption.create(title, {
                    defaultProps: () => ({
                      variant: "03",
                      weight: "medium"
                    })
                  })
                }
              ),
              /* @__PURE__ */ jsx102(
                Flex,
                {
                  hAlign: "center",
                  className: filterContentLayoutSlotClassNames.closeBtn,
                  children: Button.create(closeButton, {
                    generateKey: true,
                    defaultProps: () => {
                      return {
                        icon: /* @__PURE__ */ jsx102(CloseLine5, {}),
                        variant: "text",
                        variables: {
                          buttonPadding: 8,
                          iconPadding: 0
                        },
                        onClick: (e) => onClose?.(e),
                        size: 24
                      };
                    }
                  })
                }
              )
            ] })
          }
        ),
        /* @__PURE__ */ jsx102(
          Flex,
          {
            className: cx22(
              filterContentLayoutSlotClassNames.content,
              classes["content"]
            ),
            children: content
          }
        ),
        /* @__PURE__ */ jsx102(
          Flex,
          {
            className: cx22(
              filterContentLayoutSlotClassNames.footer,
              classes["footer"]
            ),
            children: Button.create(submitButton, {
              generateKey: true,
              defaultProps: () => {
                return {
                  color: "primary",
                  variables: {
                    buttonPadding: 16,
                    buttonMinWidth: "100%"
                  },
                  onClick: (e) => onSubmit?.(e),
                  size: 16
                };
              }
            })
          }
        )
      ] })
    }
  );
  setEnd();
  return result;
});
FilterContentLayout.displayName = "FilterContentLayout";
FilterContentLayout.defaultProps = {
  as: defaultRootElementType38,
  accessibility: cardBehavior16
};
FilterContentLayout.handledProps = [
  ...commonHandledProps(),
  "disabled",
  "closeButton",
  "headerAction",
  "submitButton",
  "onSubmit",
  "onClose",
  "onHeaderAction"
];

// packages/aria/src/components/Modal/EnquiryModal/EnquiryModal.tsx
import { forwardRef as forwardRef95 } from "react";
import {
  useFluentContext as useFluentContext106,
  useAccessibility as useAccessibility97,
  useTelemetry as useTelemetry104,
  useStyles as useStyles105,
  useUnhandledProps as useUnhandledProps103,
  popupBehavior as popupBehavior4
} from "@fluentui/react-northstar";
import { CloseLine as CloseLine6 } from "@archipro-design/icons";
import _11 from "lodash";
import { jsx as jsx103 } from "react/jsx-runtime";
var defaultRootElementType39 = "div";
var enquiryModalClassName = "ui-enquiryModal";
var enquiryModalSlotClassNames = {
  dialog: `${enquiryModalClassName}__dialog`,
  headerAction: `${enquiryModalClassName}__headerAction`,
  header: `${enquiryModalClassName}__header`
};
var EnquiryModal = forwardRef95(
  (props, ref) => {
    const context = useFluentContext106();
    const { setStart, setEnd } = useTelemetry104(
      EnquiryModal.displayName,
      context.telemetry
    );
    setStart();
    const theme = useTheme();
    const {
      headerAction = {},
      footer = [
        {
          children: "Send",
          size: 20,
          variables: {
            buttonPadding: 16
          }
        }
      ],
      ...dialogProps
    } = props;
    const { className, styles, variables, accessibility, design } = dialogProps;
    const getA11yProps = useAccessibility97(
      accessibility,
      {
        debugName: EnquiryModal.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles105(
      EnquiryModal.displayName,
      {
        className: enquiryModalClassName,
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps103(
      EnquiryModal.handledProps,
      props
    );
    const renderHeaderAction = () => {
      return Button.create(headerAction, {
        defaultProps: () => {
          return {
            icon: /* @__PURE__ */ jsx103(CloseLine6, {}),
            size: 20,
            variant: "text",
            variables: {
              buttonPadding: 0,
              iconPadding: 0
            }
          };
        },
        overrideProps: (props2) => ({
          className: mergeClassName(
            props2.className,
            enquiryModalSlotClassNames.headerAction
          ),
          onClick: (e) => {
            _11.invoke(props2, "onClose", e, {});
            if (props2.onClick) props2.onClick(e);
          }
        })
      });
    };
    const renderFooterButtons = () => {
      return /* @__PURE__ */ jsx103(Flex, { vAlign: "center", hAlign: "end", gap: "space-4", wrap: false, children: footer.map((button) => {
        return Button.create(button);
      }) });
    };
    if (!Dialog.create) return null;
    const result = Dialog.create(dialogProps, {
      defaultProps: () => {
        return {
          ...getA11yProps("root", {
            className: classes["root"],
            ref,
            ...unhandledProps
          }),
          header: {
            children: renderHeaderAction()
          },
          footer: {
            children: renderFooterButtons()
          }
        };
      },
      overrideProps: () => {
        return {
          variables: {
            border: 0,
            rootBorderRadius: 0,
            rootWidth: pxToRem(900),
            rootBackground: theme?.siteVariables?.colors?.white["100A"],
            ...variables
          }
        };
      }
    });
    setEnd();
    return result ?? null;
  }
);
EnquiryModal.displayName = "EnquiryModal";
EnquiryModal.defaultProps = {
  as: defaultRootElementType39,
  accessibility: popupBehavior4
};
EnquiryModal.handledProps = [...commonHandledProps(), "headerAction", "footer"];

// packages/aria/src/components/TextInput/TextInput.tsx
import { useRef as useRef16 } from "react";
import { forwardRef as forwardRef96 } from "react";
import {
  useFluentContext as useFluentContext107,
  getElementType as getElementType83,
  useAccessibility as useAccessibility98,
  useTelemetry as useTelemetry105,
  useStyles as useStyles106,
  useUnhandledProps as useUnhandledProps104,
  inputBehavior as inputBehavior4,
  createShorthandFactory as createShorthandFactory95
} from "@fluentui/react-northstar";
import { CheckLine as CheckLine7 } from "@archipro-design/icons";
import { CloseLine as CloseLine7 } from "@archipro-design/icons";
import cx23 from "classnames";
import { useIsomorphicLayoutEffect as useIsomorphicLayoutEffect2 } from "ahooks";
import { Fragment as Fragment15, jsx as jsx104, jsxs as jsxs73 } from "react/jsx-runtime";
var defaultRootElementType40 = "div";
var textInputClassName = "ui-textInput";
var textInputSlotClassNames = {
  prefixIcon: `${textInputClassName}__prefixIcon`,
  primaryContent: `${textInputClassName}__primaryContent`,
  message: `${textInputClassName}__message`,
  clearIcon: `${textInputClassName}__clearIcon`
};
var TextInput = forwardRef96(
  (props, ref) => {
    const context = useFluentContext107();
    const { setStart, setEnd } = useTelemetry105(
      TextInput.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      prefixIcon,
      input,
      message,
      size = "lg",
      state = "default",
      suffixIcons,
      clearButton = {},
      divider = {},
      // Determines if we use javascript to handle the text elements height
      autoExpandMode = "auto"
    } = props;
    const getA11yProps = useAccessibility98(
      accessibility,
      {
        debugName: TextInput.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles106(
      TextInput.displayName,
      {
        className: textInputClassName,
        mapPropsToStyles: () => ({
          size,
          state,
          disabled: !!input?.disabled,
          autoExpandMode
        }),
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps104(TextInput.handledProps, props);
    const ElementType = getElementType83(props);
    const internalRef = useRef16(null);
    const handleTextAreaResize = () => {
      const el = internalRef?.current;
      if (!el || el.nodeName !== "TEXTAREA") return;
      if (autoExpandMode === "auto") {
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
        el.setAttribute("rows", "1");
      }
    };
    useIsomorphicLayoutEffect2(() => {
      handleTextAreaResize();
    }, [props.input]);
    const renderClearButton = (inputProps) => {
      return Button.create(clearButton, {
        defaultProps: () => {
          return {
            className: textInputSlotClassNames.clearIcon,
            icon: /* @__PURE__ */ jsx104(CloseLine7, {}),
            variant: "text",
            variables: { buttonPadding: 0, iconPadding: 0 }
          };
        },
        overrideProps: (props2) => {
          return {
            ...!props2.disabled && {
              onClick: (e) => {
                internalRef.current.focus();
                e.target.value = "";
                inputProps?.onChange?.(e);
                props2?.onClick?.(e);
              }
            }
          };
        }
      });
    };
    const result = /* @__PURE__ */ jsxs73(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: [
          /* @__PURE__ */ jsxs73(
            Flex,
            {
              fill: true,
              space: "between",
              className: textInputSlotClassNames.primaryContent,
              children: [
                /* @__PURE__ */ jsx104(
                  "div",
                  {
                    className: cx23([
                      textInputSlotClassNames.prefixIcon,
                      inputSlotClassNames.icon
                    ]),
                    children: prefixIcon
                  }
                ),
                Input.create(input, {
                  defaultProps: () => {
                    return {
                      inputRef: internalRef,
                      labelPosition: "inline",
                      showSuccessIndicator: false,
                      successIndicator: /* @__PURE__ */ jsx104(CheckLine7, {})
                    };
                  },
                  overrideProps: (props2) => {
                    if (props2.required) {
                      if (typeof props2.label === "string")
                        props2.label += "*";
                      else if (typeof props2?.label === "object" && props2?.label) {
                        props2.label["content"] += "*";
                      }
                    }
                    props2.icon = {
                      content: /* @__PURE__ */ jsxs73(Fragment15, { children: [
                        props2.clearable && renderClearButton(props2),
                        state === "success" && props2?.successIndicator,
                        state === "error" && props2?.errorIndicator,
                        suffixIcons?.map((icon, i) => {
                          return /* @__PURE__ */ jsx104(
                            "div",
                            {
                              className: inputSlotClassNames.icon,
                              children: icon
                            },
                            `icon-${i}`
                          );
                        })
                      ] })
                    };
                    props2.clearable = false;
                    return {
                      ...props2,
                      onChange: (e) => {
                        handleTextAreaResize();
                        if (props2.onChange) {
                          props2.onChange(e);
                        }
                      }
                    };
                  }
                })
              ]
            },
            "primary-content"
          ),
          /* @__PURE__ */ jsx104(Flex, { fill: true, children: Caption.create(message, {
            defaultProps: () => {
              return {
                variant: "03",
                maximumLines: 2
              };
            },
            overrideProps: (props2) => {
              return {
                className: mergeClassName(
                  props2.className,
                  textInputSlotClassNames.message
                )
              };
            }
          }) }, "message-content"),
          Divider2?.create?.(divider, {
            defaultProps: () => {
              return {
                color: "black",
                size: 2,
                fitted: true,
                design: {
                  marginTop: `-${2}px`
                }
              };
            }
          })
        ]
      }
    );
    setEnd();
    return result;
  }
);
TextInput.displayName = "TextInput";
TextInput.create = createShorthandFactory95({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: TextInput
});
TextInput.defaultProps = {
  as: defaultRootElementType40,
  accessibility: inputBehavior4
};
TextInput.handledProps = [
  ...commonHandledProps(),
  "prefixIcon",
  "input",
  "suffixIcons",
  "size",
  "message",
  "state",
  "clearButton"
];

// packages/aria/src/components/TextSelectorInput/TextSelectorInput.tsx
import { forwardRef as forwardRef97, useId as useId6 } from "react";
import {
  useFluentContext as useFluentContext108,
  useAccessibility as useAccessibility99,
  useTelemetry as useTelemetry106,
  useStyles as useStyles107,
  useUnhandledProps as useUnhandledProps105,
  inputBehavior as inputBehavior5,
  createShorthandFactory as createShorthandFactory96,
  createShorthand as createShorthand4,
  inputSlotClassNames as inputSlotClassNames2
} from "@fluentui/react-northstar";
import { useAutoControlled as useAutoControlled15 } from "@fluentui/react-bindings";
import { CloseLine as CloseLine8 } from "@archipro-design/icons";
import { CustomCaretDown as CustomCaretDown2, CustomCaretUp as CustomCaretUp2 } from "@archipro-design/icons";
import { CheckLine as CheckLine8 } from "@archipro-design/icons";
import _12 from "lodash";
import { jsx as jsx105 } from "react/jsx-runtime";
var defaultRootElementType41 = "div";
var textSelectorInputClassName = "ui-textSelectorInput";
var TextSelectorInput = forwardRef97((props, ref) => {
  const context = useFluentContext108();
  const { setStart, setEnd } = useTelemetry106(
    TextSelectorInput.displayName,
    context.telemetry
  );
  setStart();
  const {
    className,
    styles,
    variables,
    accessibility,
    design,
    input,
    state,
    dropdown,
    suffixIcons = [],
    showChevronIcon = false
  } = props;
  const getA11yProps = useAccessibility99(
    accessibility,
    {
      debugName: TextSelectorInput.displayName,
      rtl: context.rtl
    }
  );
  const { classes } = useStyles107(
    TextSelectorInput.displayName,
    {
      className: textSelectorInputClassName,
      mapPropsToStyles: () => ({
        state,
        disabled: !!input.disabled,
        hasValue: !!dropdown.value,
        showChevronIcon
      }),
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    }
  );
  const unhandledProps = useUnhandledProps105(
    TextSelectorInput.handledProps,
    props
  );
  const [dropdownOpen, setDropdownOpen] = useAutoControlled15({
    defaultValue: dropdown.defaultOpen,
    value: dropdown.open,
    initialValue: false
  });
  const id = useId6();
  const renderDropdown = (inputProps) => {
    return Dropdown.create ? Dropdown.create(dropdown, {
      defaultProps: () => {
        return {
          className: inputSlotClassNames2.input,
          placeholder: "Select an Option",
          toggleIndicator: null,
          checkable: true,
          checkableIndicator: /* @__PURE__ */ jsx105(CheckLine8, {}),
          fluid: true,
          clearIndicator: /* @__PURE__ */ jsx105(Box, { className: inputSlotClassNames2.icon, children: /* @__PURE__ */ jsx105(CloseLine8, {}) }),
          variables: { selectedItemsMaxHeight: pxToRem(74) },
          searchInput: {
            id: `downshift-${id}-input`
          },
          "aria-describedby": `dropdown-selected-items-count-${id}`,
          "aria-labelledby": `downshift-${id}-label`
        };
      },
      overrideProps: (dropdownProps) => {
        if (inputProps?.value) {
          dropdownProps.value = inputProps.value;
        }
        if (inputProps?.defaultValue) {
          dropdownProps.defaultValue = inputProps.defaultValue;
        }
        if (inputProps?.placeholder) {
          dropdownProps.placeholder = inputProps.placeholder;
        }
        if (inputProps?.disabled) {
          dropdownProps.disabled = inputProps.disabled;
        }
        dropdownProps.clearable = dropdownProps.value !== "";
        return {
          ...dropdownProps,
          onOpenChange: (e, data) => {
            setDropdownOpen(data.open);
            _12.invoke(dropdownProps, "onOpenChange", e, data);
          }
        };
      }
    }) : null;
  };
  const result = createShorthand4(TextInput, props, {
    defaultProps: () => {
      return {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        clearButton: {},
        variables: {
          inputMarginMobile: pxArrayToRem([0, 0, 0, 8]),
          inputMarginLargeDesktop: pxToRem(0),
          inputMarginMediumDesktop: pxToRem(0),
          ...props.variables
        }
      };
    },
    overrideProps: (props2) => {
      const inputProps = props2.input;
      const inputSuffixIcons = [];
      if (!dropdown.search || showChevronIcon) {
        inputSuffixIcons.push(
          dropdownOpen ? /* @__PURE__ */ jsx105(
            CustomCaretUp2,
            {
              onClick: () => _12.invoke(dropdown, "onOpenChange", null, {
                open: false
              })
            }
          ) : /* @__PURE__ */ jsx105(
            CustomCaretDown2,
            {
              onClick: () => _12.invoke(dropdown, "onOpenChange", null, {
                open: true
              })
            }
          )
        );
      }
      if (suffixIcons) {
        inputSuffixIcons.push(...suffixIcons);
      }
      return {
        suffixIcons: inputSuffixIcons,
        input: {
          ...inputProps,
          input: {
            children: (_14, props3) => {
              return renderDropdown(props3);
            }
          }
        }
      };
    }
  });
  setEnd();
  return result;
});
TextSelectorInput.displayName = "TextSelectorInput";
TextSelectorInput.create = createShorthandFactory96({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: TextSelectorInput
});
TextSelectorInput.defaultProps = {
  as: defaultRootElementType41,
  accessibility: inputBehavior5
};
TextSelectorInput.handledProps = [...commonHandledProps(), "dropdown"];

// packages/aria/src/components/Tile/AdminCaption/AdminCaption.tsx
import { forwardRef as forwardRef98 } from "react";
import {
  useFluentContext as useFluentContext109,
  getElementType as getElementType84,
  useAccessibility as useAccessibility100,
  useTelemetry as useTelemetry107,
  useStyles as useStyles108,
  useUnhandledProps as useUnhandledProps106,
  cardBehavior as cardBehavior17,
  createShorthandFactory as createShorthandFactory97
} from "@fluentui/react-northstar";
import { jsx as jsx106, jsxs as jsxs74 } from "react/jsx-runtime";
var OPERATION_TO_DISPLAY_MAPPING = {
  created: "C",
  published: "P",
  approved: "A"
};
var defaultRootElementType42 = "div";
var adminCaptionClassName = "ui-adminCaption";
var adminCaptionSlotClassNames = {
  operation: `${adminCaptionClassName}__operation`
};
var AdminCaption = forwardRef98(
  (props, ref) => {
    const context = useFluentContext109();
    const { setStart, setEnd } = useTelemetry107(
      AdminCaption.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      operations,
      caption
    } = props;
    const getA11yProps = useAccessibility100(
      accessibility,
      {
        debugName: AdminCaption.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles108(
      AdminCaption.displayName,
      {
        className: adminCaptionClassName,
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps106(
      AdminCaption.handledProps,
      props
    );
    const ElementType = getElementType84(props);
    const captionDefaultProps = {
      variant: "06",
      weight: "regular"
    };
    const captionProps = caption ?? captionDefaultProps;
    const tooltipContentDefaultProps = {
      size: 16,
      padding: 8,
      color: "black",
      variables: {
        maxWidth: "unset"
      }
    };
    const result = /* @__PURE__ */ jsx106(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: /* @__PURE__ */ jsx106(Flex, { gap: "space-16", children: operations && operations.map((operation) => {
          return /* @__PURE__ */ jsxs74(
            Flex,
            {
              gap: "space-4",
              children: [
                Caption.create(
                  OPERATION_TO_DISPLAY_MAPPING[operation.operation],
                  {
                    defaultProps: () => {
                      return {
                        ...captionProps,
                        className: adminCaptionSlotClassNames.operation,
                        weight: "medium"
                      };
                    }
                  }
                ),
                MessageTooltip.create(operation.tooltip, {
                  defaultProps: () => {
                    return {
                      position: "below",
                      align: "center",
                      trigger: Caption.create(
                        operation.initial.toUpperCase(),
                        {
                          defaultProps: () => {
                            return captionProps;
                          }
                        }
                      )
                    };
                  },
                  overrideProps: () => {
                    const customContentProps = operation.tooltip?.content ?? {};
                    return {
                      content: {
                        ...tooltipContentDefaultProps,
                        ...customContentProps
                      }
                    };
                  }
                }),
                Caption.create(` | ${operation.date}`, {
                  defaultProps: () => {
                    return captionProps;
                  }
                })
              ]
            },
            `${operation.operation}-caption`
          );
        }) })
      }
    );
    setEnd();
    return result;
  }
);
AdminCaption.displayName = "AdminCaption";
AdminCaption.create = createShorthandFactory97({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: AdminCaption
});
AdminCaption.defaultProps = {
  // What the root element will be rendered as, by default
  as: defaultRootElementType42,
  accessibility: cardBehavior17
};
AdminCaption.handledProps = [...commonHandledProps(), "operations", "caption"];

// packages/aria/src/components/Tile/HotButtons/HotButtons.tsx
import { forwardRef as forwardRef99 } from "react";
import {
  useFluentContext as useFluentContext110,
  getElementType as getElementType85,
  useAccessibility as useAccessibility101,
  useTelemetry as useTelemetry108,
  useStyles as useStyles109,
  useUnhandledProps as useUnhandledProps107,
  createShorthandFactory as createShorthandFactory98,
  cardBehavior as cardBehavior18
} from "@fluentui/react-northstar";
import {
  PushPinLine,
  PushPinFill,
  CustomPencil,
  Arrows,
  Trash,
  LinkUnlink
} from "@archipro-design/icons";
import { jsx as jsx107 } from "react/jsx-runtime";
var TYPE_TO_NORMAL_ICONS = {
  pin: /* @__PURE__ */ jsx107(PushPinLine, {}),
  edit: /* @__PURE__ */ jsx107(CustomPencil, {}),
  reposition: /* @__PURE__ */ jsx107(Arrows, {}),
  delete: /* @__PURE__ */ jsx107(Trash, {}),
  unlink: /* @__PURE__ */ jsx107(LinkUnlink, {})
};
var TYPE_TO_TOGGLED_ICONS = {
  pin: /* @__PURE__ */ jsx107(PushPinFill, {})
};
var TYPE_TO_TOOLTIP_MAPPING = {
  pin: "Pin this item",
  edit: `Edit this item`,
  reposition: "Reposition thumbnail image",
  delete: "Permanently DELETE this item",
  unlink: "Unlink this item"
};
var TYPE_TO_TOGGLED_TOOLTIP_MAPPING = {
  pin: "Unpin this item"
};
var defaultRootElementType43 = "div";
var hotButtonsClassName = "ui-hotButtons";
var HotButtons = forwardRef99(
  (props, ref) => {
    const context = useFluentContext110();
    const { setStart, setEnd } = useTelemetry108(
      HotButtons.displayName,
      context.telemetry
    );
    setStart();
    const { className, styles, variables, accessibility, design, items } = props;
    const getA11yProps = useAccessibility101(
      accessibility,
      {
        debugName: HotButtons.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles109(
      HotButtons.displayName,
      {
        className: hotButtonsClassName,
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps107(
      HotButtons.handledProps,
      props
    );
    const ElementType = getElementType85(props);
    const getButtonIcon = (type, toggled) => {
      let icon = null;
      if (toggled) {
        icon = TYPE_TO_TOGGLED_ICONS[type];
      }
      return icon || TYPE_TO_NORMAL_ICONS[type];
    };
    const getTooltip = (type, toggled) => {
      let tooltip = void 0;
      if (toggled) {
        tooltip = TYPE_TO_TOGGLED_TOOLTIP_MAPPING[type];
      }
      tooltip = tooltip || TYPE_TO_TOOLTIP_MAPPING[type];
      return tooltip;
    };
    const renderContent = () => {
      if (!items) {
        return void 0;
      }
      return items.map((buttonProps) => {
        const { type, toggled, tooltip, ...rest } = buttonProps;
        const button = Button.create(rest, {
          generateKey: true,
          defaultProps: () => {
            return {
              size: 16,
              color: "white",
              variables: {
                buttonPadding: 10,
                iconPadding: 0,
                buttonRadius: "50%",
                pointerEvents: "auto"
              },
              icon: getButtonIcon(type, !!toggled)
            };
          }
        });
        if (!tooltip) {
          return button;
        }
        const tooltipContentDefaultProps = {
          key: `${type}-tooltip`,
          children: getTooltip(type, !!toggled),
          size: 16,
          padding: 8,
          color: "black",
          variables: {
            maxWidth: "unset"
          }
        };
        return MessageTooltip.create(
          {
            key: `${type}-tooltip`
          },
          {
            defaultProps: () => {
              return {
                position: "above",
                offset: [0, 8],
                trigger: button
              };
            },
            overrideProps: () => {
              const customContentProps = tooltip.content ?? {};
              return {
                content: {
                  ...tooltipContentDefaultProps,
                  ...customContentProps
                }
              };
            }
          }
        );
      });
    };
    const result = /* @__PURE__ */ jsx107(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: /* @__PURE__ */ jsx107(Flex, { hAlign: "end", vAlign: "center", gap: "space-6", children: renderContent() })
      }
    );
    setEnd();
    return result;
  }
);
HotButtons.displayName = "HotButtons";
HotButtons.create = createShorthandFactory98({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: HotButtons,
  mappedArrayProp: "items"
});
HotButtons.defaultProps = {
  as: defaultRootElementType43,
  accessibility: cardBehavior18
};
HotButtons.handledProps = [...commonHandledProps(), "items"];

// packages/aria/src/components/BottomNav/BottomNav.tsx
import { forwardRef as forwardRef100 } from "react";
import {
  useFluentContext as useFluentContext111,
  getElementType as getElementType86,
  useAccessibility as useAccessibility102,
  useTelemetry as useTelemetry109,
  useStyles as useStyles110,
  useUnhandledProps as useUnhandledProps108
} from "@fluentui/react-northstar";
import { Animation as Animation7 } from "@fluentui/react-northstar";
import { jsx as jsx108 } from "react/jsx-runtime";
var defaultRootElementType44 = "div";
var bottomNavClassName = "ui-bottomNav";
var activeClassButton = "active";
var bottomNavSlotClassNames = {
  icon: `${bottomNavClassName}__buttonIcon`,
  active: `${bottomNavClassName}__buttonIcon.${activeClassButton}`
};
var BottomNav = forwardRef100(
  (props, ref) => {
    const context = useFluentContext111();
    const { setStart, setEnd } = useTelemetry109(
      BottomNav.displayName,
      context.telemetry
    );
    setStart();
    const {
      className,
      styles,
      variables,
      accessibility,
      design,
      itemsSlot,
      animationState
    } = props;
    const getA11yProps = useAccessibility102(
      accessibility,
      {
        debugName: BottomNav.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles110(
      BottomNav.displayName,
      {
        className: bottomNavClassName,
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        mapPropsToStyles: () => ({
          animationState
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const renderButtonSlot = () => {
      return itemsSlot.map((buttonType) => {
        const { type, icon, key } = buttonType;
        return Button.create(buttonType, {
          defaultProps: () => {
            return {
              variant: "text",
              variables: {
                buttonPadding: 0,
                pointerEvents: "auto",
                iconPadding: 0
              },
              className: bottomNavSlotClassNames.icon,
              icon,
              size: 44,
              "data-testid": type,
              "aria-label": type
            };
          },
          overrideProps: () => ({
            key
          })
        });
      });
    };
    const unhandledProps = useUnhandledProps108(BottomNav.handledProps, props);
    const ElementType = getElementType86(props);
    const result = /* @__PURE__ */ jsx108(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: /* @__PURE__ */ jsx108(Animation7, { name: animationState, children: /* @__PURE__ */ jsx108("div", { className: classes["wrapper"], children: renderButtonSlot() }) })
      }
    );
    setEnd();
    return result;
  }
);
BottomNav.displayName = "BottomNav";
BottomNav.defaultProps = {
  as: defaultRootElementType44
};
BottomNav.handledProps = [
  ...commonHandledProps(),
  "itemsSlot",
  "animationState"
];

// packages/aria/src/components/Modal/LoginModal/LoginModal.tsx
import { forwardRef as forwardRef101 } from "react";
import {
  useFluentContext as useFluentContext112,
  useAccessibility as useAccessibility103,
  useTelemetry as useTelemetry110,
  useStyles as useStyles111,
  useUnhandledProps as useUnhandledProps109,
  popupBehavior as popupBehavior5
} from "@fluentui/react-northstar";
import { CloseLine as CloseLine9 } from "@archipro-design/icons";
import _13 from "lodash";
import cx24 from "classnames";
import { jsx as jsx109, jsxs as jsxs75 } from "react/jsx-runtime";
var defaultRootElementType45 = "div";
var loginModalClassName = "ui-LoginModal";
var loginModalSlotClassNames = {
  dialog: `${loginModalClassName}__dialog`,
  header: `${loginModalClassName}__header`,
  footerButton: `${loginModalClassName}__footerButton`,
  footerContent: `${loginModalClassName}__footerContent`
};
var LoginModal = forwardRef101(
  (props, ref) => {
    const context = useFluentContext112();
    const { setStart, setEnd } = useTelemetry110(
      LoginModal.displayName,
      context.telemetry
    );
    setStart();
    const theme = useTheme();
    const {
      header = {},
      headerAction = {},
      footerButton = {},
      footerContent,
      ...dialogProps
    } = props;
    const { className, styles, variables, accessibility, design } = dialogProps;
    const getA11yProps = useAccessibility103(
      accessibility,
      {
        debugName: LoginModal.displayName,
        rtl: context.rtl
      }
    );
    const { classes } = useStyles111(
      LoginModal.displayName,
      {
        className: loginModalClassName,
        mapPropsToInlineStyles: () => ({
          className,
          styles,
          variables,
          design
        }),
        rtl: context.rtl,
        unstable_props: props
      }
    );
    const unhandledProps = useUnhandledProps109(
      LoginModal.handledProps,
      props
    );
    const renderLoginHeader = () => {
      return /* @__PURE__ */ jsxs75(
        Flex,
        {
          className: loginModalSlotClassNames.header,
          vAlign: "center",
          wrap: false,
          children: [
            Caption.create(header, {
              defaultProps: () => {
                return {
                  children: "Sign up",
                  variant: "04",
                  weight: "medium"
                };
              }
            }),
            Button.create(headerAction, {
              defaultProps: () => {
                return {
                  icon: /* @__PURE__ */ jsx109(CloseLine9, {}),
                  size: 30,
                  transparent: true,
                  color: "dark",
                  variables: {
                    buttonPadding: pxToRem(4)
                  },
                  onClick: (e) => {
                    _13.invoke(props, "onClose", e, {});
                  }
                };
              }
            })
          ]
        }
      );
    };
    const renderFooter = () => {
      return /* @__PURE__ */ jsxs75(Flex, { vAlign: "center", hAlign: "center", gap: "space-16", column: true, children: [
        Button.create(footerButton, {
          defaultProps: () => {
            return {
              color: "dark"
            };
          },
          overrideProps: (props2) => {
            return {
              className: cx24([
                props2.className,
                loginModalSlotClassNames.footerButton
              ])
            };
          }
        }),
        Box.create(footerContent, {
          overrideProps: (props2) => {
            return {
              className: cx24([
                props2.className,
                loginModalSlotClassNames.footerContent
              ])
            };
          }
        })
      ] });
    };
    const result = Dialog.create?.(dialogProps, {
      defaultProps: () => {
        return {
          ...getA11yProps("root", {
            className: classes["root"],
            ref,
            ...unhandledProps
          }),
          header: {
            children: renderLoginHeader()
          },
          footer: {
            children: renderFooter()
          }
        };
      },
      overrideProps: () => {
        return {
          variables: {
            border: 0,
            rootBorderRadius: 0,
            rootWidth: pxToRem(574),
            rootBackground: theme?.siteVariables?.colors?.primitive?.white,
            ...variables
          }
        };
      }
    });
    setEnd();
    return result ?? null;
  }
);
LoginModal.displayName = "LoginModal";
LoginModal.defaultProps = {
  as: defaultRootElementType45,
  accessibility: popupBehavior5
};
LoginModal.handledProps = [
  ...commonHandledProps(),
  "header",
  "headerAction",
  "footerButton",
  "footerContent"
];

// packages/aria/src/components/Loader/Loader.tsx
import React18 from "react";
import { Animation as Animation8 } from "@fluentui/react-northstar";
import { Loader as LoaderBase } from "@fluentui/react-northstar";
import { Loader as LoaderSVG } from "@archipro-design/icons";
import { loaderClassName } from "@fluentui/react-northstar";
import { jsx as jsx110 } from "react/jsx-runtime";
var displayName24 = "Loader";
var Loader3 = React18.forwardRef((props, ref) => {
  const { animationName = "rotateUltraSlow" } = props;
  return /* @__PURE__ */ jsx110(
    LoaderBase,
    {
      indicator: {
        children: /* @__PURE__ */ jsx110(Animation8, { name: animationName, children: /* @__PURE__ */ jsx110(LoaderSVG, {}) })
      },
      ...props,
      ref
    }
  );
});
Loader3.displayName = displayName24;

// packages/aria/src/components/Switch/Switch.tsx
import { forwardRef as forwardRef102 } from "react";
import {
  useFluentContext as useFluentContext113,
  getElementType as getElementType87,
  useAccessibility as useAccessibility104,
  useTelemetry as useTelemetry111,
  useStyles as useStyles112,
  useUnhandledProps as useUnhandledProps110,
  createShorthandFactory as createShorthandFactory99,
  checkboxBehavior as checkboxBehavior5
} from "@fluentui/react-northstar";
import {
  useStateManager as useStateManager2
} from "@fluentui/react-bindings";
import { createCheckboxManager as createCheckboxManager2 } from "@fluentui/state";
import { jsx as jsx111, jsxs as jsxs76 } from "react/jsx-runtime";
var switchClassName = "ui-switch";
var switchSlotClassNames = {
  indicatorContainer: `${switchClassName}__indicatorContainer`
};
var Switch = forwardRef102((props, ref) => {
  const context = useFluentContext113();
  const { setStart, setEnd } = useTelemetry111(
    Switch.displayName,
    context.telemetry
  );
  setStart();
  const {
    className,
    styles,
    variables,
    accessibility,
    design,
    disabled,
    label,
    checked,
    defaultChecked,
    onChange,
    thumbChildren
  } = props;
  const { state, actions } = useStateManager2(createCheckboxManager2, {
    mapPropsToInitialState: () => ({ checked: defaultChecked }),
    mapPropsToState: () => ({ checked })
  });
  const getA11yProps = useAccessibility104(
    accessibility,
    {
      debugName: Switch.displayName,
      mapPropsToBehavior: () => ({
        checked: state.checked,
        disabled
      }),
      rtl: context.rtl
    }
  );
  const { classes } = useStyles112(Switch.displayName, {
    className: switchClassName,
    mapPropsToStyles: () => ({
      disabled,
      checked: state.checked
    }),
    mapPropsToInlineStyles: () => ({
      className,
      styles,
      variables,
      design
    }),
    rtl: context.rtl,
    unstable_props: props
  });
  const unhandledProps = useUnhandledProps110(Switch.handledProps, props);
  const labelElement = Label.create(label, {
    defaultProps: () => ({ variant: "02" })
  });
  const ElementType = getElementType87(props);
  const handleClick = (e) => {
    if (!disabled) {
      const newChecked = !state.checked;
      actions.toggle(newChecked);
      onChange?.(e, { ...props, checked: newChecked });
    }
  };
  const result = /* @__PURE__ */ jsxs76(
    ElementType,
    {
      ...getA11yProps("root", {
        className: classes["root"],
        ref,
        ...unhandledProps,
        onClick: handleClick
      }),
      children: [
        labelElement,
        /* @__PURE__ */ jsx111("span", { className: classes["switch"], children: /* @__PURE__ */ jsx111("span", { className: classes["thumb"], children: thumbChildren }) })
      ]
    }
  );
  setEnd();
  return result;
});
Switch.displayName = "Switch";
Switch.create = createShorthandFactory99({
  Component: Switch
});
Switch.defaultProps = {
  accessibility: checkboxBehavior5
};
Switch.handledProps = [
  // By default, these are all handled
  ...commonHandledProps(),
  // Extra props that are handled
  "checked",
  "disabled",
  "defaultChecked",
  "label",
  "onChange"
];

// packages/aria/src/components/SearchableDropdown/SearchableDropdown.tsx
import { forwardRef as forwardRef103 } from "react";
import {
  useFluentContext as useFluentContext114,
  useTelemetry as useTelemetry112,
  useStyles as useStyles113,
  Dropdown as Dropdown3,
  createShorthandFactory as createShorthandFactory100
} from "@fluentui/react-northstar";
import { CloseLine as CloseLine10, SearchLine as SearchLine4 } from "@archipro-design/icons";
import { jsx as jsx112 } from "react/jsx-runtime";
var defaultRootElementType46 = "div";
var searchableDropdownClassName = "ui-searchableDropdown";
var SearchableDropdown = forwardRef103((props, ref) => {
  const context = useFluentContext114();
  const { setStart, setEnd } = useTelemetry112(
    SearchableDropdown.displayName,
    context.telemetry
  );
  setStart();
  const { className, styles, variables, design } = props;
  const { classes } = useStyles113(
    SearchableDropdown.displayName,
    {
      className: searchableDropdownClassName,
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      rtl: context.rtl,
      unstable_props: props
    }
  );
  const result = /* @__PURE__ */ jsx112(
    Dropdown3,
    {
      className: classes["root"],
      ref,
      search: (x) => x,
      fluid: true,
      clearable: true,
      checkable: true,
      allowFreeform: true,
      placeholder: "Search",
      clearIndicator: /* @__PURE__ */ jsx112(CloseLine10, { size: "2x" }),
      toggleIndicator: /* @__PURE__ */ jsx112(SearchLine4, { size: "2x" }),
      ...props
    }
  );
  setEnd();
  return result;
});
SearchableDropdown.displayName = "SearchableDropdown";
SearchableDropdown.create = createShorthandFactory100({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: SearchableDropdown
});
SearchableDropdown.defaultProps = {
  as: defaultRootElementType46
};
SearchableDropdown.handledProps = [...commonHandledProps(), "disabled"];

// packages/aria/src/components/Tile/FileTile/FileTile.tsx
import { forwardRef as forwardRef104 } from "react";
import {
  useFluentContext as useFluentContext115,
  getElementType as getElementType88,
  useAccessibility as useAccessibility105,
  useTelemetry as useTelemetry113,
  useStyles as useStyles114,
  useUnhandledProps as useUnhandledProps111,
  cardBehavior as cardBehavior19,
  createShorthandFactory as createShorthandFactory101
} from "@fluentui/react-northstar";
import cx25 from "classnames";
import { jsx as jsx113, jsxs as jsxs77 } from "react/jsx-runtime";
var defaultRootElementType47 = "div";
var fileTileClassName = "ui-fileTile";
var fileTileSlotClassNames = {
  contentWrap: `${fileTileClassName}__contentWrap`,
  description: `${fileTileClassName}__description`,
  fileTileImage: `${fileTileClassName}__fileTileImage`,
  extensionText: `${fileTileClassName}__extensionText`
};
var FileTile = forwardRef104((props, ref) => {
  const context = useFluentContext115();
  const { setStart, setEnd } = useTelemetry113(
    FileTile.displayName,
    context.telemetry
  );
  setStart();
  const {
    className,
    styles,
    variables,
    accessibility,
    design,
    size = "medium",
    description,
    variant = "01",
    tileImage,
    iconText,
    topActions
  } = props;
  const getA11yProps = useAccessibility105(
    accessibility,
    {
      debugName: FileTile.displayName,
      rtl: context.rtl
    }
  );
  const { classes } = useStyles114(FileTile.displayName, {
    className: fileTileClassName,
    mapPropsToStyles: () => ({
      size,
      variant
    }),
    mapPropsToInlineStyles: () => ({
      className,
      styles,
      variables,
      design
    }),
    rtl: context.rtl,
    unstable_props: props
  });
  const unhandledProps = useUnhandledProps111(FileTile.handledProps, props);
  const ElementType = getElementType88(props);
  const isFileIconTile = variant === "02";
  const result = /* @__PURE__ */ jsxs77(
    ElementType,
    {
      ...getA11yProps("root", {
        className: cx25([classes["root"], classes[`variant${variant}`]]),
        ref,
        ...unhandledProps
      }),
      children: [
        TileImage.create(tileImage, {
          defaultProps: () => {
            return {
              className: classes["image"],
              width: "100%",
              hideFavourite: true,
              allowClickThrough: true,
              topActions,
              ...isFileIconTile && {
                topContent: /* @__PURE__ */ jsx113(
                  Flex,
                  {
                    className: fileTileSlotClassNames.extensionText,
                    children: Caption.create(iconText, {
                      defaultProps: () => {
                        return {
                          variant: "03"
                        };
                      }
                    })
                  }
                )
              }
            };
          },
          overrideProps: () => {
            return {
              ...isFileIconTile && {
                className: mergeClassName(
                  tileImage,
                  fileTileSlotClassNames.fileTileImage
                )
              }
            };
          }
        }),
        /* @__PURE__ */ jsx113(Flex, { className: fileTileSlotClassNames.contentWrap, column: false, children: /* @__PURE__ */ jsx113(Flex, { column: true, children: Caption.create(description, {
          defaultProps: () => {
            return {
              className: fileTileSlotClassNames.description,
              variant: "04",
              maximumLines: 3
            };
          }
        }) }) })
      ]
    }
  );
  setEnd();
  return result;
});
FileTile.displayName = "FileTile";
FileTile.create = createShorthandFactory101({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: FileTile
});
FileTile.defaultProps = {
  as: defaultRootElementType47,
  accessibility: cardBehavior19
};
FileTile.handledProps = [
  ...commonHandledProps(),
  // Extra props that are handled
  "tileImage",
  "size",
  "description",
  "variant",
  ...adminTileHandledProps
];

// packages/aria/src/components/FlagSelector/FlagSelector.tsx
import { useUnhandledProps as useUnhandledProps112 } from "@fluentui/react-bindings";
import {
  getElementType as getElementType89,
  useStyles as useStyles115,
  useTelemetry as useTelemetry114
} from "@fluentui/react-bindings";
import {
  menuBehavior as menuBehavior12,
  useAccessibility as useAccessibility106,
  useFluentContext as useFluentContext116
} from "@fluentui/react-northstar";
import { forwardRef as forwardRef105 } from "react";
import { jsx as jsx114 } from "react/jsx-runtime";
var flagSelectorClassName = "ui-flagSelector";
var flagSelectorSlotClassNames = {
  flag: `${flagSelectorClassName}__flag`
};
var displayName25 = "FlagSelector";
var FlagSelector = forwardRef105(
  (props, ref) => {
    const context = useFluentContext116();
    const { setStart, setEnd } = useTelemetry114(
      displayName25,
      context.telemetry
    );
    setStart();
    const { className, styles, variables, design, flagSlot } = props;
    const iconButtonSize = 40;
    const getA11yProps = useAccessibility106(menuBehavior12, {
      debugName: displayName25,
      rtl: context.rtl
    });
    const { classes } = useStyles115(displayName25, {
      className: flagSelectorClassName,
      mapPropsToInlineStyles: () => ({
        className,
        styles,
        variables,
        design
      }),
      mapPropsToStyles: () => ({}),
      unstable_props: props,
      rtl: context.rtl
    });
    const ElementType = getElementType89(props);
    const unhandledProps = useUnhandledProps112(
      FlagSelector.handledProps,
      props
    );
    const renderFlagSlot = () => {
      return Button.create(flagSlot, {
        generateKey: true,
        defaultProps: () => ({
          className: classes["menuItem"],
          variant: "text",
          size: iconButtonSize,
          variables: {
            buttonPadding: 0,
            iconPadding: 0
          }
        }),
        overrideProps: () => ({
          className: mergeClassName(
            flagSlot,
            flagSelectorSlotClassNames.flag
          )
        })
      });
    };
    const result = /* @__PURE__ */ jsx114(
      ElementType,
      {
        ...getA11yProps("root", {
          className: classes["root"],
          ref,
          ...unhandledProps
        }),
        children: /* @__PURE__ */ jsx114(
          Container_default,
          {
            variables: {
              background: "transparent"
            },
            children: renderFlagSlot()
          }
        )
      }
    );
    setEnd();
    return result;
  }
);
FlagSelector.displayName = displayName25;
FlagSelector.handledProps = [
  "className",
  "styles",
  "variables",
  "design",
  "flagSlot"
];

// packages/aria/src/themes/base/index.ts
import { createTheme as createTheme2 } from "@fluentui/styles";

// packages/aria/src/themes/base/animations/rotate.ts
var rotateAnimations = {
  // Basic Rotate Animation -- Fast
  rotateFast: {
    keyframe: {
      from: {
        transform: "rotate(0deg)"
      },
      to: {
        transform: "rotate(360deg)"
      }
    },
    duration: fast,
    iterationCount: "infinite"
  },
  // Basic Rotate Animation -- Slow
  rotateUltraSlow: {
    keyframe: {
      from: {
        transform: "rotate(0deg)"
      },
      to: {
        transform: "rotate(360deg)"
      }
    },
    duration: "1000ms",
    timingFunction: linear,
    iterationCount: "infinite"
  }
};

// packages/aria/src/themes/base/animations/slide.ts
var slideBase = {
  timingFunction: "cubic-bezier(0.33,0.00,0.67,1.00)",
  fillMode: "forwards"
};
var enterFromRight = {
  ...slideBase,
  keyframe: () => ({
    "0%": {
      transform: `translateX(100%)`
    },
    "100%": {
      transform: `translateX(0)`
    }
  }),
  duration: "0.4s"
};
var exitToRight = {
  ...slideBase,
  keyframe: () => ({
    "0%": {
      position: "absolute",
      transform: `translateX(0px)`
    },
    "100%": {
      position: "absolute",
      transform: `translateX(100%)`
    }
  }),
  duration: "0.4s"
};
var enterFromLeft = {
  ...slideBase,
  keyframe: ({ delta }) => ({
    "0%": {
      transform: `translateX(-100%)`
    },
    "100%": {
      transform: `translateX(${delta}px)`
    }
  }),
  keyframeParams: { delta: "0" },
  duration: "0.4s"
};
var exitToLeft = {
  ...slideBase,
  keyframe: () => ({
    "0%": {
      position: "absolute",
      transform: `translateX(0px)`
    },
    "100%": {
      position: "absolute",
      transform: `translateX(-100%)`
    }
  }),
  duration: "0.4s"
};
var enterFromTop = {
  ...slideBase,
  keyframe: ({ delta }) => ({
    "0%": {
      transform: `translateY(-100%)`
    },
    "100%": {
      transform: `translateY(${delta}px)`
    }
  }),
  keyframeParams: { delta: "0" },
  duration: "0.4s"
};
var exitToTop = {
  ...slideBase,
  keyframe: () => ({
    "0%": {
      transform: `translateY(0px)`,
      position: "absolute"
    },
    "100%": {
      transform: `translateY(-100%)`,
      position: "absolute"
    }
  }),
  duration: "0.4s"
};
var enterFromBottom = {
  ...slideBase,
  keyframe: ({ delta }) => ({
    "0%": {
      transform: `translateY(100%)`
    },
    "100%": {
      transform: `translateY(${delta}px)`
    }
  }),
  keyframeParams: { delta: "0" },
  duration: "0.4s"
};
var exitToBottom = {
  ...slideBase,
  keyframe: () => ({
    "0%": {
      transform: `translateY(0px)`
    },
    "100%": {
      transform: `translateY(100%)`
    }
  }),
  duration: "0.4s"
};
var enterFromBottomToast = {
  ...slideBase,
  keyframe: ({ delta }) => ({
    "0%": {
      transform: `translateY(100%)`,
      opacity: 0
    },
    "100%": {
      transform: `translateY(${delta}px)`,
      opacity: 1
    }
  }),
  keyframeParams: { delta: "0" },
  duration: "0.4s"
};
var exitToBottomToast = {
  ...slideBase,
  keyframe: () => ({
    "0%": {
      transform: `translateY(0px)`,
      opacity: 1
    },
    "100%": {
      transform: `translateY(100%)`,
      opacity: 0
    }
  }),
  duration: "0.4s"
};
var mobileHeaderSectionSlideIn = {
  ...slideBase,
  keyframe: ({ delta }) => ({
    "0%": {
      transform: `translateY(-100%)`,
      position: "absolute"
    },
    "100%": {
      transform: `translateY(${delta}px)`,
      position: "fixed"
    }
  }),
  keyframeParams: { delta: "0" },
  duration: fast,
  timingFunction: "ease"
};
var mobileHeaderSectionSlideOut = {
  ...slideBase,
  keyframe: () => ({
    "0%": {
      transform: `translateY(0px)`,
      position: "fixed"
    },
    "100%": {
      transform: `translateY(-100%)`,
      position: "absolute"
    }
  }),
  keyframeParams: { delta: "0" },
  duration: fast,
  timingFunction: "ease"
};
var slideAnimations = {
  enterFromRight,
  exitToRight,
  enterFromRightSlow: { ...enterFromRight, duration: slow },
  exitToRightSlow: { ...exitToRight, duration: slow },
  enterFromLeft,
  exitToLeft,
  enterFromLeftSlow: { ...enterFromLeft, duration: slow },
  exitToLeftSlow: { ...exitToLeft, duration: slow },
  enterFromTop,
  exitToTop,
  enterFromTopFast: { ...enterFromTop, duration: fast },
  exitToTopFast: { ...exitToTop, duration: fast },
  enterFromTopSlow: { ...enterFromTop, duration: slow },
  exitToTopSlow: { ...exitToTop, duration: slow },
  enterFromBottom,
  exitToBottom,
  enterFromBottomSlow: { ...enterFromBottom, duration: slow },
  exitToBottomSlow: { ...exitToBottom, duration: slow },
  mobileHeaderSectionSlideIn,
  mobileHeaderSectionSlideOut,
  enterFromBottomToast,
  exitToBottomToast
};

// packages/aria/src/themes/base/animations/grow.ts
var growAnimations = {
  growNormal: {
    keyframe: () => ({
      "0%": {
        width: 0
      },
      "100%": {
        width: "100%"
      }
    }),
    keyframeParams: { delta: 0 },
    duration: normal,
    timingFunction: linear,
    fillMode: "forwards"
  },
  growHeight: {
    keyframe: () => ({
      "0%": {
        height: 0
      },
      "100%": {
        height: "100%"
      }
    }),
    keyframeParams: { delta: 0 },
    duration: normal,
    timingFunction: linear,
    fillMode: "forwards"
  }
};

// packages/aria/src/themes/base/animations/pulse.ts
var pulse = {
  from: {
    opacity: 1
  },
  "50%": {
    opacity: 0.5
  },
  to: {
    opacity: 1
  }
};

// packages/aria/src/themes/base/animations/wave.ts
var wave = {
  "0%": {
    backgroundPosition: "-1200px 0"
  },
  "100%": {
    backgroundPosition: "1200px 0"
  }
};

// packages/aria/src/themes/base/animations/growing.ts
var growing = {
  keyframe: {
    from: {
      transform: "scale(1)"
    },
    to: {
      transform: "scale(3.1807, 3.18)",
      transformOrigin: "left 0"
    }
  },
  delay: normal,
  duration: normal,
  timingFunction: linear,
  fillMode: "forwards",
  iterationCount: "1"
};

// packages/aria/src/themes/base/animations/shrinking.ts
var shrinking = {
  keyframe: {
    "0%": {
      transform: "scale(1)"
    },
    "100%": {
      transform: "scale(0.3)",
      transformOrigin: "top left"
    }
  },
  delay: normal,
  keyframeParams: { delta: 0 },
  duration: faster,
  timingFunction: linear,
  fillMode: "forwards",
  iterationCount: "1"
};

// packages/aria/src/themes/base/animations/scale.ts
var scaleUp = {
  keyframe: () => ({
    "0%": {
      transform: "scale(0.5)"
    },
    "100%": {
      transform: "scale(1)"
    }
  }),
  keyframeParams: { delta: 0 },
  duration: normal,
  timingFunction: linear,
  fillMode: "forwards"
};
var scaleDown = {
  keyframe: () => ({
    "0%": {
      transform: "scale(1)"
    },
    "100%": {
      transform: "scale(0.5)"
    }
  }),
  keyframeParams: { delta: 0 },
  duration: normal,
  timingFunction: linear,
  fillMode: "forwards"
};

// packages/aria/src/themes/base/animations/index.ts
var animations = {
  ...fadeInOutAnimations,
  ...rotateAnimations,
  ...slideAnimations,
  ...growAnimations,
  pulse,
  wave,
  growing,
  shrinking,
  scaleUp,
  scaleDown,
  "carousel-slide-to-next-enter": slideAnimations.enterFromRight,
  "carousel-slide-to-previous-enter": slideAnimations.enterFromLeft,
  "carousel-exit-to-prev": slideAnimations.exitToLeft,
  "carousel-exit-to-next": slideAnimations.exitToRight,
  "popup-show": fadeInOutAnimations.fadeEnterUltraFast,
  "popup-hide": fadeInOutAnimations.fadeExitUltraFast
};

// packages/aria/src/themes/base/siteVariables.ts
var siteVariables_exports = {};
__export(siteVariables_exports, {
  bodyBackground: () => bodyBackground,
  bodyColor: () => bodyColor,
  bodyFontFamily: () => bodyFontFamily,
  bodyFontSize: () => bodyFontSize,
  bodyLineHeight: () => bodyLineHeight,
  bodyMargin: () => bodyMargin,
  bodyPadding: () => bodyPadding,
  borderRadiusMedium: () => borderRadiusMedium,
  borderRadiusSmall: () => borderRadiusSmall,
  borderRadiusXLarge: () => borderRadiusXLarge,
  borderWidth: () => borderWidth,
  colorScheme: () => colorScheme,
  colors: () => colors,
  contextualColors: () => contextualColors,
  focusInnerBorderColor: () => focusInnerBorderColor,
  focusOuterBorderColor: () => focusOuterBorderColor,
  fontSizes: () => fontSizes,
  fontWeightBold: () => fontWeightBold,
  fontWeightExtraLight: () => fontWeightExtraLight,
  fontWeightLight: () => fontWeightLight,
  fontWeightRegular: () => fontWeightRegular,
  fontWeightSemiBold: () => fontWeightSemiBold,
  getTextStyle: () => getTextStyle,
  letterSpacingMedium: () => letterSpacingMedium,
  letterSpacingNormal: () => letterSpacingNormal,
  letterSpacingSmall: () => letterSpacingSmall,
  lineHeightDefault: () => lineHeightDefault,
  lineHeightLarge: () => lineHeightLarge,
  lineHeightLarger: () => lineHeightLarger,
  lineHeightLargest: () => lineHeightLargest,
  lineHeightMedium: () => lineHeightMedium,
  lineHeightSmall: () => lineHeightSmall,
  lineHeightSmaller: () => lineHeightSmaller,
  lineHeightSmallest: () => lineHeightSmallest,
  naturalColors: () => naturalColors,
  shadowLevel1: () => shadowLevel1,
  shadowLevel2: () => shadowLevel2,
  shadowLevel3: () => shadowLevel3,
  spaces: () => spaces,
  spaces_raw: () => spaces_raw,
  textStyles: () => textStyles,
  zIndexes: () => zIndexes
});
var white = "#fff";
var black = "#000";
var borderWidth = "1px";
var borderRadiusSmall = "2px";
var borderRadiusMedium = "4px";
var borderRadiusXLarge = "8px";
var focusInnerBorderColor = white;
var focusOuterBorderColor = black;
var shadowLevel1 = `none`;
var shadowLevel2 = `none`;
var shadowLevel3 = `0px 14px 24px 2px rgba(0, 0, 0, 0.08)`;
var fontSizes = {
  heading01: pxToRem(120),
  heading02: pxToRem(77),
  heading03: pxToRem(60),
  heading04: pxToRem(55),
  heading05: pxToRem(42),
  subheading01: pxToRem(36),
  subheading02: pxToRem(32),
  subheading03: pxToRem(30),
  "subheading03-alt": pxToRem(22),
  subheading04: pxToRem(16),
  subheading05: pxToRem(16),
  paragraph01: pxToRem(30),
  label01: pxToRem(24),
  label02: pxToRem(20),
  label03: pxToRem(22),
  "label03-alt": pxToRem(16),
  label04: pxToRem(14),
  caption01: pxToRem(20),
  caption02: pxToRem(18),
  caption03: pxToRem(16),
  caption03s: pxToRem(15),
  "caption03-alt": pxToRem(16),
  caption04: pxToRem(14),
  caption05: pxToRem(12),
  "caption05-alt": pxToRem(12),
  caption06: pxToRem(10)
};
var fontWeightExtraLight = 300;
var fontWeightLight = 400;
var fontWeightRegular = 500;
var fontWeightSemiBold = 600;
var fontWeightBold = 700;
var fontFamilyNeueHaasGroteskText = `'neue-haas-grotesk-text', sans-serif`;
var fontFamilyNeueHaasGroteskDisplay = `'neue-haas-grotesk-display', sans-serif`;
var fontFamilyGesturaHeadline = `Gestura Headline Regular`;
var fontFamilyGTAmerica = `'GT America', sans-serif`;
var fontFamilyGTAmericaTrial = `'GT America Trial', sans-serif`;
var fontFamilyGTAmericaTrialLight = `'GT America Trial Light', sans-serif`;
var fontFamilyGTAmericaTrialRegular = `'GT America Trial Regular', sans-serif`;
var textStyles = {
  Text: {
    Roman55: {
      fontFamily: fontFamilyNeueHaasGroteskText,
      fontWeight: fontWeightLight
    },
    Medium65: {
      fontFamily: fontFamilyNeueHaasGroteskText,
      fontWeight: fontWeightRegular
    },
    Bold75: {
      fontFamily: fontFamilyNeueHaasGroteskText,
      fontWeight: fontWeightBold
    }
  },
  Display: {
    Light45: {
      fontFamily: fontFamilyNeueHaasGroteskDisplay,
      fontWeight: fontWeightLight
    },
    Roman55: {
      fontFamily: fontFamilyNeueHaasGroteskDisplay,
      fontWeight: fontWeightRegular
    },
    Medium65: {
      fontFamily: fontFamilyNeueHaasGroteskDisplay,
      fontWeight: fontWeightSemiBold
    },
    Bold75: {
      fontFamily: fontFamilyNeueHaasGroteskDisplay,
      fontWeight: fontWeightBold
    }
  },
  Article: {
    Roman55: {
      fontFamily: fontFamilyGesturaHeadline,
      fontWeight: fontWeightLight
    },
    Medium65: {
      fontFamily: fontFamilyGesturaHeadline,
      fontWeight: fontWeightRegular
    },
    Bold75: {
      fontFamily: fontFamilyGesturaHeadline,
      fontWeight: fontWeightBold
    }
  },
  Heading: {
    Roman55: {
      fontFamily: fontFamilyGTAmerica,
      fontWeight: fontWeightLight
    },
    Medium65: {
      fontFamily: fontFamilyGTAmerica,
      fontWeight: fontWeightRegular
    },
    Bold75: {
      fontFamily: fontFamilyGTAmerica,
      fontWeight: fontWeightBold
    }
  },
  GTAmericaTrialFont: {
    Light45: {
      fontFamily: fontFamilyGTAmericaTrialLight,
      fontWeight: fontWeightExtraLight
    },
    Roman55: {
      fontFamily: fontFamilyGTAmericaTrialRegular,
      fontWeight: fontWeightRegular
    },
    Medium65: {
      fontFamily: fontFamilyGTAmericaTrialRegular,
      fontWeight: fontWeightRegular
    },
    Bold75: {
      fontFamily: fontFamilyGTAmericaTrial,
      fontWeight: fontWeightRegular
    }
  }
};
var getTextStyle = (style, baseFont) => {
  return textStyles[baseFont][style];
};
var lineHeightDefault = 1;
var lineHeightSmallest = 1.1;
var lineHeightSmaller = 1.2;
var lineHeightSmall = 1.3;
var lineHeightMedium = 1.3333;
var lineHeightLarge = 1.4286;
var lineHeightLarger = 1.3333;
var lineHeightLargest = 1.3333;
var letterSpacingNormal = "0";
var letterSpacingSmall = "-0.01em";
var letterSpacingMedium = "-0.02em";
var spaces_old = [
  6,
  10,
  20,
  44,
  50,
  60,
  90,
  130,
  160,
  190,
  210,
  240
];
var spaces_new = [
  4,
  6,
  8,
  12,
  16,
  24,
  28,
  32,
  36,
  40,
  48,
  56,
  64,
  71,
  80,
  88,
  96,
  144,
  244
];
var spaces_raw = [...spaces_old, ...spaces_new];
var spaces = spaces_raw.reduce((acc, curr) => {
  acc[`space-${curr}`] = pxToRem(curr);
  return acc;
}, {});
var zIndexes = {
  background: 0,
  // Default value
  foreground: 1,
  // Put a component in front
  menuItem: 2,
  // Currently used only for menu item beak element
  imageOverlay: 10,
  topNav: 120,
  overlay: 140,
  // Dialog/popup/menu overlays
  overlayPriority: 141,
  // for nested overlays, like tooltip in dialog.
  debug: 999999999
  // for debug purposes
};
var bodyPadding = 0;
var bodyMargin = 0;
var bodyFontFamily = `'neue-haas-grotesk-text', sans-serif`;
var bodyFontSize = pxToRem(14);
var bodyBackground = "#ffffff";
var bodyColor = colors.charcoal[250];
var bodyLineHeight = lineHeightMedium;

// packages/aria/src/themes/base/componentVariables.ts
var componentVariables_exports = {};
__export(componentVariables_exports, {
  Accordion: () => accordionVariables,
  AdminCaption: () => adminCaptionVariables,
  AppMenu: () => appMenuVariables,
  ArchizeenTile: () => archizeenTileVariables,
  ArticleTile: () => articleTileVariables,
  Avatar: () => avatarVariables,
  Badge: () => badgeVariables,
  BannerAdTile: () => bannerAdTileVariables,
  BottomNav: () => bottomNavVariables,
  Box: () => boxVariables,
  Breadcrumb: () => breadcrumbVariables,
  BreadcrumbDivider: () => breadcrumbDividerVariables,
  BreadcrumbItem: () => breadcrumbItemVariables,
  BreadcrumbNavigation: () => breadcrumbNavigationVariables,
  BusinessProfile: () => businessProfileVariables,
  Button: () => buttonVariables,
  Caption: () => captionVariables,
  Carousel: () => carouselVariables,
  CarouselIndicator: () => carouselIndicatorVariables,
  CarouselItem: () => carouselItemVariables,
  CarouselNavigation: () => carouselVariables,
  CarouselNavigationItem: () => carouselVariables,
  CarouselOverlay: () => carouselVariables,
  CarouselPaddle: () => carouselVariables,
  CarouselPaddlesContainer: () => carouselPaddlesContainerVariables,
  CategoryList: () => categoryListVariables,
  CategorySelector: () => categorySelectorVariables,
  CategoryTree: () => categoryTreeVariables,
  CategoryTreeHeader: () => categoryTreeHeaderVariables,
  Checkbox: () => checkboxVariables,
  CheckboxGroup: () => checkboxGroupVariables,
  Container: () => containerVariables,
  Dialog: () => dialogVariables,
  Divider: () => dividerVariables,
  Drawer: () => drawerVariables,
  Dropdown: () => dropdownVariables,
  DropdownItem: () => dropdownVariables,
  DropdownMenu: () => dropdownMenuVariables,
  DropdownMenuOutlineButtonTop: () => dropdownMenuOutlineButtonTopVariables,
  DropdownMenuOutlineOption: () => dropdownMenuOutlineOptionVariables,
  DropdownMenuOutlineOptionsList: () => dropdownMenuOutlineOptionsListVariables,
  DropdownSort: () => dropdownSortVariables,
  EnquiryModal: () => enquiryModalVariables,
  FeaturedArticle: () => featuredArticleVariables,
  FeaturedTile: () => featuredTileVariables,
  FeaturedTileSkeleton: () => featuredTileSkeletonVariables,
  Flex: () => flexVariables,
  FlexItem: () => flexItemVariables,
  FooterNavigation: () => footerNavigationVariables,
  FooterNavigationItem: () => footerNavigationItemVariables,
  Grid: () => gridVariables,
  Header: () => headerVariables,
  HotButtons: () => hotButtonsVariables,
  Image: () => imageVariables,
  ImageOverlay: () => imageOverlayVariables,
  IndicatorCheckbox: () => indicatorCheckboxVariables,
  IndicatorColor: () => indicatorColorVariables,
  IndicatorRadio: () => indicatorRadioVariables,
  Input: () => inputVariables,
  Label: () => labelVariables,
  List: () => listVariables,
  ListItem: () => listItemVariables,
  Loader: () => loaderVariables,
  LoginModal: () => loginModalVariables,
  Logo: () => logoVariables,
  MegaMenu: () => megaMenuVariables,
  MemberTile: () => memberTileVariables,
  MessageTooltipContent: () => messageTooltipContentVariables,
  MobileHeaderSection: () => mobileHeaderSectionVariables,
  MultiSlider: () => multiSliderVariables,
  NavigationItem: () => navigationItemVariables,
  NavigationList: () => navigationListVariables,
  NavigationListGroup: () => navigationListGroupVariables,
  NavigationListSkeleton: () => navigationListSkeletonVariables,
  NavigationMenu: () => navigationMenuVariables,
  NumberInput: () => numberInputVariables,
  Pagination: () => paginationVariables,
  PaginationItem: () => paginationItemVariables,
  PaginationItemContent: () => paginationItemContentVariables,
  PaginationItemCounter: () => paginationItemCounterVariables,
  PaginationItemsPerPage: () => paginationItemsPerPageVariables,
  Paragraph: () => paragraphVariables,
  PersonalMenu: () => personalMenuVariables,
  PersonalProfile: () => personalProfileVariables,
  PersonalProfileMenu: () => personalProfileMenuVariables,
  Popup: () => popupVariables,
  PopupContent: () => popupContentVariables,
  ProductTile: () => productTileVariables,
  ProfessionalTile: () => professionalTileVariables,
  ProfileMenuItem: () => profileMenuItemVariables,
  ProfileNavigation: () => profileNavigationVariables,
  ProjectTile: () => projectTileVariables,
  RangeInput: () => rangeInputVariables,
  RangeSelector: () => rangeSelectorVariables,
  ReadMore: () => readMoreVariables,
  Review: () => reviewVariables,
  SearchBar: () => searchBarVariables,
  SearchMenu: () => searchMenuVariables,
  SearchResult: () => searchResultVariables,
  SearchableDropdown: () => searchableDropdownVariables,
  SearchableList: () => searchableListVariables,
  Skeleton: () => skeletonVariables,
  SkeletonLine: () => skeletonVariables,
  Subheader: () => subheaderVariables,
  Subscription: () => subscriptionVariables,
  SvgIcon: () => svgIconVariables,
  SwipeableEdge: () => swipeableEdgeVariables,
  Switch: () => switchVariables,
  Tabs: () => tabsVariables,
  TextInput: () => textInputVariables,
  TextSelectorInput: () => textSelectorInputVariables,
  TileGrid: () => tileGridVariables,
  TileImage: () => tileImageVariables,
  TileOverlay: () => tileOverlayVariables,
  TileVideo: () => tileVideoVariables,
  ToastMessage: () => toastMessageVariables,
  TopNavigation: () => topNavigationVariables,
  VariantColor: () => variantColorVariables,
  VideoPlayer: () => videoPlayerVariables,
  VirtualList: () => virtualListVariables,
  VirtualListItem: () => virtualListItemVariables
});

// packages/aria/src/themes/base/components/Button/buttonVariables.ts
var buttonVariables = (siteVars) => ({
  primaryButtonTextColorNormal: siteVars.colors.white["100A"],
  primaryButtonTextColorHover: siteVars.colors.white["100A"],
  primaryButtonTextColorSelected: siteVars.colors.white["100A"],
  primaryButtonTextColorDisable: siteVars.colors.grey[200],
  primaryButtonBackgroundColorNormal: siteVars.colors.charcoal[250],
  primaryButtonBackgroundColorHover: siteVars.colors.charcoal[260],
  primaryButtonBackgroundColorSelected: siteVars.colors.charcoal[260],
  primaryButtonBackgroundColorDisable: siteVars.colors.charcoal[200],
  primaryButtonBorderNormal: "none",
  primaryButtonBorderHover: "none",
  primaryButtonBorderSelected: "none",
  primaryButtonBorderDisable: "none",
  primaryNoBKGButtonTextColorNormal: siteVars.colors.charcoal[250],
  primaryNoBKGButtonTextColorHover: siteVars.colors.primitive.black,
  primaryNoBKGButtonTextColorSelected: siteVars.colors.primitive.black,
  primaryNoBKGButtonTextColorDisable: siteVars.colors.grey[200],
  primaryNoBKGButtonBackgroundColorNormal: "transparent",
  primaryNoBKGButtonBackgroundColorHover: "rgba(0, 0, 0, 0.06)",
  primaryNoBKGButtonBackgroundColorSelected: "rgba(0, 0, 0, 0.06)",
  primaryNoBKGButtonBackgroundColorDisable: "transparent",
  primaryNoBKGButtonBorderNormal: "none",
  primaryNoBKGButtonBorderHover: "none",
  primaryNoBKGButtonBorderSelected: "none",
  primaryNoBKGButtonBorderDisable: "none",
  primaryOutlinedButtonTextColorNormal: siteVars.colors.primitive.black,
  primaryOutlinedButtonTextColorHover: siteVars.colors.white["FFF"],
  primaryOutlinedButtonTextColorSelected: siteVars.colors.white["FFF"],
  primaryOutlinedButtonTextColorDisable: siteVars.colors.grey[200],
  primaryOutlinedButtonBackgroundColorNormal: "transparent",
  primaryOutlinedButtonBackgroundColorHover: siteVars.colors.charcoal[250],
  primaryOutlinedButtonBackgroundColorSelected: siteVars.colors.charcoal[250],
  primaryOutlinedButtonBackgroundColorDisable: "transparent",
  primaryOutlinedButtonBorderNormal: `1px solid ${siteVars.colors.transparency.black["10%"]}`,
  primaryOutlinedButtonBorderHover: `1px solid transparent`,
  primaryOutlinedButtonBorderSelected: `1px solid transparent`,
  primaryOutlinedButtonBorderDisable: `1px solid ${siteVars.colors.transparency.black["10%"]}`,
  darkButtonTextColorNormal: siteVars.colors.white["100A"],
  darkButtonTextColorHover: siteVars.colors.white["100A"],
  darkButtonTextColorSelected: siteVars.colors.white["100A"],
  darkButtonTextColorDisable: siteVars.colors.grey[200],
  darkButtonBackgroundColorNormal: siteVars.colors.charcoal[250],
  darkButtonBackgroundColorHover: siteVars.colors.charcoal[260],
  darkButtonBackgroundColorSelected: siteVars.colors.charcoal[260],
  darkButtonBackgroundColorDisable: siteVars.colors.charcoal[200],
  darkButtonBorderNormal: "none",
  darkButtonBorderHover: "none",
  darkButtonBorderSelected: "none",
  darkButtonBorderDisable: "none",
  darkNoBKGButtonTextColorNormal: siteVars.colors.charcoal[250],
  darkNoBKGButtonTextColorHover: siteVars.colors.primitive.black,
  darkNoBKGButtonTextColorSelected: siteVars.colors.primitive.black,
  darkNoBKGButtonTextColorDisable: siteVars.colors.grey[200],
  darkNoBKGButtonBackgroundColorNormal: "transparent",
  darkNoBKGButtonBackgroundColorHover: "rgba(0, 0, 0, 0.06)",
  darkNoBKGButtonBackgroundColorSelected: "rgba(0, 0, 0, 0.06)",
  darkNoBKGButtonBackgroundColorDisable: "transparent",
  darkNoBKGButtonBorderNormal: "none",
  darkNoBKGButtonBorderHover: "none",
  darkNoBKGButtonBorderSelected: "none",
  darkNoBKGButtonBorderDisable: "none",
  darkOutlinedButtonTextColorNormal: siteVars.colors.primitive.black,
  darkOutlinedButtonTextColorHover: siteVars.colors.white["FFF"],
  darkOutlinedButtonTextColorSelected: siteVars.colors.white["FFF"],
  darkOutlinedButtonTextColorDisable: siteVars.colors.grey[200],
  darkOutlinedButtonBackgroundColorNormal: "transparent",
  darkOutlinedButtonBackgroundColorHover: siteVars.colors.charcoal[250],
  darkOutlinedButtonBackgroundColorSelected: siteVars.colors.charcoal[250],
  darkOutlinedButtonBackgroundColorDisable: "transparent",
  darkOutlinedButtonBorderNormal: `1px solid ${siteVars.colors.transparency.black["10%"]}`,
  darkOutlinedButtonBorderHover: `1px solid transparent`,
  darkOutlinedButtonBorderSelected: `1px solid transparent`,
  darkOutlinedButtonBorderDisable: `1px solid ${siteVars.colors.transparency.black["10%"]}`,
  greyButtonTextColorNormal: siteVars.colors.charcoal[250],
  greyButtonTextColorHover: siteVars.colors.charcoal[250],
  greyButtonTextColorSelected: siteVars.colors.charcoal[250],
  greyButtonTextColorDisable: siteVars.colors.grey[300],
  greyButtonBackgroundColorNormal: siteVars.colors.white[300],
  greyButtonBackgroundColorHover: siteVars.colors.white[250],
  greyButtonBackgroundColorSelected: siteVars.colors.white[250],
  greyButtonBackgroundColorDisable: siteVars.colors.white[300],
  greyButtonBorderNormal: "none",
  greyButtonBorderHover: "none",
  greyButtonBorderSelected: "none",
  greyButtonBorderDisable: "none",
  greyNoBKGButtonTextColorNormal: siteVars.colors.grey[300],
  greyNoBKGButtonTextColorHover: siteVars.colors.charcoal[250],
  greyNoBKGButtonTextColorSelected: siteVars.colors.charcoal[250],
  greyNoBKGButtonTextColorDisable: siteVars.colors.grey[200],
  greyNoBKGButtonBackgroundColorNormal: "transparent",
  greyNoBKGButtonBackgroundColorHover: "rgba(0, 0, 0, 0.06)",
  greyNoBKGButtonBackgroundColorSelected: "rgba(0, 0, 0, 0.06)",
  greyNoBKGButtonBackgroundColorDisable: "transparent",
  greyNoBKGButtonBorderNormal: "none",
  greyNoBKGButtonBorderHover: "none",
  greyNoBKGButtonBorderSelected: "none",
  greyNoBKGButtonBorderDisable: "none",
  greyOutlinedButtonTextColorNormal: siteVars.colors.grey[300],
  greyOutlinedButtonTextColorHover: siteVars.colors.charcoal[250],
  greyOutlinedButtonTextColorSelected: siteVars.colors.charcoal[250],
  greyOutlinedButtonTextColorDisable: siteVars.colors.grey[200],
  greyOutlinedButtonBackgroundColorNormal: "transparent",
  greyOutlinedButtonBackgroundColorHover: "rgba(0, 0, 0, 0.06)",
  greyOutlinedButtonBackgroundColorSelected: "rgba(0, 0, 0, 0.06)",
  greyOutlinedButtonBackgroundColorDisable: "transparent",
  greyOutlinedButtonBorderNormal: `1px solid ${siteVars.colors.transparency.black["10%"]}`,
  greyOutlinedButtonBorderHover: `1px solid transparent`,
  greyOutlinedButtonBorderSelected: `1px solid transparent`,
  greyOutlinedButtonBorderDisable: `1px solid ${siteVars.colors.transparency.black["10%"]}`,
  whiteButtonTextColorNormal: siteVars.colors.charcoal[250],
  whiteButtonTextColorHover: siteVars.colors.charcoal[250],
  whiteButtonTextColorSelected: siteVars.colors.charcoal[250],
  whiteButtonTextColorDisable: siteVars.colors.grey[200],
  whiteButtonBackgroundColorNormal: siteVars.colors.primitive.white,
  whiteButtonBackgroundColorHover: siteVars.colors.white[100],
  whiteButtonBackgroundColorSelected: siteVars.colors.white[100],
  whiteButtonBackgroundColorDisable: siteVars.colors.white["000"],
  whiteButtonBorderNormal: "none",
  whiteButtonBorderHover: "none",
  whiteButtonBorderSelected: "none",
  whiteButtonBorderDisable: "none",
  whiteNoBKGButtonTextColorNormal: siteVars.colors.white["000"],
  whiteNoBKGButtonTextColorHover: siteVars.colors.white["FFF"],
  whiteNoBKGButtonTextColorSelected: siteVars.colors.white["FFF"],
  whiteNoBKGButtonTextColorDisable: siteVars.colors.grey[200],
  whiteNoBKGButtonBackgroundColorNormal: "transparent",
  whiteNoBKGButtonBackgroundColorHover: "rgba(255, 255, 255, 0.1)",
  whiteNoBKGButtonBackgroundColorSelected: "rgba(255, 255, 255, 0.1)",
  whiteNoBKGButtonBackgroundColorDisable: "transparent",
  whiteNoBKGButtonBorderNormal: "none",
  whiteNoBKGButtonBorderHover: "none",
  whiteNoBKGButtonBorderSelected: "none",
  whiteNoBKGButtonBorderDisable: "none",
  whiteOutlinedButtonTextColorNormal: siteVars.colors.white["FFF"],
  whiteOutlinedButtonTextColorHover: siteVars.colors.charcoal[250],
  whiteOutlinedButtonTextColorSelected: siteVars.colors.charcoal[250],
  whiteOutlinedButtonTextColorDisable: siteVars.colors.grey[100],
  whiteOutlinedButtonBackgroundColorNormal: "transparent",
  whiteOutlinedButtonBackgroundColorHover: siteVars.colors.white["FFF"],
  whiteOutlinedButtonBackgroundColorSelected: siteVars.colors.white["FFF"],
  whiteOutlinedButtonBackgroundColorDisable: "transparent",
  whiteOutlinedButtonBorderNormal: `1px solid ${siteVars.colors.transparency.white["10%"]}`,
  whiteOutlinedButtonBorderHover: `1px solid transparent`,
  whiteOutlinedButtonBorderSelected: `1px solid transparent`,
  whiteOutlinedButtonBorderDisable: `1px solid ${siteVars.colors.transparency.white["20%"]}`,
  outlinedBorderWidth: "1px",
  outlinedBorderStyle: "solid",
  primaryButtonIconXMargin: pxToRem(0),
  buttonPadding: pxToRem(16),
  iconPadding: 4,
  primaryButtonXPadding: pxToRem(36),
  primaryButtonYPadding: pxToRem(14),
  buttonMinWidth: void 0,
  buttonSize36TextFontSize: pxToRem(36),
  buttonSize36LineHeight: pxToRem(36),
  buttonSize36LetterSpacing: siteVars.letterSpacingNormal,
  buttonSize36IconXMargin: pxToRem(8),
  buttonSize30TextFontSize: pxToRem(30),
  buttonSize30LineHeight: pxToRem(30),
  buttonSize30LetterSpacing: siteVars.letterSpacingSmall,
  buttonSize30IconXMargin: pxToRem(8),
  buttonSize24TextFontSize: pxToRem(24),
  buttonSize24LineHeight: pxToRem(26),
  buttonSize24LetterSpacing: siteVars.letterSpacingMedium,
  buttonSize24IconXMargin: pxToRem(8),
  buttonSize20TextFontSize: pxToRem(20),
  buttonSize20LineHeight: pxToRem(26),
  buttonSize20LetterSpacing: siteVars.letterSpacingNormal,
  buttonSize20IconXMargin: pxToRem(4),
  buttonSize18TextFontSize: pxToRem(18),
  buttonSize18LineHeight: pxToRem(18),
  buttonSize18LetterSpacing: siteVars.letterSpacingNormal,
  buttonSize18IconXMargin: pxToRem(4),
  buttonSize16TextFontSize: pxToRem(16),
  buttonSize16LineHeight: pxToRem(16),
  buttonSize16LetterSpacing: siteVars.letterSpacingNormal,
  buttonSize16IconXMargin: pxToRem(4),
  buttonSize14TextFontSize: pxToRem(14),
  buttonSize14LineHeight: pxToRem(14),
  buttonSize14LetterSpacing: siteVars.letterSpacingNormal,
  buttonSize14IconXMargin: pxToRem(4),
  buttonRadius: "unset"
});

// packages/aria/src/themes/base/components/Typography/Header/headerVariables.ts
var headerVariables = (siteVars) => {
  return {
    color: siteVars.colors.charcoal[250]
  };
};

// packages/aria/src/themes/base/components/Typography/Subheader/subheaderVariables.ts
var subheaderVariables = (siteVars) => ({
  color: siteVars.colors.charcoal["250"]
});

// packages/aria/src/themes/base/components/Typography/Paragraph/paragraphVariables.ts
var paragraphVariables = (siteVars) => ({ color: siteVars.colors.charcoal["250"] });

// packages/aria/src/themes/base/components/Typography/Label/labelVariables.ts
var labelVariables = (siteVars) => ({
  color: siteVars.colors.charcoal[250]
});

// packages/aria/src/themes/base/components/TopNavigation/topNavigationVariables.ts
var topNavigationVariables = (siteVars) => ({
  topNavigationBackgroundColor: siteVars.colors.white["100A"],
  topNavigationExpandedMenuBackgroundColor: siteVars.colors.primitive.white,
  topNavigationExpandedSvgColor: siteVars.colors.charcoal[250],
  topNavigationZIndex: siteVars.zIndexes.topNav,
  menuItemDividerColor: siteVars.colors["grey"][300],
  menuItemIconColor: siteVars.colors.charcoal[250],
  topNavigationTopRightColor: siteVars.colors.charcoal[250],
  // Mobile
  topNavigationMobilePadding: pxArrayToRem([13, 12]),
  topNavigationMobileWithShortLogoHeight: pxToRem(
    MOBILE_TOP_NAV_HEIGHT_SHORT_LOGO
  ),
  topNavigationMobileWithFullLogoHeight: pxToRem(
    MOBILE_TOP_NAV_HEIGHT_FULL_LOGO
  ),
  mobileSearchPlaceholderColor: siteVars.colors.grey[300],
  mobileSearchPlaceholderInputColor: siteVars.colors.charcoal[250],
  topNavigationMobileLogoWidth: pxToRem(82.95),
  topNavigationMobileLogoHeight: pxToRem(75),
  topNavigationMobileExpandedLogoWidth: pxToRem(143),
  topNavigationMobileExpandedLogoHeight: pxToRem(26),
  mobileRightSizeButtonHeightFullLogo: pxToRem(50),
  mobileRightSizeButtonHeight: pxToRem(40),
  // Desktop
  topNavigationDesktopPadding: pxArrayToRem([34, 50, 0, 50]),
  topNavigationDesktopHeight: pxToRem(DESKTOP_TOP_NAV_HEIGHT_SHORT_LOGO),
  topNavigationDesktopFullHeight: pxToRem(DESKTOP_TOP_NAV_HEIGHT_FULL_LOGO),
  topNavigationDesktopHasSecondaryNavHeight: pxToRem(
    DESKTOP_TOP_NAV_HEIGHT_WITH_SECOND_NAV
  ),
  topNavigationDesktopLogoHeight: pxToRem(150),
  topNavigationDesktopLogoWidth: pxToRem(165.4),
  topNavigationDesktopFullLogoHeight: pxToRem(45),
  topNavigationDesktopFullLogoWidth: pxToRem(266),
  topNavigationDesktopItemsGap: pxToRem(91),
  rightSizeButtonHeight: pxToRem(48),
  // Tablet
  tabletPadding: pxArrayToRem([30, 16, 46, 16]),
  tabletHeight: pxToRem(TABLET_TOP_NAV_HEIGHT),
  tabletFullLogoWidth: pxToRem(120),
  tabletFullLogoHeight: pxToRem(24.91),
  // animation
  searchBarExpandDelay: `.1s`,
  searchBarAnimationDuration: `.25s`,
  searchBarMenuFadeInDelay: `.35s`,
  searchBarCollapseDelay: `.2s`,
  menuItemsRevealDelay: `.3s`,
  megaMenuExpandDelay: `.1s`,
  megaMenuAnimationDuration: `.2s`,
  megaMenuExpandedHeight: pxToRem(570),
  secondaryNavButtonSize: pxToRem(16),
  secondaryNavButtonGap: pxToRem(19),
  secondaryNavButtonPadding: pxArrayToRem([10, 0]),
  secondaryNavButtonActivePadding: pxArrayToRem([10, 10]),
  secondaryNavContainerPadding: "0",
  appMenuContainerPadding: pxArrayToRem([0, 0]),
  appMenuTopOffset: pxToRem(MOBILE_TOP_NAV_HEIGHT_FULL_LOGO)
});

// packages/aria/src/themes/base/components/Logo/logoVariables.ts
var logoVariables = (siteVars) => ({
  logoPrimaryColor: siteVars.colors.charcoal[250],
  logoWidth: pxToRem(165.4),
  logoHeight: pxToRem(150),
  animatedFullLogoWidth: pxToRem(286),
  animatedShortLogoWidth: pxToRem(52),
  animatedShortLogoHeight: pxToRem(159),
  animatedMobileShortLogoHeight: pxToRem(76),
  animatedMobileShortLogoWidth: pxToRem(82),
  logoSize: {
    width: 165.4,
    height: 150
  }
});

// packages/aria/src/themes/base/components/TopNavigation/navigationItemVariables.ts
var navigationItemVariables = (siteVars) => ({
  topNavNavigationItemColor: siteVars.colorScheme.default?.["foreground2"],
  topNavNavigationItemWeight: 500,
  topNavNavigationItemPrefixColor: siteVars.colorScheme.default?.["foreground2"]
});

// packages/aria/src/themes/base/components/Tile/ProjectTile/projectTileVariables.ts
var projectTileVariables = (siteVars) => ({
  color: siteVars.colorScheme.default?.["foreground1"],
  paginationPadding: 140,
  tilePadding: pxArrayToRem([14, 9, 14, 9]),
  width: "100%",
  height: "100%",
  dynamicSizingWidth: 330,
  dynamicSizingGapWidth: 10,
  contentTilePadding: 0,
  borderRadius: "0.625rem"
});

// packages/aria/src/themes/base/components/Image/imageVariables.ts
var imageVariables = (siteVars) => {
  const colorScheme7 = siteVars.colorScheme.default;
  return {
    placeholderBackgroundColorLight: colorScheme7.placeholder,
    placeholderBackgroundColorDark: colorScheme7.placeholderAlt,
    overlayBackgroundColor: `rgba(31, 31, 33, 0.5)`,
    overlayIconColor: siteVars.colors.white["FFF"],
    overlayFadeSpeed: "0.3s",
    overlayIconSizeDesktop: 56,
    overlayIconSizeMobile: 34,
    mixBlendMode: "normal",
    borderRadius: "0"
  };
};

// packages/aria/src/themes/base/components/Typography/Caption/captionVariables.ts
var captionVariables = (siteVars) => ({
  captionColor: siteVars.colors.charcoal["250"]
});

// packages/aria/src/themes/base/components/Grid/gridVariables.ts
var gridVariables = () => ({
  defaultColumnCount: 5,
  gridGap: void 0,
  padding: void 0
});

// packages/aria/src/themes/base/components/Flex/flexVariables.ts
var flexVariables = () => ({});

// packages/aria/src/themes/base/components/Flex/flexItemVariables.ts
var flexItemVariables = () => ({
  "size.half": "50%",
  "size.quarter": "25%",
  "size.small": pxToRem(150),
  "size.medium": pxToRem(200),
  "size.large": pxToRem(300)
});

// packages/aria/src/themes/base/components/Box/boxVariables.ts
var boxVariables = () => ({
  boxBackgroundColor: "transparent"
});

// packages/aria/src/themes/base/components/Tile/ArticleTile/articleTileVariables.ts
var articleTileVariables = () => ({
  width: "100%",
  height: "100%"
});

// packages/aria/src/themes/base/components/Tile/ProfessionalTile/professionalTileVariables.ts
var professionalTileVariables = (siteVars) => ({
  color: siteVars.colorScheme.default?.["foreground1"],
  tilePadding: pxToRem(10),
  width: "100%",
  height: "100%",
  dynamicSizingWidth: 434,
  dynamicSizingGapWidth: 10,
  avatarYMargin: 26
});

// packages/aria/src/themes/base/components/Avatar/avatarVariables.ts
var avatarVariables = (siteVars) => ({
  avatarBackgroundColor: siteVars.colors.charcoal["250"],
  avatarImageBackgroundColor: siteVars.colors.primitive.white,
  showBorder: true,
  borderRadius: "50%",
  borderWidth: "1px",
  initialsColor: siteVars.colors.white["000"],
  avatarPadding: "0"
});

// packages/aria/src/themes/base/components/Tile/ProductTile/productTileVariables.ts
var productTileVariables = (siteVars) => ({
  color: siteVars.colorScheme.default?.["foreground1"],
  paginationPadding: 85,
  tilePadding: pxToRem(10),
  colorVariantSize: pxToRem(13),
  colorVariantMobileSize: pxToRem(9),
  width: "100%",
  height: "100%",
  dynamicSizingWidth: 330,
  dynamicSizingGapWidth: 10
});

// packages/aria/src/themes/base/components/Tile/TileOverlay/tileOverlayVariables.ts
var tileOverlayVariables = (siteVars) => ({
  tileOverlayBackgroundColor: siteVars.colors.gold[100],
  padding: pxToRem(16)
});

// packages/aria/src/themes/base/components/VideoPlayer/videoPlayerVariables.ts
var videoPlayerVariables = (siteVars) => ({
  playIconColor: siteVars.colors.white["000"],
  modalIconColour: siteVars.colors.white["300"],
  videoCoverFadeSpeed: "0.3s",
  playIconSizeDesktop: 190,
  playIconSizeMobile: 34,
  closeIconSize: 30,
  coverImagePosition: "absolute"
});

// packages/aria/src/themes/base/components/SvgIcon/svgIconVariables.ts
var svgIconVariables = (siteVars) => ({
  color: void 0,
  backgroundColor: void 0,
  disabledColor: siteVars.colors.grey[250],
  horizontalSpace: void 0,
  "1xSize": pxToRem(24),
  "2xSize": pxToRem(32)
});

// packages/aria/src/themes/base/components/FooterNavigation/footerNavigationVariables.ts
var footerNavigationVariables = (siteVars) => ({
  footerNavigationBackgroundColor: siteVars.colors.charcoal["260"],
  footerCopyrightLinksPrimaryColor: hex2rgba(
    siteVars.colors.white["FFF"],
    0.5
  ),
  footerCopyrightDividerColor: hex2rgba(siteVars.colors.white["FFF"], 0.5),
  footerNavigationLinksPrimaryColor: hex2rgba(
    siteVars.colors.white["FFF"],
    0.8
  ),
  footerLogoColor: siteVars.colors.white["000"],
  footerBorderColor: hex2rgba(siteVars.colors.white["000"], 0.2),
  footerSVGColor: siteVars.colors.white["000"],
  footerTextColor: siteVars.colors.white["FFF"]
});

// packages/aria/src/themes/base/components/FooterNavigation/footerNavigationItemVariables.ts
var footerNavigationItemVariables = (siteVars) => ({
  footerNavigationItemBackgroundColor: siteVars.colors.charcoal[250],
  footerNavigationLinksPrimaryColor: hex2rgba(
    siteVars.colors.white["FFF"],
    0.8
  )
});

// packages/aria/src/themes/base/components/Subscription/subscriptionVariables.ts
var subscriptionVariables = (siteVars) => ({
  subscriptionBackgroundColor: siteVars.colors.charcoal["250"],
  subscriptionButtonBackgroundColor: "transparent",
  subscriptionButtonPrimaryTextColor: siteVars.colors.charcoal["250"],
  subscriptionErrorMessageColor: siteVars.colors.danger[200],
  primaryTextColor: siteVars.colors.white["000"],
  placeholderTextColor: siteVars.colors.white["000"],
  textBorderBottomColor: siteVars.colors.white["000"],
  textInputWidth: pxToRem(310)
});

// packages/aria/src/themes/base/components/List/listVariables.ts
var listVariables = (siteVars) => ({
  listBorderColor: siteVars.colors.charcoal[250],
  listBorderStyle: "solid",
  listBorderWidth: "1px"
});

// packages/aria/src/themes/base/components/List/listItemVariables.ts
var listItemVariables = (siteVars) => ({
  listItemBorderColor: siteVars.colors.charcoal[250],
  listItemTextColor: siteVars.colors.charcoal[250],
  listItemHoverTextColor: siteVars.colors.charcoal[300],
  listItemIconColor: siteVars.colors.charcoal[250],
  listItemIconHoverColor: siteVars.colors.charcoal[300]
});

// packages/aria/src/themes/base/components/Profile/PersonalProfile/personalProfileVariables.ts
var personalProfileVariables = (siteVars) => ({
  personalProfileViewProfileLabelColor: siteVars.colors.charcoal[250],
  avatarBackgroundColor: siteVars.colors.charcoal[250],
  avatarInitialColor: siteVars.colors.white["FFF"],
  fullNameColor: siteVars.colors.charcoal[250]
});

// packages/aria/src/themes/base/components/NavigationMenu/navigationMenuVariables.ts
var navigationMenuVariables = (siteVars) => ({
  navigationMenuBackgroundColor: siteVars.colors.white["100A"],
  navigationMenuBorderBottom: `solid 2px ${siteVars.colors.charcoal[250]}`,
  navigationMenuMajorItemPadding: pxArrayToRem([20, 16, 20, 16]),
  navigationMenuItemColor: siteVars.colors.charcoal[250],
  navigationMenuMajorPaddingBottom: pxToRem(56),
  navigationMenuMinorPaddingBottom: pxToRem(48)
});

// packages/aria/src/themes/base/components/Breadcrumb/breadcrumbItemVariables.ts
var breadcrumbItemVariables = (siteVars) => ({
  breadcrumbItemTextColor: siteVars.colorScheme.default?.["foreground2"],
  breadcrumbItemHoverTextColor: siteVars.colorScheme.default?.["foreground2"],
  breadcrumbItemHoverLineColor: siteVars.colorScheme.default?.["foreground2"]
});

// packages/aria/src/themes/base/components/Breadcrumb/breadcrumbDividerVariables.ts
var breadcrumbDividerVariables = (siteVars) => ({
  breadcrumbDividerColor: siteVars.colors.grey[300]
});

// packages/aria/src/themes/base/components/PersonalMenu/personalMenuVariables.ts
var personalMenuVariables = (siteVars) => ({
  personalMenuTextColor: siteVars.colors.charcoal["300"],
  personalMenuBackgroundColor: siteVars.colors.white["100A"],
  personalMenuDividerStyle: `1px solid  ${siteVars.colors.grey[125]}`,
  contentPadding: pxArrayToRem([20, 10]),
  maxHeight: `calc(100vh - ${pxToRem(DESKTOP_TOP_NAV_HEIGHT_FULL_LOGO)})`,
  contentMinHeight: "unset"
});

// packages/aria/src/themes/base/components/Dialog/dialogVariables.ts
var dialogVariables = (siteVariables) => ({
  border: "none",
  rootBackground: siteVariables.colors.white[100],
  rootBorderRadius: siteVariables.borderRadiusXLarge,
  rootWidth: "1000px",
  rootWidthMobile: "100vw",
  rootPadding: `${pxToRem(27)} ${pxToRem(32)} ${pxToRem(32)} ${pxToRem(32)}`,
  rootPaddingZoom: `${pxToRem(12)}`,
  rootMaxWidth: `90vw`,
  rootMaxHeight: `90vh`,
  contentMargin: `0 0 ${pxToRem(20)} 0`,
  contentTopBorderColor: siteVariables.colors.white[300],
  boxShadow: siteVariables.shadowLevel4,
  foregroundColor: siteVariables.colors.grey[900],
  headerFontSize: siteVariables.fontSizes.subheading02,
  headerFontWeight: siteVariables.fontWeightBold,
  headerMargin: `${pxToRem(25)}`,
  overlayBackground: "rgba(37, 36, 36, .75)",
  // todo: update to a palette value when daisy has mapped one
  overlayBackdrop: "none",
  overlayZIndex: siteVariables.zIndexes.overlay,
  headerActionMargin: `${pxToRem(-3)} ${pxToRem(-8)} 0 0`,
  // in case of customized footer this var allows to align styles
  footerActionsBreakpoint: "400px",
  overflow: true
});

// packages/aria/src/themes/base/components/Popup/popupVariables.ts
var popupVariables = (siteVars) => ({
  popupBackgroundColor: siteVars.colors.charcoal[300]
});

// packages/aria/src/themes/base/components/Accordion/accordionVariables.ts
var accordionVariables = (siteVars) => ({
  color: siteVars.bodyColor,
  focusBorderColor: siteVars.focusOuterBorderColor,
  focusBorderRadius: siteVars.borderRadiusMedium,
  focusBorderWidth: siteVars.borderWidth,
  primaryTextColor: siteVars.colorScheme.default?.["foreground"],
  primaryBorderColor: siteVars.colorScheme.default?.["foreground"],
  primarySVGColor: siteVars.colorScheme.default?.["foreground"]
});

// packages/aria/src/themes/base/components/DropdownMenu/dropdownMenuOutlineOptionsListVariables.ts
var dropdownMenuOutlineOptionsListVariables = (siteVars) => ({
  dropdownMenuOutlineOptionsListBackgroundColor: siteVars.bodyBackground
});

// packages/aria/src/themes/base/components/DropdownMenu/dropdownMenuOutlineButtonTopVariables.ts
var dropdownMenuOutlineButtonTopVariables = (siteVars) => ({
  DropdownMenuOutlineButtonTopBackgroundColor: siteVars.bodyBackground
});

// packages/aria/src/themes/base/components/DropdownMenu/dropdownMenuOutlineVariables.ts
var dropdownMenuVariables = (siteVars) => ({
  dropdownMenuBackgroundColor: siteVars.bodyBackground,
  navMenuTextColor: siteVars.colors.charcoal["250"]
});

// packages/aria/src/themes/base/components/Tile/TileImage/tileImageVariables.ts
var tileImageVariables = () => ({
  width: "100%",
  height: "100%",
  borderRadius: "0.625rem"
});

// packages/aria/src/themes/base/components/Input/inputVariables.ts
var inputVariables = (siteVars) => ({
  colorDisabled: siteVars.colors.grey[300],
  colorError: siteVars.colors.danger[200],
  borderColorError: siteVars.colors.danger[200],
  iconPosition: "absolute",
  iconRight: pxToRem(10),
  iconLeft: pxToRem(9),
  inputPaddingWithIconAtStart: `${pxToRem(5)} ${pxToRem(12)} ${pxToRem(
    5
  )} ${pxToRem(34)}`,
  inputPaddingWithIconAtEnd: `${pxToRem(5)} ${pxToRem(35)} ${pxToRem(
    5
  )} ${pxToRem(0)}`,
  inputPadding: `${pxToRem(5)} ${pxToRem(12)} ${pxToRem(5)} 0`,
  inputInsideLabelPaddingTop: pxToRem(14),
  borderColor: siteVars.colors.charcoal[250],
  borderRadius: "0",
  borderWidth: `0 0 ${pxToRem(2)} 0`,
  backgroundColor: "transparent",
  backgroundColorInverted: siteVars.colors.primitive.white,
  fontColor: siteVars.colors.charcoal[250],
  fontSize: `max(16px, ${pxToRem(16)})`,
  // Ensure iOS doesn't zoom in on input
  iconColor: siteVars.colors.charcoal[250],
  successfulColor: siteVars.colors.success[100],
  inputFocusBorderColor: `transparent transparent ${siteVars.colors.charcoal[250]} transparent`,
  inputFocusBorderRadius: `0`,
  placeholderColor: siteVars.colors.grey[300]
});

// packages/aria/src/themes/base/components/DropdownMenu/dropdownMenuOutlineOptionVariables.ts
var dropdownMenuOutlineOptionVariables = (siteVars) => ({
  dropdownMenuOutlineOptionBackgroundColor: siteVars.colors.charcoal[300],
  inactiveColor: siteVars.colors.charcoal[100],
  activeColor: siteVars.colors.charcoal[250],
  inactiveOpacity: 1
});

// packages/aria/src/themes/base/components/Popup/popupContentVariables.ts
var popupContentVariables = (siteVars) => {
  return {
    borderColor: "transparent",
    borderRadius: "0",
    borderSize: "0px",
    backgroundColor: siteVars.colors.white["100A"],
    color: siteVars.colors.charcoal[250],
    boxShadow: siteVars.shadowLevel3,
    padding: `${pxToRem(16)}`,
    pointerGap: pxToRem(5),
    pointerMargin: pxToRem(10),
    pointerHeight: pxToRem(7),
    pointerWidth: pxToRem(14),
    zIndex: siteVars.zIndexes.overlay
  };
};

// packages/aria/src/themes/base/components/MegaMenu/NavigationList/navigationListVariables.ts
var navigationListVariables = (siteVars) => ({
  titleOptionGap: siteVars.spaces["space-12"],
  optionGap: "0",
  optionMoreButtonGap: siteVars.spaces["space-24"],
  listWidth: "unset",
  titleMinHeight: pxToRem(41.6)
});

// packages/aria/src/themes/base/components/MegaMenu/NavigationListGroup/navigationListGroupVariables.ts
var navigationListGroupVariables = () => ({
  listGap: pxToRem(57)
});

// packages/aria/src/themes/base/components/Dropdown/dropdownVariables.ts
var dropdownVariables = (siteVars) => ({
  backgroundColor: "transparent",
  backgroundColorHover: siteVars.colors.transparency.black["6%"],
  invertedBackgroundColor: "transparent",
  invertedBackgroundColorHover: siteVars.colors.transparency.black["6%"],
  borderColor: "transparent",
  borderColorHover: "transparent",
  borderColorFocus: siteVars.colors.charcoal[250],
  borderError: siteVars.colors.danger["100"],
  borderWidth: "0px",
  openBorderColorHover: void 0,
  containerBorderRadius: "0px",
  disabledColor: siteVars.colors.grey["200"],
  openAboveContainerBorderRadius: `0 0 ${siteVars.borderRadiusMedium} ${siteVars.borderRadiusMedium}`,
  openBelowContainerBorderRadius: `${siteVars.borderRadiusMedium} ${siteVars.borderRadiusMedium} 0 0`,
  searchBorderBottomWidth: pxToRem(2),
  color: siteVars.colors.charcoal[250],
  comboboxPaddingButton: `0 ${pxToRem(12)}`,
  comboboxFlexBasis: pxToRem(50),
  aboveListBorderRadius: `${siteVars.borderRadiusMedium} ${siteVars.borderRadiusMedium} 0 0`,
  belowListBorderRadius: `0 0 ${siteVars.borderRadiusMedium} ${siteVars.borderRadiusMedium}`,
  listBackgroundColor: siteVars.colors.primitive.white,
  listBorderColor: "transparent",
  listBorderWidth: pxToRem(1),
  listPadding: `${pxToRem(8)} 0 ${pxToRem(6)}`,
  listBoxShadow: siteVars.shadowLevel3,
  listMaxHeight: pxToRem(296),
  listItemFocusBorderWidth: pxToRem(1),
  listItemBackgroundColor: "transparent",
  listItemBackgroundColorActive: siteVars.colors.transparency.black["6%"],
  listItemBackgroundColorHover: siteVars.colors.transparency.black["6%"],
  listItemColorActive: siteVars.colors.primitive.black,
  listItemColorHover: siteVars.colors.primitive.black,
  listItemSelectedColor: siteVars.colors.primitive.black,
  listItemSelectedFontWeight: siteVars.fontWeightSemibold,
  // TODO: prod app uses 17.5px here, it should be 16px per the design guide!
  listItemHeaderLineHeight: String(siteVars.lineHeightSmall),
  listItemContentLineHeight: String(siteVars.lineHeightSmall),
  selectedItemBackgroundColor: siteVars.colors.transparency.black["6%"],
  selectedItemBackgroundColorHover: siteVars.colors.transparency.black["6%"],
  selectedItemBorder: "none",
  selectedItemColor: siteVars.colors.primitive.black,
  selectedItemColorHover: siteVars.colors.primitive.black,
  selectedItemIconColor: siteVars.colors.primitive.white,
  selectedItemIconColorHover: siteVars.colors.primitive.white,
  selectedItemsMaxWidth: pxToRem(140),
  selectedItemColorFocus: siteVars.bodyColor,
  selectedItemsMaxHeight: pxToRem(82),
  toggleIndicatorSize: pxToRem(16),
  triggerButtonColorFocusActive: void 0,
  triggerButtonColorHover: siteVars.bodyColor,
  width: pxToRem(356),
  overlayZIndex: siteVars.zIndexes.overlay,
  // disabled state
  disabledBorderColorHover: "transparent",
  disabledTriggerColorHover: siteVars.colors.grey["200"],
  disabledBackgroundColorHover: siteVars.colors.grey["200"],
  // these should only apply when there is content in the image/media slot:
  listItemHeaderFontSize: siteVars.fontSizes.caption01,
  listItemHeaderColor: siteVars.colors.charcoal[250],
  listItemContentFontSize: siteVars.colors.charcoal[250],
  listItemContentColor: siteVars.colors.charcoal[250]
});

// packages/aria/src/themes/base/components/Tile/tileGridVariables.ts
var tileGridVariables = (siteVars) => ({
  tileGridBackgroundColor: siteVars.colors.charcoal[300]
});

// packages/aria/src/themes/base/components/SearchBar/searchBarVariables.ts
var searchBarVariables = (siteVars) => ({
  searchBarBackgroundColor: siteVars.colors.transparency.black["6%"],
  searchBarPadding: pxToRem(4),
  searchBarBorderRadius: pxToRem(64),
  searchInputPaddingX: pxToRem(8),
  searchBarIconColor: siteVars.colors.charcoal[300],
  searchBarInputColor: siteVars.colors.grey[300],
  searchBarFocusedInputColor: siteVars.colors.charcoal[300],
  widthSearchCancel: "100%",
  searchBarBorderColor: "transparent",
  searchBarBorderWidth: "1px"
});

// packages/aria/src/themes/base/components/SearchMenu/searchMenuVariables.ts
var searchMenuVariables = () => ({
  searchMenuBackgroundColor: "transparent",
  searchMenuPadding: pxArrayToRem([24, 12, 89]),
  searchMenuResultsGap: pxToRem(36)
});

// packages/aria/src/themes/base/components/SearchMenu/searchResultVariables.ts
var searchResultVariables = (siteVars) => ({
  searchResultItemColor: siteVars.colors.grey[300],
  searchResultItemMatchColor: siteVars.colors.charcoal[350],
  searchResultUnderlineThickness: pxToRem(2),
  searchResultsHeaderMarginBottom: pxToRem(24),
  searchResultsItemMarginBottom: pxToRem(2),
  searchResultsMaxWidth: pxToRem(786),
  searchResultsItemFontSize: pxToRem(16),
  searchResultActionGap: pxToRem(29),
  // mobile
  searchMobileResultsItemMarginBottom: pxToRem(12)
});

// packages/aria/src/themes/base/components/Skeleton/skeletonVariables.ts
var skeletonVariables = (siteVars) => {
  return {
    lineBackground: siteVars.colors.white[300],
    shapeBackground: siteVars.colors.white[300],
    animationBackground: siteVars.colors.white[300],
    animationBackgroundSecondary: siteVars.colors.white[300],
    lineMargin: `0 0 ${siteVars.lineHeightLarge} 0`,
    shapeMargin: `0 0 ${pxToRem(8)} 0`,
    // Skeleton Button
    buttonHeight: pxToRem(32),
    buttonWidth: pxToRem(96),
    buttonSmallHeight: pxToRem(24),
    buttonSmallWidth: pxToRem(72),
    buttonBackground: siteVars.colors.white[300],
    buttonCircularBorderRadius: pxToRem(999),
    // Skeleton Text
    textBackground: siteVars.colors.white[300],
    textWidth: "100%",
    textSmallerHeight: pxToRem(14),
    textSmallHeight: pxToRem(16),
    textMediumHeight: pxToRem(19),
    textLargeHeight: pxToRem(24),
    textLargerHeight: pxToRem(32),
    // Skeleton Input
    inputHeight: pxToRem(32),
    inputWidth: pxToRem(154),
    inputBackground: siteVars.colors.white[300],
    // Skeleton Avatar
    avatarBackground: siteVars.colors.white[300],
    avatarSmallest: pxToRem(20),
    avatarSmaller: pxToRem(24),
    avatarSmall: pxToRem(28),
    avatarMedium: pxToRem(32),
    avatarLarge: pxToRem(44),
    avatarLarger: pxToRem(64),
    avatarLargest: pxToRem(96)
  };
};

// packages/aria/src/themes/base/components/MegaMenu/BreadcrumbNavigation/breadcrumbNavigationVariables.ts
var breadcrumbNavigationVariables = (siteVars) => ({
  listXPadding: pxToRem(22),
  dividerColor: siteVars.colors.grey[300],
  dividerHeight: "100%",
  dividerWidth: "1px",
  dividerOpacity: 0.3
});

// packages/aria/src/themes/base/components/MegaMenu/megaMenuVariables.ts
var megaMenuVariables = (siteVars) => ({
  dividerColor: siteVars.colors.grey[200],
  padding: "0"
});

// packages/aria/src/themes/base/components/Carousel/carouselVariables.ts
var carouselVariables = (siteVars) => ({
  overlayZIndex: siteVars.zIndexes.overlay,
  overlayInitialOpacity: 0,
  overlayInitialOpacityDesktop: 0,
  overlayPadding: pxToRem(16),
  containerWidth: pxToRem(440),
  containerHeight: pxToRem(440),
  containerVAlign: "normal",
  containerHAlign: "normal",
  containerBackgroundColor: "transparent",
  paddleHeight: pxToRem(48),
  paddleIndicatorSize: pxToRem(16),
  paddleBackgroundColor: siteVars.colors.white[300],
  paddleHoverBackgroundColor: siteVars.colors.grey[150],
  paddlesZIndex: siteVars.zIndexes.foreground,
  thumbnailSizeRaw: 140,
  focusBorderColor: siteVars.colors.charcoal[250],
  borderWidth: "1px",
  hoverBorderColor: siteVars.colors.grey[300],
  navigationTabButtonHeight: "100%",
  navigationTabButtonWidth: "20%",
  navigationZIndex: siteVars.zIndexes.menuItem,
  indicatorSize: pxToRem(5.5),
  activeIndicatorSize: pxToRem(10),
  mixBlendMode: "normal"
});

// packages/aria/src/themes/base/components/Carousel/carouselItemVariables.ts
var carouselItemVariables = (siteVars) => ({
  carouselItemBackgroundColor: siteVars.colors.charcoal[300]
});

// packages/aria/src/themes/base/components/Carousel/carouselPaddlesContainerVariables.ts
var carouselPaddlesContainerVariables = (siteVars) => ({
  carouselPaddlesContainerPadding: pxToRem(16),
  carouselPaddlesContainerBackgroundColor: siteVars.colors.charcoal[300],
  mixBlendMode: "normal"
});

// packages/aria/src/themes/base/components/Carousel/carouselIndicatorVariables.ts
var carouselIndicatorVariables = () => ({
  indicatorSize: pxToRem(5.5),
  activeIndicatorSize: pxToRem(10)
});

// packages/aria/src/themes/base/components/VariantColor/variantColorVariables.ts
var variantColorVariables = (siteVars) => ({
  border: `${pxToRem(2)} solid ${siteVars.colors.primitive.white}`,
  borderHighlighted: `${pxToRem(2)} solid ${siteVars.colors.charcoal["250"]}`,
  containerBorder: `${pxToRem(4)} solid ${siteVars.colors.primitive.white}`,
  imageBorder: `1px solid ${siteVars.colors.charcoal["000"]}`,
  imageBorderHighlighted: `${pxToRem(4)} solid ${siteVars.colors.primitive.white}`,
  ringColor: siteVars.colors.grey["200"]
});

// packages/aria/src/themes/base/components/Tile/ArchizeenTile/archizeenTileVariables.ts
var archizeenTileVariables = (siteVars) => ({
  color: siteVars.colorScheme.default?.["foreground1"],
  tilePadding: pxToRem(14),
  smallTitleGap: pxToRem(16),
  mediumTitleGap: pxToRem(20),
  largeTitleGap: pxToRem(32),
  dynamicTitleGap: pxToRem(20),
  width: "100%",
  height: "100%",
  dynamicSizingWidth: 330,
  dynamicSizingGapWidth: 10
});

// packages/aria/src/themes/base/components/MegaMenu/FeaturedTile/featuredTileVariables.ts
var featuredTileVariables = () => ({
  titleTileGap: pxToRem(10),
  titleLeftPadding: pxToRem(14),
  titleMinHeight: pxToRem(46)
});

// packages/aria/src/themes/base/components/Input/numberInputVariables.ts
var numberInputVariables = (siteVars) => ({
  numberInputBackgroundColor: siteVars.colors.charcoal[300]
});

// packages/aria/src/themes/base/components/DropdownSort/dropdownSortVariables.ts
var dropdownSortVariables = (siteVars) => {
  const colorScheme7 = siteVars.colorScheme.default;
  return {
    itemSlotLineHeight: pxToRem(26),
    outLineWidth: "0",
    triggerButtonBGcolor: "transparent",
    listDropShadow: `${pxArrayToRem([0, 14, 24, 2])} rgba(0, 0, 0, 0.08)`,
    listMaxHeight: pxToRem(320),
    primaryDropdownFontSize: pxToRem(30),
    primaryDropdownHeight: pxToRem(30),
    primaryDropdownLineHeight: pxToRem(34),
    placeholderHoverColor: "transparent",
    dropdownItemFontSize: pxToRem(20),
    checkIconWidth: pxToRem(30),
    checkIconHeight: pxToRem(30),
    iconLabelGap: pxToRem(8),
    triggerButtonPadding: "0",
    triggerButtonColor: colorScheme7.foreground,
    triggerButtonIconColor: colorScheme7.foreground,
    dropdownHeaderLetterSpacing: pxToRem(-0.1),
    dropdownItemLetterSpacing: "0",
    paddingItemSlot: pxToRem(7),
    dropdownItemWidth: pxToRem(300)
  };
};

// packages/aria/src/themes/base/components/Image/imageOverlayVariables.ts
var imageOverlayVariables = (siteVars) => ({
  backgroundColor: `rgba(31, 31, 33, 0.5)`,
  iconColor: siteVars.colors.white["FFF"],
  fadeSpeed: "0.3s",
  iconSizeDesktop: 56,
  iconSizeMobile: 34
});

// packages/aria/src/themes/base/components/Divider/dividerVariables.ts
var dividerVariables = (siteVars) => ({
  dividerPrimaryColor: siteVars.colors.charcoal["000"],
  dividerSecondaryColor: siteVars.colors.grey["300"],
  dividerContrastColor: siteVars.colors.white["000"],
  textColor: siteVars.colors.grey[450],
  textFontSize: siteVars.fontSizes.caption01,
  contentMargin: pxToRem(20),
  textLineHeight: pxToRem(siteVars.lineHeightSmall),
  dividerPadding: pxToRem(4)
});

// packages/aria/src/themes/base/components/Tooltip/messageTooltipContentVariables.ts
var messageTooltipContentVariables = (siteVars) => ({
  borderSize: "1px",
  pointerMargin: "0px",
  pointerGap: pxToRem(2),
  pointerHeight: pxToRem(8),
  pointerWidth: pxToRem(14),
  variantBlackBackgroundColor: siteVars.colors.charcoal[250],
  variantBlackTextColor: siteVars.colors.white["000"],
  variantBlackBorderColor: siteVars.colors.charcoal[250],
  variantWhiteBackgroundColor: siteVars.colors.white["FFF"],
  variantWhiteTextColor: siteVars.colors.charcoal[250],
  variantWhiteBorderColor: siteVars.colors.white["FFF"],
  variantGreyBlackBackgroundColor: siteVars.colors.white[300],
  variantGreyBlackTextColor: siteVars.colors.charcoal[250],
  variantGreyBlackBorderColor: siteVars.colors.white[300],
  size16: pxToRem(16),
  size18: pxToRem(18),
  size20: pxToRem(20),
  size24: pxToRem(24),
  size30: pxToRem(30),
  size36: pxToRem(36),
  padding0: "0px",
  padding4: pxToRem(4),
  padding8: pxToRem(8),
  padding12: pxToRem(12),
  padding16: pxToRem(16),
  padding18: pxToRem(18),
  padding24: pxToRem(24),
  margin16: pxToRem(16),
  margin18: pxToRem(18),
  margin20: pxToRem(20),
  margin24: pxToRem(24),
  margin30: pxToRem(30),
  margin36: pxToRem(36),
  iconMargin: siteVars.spaces["space-4"],
  maxWidth: pxToRem(180),
  lineHeight: siteVars.lineHeightDefault
});

// packages/aria/src/themes/base/components/MegaMenu/NavigationList/navigationListSkeletonVariables.ts
var navigationListSkeletonVariables = () => ({
  listWidth: "unset",
  skeletonColor: "rgba(0, 0, 0, 0.1)",
  smallSkeletonTitleItemGap: pxToRem(24),
  smallSkeletonButtonItemGap: pxToRem(12),
  smallSkeletonItemGap: pxToRem(12),
  smallSkeletonTitleHeight: pxToRem(12),
  smallSkeletonTitleWidth: pxToRem(80),
  smallSkeletonShortItemHeight: pxToRem(12),
  smallSkeletonShortItemWidth: pxToRem(120),
  smallSkeletonLongItemHeight: pxToRem(12),
  smallSkeletonLongItemWidth: pxToRem(148),
  smallSkeletonButtonHeight: pxToRem(12),
  smallSkeletonButtonWidth: pxToRem(80),
  mediumSkeletonTitleItemGap: pxToRem(24),
  mediumSkeletonButtonItemGap: pxToRem(16),
  mediumSkeletonItemGap: pxToRem(16),
  mediumSkeletonTitleHeight: pxToRem(12),
  mediumSkeletonTitleWidth: pxToRem(80),
  mediumSkeletonShortItemHeight: pxToRem(12),
  mediumSkeletonShortItemWidth: pxToRem(140),
  mediumSkeletonLongItemHeight: pxToRem(12),
  mediumSkeletonLongItemWidth: pxToRem(160),
  mediumSkeletonButtonHeight: pxToRem(12),
  mediumSkeletonButtonWidth: pxToRem(80),
  largeSkeletonTitleItemGap: pxToRem(28),
  largeSkeletonButtonItemGap: pxToRem(24),
  largeSkeletonItemGap: pxToRem(24),
  largeSkeletonTitleHeight: pxToRem(12),
  largeSkeletonTitleWidth: pxToRem(80),
  largeSkeletonShortItemHeight: pxToRem(28),
  largeSkeletonShortItemWidth: pxToRem(220),
  largeSkeletonLongItemHeight: pxToRem(28),
  largeSkeletonLongItemWidth: pxToRem(260),
  largeSkeletonButtonHeight: pxToRem(28),
  largeSkeletonButtonWidth: pxToRem(124)
});

// packages/aria/src/themes/base/components/MegaMenu/FeaturedTile/featuredTileSkeletonVariables.ts
var featuredTileSkeletonVariables = () => ({
  skeletonColor: "rgba(0, 0, 0, 0.1)",
  skeletonTitleTileGap: pxToRem(24),
  skeletonTileImageHeaderGap: pxToRem(16),
  skeletonTileHeaderSubjectGap: pxToRem(16)
});

// packages/aria/src/themes/base/components/Pagination/paginationVariables.ts
var paginationVariables = (siteVars) => ({
  gap: pxToRem(24),
  gapMobile: pxToRem(110 / 6),
  listHeight: pxToRem(50),
  listHeightMobile: pxToRem(42),
  metaColor: siteVars.colors.charcoal[100],
  metaFontSize: pxToRem(16),
  metaFontSizeMobile: pxToRem(14),
  metaLineHeight: pxToRem(16)
});

// packages/aria/src/themes/base/components/Pagination/paginationItemVariables.ts
var paginationItemVariables = (siteVars) => ({
  paginationItemBackgroundColor: siteVars.colors.charcoal[300],
  paginationEllipsisColor: siteVars.colors.charcoal[300]
});

// packages/aria/src/themes/base/components/Pagination/paginationItemContentVariables.ts
var paginationItemContentVariables = (siteVars) => ({
  pageItemColor: siteVars.colors.charcoal[250],
  pageItemHoverColor: siteVars.colors.primitive.black,
  pageItemSelectedColor: siteVars.colors.primitive.white,
  pageItemSelectedHoverColor: siteVars.colors.primitive.white,
  pageItemBackgroundColor: "transparent",
  pageItemHoverBackgroundColor: "rgba(0, 0, 0, 0.06)",
  pageItemSelectedBackgroundColor: siteVars.colors.charcoal[250],
  pageItemSelectedHoverBackgroundColor: siteVars.colors.charcoal[250],
  pageItemDisabledOpacity: "0.3"
});

// packages/aria/src/themes/base/components/Pagination/paginationItemCounterVariables.ts
var paginationItemCounterVariables = (_siteVars) => ({
  color: "inherit"
});

// packages/aria/src/themes/base/components/Pagination/paginationItemsPerPageVariables.ts
var paginationItemsPerPageVariables = (_siteVars) => ({
  color: "inherit",
  dropdownColor: "inherit",
  fontSize: "inherit",
  lineHeight: pxToRem(16)
});

// packages/aria/src/themes/base/components/FeaturedArticle/featuredArticleVariables.ts
var featuredArticleVariables = (siteVars) => ({
  hoverColor: "rgba(0, 0, 0, 0.06)",
  textColor: siteVars.colors.charcoal[350],
  backgroundColor: siteVars.colors.primitive.white,
  contentGridPadding: pxArrayToRem([49, 192, 0, 50]),
  width: pxToRem(1920),
  imageWidth: pxToRem(1920),
  imageHeight: pxToRem(880),
  imageBorderRadius: "0.625rem",
  playIconSize: pxToRem(128),
  playIconColor: siteVars.colors.white["FFF"],
  contentGridColumns: pxArrayToRem([498, 754]),
  overlayBackgroundColor: `rgba(31, 31, 33, 0.2)`,
  tilePadding: "0"
});

// packages/aria/src/themes/base/components/Checkbox/checkboxVariables.ts
var checkboxVariables = (siteVars) => ({
  checkboxIndicatorLabelGap: pxToRem(8),
  checkboxIndicatorLabelCheckedGap: pxToRem(8),
  checkboxIndicatorSize: pxToRem(16),
  checkboxLabelMarginTop: pxToRem(0),
  radioIndicatorLabelGap: pxToRem(8),
  radioIndicatorLabelCheckedGap: pxToRem(8),
  radioIndicatorSize: pxToRem(16),
  radioLabelMarginTop: pxToRem(0),
  colorIndicatorLabelGap: pxToRem(5),
  colorIndicatorLabelCheckedGap: pxToRem(5),
  colorIndicatorSize: pxToRem(30),
  colorLabelMarginTop: pxToRem(2),
  labelLineHeight: pxToRem(26),
  labelDisabledColor: siteVars.colors.grey[200]
});

// packages/aria/src/themes/base/components/Checkbox/indicatorCheckboxVariables.ts
var indicatorCheckboxVariables = (siteVars) => ({
  backgroundColor: "transparent",
  checkedBackgroundColor: siteVars.colors.charcoal[250],
  checkedBackgroundColorDisabled: siteVars.colors.grey[200],
  size: pxToRem(16),
  borderRadius: "12.5%",
  border: `1px solid ${siteVars.colors.charcoal[250]}`,
  borderDisabled: `1px solid ${siteVars.colors.grey[200]}`,
  iconColor: siteVars.colors.white["000"],
  iconSize: pxToRem(12)
});

// packages/aria/src/themes/base/components/Checkbox/indicatorRadioVariables.ts
var indicatorRadioVariables = (siteVars) => ({
  backgroundColor: "transparent",
  checkedBackgroundColor: siteVars.colors.charcoal[250],
  checkedBackgroundColorDisabled: siteVars.colors.grey[200],
  size: pxToRem(16),
  middleRingSize: pxToRem(12),
  innerRingSize: pxToRem(8),
  border: `1px solid ${siteVars.colors.charcoal[250]}`,
  borderDisabled: `1px solid ${siteVars.colors.grey[200]}`,
  middleRingColor: siteVars.colors.white["000"]
});

// packages/aria/src/themes/base/components/Switch/switchVariables.ts
var switchVariables = (siteVars) => ({
  backgroundColor: siteVars.colors.grey[100],
  checkedBackgroundColor: siteVars.colors.gold["000"],
  thumbBackgroundColor: siteVars.colors.white[100],
  thumbBackgroundColorDisabled: siteVars.colors.white[300],
  labelLineHeight: pxToRem(26),
  labelDisabledColor: siteVars.colors.grey[200],
  labelSpacing: pxToRem(8),
  thumbWidth: pxToRem(20),
  thumbHeight: pxToRem(20),
  switchWidth: pxToRem(44),
  switchHeight: pxToRem(24),
  switchBorderRadius: pxToRem(12)
});

// packages/aria/src/themes/base/components/Checkbox/indicatorColorVariables.ts
var indicatorColorVariables = (siteVars) => ({
  iconColor: siteVars.colors.white["000"],
  iconSize: pxToRem(12),
  borderColor: "transparent",
  borderHighlightedColor: siteVars.colors.charcoal[250]
});

// packages/aria/src/themes/base/components/CheckboxGroup/checkboxGroupVariables.ts
var checkboxGroupVariables = () => ({
  optionGap: pxToRem(8)
});

// packages/aria/src/themes/base/components/RangeSelector/rangeSelectorVariables.ts
var rangeSelectorVariables = () => ({
  contentGap: pxToRem(16),
  inputWidth: pxToRem(136),
  titleLineHeight: pxToRem(26)
});

// packages/aria/src/themes/base/components/RangeInput/rangeInputVariables.ts
var rangeInputVariables = (siteVars) => ({
  borderColorNormal: siteVars.colors.grey[300],
  borderColorError: siteVars.colors.danger[200],
  borderColorDisabled: siteVars.colors.grey[100],
  borderRadius: "2px",
  padding: pxToRem(16),
  numberAndUnitGap: pxToRem(4),
  unitColor: siteVars.colors.grey[300],
  unitColorDisabled: siteVars.colors.grey[200],
  unitFontSize: pxToRem(16),
  unitLineHeight: pxToRem(16),
  numberColor: siteVars.colors.charcoal[250],
  numberColorDisabled: siteVars.colors.grey[200],
  numberFontSize: pxToRem(16),
  numberLineHeight: pxToRem(16)
});

// packages/aria/src/themes/base/components/MultiSlider/multiSliderVariables.ts
var multiSliderVariables = (siteVars) => {
  return {
    height: pxToRem(20),
    width: "100%",
    railHeight: "2px",
    railColor: siteVars.colors.grey[300],
    disabledRailColor: siteVars.colors.grey[200],
    trackColor: siteVars.colors.charcoal[250],
    disabledTrackColor: siteVars.colors.grey[300],
    thumbColor: siteVars.colors.charcoal[250],
    disabledThumbColor: siteVars.colors.grey[300],
    thumbSize: pxToRem(20)
  };
};

// packages/aria/src/themes/base/components/Review/reviewVariables.ts
var reviewVariables = (siteVars) => ({
  reviewBackgroundColor: siteVars.colors.charcoal[300]
});

// packages/aria/src/themes/base/components/Profile/BusinessProfile/businessProfileVariables.ts
var businessProfileVariables = (siteVars) => ({
  businessProfileBackgroundColor: siteVars.colors.charcoal[300],
  accordianButtonColor: siteVars.colors.charcoal[300],
  businessProfileLabelColor: siteVars.colors.charcoal[250],
  iconColor: hex2rgba(siteVars.colors.primitive.black, 0.2)
});

// packages/aria/src/themes/base/components/Profile/PersonalProfileMenu/personalProfileMenuVariables.ts
var personalProfileMenuVariables = (siteVars) => ({
  personalProfileMenuLabelColor: siteVars.colors.charcoal[250],
  personalProfileMenuDividerStyle: `1px solid  ${siteVars.colors.grey[125]}`
});

// packages/aria/src/themes/base/components/Profile/ProfileMenuItem/profileMenuItemVariables.ts
var profileMenuItemVariables = (siteVars) => ({
  profileMenuItemFontColor: siteVars.colors.charcoal["000"],
  iconAfterColor: hex2rgba(siteVars.colors.primitive.black, 0.2)
});

// packages/aria/src/themes/base/components/Tile/BannerAdTile/bannerAdTileVariables.ts
var bannerAdTileVariables = (siteVars) => ({
  bannerAdTileHoverColor: siteVars?.colorScheme?.default?.backgroundHover || "rgba(0, 0, 0, 0.06)",
  width: "100%",
  height: "100%",
  contentWrapSpacing: pxArrayToRem([25, 0, 0, 923]),
  borderRadius: "0.625rem"
});

// packages/aria/src/themes/base/components/CategorySelector/categorySelectorVariables.ts
var categorySelectorVariables = () => ({
  width: "100%",
  heightMobile: "100%",
  heightDesktop: "unset",
  mobilePadding: "0",
  desktopPadding: "0",
  headerCategoryGap: pxToRem(60),
  categoryGap: pxToRem(16),
  categoryPaddingMobile: pxToRem(8),
  categoryPaddingDesktop: "0",
  categoryBorderBottomMobile: "none",
  categoryBorderBottomDesktop: "none",
  categoryLabelLineHeightMobile: pxToRem(18),
  categoryLabelLineHeightDesktop: pxToRem(39),
  categoryIconSize: pxToRem(23),
  headerCategoryWeight: 500,
  selectedCategoryWeight: 400,
  selectedHeaderCategoryWeight: 500,
  headerCategoryItemGap: "0",
  selectedCategoryIconOpacity: 1
});

// packages/aria/src/themes/base/components/SearchableList/searchableListVariables.ts
var searchableListVariables = (siteVars) => ({
  searchableListBackgroundColor: "transparent",
  searchInputFontSize: pxToRem(16),
  searchInputFontColor: siteVars.colors.charcoal[250],
  searchInputLineHeight: pxToRem(16),
  searchInputPadding: pxArrayToRem([11, 12]),
  searchInputBorder: `1px solid ${siteVars.colors.grey[125]}`,
  searchInputBorderRadius: pxToRem(4),
  searchInputIconColor: "#757575",
  searchInputIconColorDisabled: siteVars.colors.grey[200],
  searchInputIconSize: pxToRem(21),
  inputOptionGap: pxToRem(24),
  optionGap: pxToRem(8),
  optionListHeight: pxToRem(424),
  optionListWidth: pxToRem(320),
  noResultGap: pxToRem(12),
  noResultFontSize: pxToRem(16),
  noResultTextColor: siteVars.colors.grey[300],
  noResultLineHeight: pxToRem(16),
  subtextMargin: pxArrayToRem([0, 0, 12, 0]),
  subtextFontSize: pxToRem(14),
  subtextFontSizeDesktop: pxToRem(16),
  subtextColor: siteVars.colors.charcoal[250],
  subtextLineHeight: pxToRem(16)
});

// packages/aria/src/themes/base/components/ToastMessage/toastMessageVariables.ts
var toastMessageVariables = (siteVars) => {
  const commonVariables = {
    width: pxToRem(386),
    maxWidth: pxToRem(440),
    marginLeft: pxToRem(-193),
    marginBottom: pxToRem(36),
    boxShadow: `0px 14px 24px 2px rgba(0, 0, 0, 0.08)`
  };
  const sizeVariables = {
    heightRegular: pxToRem(64),
    iconBeforeWidthRegular: pxToRem(24),
    iconCloseWidthRegular: pxToRem(20),
    fontSizeRegular: pxToRem(18),
    contentGapRegular: pxToRem(16),
    heightSmall: pxToRem(48),
    iconBeforeWidthSmall: pxToRem(16),
    iconCloseWidthSmall: pxToRem(16),
    fontSizeSmall: pxToRem(16),
    contentGapSmall: pxToRem(8)
  };
  const colorVariables = {
    textColorWhite: siteVars.colors.charcoal[250],
    hoverColorWhite: siteVars.colors.grey["500A"],
    backgroundColorWhite: siteVars.colors.white["FFF"],
    closeBtnHoverBgColorWhite: siteVars.colors.transparency.black["10%"],
    textColorDark: siteVars.colors.white["000"],
    hoverColorDark: siteVars.colors.grey[100],
    backgroundColorDark: siteVars.colors.charcoal[250],
    closeBtnHoverBgColorDark: siteVars.colors.transparency.white["10%"]
  };
  return {
    ...commonVariables,
    ...sizeVariables,
    ...colorVariables
  };
};

// packages/aria/src/themes/base/components/Drawer/drawerVariables.ts
var drawerVariables = (siteVars) => ({
  backdropColor: hex2rgba(siteVars.colors.primitive.black, 0.5),
  contentBackgroundColor: siteVars.colors.white["100A"],
  contentBorderRadius: "0"
});

// packages/aria/src/themes/base/components/ProfileNavigation/profileNavigationVariables.ts
var profileNavigationVariables = () => ({
  gap: pxToRem(8),
  buttonHeight: pxToRem(44),
  buttonPadding: pxArrayToRem([12, 16, 12, 16]),
  filterButtonMinWidth: pxToRem(49)
});

// packages/aria/src/themes/base/components/MobileHeaderSection/mobileHeaderSectionVariables.ts
var mobileHeaderSectionVariables = (_siteVars) => ({
  contentCaptionColor: "#6E6E71",
  headingsGap: pxToRem(7),
  marginBottomHeadings: pxToRem(12),
  contentTopPositionOnAnimate: "0",
  contentPadding: pxArrayToRem([48, 18, 23, 18])
});

// packages/aria/src/themes/base/components/VirtualList/virtualListVariables.ts
var virtualListVariables = () => ({
  minHeight: "unset",
  maxHeight: "unset"
});

// packages/aria/src/themes/base/components/VirtualList/virtualListItemVariables.ts
var virtualListItemVariables = () => ({
  backgroundColor: "transparent"
});

// packages/aria/src/themes/base/components/Modal/EnquiryModal/enquiryModalVariables.ts
var enquiryModalVariables = (siteVars) => ({
  backgroundColor: siteVars.colors.white["100A"],
  color: siteVars.colors.charcoal[250]
});

// packages/aria/src/themes/base/components/Container/containerVariables.ts
var containerVariables = () => ({
  background: "inherit"
});

// packages/aria/src/themes/base/components/TextInput/textInputVariables.ts
var textInputVariables = (siteVars) => ({
  backgroundColor: siteVars.colors.charcoal[300],
  hoverBackgroundColor: siteVars.colors.transparency.black["6%"],
  disabledColor: siteVars?.colors.grey[300],
  defaultColor: siteVars?.colors.charcoal[250],
  hoverColor: siteVars?.colors.charcoal[250],
  successColor: siteVars.contextualColors.success[100],
  errorColor: "#838fb5",
  paddingMobile: pxArrayToRem([8, 0]),
  inputMarginLargeDesktop: pxArrayToRem([24, 8]),
  inputMarginMediumDesktop: pxArrayToRem([14, 8, 12, 8]),
  inputMarginMobile: pxArrayToRem([8, 8]),
  minHeightLargeDesktop: pxToRem(74),
  minHeightMediumDesktop: pxToRem(50),
  minInputHeightMobile: pxToRem(40),
  iconContainerLargeDesktop: pxToRem(74),
  iconContainerMediumDesktop: pxToRem(48),
  iconContainerMobile: pxToRem(34),
  iconSizeMobile: pxToRem(18),
  iconMarginMobile: pxToRem(8),
  iconSizeDesktopLarge: pxToRem(32),
  iconMarginDesktopLarge: pxArrayToRem([16, 8]),
  iconSizeDesktopMedium: pxToRem(24),
  iconMarginDesktopMedium: pxArrayToRem([12, 12])
});

// packages/aria/src/themes/base/components/TextSelectorInput/textSelectorInputVariables.ts
var textSelectorInputVariables = (siteVars) => ({
  backgroundColor: siteVars.colors.charcoal[300],
  hoverBackgroundColor: siteVars.colors.transparency.black["6%"],
  disabledColor: siteVars?.colors.grey[300],
  defaultColor: siteVars?.colors.charcoal[250],
  hoverColor: siteVars?.colors.charcoal[250],
  successColor: siteVars.contextualColors.success[100],
  errorColor: siteVars.contextualColors.danger[100],
  maxDropdownHeight: pxToRem(320)
});

// packages/aria/src/themes/base/components/Tile/AdminCaption/adminCaptionVariables.ts
var adminCaptionVariables = (siteVars) => ({
  operationColor: siteVars.colors.primitive.black,
  captionColor: siteVars.colorScheme.default?.["foreground5"]
});

// packages/aria/src/themes/base/components/Tile/HotButtons/hotButtonsVariables.ts
var hotButtonsVariables = () => ({});

// packages/aria/src/themes/base/components/BottomNav/bottomNavVariables.ts
var bottomNavVariables = (siteVars) => ({
  backgroundColor: siteVars?.colorScheme?.default?.background,
  iconColor: siteVars?.colors.charcoal["000"],
  padding: pxArrayToRem([16, 21]),
  zIndex: siteVars.zIndexes.topNav,
  columnGap: pxToRem(38),
  inactiveOpacity: 0.3,
  activeOpacity: 1,
  activeBackground: "transparent"
});

// packages/aria/src/themes/base/components/Modal/LoginModal/loginModalVariables.ts
var loginModalVariables = (siteVars) => ({
  backgroundColor: siteVars.colors.white["100A"],
  color: siteVars.colors.charcoal[250]
});

// packages/aria/src/themes/base/components/CategoryTree/categoryTreeVariables.ts
var categoryTreeVariables = (siteVars) => ({
  backgroundColor: siteVars.colorScheme.default?.["background"],
  filterPadding: pxArrayToRem([16, 0, 0, 18]),
  filterButtonHeight: pxToRem(44),
  filterButtonPadding: pxArrayToRem([12, 15, 12, 15]),
  listPadding: pxArrayToRem([27, 0, 0, 17])
});

// packages/aria/src/themes/base/components/CategoryTree/categoryListVariables.ts
var categoryListVariables = (siteVars) => ({
  backgroundColor: "transparent",
  padding: pxArrayToRem([0, 0, 0, 17]),
  itemBorderBottom: `1px solid ${hex2rgba(
    siteVars.colorScheme.default?.["foreground"] ?? "",
    0.1
  )}`,
  itemPadding: `${pxToRem(16)} ${pxToRem(12)} calc(${pxToRem(15)} - 1px) 0`,
  itemFontSize: pxToRem(16),
  itemLineHeight: pxToRem(18),
  labelPadding: pxArrayToRem([2, 0, 2, 0]),
  labelLetterSpacing: siteVars.letterSpacingMedium,
  iconSize: pxToRem(23)
});

// packages/aria/src/themes/base/components/CategoryTree/categoryTreeHeaderVariables.ts
var categoryTreeHeaderVariables = (siteVars) => ({
  backgroundColor: "transparent",
  // can not set borderBox with fix height
  padding: pxArrayToRem([16, 8]),
  marginBottom: "0",
  borderBottom: `1px solid ${hex2rgba(
    siteVars.colorScheme.default?.["foreground"] ?? "",
    0.1
  )}`,
  backButtonIconColor: siteVars.colorScheme.default?.["foreground"],
  backButtonIconWidth: pxToRem(30),
  backButtonIconHeight: pxToRem(30),
  contentContainerPadding: pxArrayToRem([0, 42, 0, 12]),
  titleFontSize: pxToRem(20),
  titleLineHeight: pxToRem(26),
  titleColor: siteVars.colorScheme.default?.["foreground"],
  subtitleFontSize: pxToRem(14),
  subtitleLineHeight: pxToRem(18),
  subtitleColor: siteVars.colorScheme.default?.["foreground5"]
});

// packages/aria/src/themes/base/components/Loader/loaderVariables.ts
var loaderVariables = (siteVars) => ({
  containerHeights: {
    smallest: pxToRem(24),
    smaller: pxToRem(24),
    small: pxToRem(24),
    medium: pxToRem(32),
    large: pxToRem(48),
    larger: pxToRem(64),
    largest: pxToRem(72)
  },
  containerWidths: {
    smallest: pxToRem(24),
    smaller: pxToRem(24),
    small: pxToRem(24),
    medium: pxToRem(32),
    large: pxToRem(48),
    larger: pxToRem(64),
    largest: pxToRem(72)
  },
  primarySVGFill: siteVars.colorScheme.default?.foreground
});

// packages/aria/src/themes/base/components/SearchableDropdown/searchableDropdownVariables.ts
var searchableDropdownVariables = (siteVars) => ({
  searchInputBorderRadius: pxToRem(4),
  searchInputBorder: `1px solid #D3D3D3`,
  searchInputFontSize: pxToRem(16),
  searchInputFontColor: siteVars.colors.charcoal[250],
  searchInputLineHeight: pxToRem(20),
  searchInputPadding: pxArrayToRem([11, 12]),
  searchInputPaddingMobile: pxArrayToRem([11, 48, 11, 48]),
  searchInputIconColor: "#757575",
  searchInputIconColorDisabled: siteVars.colors.grey[200],
  searchInputIconSize: pxToRem(21),
  searchInputIconSizeMobile: pxToRem(24),
  inputOptionGap: pxToRem(24),
  optionGap: pxToRem(8),
  optionListHeight: pxToRem(424),
  optionListWidth: pxToRem(370)
});

// packages/aria/src/themes/base/components/Tile/TileVideo/tileVideoVariables.ts
var tileVideoVariables = (siteVars) => ({
  tileVideoBackgroundColor: siteVars.colors.charcoal[300],
  borderRadius: "0.625rem"
});

// packages/aria/src/themes/base/components/Drawer/swipeableEdgeVariables.ts
var swipeableEdgeVariables = (siteVars) => ({
  containerPaddingHorizontal: pxArrayToRem([0, 20]),
  containerPaddingVertical: pxArrayToRem([12, 0, 28]),
  pullerWidth: pxToRem(38),
  pullerHeight: pxToRem(4),
  pullerColor: hex2rgba(siteVars.colors.charcoal[200], 0.3),
  pullerBorderRadius: pxToRem(2)
});

// packages/aria/src/themes/base/components/TopNavigation/appMenuVariables.ts
var appMenuVariables = () => ({
  contentPadding: pxArrayToRem([0, 0, 0, 0]),
  searchBarSectionSpacing: pxArrayToRem([6, 10, 24]),
  contentHeight: `100vh`,
  contentWidth: "100vw",
  mobileSpacerHeight: pxToRem(200)
});

// packages/aria/src/themes/base/components/ReadMore/readMoreVariables.ts
var readMoreVariables = (siteVars) => ({
  linkColor: siteVars.colors.gold["000"],
  elementGap: pxToRem(39),
  toggleFontSize: pxToRem(39),
  contentFontSizeInPx: 16,
  contentLineHeight: siteVars.lineHeightSmall,
  showGradient: true,
  toggleButtonSpacing: "0"
});

// packages/aria/src/themes/base/componentStyles.ts
var componentStyles_exports = {};
__export(componentStyles_exports, {
  Accordion: () => accordionStyles,
  AccordionContent: () => accordionContentStyles,
  AccordionTitle: () => accordionTitleStyles,
  AdminCaption: () => adminCaptionStyles,
  AppMenu: () => appMenuStyles,
  ArchizeenTile: () => archizeenTileStyles,
  ArticleTile: () => articleTileStyles,
  Avatar: () => avatarStyles,
  Badge: () => badgeStyles,
  BannerAdTile: () => bannerAdTileStyles,
  BottomNav: () => bottomNavStyles,
  Box: () => boxStyles,
  Breadcrumb: () => breadcrumbStyles,
  BreadcrumbDivider: () => breadcrumbDividerStyles,
  BreadcrumbItem: () => breadcrumbItemStyles,
  BreadcrumbNavigation: () => breadcrumbNavigationStyles,
  BusinessProfile: () => businessProfileStyles,
  Button: () => buttonStyles,
  Caption: () => captionStyles,
  Carousel: () => carouselStyles,
  CarouselIndicator: () => carouselIndicatorStyles,
  CarouselItem: () => carouselItemStyles,
  CarouselNavigation: () => carouselNavigationStyles,
  CarouselNavigationItem: () => carouselNavigationItemStyles,
  CarouselOverlay: () => carouselOverlayStyles,
  CarouselPaddle: () => carouselPaddleStyles,
  CarouselPaddlesContainer: () => carouselPaddlesContainerStyles,
  CategoryList: () => categoryListStyles,
  CategorySelector: () => categorySelectorStyles,
  CategoryTree: () => categoryTreeStyles,
  CategoryTreeHeader: () => categoryTreeHeaderStyles,
  Checkbox: () => checkboxStyles,
  CheckboxGroup: () => checkboxGroupStyles,
  Container: () => containerStyles,
  Dialog: () => dialogStyles,
  Divider: () => dividerStyles,
  Drawer: () => drawerStyles,
  Dropdown: () => dropdownStyles,
  DropdownItem: () => dropdownItemStyles,
  DropdownMenu: () => dropdownMenuStyles,
  DropdownMenuOutlineButtonTop: () => dropdownMenuOutlineButtonTopStyles,
  DropdownMenuOutlineOption: () => dropdownMenuOutlineOptionStyles,
  DropdownMenuOutlineOptionsList: () => dropdownMenuOutlineOptionsListStyles,
  DropdownSort: () => dropdownSortStyles,
  EnquiryModal: () => enquiryModalStyles,
  FeaturedArticle: () => featuredArticleStyles,
  FeaturedTile: () => featuredTileStyles,
  FeaturedTileSkeleton: () => featuredTileSkeletonStyles,
  FilterContentLayout: () => filterContentLayoutStyles,
  Flex: () => flexStyles,
  FlexItem: () => flexItemStyles,
  FooterNavigation: () => footerNavigationStyles,
  FooterNavigationItem: () => footerNavigationItemStyles,
  Grid: () => gridStyles,
  Header: () => headerStyles,
  HotButtons: () => hotButtonsStyles,
  Image: () => imageStyles,
  ImageElement: () => imageElementStyles,
  ImageOverlay: () => imageOverlayStyles,
  IndicatorCheckbox: () => indicatorCheckboxStyles,
  IndicatorColor: () => indicatorColorStyles,
  IndicatorRadio: () => indicatorRadioStyles,
  Input: () => inputStyles,
  Label: () => labelStyles,
  List: () => listStyles,
  ListItem: () => listItemStyles,
  Loader: () => loaderStyles,
  LoginModal: () => loginModalStyles,
  Logo: () => logoStyles,
  MegaMenu: () => megaMenuStyles,
  MemberTile: () => memberTileStyles,
  MessageTooltipContent: () => messageTooltipContentStyles,
  MobileHeaderSection: () => mobileHeaderSectionStyles,
  MultiSlider: () => multiSliderStyles,
  NavigationItem: () => navigationItemStyles,
  NavigationList: () => navigationListStyles,
  NavigationListGroup: () => navigationListGroupStyles,
  NavigationListSkeleton: () => navigationListSkeletonStyles,
  NavigationMenu: () => navigationMenuStyles,
  NumberInput: () => numberInputStyles,
  Pagination: () => paginationStyles,
  PaginationItem: () => paginationItemStyles,
  PaginationItemContent: () => paginationItemContentStyles,
  PaginationItemCounter: () => paginationItemCounterStyles,
  PaginationItemsPerPage: () => paginationItemsPerPageStyles,
  Paragraph: () => paragraphStyles,
  PersonalMenu: () => personalMenuStyles,
  PersonalProfile: () => personalProfileStyles,
  PersonalProfileMenu: () => personalProfileMenuStyles,
  Popup: () => popupStyles,
  PopupContent: () => popupContentStyles,
  ProductInfo: () => productInfoStyles,
  ProductTile: () => productTileStyles,
  ProfessionalTile: () => professionalTileStyles,
  ProfileMenuItem: () => profileMenuItemStyles,
  ProfileNavigation: () => profileNavigationStyles,
  ProjectTile: () => projectTileStyles,
  RangeInput: () => rangeInputStyles,
  RangeSelector: () => rangeSelectorStyles,
  ReadMore: () => readMoreStyles,
  Review: () => reviewStyles,
  SearchBar: () => searchBarStyles,
  SearchMenu: () => searchMenuStyles,
  SearchResult: () => searchResultStyles,
  SearchableDropdown: () => searchableDropdownStyles,
  SearchableList: () => searchableListStyles,
  Skeleton: () => skeletonStyles,
  SkeletonLine: () => skeletonLineStyles,
  Subheader: () => subheaderStyles,
  Subscription: () => subscriptionStyles,
  SvgIcon: () => svgIconStyles,
  SwipeableEdge: () => swipeableEdgeStyles,
  Switch: () => switchStyles,
  Tabs: () => tabsStyles,
  TextInput: () => textInputStyles,
  TextSelectorInput: () => textSelectorInputStyles,
  TileGrid: () => tileGridStyles,
  TileImage: () => tileStyles,
  TileOverlay: () => tileOverlayStyles,
  TileVideo: () => tileVideoStyles,
  ToastMessage: () => toastMessageStyles,
  TopNavigation: () => topNavigationStyles,
  VariantColor: () => variantColorStyles,
  VideoPlayer: () => videoPlayerStyles,
  VirtualList: () => virtualListStyles,
  VirtualListItem: () => virtualListItemStyles
});

// packages/aria/src/themes/base/components/Button/buttonStyles.ts
import { svgIconClassName } from "@archipro-design/icons";
var VARIANT_NAME_MAP = {
  contained: "",
  text: "NoBKG",
  outlined: "Outlined"
};
var buttonStyles = {
  root: ({ variables: v, props: p, theme }) => {
    const colorVariant = `${p.color}${VARIANT_NAME_MAP[p.variant ?? "contained"]}`;
    const state = p.disabled ? "Disable" : p.selected ? "Selected" : "Normal";
    const hoverStyles = {
      color: v[`${colorVariant}ButtonTextColorHover`],
      backgroundColor: `${p.variant === "text" && !v.buttonPadding ? "transparent" : v[`${colorVariant}ButtonBackgroundColorHover`]}`,
      border: v[`${colorVariant}ButtonBorderHover`],
      textDecoration: `${p.variant === "text" && !v.buttonPadding ? "underline" : "unset"}`,
      [`& .${svgIconClassName}`]: {
        fill: v[`${colorVariant}ButtonTextColorHover`]
      }
    };
    return {
      borderRadius: v.buttonRadius,
      fontSize: pxToRem(p.size ?? 16),
      padding: typeof v.buttonPadding === "string" ? v.buttonPadding : pxToRem(v.buttonPadding),
      cursor: "pointer",
      minWidth: v.buttonMinWidth,
      display: "inline-flex",
      justifyContent: "center",
      alignItems: "center",
      position: "relative",
      lineHeight: "100%",
      color: v[`${colorVariant}ButtonTextColor${state}`],
      backgroundColor: `${p.variant === "text" && !v.buttonPadding ? "transparent" : v[`${colorVariant}ButtonBackgroundColor${state}`]}`,
      border: v[`${colorVariant}ButtonBorder${state}`],
      [`& .${svgIconClassName}`]: {
        fill: v[`${colorVariant}ButtonTextColor${state}`]
      },
      ...p.selected && {
        textDecoration: `${p.variant === "text" && !v.buttonPadding ? "underline" : "unset"}`
      },
      ...!p.disabled && {
        [theme.screen.max.laptop]: {
          ":active": hoverStyles
        },
        [theme.screen.laptop]: {
          ":hover": hoverStyles
        }
      },
      ...p.disabled && {
        cursor: "not-allowed"
      },
      ...p.fluid && {
        width: "100%",
        maxWidth: "100%"
      },
      "> div": {
        color: "inherit",
        ...p.loading && {
          opacity: 0
        }
      },
      [`& .${labelClassName}`]: {
        fontSize: v[`buttonSize${p.size}TextFontSize`],
        lineHeight: v[`buttonSize${p.size}LineHeight`],
        letterSpacing: v[`buttonSize${p.size}LetterSpacing`]
      }
    };
  },
  icon: ({ props: p, variables: v }) => {
    return {
      ...v.iconPadding && { padding: pxToRem(v.iconPadding) },
      [`& .${svgIconClassName}`]: {
        ...p.size && { height: pxToRem(p.size) },
        ...p.size && { width: pxToRem(p.size) }
      },
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center"
    };
  },
  iconAfter: ({ props: p, variables: v }) => ({
    marginLeft: v[`buttonSize${p.size}IconXMargin`],
    ...p.loading && {
      opacity: 0
    }
  }),
  iconBefore: ({ props: p, variables: v }) => ({
    marginRight: v[`buttonSize${p.size}IconXMargin`],
    ...p.loading && {
      opacity: 0
    }
  }),
  loader: () => {
    return {
      position: "absolute"
    };
  }
};

// packages/aria/src/themes/base/utils/mapWeightToTextStyle.ts
function mapWeightToTextStyle(weight) {
  const weightToStyle = {
    regular: "Roman55",
    medium: "Medium65",
    bold: "Bold75"
  };
  return weightToStyle[weight];
}

// packages/aria/src/themes/base/components/Typography/Header/headerStyles.ts
var VariantMap = {
  "01": "h1",
  "02": "h2",
  "03": "h3",
  "04": "h4",
  "05": "h5"
};
var headerStyles = {
  root: ({ variables: v, theme, props }) => {
    return {
      ...v.color && { color: v.color },
      ...mapAsPropToFontStyles(v, theme, props),
      ...theme.siteVariables.getTextStyle(
        mapWeightToTextStyle("regular"),
        "Text"
      ),
      ...v.fontSize && { fontSize: v.fontSize },
      ...v.letterSpacing && { letterSpacing: v.letterSpacing },
      ...v.lineHeight && { lineHeight: v.lineHeight },
      ...v.fontFamily && { fontFamily: v.fontFamily },
      ...v.fontWeight && { fontWeight: v.fontWeight },
      "& sup": {
        fontSize: "55%"
      }
    };
  }
};
function mapAsPropToFontStyles({ as = "h1" }, theme, { variant }) {
  const asKey = variant && VariantMap[variant] ? VariantMap[variant] : as;
  const map = {
    headings: {
      h1: "heading01",
      h2: "heading02",
      h3: "heading03",
      h4: "heading04",
      h5: "heading05"
    },
    lineHeights: {
      h1: theme.siteVariables.lineHeightDefault,
      h2: theme.siteVariables.lineHeightDefault,
      h3: theme.siteVariables.lineHeightSmaller,
      h4: theme.siteVariables.lineHeightSmallest,
      h5: theme.siteVariables.lineHeightSmallest
    },
    letterSpacing: {
      h1: theme.siteVariables.letterSpacingMedium,
      h2: theme.siteVariables.letterSpacingSmall,
      h3: theme.siteVariables.letterSpacingSmall,
      h4: theme.siteVariables.letterSpacingMedium,
      h5: theme.siteVariables.letterSpacingSmall
    }
  };
  const fontSize = theme.siteVariables.fontSizes[map.headings[asKey]];
  const lineHeight = map.lineHeights[asKey];
  const letterSpacing = map.letterSpacing[asKey];
  return {
    fontSize,
    lineHeight,
    letterSpacing
  };
}

// packages/aria/src/themes/base/components/Typography/utils.ts
function handleCommonTypographyStyles({
  props,
  siteVariables
}) {
  const { inline, weight = "regular", maximumLines } = props;
  return {
    display: inline ? "inline" : "block",
    ...props.uc && { textTransform: "uppercase" },
    ...props.link && {
      cursor: "pointer",
      ":hover": {
        textDecoration: "underline"
      }
    },
    ...siteVariables.getTextStyle(mapWeightToTextStyle(weight), "Text"),
    ...maximumLines && {
      overflow: "hidden",
      display: inline ? "-webkit-inline-box" : "-webkit-box",
      WebkitLineClamp: maximumLines,
      WebkitBoxOrient: "vertical"
    }
  };
}

// packages/aria/src/themes/base/components/Typography/Subheader/subheaderStyles.ts
var subheaderStyles = {
  root: ({ variables, theme, props }) => {
    return {
      ...handleCommonTypographyStyles({
        props,
        siteVariables: theme.siteVariables
      }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ...mapSubHeadingVariantToFontStyles(props, theme),
      ...variables
    };
  }
};
function mapSubHeadingVariantToFontStyles({ variant }, theme) {
  const map = {
    lineHeights: {
      subheading01: theme.siteVariables.lineHeightDefault,
      subheading02: theme.siteVariables.lineHeightSmall,
      subheading03: theme.siteVariables.lineHeightDefault,
      subheading04: theme.siteVariables.lineHeightDefault,
      subheading05: theme.siteVariables.lineHeightSmaller
    },
    letterSpacing: {
      subheading01: theme.siteVariables.letterSpacingMedium,
      subheading02: theme.siteVariables.letterSpacingNormal,
      subheading03: theme.siteVariables.letterSpacingSmall,
      subheading04: theme.siteVariables.letterSpacingSmall,
      subheading05: theme.siteVariables.letterSpacingSmall
    }
  };
  return {
    fontSize: theme.siteVariables.fontSizes[`subheading${variant}`],
    lineHeight: map.lineHeights[`subheading${variant}`],
    letterSpacing: map.letterSpacing[`subheading${variant}`]
  };
}

// packages/aria/src/themes/base/components/Typography/Paragraph/paragraphStyles.ts
var paragraphStyles = {
  root: ({ props, variables: v, theme }) => {
    return {
      ...v,
      ...handleCommonTypographyStyles({
        props,
        siteVariables: theme.siteVariables
      }),
      ...mapVariantToFontStyles(theme)
    };
  }
};
function mapVariantToFontStyles(theme) {
  const map = {
    lineHeights: {
      paragraph01: theme.siteVariables.lineHeightSmall
    },
    letterSpacing: {
      paragraph01: theme.siteVariables.letterSpacingNormal
    }
  };
  return {
    fontSize: theme.siteVariables.fontSizes[`paragraph01`],
    lineHeight: map.lineHeights[`paragraph01`],
    letterSpacing: map.letterSpacing[`paragraph01`]
  };
}

// packages/aria/src/themes/base/components/Typography/Label/labelStyles.ts
var labelStyles = {
  root: ({ variables, theme, props }) => {
    return {
      ...handleCommonTypographyStyles({
        props,
        siteVariables: theme.siteVariables
      }),
      ...mapLabelVariantToFontStyles(props, theme),
      ...variables
    };
  }
};
function mapLabelVariantToFontStyles({ variant = "01" }, theme) {
  const map = {
    lineHeights: {
      "01": theme.siteVariables.lineHeightSmallest,
      "02": theme.siteVariables.lineHeightSmall,
      "03": theme.siteVariables.lineHeightSmall,
      "04": theme.siteVariables.lineHeightSmall
    },
    letterSpacing: {
      "01": theme.siteVariables.letterSpacingMedium,
      "02": theme.siteVariables.letterSpacingNormal,
      "03": theme.siteVariables.letterSpacingNormal,
      "04": theme.siteVariables.lineHeightSmall
    }
  };
  const fontSize = theme.siteVariables.fontSizes[`label${variant}`];
  const lineHeight = map.lineHeights[variant];
  const letterSpacing = map.letterSpacing[variant];
  return {
    fontSize,
    lineHeight,
    letterSpacing
  };
}

// packages/aria/src/themes/base/components/TopNavigation/topNavigationStyles.ts
import { svgIconClassName as svgIconClassName2 } from "@archipro-design/icons";
var borderAnimationEffect = {
  from: {
    opacity: 0
  },
  to: {
    opacity: 1
  }
};
var megaMenuContentAnimationEffect = {
  from: {
    opacity: 0,
    transform: `translate(0, -10px)`
  },
  to: {
    opacity: 1,
    transform: `translate(0,0)`
  }
};
var megaMenuContentAnimation = (border) => {
  return {
    animationName: border ? borderAnimationEffect : megaMenuContentAnimationEffect,
    animationIterationCount: 1,
    animationPlayState: "running",
    animationDelay: `.25s`,
    animationDuration: `.25s`,
    animationFillMode: "both",
    animationTimingFunction: "ease-in-out"
  };
};
var topNavigationStyles = {
  root: ({ variables: v, theme, props: p }) => {
    const desktopStyles = {
      root: {
        padding: v.topNavigationDesktopPadding,
        height: v.topNavigationDesktopHeight,
        ...(!p.showFullLogo || p.showSearchBar || p.showMegamenu) && {
          height: "auto",
          minHeight: v.topNavigationDesktopHeight
        },
        ...p.showFullLogo && !p.showSearchBar && !p.showMegamenu && !p.hasSecondaryNav && {
          height: v.topNavigationDesktopFullHeight
        },
        ...p.hasSecondaryNav && {
          height: v.topNavigationDesktopHasSecondaryNavHeight,
          minHeight: v.topNavigationDesktopHasSecondaryNavHeight
        }
      },
      logo: {
        ...p.showFullLogo && {
          //fullLogo with animation
          ...p.logoAnimationType && {
            "& svg:nth-child(1)": {
              width: v.topNavigationDesktopFullLogoWidth,
              height: "auto"
            },
            "& svg:nth-child(2)": {
              width: "auto",
              height: v.topNavigationDesktopFullHeight
            },
            alignSelf: "flex-start"
          },
          //fullLogo with no animation
          ...!p.logoAnimationType && {
            alignSelf: "flex-start",
            "& svg": {
              width: v.topNavigationDesktopFullLogoWidth,
              height: "auto",
              transform: `translate(${pxArrayToRem([2, 10])}))`
            }
          },
          //fullLogo, no animation and showSearchBar
          ...p.showSearchBar && {
            justifySelf: "flex-start",
            alignSelf: "flex-start",
            width: v.topNavigationDesktopFullLogoWidth,
            height: "auto",
            "& svg": {
              width: v.topNavigationDesktopFullLogoWidth,
              height: "auto",
              transform: `translate(${pxArrayToRem([2, 10])}))`
            }
          }
        },
        ...!p.showFullLogo && {
          //shortLogo with no animation
          ...!p.logoAnimationType && {
            "& svg": {
              width: v.topNavigationDesktopLogoWidth,
              height: "auto"
            }
          },
          //shortLogo with animation
          ...p.logoAnimationType && {
            "& svg:nth-child(1)": {
              width: v.topNavigationDesktopFullLogoWidth,
              height: "auto"
            },
            "& svg:nth-child(2)": {
              width: pxToRem(52),
              height: "auto"
            }
          },
          //shortlogo , no animation and showSearchBar
          ...p.showSearchBar && {
            transform: `translate(0)`,
            justifySelf: "flex-start",
            alignSelf: "flex-start",
            "& svg": {
              width: "inherit",
              height: "inherit"
            }
          }
        }
      },
      megaMenu: {
        background: v.topNavigationBackgroundColor,
        transitionProperty: "height",
        transitionDuration: v.megaMenuAnimationDuration,
        transitionDelay: v.megaMenuExpandDelay,
        height: p.showMegamenu ? v.megaMenuExpandedHeight : 0,
        position: `absolute`,
        left: 0,
        right: 0,
        top: p.hasSecondaryNav ? v.topNavigationDesktopHasSecondaryNavHeight : v.topNavigationDesktopHeight,
        [`& .${topNavigationSlotClassNames.megaMenuMask}`]: {
          padding: pxArrayToRem([0, 50]),
          [theme.screen.clamped]: {
            padding: 0
          },
          ...p.showMegamenu && !p.showSearchBar && {
            ...megaMenuContentAnimation()
          }
        }
      },
      rightButton: {
        padding: 0,
        margin: pxArrayToRem([0, 14, 0, 0]),
        whiteSpace: "nowrap",
        [`& svg`]: {
          width: pxToRem(32),
          height: pxToRem(32)
        }
      }
    };
    const tabletStyles = {
      padding: v.tabletPadding,
      background: p.showSearchBar && v.topNavigationSearchingBackgroundColor ? v.topNavigationSearchingBackgroundColor : v.topNavigationBackgroundColor,
      height: p.showSearchMenu ? "auto" : v.tabletHeight
    };
    return {
      width: "100%",
      minHeight: p.logoAnimationType === "scaleDown" ? pxToRem(100) : !p.showFullLogo ? v.topNavigationMobileWithShortLogoHeight : v.topNavigationMobileWithFullLogoHeight,
      height: p.expanded ? "-webkit-fill-available" : "auto",
      [`& .${containerClassName}`]: {
        display: "flex",
        flexDirection: "column",
        height: p.showCategoryTree ? "100%" : "unset"
      },
      background: p.showSearchBar && v.topNavigationSearchingBackgroundColor ? v.topNavigationSearchingBackgroundColor : p.expanded && !p.tabletDevice && !p.desktopDevice ? v.topNavigationExpandedMenuBackgroundColor : v.topNavigationBackgroundColor,
      zIndex: v.topNavigationZIndex,
      [theme.screen.tablet]: tabletStyles,
      ...p.desktopDevice && {
        [theme.screen.tablet]: desktopStyles.root,
        [theme.screen.max.shrankWindow]: {
          ...tabletStyles,
          ...desktopStyles.root,
          ...{
            minWidth: DEFAULT_MIN_DESKTOP
          }
        }
      },
      [theme.screen.clamped]: {
        paddingLeft: 0,
        paddingRight: 0
      },
      [`& .${topNavigationSlotClassNames.rightButton}`]: {
        padding: pxArrayToRem([0, 16, 0, 16]),
        [theme.screen.tablet]: desktopStyles.rightButton,
        [theme.screen.max.shrankWindow]: desktopStyles.rightButton
      },
      [`& .${topNavigationSlotClassNames.searchBar}`]: {
        marginTop: p.tabletDevice ? `calc((${v.rightSizeButtonHeight} - ${pxToRem(40)}) / 2)` : 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end"
      },
      [`& .${topNavigationSlotClassNames.logo}`]: {
        // use class css variable here to override mobile and prevent using important
        "& svg": {
          ...!p.showFullLogo && {
            width: v.topNavigationMobileLogoWidth,
            height: v.topNavigationMobileLogoHeight
          }
        },
        ...p.showSearchBar && {
          justifySelf: "flex-start",
          alignSelf: "flex-start"
        },
        ...p.showFullLogo && {
          "& svg": {
            width: v.topNavigationMobileExpandedLogoWidth,
            height: "auto"
          }
        },
        ...p.tabletDevice && {
          "& svg": {
            width: v.tabletFullLogoWidth,
            height: v.tabletFullLogoHeight
          },
          alignSelf: "flex-start",
          paddingTop: `calc((${v.rightSizeButtonHeight} - ${v.tabletFullLogoHeight}) / 2)`
        },
        ...p.desktopDevice && {
          // desktop and small screen
          [theme.screen.tablet]: desktopStyles.logo,
          [theme.screen.max.shrankWindow]: desktopStyles.logo
        }
      },
      [`& .${topNavigationSlotClassNames.flag}`]: {
        [`&.${buttonClassName}`]: {
          [`& .${svgIconClassName2}`]: {
            width: pxToRem(42),
            height: pxToRem(42)
          }
        }
      },
      [`& .${topNavigationSlotClassNames.megaMenu}`]: {
        [theme.screen.tablet]: desktopStyles.megaMenu,
        [theme.screen.max.shrankWindow]: desktopStyles.megaMenu
      },
      [`& .${topNavigationSlotClassNames.secondaryNavContainer}`]: {
        padding: v.secondaryNavContainerPadding,
        [theme.screen.tablet]: {
          marginLeft: pxToRem(-10)
        },
        [theme.screen.max.shrankWindow]: {
          marginLeft: pxToRem(-10)
        },
        [`& .${buttonClassName}`]: {
          [`& .${labelClassName}`]: {
            fontSize: v.secondaryNavButtonSize,
            lineHeight: theme.siteVariables.lineHeightSmallest
          },
          padding: v.secondaryNavButtonActivePadding,
          marginRight: v.secondaryNavButtonGap,
          [`&:not(:first-child)`]: {
            background: v.topNavigationBackgroundColor,
            color: hex2rgba(
              theme.siteVariables.colorScheme.default?.foreground ?? theme.siteVariables.bodyColor,
              0.6
            ),
            padding: v.secondaryNavButtonPadding,
            [`&:hover`]: {
              color: theme.siteVariables.colorScheme.default?.foreground,
              [`& .${labelClassName}`]: {
                textShadow: `0 0 ${theme.siteVariables.colorScheme.default?.foreground}`
              }
            }
          },
          [`&:last-child`]: {
            marginRight: 0
          }
        }
      },
      [`& .${topNavigationSlotClassNames.appMenu}`]: {
        top: p.showCategoryTree ? 0 : v.appMenuTopOffset,
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        [`& > div`]: {
          margin: v.appMenuContainerPadding,
          background: v.topNavigationExpandedMenuBackgroundColor
        },
        ...p.expanded && {
          [`& .${captionClassName}`]: {
            color: v.mobileSearchPlaceholderInputColor
          },
          [`& .${buttonClassName}`]: {
            color: v.mobileSearchPlaceholderInputColor
          }
        }
      }
    };
  },
  mobileSearch: ({ variables: v }) => ({
    marginTop: pxToRem(32),
    [`& div.${searchBarClassName}`]: {
      minHeight: pxToRem(46),
      height: "auto",
      [`& .${inputClassName}`]: {
        paddingRight: pxToRem(3),
        paddingLeft: pxToRem(3)
      },
      [`& .${inputSlotClassNames.input}`]: {
        color: v.mobileSearchPlaceholderInputColor,
        "&::placeholder ": {
          color: v.mobileSearchPlaceholderColor
        }
      }
    }
  }),
  topNav: ({ theme, props: p, variables: v }) => {
    const desktopStyles = {
      root: {
        padding: "unset",
        flexDirection: "row",
        ...p.showFullLogo && !p.showSearchBar && {
          paddingBottom: pxToRem(16)
        }
      },
      logoAndRightSideWrapper: {
        display: "flex",
        width: "100%",
        justifyContent: "flex-end",
        [`& .${topNavigationSlotClassNames.logo}`]: {
          width: pxToRem(p.showSearchBar ? 505 : 262),
          transform: `translate(${pxToRem(-2)}, ${pxToRem(10)})`,
          transitionProperty: "width",
          transitionDuration: v.searchBarAnimationDuration,
          transitionDelay: v.searchBarExpandDelay,
          ...!p.showSearchBar && {
            transitionDelay: v.searchBarCollapseDelay
          }
        },
        [`& .${topNavigationSlotClassNames.right}`]: {
          width: pxToRem(p.showSearchBar ? 1315 : 634),
          transitionProperty: "width",
          transitionDuration: v.searchBarAnimationDuration,
          transitionDelay: v.searchBarExpandDelay,
          ...!p.showSearchBar && {
            transitionDelay: v.searchBarCollapseDelay
          },
          [theme.screen.clamped]: {
            width: pxToRem(p.showSearchBar ? 1315 : 734)
          }
        },
        [`& .${searchMenuClassName}`]: {
          opacity: 0,
          transform: `translate(0, ${pxToRem(-5)})`,
          paddingLeft: pxToRem(143),
          height: 0,
          overflow: "hidden",
          ...!p.showSearchBar && {
            paddingTop: 0,
            paddingBottom: 0
          },
          ...p.showSearchBar && {
            transitionProperty: "opacity, transform",
            transitionDuration: v.searchBarAnimationDuration,
            transitionDelay: v.searchBarMenuFadeInDelay,
            opacity: 1,
            transform: `translate(0, 0)`,
            height: "unset"
          }
        },
        [`& .${topNavigationSlotClassNames.searchBar}`]: {
          marginTop: pxToRem(3),
          transitionProperty: "transform",
          transitionDuration: v.searchBarAnimationDuration,
          ...p.showSearchBar && {
            transitionDelay: v.searchBarExpandDelay,
            transform: `translate(${pxToRem(-247)})`
          },
          ...!p.showSearchBar && {
            transitionDelay: v.searchBarCollapseDelay,
            transform: `translate(0)`
          }
        },
        [`& .${searchBarClassName}`]: {
          width: pxToRem(271),
          transition: `all ${v.searchBarAnimationDuration}`,
          ...p.showSearchBar && {
            transitionDelay: v.searchBarExpandDelay,
            width: pxToRem(860),
            background: "#ECECEC",
            borderColor: "transparent"
          },
          ...!p.showSearchBar && {
            transitionDelay: v.searchBarCollapseDelay
          }
        }
      },
      left: {
        gap: v.topNavigationDesktopItemsGap,
        width: pxToRem(924),
        position: "absolute",
        opacity: 1,
        transitionProperty: "opacity",
        transitionDuration: p.showSearchBar ? 0 : v.searchBarAnimationDuration,
        transitionDelay: p.showSearchBar ? 0 : v.menuItemsRevealDelay,
        ...p.showSearchBar && {
          opacity: 0
        }
      },
      rightActionToggles: {
        height: v.rightSizeButtonHeight,
        gridGap: pxToRem(12),
        marginLeft: pxToRem(20)
      }
    };
    return {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      alignContent: "flex-start",
      width: "100%",
      ...p.showSearchMenu && {
        height: "auto"
      },
      padding: v.topNavigationMobilePadding,
      ...p.desktopDevice && {
        [theme.screen.tablet]: desktopStyles.root,
        // desktop and small screen
        [theme.screen.max.shrankWindow]: desktopStyles.root
      },
      [`& .${topNavigationSlotClassNames.rightActionToggles}`]: {
        height: p.showFullLogo ? v.mobileRightSizeButtonHeightFullLogo : v.mobileRightSizeButtonHeight,
        [theme.screen.tablet]: desktopStyles.rightActionToggles,
        [theme.screen.max.shrankWindow]: desktopStyles.rightActionToggles,
        boxSizing: "border-box",
        [`& .${buttonClassName}`]: {
          color: v.topNavigationTopRightColor,
          "& svg": {
            "& circle": {
              stroke: v.topNavigationTopRightColor
            },
            "& circle + path": {
              stroke: v.topNavigationTopRightColor
            }
          }
        }
      },
      [`& .${topNavigationSlotClassNames.left}`]: {
        display: "flex",
        [theme.screen.tablet]: desktopStyles.left,
        [theme.screen.max.shrankWindow]: desktopStyles.left,
        [`&:has(.${topNavigationSlotClassNames.activeItem}) > *:not(.${topNavigationSlotClassNames.activeItem})`]: {
          [`& .${labelClassName}`]: {
            color: theme.siteVariables.colorScheme.default?.foregroundDisabled
          }
        }
      },
      [`& .${topNavigationSlotClassNames.logoAndRightSideWrapper}`]: {
        alignItems: !p.showFullLogo ? "flex-start" : "center",
        [theme.screen.tablet]: desktopStyles.logoAndRightSideWrapper,
        [theme.screen.max.shrankWindow]: desktopStyles.logoAndRightSideWrapper
      }
    };
  },
  mobileTopNav: ({ props: p, variables: v }) => ({
    position: "relative",
    [`& .${topNavigationSlotClassNames.logo}`]: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)"
    },
    [`& .${topNavigationSlotClassNames.menuButton}`]: {
      ...!p.expanded && {
        [`& .${svgIconClassName2}`]: {
          width: pxToRem(50),
          height: pxToRem(50)
        }
      },
      [`&.${buttonClassName}`]: {
        color: v.topNavigationTopRightColor,
        "& svg": {
          fill: v.topNavigationTopRightColor
        }
      }
    },
    ...p.expanded && {
      [`& .${topNavigationSlotClassNames.rightActionToggles}`]: {
        [`& .${buttonClassName}`]: {
          color: v.mobileSearchPlaceholderInputColor
        }
      },
      [`& .${topNavigationSlotClassNames.menuButton}`]: {
        [`& .${svgIconClassName2}`]: {
          color: v.mobileSearchPlaceholderInputColor,
          "& svg": {
            fill: v.mobileSearchPlaceholderInputColor
          }
        }
      }
    }
  }),
  divider: ({ props: p, variables: v }) => ({
    height: "1px",
    width: "100%",
    background: v.menuItemDividerColor,
    ...p.showMegamenu && !p.showSearchBar && {
      ...megaMenuContentAnimation(true)
    }
  }),
  textMenuItem: () => ({
    padding: `${pxArrayToRem([0, 16, 0, 16])}`
  })
};

// packages/aria/src/themes/base/components/Logo/logoStyles.ts
import { svgIconClassName as svgIconClassName3 } from "@archipro-design/icons";
var eraseOutEffect = {
  from: {
    opacity: 1
  },
  to: {
    opacity: 0
  }
};
var typingEffect = {
  from: {
    opacity: 0
  },
  to: {
    opacity: 1
  }
};
var logoAnimationEraseStep = 0.025;
var logoAnimationTypingStep = 0.3;
var animatedEraseoutLogo = (delay, type) => {
  return {
    animationName: type === "eraseOut" ? eraseOutEffect : typingEffect,
    animationIterationCount: 1,
    animationPlayState: "running",
    animationDelay: `${delay}s`,
    animationDuration: `${logoAnimationEraseStep * 2}s`,
    animationFillMode: "both",
    animationTimingFunction: "ease-in-out"
  };
};
var generateLogoLetterAnimations = (type) => {
  const svgStyleObjects = {};
  const baseChild = type === "typing" ? 2 : 1;
  const svgPaths = Array(9).fill(null).map((_14, index) => {
    const svgPath = `& g > path:nth-child(${index + baseChild})`;
    return {
      [svgPath]: {
        ...animatedEraseoutLogo(
          type === "eraseOut" ? (8 - index) * logoAnimationEraseStep : index * 0.025 + logoAnimationTypingStep,
          type
        )
      }
    };
  });
  svgPaths.forEach((obj) => Object.assign(svgStyleObjects, obj));
  return svgStyleObjects;
};
var logoStyles = {
  root: ({ props: p, variables: v }) => {
    return {
      "& svg": {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        ...!p.animationType && {
          fill: v.logoPrimaryColor,
          "& path": {
            fill: v.logoPrimaryColor
          },
          width: v.logoWidth,
          height: v.logoHeight
        }
      },
      [`& .${svgIconClassName3}`]: {
        width: "fit-content",
        height: "auto"
      }
    };
  },
  animatedMobileShortLogo: ({ variables: v }) => {
    return {
      "& path": {
        fill: v.logoPrimaryColor
      },
      height: v.animatedMobileShortLogoHeight,
      width: v.animatedMobileShortLogoWidth
    };
  },
  animatedLogoWrapper: () => {
    return {
      position: "relative"
    };
  },
  animatedFullLogo: ({ variables: v, props: p }) => {
    return {
      "& path": {
        fill: v.logoPrimaryColor
      },
      position: "absolute",
      height: "auto",
      width: v.animatedFullLogoWidth,
      ...generateLogoLetterAnimations(p.animationType),
      ...p.animationType === "typing" && {
        "& g > path": {
          opacity: 0,
          fill: v.logoPrimaryColor
        }
      }
    };
  },
  animatedShortLogo: ({ variables: v, props: p }) => {
    return {
      "& path": {
        fill: v.logoPrimaryColor
      },
      position: "absolute",
      height: p.animationType === "typing" ? v.animatedShortLogoHeight : "auto",
      width: p.animationType === "typing" ? "auto" : v.animatedShortLogoWidth
    };
  }
};

// packages/aria/src/themes/base/components/TopNavigation/navigationItemStyles.ts
var navigationItemStyles = {
  root: ({ variables: v, theme }) => {
    return {
      display: "flex",
      flexDirection: "column",
      cursor: "pointer",
      width: "fit-content",
      color: v.topNavNavigationItemColor,
      [`& .${labelClassName}`]: {
        lineHeight: theme.siteVariables.lineHeightDefault
      },
      [`& .${navigationItemSlotClassNames.label}`]: {
        [`&.${labelClassName}`]: {
          color: v.topNavNavigationItemColor,
          letterSpacing: theme.siteVariables.letterSpacingMedium,
          ...theme.siteVariables.textStyles.Heading.Roman55
        }
      },
      [`& .${navigationItemSlotClassNames.labelPrefix}`]: {
        marginBottom: pxToRem(5),
        [`&.${labelClassName}`]: {
          lineHeight: theme.siteVariables.lineHeightDefault,
          letterSpacing: theme.siteVariables.letterSpacingSmall,
          color: v.topNavNavigationItemPrefixColor
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/Typography/Caption/captionStyles.ts
var captionStyles = {
  root: ({ props, variables: v, theme }) => {
    return {
      color: v.captionColor,
      ...handleCommonTypographyStyles({
        props,
        siteVariables: theme.siteVariables
      }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ...mapCaptionVariantToFontStyles(props, theme),
      ...v.fontSize && { fontSize: v.fontSize },
      ...v.letterSpacing && { letterSpacing: v.letterSpacing },
      ...v.lineHeight && { lineHeight: v.lineHeight },
      ...v.fontFamily && { fontFamily: v.fontFamily },
      ...v.fontWeight && { fontWeight: v.fontWeight }
    };
  }
};
function mapCaptionVariantToFontStyles({ variant = "01" }, theme) {
  const map = {
    lineHeights: {
      "01": theme.siteVariables.lineHeightDefault,
      "02": theme.siteVariables.lineHeightDefault,
      "03": theme.siteVariables.lineHeightDefault,
      "03s": theme.siteVariables.lineHeightSmaller,
      "03-alt": 1.5,
      "04": pxToRem(18),
      "05": pxToRem(15),
      "05-alt": 1.25,
      "06": pxToRem(12.5)
    },
    letterSpacing: {
      "01": theme.siteVariables.letterSpacingNormal,
      "02": theme.siteVariables.letterSpacingNormal,
      "03": theme.siteVariables.letterSpacingNormal,
      "03s": theme.siteVariables.letterSpacingNormal,
      "03-alt": theme.siteVariables.letterSpacingNormal,
      "04": theme.siteVariables.letterSpacingNormal,
      "05": "0.08em",
      "05-alt": theme.siteVariables.letterSpacingNormal,
      "06": "0.08em"
    }
  };
  const fontSize = theme.siteVariables.fontSizes[`caption${variant}`];
  const lineHeight = map.lineHeights[variant];
  const letterSpacing = map.letterSpacing[variant];
  return {
    fontSize,
    lineHeight,
    letterSpacing
  };
}

// packages/aria/src/themes/base/components/Tile/ProjectTile/projectTileStyles.ts
import { svgIconClassName as svgIconClassName4 } from "@archipro-design/icons";

// packages/aria/src/themes/base/components/Tile/dynamic-sizing.ts
var dynamicSizing = (containerWidth, gapWidth, ratio) => {
  const containerSize = pxToRem(containerWidth);
  const imageSize = containerWidth - gapWidth * 2;
  const imageSizes = {
    width: pxToRem(imageSize),
    height: pxToRem(imageSize * ratio)
  };
  return { containerSize, imageSizes };
};
var dynamicSizingStyle = (size, gap, scaleRatio) => {
  const { containerSize, imageSizes } = dynamicSizing(size, gap, scaleRatio);
  return {
    width: containerSize,
    maxWidth: "100%",
    [`& .${tileImageClassName}`]: {
      ...imageSizes,
      [`& > .${tileOverlayClassName}`]: {
        width: "100%",
        height: "100%"
      }
    },
    [`& .${tileImageSlotClassNames.imageContainer}`]: {
      ...imageSizes
    }
  };
};

// packages/aria/src/themes/base/components/Tile/ProjectTile/projectTileStyles.ts
var dynamicStyle = (size, gap) => {
  const scaleRatio = 190 / 286;
  return dynamicSizingStyle(size, gap, scaleRatio);
};
var projectTileStyles = {
  // @ts-expect-error Project getting stale, don't want to fix whats not broken
  root: ({ props: p, theme, variables: v }) => {
    return {
      display: "block",
      // Incase "as" type is <a/> which is inline
      position: "relative",
      textDecoration: "none",
      width: v.width,
      padding: pxToRem(0),
      cursor: "pointer",
      [`& .${captionClassName}`]: {
        color: v.color
      },
      [`& .${labelClassName}`]: {
        color: v.color
      },
      [`& .${tileOverlayClassName}`]: {
        padding: p.variant === "02" ? pxToRem(12) : pxToRem(10),
        [`& .${boxClassName}`]: {
          display: "flex"
        }
      },
      [`& .${projectTileSlotClassNames.contentTile}`]: {
        padding: v.contentTilePadding
      },
      [`& .${projectTileSlotClassNames.projectName}`]: p.newDesignEnabled ? {
        marginBottom: pxToRem(2),
        marginTop: p.variant === "02" ? pxToRem(24) : pxToRem(14),
        padding: p.size === "small" ? 0 : pxArrayToRem([0, 12]),
        fontWeight: theme.siteVariables.fontWeightRegular,
        letterSpacing: pxToRem(-0.16),
        color: theme.siteVariables.colors.primitive.black,
        fontSize: theme.siteVariables.fontSizes["caption02"],
        lineHeight: 1.3,
        [`& .${captionClassName}`]: {
          fontWeight: theme.siteVariables.fontWeightRegular,
          letterSpacing: pxToRem(-0.16),
          color: theme.siteVariables.colors.primitive.black,
          lineHeight: 1.3,
          fontSize: theme.siteVariables.fontSizes["caption02"]
        },
        [theme.screen.laptop]: {
          padding: 0,
          marginTop: p.variant === "01" ? pxToRem(24) : pxToRem(18),
          lineHeight: 1.3
        }
      } : {
        marginBottom: pxToRem(2),
        ...mapCaptionVariantToFontStyles(
          { variant: "04" },
          theme
        ),
        paddingLeft: p.size === "small" ? 0 : pxToRem(18),
        marginTop: p.size === "small" ? pxToRem(8) : pxToRem(18),
        [theme.screen.laptop]: {
          paddingLeft: pxToRem(0),
          marginBottom: pxToRem(4),
          ...mapLabelVariantToFontStyles(
            { variant: "04" },
            theme
          ),
          marginTop: pxToRem(26),
          ...theme.siteVariables.textStyles.Heading.Roman55,
          textTransform: "uppercase"
        }
      },
      [`& .${projectTileSlotClassNames.professionalName}`]: p.newDesignEnabled ? {
        padding: p.size === "small" ? 0 : pxArrayToRem([0, 12]),
        marginBottom: pxToRem(4),
        color: "#717171",
        fontWeight: theme.siteVariables.fontWeightLight,
        fontSize: theme.siteVariables.fontSizes["caption03"],
        [`& .${captionClassName}`]: {
          color: "#717171",
          fontWeight: theme.siteVariables.fontWeightLight,
          fontSize: theme.siteVariables.fontSizes["caption03"]
        },
        [theme.screen.laptop]: {
          padding: 0
        }
      } : {
        ...mapCaptionVariantToFontStyles(
          { variant: "04" },
          theme
        ),
        paddingLeft: p.size === "small" ? 0 : pxToRem(18),
        textDecoration: "underline",
        [theme.screen.laptop]: {
          textDecoration: "none",
          paddingLeft: pxToRem(0),
          ...mapLabelVariantToFontStyles(
            { variant: "04" },
            theme
          )
        }
      },
      [`& .${projectTileSlotClassNames.viewCount}`]: {
        pointerEvents: "none",
        position: "absolute",
        right: 0,
        height: pxToRem(32),
        gap: pxToRem(1),
        [`& .${labelClassName}`]: {
          fontSize: pxToRem(13)
        },
        [`& .${svgIconClassName4}`]: {
          width: pxToRem(18),
          height: pxToRem(14)
        },
        [`& .${buttonClassName}`]: {
          [`&:first-of-type .${labelClassName}`]: {
            marginLeft: pxToRem(-2)
          },
          [`&:last-of-type .${labelClassName}`]: {
            marginLeft: pxToRem(2)
          }
        }
      },
      [`& .${projectTileSlotClassNames.contentBlock}`]: {
        justifyContent: "space-between",
        width: "100%",
        [theme.screen.laptop]: {
          minHeight: pxToRem(95)
        }
      },
      [`& .${projectTileSlotClassNames.tag}`]: {
        marginTop: p.newDesignEnabled ? pxToRem(7) : pxToRem(14),
        fontSize: p.newDesignEnabled ? pxToRem(12) : pxToRem(17),
        letterSpacing: theme.siteVariables.letterSpacingMedium,
        color: p.newDesignEnabled ? theme.siteVariables.colors.grey["300"] : theme.siteVariables.colors.grey["200"],
        fontWeight: 200,
        paddingRight: pxToRem(15),
        [theme.screen.laptop]: {
          paddingRight: "unset"
        }
      },
      ":hover": {
        [`& .${tileOverlayClassName}`]: {
          opacity: 1
        }
      },
      [`& .${adminCaptionClassName}`]: {
        marginTop: pxToRem(14)
      },
      ...p.size === "dynamic" && {
        ...dynamicStyle(v.dynamicSizingWidth, v.dynamicSizingGapWidth)
      },
      [`& .${carouselSlotClassNames.paddleContainer}`]: {
        display: "none"
      },
      [theme.screen.tablet]: {
        [theme.screen.nonTouch]: {
          [`& .${carouselClassName}:hover`]: {
            [`& .${carouselSlotClassNames.paddleContainer}`]: {
              opacity: 1,
              [`& button`]: {
                bottom: 0
              }
            }
          },
          [`& .${carouselSlotClassNames.paddleContainer}`]: {
            transition: `all 200ms linear`,
            opacity: 0,
            height: pxToRem(88),
            zIndex: 1,
            top: "auto",
            bottom: 0,
            width: "100%",
            padding: 0,
            display: "flex",
            justifyContent: "space-between",
            background: "linear-gradient(0deg, rgba(0, 0, 0, 0.4) -32.39%, rgba(0, 0, 0, 0) 85.23%)",
            [`& button`]: {
              position: "relative",
              width: "50%",
              right: 0,
              left: 0,
              bottom: pxToRem(-30),
              transition: "bottom 150ms ease",
              height: pxToRem(58),
              background: "transparent",
              [`& svg`]: {
                fill: theme.siteVariables.colors.primitive.white
              },
              [`&:first-of-type`]: {
                paddingLeft: pxToRem(
                  v.paginationPadding || 140
                )
              },
              [`&:last-of-type`]: {
                paddingRight: pxToRem(
                  v.paginationPadding || 140
                )
              },
              [`&:focus-visible`]: {
                borderColor: "transparent",
                borderStyle: "none",
                outline: "none",
                borderWidth: 0,
                ":after": {
                  borderColor: "transparent",
                  borderStyle: "none",
                  outline: "none",
                  borderWidth: 0
                },
                ":before": {
                  borderColor: "transparent",
                  borderStyle: "none",
                  outline: "none",
                  borderWidth: 0
                }
              }
            }
          }
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/Image/imageStyles.ts
function getPlaceholderColor(props, v) {
  switch (props.placeholderColor) {
    case "dark":
      return v.placeholderBackgroundColorDark;
    case "transparent":
      return "transparent";
    case "light":
    default:
      return v.placeholderBackgroundColorLight;
  }
}
var imageStyles = {
  root: ({ theme, props, variables: v }) => {
    const genericStyle = {
      boxSizing: "border-box",
      display: "flex",
      overflow: "hidden",
      borderRadius: v.borderRadius,
      width: "initial",
      height: "initial",
      background: v.mixBlendMode === "multiply" ? theme.siteVariables.colors.white["200A"] : getPlaceholderColor(props, v),
      opacity: 1,
      border: 0,
      margin: 0,
      padding: 0,
      lineHeight: 1,
      ["& img"]: { mixBlendMode: v.mixBlendMode }
    };
    if (props.layout === "fill") {
      return {
        ...genericStyle,
        display: "flex",
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      };
    }
    if (props.width && props.height) {
      if (props.layout === "responsive") {
        return {
          ...genericStyle,
          display: "flex",
          position: "relative"
        };
      }
      if (props.layout === "intrinsic") {
        return {
          ...genericStyle,
          display: "inline-flex",
          position: "relative",
          maxWidth: "100%"
        };
      }
      if (props.layout === "fixed") {
        return {
          ...genericStyle,
          display: "inline-flex",
          position: "relative",
          width: pxToRem(props.width),
          height: pxToRem(props.height)
        };
      }
    }
    return genericStyle;
  }
};

// packages/aria/src/themes/base/components/Grid/gridStyles.ts
var getCSSTemplateValue = (template) => {
  const templateAsNumber = Number(template);
  return !isNaN(templateAsNumber) && templateAsNumber > 0 ? `repeat(${template}, 1fr)` : String(template);
};
var gridStyles = {
  root: ({
    props,
    variables: { height, width, defaultColumnCount, gridGap, padding },
    theme: { siteVariables }
  }) => {
    const {
      rows,
      columns = !props.rows && defaultColumnCount,
      inline
    } = props;
    return {
      height,
      width,
      display: inline ? "inline-grid" : "grid",
      justifyContent: "space-evenly",
      ...padding && {
        padding: siteVariables.spaces[padding]
      },
      ...gridGap && {
        gridGap: siteVariables.spaces[gridGap]
      },
      ...rows && !columns && { gridAutoFlow: "column" },
      ...rows && {
        gridTemplateRows: getCSSTemplateValue(rows),
        msGridRows: getCSSTemplateValue(rows)
      },
      ...columns && {
        gridTemplateColumns: getCSSTemplateValue(columns),
        msGridColumns: getCSSTemplateValue(columns)
      },
      "& > *": { outlineOffset: "-3px" }
    };
  }
};

// packages/aria/src/themes/base/components/Flex/utils.ts
var toFlexAlignment = (propValue) => {
  const trimmedValue = propValue.trim();
  if (trimmedValue === "start" || trimmedValue === "end") {
    return `flex-${trimmedValue}`;
  }
  return trimmedValue;
};
var toFlexItemSizeValues = (sizeValue) => ({
  flexBasis: sizeValue
});

// packages/aria/src/themes/base/components/Flex/flexStyles.ts
var flexStyles = {
  root: ({ props: p, theme: { siteVariables } }) => ({
    display: "flex",
    ...p.debug && { border: "1px dotted grey", background: "lightgrey" },
    ...p.inline && { display: "inline-flex" },
    ...p.column && { flexDirection: "column" },
    ...p.hAlign && (p.column ? { alignItems: toFlexAlignment(p.hAlign) } : { justifyContent: toFlexAlignment(p.hAlign) }),
    ...p.vAlign && (p.column ? { justifyContent: toFlexAlignment(p.vAlign) } : { alignItems: toFlexAlignment(p.vAlign) }),
    ...p.space && { justifyContent: `space-${p.space}` },
    ...p.wrap && { flexWrap: "wrap" },
    ...p.fill && {
      width: "100%",
      height: "100%"
    },
    ...p.padding && { padding: siteVariables.spaces[p.padding] },
    ...p.gap && {
      "> *:not(:last-child)": {
        [p.column ? "marginBottom" : "marginRight"]: siteVariables.spaces[p.gap]
      }
    }
  })
};

// packages/aria/src/themes/base/components/Flex/flexItemStyles.ts
var flexItemStyles = {
  root: ({ props: p, variables: v }) => {
    return {
      ...p.align && { alignSelf: toFlexAlignment(p.align) },
      ...p.size && toFlexItemSizeValues(
        Object.prototype.hasOwnProperty.call(v, p.size) ? v[p.size] : p.size
      ),
      ...typeof p.shrink === "number" && { flexShrink: p.shrink },
      ...p.shrink === false && { flexShrink: 0 },
      ...p.grow && { flexGrow: Number(p.grow) },
      ...p.grow === true && { flexGrow: 1 },
      ...p.push && (p.flexDirection === "column" ? { marginTop: "auto" } : { marginLeft: "auto" })
    };
  }
};

// packages/aria/src/themes/base/components/Box/boxStyles.ts
var boxStyles = {
  root: ({ variables: v }) => {
    return {
      backgroundColor: v.boxBackgroundColor
    };
  }
};

// packages/aria/src/themes/base/components/Tile/ArticleTile/articleTileStyles.ts
var articleTileStyles = {
  root: ({ variables: v, theme }) => {
    return {
      display: "block",
      // Incase "as" type is <a/> which is inline
      position: "relative",
      textDecoration: "none",
      width: v.width,
      padding: pxToRem(14),
      cursor: "pointer",
      [`& .${tileImageClassName}`]: {
        marginBottom: pxToRem(18)
      },
      [`& .${SubheaderClassName}`]: {
        marginTop: pxToRem(16),
        fontSize: pxToRem(28),
        lineHeight: pxToRem(34),
        letterSpacing: theme.siteVariables.letterSpacingNormal,
        ...theme.siteVariables.textStyles.Article.Roman55,
        ":hover": {
          textDecoration: "underline"
        }
      },
      [`& .${tileOverlayClassName}`]: {
        opacity: 0
      },
      ":hover": {
        [`& .${tileOverlayClassName}`]: {
          opacity: 1
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/Tile/ProfessionalTile/professionalTileStyles.ts
import { svgIconClassName as svgIconClassName5 } from "@fluentui/react-northstar";
var dynamicStyle2 = (size, gap, variant = "01") => {
  let scaleRatio = 150 / 286;
  if (variant === "03") {
    scaleRatio = 464 / 811;
  }
  return dynamicSizingStyle(size, gap, scaleRatio);
};
var professionalTileStyles = {
  root: ({ variables: v }) => {
    return {
      cursor: "pointer",
      position: "relative",
      [`& .${labelClassName}`]: {
        color: v.color
      },
      [`& .${captionClassName}`]: {
        color: v.color
      },
      [`& .${professionalTileSlotClassNames.headerWrap}`]: {
        justifyContent: "space-between"
      },
      [`& .${tileOverlayClassName}`]: {
        // hot buttons
        [`& .${hotButtonsClassName}`]: {
          [`& .${buttonClassName}`]: {
            padding: pxToRem(8)
          },
          [`& .${svgIconClassName5}`]: {
            height: pxToRem(20),
            width: pxToRem(20)
          }
        }
      }
    };
  },
  // @ts-expect-error Project getting stale, don't want to fix whats not broken
  variant01: ({ theme, variables: v, props: p }) => {
    return {
      width: v.width,
      padding: v.tilePadding,
      ":hover": {
        [`& .${tileOverlayClassName}`]: {
          opacity: 1
        }
      },
      // Display the tileoverlay on mobile only and reduce its size
      [theme.screen.max.laptop]: {
        [`& .${tileOverlayClassName}`]: {
          opacity: 1,
          [`& .${boxClassName}`]: {
            display: "flex"
          },
          [`& .${buttonClassName}`]: {
            padding: pxToRem(8)
          },
          [`& .${svgIconClassName5}`]: {
            height: pxToRem(16),
            width: pxToRem(16)
          }
        }
      },
      [`& .${professionalTileSlotClassNames.content}`]: {
        flexDirection: "column",
        alignItems: "flex-start",
        [`& .${avatarClassName}`]: {
          marginTop: p.avatarSize ? pxToRem(p.avatarSize * -1 + (v.avatarYMargin ?? 30)) : pxToRem(900),
          marginBottom: p.size === "xsmall" ? pxToRem(16) : pxToRem(29),
          zIndex: theme.siteVariables.zIndexes.foreground
        }
      },
      [`& .${professionalTileSlotClassNames.title}`]: {
        marginBottom: p.size === "xsmall" ? pxToRem(6) : pxToRem(4),
        // Only applied for Mobile
        [theme.screen.max.tablet]: {
          textTransform: "uppercase",
          ...theme.siteVariables.textStyles.Heading.Medium65
        },
        [theme.screen.laptop]: {
          marginBottom: pxToRem(8)
        }
      },
      [`& .${professionalTileSlotClassNames.services}`]: {
        marginTop: p.size === "xsmall" ? pxToRem(6) : pxToRem(4),
        [theme.screen.laptop]: {
          marginTop: `1em`
        }
      },
      [`& .${professionalTileSlotClassNames.category}`]: {
        letterSpacing: "0.08em",
        lineHeight: pxToRem(17)
      },
      [`& .${professionalTileSlotClassNames.location}`]: {
        letterSpacing: "0.08em",
        lineHeight: pxToRem(17),
        textTransform: "inherit"
      },
      [`& .${professionalTileSlotClassNames.servicingArea}`]: {
        letterSpacing: "0.08em",
        lineHeight: pxToRem(17),
        textTransform: "inherit"
      },
      [`& .${professionalTileSlotClassNames.review}`]: {
        marginTop: pxToRem(40),
        [`& .${captionClassName}`]: {
          paddingLeft: pxToRem(36)
        }
      },
      [`& .${adminCaptionClassName}`]: {
        padding: pxArrayToRem([0, 24]),
        marginTop: pxToRem(14)
      },
      [`& .${professionalTileSlotClassNames.tag}`]: {
        marginTop: pxToRem(14),
        fontSize: pxToRem(17),
        letterSpacing: theme.siteVariables.letterSpacingMedium,
        color: theme.siteVariables.colors.grey["200"],
        fontWeight: 200,
        paddingRight: pxToRem(15),
        [theme.screen.laptop]: {
          paddingRight: "unset"
        }
      },
      [`& .${professionalTileSlotClassNames.headerWrap}`]: {
        paddingLeft: pxToRem(24),
        width: "100%"
      },
      [`& .${professionalTileSlotClassNames.textWrap}`]: {
        padding: pxArrayToRem([0, 24])
      },
      ...p.size === "dynamic" && {
        ...dynamicStyle2(
          v.dynamicSizingWidth,
          v.dynamicSizingGapWidth,
          "01"
        )
      }
    };
  },
  variant02: ({ theme, variables: v, props: p }) => {
    return {
      width: v.width,
      padding: pxToRem(0),
      [`& .${tileOverlayClassName}`]: {
        opacity: 1,
        [`& .${boxClassName}`]: {
          display: "flex"
        },
        [`& .${buttonClassName}`]: {
          padding: pxToRem(8)
        },
        [`& .${svgIconClassName5}`]: {
          height: pxToRem(16),
          width: pxToRem(16)
        }
      },
      [`& .${professionalTileSlotClassNames.content}`]: {
        flexDirection: "row",
        alignItems: "center",
        marginTop: p.size === "large" ? pxToRem(14) : pxToRem(8),
        marginLeft: p.size === "small" ? pxToRem(0) : pxToRem(4),
        "& > div": {
          justifyContent: "space-between",
          gap: pxToRem(4)
        },
        [`& .${avatarClassName}`]: {
          marginRight: p.size === "large" ? pxToRem(12) : pxToRem(4),
          flexShrink: 0,
          alignSelf: "flex-start",
          zIndex: theme.siteVariables.zIndexes.foreground
        },
        [`& .${professionalTileSlotClassNames.title}`]: {
          ...mapCaptionVariantToFontStyles({ variant: "04" }, theme),
          ...p.size === "large" && {
            ...mapCaptionVariantToFontStyles(
              { variant: "02" },
              theme
            )
          }
        },
        [`& .${professionalTileSlotClassNames.category}`]: {
          ...mapCaptionVariantToFontStyles({ variant: "06" }, theme),
          ...p.size === "large" && {
            ...mapCaptionVariantToFontStyles(
              { variant: "05" },
              theme
            )
          }
        },
        [`& .${professionalTileSlotClassNames.memberSince}`]: {
          ...mapCaptionVariantToFontStyles({ variant: "06" }, theme)
        }
      },
      [`& .${professionalTileSlotClassNames.review}`]: {
        marginTop: pxToRem(12),
        marginLeft: p.size === "small" ? pxToRem(0) : pxToRem(4),
        [`& .${captionClassName}`]: {
          ...mapCaptionVariantToFontStyles({ variant: "05" }, theme),
          paddingLeft: pxToRem(12)
        },
        [`& .${svgIconClassName5}`]: {
          height: pxToRem(14),
          width: pxToRem(14)
        }
      },
      [`& .${adminCaptionClassName}`]: {
        marginTop: pxToRem(14)
      },
      ...p.size === "dynamic" && {
        ...dynamicStyle2(
          v.dynamicSizingWidth,
          v.dynamicSizingGapWidth,
          "02"
        )
      }
    };
  },
  // @ts-expect-error Project getting stale, don't want to fix whats not broken
  variant03: ({ theme, variables: v, props: p }) => {
    return {
      width: v.width,
      padding: v.tilePadding,
      ":hover": {
        [`& .${tileOverlayClassName}`]: {
          opacity: 1
        }
      },
      [`& .${professionalTileSlotClassNames.content}`]: {
        alignItems: "flex-start",
        padding: pxArrayToRem([28, 0, 0]),
        [`& .${avatarClassName}`]: {
          zIndex: theme.siteVariables.zIndexes.foreground,
          marginRight: pxToRem(28)
        }
      },
      [`& .${professionalTileSlotClassNames.title}`]: {
        marginBottom: pxToRem(9),
        [`&.${labelClassName}`]: {
          ...theme.siteVariables.textStyles.Heading.Roman55,
          textTransform: "uppercase",
          fontSize: pxToRem(16)
        }
      },
      [`& .${professionalTileSlotClassNames.services}`]: {
        marginTop: pxToRem(7),
        lineHeight: pxToRem(17),
        [`& .${captionClassName}`]: {
          lineHeight: pxToRem(17),
          letterSpacing: "0.02em"
        }
      },
      [`& .${professionalTileSlotClassNames.category}`]: {
        letterSpacing: "0.08em",
        lineHeight: pxToRem(17)
      },
      [`& .${professionalTileSlotClassNames.location}`]: {
        letterSpacing: "0.08em",
        lineHeight: pxToRem(17)
      },
      [`& .${professionalTileSlotClassNames.review}`]: {
        marginTop: pxToRem(40),
        [`& .${captionClassName}`]: {
          paddingLeft: pxToRem(36)
        }
      },
      [`& .${adminCaptionClassName}`]: {
        marginTop: pxToRem(14)
      },
      ...p.size === "dynamic" && {
        ...dynamicStyle2(
          v.dynamicSizingWidth,
          v.dynamicSizingGapWidth,
          "03"
        )
      }
    };
  },
  // @ts-expect-error Project getting stale, don't want to fix whats not broken
  variant04: ({ theme, variables: v, props: p }) => {
    return {
      width: v.width,
      padding: v.tilePadding,
      ":hover": {
        [`& .${tileOverlayClassName}`]: {
          opacity: 1
        }
      },
      // Display the tileoverlay on mobile only and reduce its size
      [theme.screen.max.laptop]: {
        [`& .${tileOverlayClassName}`]: {
          opacity: 1,
          [`& .${boxClassName}`]: {
            display: "flex"
          },
          [`& .${buttonClassName}`]: {
            padding: pxToRem(8)
          },
          [`& .${svgIconClassName5}`]: {
            height: pxToRem(16),
            width: pxToRem(16)
          }
        }
      },
      [`& .${professionalTileSlotClassNames.content}`]: {
        flexDirection: "column",
        alignItems: "flex-start",
        [`& .${avatarClassName}`]: {
          marginTop: p.avatarSize ? pxToRem(p.avatarSize * -1 + (v.avatarYMargin ?? 30)) : pxToRem(900),
          marginBottom: p.size === "xsmall" ? pxToRem(16) : pxToRem(24),
          zIndex: theme.siteVariables.zIndexes.foreground,
          [theme.screen.laptop]: {
            marginBottom: pxToRem(18)
          }
        }
      },
      [`& .${professionalTileSlotClassNames.title}`]: {
        color: theme.siteVariables.colors.primitive.black,
        letterSpacing: pxToRem(-0.18),
        marginBottom: p.size === "xsmall" ? pxToRem(6) : pxToRem(2),
        fontSize: pxToRem(18),
        [theme.screen.laptop]: {
          fontSize: pxToRem(18)
        }
      },
      [`& .${professionalTileSlotClassNames.services}`]: {
        "-webkit-line-clamp": "unset",
        maxHeight: pxToRem(30),
        overflow: "hidden",
        maxWidth: `calc(100vw - ${pxToRem(38)})`,
        display: "flex",
        marginLeft: pxToRem(-10),
        [theme.screen.laptop]: {
          maxWidth: p.tagsToShowInFull && p.tagsToShowInFull > 1 ? pxToRem(396) : pxToRem(283)
        }
      },
      [`& .${professionalTileSlotClassNames.service}`]: {
        color: "#717171",
        padding: pxArrayToRem([6, 10]),
        backgroundColor: `#F4F4F4`,
        marginRight: pxToRem(4),
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        flexShrink: 1,
        maxWidth: pxToRem(200),
        [`&:first-of-type`]: {
          flexShrink: 0
        }
      },
      [`& .${professionalTileSlotClassNames.serviceMore}`]: {
        color: "#717171",
        padding: pxArrayToRem([6, 10]),
        backgroundColor: `#F4F4F4`,
        flexShrink: 0,
        whiteSpace: "nowrap"
      },
      [`& .${professionalTileSlotClassNames.category}`]: {
        color: "#717171",
        marginBottom: pxToRem(20)
      },
      [`& .${professionalTileSlotClassNames.location}`]: {
        color: "#717171",
        marginBottom: pxToRem(5),
        [`&:empty`]: {
          display: "none"
        }
      },
      [`& .${professionalTileSlotClassNames.servicingArea}`]: {
        color: "#717171",
        marginBottom: pxToRem(18),
        [`&:empty`]: {
          display: "none"
        }
      },
      [`& .${professionalTileSlotClassNames.review}`]: {
        marginTop: pxToRem(40),
        [`& .${captionClassName}`]: {
          paddingLeft: pxToRem(36)
        }
      },
      [`& .${adminCaptionClassName}`]: {
        padding: pxArrayToRem([0, 24]),
        marginTop: pxToRem(14)
      },
      [`& .${professionalTileSlotClassNames.tag}`]: {
        marginTop: pxToRem(14),
        fontSize: pxToRem(17),
        letterSpacing: theme.siteVariables.letterSpacingMedium,
        color: theme.siteVariables.colors.grey["200"],
        fontWeight: 200,
        paddingRight: pxToRem(15),
        [theme.screen.laptop]: {
          paddingRight: "unset"
        }
      },
      [`& .${professionalTileSlotClassNames.headerWrap}`]: {
        paddingLeft: pxToRem(24),
        width: "100%"
      },
      [`& .${professionalTileSlotClassNames.textWrap}`]: {
        padding: pxArrayToRem([0, 24])
      },
      ...p.size === "dynamic" && {
        ...dynamicStyle2(
          v.dynamicSizingWidth,
          v.dynamicSizingGapWidth,
          "01"
        )
      }
    };
  }
};

// packages/aria/src/themes/base/components/Avatar/avatarStyles.ts
var avatarStyles = {
  root: ({ props: p, variables: v, theme }) => {
    return {
      boxSizing: "border-box",
      borderColor: theme.siteVariables.colors.grey["100"],
      borderWidth: v.showBorder ? v.borderWidth : 0,
      borderStyle: "solid",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: v.avatarBackgroundColor,
      borderRadius: p.shape === "square" ? 0 : v.borderRadius,
      padding: v.avatarPadding,
      ...p.size && {
        width: pxToRem(p.size),
        minWidth: pxToRem(p.size)
      },
      ...p.size && {
        height: pxToRem(p.size)
      },
      overflow: "hidden",
      [`& .${captionClassName}`]: {
        color: v.initialsColor,
        lineHeight: "normal"
      },
      [`& .${avatarSlotClassNames.imageContainer}`]: {
        position: "relative",
        height: "100%",
        width: "100%"
      },
      [`& .${avatarSlotClassNames.image}`]: {
        backgroundColor: v.avatarImageBackgroundColor,
        [`& img`]: {
          mixBlendMode: p.multiply ? "multiply" : "initial"
        }
      },
      [`& .${avatarSlotClassNames.initials}`]: {
        color: v.initialsColor
      }
    };
  }
};

// packages/aria/src/themes/base/components/Tile/ProductTile/productTileStyles.ts
import { svgIconClassName as svgIconClassName6 } from "@archipro-design/icons";
import { labelClassName as labelClassName2 } from "@fluentui/react-northstar";
var dynamicStyle3 = (size, gap) => {
  const scaleRatio = 1;
  return dynamicSizingStyle(size, gap, scaleRatio);
};
var productTileStyles = {
  root: ({ props: p, theme, variables: v }) => {
    return {
      display: "block",
      // Incase "as" type is <a/> which is inline
      cursor: "pointer",
      position: "relative",
      ":hover": {
        [`& .${tileOverlayClassName}`]: {
          opacity: 1
        }
      },
      [`& .${productTileSlotClassNames.variant}`]: {
        borderRadius: "50%"
      },
      [`& .${adminCaptionClassName}`]: {
        marginTop: pxToRem(14),
        [`& .${captionClassName}`]: {
          fontSize: pxToRem(9),
          letterSpacing: pxToRem(0.02)
        }
      },
      [`& .${productTileSlotClassNames.professional}`]: {
        ...p.newDesignEnabled ? {
          fontWeight: theme.siteVariables.fontWeightLight,
          fontSize: pxToRem(16),
          [theme.screen.laptop]: {
            color: theme.siteVariables.colors.grey["400"]
          }
        } : {
          textDecoration: "underline",
          marginBottom: "1em",
          [`&:empty`]: {
            marginBottom: 0
          }
        }
      },
      [`& .${carouselSlotClassNames.paddleContainer}`]: {
        display: "none"
      },
      [`& .${productTileSlotClassNames.downloads}`]: {
        gap: pxToRem(6),
        margin: pxArrayToRem([0, 12]),
        [`& .${svgIconClassName6}`]: {
          height: pxToRem(27),
          width: pxToRem(20)
        }
      },
      [theme.screen.tablet]: {
        [`& .${productTileSlotClassNames.downloads}`]: {
          gap: pxToRem(10),
          margin: 0,
          [`& .${svgIconClassName6}`]: {
            height: pxToRem(41),
            width: pxToRem(33)
          }
        },
        [theme.screen.nonTouch]: {
          [`& .${carouselClassName}:hover`]: {
            [`& .${carouselSlotClassNames.paddleContainer}`]: {
              opacity: 1,
              [`& button`]: {
                bottom: 0
              }
            }
          },
          [`& .${carouselSlotClassNames.paddleContainer}`]: {
            transition: `all 150ms ease`,
            opacity: 0,
            height: pxToRem(88),
            zIndex: 1,
            top: "auto",
            bottom: 0,
            width: "100%",
            padding: 0,
            display: "flex",
            justifyContent: "space-between",
            background: "linear-gradient(0deg, rgba(0, 0, 0, 0.4) -32.39%, rgba(0, 0, 0, 0) 85.23%)",
            [`& button`]: {
              position: "relative",
              width: "50%",
              right: 0,
              left: 0,
              bottom: pxToRem(-30),
              transition: "bottom 150ms ease",
              height: pxToRem(58),
              borderRadius: 0,
              background: "transparent",
              [`& svg`]: {
                fill: theme.siteVariables.colors.primitive.white
              },
              [`&:first-of-type`]: {
                paddingLeft: pxToRem(v.paginationPadding || 85)
              },
              [`&:last-of-type`]: {
                paddingRight: pxToRem(
                  v.paginationPadding || 85
                )
              },
              [`&:focus-visible`]: {
                borderColor: "transparent",
                borderStyle: "none",
                outline: "none",
                borderWidth: 0,
                ":after": {
                  borderColor: "transparent",
                  borderStyle: "none",
                  outline: "none",
                  borderWidth: 0
                },
                ":before": {
                  borderColor: "transparent",
                  borderStyle: "none",
                  outline: "none",
                  borderWidth: 0
                }
              }
            }
          }
        }
      }
    };
  },
  // @ts-expect-error Project getting stale, don't want to fix whats not broken
  variant01: ({ theme, variables: v, props: p }) => {
    return {
      width: `calc(${p.imageHeight} + ${v.tilePadding} * 2)`,
      padding: v.tilePadding,
      ":hover": {
        [`& .${tileOverlayClassName}`]: {
          opacity: 1
        }
      },
      [`& .${captionClassName}`]: {
        color: v.color
      },
      [`& .${labelClassName2}`]: {
        color: v.color
      },
      [`& .${productTileSlotClassNames.content}`]: {
        marginTop: p.size === "small" ? pxToRem(16) : pxToRem(18)
      },
      [`& .${productTileSlotClassNames.product}`]: {
        ...p.newDesignEnabled ? {
          fontWeight: theme.siteVariables.fontWeightRegular,
          fontSize: pxToRem(18),
          letterSpacing: pxToRem(-0.16),
          color: theme.siteVariables.colors.primitive.black,
          marginBottom: pxToRem(2)
        } : {}
      },
      [`& .${productTileSlotClassNames.priceContainer}`]: {
        ...p.newDesignEnabled ? {
          marginTop: pxToRem(9),
          marginBottom: pxToRem(15),
          [theme.screen.laptop]: {
            [`& .${labelClassName2}`]: {
              color: theme.siteVariables.colors.grey["400"]
            }
          }
        } : {}
      },
      [`& .${productTileSlotClassNames.variants}`]: {
        marginTop: pxToRem(10),
        [`&:empty`]: {
          marginTop: 0
        }
      },
      [`& .${productTileSlotClassNames.variant}`]: {
        height: v.colorVariantSize,
        width: v.colorVariantSize,
        marginRight: pxToRem(6)
      },
      ...p.size === "dynamic" && {
        ...dynamicStyle3(v.dynamicSizingWidth, v.dynamicSizingGapWidth)
      }
    };
  },
  variant02: ({ theme, variables: v, props: p }) => {
    return {
      width: p.imageHeight,
      padding: pxToRem(0),
      [`& .${tileOverlayClassName}`]: {
        padding: pxToRem(12),
        opacity: 1,
        [`& .${boxClassName}`]: {
          display: "flex"
        },
        [`& .${buttonClassName}`]: {
          padding: pxToRem(8)
        },
        [`& .${svgIconClassName6}`]: {
          height: pxToRem(16),
          width: pxToRem(16)
        }
      },
      [`& .${productTileSlotClassNames.product}`]: {
        ...p.newDesignEnabled ? {
          ...mapCaptionVariantToFontStyles(
            { variant: "04" },
            theme
          ),
          fontWeight: theme.siteVariables.fontWeightRegular,
          letterSpacing: pxToRem(-0.14),
          color: theme.siteVariables.colors.primitive.black
        } : {
          ...theme.siteVariables.textStyles.Heading.Medium65,
          ...p.size === "xsmall" && { WebkitLineClamp: 1 }
        }
      },
      [`& .${productTileSlotClassNames.content}`]: {
        ...p.newDesignEnabled ? {
          marginTop: pxToRem(14),
          padding: pxArrayToRem([0, 12]),
          gap: pxToRem(2)
        } : {
          marginTop: pxToRem(12),
          ...!["xsmall", "small"].includes(p.size) && {
            paddingLeft: pxToRem(12),
            paddingRight: pxToRem(12)
          }
        }
      },
      [`& .${productTileSlotClassNames.professional}`]: {
        ...p.newDesignEnabled ? {
          fontWeight: theme.siteVariables.fontWeightLight,
          fontSize: pxToRem(12),
          color: theme.siteVariables.colors.charcoal["250"]
        } : {
          marginBottom: 0
        }
      },
      [`& .${labelClassName2}`]: {
        ...p.newDesignEnabled ? {} : {
          color: v.color,
          ...mapCaptionVariantToFontStyles(
            { variant: "04" },
            theme
          ),
          ...p.size === "xsmall" && { fontSize: pxToRem(12) }
        }
      },
      [`& .${captionClassName}`]: {
        ...p.newDesignEnabled ? {} : {
          color: v.color,
          ...mapCaptionVariantToFontStyles(
            { variant: "04" },
            theme
          ),
          ...p.size === "xsmall" && { fontSize: pxToRem(12) }
        }
      },
      [`& .${productTileSlotClassNames.priceContainer}`]: {
        ...p.newDesignEnabled ? {
          marginTop: pxToRem(10),
          marginBottom: pxToRem(12),
          [`& .${labelClassName2}`]: {
            fontSize: pxToRem(12),
            color: theme.siteVariables.colors.charcoal["250"]
          }
        } : {
          marginTop: pxToRem(12)
        }
      },
      [`& .${productTileSlotClassNames.variants}`]: {
        ...p.newDesignEnabled ? {
          marginTop: pxToRem(10),
          [`&:empty`]: {
            marginTop: 0
          }
        } : {
          marginTop: pxToRem(17),
          ...p.size !== "small" && {
            paddingLeft: pxToRem(12)
          },
          [`& .${captionClassName}`]: {
            ...mapCaptionVariantToFontStyles(
              { variant: "05" },
              theme
            )
          },
          [`&:empty`]: {
            marginTop: 0,
            paddingLeft: 0
          }
        }
      },
      [`& .${productTileSlotClassNames.variant}`]: {
        height: v.colorVariantMobileSize,
        width: v.colorVariantMobileSize,
        marginRight: pxToRem(3)
      },
      ...p.size === "dynamic" && {
        ...dynamicStyle3(v.dynamicSizingWidth, v.dynamicSizingGapWidth)
      }
    };
  },
  variant03: ({ theme, variables: v, props: p }) => ({
    width: p.imageHeight,
    padding: pxToRem(0),
    [`& .${tileOverlayClassName}`]: {
      opacity: 1
    },
    [`& .${productTileSlotClassNames.content}`]: {
      padding: pxArrayToRem([12, 12, 20])
    },
    [`& .${productTileSlotClassNames.title}`]: {
      color: v.color,
      ...mapCaptionVariantToFontStyles(
        { variant: "04", weight: "medium" },
        theme
      )
    },
    [`& .${productTileSlotClassNames.product}`]: {
      color: v.color,
      marginTop: pxToRem(4),
      ...mapCaptionVariantToFontStyles({ variant: "04" }, theme)
    },
    [`& .${tileOverlaySlotClassNames.bottomActions}`]: {
      display: "grid",
      gridTemplateColumns: `repeat( auto-fit, ${pxToRem(34)})`,
      gridTemplateRows: "1fr",
      gap: theme.siteVariables.spaces["space-12"],
      justifyContent: "flex-end",
      padding: pxToRem(12)
    },
    ...p.size === "dynamic" && {
      ...dynamicStyle3(v.dynamicSizingWidth, v.dynamicSizingGapWidth)
    }
  })
};

// packages/aria/src/themes/base/components/ProductInfo/productInfoStyles.ts
import { labelClassName as labelClassName3 } from "@fluentui/react-northstar";
var productInfoStyles = {
  root: ({ theme }) => {
    return {
      display: "block",
      [`& .${productInfoSlotClassNames.container}`]: {
        margin: pxArrayToRem([20, 0])
      },
      [`& .${productInfoSlotClassNames.product}`]: {
        ...theme.siteVariables.textStyles.Heading.Medium65,
        textTransform: "uppercase",
        fontSize: pxToRem(15)
      },
      [`& .${productInfoSlotClassNames.professional}`]: {
        ...theme.siteVariables.textStyles.Heading.Roman55,
        fontSize: pxToRem(14)
      },
      [`& .${productInfoSlotClassNames.price}`]: {
        marginTop: pxToRem(10),
        minHeight: pxToRem(18),
        [`& .${labelClassName3}`]: {
          ...theme.siteVariables.textStyles.Heading.Roman55,
          fontSize: pxToRem(14)
        }
      },
      [`& .${productInfoSlotClassNames.variants}`]: {
        marginTop: pxToRem(10),
        minHeight: pxToRem(16),
        gap: pxToRem(4)
      },
      [`& .${productInfoSlotClassNames.cta}`]: {
        marginTop: pxToRem(14),
        width: pxToRem(255),
        [`& .${buttonClassName}`]: {
          padding: 0
        }
      }
    };
  },
  variant01: () => {
    return {
      [`& .${productInfoSlotClassNames.container}`]: {
        flexDirection: "column"
      }
    };
  },
  variant02: () => {
    return {
      [`& .${productInfoSlotClassNames.container}`]: {
        flexDirection: "row",
        [`& >div`]: {
          flexGrow: 1
        }
      },
      [`& .${productInfoSlotClassNames.cta}`]: {
        marginTop: 0,
        width: "100%",
        minWidth: pxToRem(120)
      },
      [`& .${productInfoSlotClassNames.details}`]: {
        flexBasis: pxToRem(320)
      }
    };
  }
};

// packages/aria/src/themes/base/components/Tile/TileOverlay/tileOverlayStyles.ts
var tileOverlayStyles = {
  root: ({ variables: v, theme }) => {
    return {
      top: 0,
      left: 0,
      padding: v.padding,
      position: "absolute",
      width: "100%",
      height: "100%",
      zIndex: theme.siteVariables.zIndexes.foreground,
      pointerEvents: "none",
      // Allows swiper events to pass thorugh root element
      opacity: 1,
      transition: "opacity 150ms ease",
      [theme.screen.min.tablet]: {
        opacity: 0
      }
    };
  }
};

// packages/aria/src/themes/base/components/VideoPlayer/videoPlayerStyles.ts
import { svgIconClassName as svgIconClassName7 } from "@archipro-design/icons";
import { dialogSlotClassNames as dialogSlotClassNames2 } from "@fluentui/react-northstar";
var videoPlayerStyles = {
  root: () => {
    return {
      position: "relative"
    };
  },
  videoCover: ({ variables: v, props, theme }) => {
    const hideCover = props.shouldPlay && props.videoReady && props.playsInline && !props.playError;
    return {
      width: props.width ? pxToRem(props.width) : "100%",
      height: props.height ? pxToRem(props.height) : "100%",
      position: "relative",
      cursor: props.playError ? "default" : "pointer",
      opacity: hideCover ? 0 : 1,
      zIndex: hideCover ? -1 : 1,
      transition: props.shouldPlay ? `all ${v.videoCoverFadeSpeed} linear 200ms` : "none",
      ...props.playOnMouseOver && {
        opacity: props.debouncedMouseOver && props.mouseOver && props.videoReady && !props.playError ? 0 : 1,
        transition: props.debouncedMouseOver && props.mouseOver && props.videoReady && !props.playError ? `all ${v.videoCoverFadeSpeed} linear` : "none"
      },
      [`& .${imageClassName}`]: {
        position: v.coverImagePosition,
        ...v.coverImagePosition === "absolute" && {
          left: `50%`,
          top: `50%`,
          transform: `translate(-50%, -50%)`,
          width: "100%",
          height: "100%"
        }
      },
      [`& .${imageOverlayClassName}`]: {
        background: `rgba(0, 0, 0, 0.2)`,
        transition: `background ${v.videoCoverFadeSpeed} linear`
      },
      [`&::after`]: {
        content: '" "',
        display: `block`,
        position: "absolute",
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        background: `rgba(0, 0, 0, 0.2)`,
        zIndex: `-1`,
        transition: `opacity ${v.videoCoverFadeSpeed} linear`
      },
      [theme.screen.laptop]: {
        "&:hover": {
          [`&::after`]: {
            opacity: 0
          },
          [`& .${imageOverlayClassName}`]: {
            background: `rgba(0, 0, 0, 0)`
          },
          "& svg": {
            transform: `translate(-50%, -50%) scale(1.1)`
          }
        }
      }
    };
  },
  playIcon: ({ variables: v, theme }) => {
    return {
      "& svg": {
        fill: v.playIconColor,
        width: pxToRem(v.playIconSizeMobile),
        height: pxToRem(v.playIconSizeMobile),
        transform: `translate(-50%, -50%) scale(1)`,
        transition: `transform 150ms linear`,
        top: "50%",
        left: "50%",
        position: "absolute",
        [theme.screen.laptop]: {
          width: pxToRem(v.playIconSizeDesktop),
          height: pxToRem(v.playIconSizeDesktop)
        }
      }
    };
  },
  closeIcon: ({ variables: v }) => {
    return {
      opacity: 0.8,
      transition: `opcaity ${v.videoCoverFadeSpeed} linear`,
      "&:hover": {
        opacity: 1
      },
      "& svg": {
        fill: v.modalIconColour,
        width: pxToRem(v.closeIconSize),
        height: pxToRem(v.closeIconSize)
      }
    };
  },
  animationContainer: () => ({
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    position: "absolute"
  }),
  spinning: ({ variables: v, theme }) => ({
    position: "static",
    "& svg": {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      fill: v.playIconColor,
      width: pxToRem(v.playIconSizeMobile),
      height: pxToRem(v.playIconSizeMobile),
      [theme.screen.laptop]: {
        width: pxToRem(v.playIconSizeDesktop),
        height: pxToRem(v.playIconSizeDesktop)
      }
    }
  }),
  videoModal: ({ variables: v, theme }) => {
    const dialogMargins = 120;
    return {
      height: "100%",
      width: "100%",
      [`& .${boxClassName}`]: {
        height: "100%",
        width: `min(100%, calc(160vh - ${pxToRem(dialogMargins)}))`,
        position: "relative",
        margin: "auto",
        top: "50%",
        transform: "translateY(-50%)",
        [`& .${boxClassName}`]: {
          position: "relative",
          height: 0,
          width: "100%",
          paddingTop: "56.25%",
          overflow: "hidden",
          "&>div": {
            position: "absolute",
            width: "100%",
            height: "100%",
            top: "0",
            left: "0"
          }
        }
      },
      [`& .${dialogSlotClassNames2.headerAction}`]: {
        outline: "none",
        border: "none",
        position: "fixed",
        zIndex: 1,
        top: pxToRem(26),
        right: pxToRem(15),
        backgroundColor: `transparent`,
        [`& .${svgIconClassName7}`]: {
          height: pxToRem(v.closeIconSize),
          width: pxToRem(v.closeIconSize)
        },
        [theme.screen.laptop]: {
          top: pxToRem(35),
          right: pxToRem(22),
          transform: `scale(1.2)`
        }
      }
    };
  },
  playErrorCover: ({ theme }) => ({
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: "100%",
    height: "100%",
    zIndex: 2,
    display: "flex",
    flexDirection: "column",
    alignContent: "center",
    justifyContent: "center",
    alignItems: "center",
    background: "rgba(255, 255, 255, 0.95)",
    color: theme.siteVariables.colors.charcoal[250],
    [`& .${HeaderClassName}`]: {
      ...mapSubHeadingVariantToFontStyles({ variant: "02" }, theme),
      color: theme.siteVariables.colors.charcoal[250],
      margin: 0,
      padding: 0
    },
    [`& .${SubheaderClassName}`]: {
      fontSize: pxToRem(24),
      color: theme.siteVariables.colors.charcoal[250],
      lineHeight: theme.siteVariables.lineHeightSmallest,
      marginTop: pxToRem(8),
      letterSpacing: theme.siteVariables.letterSpacingMedium
    },
    [theme.screen.laptop]: {
      [`& .${HeaderClassName}`]: {
        fontSize: pxToRem(48),
        lineHeight: theme.siteVariables.lineHeightSmallest
      },
      [`& .${SubheaderClassName}`]: {
        ...mapSubHeadingVariantToFontStyles({ variant: "02" }, theme),
        letterSpacing: 0
      }
    }
  }),
  playErrorClose: ({ theme, variables: v }) => ({
    position: "absolute",
    top: pxToRem(16),
    right: pxToRem(16),
    zIndex: 3,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: 0,
    lineHeight: 0,
    opacity: 0.85,
    transition: `opacity ${v.videoCoverFadeSpeed} linear`,
    "&:hover": {
      opacity: 1
    },
    [theme.screen.laptop]: {
      top: pxToRem(22),
      right: pxToRem(22)
    }
  }),
  playErrorCloseIcon: ({ theme, variables: v }) => ({
    display: "inline-flex",
    [`& .${svgIconClassName7}`]: {
      fill: theme.siteVariables.colors.charcoal[250],
      width: pxToRem(v.closeIconSize),
      height: pxToRem(v.closeIconSize)
    }
  }),
  playError: ({ theme }) => {
    return {
      display: "flex",
      flexDirection: "column",
      alignContent: "center",
      justifyContent: "center",
      alignItems: "center",
      background: theme.siteVariables.colors.white[100],
      color: theme.siteVariables.colors.charcoal[250],
      [`& .${HeaderClassName}`]: {
        ...mapSubHeadingVariantToFontStyles({ variant: "02" }, theme),
        color: theme.siteVariables.colors.charcoal[250],
        margin: 0,
        padding: 0
      },
      [`& .${SubheaderClassName}`]: {
        fontSize: pxToRem(24),
        color: theme.siteVariables.colors.charcoal[250],
        lineHeight: theme.siteVariables.lineHeightSmallest,
        marginTop: pxToRem(8),
        letterSpacing: theme.siteVariables.letterSpacingMedium
      },
      [theme.screen.laptop]: {
        [`& .${HeaderClassName}`]: {
          fontSize: pxToRem(48),
          lineHeight: theme.siteVariables.lineHeightSmallest
        },
        [`& .${SubheaderClassName}`]: {
          ...mapSubHeadingVariantToFontStyles(
            { variant: "02" },
            theme
          ),
          letterSpacing: 0
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/Image/imageElementStyles.ts
var imageElementStyles = {
  root: ({ props }) => {
    const common = {
      ...props.loading !== "eager" && props.transitionIn && { transition: `${fast}` },
      pointerEvents: "none"
    };
    if (props.layout === "fill") {
      return {
        position: "absolute",
        height: "100%",
        width: "100%",
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
        ...common
      };
    }
    if (props.layout === "responsive") {
      return { width: "100%", height: "auto", ...common };
    }
    return common;
  },
  errorImage: ({ theme }) => {
    return {
      padding: pxToRem(10),
      display: "inline-block",
      height: "100%",
      width: "100%",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      color: theme.siteVariables.colors.charcoal[350]
    };
  }
};

// packages/aria/src/themes/base/components/SvgIcon/svgIconStyles.ts
import { callable } from "@fluentui/styles";
var getPaddedStyle = () => ({
  padding: pxToRem(4)
});
var getIconSize = (size, v) => {
  return v[`${size}Size`];
};
var getXSpacingStyles = (xSpacing, horizontalSpace) => {
  if (!xSpacing && !horizontalSpace) {
    return {};
  }
  switch (xSpacing) {
    case "none":
      return { marginLeft: 0, marginRight: 0 };
    case "before":
      return { marginLeft: horizontalSpace, marginRight: 0 };
    case "after":
      return { marginLeft: 0, marginRight: horizontalSpace };
    case "both":
      return {
        marginLeft: horizontalSpace,
        marginRight: horizontalSpace
      };
    default:
      return {};
  }
};
var svgIconStyles = {
  root: ({ props: p, variables: v }) => {
    const { size } = p;
    const iconSize = getIconSize(size || "1x", v);
    return {
      speak: "none",
      verticalAlign: "middle",
      width: iconSize,
      height: iconSize,
      ...getXSpacingStyles(p.xSpacing, v.horizontalSpace),
      ...p.circular && { ...getPaddedStyle(), borderRadius: "50%" },
      ...p.disabled && {
        color: v.disabledColor
      },
      display: "inline-block",
      backgroundColor: v.backgroundColor
    };
  },
  svg: ({
    props: { disabled, rotate, color },
    variables: v,
    rtl
  }) => {
    return {
      display: "block",
      width: "inherit",
      height: "inherit",
      fill: color || v.color,
      ...disabled && {
        fill: v.disabledColor
      },
      // Manual flipping to make it compatible with Emotion and Fela in the same time
      ...rotate && {
        transform: `rotate(${rotate}deg) /* @noflip */`,
        ...rtl && {
          transform: `rotate(${-1 * rotate}deg) /* @noflip */`
        }
      }
    };
  },
  svgFlippingInRtl: (config) => {
    const { props, rtl } = config;
    return {
      ...callable(svgIconStyles["svg"])(config),
      ...rtl && {
        transform: `scaleX(-1) rotate(${props.rotate}deg) /* @noflip */`
      }
    };
  }
};

// packages/aria/src/themes/base/components/FooterNavigation/footerNavigationStyles.ts
import {
  accordionClassName as accordionClassName2,
  accordionTitleClassName as accordionTitleClassName3,
  accordionTitleSlotClassNames as accordionTitleSlotClassNames2
} from "@fluentui/react-northstar";
var footerNavigationStyles = {
  root: ({ variables: v, theme, props: p }) => {
    const desktop = {
      paddingLeft: pxToRem(50),
      paddingTop: pxToRem(72),
      paddingRight: pxToRem(50),
      paddingBottom: pxToRem(37),
      [`& .${footerNavigationSlotClassNames.right}`]: {
        width: pxToRem(897)
      },
      [`& .${footerNavigationSlotClassNames.footerLinkGroups}`]: {
        marginBottom: pxToRem(58)
      },
      [`& .${footerNavigationSlotClassNames.container}`]: {
        flexDirection: "row"
      },
      [`& .${footerNavigationSlotClassNames.left}`]: {
        alignItems: "unset"
      }
    };
    return {
      background: v.footerNavigationBackgroundColor,
      paddingTop: pxToRem(48),
      paddingBottom: pxToRem(16),
      paddingRight: pxToRem(16),
      paddingLeft: pxToRem(16),
      [`& .${footerNavigationSlotClassNames.container}`]: {
        flexDirection: "column"
      },
      [`& .${footerNavigationSlotClassNames.left}`]: {
        alignItems: "center"
      },
      [theme.screen.laptop]: desktop,
      ...p.isDesktop && {
        [theme.screen.max.shrankWindow]: desktop
      },
      [`& .${logoClassName}`]: {
        "& svg": {
          fill: v.footerLogoColor,
          "& path": {
            fill: v.footerLogoColor
          }
        }
      }
    };
  },
  footerDesktop: ({ theme, props: p }) => {
    const desktop = {
      display: "flex"
    };
    return {
      display: "none",
      [theme.screen.laptop]: desktop,
      ...p.isDesktop && {
        // desktop and small screen
        [theme.screen.max.shrankWindow]: desktop
      }
    };
  },
  footerMobile: ({ variables: v, theme, props: p }) => {
    return {
      [theme.screen.laptop]: {
        display: "none"
      },
      ...p.isDesktop && {
        // desktop and small screen
        [theme.screen.max.shrankWindow]: {
          display: "none"
        }
      },
      [`& .${accordionClassName2}`]: {
        color: theme.siteVariables.colors.white["000"],
        borderBottom: `${pxToRem(1)} solid ${v.footerBorderColor}`,
        [`& div.${accordionTitleClassName3}`]: {
          borderTop: `${pxToRem(1)} solid ${v.footerBorderColor}`,
          color: v.footerNavigationLinksPrimaryColor
        },
        [`& .${accordionTitleSlotClassNames2.contentWrapper}`]: {
          paddingTop: pxToRem(15)
        },
        [`& .${accordionTitleSlotClassNames2.contentWrapper}  svg`]: {
          fill: v.footerSVGColor
        },
        [`& .${accordionTitleSlotClassNames2.contentWrapper} > div`]: {
          ...theme.siteVariables.textStyles.Heading.Roman55,
          textTransform: "uppercase",
          fontSize: pxToRem(16),
          marginBottom: pxToRem(11),
          height: "auto"
        }
      }
    };
  },
  footerLogoAndText: ({ theme, props: p }) => {
    return {
      paddingBottom: pxToRem(50),
      [theme.screen.laptop]: {
        paddingBottom: pxToRem(170)
      },
      ...p.isDesktop && {
        // desktop and small screen
        [theme.screen.max.shrankWindow]: {
          paddingBottom: pxToRem(170)
        }
      }
    };
  },
  footerDescription: ({ variables: v, theme, props: p }) => {
    const desktop = {
      fontSize: pxToRem(16),
      lineHeight: theme.siteVariables.lineHeightSmaller,
      letterSpacing: theme.siteVariables.letterSpacingNormal,
      width: pxToRem(470),
      marginBottom: pxToRem(150),
      marginLeft: 0
    };
    return {
      display: "flex",
      alignItems: "center",
      marginLeft: pxToRem(0),
      marginBottom: pxToRem(30),
      fontSize: pxToRem(20),
      lineHeight: pxToRem(26),
      textTransform: "uppercase",
      color: v.footerTextColor,
      ...theme.siteVariables.textStyles.Text.Roman55,
      [theme.screen.laptop]: desktop,
      ...p.isDesktop && {
        [theme.screen.max.shrankWindow]: desktop
      },
      [`&:empty`]: {
        display: "none"
      }
    };
  },
  footerLinkGroups: ({ variables: v, theme }) => {
    return {
      display: "flex",
      flexDirection: "column",
      width: pxToRem(200),
      [`& .${footerNavigationSlotClassNames.footerLinksHeading}`]: {
        fontStyle: "normal",
        color: v.footerNavigationLinksPrimaryColor,
        fontSize: pxToRem(14),
        lineHeight: theme.siteVariables.lineHeightDefault,
        letterSpacing: theme.siteVariables.letterSpacingNormal,
        textTransform: "uppercase",
        marginBottom: pxToRem(28)
      },
      "& ul": {
        margin: "0",
        listStyle: "none",
        textDecoration: "none",
        paddingBlockStart: "0",
        paddingBlockEnd: "0",
        paddingInlineStart: "0",
        paddingInlineEnd: "0",
        fontSize: pxToRem(14),
        lineHeight: theme.siteVariables.lineHeightDefault
      },
      "& a:hover": {
        textDecoration: "underline",
        cursor: "pointer",
        color: v.footerNavigationLinksPrimaryColor
      },
      "&:first-child": {
        marginRight: pxToRem(22)
      },
      "&:nth-child(2)": {
        marginRight: pxToRem(22)
      },
      "&:nth-child(3)": {
        marginRight: pxToRem(22)
      }
    };
  },
  footerCopyrightSubscription: ({ theme, props: p }) => {
    const desktop = {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-end"
    };
    return {
      flexDirection: "column-reverse",
      [theme.screen.laptop]: desktop,
      ...p.isDesktop && {
        [theme.screen.max.shrankWindow]: desktop
      }
    };
  },
  subscription: ({ theme, props: p }) => {
    const desktop = {
      marginTop: "0",
      marginBottom: "0",
      width: pxToRem(395),
      [`& .${subscriptionClassName}`]: {
        marginRight: 0,
        lineHeight: theme.siteVariables.lineHeightSmall,
        "& input": {
          "&::placeholder": {
            fontSize: pxToRem(14)
          }
        }
      }
    };
    return {
      display: "flex",
      marginTop: pxToRem(41),
      marginBottom: pxToRem(53),
      [theme.screen.laptop]: desktop,
      ...p.isDesktop && {
        [theme.screen.max.shrankWindow]: desktop
      }
    };
  },
  footerCopyright: ({ variables: v, theme, props: p }) => {
    const desktop = {
      [`& .${footerNavigationSlotClassNames.footerCopyrightLink}`]: {
        fontSize: pxToRem(14),
        lineHeight: theme.siteVariables.lineHeightDefault
      },
      [`& .${footerNavigationSlotClassNames.copyright}`]: {
        fontSize: pxToRem(14),
        lineHeight: theme.siteVariables.lineHeightDefault
      },
      flexDirection: "row",
      marginTop: 0,
      marginBottom: 0,
      justifyContent: "flex-end",
      opacity: 0.4
    };
    return {
      alignItems: "flex-end",
      marginTop: pxToRem(38),
      marginBottom: pxToRem(18),
      flexDirection: "row",
      justifyContent: "space-between",
      [`& .${footerNavigationSlotClassNames.footerCopyrightLink}`]: {
        color: v.footerCopyrightLinksPrimaryColor,
        fontSize: pxToRem(14),
        lineHeight: theme.siteVariables.lineHeightDefault
      },
      [`& .${footerNavigationSlotClassNames.copyright}`]: {
        fontSize: pxToRem(14),
        color: v.footerCopyrightLinksPrimaryColor
      },
      [theme.screen.laptop]: desktop,
      ...p.isDesktop && {
        [theme.screen.max.shrankWindow]: desktop
      }
    };
  },
  divider: ({ variables: v, theme, props: p }) => {
    const desktop = {
      marginRight: ".5ch",
      marginLeft: ".5ch",
      background: `${v.footerCopyrightLinksPrimaryColor}`
    };
    return {
      height: pxToRem(16),
      marginRight: ".7ch",
      marginLeft: ".7ch",
      backgroundColor: `${v.footerCopyrightDividerColor}`,
      width: pxToRem(2),
      [theme.screen.laptop]: desktop,
      ...p.isDesktop && {
        [theme.screen.max.shrankWindow]: desktop
      }
    };
  }
};

// packages/aria/src/themes/base/components/FooterNavigation/footerNavigationItemStyles.ts
var footerNavigationItemStyles = {
  root: ({ variables: v, theme }) => {
    return {
      listStyle: "none",
      textDecoration: "none",
      marginBottom: pxToRem(20),
      [`& .${labelClassName}`]: {
        fontSize: pxToRem(16),
        lineHeight: theme.siteVariables.lineHeightDefault,
        color: v.footerNavigationLinksPrimaryColor,
        fontStyle: "normal",
        textDecoration: "none"
      },
      "&:last-child": {
        marginBottom: "0"
      },
      "& a": {
        letterSpacing: "unset",
        color: v.footerNavigationLinksPrimaryColor
      },
      "& a:hover": {
        textDecoration: "underline",
        color: v.footerNavigationLinksPrimaryColor
      },
      [theme.screen.laptop]: {
        marginBottom: pxToRem(6),
        letterSpacing: theme.siteVariables.letterSpacingMedium,
        [`& .${labelClassName}`]: {
          letterSpacing: theme.siteVariables.letterSpacingNormal,
          fontSize: pxToRem(16),
          lineHeight: pxToRem(15),
          color: v.footerNavigationLinksPrimaryColor
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/Subscription/subscriptionStyles.ts
import {
  flexClassName as flexClassName2,
  inputClassName as inputClassName2,
  inputSlotClassNames as inputSlotClassNames3
} from "@fluentui/react-northstar";
var subscriptionStyles = {
  root: ({ variables: v, theme }) => {
    return {
      width: "100%",
      [`& ${flexClassName2}`]: {
        alignItems: "flex-end"
      },
      [`& .${captionClassName}`]: {
        color: v.primaryTextColor,
        lineHeight: theme.siteVariables.lineHeightSmall
      },
      [`& .${buttonClassName}`]: {
        backgroundColor: v.subscriptionButtonBackgroundColor,
        margin: pxArrayToRem([3, 10]),
        paddingTop: pxToRem(2),
        "&:hover": {
          backgroundColor: v.subscriptionButtonBackgroundColor
        }
      },
      [`& .${inputClassName2}`]: {
        width: "100%",
        [`& .${inputSlotClassNames3.input}`]: {
          height: pxToRem(40),
          width: "100%",
          display: "grid",
          boxSizing: "border-box",
          borderTop: "none",
          borderRight: "none",
          borderLeft: "none",
          borderBottom: "none",
          backgroundColor: "none",
          minWidth: pxToRem(150),
          padding: pxArrayToRem([0, 0, 6, 1]),
          fontSize: `max(16px, ${pxToRem(16)})`,
          color: v.primaryTextColor,
          "&::placeholder": {
            fontSize: `max(16px, ${pxToRem(16)})`,
            lineHeight: "150%",
            transform: `translate(0, ${pxToRem(2)})`,
            color: v.placeholderTextColor
          },
          [theme.screen.laptop]: {
            height: pxToRem(29),
            padding: pxArrayToRem([0, 0, 16]),
            borderBottom: `${pxToRem(1)} solid ${v.textBorderBottomColor}`,
            "&::placeholder": {
              fontSize: pxToRem(14),
              lineHeight: pxToRem(13),
              transform: `none`,
              color: v.placeholderTextColor
            }
          },
          "& input:focus": {
            borderBottomColor: v.textBorderBottomColor
          }
        },
        "& input:focus": {
          outline: "none"
        },
        [theme.screen.laptop]: {
          width: v.textInputWidth
        }
      }
    };
  },
  header: ({ theme }) => {
    return {
      [`&.${captionClassName}`]: {
        lineHeight: theme.siteVariables.lineHeightSmaller,
        letterSpacing: theme.siteVariables.letterSpacingSmall,
        marginBottom: pxToRem(20),
        opacity: 0.5,
        textAlign: "center",
        padding: pxArrayToRem([0, 8]),
        [theme.screen.laptop]: {
          opacity: 1,
          paddingRight: "3ch",
          paddingLeft: 0,
          textAlign: "left",
          marginBottom: pxToRem(36)
        }
      }
    };
  },
  displayError: ({ variables: v }) => {
    return {
      fontSize: pxToRem(14),
      lineHeight: pxToRem(13),
      color: v.subscriptionErrorMessageColor,
      display: "block",
      marginTop: pxToRem(4)
    };
  },
  success: () => ({
    display: "block",
    fontSize: pxToRem(14),
    lineHeight: pxToRem(13),
    marginTop: pxToRem(4)
  }),
  inputRow: ({ variables: v, theme }) => ({
    borderBottom: `1px solid ${v.textBorderBottomColor}`,
    [theme.screen.laptop]: {
      borderBottom: "none"
    }
  })
};

// packages/aria/src/themes/base/components/List/listStyles.ts
var listStyles = {
  root: ({ variables: v }) => {
    return {
      listStyle: "none",
      padding: 0,
      margin: 0,
      ":first-child": {
        borderTop: `${v.listBorderWidth} ${v.listBorderStyle} ${v.listBorderColor}`
      },
      [`& .${listSlotClassNames.moreOrLess}`]: {
        cursor: "pointer"
      }
    };
  }
};

// packages/aria/src/themes/base/components/List/listItemStyles.ts
var listItemStyles = {
  root: ({ variables: v, theme }) => {
    return {
      minHeight: pxToRem(50),
      borderBottom: `1px solid ${v.listItemBorderColor}`,
      "& > a": {
        display: `flex`,
        alignItems: `center`
      },
      [theme.screen.laptop]: {
        borderBottom: `1px solid ${v.listItemBorderColor}`
      }
    };
  },
  text: () => {
    return {
      textDecoration: "none"
    };
  },
  textWrapper: ({ variables: v, props }) => {
    return {
      color: v.listItemTextColor,
      "&:hover": {
        color: props.isLink ? v.listItemHoverTextColor : v.listItemTextColor
      },
      "& svg": {
        fill: v.listItemIconColor
      },
      "&:hover svg": {
        fill: v.listItemIconHoverColor
      },
      "& span": {
        display: "inline-block"
      }
    };
  },
  iconBefore: ({ props: p }) => {
    return {
      marginRight: pxToRem(p.iconPadding ?? 0)
    };
  },
  iconAfter: ({ props: p }) => {
    return {
      marginLeft: pxToRem(p.iconPadding ?? 0)
    };
  }
};

// packages/aria/src/themes/base/components/Profile/PersonalProfile/personalProfileStyles.ts
var personalProfileStyles = {
  root: ({ variables: v, theme, props: p }) => {
    return {
      padding: pxArrayToRem([0, 0, 11]),
      display: "flex",
      flexShrink: 0,
      alignItems: "center",
      overflow: "hidden",
      outline: "none",
      [`& .${labelClassName}`]: {
        lineHeight: theme.siteVariables.lineHeightSmallest,
        color: v.fullNameColor
      },
      ...p.variant === "02" && {
        padding: pxArrayToRem([0, 17, 22])
      },
      [`& .${personalProfileSlotClassNames.avatar}`]: {
        flexShrink: 0,
        flexGrow: 0,
        [`&.${avatarClassName}`]: {
          backgroundColor: v.avatarBackgroundColor,
          [`& .${SubheaderClassName}`]: {
            color: v.avatarInitialColor
          }
        },
        ...p.variant === "02" && {
          marginRight: pxToRem(12)
        }
      },
      [`& .${personalProfileSlotClassNames.contentContainer}`]: {
        overflow: "hidden"
      },
      [`& .${personalProfileSlotClassNames.fullName}`]: {
        color: v.personalProfileViewProfileLabelColor,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        [`&.${labelClassName}`]: {
          ...mapLabelVariantToFontStyles({ variant: "04" }, theme),
          letterSpacing: theme.siteVariables.letterSpacingNormal
        },
        ...p.variant === "02" && {
          [`&.${labelClassName}`]: {
            ...mapLabelVariantToFontStyles(
              { variant: "02" },
              theme
            ),
            marginTop: pxToRem(3)
          }
        }
      },
      [`& .${personalProfileSlotClassNames.viewProfileLink}`]: {
        marginTop: pxToRem(3),
        color: v.personalProfileViewProfileLabelColor,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        cursor: "pointer",
        userSelect: "none",
        ...mapLabelVariantToFontStyles({ variant: "04" }, theme),
        "&:hover": {
          textDecoration: "underline"
        },
        ...p.variant === "02" && {
          marginTop: 0
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/NavigationMenu/navigationMenuStyles.ts
import { headerClassName } from "@fluentui/react-northstar";
var navigationMenuStyles = {
  root: ({ variables: v }) => {
    return {
      backgroundColor: v.navigationMenuBackgroundColor
    };
  },
  majorNavigation: ({ variables: v }) => {
    return {
      [`& .${flexClassName}:last-child`]: {
        borderBottom: "none"
      },
      paddingBottom: v.navigationMenuMajorPaddingBottom
    };
  },
  minorNavigation: ({ variables: v }) => {
    return {
      paddingBottom: v.navigationMenuMinorPaddingBottom
    };
  },
  copyright: ({ theme }) => {
    return {
      paddingTop: pxToRem(6),
      opacity: "40%",
      color: theme.siteVariables.colors.charcoal["250"],
      paddingBottom: theme.siteVariables.spaces["space-48"],
      paddingRight: theme.siteVariables.spaces["space-16"]
    };
  },
  majorNavItem: ({ theme, variables: v }) => {
    return {
      cursor: "pointer",
      borderBottom: v.navigationMenuBorderBottom,
      textDecoration: "none",
      padding: v.navigationMenuMajorItemPadding,
      [`& .${headerClassName}`]: {
        margin: 0,
        color: v.navigationMenuItemColor,
        lineHeight: pxToRem(61)
      },
      [`& .${captionClassName}`]: {
        color: v.navigationMenuItemColor,
        paddingLeft: theme.siteVariables.spaces["space-6"]
      }
    };
  },
  minorNavItem: ({ variables: v }) => {
    return {
      textDecoration: "none",
      cursor: "pointer",
      padding: `${pxToRem(8)} ${pxToRem(16)}`,
      [`& .${labelClassName}`]: {
        color: v.navigationMenuItemColor
      }
    };
  }
};

// packages/aria/src/themes/base/components/Badge/badgeStyles.ts
var badgeStyles = {
  root: () => {
    return {
      boxSizing: "border-box",
      position: "relative",
      width: "fit-content",
      height: "fit-content",
      display: "inline-flex",
      outline: "none"
    };
  },
  badge: ({ variables: v }) => {
    return {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      borderRadius: pxToRem(100),
      width: v.badgeWidth,
      height: v.badgeHeight,
      minWidth: v.badgeHeight,
      backgroundColor: v.badgeBackgroundColor,
      ...v.badgePosition ?? {}
    };
  },
  counter: ({ variables: v, theme: { siteVariables } }) => {
    return {
      color: siteVariables.colors.primitive.white,
      fontSize: v.textFontSize,
      lineHeight: v.textLineHeight,
      letterSpacing: "-0.01em",
      userSelect: "none",
      ...siteVariables.textStyles.Text.Roman55
    };
  }
};

// packages/aria/src/themes/base/components/Breadcrumb/breadcrumbStyles.ts
var breadcrumbStyles = {
  root: ({ variables: v, theme }) => ({
    fontSize: v.breadcrumbFontSize,
    lineHeight: "100%",
    height: pxToRem(v.breadcrumbHeight ?? 22),
    marginTop: pxToRem(6),
    marginRight: pxToRem(10),
    paddingLeft: pxToRem(18),
    overflow: "hidden",
    [theme.screen.laptop]: {
      paddingLeft: 0,
      marginTop: 0,
      marginRight: 0,
      marginBottom: 0,
      overflow: "unset"
    }
  }),
  wrapper: () => ({
    overflowX: "auto",
    overflowY: "hidden",
    whiteSpace: "nowrap",
    width: "100%",
    height: pxToRem(40)
  }),
  logo: ({ theme, variables: v }) => ({
    display: "none",
    padding: v.breadcrumbLogoSpacing,
    marginTop: pxToRem(1),
    "&:hover": {
      backgroundColor: v.breadcrumbLogoBackgroundHover
    },
    [theme.screen.min.tablet]: {
      display: "block"
    },
    [`& .${logoClassName} svg`]: {
      fill: v.breadcrumbLogoColor,
      "& path": {
        fill: v.breadcrumbLogoColor
      },
      width: v.breadcrumbLogoWidth,
      height: v.breadcrumbLogoHeight
    }
  }),
  group: ({ theme, variables: v }) => ({
    marginLeft: theme.siteVariables.spaces["space-4"],
    marginRight: pxToRem(1.5),
    "&:first-child": {
      marginLeft: v.breadcrumbFirstChildLeftMargin,
      [`& .${breadcrumbItemClassName}`]: {
        marginLeft: 0
      }
    },
    [`& .${breadcrumbItemClassName}`]: {
      "&:hover": {
        textDecoration: "none"
      }
    },
    [`& .${captionClassName}`]: {
      fontSize: v.breadcrumbFontSize
    },
    [theme.screen.min.tablet]: {
      marginLeft: theme.siteVariables.spaces["space-10"],
      [`& .${breadcrumbItemClassName}`]: {
        "&:hover": {
          textDecoration: "underline"
        }
      }
    }
  })
};

// packages/aria/src/themes/base/components/Breadcrumb/breadcrumbItemStyles.ts
var breadcrumbItemStyles = {
  root: ({ variables: v, theme, props }) => {
    return {
      display: "inline-block",
      textDecoration: "none",
      color: v.breadcrumbItemTextColor,
      marginLeft: pxToRem(4.5),
      "&:hover": {
        color: props.link ? v.breadcrumbItemHoverTextColor : v.breadcrumbItemTextColor,
        textDecoration: "underline",
        textDecorationColor: v.breadcrumbItemHoverLineColor,
        textUnderlineOffset: pxToRem(2)
      },
      [theme.screen.min.tablet]: {
        marginLeft: theme.siteVariables.spaces["space-8"]
      }
    };
  }
};

// packages/aria/src/themes/base/components/Breadcrumb/breadcrumbDividerStyles.ts
var breadcrumbDividerStyles = {
  root: ({ variables: v }) => {
    return {
      display: "inline-block",
      color: v.breadcrumbDividerColor
    };
  }
};

// packages/aria/src/themes/base/components/PersonalMenu/personalMenuStyles.ts
var personalMenuStyles = {
  root: ({ variables: v, props: p, theme }) => {
    return {
      maxHeight: v.maxHeight,
      width: "100%",
      backgroundColor: v.personalMenuBackgroundColor,
      ...p.variant === "02" && {
        paddingTop: pxToRem(5),
        [`& .${profileMenuItemClassName}`]: {
          borderBottom: v.personalMenuDividerStyle
        }
      },
      [`& .${personalMenuSlotClassNames.mainContentList}`]: {
        flex: 1,
        overflowY: "auto",
        minHeight: v.contentMinHeight,
        [theme.screen.tablet]: {
          padding: v.contentPadding
        },
        ...p.variant === "02" && {
          paddingBottom: pxToRem(80)
        },
        ...getScrollbarStyle(theme)
      },
      [`& .${personalMenuSlotClassNames.personalProfile}`]: {
        borderBottom: v.personalMenuDividerStyle,
        boxSizing: "border-box"
      },
      [`& .${personalMenuSlotClassNames.professionalList}`]: {
        "& > div": {
          boxSizing: "border-box",
          borderBottom: v.personalMenuDividerStyle,
          [theme.screen.tablet]: {
            borderBottom: "none"
          },
          [`&:first-child`]: {
            paddingTop: 0
          }
        }
      },
      [`& .${personalMenuSlotClassNames.sections}`]: {
        flexDirection: p.variant === "01" ? "column" : "column-reverse"
      },
      [`& .${personalMenuSlotClassNames.sectionTitle}`]: {
        [`&.${captionClassName}`]: {
          margin: pxArrayToRem([0, 0, 16, 17]),
          fontSize: pxToRem(15),
          textTransform: "uppercase",
          color: hex2rgba(
            theme.siteVariables.colors.primitive.black,
            0.4
          ),
          letterSpacing: "0.08em"
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/Profile/BusinessProfile/businessProfileStyles.ts
import { svgIconClassName as svgIconClassName8 } from "@archipro-design/icons";
var businessProfileStyles = {
  root: ({ variables: v, props: p }) => {
    return {
      display: "inline",
      width: "100%",
      height: "100%",
      padding: pxArrayToRem([13, 0, 10]),
      boxSizing: "border-box",
      [`& .${labelClassName}`]: {
        color: v.businessProfileLabelColor
      },
      [`& .${buttonClassName}`]: {
        marginTop: pxToRem(2),
        [`& .${svgIconClassName8}`]: {
          color: v.iconColor,
          ...p.toggleAccordian && {
            transform: `rotate(-180deg)`
          },
          [`& > svg`]: { padding: pxToRem(3) }
        }
      },
      ...p.variant === "02" && {
        padding: pxArrayToRem([13, 0, 0]),
        [`& .${buttonClassName}`]: {
          alignSelf: "center",
          [`& .${svgIconClassName8}`]: {
            color: v.iconColor,
            ...p.toggleAccordian && {
              transform: `rotate(90deg)`
            }
          }
        }
      }
    };
  },
  accordianTitle: ({ theme, props: p }) => {
    return {
      padding: 0,
      [`& .${labelClassName}`]: {
        ...mapLabelVariantToFontStyles({ variant: "04" }, theme)
      },
      [`& .${avatarClassName}`]: {
        marginTop: pxToRem(4)
      },
      ...p.variant === "02" && {
        padding: pxArrayToRem([3, 12, 12, 17]),
        borderBottom: p.toggleAccordian ? `1px solid ${theme.siteVariables.colors.grey[125]}` : "none"
      }
    };
  },
  accordianTitleText: ({ theme, props: p }) => {
    return {
      [`& .${captionClassName}`]: {
        textDecoration: "none",
        ...mapLabelVariantToFontStyles({ variant: "04" }, theme)
      },
      ...p.variant === "02" && {
        margin: pxArrayToRem([0, 12]),
        [`& .${labelClassName}`]: {
          ...mapLabelVariantToFontStyles({ variant: "02" }, theme)
        }
      }
    };
  },
  countryTag: ({ theme, props: p }) => {
    return {
      borderRadius: "2px",
      marginRight: pxToRem(8),
      backgroundColor: theme.siteVariables.colors.grey["300"],
      [`& .${captionClassName}`]: {
        color: theme.siteVariables.colors.white["100"],
        fontSize: pxToRem(9),
        padding: `${pxToRem(2)} ${pxToRem(6)}`
      },
      ...p.variant === "02" && {
        marginRight: pxToRem(12)
      }
    };
  },
  accordianContent: ({ theme, props: p }) => {
    return {
      display: p.toggleAccordian ? "show" : "none",
      padding: pxArrayToRem([18, 0, 0]),
      [`& .${captionClassName}`]: {
        lineHeight: theme.siteVariables.lineHeightDefault
      },
      ...p.variant === "02" && {
        padding: pxArrayToRem([8, 0, 0]),
        [`& .${profileMenuItemClassName}:last-child`]: {
          borderBottom: "none"
        }
      }
    };
  },
  accordianContentTitle: () => {
    return {
      padding: `${pxToRem(0)} ${pxToRem(28)}`,
      marginBottom: pxToRem(8)
    };
  },
  label: ({ variables: v, theme: { siteVariables } }) => {
    return {
      textDecoration: "none",
      color: v.businessProfileLabelColor,
      fontSize: pxToRem(14),
      lineHeight: pxToRem(18),
      userSelect: "none",
      ...siteVariables.textStyles.Text.Roman55
    };
  }
};

// packages/aria/src/themes/base/components/Profile/ProfileMenuItem/profileMenuItemStyles.ts
var profileMenuItemStyles = {
  root: ({ variables: v, props: p }) => {
    return {
      width: "100%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      cursor: "pointer",
      color: v.profileMenuItemFontColor,
      ...p.variant === "02" && {
        padding: pxArrayToRem([15, 12, 15, 17])
      }
    };
  },
  iconContainer: () => {
    return {
      width: pxToRem(25)
    };
  },
  iconAfterContainer: ({ variables: v }) => {
    return {
      width: pxToRem(23),
      marginLeft: pxToRem(5),
      color: v.iconAfterColor
    };
  },
  label: ({ variables: v, theme, props: p }) => {
    return {
      color: v.profileMenuItemFontColor,
      userSelect: "none",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      flex: 1,
      margin: `0`,
      ...mapLabelVariantToFontStyles({ variant: "04" }, theme),
      lineHeight: theme.siteVariables.lineHeightSmallest,
      ...p.variant === "02" && {
        ...mapLabelVariantToFontStyles({ variant: "03-alt" }, theme),
        lineHeight: pxToRem(18)
      }
    };
  }
};

// packages/aria/src/themes/base/components/Profile/PersonalProfileMenu/personalProfileMenuStyles.ts
var personalProfileMenuStyles = {
  root: () => {
    return {
      width: "100%",
      display: "flex",
      flexDirection: "column"
    };
  },
  group: ({ variables: v, props: p }) => {
    return {
      padding: pxArrayToRem([12, 0, 10]),
      borderBottom: v.personalProfileMenuDividerStyle,
      ...p.variant === "02" && {
        padding: 0,
        borderBottom: "none"
      }
    };
  },
  logout: ({ props: p }) => {
    return {
      padding: pxArrayToRem([25, 0, 12]),
      ...p.variant === "02" && {
        padding: 0,
        marginBottom: pxToRem(62),
        [`& .${profileMenuItemClassName}`]: {
          borderBottom: "none"
        }
      }
    };
  },
  label: ({ variables: v, theme }) => {
    return {
      padding: pxArrayToRem([0, 10]),
      color: v.personalProfileMenuLabelColor,
      userSelect: "none",
      marginBottom: pxToRem(8),
      textTransform: "uppercase",
      ...mapLabelVariantToFontStyles({ variant: "04" }, theme)
    };
  }
};

// packages/aria/src/themes/base/components/Dialog/dialogStyles.ts
var dialogStyles = {
  root: ({ variables: v, theme }) => ({
    background: v.rootBackground,
    border: v.border,
    borderRadius: v.rootBorderRadius,
    outline: "none",
    position: "relative",
    overflowX: "hidden",
    overflowY: v.overflow ? "auto" : "hidden",
    display: "grid",
    gridTemplateRows: "auto 1fr auto",
    gridTemplateColumns: "1fr auto",
    msGridColumns: "1fr auto",
    boxShadow: v.boxShadow,
    color: v.foregroundColor,
    [`@media screen and (max-width: ${v.rootWidth})`]: {},
    minHeight: `calc(100% - env(safe-area-inset-bottom, 0) - env(safe-area-inset-top, 0))`,
    [`@supports (-webkit-touch-callout: none)`]: {
      minHeight: "-webkit-fill-available"
    },
    width: v.rootWidthMobile,
    [theme.screen.laptop]: {
      width: v.rootWidth,
      minHeight: "unset",
      maxWidth: v.rootMaxWidth,
      maxHeight: v.rootMaxHeight
    }
  }),
  footer: ({ variables: v }) => ({
    gridColumn: "1 / -1",
    gridRow: 3,
    msGridRow: 3,
    padding: v.rootPadding
  }),
  actions: ({ variables: v }) => ({
    display: "flex",
    width: "100%",
    [`& .${flexClassName}`]: {
      flexWrap: "wrap",
      width: "100%"
    },
    [`@media screen and (max-width: ${v.footerActionsBreakpoint})`]: {
      [`& .${buttonClassName}`]: {
        width: "100%",
        maxWidth: "100%",
        margin: 0
      },
      [`& .${flexClassName} > :not(:last-child)`]: {
        marginBottom: "0.5rem"
      }
    }
  }),
  content: ({ variables: v }) => ({
    margin: v.contentMargin,
    gridColumn: "1 / span 2",
    gridRow: 2,
    msGridColumn: 1,
    msGridColumnSpan: 2,
    msGridRow: 2,
    justifySelf: "left",
    width: "100%",
    padding: v.rootPadding,
    borderTop: `1px solid ${v.contentTopBorderColor}`,
    [`@media screen and (max-width: ${v.rootWidth}), screen and (max-height: 400px)`]: {
      maxHeight: "100%",
      overflow: "unset"
    }
  }),
  header: ({ variables: v }) => ({
    margin: v.headerMargin,
    gridRow: 1,
    msGridRow: 1,
    gridColumn: 1,
    msGridColumn: 1,
    justifySelf: "left",
    fontSize: v.headerFontSize,
    fontWeight: v.headerFontWeight,
    display: "flex",
    alignItems: "center"
  }),
  headerAction: ({ variables: v }) => ({
    gridRow: 1,
    msGridRow: 1,
    gridColumn: 2,
    msGridColumn: 2,
    color: v.foregroundColor,
    margin: v.headerActionMargin,
    border: "none"
  }),
  overlay: ({ theme, props: p, variables: v }) => ({
    alignItems: "center",
    backgroundColor: theme.siteVariables.colors.transparency["10%"],
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    left: 0,
    overflow: "auto",
    position: "fixed",
    right: 0,
    top: 0,
    zIndex: v.overlayZIndex,
    ...p.backdrop && {
      backgroundColor: v.overlayBackground,
      backdropFilter: v.overlayBackdrop
    }
    // BC-422: Required to fill screen on iPad portrait.
    // ['@media only screen and (min-device-width: 768px) and (max-device-width: 1024px) and (orientation: portrait)']:
    //     {
    //         position: 'absolute',
    //     },
  })
};

// packages/aria/src/themes/base/components/Popup/popupStyles.ts
var popupStyles = {
  root: () => {
    return {};
  }
};

// packages/aria/src/themes/base/components/Accordion/accordionStyles.ts
import {
  accordionContentClassName as accordionContentClassName2,
  accordionSlotClassNames as accordionSlotClassNames2,
  accordionTitleSlotClassNames as accordionTitleSlotClassNames3
} from "@fluentui/react-northstar";
var accordionStyles = {
  root: ({ variables: v }) => ({
    verticalAlign: "middle",
    display: "flex",
    flexDirection: "column",
    marginBlockEnd: 0,
    marginBlockStart: 0,
    borderBottom: `${pxToRem(2)} solid ${v.primaryBorderColor}`,
    [`& .${accordionSlotClassNames2.title}`]: {
      borderTop: `${pxToRem(2)} solid ${v.primaryBorderColor}`,
      paddingRight: 0,
      cursor: "pointer"
    },
    [`& .${accordionTitleSlotClassNames3.contentWrapper}`]: {
      paddingTop: pxToRem(16),
      paddingRight: pxToRem(18),
      cursor: "pointer"
    },
    [`& .${accordionContentClassName2}`]: {
      marginBottom: pxToRem(50),
      fontSize: pxToRem(18),
      marginInlineStart: 0
    }
  })
};

// packages/aria/src/themes/base/components/Accordion/accordionContentStyles.ts
var accordionContentStyles = {
  root: ({ props }) => ({
    display: "none",
    verticalAlign: "middle",
    ...props.active && { display: "block" },
    marginInlineStart: pxToRem(20)
  })
};

// packages/aria/src/themes/base/components/Accordion/accordionTitleStyles.ts
var accordionTitleStyles = {
  root: ({ variables: v }) => ({
    display: "inline-block",
    verticalAlign: "middle",
    paddingRight: pxToRem(18),
    color: v.primaryTextColor
  }),
  contentWrapper: () => ({
    display: "flex",
    gridTemplateColumns: "auto",
    msGridColumns: "auto",
    userSelect: "none",
    flexDirection: "row-reverse",
    outline: "none"
  }),
  indicator: ({ variables: v }) => ({
    alignItems: "center",
    justifyContent: "center",
    display: "inline-flex",
    backgroundPosition: "center",
    height: pxToRem(18.67),
    width: pxToRem(18.67),
    "& svg": {
      fill: v.primarySVGColor
    }
  }),
  content: ({ theme: { siteVariables } }) => ({
    alignItems: "center",
    display: "grid",
    flex: "100%",
    fontSize: pxToRem(24),
    lineHeight: siteVariables.lineHeightSmallest,
    letterSpacing: siteVariables.letterSpacingMedium,
    height: pxToRem(26),
    marginBottom: pxToRem(16),
    ...siteVariables.textStyles.Text.Roman55
  })
};

// packages/aria/src/themes/base/getBorderFocusStyles.ts
var defaultColor = "transparent";
var getBorderFocusStyles = (args) => {
  const sv = args.variables;
  const {
    borderRadius = sv.focusBorderRadius || sv.borderRadius,
    focusInnerBorderColor: focusInnerBorderColor2 = sv.focusInnerBorderColor || defaultColor,
    focusOuterBorderColor: focusOuterBorderColor2 = sv.focusOuterBorderColor || defaultColor,
    borderPadding,
    focusInnerBorderWidth = sv.focusInnerBorderWidth || sv.borderWidth,
    focusOuterBorderWidth = sv.focusOuterBorderWidth || sv.borderWidth
  } = args;
  const defaultPreudoStyles = {
    content: '""',
    position: "absolute",
    borderStyle: "solid",
    pointerEvents: "none",
    borderRadius
  };
  const borderPaddingTop = borderPadding?.top || borderPadding;
  const borderPaddingBottom = borderPadding?.bottom || borderPadding;
  const borderPaddingLeft = borderPadding?.left || borderPadding;
  const borderPaddingRight = borderPadding?.right || borderPadding;
  return {
    ":focus": {
      outline: "none"
    },
    ":focus-visible": {
      borderColor: "transparent",
      ":before": {
        ...defaultPreudoStyles,
        borderWidth: focusInnerBorderWidth,
        zIndex: sv.zIndexes.foreground,
        borderColor: focusInnerBorderColor2,
        top: borderPadding == null ? "0" : `-${borderPaddingTop}`,
        bottom: borderPadding == null ? "0" : `-${borderPaddingBottom}`,
        left: borderPadding == null ? "0" : `-${borderPaddingLeft}`,
        right: borderPadding == null ? "0" : `-${borderPaddingRight}`
      },
      ":after": {
        ...defaultPreudoStyles,
        borderWidth: focusOuterBorderWidth,
        zIndex: sv.zIndexes.foreground,
        borderColor: focusOuterBorderColor2,
        top: borderPadding == null ? `-${focusInnerBorderWidth}` : `calc(0px - ${borderPaddingTop} - ${focusInnerBorderWidth})`,
        bottom: borderPadding == null ? `-${focusInnerBorderWidth}` : `calc(0px - ${borderPaddingBottom} - ${focusInnerBorderWidth})`,
        left: borderPadding == null ? `-${focusInnerBorderWidth}` : `calc(0px - ${borderPaddingLeft} - ${focusInnerBorderWidth})`,
        right: borderPadding == null ? `-${focusInnerBorderWidth}` : `calc(0px - ${borderPaddingRight} - ${focusInnerBorderWidth})`
      }
    }
  };
};

// packages/aria/src/themes/base/components/DropdownMenu/dropdownMenuOutlineOptionsListStyles.ts
var dropdownMenuOutlineOptionsListStyles = {
  root: ({ variables: v, theme: { siteVariables } }) => {
    return {
      backgroundColor: v.dropdownMenuOutlineOptionsListBackgroundColor,
      borderBottom: `${pxToRem(2)} solid ${siteVariables.bodyColor}`,
      userSelect: "none",
      WebkitUserSelect: "none",
      msUserSelect: "none",
      MozUserSelect: "none",
      ...getBorderFocusStyles({ variables: siteVariables })
    };
  }
};

// packages/aria/src/themes/base/components/DropdownMenu/dropdownMenuOutlineButtonTopStyles.ts
var dropdownMenuOutlineButtonTopStyles = {
  root: ({ variables: v, theme: { siteVariables } }) => {
    return {
      backgroundColor: v.DropdownMenuOutlineButtonTopBackgroundColor,
      borderTop: `${pxToRem(2)} solid`,
      borderBottom: `${pxToRem(2)} solid`,
      borderColor: siteVariables.bodyColor,
      userSelect: "none",
      WebkitUserSelect: "none",
      msUserSelect: "none",
      MozUserSelect: "none",
      cursor: "pointer",
      ...getBorderFocusStyles({ variables: siteVariables }),
      "& > span": {
        display: "inline-block",
        margin: 0,
        color: siteVariables.bodyColor
      }
    };
  },
  navMenuIconDiv: ({ theme }) => {
    return {
      display: "inline",
      float: "right",
      position: "relative",
      "& span": {
        height: "auto",
        width: "auto",
        marginRight: "none"
      },
      "& svg": {
        position: "relative",
        height: pxToRem(14),
        width: "auto",
        marginBottom: pxToRem(4),
        "& path": {
          fill: theme.siteVariables.bodyColor
        }
      }
    };
  },
  navMenuDropdownIcon: () => {
    return {
      display: "inline-block",
      right: "0"
    };
  }
};

// packages/aria/src/themes/base/components/DropdownMenu/dropdownMenuOutlineStyles.ts
var dropdownMenuStyles = {
  root: ({ variables: v, theme }) => {
    return {
      backgroundColor: v.dropdownMenuBackgroundColor,
      cursor: "pointer",
      color: v.navMenuTextColor,
      "& path": {
        fill: theme.siteVariables.bodyColor
      }
    };
  }
};

// packages/aria/src/themes/base/components/Tile/TileImage/tileImageStyles.ts
var tileStyles = {
  // @ts-expect-error Project getting stale, don't want to fix whats not broken
  root: ({ variables: v, theme, props: p }) => {
    return {
      position: "relative",
      width: p.width,
      height: p.height,
      borderRadius: v.borderRadius,
      overflow: "hidden",
      [`& .${tileImageSlotClassNames.imageContainer}`]: {
        width: p.width,
        height: p.height
      },
      [`& .${tileOverlayClassName}`]: {
        width: p.width,
        height: p.height
      },
      ...p.aspectRatio && {
        aspectRatio: p.aspectRatio,
        width: "100%",
        height: "auto",
        [`& .${tileImageSlotClassNames.imageContainer}`]: {
          aspectRatio: p.aspectRatio,
          width: "100%",
          height: "auto"
        },
        [`& .${tileOverlayClassName}`]: {
          aspectRatio: p.aspectRatio,
          width: "100%",
          height: "auto"
        }
      },
      [`& .${buttonClassName}`]: {
        borderRadius: "50%",
        pointerEvents: "auto"
      },
      [`& .${tileImageSlotClassNames.awardIndicator}`]: {
        position: "absolute",
        top: 0,
        left: pxToRem(18),
        [theme.screen.laptop]: {
          opacity: 0,
          top: pxToRem(-65),
          transition: "all 100ms ease"
        }
      },
      ":hover": {
        [`& .${tileOverlayClassName}`]: {
          opacity: 1
        },
        [`& .${tileImageSlotClassNames.awardIndicator}`]: {
          top: 0,
          opacity: 1
        }
      },
      [theme.screen.touchScreen]: {
        [`& .${tileOverlayClassName}`]: {
          opacity: 1
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/Input/inputStyles.ts
import { svgIconClassName as svgIconClassName9 } from "@archipro-design/icons";
var inputStyles = {
  root: ({ props: p }) => ({
    flexDirection: "column",
    justifyContent: "center",
    display: "inline-flex",
    position: "relative",
    outline: 0,
    verticalAlign: "middle",
    ...p.fluid && { width: "100%" },
    ...p.labelPosition === "inline" && {
      flexDirection: "row",
      alignItems: "center"
    }
  }),
  input: ({ props: p, variables: v, theme }) => ({
    backgroundColor: v.backgroundColor,
    ...p.inverted && {
      backgroundColor: v.backgroundColorInverted
    },
    lineHeight: "unset",
    color: v.fontColor,
    borderColor: v.borderColor,
    borderRadius: v.borderRadius,
    borderStyle: "solid",
    borderWidth: v.borderWidth,
    outline: "none",
    padding: v.inputPadding,
    position: "relative",
    ...p.fluid && { width: "100%" },
    ...p.inline && { float: "left" },
    // Overrides for "disabled" inputs
    ...p.disabled && {
      color: v.colorDisabled,
      boxShadow: "none"
    },
    "::placeholder": {
      color: v.placeholderColor ?? theme.siteVariables.colorScheme.default?.["placeholder"],
      opacity: 1,
      // undo Firefox default opacity
      ...p.disabled && {
        color: v.colorDisabled
      }
    },
    ":focus": {
      ...!p.error && {
        borderColor: v.inputFocusBorderColor,
        borderRadius: v.inputFocusBorderRadius
      }
    },
    ...p.clearable && { padding: v.inputPaddingWithIconAtEnd },
    ...p.hasIcon && {
      padding: p.iconPosition === "start" ? v.inputPaddingWithIconAtStart : v.inputPaddingWithIconAtEnd
    },
    ...p.labelPosition === "inside" && {
      paddingTop: v.inputInsideLabelPaddingTop
    },
    ...p.error && { border: `${pxToRem(1)} solid ${v.borderColorError}` },
    "::-ms-clear": {
      display: "none"
    },
    fontSize: v.fontSize
  }),
  icon: ({ props: p, variables: v }) => ({
    color: v.iconColor,
    outline: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: v.iconPosition,
    top: 0,
    bottom: 0,
    ...p.error && { color: v.colorError },
    ...p.requiredAndSuccessful && {
      color: v.successfulColor
    },
    ...p.disabled && {
      color: v.colorDisabled
    },
    ...p.iconPosition === "start" && {
      left: v.iconLeft
    },
    ...p.iconPosition === "end" && {
      right: v.iconRight
    },
    ...p.clearable && p.hasValue && {
      [`& .${svgIconClassName9}`]: {
        height: "100%",
        width: pxToRem(16),
        color: v.iconColor,
        ...p.disabled && {
          color: v.colorDisabled
        }
      }
    }
  }),
  inputContainer: () => ({
    position: "relative"
  })
};

// packages/aria/src/themes/base/components/DropdownMenu/dropdownMenuOutlineOptionStyles.ts
var dropdownMenuOutlineOptionStyles = {
  root: ({ variables: v }) => {
    return {
      userSelect: "none",
      WebkitUserSelect: "none",
      msUserSelect: "none",
      MozUserSelect: "none",
      display: "block",
      borderTop: `${pxToRem(2)} solid`,
      cursor: "pointer",
      ":first-child": {
        borderTop: `none`
      },
      color: v.activeColor
    };
  },
  menuItemContainer: ({ variables: v }) => {
    return {
      ":hover span": {
        opacity: 1,
        color: v.activeColor
      }
    };
  },
  menuListItemNotSelected: ({ variables: v }) => {
    return {
      opacity: v.inactiveOpacity,
      color: v.inactiveColor
    };
  }
};

// packages/aria/src/themes/base/getPointerStyles.ts
var getContainerStyles = (options) => {
  const { padding, placement } = options;
  return {
    ...placement === "bottom" && {
      paddingTop: padding
    },
    ...placement === "top" && {
      paddingBottom: padding
    },
    ...placement === "left" && {
      paddingRight: padding
    },
    ...placement === "right" && {
      paddingLeft: padding
    }
  };
};
var getPointerStyles = (options) => {
  const {
    backgroundColor,
    borderColor,
    rtl,
    borderSize,
    gap,
    height,
    padding,
    placement,
    svg,
    width
  } = options;
  return {
    display: "block",
    position: "absolute",
    zIndex: 1,
    ...(placement === "bottom" || placement === "top") && {
      paddingLeft: gap,
      paddingRight: gap,
      height,
      width: `calc(${width} + (${gap} * 2))`
    },
    ...(placement === "left" || placement === "right") && {
      paddingBottom: gap,
      paddingTop: gap,
      height: `calc(${width} + (${gap} * 2))`,
      width: height
    },
    ...placement === "bottom" && {
      top: `calc(${padding} - ${height} + (${borderSize} * 2))`
    },
    ...placement === "top" && {
      bottom: `calc(${padding} - ${height} + ${borderSize})`
    },
    ...placement === "left" && {
      right: `calc(${padding} - ${height} + ${borderSize})`
    },
    ...placement === "right" && {
      left: `calc(${padding} - ${height} + ${borderSize})`
    },
    "::before": {
      content: '" "',
      display: "block",
      height,
      position: "relative",
      transformOrigin: "center top",
      borderBottomColor: "transparent",
      borderLeftColor: "transparent",
      borderRightColor: "transparent",
      borderTopColor: "transparent",
      borderStyle: "solid",
      left: 0,
      top: 0,
      ...placement === "bottom" && {
        borderBottomColor: backgroundColor,
        borderWidth: `0 ${height} ${height}`
      },
      ...placement === "top" && {
        borderTopColor: backgroundColor,
        borderWidth: `${height} ${height} 0`,
        top: `calc(${borderSize} * -1)`
      },
      ...placement === "left" && {
        borderLeftColor: backgroundColor,
        borderWidth: `${height} 0 ${height} ${height}`
      },
      ...placement === "right" && {
        borderRightColor: backgroundColor,
        borderWidth: `${height} ${height} ${height} 0`
      }
    },
    "::after": {
      content: '" "',
      display: "block",
      height,
      position: "relative",
      transformOrigin: "center top",
      zIndex: -1,
      borderBottomColor: "transparent",
      borderLeftColor: "transparent",
      borderRightColor: "transparent",
      borderTopColor: "transparent",
      borderStyle: "solid",
      ...placement === "bottom" && {
        borderBottomColor: borderColor,
        borderWidth: `0 ${height} ${height}`,
        left: 0,
        bottom: `calc(${height} + 1px)`
      },
      ...placement === "top" && {
        borderTopColor: borderColor,
        borderWidth: `${height} ${height} 0`,
        left: 0,
        bottom: height
      },
      ...placement === "left" && {
        borderLeftColor: borderColor,
        borderWidth: `${height} 0 ${height} ${height}`,
        left: borderSize,
        bottom: width
      },
      ...placement === "right" && {
        borderRightColor: borderColor,
        borderWidth: `${height} ${height} ${height} 0`,
        right: borderSize,
        bottom: width
      }
    },
    ...svg && {
      // :before & :after are used to draw CSS triangles, not valid for SVG
      "::before": {
        content: '" "',
        backgroundImage: svg,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        display: "block",
        position: "relative",
        ...placement === "bottom" && {
          height: `calc(${width} + (${gap} * 2))`,
          width: height,
          left: gap,
          bottom: `calc(${width} - ${height} + ${borderSize})`,
          transform: "rotate(90deg) /* @noflip */"
        },
        ...placement === "top" && {
          height: `calc(${width} + (${gap} * 2))`,
          width: height,
          left: gap,
          bottom: `calc(${gap} + ${height} - ${borderSize})`,
          transform: "rotate(-90deg) /* @noflip */"
        },
        ...placement === "left" && {
          height: width,
          width: height,
          left: 0,
          transform: rtl ? "rotate(0) /* @noflip */" : "rotate(180deg) /* @noflip */"
        },
        ...placement === "right" && {
          height: width,
          width: height,
          right: 0,
          transform: rtl ? "rotate(180deg) /* @noflip */" : "rotate(0) /* @noflip */"
        }
      },
      "::after": void 0
    }
  };
};

// packages/aria/src/themes/base/components/Popup/popupContentStyles.ts
var popupContentStyles = {
  root: ({ props: p, variables: v }) => ({
    display: "block",
    zIndex: v.zIndex,
    ...p.pointing && {
      pointerEvents: "none",
      ...getContainerStyles({
        placement: p.basePlacement,
        padding: v.pointerMargin
      })
    },
    ...p.autoSize && {
      // when autoSize is true, root will have inline style maxWidth and maxHeight set by popper
      display: "flex"
    }
  }),
  pointer: ({ props: p, variables: v, rtl }) => getPointerStyles({
    backgroundColor: v.backgroundColor,
    borderColor: v.borderColor,
    borderSize: v.borderSize,
    gap: v.pointerGap,
    padding: v.pointerMargin,
    height: v.pointerHeight,
    width: v.pointerWidth,
    placement: p.basePlacement,
    rtl
  }),
  content: ({ props: p, variables: v }) => ({
    display: "block",
    background: v.backgroundColor,
    color: v.color,
    boxShadow: v.boxShadow,
    border: `${v.borderSize} solid ${v.borderColor}`,
    borderRadius: v.borderRadius,
    padding: v.padding,
    transform: "rotate(360deg)",
    ...p.pointing && {
      pointerEvents: "all"
    },
    ...p.autoSize && {
      overflow: "auto"
    }
  })
};

// packages/aria/src/themes/base/components/Tabs/tabsStyles.ts
var tabsStyles = {
  root: ({ variables: v, props: p, theme }) => {
    return {
      width: "100%",
      display: "inline-flex",
      justifyContent: "center",
      alignContent: "flex-start",
      [`& .${buttonClassName}`]: {
        margin: v.tabsButtonMargin,
        ...p.variant === "rounded" && {
          boxSizing: "border-box",
          border: `solid transparent ${v.tabsRoundedBorderWidth}`,
          borderRadius: v.tabsRoundedBorderRadius,
          ":hover": {
            border: `solid ${v.tabsRoundedBorderColor} ${v.tabsRoundedBorderWidth}`,
            backgroundColor: "transparent"
          },
          ...getBorderFocusStyles({
            variables: {
              ...theme.siteVariables,
              borderRadius: v.tabsRoundedBorderRadius
            }
          })
        }
      },
      [`& .${tabsClassNames.active}`]: {
        ...p.variant === "rounded" && {
          border: `solid ${v.tabsRoundedBorderColor} ${v.tabsRoundedBorderWidth}`
        }
      },
      ...p.underline && {
        ...p.variant === "rounded" && {
          borderTop: `${v.tabsUnderlineWidth} solid ${v.tabsBorderColor}`,
          paddingTop: v.tabsUnderlinePaddingTop
        },
        ...p.variant === "square" && {
          borderBottom: `${v.tabsUnderlineWidth} solid ${v.tabsBorderColor}`
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/MegaMenu/NavigationList/navigationListStyles.ts
var navigationListStyles = {
  root: ({ variables: v }) => {
    return {
      width: v.listWidth
    };
  },
  title: ({ variables: v }) => {
    return { marginBottom: v.titleOptionGap, minHeight: v.titleMinHeight };
  },
  options: ({ variables: v }) => {
    return {
      width: "100%",
      "> *:not(:first-child)": {
        marginTop: v.optionGap
      }
    };
  },
  more: ({ variables: v, theme }) => {
    return {
      marginTop: v.optionMoreButtonGap,
      [`& .${labelClassName}`]: {
        color: theme.siteVariables.colorScheme.default?.foreground,
        fontWeight: theme.siteVariables.textStyles.Text.Medium65.fontWeight
      }
    };
  }
};

// packages/aria/src/themes/base/components/MegaMenu/NavigationListGroup/navigationListGroupStyles.ts
var navigationListGroupStyles = {
  root: ({ variables: v }) => {
    return {
      "> *:not(:first-child)": {
        marginTop: v.listGap
      }
    };
  }
};

// packages/aria/src/themes/base/components/Tile/tileGridStyles.ts
var tileGridStyles = {
  root: ({ theme }) => {
    return {
      width: "100%",
      [`& .${HeaderClassName}`]: {
        fontSize: theme.siteVariables.fontSizes["label01"],
        lineHeight: theme.siteVariables.lineHeightSmallest,
        textAlign: "left",
        color: theme.siteVariables.colors.charcoal["250"],
        margin: 0,
        [theme.screen.min.tablet]: {
          fontSize: pxToRem(58),
          textAlign: "center"
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/SearchBar/searchBarStyles.ts
import { svgIconClassName as svgIconClassName10 } from "@archipro-design/icons";
import { boxClassName as boxClassName2, inputClassName as inputClassName3 } from "@fluentui/react-northstar";
var searchBarStyles = {
  root: ({ variables: v, theme, props: p }) => {
    return {
      padding: v.searchBarPadding,
      backgroundColor: v.searchBarBackgroundColor,
      borderRadius: v.searchBarBorderRadius,
      border: `${v.searchBarBorderWidth} solid ${v.searchBarBorderColor}`,
      ...p.searchWithCancelButton && { width: v.widthSearchCancel },
      //render 100% only when there is cancel button
      [`& .${buttonClassName}`]: {
        ":hover": {
          backgroundColor: "transparent"
        }
      },
      [`& .${inputClassName3}`]: {
        paddingLeft: v.searchInputPaddingX,
        paddingRight: v.searchInputPaddingX,
        [`& .${boxClassName2}`]: {
          fontSize: `max(16px, ${pxToRem(16)})`,
          padding: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          color: v.searchBarInputColor,
          ":focus": {
            color: v.searchBarFocusedInputColor
          },
          [theme.screen.laptop]: {
            fontSize: pxToRem(18)
          }
        }
      },
      [`& .${svgIconClassName10}`]: {
        fill: v.searchBarIconColor
      }
    };
  }
};

// packages/aria/src/themes/base/components/SearchMenu/searchMenuStyles.ts
var searchMenuStyles = {
  root: ({ variables: v }) => {
    return {
      backgroundColor: v.searchMenuBackgroundColor,
      width: "100%",
      [`& .${searchMenuClassNames.firstResult}`]: {
        marginBottom: v.searchMenuResultsGap
      }
    };
  },
  container: ({ variables: v }) => {
    return {
      width: "100%",
      padding: v.searchMenuPadding,
      [`&:empty`]: {
        padding: 0
      }
    };
  }
};

// packages/aria/src/themes/base/components/SearchMenu/searchResultStyles.ts
var searchResultStyles = {
  root: ({ variables: v, theme }) => {
    return {
      [`& .${searchResultSlotClassNames.match}`]: {
        color: `${v.searchResultItemMatchColor} !important`,
        whiteSpace: "pre"
        // whiteSpace: pre breaks ellpisis and truncation
      },
      [`& .${searchResultSlotClassNames.searchCategory}`]: {
        marginBottom: v.searchResultsHeaderMarginBottom
      },
      [`& .${searchResultSlotClassNames.searchItem}`]: {
        margin: 0,
        marginBottom: v.searchMobileResultsItemMarginBottom,
        maxWidth: v.searchResultsMaxWidth,
        [`& .${captionClassName}`]: {
          textAlign: "left",
          color: v.searchResultItemColor
        },
        [theme.screen.laptop]: {
          marginBottom: v.searchResultsItemMarginBottom,
          [`& .${captionClassName}`]: {
            fontSize: v.searchResultsItemFontSize,
            letterSpacing: theme.siteVariables.letterSpacingSmall,
            lineHeight: theme.siteVariables.lineHeightSmall
          }
        }
      },
      [`& .${searchResultSlotClassNames.action}`]: {
        [theme.screen.laptop]: {
          marginTop: v.searchResultActionGap,
          [`& .${buttonClassName}`]: {
            textDecoration: "underline",
            textUnderlineOffset: pxToRem(2)
          }
        }
      },
      [`& .${searchResultSlotClassNames.itemContainer}`]: {
        width: "100%",
        overflow: "hidden",
        justifyContent: "flex-start",
        [`& .${labelClassName}`]: {
          width: "100%",
          overflow: "hidden"
        }
      },
      [`& .${searchResultSlotClassNames.itemContentContainer}`]: {
        flex: 1,
        overflow: "hidden"
      },
      [`& .${searchResultSlotClassNames.unmatch}`]: {
        width: "auto",
        display: "inline-block",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        lineHeight: theme.siteVariables.lineHeightSmaller
      }
    };
  },
  preserveWhitespace: () => {
    return {
      whiteSpace: "pre"
    };
  },
  skeletonSearchItem: () => {
    return {
      marginBottom: pxToRem(4)
    };
  }
};

// packages/aria/src/themes/base/animations/skeleton.ts
var waveStyles = (primaryColor, secondoryColor) => ({
  position: "static",
  overflow: "hidden",
  animationDuration: "4.5s",
  animationName: wave,
  animationIterationCount: "infinite",
  backgroundColor: "#FFFFFF",
  animationTimingFunction: "linear",
  backgroundImage: `linear-gradient(to right, ${secondoryColor} 0%, ${primaryColor} 20%, ${secondoryColor} 40%)`,
  backgroundSize: "1200px 100%"
});
var pulseStyles = {
  animationName: pulse,
  animationDuration: "1.5s",
  animationIterationCount: "infinite"
};
var getAnimations = (primaryColor, secondoryColor) => ({
  pulse: {
    [`& .${skeletonLineClassName}`]: pulseStyles
  },
  wave: {
    [`& .${skeletonLineClassName}`]: waveStyles(
      primaryColor,
      secondoryColor
    )
  }
});

// packages/aria/src/themes/base/components/Skeleton/skeletonStyles.ts
var skeletonStyles = {
  root: ({ props: p, variables: v }) => {
    const animations2 = getAnimations(
      v.animationBackground,
      v.animationBackgroundSecondary
    );
    return {
      width: "100%",
      ...p.animation && animations2[p.animation]
    };
  }
};

// packages/aria/src/themes/base/components/Skeleton/skeletonLineStyles.ts
var skeletonLineStyles = {
  root: ({ props: p, variables: v }) => {
    return {
      display: "block",
      width: p.width,
      height: p.height,
      backgroundColor: v.lineBackground,
      margin: v.lineMargin
    };
  }
};

// packages/aria/src/themes/base/components/MegaMenu/BreadcrumbNavigation/breadcrumbNavigationStyles.ts
import { dividerClassName as dividerClassName2, svgIconClassName as svgIconClassName11 } from "@fluentui/react-northstar";
var breadcrumbNavigationStyles = {
  root: ({ variables: v, theme }) => {
    return {
      [`& .${buttonClassName}`]: {
        color: theme.siteVariables.colorScheme.default?.foregroundDisabled,
        maxWidth: `calc(100% - ${pxToRem(30)})`
      },
      [`& .${navigationListSlotClassNames.selected}`]: {
        color: theme.siteVariables.colorScheme.default?.foreground,
        ":hover": {
          color: theme.siteVariables.colorScheme.default?.foreground
        },
        [`& .${svgIconClassName11}`]: {
          fill: theme.siteVariables.colorScheme.default?.foreground
        }
      },
      [`& .${breadcrumbNavigationSlotClassNames.selectedSecondary}`]: {
        color: theme.siteVariables.colorScheme.default?.foreground,
        textDecoration: "none",
        [`& .${svgIconClassName11}`]: {
          display: "none"
        }
      },
      [`& .${dividerClassName2}`]: {
        margin: `0 ${v.listXPadding}`,
        padding: 0,
        width: v.dividerWidth,
        opacity: v.dividerOpacity,
        height: v.dividerHeight,
        "::before": {
          backgroundColor: v.dividerColor
        }
      },
      [`& .${dividerClassName2}:last-of-type`]: {
        marginRight: 0
      }
    };
  }
};

// packages/aria/src/themes/base/components/MegaMenu/megaMenuStyles.ts
var megaMenuStyles = {
  root: ({ variables: v }) => {
    return {
      padding: v.padding,
      marginTop: pxToRem(62)
    };
  },
  navListContainer: () => {
    return {
      height: "fit-content"
    };
  }
};

// packages/aria/src/themes/base/components/Carousel/carouselStyles.ts
var carouselStyles = {
  root: ({ variables: v, props: p, theme }) => {
    return {
      display: "inline-block",
      position: "relative",
      width: v.containerWidth,
      height: v.containerHeight,
      [`& .${carouselPaddleClassName}`]: {
        borderRadius: "50%",
        pointerEvents: "auto"
      },
      [`& .${carouselOverlayClassName}`]: {
        transition: "opacity 150ms ease",
        opacity: v.overlayInitialOpacity,
        padding: v.overlayPadding
      },
      [theme.screen.tablet]: {
        [theme.screen.nonTouch]: {
          [`& .${carouselOverlayClassName}`]: {
            opacity: v.overlayInitialOpacityDesktop !== void 0 ? v.overlayInitialOpacityDesktop : v.overlayInitialOpacity
          }
        }
      },
      ":hover": {
        [`& .${carouselOverlayClassName}`]: {
          opacity: 1
        },
        [`& .${carouselSlotClassNames.paddleContainer}`]: {
          alignItems: p.paddlePosition,
          [`& .${carouselPaddleClassName}`]: {
            opacity: 1
          }
        }
      }
    };
  },
  itemsContainerWrapper: () => ({
    position: "relative",
    overflowX: "hidden",
    height: "100%"
  }),
  itemsContainer: ({ variables: v, props: p, theme }) => {
    return {
      padding: 0,
      margin: 0,
      display: "flex",
      listStyle: "none",
      willChange: "transform",
      width: v.containerWidth,
      height: v.containerHeight,
      overflow: "hidden",
      justifyContent: v.containerHAlign,
      alignItems: v.containerVAlign,
      background: v.containerBackgroundColor,
      "& .swiper": {
        width: "100%",
        "& .swiper-pagination-fraction": {
          display: "flex",
          justifyContent: "center",
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          borderRadius: pxToRem(36),
          width: pxToRem(48),
          padding: pxToRem(4),
          margin: pxToRem(8),
          background: theme.siteVariables.colors.white["300"],
          textAlign: "center",
          fontSize: theme.siteVariables.fontSizes["caption03"],
          lineHeight: 1
        },
        "& .swiper-pagination-bullets": {
          "--swiper-pagination-bullet-horizontal-gap": pxToRem(
            p.customBullet?.gap || 2
          ),
          "--swiper-pagination-color": theme.siteVariables.colors.primitive.white,
          "--swiper-pagination-bullet-inactive-color": theme.siteVariables.colors.white["300"],
          "--swiper-pagination-bullet-inactive-opacity": 1,
          maxWidth: pxToRem(55),
          width: "auto",
          whiteSpace: "nowrap",
          overflow: "hidden",
          transition: "bottom 150ms ease",
          position: "absolute",
          bottom: pxToRem(9),
          left: "50%",
          transform: `translateX(-50%)`,
          pointerEvents: "none",
          "& .swiper-pagination-bullet": {
            "--swiper-pagination-bullet-size": pxToRem(
              p.customBullet?.size || 7
            ),
            transform: `scale(1) translateX(var(--carousel-bullets-offset, 0px))`,
            transition: "transform 250ms ease 100ms",
            filter: "drop-shadow(0 1px 1px #C7C7C7)"
          }
        }
      },
      [theme.screen.tablet]: {
        [theme.screen.nonTouch]: {
          "& .swiper": {
            "& .swiper-pagination": {
              opacity: 0
            },
            "& .swiper-pagination-bullets": {
              bottom: 0
            }
          },
          "&:hover .swiper": {
            "& .swiper-pagination": {
              opacity: 1
            },
            "& .swiper-pagination-bullets": {
              bottom: pxToRem(21),
              "& .swiper-pagination-bullet": {
                filter: "none"
              }
            }
          }
        }
      }
    };
  },
  tabNavButton: ({ variables: v }) => ({
    position: "absolute",
    top: "50%",
    width: pxToRem(v.thumbnailSizeRaw),
    // thumbnailSizeRaw required as number
    height: pxToRem(v.thumbnailSizeRaw),
    [`& .${labelClassName}`]: {
      paddingBottom: pxToRem(14)
    }
  }),
  leftNavButton: () => ({
    left: pxToRem(140),
    transform: "translate(-100%, -50%)"
  }),
  rightNavButton: () => ({
    right: pxToRem(140),
    transform: "translate(100%, -50%)"
  }),
  navigationContainer: () => ({
    position: "relative",
    overflow: "hidden"
  }),
  listNavigation: ({ variables: v, props: p }) => {
    const { showPrevTabBtn, showNextTabBtn, maxNavigationItems = 5 } = p;
    const numOfItems = showPrevTabBtn && showNextTabBtn ? maxNavigationItems - 1 : maxNavigationItems;
    return {
      width: pxToRem((v.thumbnailSizeRaw + 8) * numOfItems),
      ...showPrevTabBtn && showNextTabBtn && {
        margin: "auto"
      },
      ...showPrevTabBtn && !showNextTabBtn && {
        marginLeft: pxToRem(v.thumbnailSizeRaw + 8)
      },
      overflow: "hidden"
    };
  }
};

// packages/aria/src/themes/base/components/Carousel/carouselItemStyles.ts
var carouselItemStyles = {
  root: () => {
    return {
      width: "100%",
      height: "100%",
      ":focus": {
        outline: "none"
      }
    };
  }
};

// packages/aria/src/themes/base/components/Carousel/carouselNavigationStyles.ts
var carouselNavigationStyles = {
  root: ({ props: p, variables: v }) => {
    const { activeTabIndex = 0, numOfItems, maxTabs = 5 } = p;
    const thumbnailWidth = v.thumbnailSizeRaw + 8;
    const sliderWidth = numOfItems * thumbnailWidth;
    const trackWidth = activeTabIndex > maxTabs - 1 ? -activeTabIndex * thumbnailWidth : 0;
    const maxTrackWidth = numOfItems - maxTabs > 0 ? -(numOfItems - maxTabs) * thumbnailWidth : 0;
    const translateTrackWidth = trackWidth < maxTrackWidth ? maxTrackWidth : trackWidth;
    return {
      display: "flex",
      justifyContent: "center",
      scrollbarWidth: "none",
      margin: "0",
      overflow: "hidden",
      padding: pxArrayToRem([10, 0, 0, 0]),
      width: pxToRem(sliderWidth),
      backgroundColor: "inherit",
      listStyleType: "none",
      zIndex: v.navigationZIndex,
      transform: `translate3d(${pxToRem(translateTrackWidth)}, 0, 0)`,
      transition: "transform .5s ease"
    };
  },
  leftNavigationButton: ({ variables: v }) => ({
    height: v.navigationTabButtonHeight,
    width: v.navigationTabButtonWidth
  }),
  rightNavigationButton: ({ variables: v }) => ({
    height: v.navigationTabButtonHeight,
    width: v.navigationTabButtonWidth
  })
};

// packages/aria/src/themes/base/components/Carousel/carouselNavigationItemStyles.ts
var carouselNavigationItemStyles = {
  root: ({ variables: v, props: p, theme }) => {
    return {
      display: "block",
      color: "inherit",
      cursor: "pointer",
      whiteSpace: "nowrap",
      scrollSnapType: "center",
      marginRight: pxToRem(8),
      border: `${v.borderWidth} solid transparent`,
      ...p.active && {
        border: `${v.borderWidth} solid ${v.focusBorderColor}`
      },
      ":focus": {
        outline: 0,
        borderColor: theme.siteVariables.colors.grey["300"]
      },
      ":hover": {
        ...!p.active && {
          borderColor: theme.siteVariables.colors.grey["300"]
        }
      }
    };
  },
  content: ({ variables: v }) => ({
    display: "inline-block",
    whiteSpace: "normal",
    width: `${pxToRem(v.thumbnailSizeRaw)}`,
    // thumbnailSizeRaw required as number
    height: `${pxToRem(v.thumbnailSizeRaw)}`
  }),
  indicator: () => ({
    borderRadius: "50%",
    width: pxToRem(7),
    height: pxToRem(7)
  })
};

// packages/aria/src/themes/base/components/Carousel/carouselPaddlesContainerStyles.ts
var carouselPaddlesContainerStyles = {
  root: ({ variables: v }) => {
    return {
      display: "flex",
      width: "100%",
      height: "100%",
      justifyContent: "space-between",
      position: "absolute",
      top: 0,
      right: 0,
      left: 0,
      bottom: 0,
      padding: v.carouselPaddlesContainerPadding,
      mixBlendMode: v.mixBlendMode
    };
  }
};

// packages/aria/src/themes/base/components/Carousel/carouselPaddleStyles.ts
var carouselPaddleStyles = {
  root: ({ props: p, variables: v, theme: { siteVariables } }) => {
    return {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      opacity: 0,
      backgroundColor: v.paddleBackgroundColor,
      height: v.paddleHeight,
      width: v.paddleHeight,
      borderRadius: "50%",
      border: 0,
      position: "absolute",
      cursor: "pointer",
      outline: 0,
      padding: 0,
      transition: "faster",
      zIndex: v.paddlesZIndex,
      ...p.hidden && {
        visibility: "hidden"
      },
      ":active": {
        transition: "ultra-fast"
      },
      ":hover": {
        background: v.paddleHoverBackgroundColor
      },
      ...getBorderFocusStyles({ variables: siteVariables }),
      // Overrides for "disabled" buttons
      ...p.disabled && {
        cursor: "default",
        pointerEvents: "none"
      }
    };
  },
  content: ({ rtl }) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    ...rtl && {
      transform: "scaleX(-1)"
    }
  })
};

// packages/aria/src/themes/base/components/Carousel/carouselIndicatorStyles.ts
var carouselIndicatorStyles = {
  root: ({ variables: v, props: p }) => {
    const { indicatorSize, activeIndicatorSize } = v;
    const size = p.active ? activeIndicatorSize : indicatorSize;
    return {
      width: size,
      height: size,
      background: "#D3D4D5",
      borderRadius: "50%"
    };
  }
};

// packages/aria/src/themes/base/components/Carousel/carouselOverlayStyles.ts
var carouselOverlayStyles = {
  root: ({ props: p, variables: v }) => {
    return {
      top: 0,
      left: 0,
      padding: v.overlayPadding,
      position: "absolute",
      width: "100%",
      height: "100%",
      ...p.allowClickThrough && {
        pointerEvents: "none"
      },
      zIndex: v.overlayZIndex,
      pointerEvents: "none",
      [`& .${flexClassName}`]: {
        [`& .${buttonClassName}`]: {
          borderRadius: "50%"
        }
      }
    };
  },
  paginationText: ({ theme }) => ({
    borderRadius: pxToRem(36),
    width: pxToRem(48),
    padding: pxToRem(4),
    margin: pxToRem(8),
    background: theme.siteVariables.colors.white["300"],
    textAlign: "center"
  })
};

// packages/aria/src/themes/base/components/Tile/MemberTile/memberTileStyles.ts
var memberTileStyles = {
  root: () => {
    return {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      textDecoration: "none",
      padding: 0
    };
  },
  imageWrapper: ({ variables: v }) => ({
    position: "relative",
    width: pxToRem(v.tileWidth),
    height: pxToRem(v.tileHeight),
    borderRadius: pxToRem(v.tileBorderRadius),
    overflow: "hidden"
  }),
  image: ({ variables: v }) => {
    return {
      display: "block",
      position: "relative",
      height: pxToRem(v.tileHeight),
      width: pxToRem(v.tileWidth)
    };
  },
  placeholder: ({ variables: v }) => {
    return {
      height: pxToRem(v.tileHeight),
      width: pxToRem(v.tileWidth)
    };
  },
  firstLabel: ({ theme, variables: v }) => {
    return {
      marginTop: pxToRem(26),
      marginBottom: 0,
      display: "block",
      color: v.titleColor,
      cursor: "pointer",
      lineHeight: theme.siteVariables.lineHeightSmall,
      "&:hover": {
        color: v.titleColorHover
      }
    };
  },
  secondLabel: ({ theme, variables: v }) => {
    return {
      marginTop: 0,
      marginBottom: 0,
      cursor: "pointer",
      lineHeight: theme.siteVariables.lineHeightSmall,
      color: v.jobTitleColor,
      "&:hover": {
        color: v.jobTitleColorHover
      }
    };
  }
};

// packages/aria/src/themes/base/components/VariantColor/variantColorStyles.ts
var variantColorStyles = {
  root: ({ variables: v, props }) => {
    return {
      position: "relative",
      borderRadius: "50%",
      border: props.highlighted ? v.borderHighlighted : v.border,
      width: typeof props.size === "number" ? pxToRem(props.size) : props.size,
      transform: "translate3d(0, 0, 0)",
      [`& .${variantColorSlotClassNames.gradient}`]: {
        borderRadius: "50%",
        display: "block",
        width: "100%",
        height: "100%",
        ...props.colorCode && {
          backgroundImage: props.colorCode
        }
      }
    };
  },
  [variantColorSlotClassNames.imageContainer]: ({ variables: v }) => ({
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    border: v.containerBorder,
    borderRadius: "50%"
  }),
  [variantColorSlotClassNames.circleContainer]: () => ({
    width: "100%",
    height: "100%",
    position: "relative",
    borderRadius: "50%",
    overflow: "hidden"
  }),
  [variantColorSlotClassNames.swatchIcon]: ({ theme }) => ({
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    "& svg": {
      width: "30%",
      height: "30%",
      fill: hex2rgba(theme.siteVariables.colors.charcoal["250"], 0.5),
      position: "absolute",
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%)"
    }
  }),
  [variantColorSlotClassNames.image]: () => ({
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    "& img": {
      borderRadius: "50%"
    }
  }),
  [variantColorSlotClassNames.filler]: () => ({
    paddingBottom: "100%"
  }),
  [variantColorSlotClassNames.svg]: () => ({
    display: "block",
    width: "100%",
    height: "100%"
  }),
  [variantColorSlotClassNames.ring]: ({ variables: v }) => ({
    position: "absolute",
    border: `solid ${pxToRem(1)} ${v.ringColor}`,
    // Using rem for smooth border randering
    boxShadow: `0 0 ${pxToRem(1)} 0 ${v.ringColor} inset, 0 0 ${pxToRem(
      1
    )} 0px ${v.ringColor}`,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    borderRadius: "50%"
  }),
  [variantColorSlotClassNames.soloCircle]: ({ props: { colorCode } }) => ({
    backgroundColor: colorCode ? colorCode : "#E5E5E5",
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    overflow: "hidden"
  })
};

// packages/aria/src/themes/base/components/Tile/ArchizeenTile/archizeenTileStyles.ts
import { svgIconClassName as svgIconClassName12 } from "@archipro-design/icons";
var archizeenTileStyles = {
  // @ts-expect-error Project getting stale, don't want to fix whats not broken
  root: ({ variables: v, theme, props: p }) => {
    return {
      display: "block",
      // Incase "as" type is <a/> which is inline
      position: "relative",
      textDecoration: "none",
      width: v.width,
      padding: v.tilePadding,
      cursor: "pointer",
      [`& > .${tileImageClassName}`]: {
        marginBottom: v[`${p.size}TitleGap`]
      },
      [`& > .${buttonClassName}`]: {
        display: "block",
        ":hover": {
          textDecoration: "underline"
        }
      },
      [`& .${archizeenTileSlotClassNames.subtitle}`]: {
        marginBottom: pxToRem(9),
        // Only change font for mobile
        [theme.screen.max.laptop]: {
          ...theme.siteVariables.textStyles.Heading.Medium65,
          textTransform: "uppercase"
        }
      },
      [`& .${SubheaderClassName}`]: {
        color: v.color,
        fontSize: pxToRem(28),
        lineHeight: theme.siteVariables.lineHeightSmaller,
        letterSpacing: theme.siteVariables.letterSpacingNormal,
        ...theme.siteVariables.textStyles.Article.Roman55,
        marginBottom: v[`${p.size}TitleGap`],
        textUnderlineOffset: pxToRem(2)
      },
      [`& .${captionClassName}`]: {
        color: v.color
      },
      [`& .${labelClassName}`]: {
        color: v.color
      },
      ":hover": {
        [`& .${tileOverlayClassName}`]: {
          opacity: 1
        }
      },
      [`& .${archizeenTileSlotClassNames.tag}`]: {
        textAlign: "right",
        marginTop: pxToRem(-27),
        marginBottom: pxToRem(12),
        ...theme.siteVariables.textStyles.Heading.Roman55,
        fontSize: pxToRem(15),
        lineHeight: pxToRem(15),
        letterSpacing: theme.siteVariables.letterSpacingMedium,
        color: theme.siteVariables.colors.primitive.black
      }
    };
  },
  ellipse: ({ variables: v }) => {
    return {
      height: pxToRem(2),
      width: pxToRem(2),
      backgroundColor: v.color,
      borderRadius: "50%",
      display: "inline-block"
    };
  },
  variant01: ({ variables: v, props: p }) => {
    return {
      [`&.${archizeenTileClassName}`]: {
        padding: v.tilePadding,
        width: `calc(${p.imageHeight} + ${v.tilePadding} * 2)`,
        ...(p.size === "small" || p.size === "xsmall") && {
          padding: 0,
          width: p.imageHeight,
          [`& > .${flexClassName}`]: {
            padding: pxArrayToRem([16, 18, 0])
          },
          [`& .${SubheaderClassName}`]: {
            marginBottom: pxToRem(14)
          },
          [`& > .${tileImageClassName}`]: {
            marginBottom: 0
          },
          ...mobileFavoriteOverlay,
          ":hover": {
            backgroundColor: "transparent"
          }
        },
        ...p.size === "medium" && {
          [`& .${SubheaderClassName}`]: {
            fontSize: pxToRem(20)
          }
        },
        ...p.size === "large" && {
          [`& .${SubheaderClassName}`]: {
            fontSize: pxToRem(28)
          }
        },
        ...p.size === "dynamic" && {
          ...dynamicSizingStyle(
            v.dynamicSizingWidth,
            v.dynamicSizingGapWidth,
            1 / 1
          ),
          [`& .${SubheaderClassName}`]: {
            fontSize: pxToRem(20)
          }
        }
      }
    };
  },
  variant02: ({ variables: v, theme, props: p }) => {
    return {
      [`&.${archizeenTileClassName}`]: {
        ...p.size === "small" && {
          padding: 0,
          [`& > .${flexClassName}`]: {
            padding: 0
          },
          [`& > .${tileImageClassName}`]: {
            marginBottom: pxToRem(8)
          },
          ...mobileFavoriteOverlay,
          [`& .${SubheaderClassName}`]: {
            marginBottom: pxToRem(12),
            ...mapSubHeadingVariantToFontStyles(
              { variant: "05", maximumLines: 3 },
              theme
            )
          },
          [`& .${captionClassName}`]: {
            ...mapCaptionVariantToFontStyles(
              { variant: "06" },
              theme
            )
          },
          ":hover": {
            backgroundColor: "transparent"
          }
        },
        ...p.size === "medium" && {
          [`& .${SubheaderClassName}`]: {
            fontSize: pxToRem(20)
          }
        },
        ...p.size === "large" && {
          [`& .${SubheaderClassName}`]: {
            fontSize: pxToRem(28)
          }
        },
        ...p.size === "dynamic" && {
          ...dynamicSizingStyle(
            v.dynamicSizingWidth,
            v.dynamicSizingGapWidth,
            400 / 340
          ),
          [`& .${SubheaderClassName}`]: {
            fontSize: pxToRem(20)
          }
        }
      }
    };
  },
  variant03: ({ variables: v, theme, props: p }) => {
    return {
      [`&.${archizeenTileClassName}`]: {
        ...p.size === "small" && {
          padding: 0,
          [`& > .${flexClassName}`]: {
            padding: 0
          },
          [`& > .${tileImageClassName}`]: {
            marginBottom: pxToRem(8)
          },
          ...mobileFavoriteOverlay,
          [`& .${SubheaderClassName}`]: {
            marginBottom: pxToRem(12),
            ...mapSubHeadingVariantToFontStyles(
              { variant: "05", maximumLines: 3 },
              theme
            )
          },
          [`& .${captionClassName}`]: {
            ...mapCaptionVariantToFontStyles(
              { variant: "06" },
              theme
            )
          },
          ":hover": {
            backgroundColor: "transparent"
          }
        },
        ...p.size === "medium" && {
          [`& .${SubheaderClassName}`]: {
            fontSize: pxToRem(22)
          }
        },
        ...p.size === "dynamic" && {
          ...dynamicSizingStyle(
            v.dynamicSizingWidth,
            v.dynamicSizingGapWidth,
            245 / 378
          ),
          [`& .${SubheaderClassName}`]: {
            fontSize: pxToRem(22)
          }
        }
      }
    };
  },
  variant04: ({ theme }) => {
    return {
      [`&.${archizeenTileClassName}`]: {
        display: "grid",
        gridTemplateColumns: `[image] ${pxToRem(120)} [info] 1fr`,
        gap: pxToRem(8),
        padding: pxToRem(18),
        [`& > .${tileImageClassName}`]: {
          marginBottom: 0
        },
        [`& > .${flexClassName}`]: {
          justifyContent: "space-between"
        },
        [`& .${captionClassName}`]: {
          ...mapCaptionVariantToFontStyles({ variant: "06" }, theme)
        },
        [`& .${SubheaderClassName}`]: {
          ...mapSubHeadingVariantToFontStyles(
            { maximumLines: 3 },
            theme
          ),
          fontSize: pxToRem(18),
          lineHeight: theme.siteVariables.lineHeightSmaller,
          letterSpacing: theme.siteVariables.letterSpacingSmall,
          marginBottom: 0,
          marginTop: pxToRem(8)
        }
      }
    };
  },
  variant06: ({ theme, props: p }) => {
    return {
      [`&.${archizeenTileClassName}`]: {
        ...p.size === "small" && {
          padding: 0,
          borderBottom: "unset",
          [`& > .${tileImageClassName}`]: {
            marginBottom: pxToRem(11.25)
          },
          [`& > .${flexClassName}`]: {
            padding: pxArrayToRem([0, 12]),
            [`& > .${flexClassName}`]: {
              margin: 0
            }
          },
          [`& .${SubheaderClassName}`]: {
            ...mapSubHeadingVariantToFontStyles(
              { maximumLines: 3 },
              theme
            ),
            fontSize: pxToRem(13),
            lineHeight: theme.siteVariables.lineHeightSmaller,
            letterSpacing: theme.siteVariables.letterSpacingSmall,
            marginBottom: pxToRem(4)
          },
          [`& .${captionClassName}`]: {
            ...mapCaptionVariantToFontStyles(
              { variant: "06" },
              theme
            )
          }
        },
        ...p.size === "large" && {
          padding: 0,
          borderBottom: "unset",
          [`& > .${tileImageClassName}`]: {
            marginBottom: pxToRem(16)
          },
          [`& > .${flexClassName}`]: {
            padding: pxArrayToRem([0, 18]),
            [`& > .${flexClassName}`]: {
              margin: 0
            }
          },
          [`& .${SubheaderClassName}`]: {
            ...mapSubHeadingVariantToFontStyles(
              { maximumLines: 3 },
              theme
            ),
            fontSize: pxToRem(13),
            lineHeight: theme.siteVariables.lineHeightSmaller,
            letterSpacing: theme.siteVariables.letterSpacingSmall,
            marginBottom: pxToRem(16)
          },
          [`& .${captionClassName}`]: {
            ...mapCaptionVariantToFontStyles(
              { variant: "06" },
              theme
            )
          }
        }
      }
    };
  },
  variant07: ({ props: p }) => {
    return {
      [`&.${archizeenTileClassName}`]: {
        ...p.size === "medium" && {
          [`& .${SubheaderClassName}`]: {
            fontSize: pxToRem(22)
          }
        },
        ...p.size === "large" && {
          [`& .${SubheaderClassName}`]: {
            fontSize: pxToRem(28)
          },
          [`& > .${tileImageClassName}`]: {
            width: pxToRem(896)
          }
        }
      }
    };
  },
  variant08: ({ variables: v, props: p }) => {
    return {
      [`&.${archizeenTileClassName}`]: {
        ...p.size === "medium" && {
          [`& .${SubheaderClassName}`]: {
            fontSize: pxToRem(20)
          }
        },
        ...p.size === "large" && {
          [`& .${SubheaderClassName}`]: {
            fontSize: pxToRem(20)
          }
        },
        ...p.size === "dynamic" && {
          ...dynamicSizingStyle(
            v.dynamicSizingWidth,
            v.dynamicSizingGapWidth,
            310 / 371
          ),
          [`& .${SubheaderClassName}`]: {
            fontSize: pxToRem(20)
          }
        }
      }
    };
  },
  videoContainer: ({ theme, props: p }) => {
    return {
      position: "relative",
      width: "100%",
      paddingTop: "56.25%",
      marginBottom: p.size === "small" ? pxToRem(12) : pxToRem(16),
      [theme.screen.laptop]: {
        marginBottom: pxToRem(32)
      },
      [`& .${videoPlayerClassName}`]: {
        position: "absolute",
        top: "0",
        left: "0",
        width: "100%",
        height: "100%",
        [`& .${boxClassName}`]: {
          position: "absolute"
        }
      }
    };
  }
};
var mobileFavoriteOverlay = {
  [`& .${tileOverlayClassName}`]: {
    opacity: 1,
    padding: pxToRem(8),
    [`& > .${flexClassName}`]: {
      fontSize: 0
    },
    [`& .${buttonClassName}`]: {
      padding: pxToRem(8),
      [`& .${svgIconClassName12}`]: {
        width: pxToRem(16),
        height: pxToRem(16)
      }
    }
  }
};

// packages/aria/src/themes/base/components/MegaMenu/FeaturedTile/featuredTileStyles.ts
var featuredTileStyles = {
  root: ({ variables: v }) => {
    return {
      height: "fit-content",
      "> *:not(:first-child)": {
        marginTop: v.titleTileGap
      }
    };
  },
  title: ({ variables: v }) => {
    return {
      paddingLeft: v.titleLeftPadding,
      minHeight: v.titleMinHeight
    };
  }
};

// packages/aria/src/themes/base/components/Input/numberInputStyles.ts
var numberInputStyles = {
  root: () => {
    return {};
  }
};

// packages/aria/src/themes/base/components/DropdownSort/dropdownSortStyles.ts
import { svgIconClassName as svgIconClassName13 } from "@archipro-design/icons";
import {
  dropdownItemClassName as dropdownItemClassName2,
  dropdownItemSlotClassNames as dropdownItemSlotClassNames2
} from "@fluentui/react-northstar";
import {
  dropdownSlotClassNames as dropdownSlotClassNames4,
  buttonContentClassName
} from "@fluentui/react-northstar";
var dropdownSortStyles = {
  root: ({ variables: v, theme }) => ({
    fontSize: v.primaryDropdownFontSize,
    [`& .${dropdownSlotClassNames4.container}`]: {
      width: "fit-content",
      "&:hover": {
        backgroundColor: v.placeholderHoverColor
      }
    },
    [`& .${dropdownSlotClassNames4.triggerButton}`]: {
      padding: v.triggerButtonPadding,
      fontSize: v.primaryDropdownFontSize,
      lineHeight: v.primaryDropdownLineHeight,
      backgroundColor: v.triggerButtonBGcolor,
      [`& .${svgIconClassName13}`]: {
        width: v.checkIconWidth,
        height: v.checkIconHeight,
        marginRight: v.iconLabelGap,
        [`& svg`]: {
          fill: v.triggerButtonIconColor,
          [`& path`]: {
            fill: v.triggerButtonIconColor
          },
          [`& stroke`]: {
            fill: v.triggerButtonIconColor
          }
        }
      }
    },
    [`& .${dropdownSlotClassNames4.triggerButton} .${buttonContentClassName}`]: {
      display: "contents",
      color: v.triggerButtonColor,
      letterSpacing: v.dropdownHeaderLetterSpacing,
      "&:hover": {
        [`& .${dropdownSortSlotClassNames.currentItem}`]: {
          textDecoration: "underline"
        }
      }
    },
    [`& .${dropdownItemClassName2}`]: {
      padding: v.paddingItemSlot
    },
    [`& .${dropdownItemSlotClassNames2.header}`]: {
      fontSize: v.dropdownItemFontSize,
      lineHeight: v.itemSlotLineHeight,
      letterSpacing: v.dropdownItemLetterSpacing,
      display: "grid",
      gridTemplateColumns: `1fr ${pxToRem(24)}`,
      columnGap: pxToRem(8),
      ...theme.siteVariables.textStyles.Text.Roman55
    },
    [`& .${dropdownSlotClassNames4.itemsList}`]: {
      border: 0,
      width: "unset",
      maxHeight: v.listMaxHeight,
      ...getScrollbarStyle(theme, "0"),
      ":hover": {
        cursor: "pointer"
      },
      '&[aria-hidden="false"]': {
        padding: 0,
        outlineWidth: v.outLineWidth,
        boxShadow: v.listDropShadow
      },
      [`& .${dropdownSlotClassNames4.item}`]: {
        border: "none",
        width: v.dropdownItemWidth
      },
      [`& .${dropdownSlotClassNames4.selectedItems}`]: {
        padding: 0,
        height: "fit-content",
        [`& .${buttonClassName}`]: {
          height: "fit-content"
        }
      }
    },
    [`& .${dropdownSortSlotClassNames.currentItem}`]: {
      display: "inline-block"
    }
  })
};

// packages/aria/src/themes/base/components/Image/imageOverlayStyles.ts
import { svgIconClassName as svgIconClassName14 } from "@archipro-design/icons";
var imageOverlayStyles = {
  root: ({ variables: v, theme, props: p }) => {
    return {
      width: "100%",
      height: "100%",
      position: "absolute",
      top: 0,
      left: 0,
      cursor: "pointer",
      opacity: `${p.alwaysVisible ? 1 : 0}`,
      transition: `all ${v.fadeSpeed} linear`,
      background: v.backgroundColor,
      [theme.screen.laptop]: {
        "&:hover": {
          opacity: 1,
          "& svg": {
            transform: `translate(-50%, -50%) scale(1.1)`
          }
        }
      },
      [`& .${svgIconClassName14}`]: {
        "& svg": {
          fill: v.iconColor,
          width: pxToRem(v.iconSizeMobile),
          height: pxToRem(v.iconSizeMobile),
          transform: `translate(-50%, -50%) scale(1)`,
          transition: `transform 150ms linear`,
          top: "50%",
          left: "50%",
          position: "absolute",
          [theme.screen.laptop]: {
            width: pxToRem(v.iconSizeDesktop),
            height: pxToRem(v.iconSizeDesktop)
          }
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/Divider/dividerStyles.ts
var dividerStyles = {
  root: ({ variables: v, props }) => {
    const { fitted, vertical, hasContent, size, color } = props;
    const dividerColor = mapDividerVariants(color, v);
    return {
      color: dividerColor,
      display: "flex",
      alignItems: "center",
      ...!fitted && {
        padding: vertical ? `0 ${v.dividerPadding}` : `${v.dividerPadding} 0`
      },
      ...vertical && { height: "100%" },
      ...hasContent ? {
        textAlign: "center",
        fontSize: v.textFontSize,
        lineHeight: v.textLineHeight,
        "::before": {
          ...beforeAndAfter(size, dividerColor, props)
        },
        "::after": {
          ...beforeAndAfter(size, dividerColor, props)
        }
      } : {
        "::before": {
          ...beforeAndAfter(size, dividerColor, props)
        }
      },
      [`& .${dividerContentClassName}`]: {
        marginLeft: v.contentMargin,
        marginRight: v.contentMargin
      }
    };
  }
};
var beforeAndAfter = (size, color, props) => ({
  content: '""',
  flex: 1,
  ...props.vertical ? { width: `${size}px`, height: "100%" } : { height: `${size}px` },
  background: color
});
var mapDividerVariants = (color, v) => {
  switch (color) {
    case "secondary":
      return v.dividerSecondaryColor;
    case "contrast":
      return v.dividerContrastColor;
    case "primary":
    default:
      return v.dividerPrimaryColor;
  }
};

// packages/aria/src/themes/base/components/Tooltip/messageTooltipContentStyles.ts
import { svgIconClassName as svgIconClassName15 } from "@fluentui/react-northstar";

// packages/aria/src/themes/base/components/Tooltip/utils.ts
var normalizeColorString = (color) => {
  switch (color) {
    case "black":
    case "white":
      return color.charAt(0).toUpperCase() + color.slice(1);
    case "grey-black":
      return "GreyBlack";
    default:
      return "Black";
  }
};
var getColor = (variables, color) => {
  return variables[`variant${normalizeColorString(color)}TextColor`];
};
var getBackgroundColor = (variables, color) => {
  return variables[`variant${normalizeColorString(color)}BackgroundColor`];
};
var getBorderColor = (variables, color) => {
  return variables[`variant${normalizeColorString(color)}BackgroundColor`];
};

// packages/aria/src/themes/base/components/Tooltip/messageTooltipContentStyles.ts
var messageTooltipContentStyles = {
  root: ({ variables: v, props: p }) => {
    const backgroundColor = getBackgroundColor(v, p.color);
    const color = getColor(v, p.color);
    return {
      backgroundColor,
      color,
      '&[aria-hidden="true"]': {
        display: "none"
      },
      maxWidth: v.maxWidth,
      padding: v[`padding${p.padding}`],
      fontSize: v[`size${p.size}`],
      lineHeight: v.lineHeight,
      flexWrap: "nowrap",
      display: "flex",
      alignItems: "center",
      zIndex: 160
      // match z-index of tooltip in rocco
    };
  },
  pointer: ({ props: p, variables: v, rtl }) => {
    const backgroundColor = getBackgroundColor(v, p.color);
    const borderColor = getBorderColor(v, p.color);
    return getPointerStyles({
      backgroundColor,
      borderColor,
      borderSize: v.borderSize,
      gap: v.pointerGap,
      padding: v.pointerMargin,
      height: v.pointerHeight,
      width: v.pointerWidth,
      placement: p.basePlacement,
      rtl
    });
  },
  [messageTooltipSlotClassNames.icon]: ({ variables: v, props: p }) => {
    const color = getColor(v, p.color);
    return {
      ["& ." + svgIconClassName15]: {
        width: "1em",
        height: "1em",
        lineHeight: v[`size${p.size}`],
        color,
        fill: color,
        ...p.iconPosition === "before" ? { marginRight: v.iconMargin } : { marginLeft: v.iconMargin }
      }
    };
  }
};

// packages/aria/src/themes/base/components/MegaMenu/NavigationList/navigationListSkeletonStyles.ts
var navigationListSkeletonStyles = {
  root: ({ props: p, variables: v }) => {
    return {
      width: v.listWidth,
      "> *:first-child": {
        marginBottom: v[`${p.size}SkeletonTitleItemGap`]
      },
      [`& .${skeletonLineClassName}`]: {
        backgroundColor: v.skeletonColor
      },
      [`> .${navigationListSkeletonClassNames.button}`]: {
        marginTop: v[`${p.size}SkeletonButtonItemGap`]
      }
    };
  },
  skeletonItemContainer: ({ props: p, variables: v }) => {
    return {
      "> *:not(:first-child)": {
        marginTop: v[`${p.size}SkeletonItemGap`]
      }
    };
  }
};

// packages/aria/src/themes/base/components/MegaMenu/FeaturedTile/featuredTileSkeletonStyles.ts
var featuredTileSkeletonStyles = {
  root: ({ variables: v }) => {
    return {
      [`& .${skeletonLineClassName}`]: {
        backgroundColor: v.skeletonColor
      },
      [`> .${featuredTileSkeletonClassNames.tile}`]: {
        marginTop: v.skeletonTitleTileGap
      },
      [`> .${featuredTileSkeletonClassNames.header}`]: {
        marginTop: v.skeletonTileImageHeaderGap
      },
      [`> .${featuredTileSkeletonClassNames.subject}`]: {
        marginTop: v.skeletonTileHeaderSubjectGap
      }
    };
  }
};

// packages/aria/src/themes/base/components/Pagination/paginationStyles.ts
var paginationStyles = {
  root: ({ variables: v, theme }) => {
    return {
      [`& .${paginationSlotClassNames.paginationMetaData}`]: {
        height: v.listHeightMobile,
        color: v.metaColor,
        fontSize: v.metaFontSizeMobile,
        [theme.screen.laptop]: {
          fontSize: v.metaFontSize,
          height: pxToRem(32)
        }
      },
      [`& .${paginationSlotClassNames.divider}`]: {
        ...v.metaColor && {
          "::before": {
            backgroundColor: v.metaColor
          }
        }
      },
      [`& .${paginationSlotClassNames.list}`]: {
        gap: v.gapMobile,
        height: v.listHeightMobile,
        margin: 0,
        padding: 0,
        marginBottom: pxToRem(8),
        [theme.screen.laptop]: {
          gap: v.gap,
          marginBottom: pxToRem(32),
          height: v.listHeight
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/Pagination/paginationItemStyles.ts
import { svgIconClassName as svgIconClassName16 } from "@archipro-design/icons";
var paginationItemStyles = {
  root: ({ variables: v, theme }) => {
    return {
      listStyle: "none",
      lineHeight: 0,
      [`& .${paginationItemEllipsisClassName}`]: {
        [`& .${svgIconClassName16}`]: {
          fill: v.paginationEllipsisColor,
          width: pxToRem(16),
          height: pxToRem(16),
          [theme.screen.tablet]: {
            width: pxToRem(24),
            height: pxToRem(24)
          }
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/Pagination/paginationItemContentStyles.ts
import { svgIconClassName as svgIconClassName17 } from "@archipro-design/icons";
var paginationItemContentStyles = {
  root: ({ variables: v, props: p, theme }) => {
    return {
      height: pxToRem(42),
      [theme.screen.laptop]: {
        height: pxToRem(50)
      },
      [`&.${paginationItemPageClassName}`]: {
        width: pxToRem(42),
        [theme.screen.laptop]: {
          width: pxToRem(50)
        },
        paddingLeft: pxToRem(19),
        paddingRight: pxToRem(19),
        paddingTop: pxToRem(16),
        paddingBottom: pxToRem(16),
        color: v.pageItemColor,
        [`& .${labelClassName}`]: {
          fontSize: pxToRem(14),
          [theme.screen.tablet]: {
            fontSize: pxToRem(18)
          }
        },
        backgroundColor: v.pageItemBackgroundColor,
        ":hover": {
          color: v.pageItemHoverColor,
          backgroundColor: v.pageItemHoverBackgroundColor
        },
        ...p.selected && {
          color: v.pageItemSelectedColor,
          backgroundColor: v.pageItemSelectedBackgroundColor,
          ":hover": {
            color: v.pageItemSelectedHoverColor,
            backgroundColor: v.pageItemSelectedHoverBackgroundColor
          }
        }
      },
      [`&.${paginationItemPrevNextClassName}`]: {
        ...p.type === "next" && {
          padding: pxArrayToRem([12, 0, 12, 4]),
          [theme.screen.tablet]: {
            padding: pxArrayToRem([15, 4, 15, 12])
          },
          color: v.pageItemColor,
          backgroundColor: v.pageItemBackgroundColor,
          ":hover": {
            color: v.pageItemHoverColor,
            backgroundColor: v.pageItemHoverBackgroundColor
          }
        },
        ...p.disabled && {
          opacity: v.pageItemDisabledOpacity
        },
        ...p.type === "previous" && {
          padding: pxArrayToRem([12, 4, 12, 0]),
          [theme.screen.tablet]: {
            padding: pxArrayToRem([15, 12, 15, 4])
          },
          color: v.pageItemColor,
          backgroundColor: v.pageItemBackgroundColor,
          ":hover": {
            color: v.pageItemHoverColor,
            backgroundColor: v.pageItemHoverBackgroundColor
          }
        },
        [`& .${labelClassName}`]: {
          fontSize: pxToRem(14),
          [theme.screen.tablet]: {
            fontSize: pxToRem(18)
          }
        },
        [`& .${svgIconClassName17}`]: {
          width: pxToRem(16),
          height: pxToRem(16),
          [theme.screen.tablet]: {
            width: pxToRem(20),
            height: pxToRem(20)
          },
          [`& svg`]: {
            fill: v.pageItemColor
          },
          ":hover": {
            [`& svg`]: {
              fill: v.pageItemHoverColor
            }
          }
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/Pagination/paginationItemCounterStyles.ts
var paginationItemCounterStyles = {
  root: ({ variables: v }) => {
    return {
      color: v.color,
      [`&.${captionClassName}`]: {
        fontSize: "inherit"
      }
    };
  }
};

// packages/aria/src/themes/base/components/Pagination/paginationItemsPerPageStyles.ts
import { svgIconClassName as svgIconClassName18 } from "@archipro-design/icons";
import { dropdownItemSlotClassNames as dropdownItemSlotClassNames3 } from "@fluentui/react-northstar";
var paginationItemsPerPageStyles = {
  root: ({ variables: v, theme }) => {
    return {
      fontSize: v.fontSize,
      color: v.color,
      [`& .${paginationItemsPerPageSlotClassNames.caption}`]: {
        color: v.color,
        fontSize: "inherit",
        lineHeight: v.lineHeight,
        marginRight: pxToRem(6)
      },
      [`& .${paginationItemsPerPageSlotClassNames.dropdown}`]: {
        fill: v.dropdownColor,
        borderBottom: `1px solid ${v.dropdownColor}`,
        ":hover": {
          borderBottom: `1px solid ${v.dropdownColor}`,
          backgroundColor: theme.siteVariables.colors.transparency.black["6%"]
        }
      },
      [`& .${dropdownSlotClassNames.container}`]: {
        cursor: "pointer",
        minWidth: pxToRem(52),
        borderRadius: 0
      },
      [`& .${dropdownSlotClassNames.itemsList}`]: {
        padding: 0,
        border: 0
      },
      [`& .${dropdownSlotClassNames.item}`]: {
        minWidth: pxToRem(80),
        padding: pxToRem(8),
        [`& .${dropdownItemSlotClassNames3.header}`]: {
          fontSize: v.fontSize,
          lineHeight: v.lineHeight
        },
        [`& .${svgIconClassName18}`]: {
          height: pxToRem(16),
          width: pxToRem(16)
        }
      },
      [`& .${dropdownSlotClassNames.triggerButton}`]: {
        fontSize: pxToRem(16),
        paddingLeft: pxToRem(8),
        color: v.dropdownColor,
        ":hover": {
          color: v.dropdownColor
        },
        ":focus": {
          color: v.dropdownColor
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/Dropdown/dropdownStyles.ts
var transparentColorStyle = {
  backgroundColor: "transparent",
  borderColor: "transparent",
  borderBottomColor: "transparent"
};
var transparentColorStyleObj = {
  ...transparentColorStyle,
  ":hover": transparentColorStyle,
  ":active": transparentColorStyle,
  ":focus": {
    ...transparentColorStyle,
    ":active": transparentColorStyle
  }
};
var getWidth = (p, v) => {
  if (p.fluid) {
    return "100%";
  }
  if (p.inline) {
    return "initial";
  }
  return v.width;
};
var dropdownStyles = {
  root: ({ props: p }) => ({
    ...p.inline && { display: "inline-flex" }
  }),
  clearIndicator: ({ variables: v, theme: { siteVariables } }) => ({
    alignItems: "center",
    alignSelf: "center",
    display: "flex",
    justifyContent: "center",
    cursor: "pointer",
    userSelect: "none",
    margin: 0,
    position: "absolute",
    right: pxToRem(6),
    padding: pxToRem(2),
    color: v.color,
    ...getBorderFocusStyles({ variables: siteVariables })
  }),
  variant01: ({ theme }) => {
    return {
      width: pxToRem(140),
      maxWidth: pxToRem(140),
      [`& .${dropdownSlotClassNames.container}`]: {
        background: theme.siteVariables.colors.white["100A"],
        width: pxToRem(140),
        borderRadius: pxArrayToRem([3, 0, 0, 3]),
        marginLeft: pxToRem(2),
        height: pxToRem(40)
      },
      [`& .${dropdownSlotClassNames.selectedItems}`]: {
        height: pxToRem(40)
      },
      [`& .${dropdownSlotClassNames.triggerButton}`]: {
        fontSize: theme.siteVariables.fontSizes["label04"]
      },
      [`& .${dropdownSlotClassNames.itemsList}`]: {
        width: pxToRem(140)
      },
      [`& .${dropdownSlotClassNames.item}`]: {
        fontSize: pxToRem(14),
        ["& .ui-dropdown__item__header"]: {
          fontSize: pxToRem(14)
        }
      }
    };
  },
  container: ({
    props: p,
    variables: v,
    theme: { siteVariables }
  }) => ({
    display: "flex",
    flexWrap: "wrap",
    position: "relative",
    borderStyle: "solid",
    borderColor: v.borderColor,
    outline: 0,
    width: getWidth(p, v),
    borderWidth: p.search ? `0 0 ${v.searchBorderBottomWidth} 0` : v.borderWidth,
    color: v.color,
    backgroundColor: v.backgroundColor,
    borderRadius: v.containerBorderRadius,
    ...p.open && p.position === "above" && {
      borderRadius: v.openAboveContainerBorderRadius
    },
    ...p.open && p.position === "below" && {
      borderRadius: v.openBelowContainerBorderRadius
    },
    ":hover": {
      backgroundColor: v.backgroundColorHover,
      borderColor: v.borderColorHover,
      ...p.open && {
        borderColor: v.openBorderColorHover
      }
    },
    ...p.error && {
      border: `${pxToRem(1)} solid ${v.borderError}`,
      ":hover": { border: `${pxToRem(1)} solid ${v.borderError}` }
    },
    ":active": {
      backgroundColor: v.backgroundColor
    },
    ":focus-within": {
      // when dropdown's selected items are focused
      // keep the focus border style
      borderBottomColor: v.borderColorFocus
    },
    ...p.focused && {
      backgroundColor: v.backgroundColor,
      ...p.search && {
        borderBottomColor: v.borderColorFocus
      },
      ...!p.search && !p.open && p.isFromKeyboard && getBorderFocusStyles({ variables: siteVariables })[":focus-visible"]
    },
    ...p.inline && {
      ...transparentColorStyleObj,
      alignItems: "center"
    },
    ...p.inverted && {
      backgroundColor: v.invertedBackgroundColor,
      ":hover": {
        backgroundColor: v.invertedBackgroundColorHover
      },
      ":active": {
        backgroundColor: v.invertedBackgroundColorHover
      },
      ":focus": {
        backgroundColor: v.invertedBackgroundColorHover
      }
    },
    ...p.disabled && {
      backgroundColor: siteVariables.colors.grey[200],
      borderColor: "none",
      userSelect: "none",
      ":hover": {
        backgroundColor: siteVariables.colors.transparency.black["6%"]
      },
      ":active": {
        backgroundColor: siteVariables.colors.transparency.black["6%"]
      }
    },
    [`& .${dropdownSlotClassNames.triggerButton}`]: {
      ...p.disabled && {
        color: siteVariables.colors.grey[200]
      },
      // Focus style is handled by dropdown. Dont need focus style on base button
      ":focus-visible": {
        outline: "none",
        boxShadow: "none"
      }
    }
  }),
  selectedItems: ({ props: p, variables: v }) => ({
    display: "flex",
    flexWrap: "wrap",
    overflowY: "auto",
    overflowX: "hidden",
    maxHeight: v.selectedItemsMaxHeight,
    width: "100%",
    ...p.hasToggleIndicator && { paddingRight: v.toggleIndicatorSize },
    ...p.multiple && p.hasItemsSelected && {
      paddingTop: pxToRem(1),
      paddingBottom: pxToRem(4)
    }
  }),
  triggerButton: ({ props: p, variables: v }) => {
    return {
      fontSize: pxToRem(14),
      overflow: "hidden",
      boxShadow: "none",
      minHeight: pxToRem(32),
      ...transparentColorStyleObj,
      margin: "0",
      justifyContent: "left",
      padding: v.comboboxPaddingButton,
      ...p.multiple && {
        minWidth: 0,
        flex: 1,
        ...p.hasItemsSelected && {
          position: "absolute",
          top: 0,
          right: 0,
          left: 0,
          bottom: 0,
          height: "100%"
        }
      },
      ...transparentColorStyleObj,
      ":focus": {
        color: v.color,
        ...transparentColorStyleObj
      },
      ":focus-visible": {
        color: v.color,
        ...transparentColorStyle,
        ":after": {
          borderColor: "transparent",
          borderRightWidth: 0
        },
        ":before": {
          borderColor: "transparent",
          borderRightWidth: 0
        }
      },
      ":active": {
        color: v.color,
        // required for HC theme
        ...transparentColorStyle,
        animationName: "unset",
        animationDuration: "unset"
      },
      ":hover": {
        ...transparentColorStyle,
        color: v.color
        // required for HC theme
      },
      ...p.inline && {
        paddingLeft: 0,
        paddingRight: 0,
        width: "initial"
      }
    };
  },
  list: ({ props: p, variables: v }) => ({
    outline: 0,
    borderStyle: "solid",
    borderWidth: p.open ? v.listBorderWidth : "0px",
    borderColor: v.listBorderColor,
    zIndex: v.overlayZIndex,
    maxHeight: v.listMaxHeight,
    overflowY: "auto",
    width: getWidth(p, v),
    background: v.listBackgroundColor,
    ...p.position === "above" && {
      borderRadius: v.aboveListBorderRadius
    },
    ...p.position === "below" && {
      borderRadius: v.belowListBorderRadius
    },
    ...p.open && {
      boxShadow: v.listBoxShadow,
      padding: v.listPadding
    }
  }),
  toggleIndicator: ({ props: p, variables: v }) => ({
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    alignSelf: "center",
    cursor: "pointer",
    ...p.disabled && {
      cursor: "default"
    },
    userSelect: "none",
    margin: 0,
    position: "absolute",
    right: pxToRem(8),
    ...p.multiple && p.hasItemsSelected && {
      top: pxToRem(8)
    },
    color: v.color,
    ...p.disabled && { color: v.disabledColor }
  })
};

// packages/aria/src/themes/base/components/Dropdown/dropdownItemStyles.ts
import { dropdownItemSlotClassNames as dropdownItemSlotClassNames4, pxToRem as pxToRem2 } from "@fluentui/react-northstar";
var dropdownItemStyles = {
  root: ({
    props: p,
    variables: v,
    theme: { siteVariables }
  }) => ({
    display: "flex",
    alignItems: "center",
    minHeight: 0,
    padding: `${pxToRem2(4)} ${pxToRem2(11)}`,
    whiteSpace: "nowrap",
    border: `${v.listItemFocusBorderWidth} solid transparent`,
    backgroundColor: v.listItemBackgroundColor,
    ...p.selected && {
      fontWeight: v.listItemSelectedFontWeight,
      color: v.listItemSelectedColor
    },
    position: "relative",
    ...p.active && {
      ...p.isFromKeyboard && getBorderFocusStyles({
        variables: siteVariables,
        borderRadius: 0
      })[":focus-visible"],
      ...!p.isFromKeyboard && {
        color: v.listItemColorHover,
        backgroundColor: v.listItemBackgroundColorHover,
        ...p.hasHeader && {
          [`& .${dropdownItemSlotClassNames4.header}`]: {
            color: v.listItemColorHover
          }
        },
        ...p.hasContent && {
          [`& .${dropdownItemSlotClassNames4.content}`]: {
            color: v.listItemColorHover
          }
        }
      }
    }
  }),
  image: () => ({
    margin: `${pxToRem2(3)} ${pxToRem2(12)} ${pxToRem2(3)} ${pxToRem2(4)}`
  }),
  header: ({ props: p, variables: v }) => ({
    flexGrow: 1,
    lineHeight: v.listItemHeaderLineHeight,
    fontSize: v.listItemHeaderFontSize,
    // if the item doesn't have content - i.e. it is header only - then it should use the content color
    color: v.listItemContentColor,
    ...p.hasContent && {
      // if there is content it needs to be "tightened up" to the header
      marginBottom: pxToRem2(-1),
      color: v.listItemHeaderColor
    },
    ...p.selected && {
      fontWeight: v.listItemSelectedFontWeight,
      color: v.listItemSelectedColor
    },
    whiteSpace: "normal"
  }),
  content: ({ variables: v }) => ({
    flexGrow: 1,
    lineHeight: v.listItemContentLineHeight,
    fontSize: v.listItemContentFontSize,
    color: v.listItemContentColor
  }),
  checkableIndicator: ({ variables: v }) => ({
    display: "flex",
    fill: v.listItemHeaderColor
  }),
  endMedia: () => ({
    flexShrink: 0,
    lineHeight: pxToRem2(16)
  }),
  main: () => ({
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    minWidth: 0
    // needed for the truncate styles to work
  })
};

// packages/aria/src/themes/base/components/FeaturedArticle/featuredArticleStyles.ts
import { svgIconClassName as svgIconClassName19 } from "@archipro-design/icons";
var featuredArticleStyles = {
  root: ({ variables: v, props: p }) => {
    return {
      display: "block",
      // Incase "as" type is <a/> which is inline
      position: "relative",
      textDecoration: "none",
      width: v.width,
      backgroundColor: v.backgroundColor,
      padding: v.tilePadding,
      [`& > .${buttonClassName}`]: {
        display: "block",
        ":hover": {
          textDecoration: "underline"
        }
      },
      ...p.variant === "02" && {
        display: "flex"
      },
      ...p.variant === "03" && {
        display: "flex",
        flexDirection: "row-reverse",
        justifyContent: "space-between"
      },
      [`& .${featuredArticleSlotClassNames.contentButton}`]: {
        marginTop: pxToRem(23)
      }
    };
  },
  imageWrap: ({ variables: v }) => {
    return {
      width: v.imageWidth,
      height: v.imageHeight,
      minWidth: v.imageWidth,
      position: "relative",
      borderRadius: v.imageBorderRadius,
      overflow: "hidden",
      [`& .${imageOverlayClassName}`]: {
        backgroundColor: v.overlayBackgroundColor,
        [`& .${svgIconClassName19} svg`]: {
          fill: v.playIconColor,
          width: v.playIconSize,
          height: v.playIconSize
        }
      },
      [`&:hover .${buttonClassName}`]: {
        display: "flex"
      }
    };
  },
  contentTitle: ({ theme, props: p }) => {
    return {
      [`&.${captionClassName}`]: {
        fontSize: pxToRem(22),
        lineHeight: theme.siteVariables.lineHeightSmaller,
        letterSpacing: theme.siteVariables.letterSpacingNormal,
        marginBottom: pxToRem(26),
        ...theme.siteVariables.textStyles.Article.Roman55,
        ...(p.variant === "02" || p.variant === "03") && {
          fontSize: pxToRem(56),
          lineHeight: theme.siteVariables.lineHeightDefault
        }
      },
      ...p.variant === "02" && {
        maxWidth: pxToRem(600),
        marginBottom: pxToRem(29)
      },
      ...p.variant === "03" && {
        maxWidth: pxToRem(640),
        marginBottom: pxToRem(29)
      }
    };
  },
  contentWrap: ({ variables: v }) => {
    return {
      padding: v.contentGridPadding,
      gridTemplateColumns: v.contentGridColumns,
      [`&.${gridClassName}`]: {
        justifyContent: "space-between"
      }
    };
  },
  content: ({ theme }) => {
    return {
      paddingTop: pxToRem(5),
      [`&.${captionClassName}`]: {
        lineHeight: theme.siteVariables.lineHeightSmall
      }
    };
  },
  statusText: ({ theme }) => {
    return {
      paddingTop: theme.siteVariables.spaces["space-20"],
      marginBottom: theme.siteVariables.spaces["space-32"],
      fontSize: pxToRem(24)
    };
  },
  toggleButton: ({ theme }) => {
    return {
      textAlign: "left",
      [`&.${buttonClassName}`]: {
        display: "block",
        textDecoration: "underline",
        padding: 0,
        [`& .${labelClassName}`]: {
          fontWeight: theme.siteVariables.fontWeightRegular,
          lineHeight: theme.siteVariables.lineHeightSmall
        }
      }
    };
  },
  topButtons: () => {
    return {
      top: pxToRem(16),
      width: "100%",
      position: "absolute",
      paddingLeft: pxToRem(16),
      paddingRight: pxToRem(16),
      [`& .${buttonClassName}`]: {
        display: "none",
        borderRadius: "50%",
        pointerEvents: "auto"
      }
    };
  },
  hotButtons: () => {
    return {
      width: "100%"
    };
  },
  videoContainer: () => {
    return {
      position: "relative",
      width: "100%",
      paddingTop: "45.83%",
      [`& .${videoPlayerClassName}`]: {
        position: "absolute",
        top: "0",
        left: "0",
        width: "100%",
        height: "100%",
        [`& .${boxClassName}`]: {
          position: "absolute"
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/Checkbox/checkboxStyles.ts
var checkboxStyles = {
  root: ({ variables: v, props: p }) => {
    const indicatorSize = v[`${p.indicatorType}IndicatorSize`];
    return {
      display: "flex",
      "> *:last-child": {
        marginLeft: v[`${p.indicatorType}IndicatorLabel${p.checked ? "Checked" : ""}Gap`]
      },
      width: "fit-content",
      cursor: p.disabled ? "not-allowed" : "pointer",
      [`& .${labelClassName}`]: {
        userSelect: "none",
        display: "block",
        width: "fit-content",
        lineHeight: v.labelLineHeight,
        marginTop: v[`${p.indicatorType}LabelMarginTop`],
        ...p.disabled && {
          color: v.labelDisabledColor
        }
      },
      [`& .${checkboxSlotClassNames.indicatorContainer}`]: {
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: `max(${indicatorSize}, ${v.labelLineHeight})`
      }
    };
  },
  indicator: () => {
    return {
      flexShrink: 0
    };
  }
};

// packages/aria/src/themes/base/components/Checkbox/indicatorCheckboxStyles.ts
import { svgIconClassName as svgIconClassName20 } from "@archipro-design/icons";
var indicatorCheckboxStyles = {
  root: ({ variables: v, props: p }) => {
    return {
      width: v.size,
      height: v.size,
      backgroundColor: p.checked ? p.disabled ? v.checkedBackgroundColorDisabled : v.checkedBackgroundColor : v.backgroundColor,
      border: p.disabled ? v.borderDisabled : v.border,
      borderRadius: v.borderRadius,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      [`& .${svgIconClassName20}`]: {
        width: v.iconSize,
        height: v.iconSize,
        color: v.iconColor,
        fill: v.iconColor
      }
    };
  }
};

// packages/aria/src/themes/base/components/Checkbox/indicatorRadioStyles.ts
var indicatorRadioStyles = {
  root: ({ variables: v, props: p }) => {
    return {
      width: v.size,
      height: v.size,
      backgroundColor: p.checked ? p.disabled ? v.checkedBackgroundColorDisabled : v.checkedBackgroundColor : v.backgroundColor,
      border: p.disabled ? v.borderDisabled : v.border,
      borderRadius: "50%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center"
    };
  },
  middleRing: ({ variables: v }) => {
    return {
      width: v.middleRingSize,
      height: v.middleRingSize,
      backgroundColor: v.middleRingColor,
      borderRadius: "50%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center"
    };
  },
  innerRing: ({ variables: v, props: p }) => {
    return {
      width: v.innerRingSize,
      height: v.innerRingSize,
      backgroundColor: p.disabled ? v.checkedBackgroundColorDisabled : v.checkedBackgroundColor,
      borderRadius: "50%"
    };
  }
};

// packages/aria/src/themes/base/components/Switch/switchStyles.ts
var switchStyles = {
  root: ({ variables: v, props: p }) => {
    return {
      display: "flex",
      width: "fit-content",
      cursor: p.disabled ? "not-allowed" : "pointer",
      [`& .${labelClassName}`]: {
        userSelect: "none",
        lineHeight: v.labelLineHeight,
        ...p.disabled && {
          color: v.labelDisabledColor
        }
      }
    };
  },
  switch: ({ variables: v, props: p }) => {
    return {
      minWidth: v.switchWidth,
      width: v.switchWidth,
      height: v.switchHeight,
      backgroundColor: p.checked ? v.checkedBackgroundColor : v.backgroundColor,
      borderRadius: v.switchBorderRadius,
      display: "flex",
      justifyContent: p.checked ? "flex-end" : "flex-start",
      alignItems: "center",
      position: "relative",
      marginLeft: v.labelSpacing
    };
  },
  thumb: ({ variables: v, props: p }) => {
    return {
      width: v.thumbWidth,
      height: v.thumbHeight,
      backgroundColor: p.disabled ? v.thumbBackgroundColorDisabled : v.thumbBackgroundColor,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "50%",
      position: "absolute",
      left: p.checked ? v.switchWidth : "1px",
      transform: p.checked ? `translateX(calc(-${v.thumbWidth} - 1px))` : "none",
      transition: "all 0.3s ease",
      boxShadow: !p.disabled ? `-1px 0 1px rgba(0, 0, 0, 0.2)` : void 0
    };
  }
};

// packages/aria/src/themes/base/components/Checkbox/indicatorColorStyles.ts
import { svgIconClassName as svgIconClassName21 } from "@archipro-design/icons";
var indicatorColorStyles = {
  root: ({ props: p, variables: v }) => {
    return {
      position: "relative",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "fit-content",
      height: "fit-content",
      [`& .${variantColorClassName}`]: {
        opacity: p.disabled ? 0.3 : 1,
        border: `1px solid ${p.checked ? v.borderHighlightedColor : v.borderColor}`
      }
    };
  },
  iconContainer: ({ variables: v }) => {
    return {
      position: "absolute",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      height: "100%",
      [`& .${svgIconClassName21}`]: {
        width: v.iconSize,
        height: v.iconSize,
        color: v.iconColor,
        fill: v.iconColor
      }
    };
  }
};

// packages/aria/src/themes/base/components/CheckboxGroup/checkboxGroupStyles.ts
var checkboxGroupStyles = {
  root: ({ variables: v, props: p }) => {
    const numberOfColumns = p?.numberOfColumns;
    const isGrid = numberOfColumns && numberOfColumns > 1;
    return {
      display: isGrid ? "grid" : "flex",
      ...isGrid ? {
        gridTemplateColumns: `repeat(${numberOfColumns}, auto)`
      } : {
        flexDirection: p?.horizontal ? "row" : "column"
      },
      "> *:not(:first-child)": {
        marginTop: p?.horizontal ? 0 : v.optionGap,
        marginLeft: p?.horizontal ? v.optionGap : 0
      }
    };
  }
};

// packages/aria/src/themes/base/components/RangeSelector/rangeSelectorStyles.ts
var rangeSelectorStyles = {
  root: ({ variables: v, theme }) => {
    return {
      display: "flex",
      flexDirection: "column",
      "> *:not(:first-child)": {
        marginTop: v.contentGap
      },
      [`& .${rangeSelectorSlotClassNames.title}`]: {
        lineHeight: v.titleLineHeight
      },
      [`& .${rangeSelectorSlotClassNames.minInput}`]: {
        width: v.inputWidth
      },
      [`& .${rangeSelectorSlotClassNames.maxInput}`]: {
        width: v.inputWidth
      },
      [`& .${rangeSelectorSlotClassNames.inputWrapper}`]: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center"
      },
      [`& .${multiSliderSlotClassNames.thumb}`]: {
        border: `1px solid ${theme.siteVariables.colors.white["FFF"]}`
      }
    };
  }
};

// packages/aria/src/themes/base/components/RangeInput/rangeInputStyles.ts
var rangeInputStyles = {
  root: ({ variables: v, props: p }) => {
    return {
      display: "flex",
      boxSizing: "border-box",
      padding: `0 ${v.padding}`,
      alignItems: "center",
      height: `calc(${v.numberLineHeight} + 2 * ${v.padding})`,
      borderRadius: v.borderRadius,
      cursor: p.disabled ? "not-allowed" : "unset",
      border: `1px solid ${p.error ? v.borderColorError : p.disabled ? v.borderColorDisabled : v.borderColorNormal}`,
      overflow: "hidden",
      "> *:not(:first-child)": {
        marginLeft: v.numberAndUnitGap
      },
      [`& .${rangeInputSlotClassNames.unit}`]: {
        lineHeight: v.unitLineHeight,
        fontSize: v.unitFontSize,
        color: p.disabled ? v.unitColorDisabled : v.unitColor
      },
      [`& .${boxClassName}`]: {
        height: "fit-content",
        display: "flex",
        [`& input`]: {
          padding: 0,
          width: "100%",
          height: "fit-content",
          border: "none",
          lineHeight: v.numberLineHeight,
          fontSize: v.numberFontSize,
          color: p.disabled ? v.numberColorDisabled : v.numberColor,
          cursor: p.disabled ? "not-allowed" : "unset"
        }
      },
      [`& .${rangeInputSlotClassNames.input}`]: {
        flex: 1,
        width: "100%",
        height: "fit-content",
        display: "flex"
      }
    };
  }
};

// packages/aria/src/themes/base/components/MultiSlider/multiSliderStyles.ts
var selectors = {
  WEBKIT_THUMB: "::-webkit-multiSlider-thumb",
  MOZ_THUMB: "::-moz-range-thumb",
  MS_FILL_LOWER: "::-ms-fill-lower",
  MS_FILL_UPPER: "::-ms-fill-upper",
  MS_THUMB: "::-ms-thumb"
};
var multiSliderStyles = {
  root: ({ variables: v }) => ({
    height: v.height,
    width: v.width,
    display: "flex",
    position: "relative"
  }),
  rail: ({ props: p, variables: v }) => ({
    width: "100%",
    background: p.disabled ? v.disabledRailColor : v.railColor,
    position: "absolute",
    height: v.railHeight,
    marginTop: `calc(${v.height} / 2 - ${v.railHeight} / 2)`
  }),
  track: ({ props: p, variables: v }) => ({
    background: p.disabled ? v.disabledTrackColor : v.trackColor,
    position: "absolute",
    height: v.railHeight,
    marginTop: `calc(${v.height} / 2 - ${v.railHeight} / 2)`
  }),
  input: ({ props: p, variables: v }) => {
    const thumbStyles = { border: 0, width: "1px" };
    return {
      WebkitAppearance: "none",
      cursor: p.disabled ? "not-allowed" : "pointer",
      position: "absolute",
      height: "100%",
      width: "100%",
      margin: 0,
      padding: 0,
      opacity: 0,
      [selectors.WEBKIT_THUMB]: {
        ...thumbStyles,
        "-webkit-appearance": "none"
      },
      [selectors.MOZ_THUMB]: thumbStyles,
      [selectors.MS_THUMB]: {
        ...thumbStyles,
        marginTop: `calc(-${v.thumbSize} / 2)`
      },
      [selectors.MS_FILL_LOWER]: { display: "none" },
      [selectors.MS_FILL_UPPER]: { display: "none" }
    };
  },
  thumbWrapper: ({ variables: v }) => ({
    height: "100%",
    width: "100%",
    margin: `0 calc(${v.thumbSize} / 2)`,
    position: "relative",
    pointerEvents: "none"
  }),
  thumb: ({ props: p, variables: v }) => ({
    borderRadius: "100%",
    cursor: p.disabled ? "not-allowed" : "pointer",
    pointerEvents: "none",
    position: "absolute",
    background: p.disabled ? v.disabledThumbColor : v.thumbColor,
    height: v.thumbSize,
    width: v.thumbSize,
    marginTop: `calc(${v.height} / 2  - ${v.thumbSize} / 2)`,
    marginLeft: `calc(-${v.thumbSize} / 2)`
  })
};

// packages/aria/src/themes/base/components/Tile/BannerAdTile/bannerAdTileStyles.ts
var bannerAdTileStyles = {
  root: ({ theme }) => {
    return {
      display: "block",
      // Incase "as" type is <a/> which is inline
      [`& .${bannerAdTileSlotClassNames.actionButton}`]: {
        outline: `1px solid ${theme.siteVariables.colorScheme.default?.foreground}`,
        [`& .${labelClassName}`]: {
          lineHeight: theme.siteVariables.lineHeightSmaller
        }
      }
    };
  },
  mediaWrap: ({ variables: v }) => {
    return {
      borderRadius: v.borderRadius,
      overflow: "hidden"
    };
  },
  variant01: ({ theme, variables: v }) => {
    return {
      [`& .${bannerAdTileSlotClassNames.contentWrap}`]: {
        justifyContent: "space-between",
        padding: v.contentWrapSpacing
      },
      [`& .${bannerAdTileSlotClassNames.company}`]: {
        lineHeight: theme.siteVariables.lineHeightDefault,
        letterSpacing: theme.siteVariables.letterSpacingMedium
      },
      [`& .${bannerAdTileSlotClassNames.title}`]: {
        lineHeight: theme.siteVariables.lineHeightDefault,
        letterSpacing: theme.siteVariables.letterSpacingMedium,
        marginBottom: pxToRem(5),
        marginTop: pxToRem(15),
        textTransform: "uppercase"
      },
      [`& .${bannerAdTileSlotClassNames.description}`]: {
        marginTop: pxToRem(5),
        maxWidth: pxToRem(600),
        lineHeight: theme.siteVariables.lineHeightLarge,
        letterSpacing: theme.siteVariables.letterSpacingNormal
      }
    };
  },
  variant02: ({ theme }) => {
    return {
      [`& .${bannerAdTileSlotClassNames.contentWrap}`]: {
        justifyContent: "space-between",
        marginTop: pxToRem(32)
      },
      [`& .${bannerAdTileSlotClassNames.company}`]: {
        lineHeight: theme.siteVariables.lineHeightSmall,
        paddingBottom: pxToRem(24)
      },
      [`& .${bannerAdTileSlotClassNames.title}`]: {
        marginTop: pxToRem(-5),
        lineHeight: theme.siteVariables.lineHeightLarge,
        letterSpacing: theme.siteVariables.letterSpacingNormal
      },
      [`& .${bannerAdTileSlotClassNames.description}`]: {
        marginTop: pxToRem(7),
        maxWidth: pxToRem(600),
        lineHeight: theme.siteVariables.lineHeightLarge,
        letterSpacing: theme.siteVariables.letterSpacingNormal
      },
      [`& .${bannerAdTileSlotClassNames.actionButton}`]: {
        marginTop: pxToRem(-9)
      }
    };
  },
  variant03: ({ theme }) => {
    return {
      [`& .${bannerAdTileSlotClassNames.contentWrap}`]: {
        justifyContent: "space-between",
        marginTop: pxToRem(10)
      },
      [`& .${bannerAdTileSlotClassNames.company}`]: {
        lineHeight: theme.siteVariables.lineHeightLarge,
        letterSpacing: theme.siteVariables.letterSpacingNormal,
        marginBottom: pxToRem(5)
      },
      [`& .${bannerAdTileSlotClassNames.title}`]: {
        marginTop: pxToRem(0),
        marginBottom: pxToRem(10),
        ...mapLabelVariantToFontStyles({ variant: "01" }, theme),
        lineHeight: theme.siteVariables.lineHeightSmall
      },
      [`& .${bannerAdTileSlotClassNames.description}`]: {
        maxWidth: pxToRem(600),
        fontSize: pxToRem(14),
        lineHeight: theme.siteVariables.lineHeightLarge,
        letterSpacing: theme.siteVariables.letterSpacingMedium
      },
      [`& .${bannerAdTileSlotClassNames.actionButton}`]: {
        marginTop: pxToRem(15)
      }
    };
  },
  image: ({ props: p }) => {
    return {
      aspectRatio: p.aspectRatio
    };
  }
};

// packages/aria/src/themes/base/components/CategorySelector/categorySelectorStyles.ts
import { svgIconClassName as svgIconClassName22 } from "@archipro-design/icons";
var categorySelectorStyles = {
  root: ({ theme, variables: v }) => {
    return {
      width: v.width,
      height: v.heightMobile,
      padding: v.mobilePadding,
      [theme.screen.laptop]: {
        padding: v.desktopPadding,
        height: v.heightDesktop
      },
      [`& .${categorySelectorSlotClassNames.header}`]: {
        alignItems: "flex-start",
        marginBottom: pxToRem(16),
        display: "flex",
        gap: v.headerCategoryItemGap,
        [`& .${labelClassName}`]: {
          display: "flex",
          fontWeight: v.headerCategoryWeight
        },
        [theme.screen.laptop]: {
          display: "flex"
        }
      },
      [`& .${categorySelectorSlotClassNames.categoriesContainer}`]: {
        alignItems: "flex-start",
        flex: 1,
        overflowY: "auto",
        [theme.screen.laptop]: {
          overflowY: "unset",
          "> *:not(:first-child)": {
            marginTop: v.categoryGap
          }
        }
      },
      [`& .${categorySelectorSlotClassNames.category}`]: {
        textAlign: "left",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "-webkit-fill-available",
        padding: v.categoryPaddingMobile,
        borderBottom: v.categoryBorderBottomMobile,
        [`& .${svgIconClassName22}`]: {
          height: v.categoryIconSize,
          width: v.categoryIconSize
        },
        [`& .${labelClassName}`]: {
          display: "flex",
          flex: 1,
          alignItems: "center",
          letterSpacing: theme.siteVariables.letterSpacingMedium,
          lineHeight: v.categoryLabelLineHeightMobile,
          minHeight: `max(${v.categoryIconSize}, ${v.categoryLabelLineHeightMobile})`,
          [theme.screen.laptop]: {
            padding: 0,
            minHeight: "unset",
            letterSpacing: theme.siteVariables.letterSpacingNormal,
            lineHeight: v.categoryLabelLineHeightDesktop
          }
        },
        [theme.screen.laptop]: {
          padding: v.categoryPaddingDesktop,
          borderBottom: v.categoryBorderBottomDesktop,
          width: "100%"
        }
      },
      [`& .${categorySelectorSlotClassNames.selectedCategory}`]: {
        [`& .${labelClassName}`]: {
          fontWeight: v.selectedCategoryWeight
        },
        [`& .${svgIconClassName22}`]: {
          opacity: v.selectedCategoryIconOpacity
        }
      },
      [`& .${categorySelectorSlotClassNames.selectedHeaderCategory}`]: {
        [`& .${labelClassName}`]: {
          fontWeight: v.selectedHeaderCategoryWeight,
          display: "flex"
        },
        [`& .${svgIconClassName22}`]: {
          opacity: v.selectedCategoryIconOpacity
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/SearchableList/searchableListStyles.ts
import { svgIconClassName as svgIconClassName23 } from "@archipro-design/icons";
var searchableListStyles = {
  root: ({ props: p, variables: v, theme }) => {
    return {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      position: "relative",
      "> *:not(:first-child)": {
        marginTop: v.inputOptionGap
      },
      [`& .${searchableListSlotClassNames.input}`]: {
        width: "100%",
        cursor: p.disabled ? "not-allowed" : "unset",
        backgroundColor: v.searchableListBackgroundColor,
        padding: v.searchInputPadding,
        fontSize: v.searchInputFontSize,
        lineHeight: v.searchInputLineHeight,
        border: v.searchInputBorder,
        borderRadius: v.searchInputBorderRadius,
        [`& .${inputClassName}`]: {
          padding: 0
        },
        [`& .${inputClassName} .${boxClassName}`]: {
          padding: 0,
          cursor: p.disabled ? "not-allowed" : "text",
          pointerEvents: p.disabled ? "none" : "auto",
          color: v.searchInputFontColor,
          ":focus": {
            color: v.searchInputFontColor
          },
          border: "none"
        },
        [`& .${buttonClassName}`]: {
          padding: 0,
          [`& .${svgIconClassName23}`]: {
            width: `${v.searchInputIconSize}`,
            height: `${v.searchInputIconSize}`,
            color: p.disabled ? v.searchInputIconColorDisabled : v.searchInputIconColor,
            fill: p.disabled ? v.searchInputIconColorDisabled : v.searchInputIconColor
          }
        },
        [`& input`]: {
          fontSize: `max(16px, ${v.searchInputFontSize})`,
          lineHeight: `max(16px, ${v.searchInputLineHeight})`,
          [theme.screen.laptop]: {
            fontSize: v.searchInputFontSize,
            lineHeight: v.searchInputLineHeight
          }
        }
      },
      [`& .${searchableListSlotClassNames.options}`]: {
        width: v.optionListWidth,
        maxHeight: v.optionListHeight,
        [`& .${checkboxClassName}`]: {
          margin: 0
        },
        ...getScrollbarStyle(theme, "0")
      },
      [`& .${searchableListSlotClassNames.noResult}`]: {
        marginTop: v.noResultGap,
        fontSize: v.noResultFontSize,
        lineHeight: v.noResultLineHeight,
        color: v.noResultTextColor
      },
      [`& .${searchableListSlotClassNames.subtext}`]: {
        margin: v.subtextMargin,
        fontSize: v.subtextFontSize,
        lineHeight: v.subtextLineHeight,
        color: v.subtextColor,
        [theme.screen.laptop]: {
          fontSize: v.subtextFontSizeDesktop
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/Drawer/drawerStyles.ts
import { popupContentSlotClassNames as popupContentSlotClassNames2 } from "@fluentui/react-northstar";
var drawerStyles = {
  root: ({ variables: v, props: p, theme }) => {
    const isHorizontal = p.anchor === "left" || p.anchor === "right";
    const contentAlignment = (anchor = "bottom") => {
      switch (anchor) {
        case "left":
          return "row-reverse";
        case "right":
          return "row";
        case "top":
          return "column-reverse";
        case "bottom":
          return "column";
      }
    };
    const positionDrawer = (anchor = "bottom", hasSwipeableEdge) => {
      if (hasSwipeableEdge) {
        switch (anchor) {
          case "left":
            return {
              left: "env(safe-area-inset-left, 0)"
            };
          case "right":
            return {
              right: "env(safe-area-inset-right, 0)"
            };
          case "top":
            return {
              top: "env(safe-area-inset-top, 0)"
            };
          case "bottom":
            return {
              bottom: "env(safe-area-inset-bottom, 0)"
            };
        }
      } else {
        return {
          top: 0,
          left: 0
        };
      }
    };
    const contentHeightStyles = (anchor = "bottom", variableHeight) => {
      if (variableHeight) {
        return { height: variableHeight };
      }
      switch (anchor) {
        case "left":
        case "right":
          return { height: p.fluid ? "100%" : "fit-content" };
        case "top":
          return { height: "fit-content" };
        case "bottom":
          return {
            height: `calc(100vh - ${pxToRem(
              MOBILE_TOP_NAV_HEIGHT_SHORT_LOGO_WITHOUT_DVH_SUPPORT
            )})`,
            // Allow drawer content to be size of the screen minus the top nav
            "@supports (height: 100dvh)": {
              height: `calc(100dvh - ${pxToRem(
                MOBILE_TOP_NAV_HEIGHT_SHORT_LOGO
              )})`
            },
            [theme.screen.tablet]: {
              height: "fit-content"
            }
          };
        default:
          return {
            height: "fit-content"
          };
      }
    };
    return {
      position: "fixed",
      ...positionDrawer(p.anchor, p.hasSwipeableEdge),
      width: "100vw",
      //background stays while drawer moves
      height: p.anchor === "bottom" ? "200vh" : "100vh",
      "@supports (height: 100dvh)": {
        height: p.anchor === "bottom" ? "200dvh" : "100dvh"
      },
      //ios chrome top addressbar placement setting
      [theme.screen.max.tablet]: {
        bottom: pxToRem(-40),
        marginBottom: pxToRem(40),
        paddingBottom: 0
      },
      [theme.screen.tablet]: {
        height: "100vh",
        "@supports (height: 100dvh)": {
          height: "100dvh"
        }
      },
      padding: "env(safe-area-inset-top, 0) env(safe-area-inset-right, 0) env(safe-area-inset-bottom, 0) env(safe-area-inset-left, 0)",
      ovewrflow: "hidden",
      [`& .${popupContentSlotClassNames2.content}`]: {
        width: "100%",
        height: "100%",
        padding: 0,
        display: "flex",
        alignItems: "center",
        flexDirection: isHorizontal ? "row" : "column",
        justifyContent: p.anchor === "left" || p.anchor === "top" ? "flex-start" : "flex-end",
        backgroundColor: v.backdropColor,
        [`& .${drawerSlotClassNames.content}`]: {
          width: !isHorizontal && p.fluid ? "100%" : "fit-content",
          ...contentHeightStyles(p.anchor, v.contentHeight),
          flexDirection: p.hasSwipeableEdge ? contentAlignment(p.anchor) : "column",
          backgroundColor: v.contentBackgroundColor,
          borderRadius: v.contentBorderRadius
        },
        [`& .${drawerSlotClassNames.swipeHandle}`]: {
          width: !isHorizontal && p.fluid ? "100%" : "fit-content"
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/ToastMessage/toastMessageStyles.ts
import { svgIconClassName as svgIconClassName24 } from "@archipro-design/icons";
var toastMessageStyles = {
  root: ({ variables: v, theme, props: p }) => {
    const variant = p.variant === "regular" ? "Regular" : "Small";
    const color = p.color === "dark" ? "Dark" : "White";
    return {
      [`& .${toastMessageClassNames.container}`]: {
        width: v.width,
        height: "auto",
        marginLeft: v.marginLeft,
        bottom: v.marginBottom,
        backgroundColor: v[`backgroundColor${color}`],
        color: v[`textColor${color}`],
        boxShadow: v.boxShadow,
        textAlign: "center",
        borderRadius: "2px",
        position: "fixed",
        zIndex: theme.siteVariables.zIndexes.overlayPriority,
        left: "50%",
        fontSize: v[`fontSize${variant}`],
        ...theme.siteVariables.textStyles.Text.Roman55,
        [theme.screen.laptop]: {
          width: "auto",
          minWidth: v.width,
          maxWidth: v.maxWidth
        }
      },
      [`& .${toastMessageClassNames.content}`]: {
        height: "100%",
        alignItems: "flex-start",
        padding: v[`contentGap${variant}`]
      },
      [`& .${toastMessageClassNames.iconBefore}`]: {
        margin: pxArrayToRem([2, 8, 0, 0]),
        [`& .${svgIconClassName24}`]: {
          height: v[`iconBeforeWidth${variant}`],
          width: v[`iconBeforeWidth${variant}`],
          "& svg": {
            "& path:last-child": {
              fill: v[`textColor${color}`]
            }
          }
        }
      },
      [`& .${toastMessageClassNames.closeButton}`]: {
        width: pxToRem(32),
        height: pxToRem(32),
        [`& .${svgIconClassName24}`]: {
          height: v[`iconCloseWidth${variant}`],
          width: v[`iconCloseWidth${variant}`],
          "& svg": {
            cursor: "pointer",
            "& path:last-child": {
              fill: v[`textColor${color}`]
            }
          }
        },
        ":hover": {
          background: v[`closeBtnHoverBgColor${color}`],
          borderRadius: "100%"
        }
      },
      [`& .${toastMessageClassNames.message}`]: {
        textAlign: "left",
        flexGrow: 1,
        ...theme.siteVariables.textStyles.Text.Roman55,
        padding: pxArrayToRem([4, 8, 4, 0]),
        lineHeight: 1.35,
        [`& .${toastMessageClassNames.clickHere}`]: {
          paddingLeft: pxToRem(8),
          textDecoration: "underline",
          color: v[`textColor${color}`],
          display: "inline",
          ...theme.siteVariables.textStyles.Text.Roman55,
          fontSize: v[`fontSize${variant}`],
          ":hover": {
            color: v[`hoverColor${color}`]
          }
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/ProfileNavigation/profileNavigationStyles.ts
var profileNavigationStyles = {
  root: ({ variables: v, theme }) => {
    return {
      [`& .${profileNavigationClassNames.container}`]: {
        gap: v.gap,
        [`& .${profileNavigationClassNames.itemContainer}`]: {
          flex: 1,
          gap: v.gap,
          overscrollBehaviorX: "none",
          overflowX: "auto",
          "&::-webkit-scrollbar": {
            height: 0,
            display: "none"
          }
        }
      },
      [`& .${profileNavigationItemClassName}`]: {
        height: v.buttonHeight,
        padding: v.buttonPadding,
        [`& .${labelClassName}`]: {
          whiteSpace: "nowrap"
        }
      },
      [`& .${profileNavigationClassNames.filterButton}`]: {
        minWidth: v.filterButtonMinWidth,
        maxWidth: "fit-content",
        overflow: "hidden",
        [`& .${labelClassName}`]: {
          flexShrink: 1,
          display: "flex",
          lineHeight: theme.siteVariables.lineHeightLarge,
          overflowX: "hidden",
          maxWidth: "fit-content"
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/MobileHeaderSection/mobileHeaderSectionStyles.ts
import { labelClassName as labelClassName4 } from "@fluentui/react-northstar";
var mobileHeaderSectionStyles = {
  root: () => {
    return {
      width: "100%",
      overflow: "hidden"
    };
  },
  fixedWrapper: ({ theme, variables: v }) => {
    return {
      position: "relative",
      backgroundColor: theme.siteVariables.colorScheme.default?.background,
      marginBottom: pxToRem(14),
      [`& .${labelClassName4}`]: {
        textTransform: "uppercase"
      },
      [`& .${captionClassName}`]: {
        color: v.contentCaptionColor
      }
    };
  },
  container: ({ props: p, variables: v }) => ({
    padding: v.contentPadding,
    visibility: p.animationState === "mobileHeaderSectionSlideIn" ? "hidden" : "visible"
  }),
  headings: ({ variables: v }) => {
    return {
      marginBottom: v.marginBottomHeadings,
      gap: v.headingsGap,
      [`& .${captionClassName}`]: {
        marginTop: pxToRem(-1)
      }
    };
  },
  contentWrapper: ({ theme, props: p, variables: v }) => {
    return {
      position: p.animationState === "mobileHeaderSectionSlideIn" ? "fixed" : "relative",
      top: p.animationState === "mobileHeaderSectionSlideIn" ? v.contentTopPositionOnAnimate : "unset",
      width: "100%",
      backgroundColor: theme.siteVariables.colorScheme.default?.background,
      padding: pxArrayToRem([10, 0, 10, 13])
    };
  }
};

// packages/aria/src/themes/base/components/Review/reviewStyles.ts
var reviewStyles = {
  root: () => {
    return {
      [`& .${captionClassName}`]: {
        paddingLeft: pxToRem(36)
      }
    };
  }
};

// packages/aria/src/themes/base/components/FilterContentLayout/filterContentLayoutStyles.ts
var filterContentLayoutStyles = {
  root: ({ variables: v }) => {
    return {
      color: v.textColor,
      background: v.filterContentLayoutBackgroundColor,
      width: `100%`,
      height: `100%`
    };
  },
  layout: () => {
    return {
      height: `100%`,
      gridTemplateRows: `auto 1fr auto`,
      [`& > div`]: {
        width: `100%`
      }
    };
  },
  header: ({ variables: v }) => {
    return {
      padding: v.headerPadding
    };
  },
  headerInner: ({ variables: v, theme }) => {
    return {
      padding: pxArrayToRem([5, 8]),
      height: v.headerHeight,
      width: "100%",
      gap: pxToRem(8),
      borderBottom: `1px solid ${theme.siteVariables.colors.charcoal["250"]}`,
      [`& .${filterContentLayoutSlotClassNames.headerAction}`]: {
        width: v.headerActionWidth,
        justifyContent: "flex-start"
      },
      [`& .${filterContentLayoutSlotClassNames.title}`]: {
        flex: 1
      },
      [`& .${filterContentLayoutSlotClassNames.closeBtn}`]: {
        width: v.closeButtonWidth,
        justifyContent: "flex-end"
      }
    };
  },
  content: ({ variables: v, theme }) => {
    return {
      padding: v.contentPadding,
      maxHeight: `calc(100vh - ${v.footerHeight} - ${v.headerHeight} - ${v.contentScrollSpacing})`,
      overflow: `auto`,
      ...getScrollbarStyle(theme)
    };
  },
  footer: ({ variables: v, theme }) => {
    return {
      padding: v.footerPadding,
      height: v.footerHeight,
      borderTop: `1px solid ${theme.siteVariables.colors.transparency.black["10%"]}`
    };
  }
};

// packages/aria/src/themes/base/components/VirtualList/virtualListStyles.ts
var virtualListStyles = {
  root: ({ variables: v, theme, props }) => {
    return {
      width: "100%",
      height: `${props.listContentHeight}px`,
      maxHeight: v.maxHeight,
      minHeight: v.minHeight,
      [`& .${virtualListSlotClassNames.listContainer}`]: {
        ...getScrollbarStyle(theme, "0")
      }
    };
  }
};

// packages/aria/src/themes/base/components/VirtualList/virtualListItemStyles.ts
var virtualListItemStyles = {
  root: ({ variables: v }) => {
    return {
      width: "100%",
      backgroundColor: v.backgroundColor
    };
  }
};

// packages/aria/src/themes/base/components/Modal/EnquiryModal/enquiryModalStyles.ts
var enquiryModalStyles = {
  root: ({ theme }) => {
    return {
      padding: pxToRem(24),
      [theme.screen.laptop]: {
        padding: pxArrayToRem([82, 48])
      },
      [`& .${dialogSlotClassNames.header}`]: {
        width: "100%",
        margin: 0,
        padding: 0
      },
      [`& .${enquiryModalSlotClassNames.headerAction}`]: {
        top: pxToRem(14),
        right: pxToRem(14),
        position: "absolute"
      },
      [`& .${dialogSlotClassNames.content}`]: {
        margin: 0,
        padding: 0,
        border: "none"
      },
      [`& .${dialogSlotClassNames.footer}`]: {
        padding: 0,
        alignItems: "flex-end",
        marginTop: pxToRem(32),
        [`& .${buttonClassName}`]: {
          width: "100%",
          [theme.screen.laptop]: {
            width: pxToRem(120)
          }
        }
      },
      ...getScrollbarStyle(theme, "0")
    };
  }
};

// packages/aria/src/themes/base/components/Container/containerStyles.ts
var containerStyles = {
  root: ({ variables: v, props: p }) => {
    return {
      width: "100%",
      marginLeft: "auto",
      marginRight: "auto",
      boxSizing: "border-box",
      display: "block",
      maxWidth: p.maxWidth,
      background: v.background
    };
  }
};

// packages/aria/src/themes/base/components/TextInput/textInputStyles.ts
import { svgIconClassName as svgIconClassName25 } from "@archipro-design/icons";
import {
  inputClassName as inputClassName4,
  inputLabelClassName,
  inputSlotClassNames as inputSlotClassNames4
} from "@fluentui/react-northstar";
var textInputStyles = {
  root: ({ variables: v, theme, props: p }) => {
    let mobileFontStyles = mapCaptionVariantToFontStyles(
      { variant: "03" },
      theme
    );
    mobileFontStyles = {
      ...mobileFontStyles,
      fontSize: `max(16px, ${mobileFontStyles.fontSize})`
    };
    return {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      boxSizing: "border-box",
      [`& .${captionClassName}`]: {
        color: v.defaultColor
      },
      // Slot Styles
      [`& .${textInputSlotClassNames.primaryContent}`]: {
        display: "flex",
        alignItems: "start",
        width: "100%",
        height: "100%",
        flexDirection: "column",
        padding: v.paddingMobile,
        [theme.screen.laptop]: {
          flexDirection: "row",
          padding: pxToRem(0)
        }
      },
      [`& .${inputClassName4}`]: {
        display: "block",
        alignItems: "center",
        justifyContent: "stretch",
        width: "100%",
        [theme.screen.laptop]: {
          alignItems: "flex-start",
          display: "flex",
          minHeight: v.minHeightLargeDesktop,
          ...p.size === "md" && {
            minHeight: v.minHeightMediumDesktop
          }
        },
        [`& .${inputLabelClassName}`]: {
          display: "block",
          alignSelf: "self-start",
          color: p.disabled ? v.disabledColor : v.hoverColor,
          cursor: "inherit",
          width: "100%",
          padding: pxArrayToRem([0, 8, 6, 8]),
          ...mapCaptionVariantToFontStyles({ variant: "03s" }, theme),
          [theme.screen.laptop]: {
            flexShrink: 0,
            padding: pxArrayToRem([24, 8]),
            width: pxToRem(240),
            ...mapLabelVariantToFontStyles(
              { variant: "01" },
              theme
            ),
            ...p.size === "md" && {
              padding: pxArrayToRem([16, 8]),
              width: pxToRem(200),
              ...mapCaptionVariantToFontStyles(
                { variant: "02" },
                theme
              )
            }
          }
        },
        [`& > .${boxClassName}`]: {
          display: "flex",
          flexGrow: 1,
          fontSize: 0,
          minHeight: v.minInputHeightMobile,
          alignItems: "center",
          [theme.screen.laptop]: {
            paddingBottom: 0,
            alignItems: "flex-start"
          }
        },
        [`& .${inputSlotClassNames4.input}`]: {
          border: "none",
          width: "100%",
          cursor: "inherit",
          color: "inherit",
          padding: 0,
          textOverflow: "ellipsis",
          margin: v.inputMarginMobile,
          ...mobileFontStyles,
          [theme.screen.laptop]: {
            margin: v.inputMarginLargeDesktop,
            ...mapLabelVariantToFontStyles(
              { variant: "01" },
              theme
            ),
            ...p.size === "md" && {
              margin: v.inputMarginMediumDesktop,
              ...mapCaptionVariantToFontStyles(
                { variant: "02" },
                theme
              )
            }
          }
        },
        [`& textarea.${boxClassName}`]: {
          lineHeight: 1,
          resize: "none",
          [theme.screen.laptop]: {
            maxHeight: pxToRem(200),
            ...p.autoExpandMode === "expanded" && {
              minHeight: pxToRem(200)
            }
          }
        }
      },
      [`& .${inputSlotClassNames4.icon}`]: {
        right: 0,
        left: 0,
        flexGrow: 0,
        position: "relative",
        display: "flex",
        alignItems: "center",
        height: v.iconContainerMobile,
        [theme.screen.laptop]: {
          height: v.minHeightLargeDesktop,
          ...p.size === "md" && {
            height: v.minHeightMediumDesktop
          }
        },
        [`& .${svgIconClassName25}`]: {
          fill: v.hoverColor,
          margin: v.iconMarginMobile,
          width: v.iconSizeMobile,
          height: v.iconSizeMobile,
          [theme.screen.laptop]: {
            width: v.iconSizeDesktopLarge,
            height: v.iconSizeDesktopLarge,
            margin: v.iconMarginDesktopLarge,
            ...p.size === "md" && {
              width: v.iconSizeDesktopMedium,
              height: v.iconSizeDesktopMedium,
              margin: v.iconMarginDesktopMedium
            }
          }
        }
      },
      [`& .${textInputSlotClassNames.prefixIcon}`]: {
        display: "none",
        [theme.screen.laptop]: {
          display: "flex"
        }
      },
      [`& .${textInputSlotClassNames.clearIcon}`]: {
        ...p.state === "default" && { visibility: "hidden" },
        [`& .${svgIconClassName25}`]: {
          margin: pxToRem(8),
          width: pxToRem(18),
          height: pxToRem(18)
        },
        [theme.screen.laptop]: {
          [`& .${svgIconClassName25}`]: {
            width: pxToRem(32),
            height: pxToRem(32),
            margin: pxArrayToRem([16, 8]),
            ...p.size === "md" && {
              width: pxToRem(24),
              height: pxToRem(24)
            }
          }
        }
      },
      [`& .${textInputSlotClassNames.message}`]: {
        display: "flex",
        width: "100%",
        justifyContent: "flex-end",
        padding: pxArrayToRem([0, 8, 8, 8]),
        ...p.state === "success" && {
          color: v.successColor
        },
        ...p.state === "error" && {
          color: v.errorColor
        },
        ...mapCaptionVariantToFontStyles({ variant: "04" }, theme),
        [theme.screen.laptop]: {
          ...mapCaptionVariantToFontStyles({ variant: "03" }, theme),
          ...p.size === "md" && {
            ...mapCaptionVariantToFontStyles(
              { variant: "04" },
              theme
            )
          }
        },
        [`& a`]: {
          color: "inherit",
          textDecoration: "underline"
        }
      },
      // States: Default, Hover, Disabled
      ...!p.disabled && {
        ":hover": {
          backgroundColor: v.hoverBackgroundColor,
          [`& .${inputClassName4}`]: {
            [`& .${inputSlotClassNames4.input}`]: {
              color: v.hoverColor
            }
          }
        },
        ":focus-within": {
          backgroundColor: v.hoverBackgroundColor,
          [`& .${inputSlotClassNames4.input}`]: {
            color: v.hoverColor
          },
          [`& .${textInputSlotClassNames.clearIcon}`]: {
            visibility: "visible"
          }
        }
      },
      ...p.disabled && {
        cursor: "not-allowed",
        color: v.disabledColor,
        [`& .${svgIconClassName25} > svg`]: {
          fill: v.disabledColor
        },
        [`& .${captionClassName}`]: {
          color: v.disabledColor
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/TextSelectorInput/textSelectorInputStyles.ts
import { svgIconClassName as svgIconClassName26 } from "@archipro-design/icons";
import {
  dropdownClassName as dropdownClassName2,
  dropdownItemSlotClassNames as dropdownItemSlotClassNames5,
  dropdownSlotClassNames as dropdownSlotClassNames5,
  inputClassName as inputClassName5,
  inputSlotClassNames as inputSlotClassNames5
} from "@fluentui/react-northstar";
var textSelectorInputStyles = {
  root: ({ variables: v, theme, props: p }) => {
    return {
      [`& .${dropdownClassName2}`]: {
        width: "100%",
        border: "none",
        ...mapLabelVariantToFontStyles({ variant: "01" }, theme),
        margin: pxToRem(8)
      },
      [`& .${dropdownSlotClassNames5.container}`]: {
        border: "none",
        ":hover": {
          backgroundColor: "transparent"
        }
      },
      [`& .${dropdownSlotClassNames5.selectedItems}`]: {
        [`& .${inputClassName5}`]: {
          minHeight: "unset"
        }
      },
      [`& .${dropdownSlotClassNames5.searchInput}`]: {
        flexBasis: `calc(100% - ${pxToRem(32)})`,
        padding: 0,
        [theme.screen.laptop]: {
          padding: pxArrayToRem([16, 8]),
          flexBasis: `calc(100% - ${pxToRem(48)})`
        },
        [`& .${boxClassName}`]: {
          width: "100%",
          marginRight: 0,
          [`& .${inputSlotClassNames5.input}`]: {
            alignSelf: "center",
            margin: 0,
            justifyContent: "center",
            textOverflow: "ellipsis"
          }
        }
      },
      [`& .${dropdownSlotClassNames5.itemsList}`]: {
        border: "none",
        maxHeight: v.maxDropdownHeight,
        cursor: "pointer",
        paddingTop: 0,
        borderRadius: 0,
        ...getScrollbarStyle(theme),
        ...p.showChevronIcon && {
          width: `calc( 100% + ${pxToRem(48)})`
        },
        "&::-webkit-scrollbar-track": {
          margin: 0,
          backgroundColor: theme.siteVariables.colors.white[300]
        }
      },
      [`& .${dropdownSlotClassNames5.item}`]: {
        padding: pxToRem(8),
        [`& .${dropdownItemSlotClassNames5.header}`]: {
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          overflow: "hidden",
          ...mapCaptionVariantToFontStyles(
            { variant: "03-alt" },
            theme
          ),
          [theme.screen.laptop]: {
            ...mapLabelVariantToFontStyles(
              { variant: "02" },
              theme
            )
          }
        }
      },
      [`& .${dropdownSlotClassNames5.clearIndicator}`]: {
        padding: 0,
        right: 0
      },
      [`& .${dropdownItemSlotClassNames5.checkableIndicator}`]: {
        [`& .${svgIconClassName26}`]: {
          margin: 0,
          height: pxToRem(20),
          width: pxToRem(20)
        }
      },
      [`& .${dropdownSlotClassNames5.triggerButton}`]: {
        margin: 0,
        padding: 0,
        color: p.hasValue ? v.defaultColor : v.disabledColor,
        ...mapCaptionVariantToFontStyles({ variant: "03" }, theme),
        [theme.screen.laptop]: {
          margin: 0,
          textAlign: "left",
          flexBasis: `calc(100% - ${pxToRem(48)})`,
          padding: pxArrayToRem([24, 8]),
          lineHeight: pxToRem(26),
          ...mapLabelVariantToFontStyles({ variant: "01" }, theme),
          ...p.size === "md" && {
            ...mapCaptionVariantToFontStyles(
              { variant: "02" },
              theme
            )
          }
        }
      },
      [`& .${inputSlotClassNames5.icon}`]: {
        position: "relative",
        display: "flex",
        alignItems: "center",
        bottom: 0,
        right: 0,
        [theme.screen.laptop]: {
          minHeight: pxToRem(74)
        }
      },
      [`& .${svgIconClassName26}`]: {
        margin: 0,
        height: pxToRem(20),
        width: pxToRem(20)
      },
      ...!p.disabled && {
        ":hover": {
          [`& .${dropdownSlotClassNames5.triggerButton}`]: {
            color: v.hoverColor,
            background: "none"
          },
          [`& .${dropdownSlotClassNames5.searchInput} > .${boxClassName} > .${inputSlotClassNames5.input}`]: {
            "::placeholder": {
              color: v.hoverColor
            }
          }
        },
        ":focus-within": {
          [`& .${dropdownSlotClassNames5.triggerButton}`]: {
            backgroundColor: v.hoverBackgroundColor,
            color: v.hoverColor
          }
        }
      },
      ...p.disabled && {
        cursor: "not-allowed",
        color: v.disabledColor,
        [`& .${svgIconClassName26} > svg`]: {
          fill: v.disabledColor
        },
        [`& .${dropdownSlotClassNames5.triggerButton} > span`]: {
          cursor: "not-allowed",
          color: v.disabledColor
        },
        [`& .${dropdownSlotClassNames5.container}`]: {
          backgroundColor: "inherit"
        },
        ":hover": {
          [`& .${dropdownSlotClassNames5.container}`]: {
            backgroundColor: "transparent"
          }
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/Tile/AdminCaption/adminCaptionStyles.ts
var adminCaptionStyles = {
  root: ({ variables: v }) => {
    return {
      [`& .${captionClassName}`]: {
        color: v.captionColor
      },
      [`& .${adminCaptionSlotClassNames.operation}`]: {
        color: v.operationColor
      }
    };
  }
};

// packages/aria/src/themes/base/components/Tile/HotButtons/hotButtonsStyles.ts
var hotButtonsStyles = {
  root: () => {
    return {};
  }
};

// packages/aria/src/themes/base/components/BottomNav/bottomNavStyles.ts
import { svgIconClassName as svgIconClassName27 } from "@fluentui/react-northstar";
var bottomNavStyles = {
  wrapper: ({ variables: v, props: p }) => {
    return {
      display: p.animationState === "static" ? "block" : "inline-flex",
      padding: v.padding,
      background: v.backgroundColor,
      alignItems: "center",
      position: p.animationState === "static" ? "static" : "fixed",
      bottom: 0,
      zIndex: v.zIndex,
      [`& .${bottomNavSlotClassNames.icon}`]: {
        opacity: v.inactiveOpacity,
        cursor: "pointer",
        marginRight: v.columnGap,
        "&:last-child": {
          marginRight: 0
        },
        [`& .${svgIconClassName27}`]: {
          [`& svg`]: {
            fill: v.iconColor
          }
        }
      },
      [`& .${bottomNavSlotClassNames.active}`]: {
        opacity: v.activeOpacity,
        background: v.activeBackground,
        "&:last-child": {
          marginRight: 0
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/Modal/LoginModal/loginModalStyles.ts
var loginModalStyles = {
  root: ({ variables: v, theme }) => {
    return {
      padding: pxArrayToRem([0, 18, 18, 18]),
      [theme.screen.laptop]: {
        padding: pxArrayToRem([0, 28, 40, 28])
      },
      [`& .${dialogSlotClassNames.header}`]: {
        width: "100%",
        margin: 0,
        padding: 0
      },
      [`& .${dialogSlotClassNames.content}`]: {
        margin: 0,
        padding: 0,
        border: "none"
      },
      [`& .${dialogSlotClassNames.footer}`]: {
        alignItems: "flex-end",
        padding: 0
      },
      [`& .${loginModalSlotClassNames.header}`]: {
        height: pxToRem(64),
        justifyContent: "center",
        padding: pxToRem(12),
        width: "100%",
        boxSizing: "border-box",
        borderBottom: `1px solid ${theme.siteVariables.colors.transparency.black["10%"]}`,
        marginBottom: pxToRem(18),
        [theme.screen.laptop]: {
          marginBottom: pxToRem(28)
        },
        [`& .${captionClassName}`]: {
          margin: "0 auto",
          alignSelf: "center",
          color: v.color
        },
        [`& .${buttonClassName}`]: {
          position: "absolute",
          color: v.color,
          right: pxToRem(5),
          [theme.screen.laptop]: {
            right: pxToRem(15)
          }
        }
      },
      [`& .${loginModalSlotClassNames.footerButton}`]: {
        [`& .${labelClassName}`]: {
          ...mapCaptionVariantToFontStyles({ variant: "02" }, theme)
        }
      },
      [`& .${loginModalSlotClassNames.footerContent}`]: {
        ...mapCaptionVariantToFontStyles({ variant: "05-alt" }, theme)
      },
      ...getScrollbarStyle(theme, "0")
    };
  }
};

// packages/aria/src/themes/base/components/CategoryTree/categoryTreeStyles.ts
var categoryTreeStyles = {
  root: ({ variables: v }) => {
    return {
      width: "100%",
      height: "100%",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      [`& .${profileNavigationClassName}`]: {
        padding: v.filterPadding,
        [`& .${buttonClassName}`]: {
          height: v.filterButtonHeight,
          padding: v.filterButtonPadding
        }
      },
      [`& .${categoryListClassName}`]: {
        padding: v.listPadding
      }
    };
  }
};

// packages/aria/src/themes/base/components/CategoryTree/categoryListStyles.ts
import { svgIconClassName as svgIconClassName28 } from "@archipro-design/icons";
var categoryListStyles = {
  root: ({ variables: v }) => {
    return {
      display: "flex",
      flex: 1,
      width: "100%",
      flexDirection: "column",
      overflowY: "auto",
      overflowX: "hidden",
      backgroundColor: v.backgroundColor,
      padding: v.padding,
      [`& .${categoryListSlotClassNames.item}`]: {
        padding: v.itemPadding,
        borderBottom: v.itemBorderBottom,
        ":hover": {
          borderBottom: v.itemBorderBottom
        },
        [`& .${labelClassName}`]: {
          padding: v.labelPadding,
          fontSize: v.itemFontSize,
          lineHeight: v.itemLineHeight,
          letterSpacing: v.labelLetterSpacing,
          textAlign: "left",
          flex: 1
        },
        [`& .${svgIconClassName28}`]: {
          width: v.iconSize,
          height: v.iconSize
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/CategoryTree/categoryTreeHeaderStyles.ts
import { svgIconClassName as svgIconClassName29 } from "@archipro-design/icons";
var categoryTreeHeaderStyles = {
  root: ({ variables: v }) => {
    return {
      display: "flex",
      width: "100%",
      background: v.backgroundColor,
      padding: v.padding,
      marginBottom: v.marginBottom,
      borderBottom: v.borderBottom,
      [`& .${categoryTreeHeaderSlotClassNames.backButton}`]: {
        [`& .${svgIconClassName29}`]: {
          color: v.backButtonIconColor,
          width: v.backButtonIconWidth,
          height: v.backButtonIconHeight
        }
      },
      [`& .${categoryTreeHeaderSlotClassNames.contentContainer}`]: {
        flex: 1,
        padding: v.contentContainerPadding,
        [`& .${categoryTreeHeaderSlotClassNames.title}`]: {
          fontSize: v.titleFontSize,
          lineHeight: v.titleLineHeight,
          color: v.titleColor,
          textAlign: "center"
        },
        [`& .${categoryTreeHeaderSlotClassNames.subtitle}`]: {
          fontSize: v.subtitleFontSize,
          lineHeight: v.subtitleLineHeight,
          color: v.subtitleColor,
          textAlign: "center"
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/Loader/loaderStyles.ts
import { svgIconClassName as svgIconClassName30 } from "@archipro-design/icons";
var rootFlexDirections = {
  above: "column-reverse",
  below: "column",
  start: "row-reverse",
  end: "row"
};
var loaderStyles = {
  root: ({ props: p, variables: v }) => ({
    alignItems: "center",
    display: p.inline ? "inline-flex" : "flex",
    justifyContent: "center",
    flexDirection: rootFlexDirections[p.labelPosition ?? "below"],
    [`& .${svgIconClassName30}`]: {
      height: v.containerHeights[p.size ?? "medium"],
      width: v.containerWidths[p.size ?? "medium"],
      "& svg": {
        fill: v.primarySVGFill
      }
    }
  })
};

// packages/aria/src/themes/base/components/SearchableDropdown/searchableDropdownStyles.ts
import { svgIconClassName as svgIconClassName31 } from "@archipro-design/icons";
import { dropdownSlotClassNames as dropdownSlotClassNames6 } from "@fluentui/react-northstar";
var searchableDropdownStyles = {
  root: ({ variables: v, theme, props: p }) => {
    return {
      [`& .${dropdownSlotClassNames6.selectedItems}`]: {
        borderRadius: v.searchInputBorderRadius,
        padding: v.searchInputPaddingMobile,
        border: v.searchInputBorder,
        [theme.screen.laptop]: {
          padding: v.searchInputPadding
        }
      },
      [`& .${dropdownSlotClassNames6.container}`]: {
        [`:hover`]: {
          backgroundColor: "transparent"
        },
        backgroundColor: "transparent",
        borderBottom: "none"
      },
      [`& .${dropdownSlotClassNames6.searchInput}`]: {
        width: `calc(100% - ${pxToRem(18)})`,
        [`:focus`]: {
          border: 0,
          borderImageWidth: 0
        },
        [`& input`]: {
          width: `100%`,
          overflow: "hidden",
          textOverflow: "ellipsis",
          padding: 0,
          fontSize: v.searchInputFontSize,
          lineHeight: v.searchInputLineHeight,
          border: 0
        }
      },
      [`& .${dropdownSlotClassNames6.itemsList}`]: {
        padding: 0,
        border: 0
      },
      [`& .${dropdownSlotClassNames6.item}`]: {
        cursor: "pointer",
        padding: pxToRem(8),
        border: "none"
      },
      [`& .${svgIconClassName31}`]: {
        width: v.searchInputIconSizeMobile,
        height: v.searchInputIconSizeMobile,
        color: p.disabled ? v.searchInputIconColorDisabled : v.searchInputIconColor,
        fill: p.disabled ? v.searchInputIconColorDisabled : v.searchInputIconColor,
        [theme.screen.laptop]: {
          width: v.searchInputIconSize,
          height: v.searchInputIconSize
        }
      },
      [`& .${dropdownSlotClassNames6.toggleIndicator}`]: {
        width: "fit-content",
        left: pxToRem(12),
        right: 0,
        alignItems: "start",
        [theme.screen.laptop]: {
          width: "unset",
          left: "unset",
          right: pxToRem(10),
          alignItems: "unset"
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/Tile/TileVideo/tileVideoStyles.ts
var tileVideoStyles = {
  root: ({ variables: v, theme, props: p }) => {
    return {
      position: "relative",
      borderRadius: v.borderRadius,
      [`& .${tileVideoSlotClassNames.playOnHover}`]: {
        position: "absolute",
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        zIndex: theme.siteVariables.zIndexes.foreground
      },
      [`& video`]: {
        zIndex: theme.siteVariables.zIndexes.background,
        objectFit: "cover"
      },
      [`& .${tileOverlayClassName}`]: {
        aspectRatio: p.width && p.height ? p.width / p.height : 0,
        zIndex: theme.siteVariables.zIndexes.foreground + 1
      },
      ":hover": {
        [`& .${tileOverlayClassName}`]: {
          opacity: 1
        }
      },
      [`& .${buttonClassName}`]: {
        borderRadius: "50%",
        pointerEvents: "auto"
      }
    };
  }
};

// packages/aria/src/themes/base/components/Drawer/swipeableEdgeStyles.ts
var swipeableEdgeStyles = {
  root: ({ variables: v, theme, props: p }) => {
    const isHorizontal = p.anchor === "left" || p.anchor === "right";
    return {
      [`& .${swipeableEdgeSlotClassNames.puller}`]: {
        position: "relative",
        padding: isHorizontal ? v.containerPaddingHorizontal : v.containerPaddingVertical,
        width: isHorizontal ? "fit-content" : "100%",
        height: isHorizontal ? "100%" : "fit-content",
        [`&:before`]: {
          content: '""',
          display: "block",
          width: isHorizontal ? v.pullerHeight : v.pullerWidth,
          height: isHorizontal ? v.pullerWidth : v.pullerHeight,
          borderRadius: v.pullerBorderRadius,
          background: v.pullerColor,
          position: "absolute",
          zIndex: theme.siteVariables.zIndexes.foreground,
          ...isHorizontal && {
            top: "50%",
            transform: "translate(0, -50%)"
          },
          ...!isHorizontal && {
            left: "50%",
            transform: "translate(-50%, 0)"
          }
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/TopNavigation/appMenuStyles.ts
import { svgIconClassName as svgIconClassName32 } from "@fluentui/react-northstar";
var appMenuStyles = {
  root: ({ variables: v, theme, props: p }) => {
    return {
      height: v.contentHeight,
      width: v.contentWidth,
      minHeight: `calc(100% - env(safe-area-inset-bottom, 0) - env(safe-area-inset-top, 0))`,
      [`@supports (-webkit-touch-callout: none)`]: {
        minHeight: "-webkit-fill-available"
      },
      [`& .${appMenuSlotClassNames.content}`]: {
        padding: v.contentPadding,
        height: "100vh",
        "@supports (height: 100dvh)": {
          height: "100dvh"
        }
      },
      [`& .${appMenuSlotClassNames.scrollableContainer}`]: {
        height: "100%",
        overflowY: "scroll",
        paddingBottom: v.mobileSpacerHeight
      },
      [`& .${appMenuSlotClassNames.screen}`]: {
        flexDirection: "column",
        width: "100%",
        flex: 1,
        overflow: "hidden"
      },
      [`& .${appMenuSlotClassNames.categoryTree}`]: {
        [`& .${categoryListClassName}`]: {
          paddingBottom: v.mobileSpacerHeight
        }
      },
      [`& .${appMenuSlotClassNames.searchBar}`]: {
        margin: v.searchBarSectionSpacing,
        [`& .${searchBarClassName}`]: {
          [`& .${inputClassName} input`]: {
            fontSize: `max(16px, ${pxToRem(17)})`
          },
          ...!p.showSearchMenu && {
            [`& .${svgIconClassName32}`]: {
              width: pxToRem(21),
              height: pxToRem(21)
            }
          }
        }
      },
      [`& .${appMenuSlotClassNames.events}`]: {
        padding: pxArrayToRem([44, 10, 38]),
        borderBottom: `1px solid #E6E6E6`,
        [`& > .${captionClassName}`]: {
          ...theme.siteVariables.textStyles.Heading.Medium65,
          fontSize: pxToRem(12),
          letterSpacing: "0.25em",
          marginBottom: pxToRem(11)
        },
        [`& > .${gridClassName}`]: {
          gridGap: pxToRem(12)
        }
      },
      [`& .${appMenuSlotClassNames.navMenu}`]: {
        [`& .${listItemClassName}`]: {
          padding: pxArrayToRem([0, 18, 0, 22]),
          display: "flex",
          alignItems: "center",
          borderBottom: `1px solid #E6E6E6`,
          [`& .${captionClassName}`]: {
            fontSize: pxToRem(16),
            lineHeight: theme.siteVariables.lineHeightSmallest,
            display: "flex",
            justifyContent: "space-between",
            width: "100%"
          }
        }
      }
    };
  }
};

// packages/aria/src/themes/base/components/ReadMore/readMoreStyles.ts
var readMoreStyles = {
  root: () => ({}),
  paragraphContent: ({ props, theme, variables: v }) => {
    const { clamped, maxLines, isSafari, forceClamp, forceClampMinHeight } = props;
    const {
      elementGap,
      linkColor,
      contentFontSizeInPx,
      contentLineHeight
    } = v;
    const safariParagraphStyles = {
      display: "inline-block",
      marginBottom: pxToRem(contentFontSizeInPx * contentLineHeight)
    };
    const heightStyles = {
      height: "auto",
      maxHeight: !clamped ? "auto" : pxToRem(maxLines * contentLineHeight * contentFontSizeInPx)
    };
    return {
      transition: forceClamp ? "none" : "all 300ms",
      marginBottom: 0,
      overflow: "hidden",
      display: "-webkit-box",
      WebkitLineClamp: clamped ? maxLines : "initial",
      lineClamp: clamped ? maxLines : "initial",
      WebkitBoxOrient: "vertical",
      overflowWrap: "break-word",
      fontSize: pxToRem(contentFontSizeInPx),
      lineHeight: contentLineHeight,
      position: "relative",
      ...heightStyles,
      ...forceClamp && {
        maxHeight: clamped ? forceClampMinHeight ? `${forceClampMinHeight}px` : "unset" : "unset"
      },
      [`& p`]: {
        marginTop: 0,
        marginBottom: pxToRem(contentFontSizeInPx * contentLineHeight),
        height: "100%"
      },
      ...clamped && v.showGradient && {
        // add a slight gradient to the bottom of the clamped text on Safari to hide some cut off text caused by HTML fontSize hack based on screensize
        [`&::after`]: {
          content: '" "',
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          height: pxToRem(2 * contentFontSizeInPx),
          background: `linear-gradient(180deg, ${hex2rgba(
            theme.siteVariables.bodyBackground,
            0
          )} 0%, ${hex2rgba(
            theme.siteVariables.bodyBackground,
            100
          )} 90%)`
        }
      },
      ...isSafari && clamped ? {
        [`& > .${boxClassName}`]: {
          ...safariParagraphStyles
        },
        [`& > .${boxClassName} li`]: {
          ...safariParagraphStyles,
          display: "-webkit-inline-flex"
        },
        [`& > .${boxClassName} p`]: {
          ...safariParagraphStyles
        }
      } : {},
      [`& > .${boxClassName} > *`]: {
        margin: 0,
        fontSize: "inherit",
        lineHeight: "inherit",
        letterSpacing: theme.siteVariables.letterSpacingNormal,
        "& a": {
          color: linkColor
        },
        "& a:link": {
          color: linkColor
        },
        "& strong, & b": {
          fontWeight: theme.siteVariables.textStyles.Text.Medium65.fontWeight
        },
        ...!isSafari ? {
          "&:not(:last-child)": {
            marginBottom: elementGap
          }
        } : {},
        ...clamped && isSafari ? {
          [`&:after`]: {
            content: '" \\a"'
          }
        } : {
          paddingBottom: pxToRem(20)
        }
      },
      [`& .${boxClassName} > :not(:last-child)`]: {
        paddingBottom: !clamped && isSafari ? elementGap : 0
      }
    };
  },
  toggleButton: ({ theme, variables: v }) => {
    return {
      margin: v.toggleButtonSpacing,
      [`& > .${labelClassName}`]: {
        textTransform: "unset",
        textDecoration: "underline",
        fontSize: v.toggleFontSize,
        fontWeight: theme.fontFaces[1].props.fontWeight
      }
    };
  }
};

// packages/aria/src/themes/base/fontFaces.ts
var fontFaces = [
  {
    name: "neue-haas-grotesk-display",
    paths: ["/assets/website/fonts/neue-haas-grotesk-display-400.woff2"],
    props: {
      fontWeight: 400,
      fontStyle: "normal",
      fontStretch: "normal",
      // @ts-ignore
      fontDisplay: "swap"
    }
  },
  {
    name: "neue-haas-grotesk-display",
    paths: ["/assets/website/fonts/neue-haas-grotesk-display-500.woff2"],
    props: {
      fontWeight: 500,
      fontStyle: "normal",
      fontStretch: "normal",
      // @ts-ignore
      fontDisplay: "swap"
    }
  },
  {
    name: "neue-haas-grotesk-display",
    paths: ["/assets/website/fonts/neue-haas-grotesk-display-600.woff2"],
    props: {
      fontWeight: 600,
      fontStyle: "normal",
      fontStretch: "normal",
      // @ts-ignore
      fontDisplay: "swap"
    }
  },
  {
    name: "neue-haas-grotesk-text",
    paths: ["/assets/website/fonts/neue-haas-grotesk-text-400.woff2"],
    props: {
      fontWeight: 400,
      fontStyle: "normal",
      fontStretch: "normal",
      // @ts-ignore
      fontDisplay: "swap"
    }
  },
  {
    name: "neue-haas-grotesk-text",
    paths: ["/assets/website/fonts/neue-haas-grotesk-text-500.woff2"],
    props: {
      fontWeight: 500,
      fontStyle: "normal",
      fontStretch: "normal",
      // @ts-ignore
      fontDisplay: "swap"
    }
  },
  {
    name: "neue-haas-grotesk-text",
    paths: ["/assets/website/fonts/neue-haas-grotesk-text-700.woff2"],
    props: {
      fontWeight: 700,
      fontStyle: "normal",
      fontStretch: "normal",
      // @ts-ignore
      fontDisplay: "swap"
    }
  },
  {
    name: "Gestura Headline Regular",
    // https://socio-type.com/wp-content/uploads/2021/05/gestura-regular.ttf
    // TODO storybook load font 404 issue
    paths: ["/assets/website/fonts/GesturaHeadline-Regular.woff2"],
    props: {
      fontWeight: 400,
      fontStyle: "normal",
      fontStretch: "normal",
      // @ts-ignore
      fontDisplay: "swap"
    }
  },
  {
    name: "GT America",
    // TODO storybook load font 404 issue
    paths: ["/assets/website/fonts/GT-America-Extended-Regular.woff2"],
    props: {
      fontWeight: 400,
      fontStyle: "normal",
      fontStretch: "normal",
      // @ts-ignore
      fontDisplay: "swap"
    }
  },
  {
    name: "GT America",
    // TODO storybook load font 404 issue
    paths: ["/assets/website/fonts/GT-America-Extended-Medium.woff2"],
    props: {
      fontWeight: 500,
      fontStyle: "normal",
      fontStretch: "normal",
      // @ts-ignore
      fontDisplay: "swap"
    }
  },
  {
    name: "GT America Trial",
    paths: ["/assets/website/fonts/GT-America-Extended-Medium-Trial.otf"],
    props: {
      fontWeight: 500,
      fontStyle: "normal",
      fontStretch: "normal",
      // @ts-ignore
      fontDisplay: "swap"
    }
  },
  {
    name: "GT America Trial Light",
    paths: ["/assets/website/fonts/GT-America-Extended-Light-Trial.otf"],
    props: {
      fontWeight: 300,
      fontStyle: "normal",
      fontStretch: "normal",
      // @ts-ignore
      fontDisplay: "swap"
    }
  },
  {
    name: "GT America Trial Regular",
    paths: ["/assets/website/fonts/GT-America-Standard-Regular-Trial.otf"],
    props: {
      fontWeight: 400,
      fontStyle: "normal",
      fontStretch: "normal",
      // @ts-ignore
      fontDisplay: "swap"
    }
  },
  {
    name: "Fira Code",
    paths: ["/assets/website/fonts/fira-code-400.ttf"],
    props: {
      fontWeight: 400,
      fontStyle: "normal",
      fontStretch: "normal",
      // @ts-ignore
      fontDisplay: "swap"
    }
  }
];

// packages/aria/src/themes/base/staticStyles/globalStyles.ts
var globalStyles = (siteVars) => ({
  body: {
    padding: siteVars?.bodyPadding,
    margin: siteVars?.bodyMargin,
    fontFamily: siteVars?.bodyFontFamily,
    fontSize: siteVars?.bodyFontSize,
    lineHeight: siteVars?.bodyLineHeight
  },
  "*": {
    boxSizing: "border-box",
    "-webkit-tap-highlight-color": "transparent"
  },
  "*:before": {
    boxSizing: "border-box"
  },
  "*:after": {
    boxSizing: "border-box"
  },
  /* Adding priority for HTML `hidden` attribute to be applied correctly */
  "[hidden]": {
    display: "none!important"
  },
  "a:-webkit-any-link": {
    textDecoration: "none"
  },
  a: {
    textDecoration: "none"
  }
});

// packages/aria/src/themes/base/staticStyles/normalizeCSS.ts
var normalizeCSS = `/*! normalize.css v8.0.1 | MIT License | github.com/necolas/normalize.css */html{line-height:1.15;-webkit-text-size-adjust:100%}body{margin:0}main{display:block}h1{font-size:2em;margin:.67em 0}hr{box-sizing:content-box;height:0;overflow:visible}pre{font-family:monospace,monospace;font-size:1em}a{background-color:transparent}abbr[title]{border-bottom:none;text-decoration:underline;text-decoration:underline dotted}b,strong{font-weight:bolder}code,kbd,samp{font-family:monospace,monospace;font-size:1em}small{font-size:80%}sub,sup{font-size:75%;line-height:0;position:relative;vertical-align:baseline}sub{bottom:-.25em}sup{top:-.5em}img{border-style:none}button,input,optgroup,select,textarea{font-family:inherit;font-size:100%;line-height:1.15;margin:0}button,input{overflow:visible}button,select{text-transform:none}[type=button],[type=reset],[type=submit],button{-webkit-appearance:button}[type=button]::-moz-focus-inner,[type=reset]::-moz-focus-inner,[type=submit]::-moz-focus-inner,button::-moz-focus-inner{border-style:none;padding:0}[type=button]:-moz-focusring,[type=reset]:-moz-focusring,[type=submit]:-moz-focusring,button:-moz-focusring{outline:1px dotted ButtonText}fieldset{padding:.35em .75em .625em}legend{box-sizing:border-box;color:inherit;display:table;max-width:100%;padding:0;white-space:normal}progress{vertical-align:baseline}textarea{overflow:auto}[type=checkbox],[type=radio]{box-sizing:border-box;padding:0}[type=number]::-webkit-inner-spin-button,[type=number]::-webkit-outer-spin-button{height:auto}input[type="search" i]:enabled:read-write:-webkit-any(:focus, :hover)::-webkit-search-cancel-button{display:none}[type=search]{-webkit-appearance:textfield;outline-offset:-2px}[type=search]::-webkit-search-decoration{-webkit-appearance:none}::-webkit-file-upload-button{-webkit-appearance:button;font:inherit}details{display:block}summary{display:list-item}template{display:none}[hidden]{display:none}`;

// packages/aria/src/themes/base/staticStyles/responsiveRemCSS.ts
var responsiveRemCSS = `html{font-size: 3.865vw;} ${screen.tablet}{ html{font-size: 0.833vw;}} ${screen.clamped}{ html{font-size: min(16px, 0.833vw);}}`;

// packages/aria/src/themes/base/staticStyles/desktopOnlyCSS.ts
var desktopOnlyCSS = `${screen.max.shrankWindow} { html { font-size: 8.5px !important; } body { min-width: 1024px; overflow-x: auto; }}`;

// packages/aria/src/themes/base/staticStyles/index.ts
var staticStyles = [
  normalizeCSS,
  globalStyles,
  responsiveRemCSS
];

// packages/aria/src/themes/base/index.ts
var baseTheme = createTheme2(
  {
    siteVariables: siteVariables_exports,
    componentVariables: componentVariables_exports,
    componentStyles: componentStyles_exports,
    fontFaces,
    staticStyles,
    animations,
    screen
  },
  "base"
);

// packages/aria/src/themes/product/index.ts
import { createTheme as createTheme3, mergeThemes } from "@fluentui/styles";

// packages/aria/src/themes/product/componentVariables.ts
var componentVariables_exports2 = {};
__export(componentVariables_exports2, {
  BottomNav: () => bottomNavVariables2,
  TopNavigation: () => topNavigationVariables2
});

// packages/aria/src/themes/product/components/BottomNav/bottomNavVariables.ts
var bottomNavVariables2 = (siteVars) => ({
  backgroundColor: siteVars?.colorScheme?.default?.background,
  iconColor: siteVars?.colors?.charcoal["000"]
});

// packages/aria/src/themes/product/components/TopNavigation/topNavigationVariables.ts
var topNavigationVariables2 = (siteVars) => ({
  topNavigationBackgroundColor: siteVars.colors.white["100A"],
  menuItemDividerColor: siteVars.colors.grey[200],
  menuItemIconColor: siteVars.colors.charcoal[250]
});

// packages/aria/src/themes/product/siteVariables.ts
var siteVariables_exports2 = {};
__export(siteVariables_exports2, {
  bodyBackground: () => bodyBackground2,
  bodyColor: () => bodyColor2,
  colorScheme: () => colorScheme2
});
var bodyBackground2 = colors.white["100A"];
var bodyColor2 = colors.charcoal[250];
var colorScheme2 = {
  default: {
    ...colorScheme.default,
    foreground: colors.charcoal[250],
    foreground2: colors.charcoal["000"],
    foregroundHover: colors.charcoal[100],
    foregroundDisabled: colors.grey[300],
    background: colors.white["100A"]
  }
};

// packages/aria/src/themes/product/index.ts
var productTheme = mergeThemes(
  baseTheme,
  createTheme3(
    {
      siteVariables: siteVariables_exports2,
      componentVariables: componentVariables_exports2
    },
    "product"
  )
);

// packages/aria/src/themes/member/index.ts
import { createTheme as createTheme4, mergeThemes as mergeThemes2 } from "@fluentui/styles";

// packages/aria/src/themes/member/componentVariables.ts
var componentVariables_exports3 = {};
__export(componentVariables_exports3, {
  TopNavigation: () => topNavigationVariables3
});

// packages/aria/src/themes/member/components/TopNavigation/topNavigationVariables.ts
var topNavigationVariables3 = (siteVars) => ({
  topNavigationBackgroundColor: siteVars.colors.white["000"]
});

// packages/aria/src/themes/member/siteVariables.ts
var siteVariables_exports3 = {};
__export(siteVariables_exports3, {
  colorScheme: () => colorScheme3
});
var colorScheme3 = {
  default: {
    ...colorScheme.default,
    foreground: colors.charcoal[250],
    background: colors.white["000"]
  }
};

// packages/aria/src/themes/member/index.ts
var memberTheme = mergeThemes2(
  baseTheme,
  createTheme4(
    {
      siteVariables: siteVariables_exports3,
      componentVariables: componentVariables_exports3
    },
    "member"
  )
);

// packages/aria/src/themes/advertising/index.ts
import { createTheme as createTheme5, mergeThemes as mergeThemes3 } from "@fluentui/styles";

// packages/aria/src/themes/advertising/componentVariables.ts
var componentVariables_exports4 = {};
__export(componentVariables_exports4, {
  Button: () => buttonVariables2,
  Caption: () => captionVariables2,
  Logo: () => logoVariables2,
  NavigationItem: () => navigationItemVariables2,
  SearchBar: () => searchBarVariables2,
  SearchResult: () => searchResultVariables2,
  TopNavigation: () => topNavigationVariables4
});

// packages/aria/src/themes/advertising/components/TopNavigation/topNavigationVariables.ts
var topNavigationVariables4 = (siteVars) => ({
  topNavigationBackgroundColor: siteVars.colors.charcoal["000"],
  menuItemDividerColor: siteVars.colors.grey[200],
  menuItemIconColor: siteVars.colors.white["000"],
  topNavigationTopRightColor: siteVars.colors.white["000"],
  mobileSearchPlaceholderColor: siteVars.colors.grey[150],
  mobileSearchPlaceholderInputColor: siteVars.colors.white["000"]
});

// packages/aria/src/themes/advertising/components/Logo/logoVariables.ts
var logoVariables2 = (siteVars) => ({
  logoPrimaryColor: siteVars.colors.white["000"]
});

// packages/aria/src/themes/advertising/components/Button/buttonVariables.ts
var buttonVariables2 = (siteVars) => ({
  primaryButtonTextColorNormal: siteVars.colors.charcoal[250],
  primaryButtonTextColorHover: siteVars.colors.charcoal[250],
  primaryButtonTextColorSelected: siteVars.colors.charcoal[250],
  primaryButtonTextColorDisable: siteVars.colors.grey[200],
  primaryButtonBackgroundColorNormal: siteVars.colors.primitive.white,
  primaryButtonBackgroundColorHover: siteVars.colors.white[100],
  primaryButtonBackgroundColorSelected: siteVars.colors.white[100],
  primaryButtonBackgroundColorDisable: siteVars.colors.white["000"],
  primaryNoBKGButtonTextColorNormal: siteVars.colors.white["000"],
  primaryNoBKGButtonTextColorHover: siteVars.colors.white["FFF"],
  primaryNoBKGButtonTextColorSelected: siteVars.colors.white["FFF"],
  primaryNoBKGButtonTextColorDisable: siteVars.colors.grey[200],
  primaryNoBKGButtonBackgroundColorNormal: "transparent",
  primaryNoBKGButtonBackgroundColorHover: "rgba(255, 255, 255, 0.1)",
  primaryNoBKGButtonBackgroundColorSelected: "rgba(255, 255, 255, 0.1)",
  primaryNoBKGButtonBackgroundColorDisable: "transparent",
  primaryOutlinedButtonTextColorNormal: siteVars.colors.white["FFF"],
  primaryOutlinedButtonTextColorHover: siteVars.colors.white["FFF"],
  primaryOutlinedButtonTextColorSelected: siteVars.colors.white["FFF"],
  primaryOutlinedButtonTextColorDisable: siteVars.colors.grey[100],
  primaryOutlinedButtonBackgroundColorNormal: "transparent",
  primaryOutlinedButtonBackgroundColorHover: siteVars.colors.charcoal[250],
  primaryOutlinedButtonBackgroundColorSelected: siteVars.colors.charcoal[250],
  primaryOutlinedButtonBackgroundColorDisable: "transparent",
  primaryOutlinedButtonBorderNormal: `1px solid ${siteVars.colors.transparency.white["30%"]}`,
  primaryOutlinedButtonBorderHover: `1px solid transparent`,
  primaryOutlinedButtonBorderSelected: `1px solid transparent`,
  primaryOutlinedButtonBorderDisable: `1px solid ${siteVars.colors.transparency.white["30%"]}`
});

// packages/aria/src/themes/advertising/components/SearchBar/searchBarVariables.ts
var searchBarVariables2 = (siteVars) => ({
  searchBarBackgroundColor: siteVars.colors.transparency.white["10%"],
  searchBarIconColor: siteVars.colors.white[300],
  searchBarInputColor: siteVars.colors.grey[150],
  searchBarFocusedInputColor: siteVars.colors.white["000"]
});

// packages/aria/src/themes/advertising/components/SearchMenu/searchResultVariables.ts
var searchResultVariables2 = (siteVars) => ({
  searchResultItemColor: siteVars.colors.grey[150],
  searchResultItemMatchColor: siteVars.colors.white["000"]
});

// packages/aria/src/themes/advertising/components/Typography/Caption/captionVariables.ts
var captionVariables2 = (siteVars) => ({
  captionColor: siteVars.colors.white["000"]
});

// packages/aria/src/themes/advertising/components/TopNavigation/navigationItemVariables.ts
var navigationItemVariables2 = (siteVars) => ({
  topNavNavigationItemColor: siteVars.colorScheme.default?.["foreground"]
});

// packages/aria/src/themes/advertising/siteVariables.ts
var siteVariables_exports4 = {};
__export(siteVariables_exports4, {
  colorScheme: () => colorScheme4
});
var colorScheme4 = {
  default: {
    ...colorScheme.default,
    foreground: colors.white["000"],
    foreground2: colors.white["000"],
    foreground5: colors.grey[100],
    foregroundDisabled: colors.grey[150],
    border: colors.primitive.white,
    borderTransparent: colors.transparency.white["30%"],
    background: colors.charcoal["000"],
    backgroundHover: colors.transparency.white["30%"],
    backgroundHover1: colors.transparency.white["30%"]
  }
};

// packages/aria/src/themes/advertising/index.ts
var advertisingTheme = mergeThemes3(
  baseTheme,
  createTheme5(
    {
      siteVariables: siteVariables_exports4,
      componentVariables: componentVariables_exports4
    },
    "advertising"
  )
);

// packages/aria/src/themes/about-us/index.ts
import { createTheme as createTheme6, mergeThemes as mergeThemes4 } from "@fluentui/styles";

// packages/aria/src/themes/about-us/componentVariables.ts
var componentVariables_exports5 = {};
__export(componentVariables_exports5, {
  Button: () => buttonVariables3,
  Caption: () => captionVariables3,
  Logo: () => logoVariables3,
  NavigationItem: () => navigationItemVariables3,
  SearchBar: () => searchBarVariables3,
  SearchResult: () => searchResultVariables3,
  TopNavigation: () => topNavigationVariables5
});

// packages/aria/src/themes/about-us/components/TopNavigation/topNavigationVariables.ts
var topNavigationVariables5 = (siteVars) => ({
  topNavigationBackgroundColor: siteVars.colors.charcoal["260"],
  menuItemDividerColor: siteVars.colors.grey[200],
  menuItemIconColor: siteVars.colors.white["000"],
  topNavigationTopRightColor: siteVars.colors.white["000"],
  mobileSearchPlaceholderColor: siteVars.colors.grey[150],
  mobileSearchPlaceholderInputColor: siteVars.colors.white["000"]
});

// packages/aria/src/themes/about-us/components/Logo/logoVariables.ts
var logoVariables3 = (siteVars) => ({
  logoPrimaryColor: siteVars.colors.white["000"]
});

// packages/aria/src/themes/about-us/components/Button/buttonVariables.ts
var buttonVariables3 = (siteVars) => ({
  primaryButtonTextColorNormal: siteVars.colors.charcoal[250],
  primaryButtonTextColorHover: siteVars.colors.charcoal[250],
  primaryButtonTextColorSelected: siteVars.colors.charcoal[250],
  primaryButtonTextColorDisable: siteVars.colors.grey[200],
  primaryButtonBackgroundColorNormal: siteVars.colors.primitive.white,
  primaryButtonBackgroundColorHover: siteVars.colors.white[100],
  primaryButtonBackgroundColorSelected: siteVars.colors.white[100],
  primaryButtonBackgroundColorDisable: siteVars.colors.white["000"],
  primaryNoBKGButtonTextColorNormal: siteVars.colors.white["000"],
  primaryNoBKGButtonTextColorHover: siteVars.colors.white["FFF"],
  primaryNoBKGButtonTextColorSelected: siteVars.colors.white["FFF"],
  primaryNoBKGButtonTextColorDisable: siteVars.colors.grey[200],
  primaryNoBKGButtonBackgroundColorNormal: "transparent",
  primaryNoBKGButtonBackgroundColorHover: "rgba(255, 255, 255, 0.1)",
  primaryNoBKGButtonBackgroundColorSelected: "rgba(255, 255, 255, 0.1)",
  primaryNoBKGButtonBackgroundColorDisable: "transparent",
  primaryOutlinedButtonTextColorNormal: siteVars.colors.white["FFF"],
  primaryOutlinedButtonTextColorHover: siteVars.colors.white["FFF"],
  primaryOutlinedButtonTextColorSelected: siteVars.colors.white["FFF"],
  primaryOutlinedButtonTextColorDisable: siteVars.colors.grey[100],
  primaryOutlinedButtonBackgroundColorNormal: "transparent",
  primaryOutlinedButtonBackgroundColorHover: siteVars.colors.charcoal[250],
  primaryOutlinedButtonBackgroundColorSelected: siteVars.colors.charcoal[250],
  primaryOutlinedButtonBackgroundColorDisable: "transparent",
  primaryOutlinedButtonBorderNormal: `1px solid ${siteVars.colors.transparency.white["30%"]}`,
  primaryOutlinedButtonBorderHover: `1px solid transparent`,
  primaryOutlinedButtonBorderSelected: `1px solid transparent`,
  primaryOutlinedButtonBorderDisable: `1px solid ${siteVars.colors.transparency.white["30%"]}`
});

// packages/aria/src/themes/about-us/components/Typography/Caption/captionVariables.ts
var captionVariables3 = (siteVars) => ({
  captionColor: siteVars.colors.white["000"]
});

// packages/aria/src/themes/about-us/components/SearchBar/searchBarVariables.ts
var searchBarVariables3 = (siteVars) => ({
  searchBarBackgroundColor: siteVars.colors.transparency.white["10%"],
  searchBarIconColor: siteVars.colors.white[300],
  searchBarInputColor: siteVars.colors.grey[150],
  searchBarFocusedInputColor: siteVars.colors.white["000"]
});

// packages/aria/src/themes/about-us/components/SearchMenu/searchResultVariables.ts
var searchResultVariables3 = (siteVars) => ({
  searchResultItemColor: siteVars.colors.grey[150],
  searchResultItemMatchColor: siteVars.colors.white["000"]
});

// packages/aria/src/themes/about-us/components/TopNavigation/navigationItemVariables.ts
var navigationItemVariables3 = (siteVars) => ({
  topNavNavigationItemColor: siteVars.colorScheme.default?.["foreground"]
});

// packages/aria/src/themes/about-us/siteVariables.ts
var siteVariables_exports5 = {};
__export(siteVariables_exports5, {
  colorScheme: () => colorScheme5
});
var colorScheme5 = {
  default: {
    ...colorScheme.default,
    foreground: colors.white["000"],
    foreground2: colors.white["000"],
    foreground5: colors.grey[100],
    foregroundDisabled: colors.grey[150],
    border: colors.primitive.white,
    borderTransparent: colors.transparency.white["30%"],
    background: colors.charcoal["260"],
    backgroundHover: colors.transparency.white["30%"],
    backgroundHover1: colors.transparency.white["30%"]
  }
};

// packages/aria/src/themes/about-us/index.ts
var aboutUsTheme = mergeThemes4(
  baseTheme,
  createTheme6(
    {
      siteVariables: siteVariables_exports5,
      componentVariables: componentVariables_exports5
    },
    "about-us"
  )
);

// packages/aria/src/themes/meet-greet-eat/index.ts
import { createTheme as createTheme7, mergeThemes as mergeThemes5 } from "@fluentui/styles";

// packages/aria/src/themes/meet-greet-eat/componentVariables.ts
var componentVariables_exports6 = {};
__export(componentVariables_exports6, {
  Button: () => buttonVariables4,
  Caption: () => captionVariables4,
  Logo: () => logoVariables4,
  SearchBar: () => searchBarVariables4,
  SearchResult: () => searchResultVariables4,
  TopNavigation: () => topNavigationVariables6
});

// packages/aria/src/themes/meet-greet-eat/components/TopNavigation/topNavigationVariables.ts
var topNavigationVariables6 = (siteVars) => ({
  topNavigationBackgroundColor: siteVars.colors.charcoal["000"],
  menuItemDividerColor: siteVars.colors.grey[200],
  menuItemIconColor: siteVars.colors.white["000"],
  topNavigationTopRightColor: siteVars.colors.white["000"],
  mobileSearchPlaceholderColor: siteVars.colors.grey[150],
  mobileSearchPlaceholderInputColor: siteVars.colors.charcoal[250]
});

// packages/aria/src/themes/meet-greet-eat/components/Logo/logoVariables.ts
var logoVariables4 = (siteVars) => ({
  logoPrimaryColor: siteVars.colors.white["000"]
});

// packages/aria/src/themes/meet-greet-eat/components/Button/buttonVariables.ts
var buttonVariables4 = (siteVars) => ({
  primaryButtonTextColorNormal: siteVars.colors.charcoal[250],
  primaryButtonTextColorHover: siteVars.colors.charcoal[250],
  primaryButtonTextColorSelected: siteVars.colors.charcoal[250],
  primaryButtonTextColorDisable: siteVars.colors.grey[200],
  primaryButtonBackgroundColorNormal: siteVars.colors.primitive.white,
  primaryButtonBackgroundColorHover: siteVars.colors.white[100],
  primaryButtonBackgroundColorSelected: siteVars.colors.white[100],
  primaryButtonBackgroundColorDisable: siteVars.colors.white["000"],
  primaryNoBKGButtonTextColorNormal: siteVars.colors.white["000"],
  primaryNoBKGButtonTextColorHover: siteVars.colors.white["FFF"],
  primaryNoBKGButtonTextColorSelected: siteVars.colors.white["FFF"],
  primaryNoBKGButtonTextColorDisable: siteVars.colors.grey[200],
  primaryNoBKGButtonBackgroundColorNormal: "transparent",
  primaryNoBKGButtonBackgroundColorHover: "rgba(255, 255, 255, 0.1)",
  primaryNoBKGButtonBackgroundColorSelected: "rgba(255, 255, 255, 0.1)",
  primaryNoBKGButtonBackgroundColorDisable: "transparent",
  primaryOutlinedButtonTextColorNormal: siteVars.colors.white["FFF"],
  primaryOutlinedButtonTextColorHover: siteVars.colors.white["FFF"],
  primaryOutlinedButtonTextColorSelected: siteVars.colors.white["FFF"],
  primaryOutlinedButtonTextColorDisable: siteVars.colors.grey[100],
  primaryOutlinedButtonBackgroundColorNormal: "transparent",
  primaryOutlinedButtonBackgroundColorHover: siteVars.colors.charcoal[250],
  primaryOutlinedButtonBackgroundColorSelected: siteVars.colors.charcoal[250],
  primaryOutlinedButtonBackgroundColorDisable: "transparent",
  primaryOutlinedButtonBorderNormal: `1px solid ${siteVars.colors.transparency.white["30%"]}`,
  primaryOutlinedButtonBorderHover: `1px solid transparent`,
  primaryOutlinedButtonBorderSelected: `1px solid transparent`,
  primaryOutlinedButtonBorderDisable: `1px solid ${siteVars.colors.transparency.white["30%"]}`
});

// packages/aria/src/themes/meet-greet-eat/components/SearchBar/searchBarVariables.ts
var searchBarVariables4 = (siteVars) => ({
  searchBarBackgroundColor: siteVars.colors.transparency.white["10%"],
  searchBarIconColor: siteVars.colors.white[300],
  searchBarInputColor: siteVars.colors.grey[150],
  searchBarFocusedInputColor: siteVars.colors.white["000"]
});

// packages/aria/src/themes/meet-greet-eat/components/SearchMenu/searchResultVariables.ts
var searchResultVariables4 = (siteVars) => ({
  searchResultItemColor: siteVars.colors.grey[150],
  searchResultItemMatchColor: siteVars.colors.white["000"]
});

// packages/aria/src/themes/meet-greet-eat/components/Typography/Caption/captionVariables.ts
var captionVariables4 = (siteVars) => ({
  captionColor: siteVars.colors.white["000"]
});

// packages/aria/src/themes/meet-greet-eat/siteVariables.ts
var siteVariables_exports6 = {};
__export(siteVariables_exports6, {
  colorScheme: () => colorScheme6
});
var colorScheme6 = {
  default: {
    ...colorScheme.default,
    foreground: colors.charcoal[250],
    foreground2: colors.white["000"],
    foreground5: colors.grey[100],
    foregroundDisabled: colors.grey[150],
    border: colors.primitive.white,
    borderTransparent: colors.transparency.white["30%"],
    background: "#0f0f0f",
    backgroundHover: colors.transparency.white["30%"],
    backgroundHover1: colors.transparency.white["30%"]
  }
};

// packages/aria/src/themes/meet-greet-eat/index.ts
var meetGreetTheme = mergeThemes5(
  baseTheme,
  createTheme7(
    {
      siteVariables: siteVariables_exports6,
      componentVariables: componentVariables_exports6
    },
    "home-design-event"
  )
);

// packages/aria/src/server/renderer.ts
import { RendererContext } from "@fluentui/react-bindings";
import { createFelaRenderer } from "@fluentui/react-northstar-fela-renderer";
var RendererProvider = RendererContext.Provider;

// packages/aria/src/utils/bodyScrollLock.ts
import {
  disableBodyScroll as disableBodyScroll2,
  enableBodyScroll as enableBodyScroll2,
  clearAllBodyScrollLocks as clearAllBodyScrollLocks2
} from "body-scroll-lock";
export {
  Accordion,
  AdminCaption,
  AppDisplayModeContextProvider,
  AppMenu,
  ArchizeenTile,
  ArticleTile,
  Avatar,
  Badge,
  BannerAdTile,
  BottomNav,
  Box,
  Breadcrumb,
  BreadcrumbDivider,
  BreadcrumbItem,
  BreadcrumbNavigation,
  BusinessProfile,
  Button,
  Caption,
  Carousel,
  CarouselIndicator,
  CarouselItem,
  CarouselNavigation,
  CarouselNavigationItem,
  CarouselOverlay,
  CarouselPaddle,
  CarouselPaddlesContainer,
  CategoryList,
  CategorySelector,
  CategoryTree,
  CategoryTreeHeader,
  Checkbox,
  CheckboxGroup,
  Container,
  DEFAULT_CLAMP,
  DEFAULT_MENU_ITEMS,
  DEFAULT_MIN_DESKTOP,
  DESKTOP_TOP_NAV_HEIGHT_FULL_LOGO,
  DESKTOP_TOP_NAV_HEIGHT_SHORT_LOGO,
  DESKTOP_TOP_NAV_HEIGHT_WITH_SECOND_NAV,
  DEVICE_SIZES,
  DeviceBrowserContext,
  DeviceBrowserContextProvider,
  DeviceTypeContextProvider,
  Dialog,
  Divider2 as Divider,
  DividerContent,
  Drawer,
  Dropdown,
  DropdownItem,
  DropdownMenuOutline,
  DropdownMenuOutlineButtonTop,
  DropdownMenuOutlineButtonTopClassName,
  DropdownMenuOutlineOption,
  DropdownMenuOutlineOptionsList,
  DropdownProduct,
  DropdownSort,
  EMPTY_DATA_URL,
  EnquiryModal,
  FeaturedArticle,
  FeaturedArticleClassName,
  FeaturedTile,
  FeaturedTileSkeleton,
  FileTile,
  FilterContentLayout,
  FlagSelector,
  Flex,
  FlexItem,
  FooterNavigation,
  FooterNavigationItem,
  Grid,
  Header,
  HeaderClassName,
  HotButtons,
  IMAGE_SIZES,
  Image,
  ImageConfigProvider,
  ImageOverlay,
  IndicatorCheckbox,
  IndicatorColor,
  IndicatorRadio,
  Input,
  KNOWN_PRIVATE_VIDEO_ERROR_CODES,
  Label,
  List,
  ListItem,
  Loader3 as Loader,
  LoginModal,
  Logo,
  MOBILE_TOP_NAV_HEIGHT_FULL_LOGO,
  MOBILE_TOP_NAV_HEIGHT_SHORT_LOGO,
  MOBILE_TOP_NAV_HEIGHT_SHORT_LOGO_WITHOUT_DVH_SUPPORT,
  MagazineTile,
  MegaMenu,
  MemberTile,
  MemberTileClassName,
  MessageTooltip,
  MessageTooltipContent,
  MobileHeaderSection,
  MultiSlider,
  NavigationItem,
  NavigationList,
  NavigationListGroup,
  NavigationListSkeleton,
  NavigationMenu,
  NumberInput,
  Pagination2 as Pagination,
  PaginationItem,
  PaginationItemContent,
  PaginationItemCounter,
  PaginationItemsPerPage,
  Paragraph,
  PersonalMenu,
  PersonalProfile,
  PersonalProfileMenu,
  Popup,
  PopupContent,
  ProductInfo,
  ProductTile,
  ProfessionalTile,
  ProfileMenuItem,
  ProfileNavigation,
  ProfileNavigationItem,
  ProjectTile,
  Provider,
  RangeInput,
  RangeSelector,
  ReadMore,
  RendererProvider,
  Review,
  SIZE_TO_HEIGHT_MAPPING4 as SIZE_TO_HEIGHT_MAPPING,
  SearchBar,
  SearchMenu,
  SearchResult,
  SearchableDropdown,
  SearchableList,
  Skeleton,
  SkeletonLine,
  Subheader,
  SubheaderClassName,
  Subscription,
  SwipeableEdge,
  Switch,
  TABLET_TOP_NAV_HEIGHT,
  Tabs,
  TextInput,
  TextSelectorInput,
  TileGrid,
  TileImage,
  TileOverlay,
  TileVideo,
  ToastMessage,
  TopNavigation,
  VALID_LAYOUT_VALUES,
  VALID_LOADING_VALUES,
  VIDEO_TYPE,
  VariantColor,
  VideoPlayer,
  VirtualList,
  VirtualListItem,
  aboutUsTheme,
  accordionClassName,
  accordionContentClassName,
  accordionSlotClassNames,
  accordionTitleClassName2 as accordionTitleClassName,
  accordionTitleCustomSlotClassNames,
  accordionTitleSlotClassNames,
  adminCaptionClassName,
  adminCaptionSlotClassNames,
  adminTileHandledProps,
  advertisingTheme,
  appMenuClassName,
  appMenuSlotClassNames,
  archiproImageLoader,
  archizeenTileClassName,
  archizeenTileSlotClassNames,
  articleTileClassName,
  avatarClassName,
  avatarSlotClassNames,
  badgeClassName,
  bannerAdTileClassName,
  bannerAdTileSlotClassNames,
  baseTheme,
  bottomNavClassName,
  bottomNavSlotClassNames,
  boxClassName,
  breadcrumbItemClassName,
  breadcrumbNavigationClassName,
  breadcrumbNavigationSlotClassNames,
  businessProfileClassName,
  buttonClassName,
  captionClassName,
  carouselClassName,
  carouselIndicatorClassName,
  carouselItemClassName,
  carouselNavigationClassName,
  carouselNavigationItemClassName,
  carouselNavigationItemSlotClassNames,
  carouselOverlayClassName,
  carouselOverlaySlotClassNames,
  carouselPaddleClassName,
  carouselPaddleSlotClassNames,
  carouselPaddlesContainerClassName,
  carouselSlotClassNames,
  categoryListClassName,
  categoryListSlotClassNames,
  categorySelectorClassName,
  categorySelectorSlotClassNames,
  categoryTreeClassName,
  categoryTreeHeaderClassName,
  categoryTreeHeaderSlotClassNames,
  checkboxClassName,
  checkboxGroupClassName,
  checkboxSlotClassNames,
  clearAllBodyScrollLocks2 as clearAllBodyScrollLocks,
  containerClassName,
  createFelaRenderer,
  deviceWidths,
  dialogClassName,
  dialogSlotClassNames,
  disableBodyScroll2 as disableBodyScroll,
  dividerClassName,
  dividerContentClassName,
  drawerClassName,
  drawerSlotClassNames,
  dropdownClassName,
  dropdownItemClassName,
  dropdownItemSlotClassNames,
  dropdownMenuClassName,
  dropdownMenuOutlineOptionClassName,
  dropdownMenuOutlineOptionsListClassName,
  dropdownProductClassName,
  dropdownProductSlotClassNames,
  dropdownSlotClassNames,
  dropdownSortClassName,
  dropdownSortSlotClassNames,
  enableBodyScroll2 as enableBodyScroll,
  enquiryModalClassName,
  enquiryModalSlotClassNames,
  featuredArticleSlotClassNames,
  featuredTileClassName,
  featuredTileSkeletonClassName,
  featuredTileSkeletonClassNames,
  fileTileClassName,
  fileTileSlotClassNames,
  filterContentLayoutClassName,
  filterContentLayoutSlotClassNames,
  flagSelectorClassName,
  flagSelectorSlotClassNames,
  flexClassName,
  flexItemClassName,
  footerNavigationClassName,
  footerNavigationItemClassName,
  footerNavigationSlotClassNames,
  getFormatNumberWithCommas,
  getScrollbarStyle,
  getSelectedChain,
  getVideoEmbedFromURL,
  getVideoEmbedFromVideoID,
  gridClassName,
  hotButtonsClassName,
  imageClassName,
  imageOverlayClassName,
  indicatorCheckboxClassName,
  indicatorColorClassName,
  indicatorRadioClassName,
  inputClassName,
  inputSlotClassNames,
  isEllipsis,
  labelClassName,
  listClassName,
  listItemClassName,
  listSlotClassNames,
  loaderClassName,
  loginModalClassName,
  loginModalSlotClassNames,
  logoClassName,
  magazineTileClassName,
  mapImageSizeToDimensions,
  meetGreetTheme,
  megaMenuClassName,
  memberTheme,
  messageTooltipClassName,
  messageTooltipContentClassName,
  messageTooltipSlotClassNames,
  mobileHeaderSectionClassName,
  mobileHeaderSectionSlotClassNames,
  multiSliderClassName,
  multiSliderSlotClassNames,
  navigationItemClassName,
  navigationItemSlotClassNames,
  navigationListClassName,
  navigationListGroupClassName,
  navigationListSkeletonClassName,
  navigationListSkeletonClassNames,
  navigationListSlotClassNames,
  navigationMenuClassName,
  inputClassName as numberInputClassName,
  inputSlotClassNames as numberInputSlotClassNames,
  paginationClassName,
  paginationItemClassName,
  paginationItemContentClassName,
  paginationItemCounterClassName,
  paginationItemEllipsisClassName,
  paginationItemPageClassName,
  paginationItemPrevNextClassName,
  paginationItemsPerPageClassName,
  paginationItemsPerPageSlotClassNames,
  paginationSlotClassNames,
  paragraphClassName,
  personalMenuClassName,
  personalMenuSlotClassNames,
  personalProfileClassName,
  personalProfileMenuClassName,
  personalProfileSlotClassNames,
  popupClassName,
  popupContentClassName,
  popupContentSlotClassNames,
  productInfoClassName,
  productInfoSlotClassNames,
  productTheme,
  productTileClassName,
  productTileSlotClassNames,
  professionalTileClassName,
  professionalTileSlotClassNames,
  profileMenuItemClassName,
  profileNavigationClassName,
  profileNavigationClassNames,
  profileNavigationItemClassName,
  projectTileClassName,
  projectTileSlotClassNames,
  pxArrayToRem,
  pxToRem,
  rangeInputClassName,
  rangeInputSlotClassNames,
  rangeSelectorClassName,
  rangeSelectorSlotClassNames,
  readMoreClassName,
  reviewClassName,
  screen,
  searchBarClassName,
  searchMenuClassName,
  searchMenuClassNames,
  searchResultClassName,
  searchResultSlotClassNames,
  searchableDropdownClassName,
  searchableListClassName,
  searchableListSlotClassNames,
  skeletonClassName,
  skeletonLineClassName,
  subscriptionClassName,
  swipeableEdgeClassName,
  swipeableEdgeSlotClassNames,
  switchClassName,
  switchSlotClassNames,
  tabsClassName,
  tabsClassNames,
  textInputClassName,
  textInputSlotClassNames,
  textSelectorInputClassName,
  tileGridClassName,
  tileImageClassName,
  tileImageSlotClassNames,
  tileOverlayClassName,
  tileOverlaySlotClassNames,
  tileVideoClassName,
  tileVideoSlotClassNames,
  toastMessageClassName,
  toastMessageClassNames,
  topNavigationClassName,
  topNavigationSlotClassNames,
  useAppDisplayModeContext,
  useDeviceBrowserContext,
  useDeviceTypeContext,
  useImageConfig,
  useScrollLock,
  useStyles102 as useStyles,
  useTheme,
  variantColorClassName,
  variantColorSlotClassNames,
  videoPlayerClassName,
  virtualListClassName,
  virtualListItemClassName,
  virtualListItemSlotClassNames,
  virtualListSlotClassNames
};
